import * as THREE from 'three';
import { PointerLockControls } from 'three/addons/controls/PointerLockControls.js';
import { Sky } from 'three/addons/objects/Sky.js';
import { LineSegments2 } from 'three/addons/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/addons/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/addons/lines/LineMaterial.js';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import RAPIER from '@dimforge/rapier3d-compat';
import w1L7Spec from './levels/w1-l7.json';
import w2L1Spec from './levels/w2-l1.json';
import w2L2Spec from './levels/w2-l2.json';
import w2L3Spec from './levels/w2-l3.json';
import w2L4Spec from './levels/w2-l4.json';
import w2L5Spec from './levels/w2-l5.json';
import w2L6Spec from './levels/w2-l6.json';
import w2L9Spec from './levels/w2-l9.json';
import w2L7Spec from './levels/w2-l7.json';
import w2L8Spec from './levels/w2-l8.json';
import w2L10Spec from './levels/w2-l10.json';
import w2FlyingSong from './songs/w2-flying.json';
import w2ScaryHorrorSong from './songs/w2-scary-horror.json';
import w2FranticChaseSong from './songs/w2-frantic-chase.json';
import w2CalmMeditationSong from './songs/w2-calm-meditation.json';
import w2RobotFactorySong from './songs/w2-robot-factory.json';
import w2AncientTempleSong from './songs/w2-ancient-temple.json';
import w2RainyCityNightSong from './songs/w2-rainy-city-night.json';
import w2AlienDancePartySong from './songs/w2-alien-dance-party.json';
import w2CheekyFishDuckSong from './songs/w2-cheeky-fish-duck.json';
import w2IceCastleCrystalSong from './songs/w2-ice-castle-crystal.json';
import w3L1Spec from './levels/w3-l1.json';
import w3L2Spec from './levels/w3-l2.json';
import w3L3Spec from './levels/w3-l3.json';
import w3L4Spec from './levels/w3-l4.json';
import w3L5Spec from './levels/w3-l5.json';
import w3L6Spec from './levels/w3-l6.json';
import w3L7Spec from './levels/w3-l7.json';
import w3L8Spec from './levels/w3-l8.json';
import w3L9Spec from './levels/w3-l9.json';
import w3L10Spec from './levels/w3-l10.json';
import testSmileysSpec from './levels/test-smileys.json';
import testTempleSpec from './levels/test-temple.json';
import testGraveyardSpec from './levels/test-graveyard.json';
import testMusicBoxSpec from './levels/test-music-box.json';
import curatedSilhouettes from './curated-silhouettes.json';
import w1L2MultihitSong from './songs/w1-l2-multihit.json';
import w1L3SteeringSong from './songs/w1-l3-steering.json';
import w1L4StrideSong from './songs/w1-l4-stride.json';
import w1L5OpenSong from './songs/w1-l5-open.json';
import w1L6ChargeSong from './songs/w1-l6-charge.json';
import w1L7DominoSong from './songs/w1-l7-domino.json';
import w1L8GrowSong from './songs/w1-l8-grow.json';
import w1L9EngineSong from './songs/w1-l9-engine.json';
import w1L10CapstoneSong from './songs/w1-l10-capstone.json';
import testSmileysSong from './songs/test-smileys.json';
import testTempleSong from './songs/test-temple.json';
import testGraveyardSong from './songs/test-graveyard.json';
import testMusicBoxSong from './songs/test-music-box.json';
import w3SkiSlalomSong from './songs/w3-ski-slalom.json';
import w3SnakeSlitherGrassSong from './songs/w3-snake-slither-grass.json';
import w3BoxingComboSong from './songs/w3-boxing-combo.json';
import w3TennisSong from './songs/w3-tennis.json';
import w3ThreeTowersSong from './songs/w3-three-towers.json';
import w3StreetLightsSong from './songs/w3-street-lights.json';
import w3JengaTowersSong from './songs/w3-jenga-towers.json';
import w3DesertPyramidsSong from './songs/w3-desert-pyramids.json';
import w3BricksTowersSong from './songs/w3-bricks-towers.json';
import w3VolcanoFinaleSong from './songs/w3-volcano-finale.json';
import w2AscendingToStarsSong from './songs/w2-ascending-to-stars.json';

await RAPIER.init();

// URL-flag for the schema round-trip experiment: ?l7spec renders L7 from
// web/src/levels/w1-l7.json via buildFromSpec instead of buildLevel7Layout.
// Used to A/B the two builds back-to-back (see SmashGame/level-schema.md).
const USE_L7_SPEC = new URLSearchParams(location.search).has('l7spec');

// Phase timings — calibrated to "high power level" feel (TRACK_SPEED ~2× from source defaults).
// Source base: Impact 0.40s, Cooldown 1.05s. Higher levels collapse these proportionally.
const IMPACT_DELAY = 0.18;
// half-beat at the active song's BPM — lets the player smash rhythmically on every beat or half-beat.
// Updated by applySong() when BPM changes (default 0.25 = half-beat at 120 BPM).
let PUNCH_COOLDOWN = 0.25;
const TARGET_RANGE = 13.0;
const GLOVE_BARE = 'bare';
const GLOVE_SPRING = 'spring';
const GLOVE_ROCKET = 'rocket';
const GLOVE_CONFIG = {
  [GLOVE_BARE]:   { lockRange: 2.25, movesPlayer: false, lungeBase: 0.08 },
  [GLOVE_SPRING]: { lockRange: TARGET_RANGE, movesPlayer: false, lungeBase: 0.6 },
  [GLOVE_ROCKET]: { lockRange: TARGET_RANGE, movesPlayer: true,  lungeBase: 0.6 }
};
function normalizeGlove(value, fallback) {
  const glove = String(value || '').trim().toLowerCase();
  return GLOVE_CONFIG[glove] ? glove : fallback;
}
const _GLOVE_LOADOUT_KEY = 'smash-glove-loadout-v1';
function _loadSavedGloveLoadout() {
  try {
    const saved = JSON.parse(localStorage.getItem(_GLOVE_LOADOUT_KEY) || 'null');
    if (Array.isArray(saved)) {
      return [
        normalizeGlove(saved[0], GLOVE_SPRING),
        normalizeGlove(saved[1], GLOVE_ROCKET)
      ];
    }
  } catch (e) { /* localStorage blocked or corrupt — use default */ }
  return [GLOVE_BARE, GLOVE_BARE];
}
function _saveGloveLoadout(loadout) {
  try { localStorage.setItem(_GLOVE_LOADOUT_KEY, JSON.stringify(loadout)); } catch (e) {}
}
const _gloveParam = new URLSearchParams(location.search).get('gloves');
const _gloveParamParts = (_gloveParam || '').split(/[,\-/]/);
const gloveLoadout = _gloveParam
  ? [
      normalizeGlove(_gloveParamParts[0], GLOVE_SPRING),
      normalizeGlove(_gloveParamParts[1], GLOVE_ROCKET)
    ]
  : _loadSavedGloveLoadout(); // Dev override: ?gloves=spring,rocket
let _runtimeGloveLoadout = [...gloveLoadout];
function gloveForFist(fistIndex) {
  return _runtimeGloveLoadout[fistIndex] || GLOVE_BARE;
}
function gloveConfig(fistIndex) {
  return GLOVE_CONFIG[gloveForFist(fistIndex)] || GLOVE_CONFIG[GLOVE_BARE];
}
function gloveTheme(glove) {
  if (glove === GLOVE_SPRING) return 'spring';
  if (glove === GLOVE_ROCKET) return 'hook';
  return 'plain';
}
function maxEquippedTargetRange() {
  return Math.max(gloveConfig(0).lockRange, gloveConfig(1).lockRange);
}
// Highlight cone — first-person: cos(~50°) ≈ 0.65. Anything wider gets hard-rejected.
// Replaces the soft front-penalty since first-person FOV is narrow enough to make a hard cutoff feel right.
const HIGHLIGHT_ALIGN_MIN = 0.65;

// Lunge-feel admin-toggle state. Declared up here (instead of next to LUNGE_REACH and the
// brake-on-impact code) because the admin toggle row at module-load reads these via the
// getter — and the admin row runs ~6000 lines BEFORE the lunge constants would otherwise
// be declared. Hoisting avoids a TDZ ReferenceError that halts module init (same trap
// CLAUDE.md warns about for _beatRingOn).
let _contextStandoffOn = true;  // off = legacy uniform LUNGE_REACH
let _impactRecoilOn = true;     // off = legacy pure-brake (RECOIL_SPEED forced to 0)
let RECOIL_SPEED = 1.2;         // m/s gentle pushback at every impact when _impactRecoilOn
const MISS_RECOIL_EXTRA_SPEED = 4.0; // extra player-only bounce; never touches block physics
const MISS_RECOVERY_MS = 360;        // short input lockout + fist knock-away on mistimed hits
let _moveSubstepOn = true;      // off = legacy single-step XZ collision (tunneling possible at high lunge speeds)
let _rayAimTargeting = true;    // off = legacy block-center X/Z scoring (wide blocks lose to centered blocks behind)
let _sharedCooldownOn = true;   // off = legacy per-fist gate set at release (single-fist locked to ~1/beat, alternating gets 2x rate)
// Dislodged-block self-destruct (single-player feel test for the co-op "move = pop" rule).
// A dislodged (dynamic) block pops once it comes to REST. _dislodgePop is the master gate;
// _dislodgePopGroundOnly narrows it to blocks that settled ON the floor (rubble that lands on
// top of other blocks survives). Both default OFF so live players see today's behaviour; these
// are dev toggles in the admin panel. See the dislodge-pop scan in stepPhysics.
let _dislodgePop = true;           // master: dislodged blocks self-destruct (ground-break is the
                                   // shipped default now). ?pop=off disables for comparison.
let _dislodgePopGroundOnly = true;  // default = ground-contact-break. ?pop=settle = move=pop (rejected)
let _dislodgeImpactMin = 3.0;       // ground mode: min downward speed (m/s) for a hit to count as a
                                    // real landing. Below this (already resting, gently tipping) the
                                    // block survives as smashable rubble. Tunable via ?popv=
let _dislodgeMinFall = 4.5;         // ground mode: min metres a block must FALL (peak height to floor)
                                    // before it can shatter. Excludes blocks jostled/shoved within a
                                    // collapsing pile that spike in speed but never actually dropped.
                                    // This is the main strictness dial. 3.0 suits campaign-scale
                                    // structures; the gauntlet's 45-70m towers want a stricter value
                                    // (see _GAUNTLET_DISLODGE_FALL). Tunable per page load via ?poph=
let _dislodgeMinFallExplicit = false; // true once ?poph= is set — then it wins over per-level pins
const _GAUNTLET_DISLODGE_FALL = 6.0;  // the gauntlet's towers are huge, so they want a bigger drop
// Multiplayer co-op prototype (slice 1). All gated behind ?mp=ROOM; null/no-op otherwise.
let _mp = null;                 // the net client (web/src/net/mp.js), set once connected
// MP can be hidden on the public production host while we chase down the rare Rapier re-entrancy freeze
// (see the physics-poison diagnostics below). Currently RE-ENABLED on prod for a community live test — the
// diagnostics, graceful-fail overlay, and source maps stay on to capture any freeze. To hide it again, set
// this back to: /^(www\.)?soundssmashing\.com$/i.test(location.hostname).
const _MP_PROD_HIDDEN = false;
// Physics-poison diagnostics (declared early so the mp/tick functions can reference them without TDZ).
// A Rapier re-entrancy panic permanently poisons the world; _physicsPoisoned latches it, _phase is a
// per-frame breadcrumb of what we were doing, _firstFatalDumped guards a one-time rich console dump.
let _physicsPoisoned = false, _firstFatalDumped = false;
let _phase = 'boot';
// Capture the room NOW, at module load, BEFORE the ?gauntlet/?play handlers strip the query string
// off the URL via history.replaceState. (That strip was eating mp= and silently disabling co-op.)
const _MP_ROOM = _MP_PROD_HIDDEN ? null : new URLSearchParams(location.search).get('mp');
// Netcode experiment selector. 'cause' (default) = each client runs its OWN local physics for peer
// collapses from the synced cause (impulse) + state events (unanchor/death); smooth + cheap on any link,
// rubble diverges slightly. 'stream' (?mpmode=stream) = authoritative collapse streaming (peer blocks are
// kinematic, driven by streamed poses; exact match but bandwidth-bound). Both share the room/relay/netId/
// avatar plumbing and only differ in how the receiver treats peer-owned blocks, so you can A/B on one tower.
// Cause is the default because it stays smooth + correct on real-world links (see the two-machine tests).
const _MP_MODE = (new URLSearchParams(location.search).get('mpmode') === 'stream') ? 'stream' : 'cause';
// Relay endpoint. Captured at module load (BEFORE the ?gauntlet/?play query-strip, same as _MP_ROOM).
// Default derives from the origin: localhost -> local dev relay (tools/mp-relay.mjs); any other host ->
// the production Durable Object relay. Override with ?relay=wss://host. Must be wss:// when the page is
// served over https (so the prod default is wss://).
const _MP_RELAY = new URLSearchParams(location.search).get('relay')
  || ((location.hostname === 'localhost' || location.hostname === '127.0.0.1')
    ? 'ws://localhost:8080'
    : 'wss://relay.soundssmashing.com');
// Per-player identity palette (kit cyan / pink / yellow / green) — the relay assigns each connection a
// stable index 0-3; mp.js paints the avatar accents + ground ring + nameplate, and the HUD scoreboard
// colours each bar to match. Keep in sync with the CSS --col-* vars.
const _MP_PLAYER_COLORS = ['#00E5FF', '#FF3B6B', '#FFE600', '#43d675'];
let _mpEnabled = !!_MP_ROOM;    // true the moment ?mp= is present (sync) — gates pause so MP never freezes
// Competitive race mode, built on top of co-op. ?race=smashes|coins turns a ?mp= room into a free-for-all
// race: same shared arena, players scramble to break the MOST blocks (smashes) or bank the MOST coins
// (coins). The relay is the authoritative referee (first death claim per netId wins). ?assist=on|off
// forces rhythm assist identically for everyone (fair); ?arena=<label> is informational for the HUD.
// Everything below is a no-op without ?race= (all these resolve null/false), so normal play + normal
// co-op are unaffected.
const _RACE_METRIC = (() => {
  const v = new URLSearchParams(location.search).get('race');
  return (v === 'coins' || v === 'smashes') ? v : null;
})();
const _RACE_ON = !!_MP_ROOM && !!_RACE_METRIC;
const _RACE_ARENA = new URLSearchParams(location.search).get('arena') || null;
const _RACE_ASSIST = (() => {
  const v = new URLSearchParams(location.search).get('assist');
  return v === 'on' ? true : v === 'off' ? false : null;
})();
// Fairness: every racer runs the SAME loadout (the gloves are a power ladder, not balanced choices), and
// the HOST picks it in the lobby (?loadout=spring-spring | spring-rocket | rocket-rocket | bare-bare...).
// A single token (?loadout=rocket) applies to both hands. Defaults to dual-spring (good reach, most fun),
// granted regardless of campaign unlock progression. Read + enforced in _enterLevel so it can't be
// clobbered by the player's own saved loadout.
const _RACE_LOADOUT = (() => {
  if (!_RACE_ON) return null;
  const v = new URLSearchParams(location.search).get('loadout');
  if (!v) return [GLOVE_SPRING, GLOVE_SPRING];
  const parts = v.split(/[,\-/]/);
  const p0 = normalizeGlove(parts[0], GLOVE_SPRING);
  return [p0, parts.length > 1 ? normalizeGlove(parts[1], p0) : p0];
})();
if (_RACE_ON) _runtimeGloveLoadout = [..._RACE_LOADOUT];
let _raceLocked = false;        // true = input gated shut before the start-gate GO (or after the finish)
let _raceStarted = false;       // the start gate has opened (GO fired)
let _raceOver = false;          // the referee has declared a result
let _raceBoard = [];            // latest standings from the referee [{id,name,smashes,coins}]
let _raceReadySent = false;     // guard so we only signal rready once per level entry
let _raceEndSent = false;       // guard so we only fire rend (arena emptied) once
let _raceGoAt = 0;              // wall-clock ms the start gate opens (from the referee's rgo)
let _raceSpawnSlot = -1;        // referee-assigned spawn index (unique per player); -1 = not yet assigned
let _raceSpawnCount = 0;        // number of players the referee spread across the start line
let _raceTotalBlocks = 0;       // destroyable blocks at match start = the goal the scoreboard bars fill toward
let _raceSpawnApply = false;    // deferred: apply the referee-assigned spawn slot at the safe tick point (not from onmessage)
let _raceRematchPending = false;// deferred: rebuild the arena for a rematch at the safe tick point (not from onmessage)
let _raceClearPending = false;  // deferred: clear leftover blocks at the result, at the safe tick point (not from onmessage)
let _raceEmptyAt = -1;          // perf.now when the arena first hit zero blocks (debounce before rend)
let _raceBanner = null;         // big centre banner: WAITING / countdown / GO / result
let _raceHud = null;            // corner standings panel
let _lastBreakCoins = 0;        // coins the most recent break paid (stashed by spawnChunks, read by breakBlock)
let _mpSpectateBadge = null;    // non-blocking "click to play" hint shown when this MP window is idle
let _netRemoteContext = false;  // true while replaying a peer's event (death/unanchor) — suppresses
                                // re-broadcast and tags any blocks we unanchor as peer-owned
let _netSeq = 0;                // deterministic per-block id; reset at the start of every level build
const _netById = new Map();     // netId -> block, so a peer's event can find the same block
// URL overrides so each feel has its own shareable link:
//   ?pop=settle  -> Rule 1: move = pop (pops wherever a dislodged block stops)
//   ?pop=ground  -> Rule 2 single-player feel (pops only blocks that settle on the floor)
//   ?pop=off / absent -> today's behaviour (rubble persists)
{
  const _popParam = new URLSearchParams(location.search).get('pop');
  if (_popParam === 'off') { _dislodgePop = false; }
  else if (_popParam === 'settle') { _dislodgePop = true; _dislodgePopGroundOnly = false; }
  else if (_popParam === 'ground') { _dislodgePop = true; _dislodgePopGroundOnly = true; }
  const _popv = new URLSearchParams(location.search).get('popv');
  if (_popv !== null && !Number.isNaN(parseFloat(_popv))) _dislodgeImpactMin = parseFloat(_popv);
  const _poph = new URLSearchParams(location.search).get('poph');
  if (_poph !== null && !Number.isNaN(parseFloat(_poph))) { _dislodgeMinFall = parseFloat(_poph); _dislodgeMinFallExplicit = true; }
}
// Music-aligned slot tracking. Each press is rounded to the nearest 8th-note slot of the
// music grid. One press per slot, regardless of fist — caps fire rate at 8ths whether the
// player single-taps, alternates, or mashes. ±125ms tolerance per slot (the natural round-
// to-nearest boundary) is wider than the perfect/good timing windows so motor variance can't
// block presses that the timing system would still score. Dual-press exception preserves the
// dual-charge radius bonus: a second press in the same slot is allowed if the other fist was
// pressed within DUAL_PRESS_WINDOW. _slotDualPressUsed prevents the exception from being
// abused (max 2 fires per slot total).
let _lastConsumedSlot = -1;
let _slotDualPressUsed = -1;
const _lastPressTime = [-Infinity, -Infinity];
const DUAL_PRESS_WINDOW = 0.1;
// Per-fist input-source tracking. Set on mousedown via e.isTrusted (synthetic events
// dispatched by the keyboard handler are isTrusted=false, real mouse is true). Read on
// mouseup so the release scoring uses the same leniency that the click-down used.
const _chargingSource = ['mouse', 'mouse'];
// Keyboard timing leniency multiplier — keyboard input has higher latency + bigger key
// travel than mouse, so the same window feels tighter on space than on click. Multiplies
// the global _timingLeniency for keyboard-triggered events only. Tunable via admin.
let _kbLeniencyMult = 1.0;  // Tightened 2026-05-13 from 1.4 → 1.1 → 1.0 (mouse parity). The accessibility bonus turned out to be the spacebar-mash exploit vector — at 1.0 keyboard plays on the same precision floor as mouse and the mash advantage disappears.
// Beat-anticipation cue on the currently-targeted brick. Hoisted up here for the same TDZ
// reason as the lunge flags above. Default on after 2026-05-16 playtest reported "window
// too tight + no visual cues" — drives the per-face wireframe squares that grow outward
// across the 2-beat cycle and reach the face edges at the downbeat.
let _approachHaloOn = true;
// Airborne hover-on-hit. When the player smashes a block at horizontal-ish aim while in
// the air, briefly suspend gravity (+ zero out downward velocity) so they can re-aim
// instead of falling immediately. Reuses the apex boost coast/float windows so the
// existing pipeline handles the gravity suspension. Doesn't fire on ground play (airborne
// check) or on look-down vault smashes (aim-angle threshold). Look-up apex smashes still
// get their full apex boost; durations are Math.max'd so hover never shortens an apex.
let _hoverHitOn = true;
let HOVER_COAST_MS = 200;
let HOVER_FLOAT_MS = 300;
let HOVER_LIFT_VEL = 3.0;  // m/s upward kick at impact — small pop so the player drifts up slightly during coast instead of just freezing
let HOVER_LIFT_LOCKOUT_MS = 700;  // ms — lift refires only after this gap. Prevents wall-climbing on stacked thin blocks (chained hits inside the gap get coast/float but no lift), while still firing on every fresh fall arc / sky smash.
let _lastHoverLiftTime = -Infinity;
const HOVER_AIM_HORIZ_THRESHOLD = 0.4;  // |lungeFwd.y| under this counts as "horizontal" — ~24° cone
// Audio metronome tick — when player has a target highlighted, play a subtle high-frequency
// blip on each beat. Helps players with weaker rhythm reading lock onto the beat without
// cluttering the visuals further. Off by default; enable in admin.
let _targetTickOn = true;
let _lastTargetTickPhase = 0;
function _getCurrentBeatSlot() {
  // Slot tracking uses the same latency-compensated time as scoring + visuals so the
  // mash gate is in lockstep with the player's perceived rhythm rather than the API.
  // Before music starts, do not fall back to wall-clock time. That creates a huge slot
  // number, then the real song clock starts back near 0 and every future press is rejected
  // as an "earlier" slot.
  const t = _beatElapsed() ?? 0;
  return Math.round(t / SCORE_GRID);
}

const scene = new THREE.Scene();

const camera = new THREE.PerspectiveCamera(78, window.innerWidth / window.innerHeight, 0.05, 2000);
camera.position.set(0, 1.65, 6);

const renderer = new THREE.WebGLRenderer({ antialias: true });
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.0;
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
document.body.appendChild(renderer.domElement);

// Procedural atmospheric sky — also used as the IBL environment for PBR reflections.
const sky = new Sky();
sky.scale.setScalar(450000);
scene.add(sky);
{
  const u = sky.material.uniforms;
  u['turbidity'].value = 6;
  u['rayleigh'].value = 2;
  u['mieCoefficient'].value = 0.005;
  u['mieDirectionalG'].value = 0.8;
}
const sunDir = new THREE.Vector3();
{
  const elevationDeg = 35;
  const azimuthDeg = 140;
  const phi = THREE.MathUtils.degToRad(90 - elevationDeg);
  const theta = THREE.MathUtils.degToRad(azimuthDeg);
  sunDir.setFromSphericalCoords(1, phi, theta);
  sky.material.uniforms['sunPosition'].value.copy(sunDir);
}

// Generate environment cubemap from the sky for PBR ambient reflections.
const pmremGenerator = new THREE.PMREMGenerator(renderer);
scene.environment = pmremGenerator.fromScene(sky, 0.04).texture;

function makeSpringMetalEnvMap() {
  const size = 64;
  const faces = [
    ['#f7fbff', '#6c879c', '#1d2832'],
    ['#dbeeff', '#8298aa', '#27313a'],
    ['#ffffff', '#c9ecff', '#5d7484'],
    ['#4d382a', '#846043', '#1e1713'],
    ['#fff4c8', '#96d5ff', '#314354'],
    ['#e7ffff', '#8293a0', '#151b22']
  ];
  const canvases = faces.map((stops, faceIndex) => {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    const grad = ctx.createLinearGradient(0, 0, 0, size);
    grad.addColorStop(0, stops[0]);
    grad.addColorStop(0.55, stops[1]);
    grad.addColorStop(1, stops[2]);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, size, size);
    ctx.fillStyle = faceIndex % 2 === 0 ? 'rgba(255,255,255,0.38)' : 'rgba(255,245,210,0.28)';
    ctx.fillRect(size * 0.12, 0, size * 0.12, size);
    ctx.fillRect(size * 0.58, 0, size * 0.08, size);
    return canvas;
  });
  const texture = new THREE.CubeTexture(canvases);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
}
const springMetalEnvMap = makeSpringMetalEnvMap();

const _hemiLight = new THREE.HemisphereLight(0xbcd4ff, 0x3a2a1e, 0.35);
scene.add(_hemiLight);
const sun = new THREE.DirectionalLight(0xfff4d6, 2.6);
sun.position.copy(sunDir).multiplyScalar(40);
sun.castShadow = true;
sun.shadow.mapSize.set(2048, 2048);
sun.shadow.camera.left = -28;
sun.shadow.camera.right = 28;
sun.shadow.camera.top = 28;
sun.shadow.camera.bottom = -28;
sun.shadow.camera.near = 0.5;
sun.shadow.camera.far = 120;
sun.shadow.bias = -0.0005;
scene.add(sun);
scene.add(sun.target);

// Camera must be in the scene graph for its children (fists) to render.
scene.add(camera);

// =========================================================
// Sky FX — clouds, birds, beat-pulse. All admin-toggled, off by default.
// Sit above the world; never touch physics. Toggling ON shows them, OFF hides
// (and restores the sky's baseline rayleigh for the pulse case).
// =========================================================

const _skyFx = {
  cloudsOn: true,
  birdsOn: true,
  beatPulseOn: false,
  boomboxOn: false,
  wallSpeakersOn: true,
  wallTrimOn: true,
};
let _SKY_BASELINE_RAYLEIGH = sky.material.uniforms['rayleigh'].value;

// One puff texture shared by every cloud sprite — built from overlapping radial
// gradients so the silhouette is soft and clearly cloud-shaped at any scale.
function _makeCloudTexture() {
  const c = document.createElement('canvas');
  c.width = 256; c.height = 128;
  const ctx = c.getContext('2d');
  const blobs = [
    { x: 128, y: 80, r: 60, a: 0.85 },
    { x:  90, y: 70, r: 45, a: 0.75 },
    { x: 170, y: 70, r: 50, a: 0.80 },
    { x:  60, y: 90, r: 35, a: 0.65 },
    { x: 200, y: 90, r: 38, a: 0.65 },
  ];
  for (const b of blobs) {
    const g = ctx.createRadialGradient(b.x, b.y, b.r * 0.1, b.x, b.y, b.r);
    g.addColorStop(0,   `rgba(255,255,255,${b.a})`);
    g.addColorStop(0.6, `rgba(255,255,255,${b.a * 0.4})`);
    g.addColorStop(1,   'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 256, 128);
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
const _cloudTexture = _makeCloudTexture();

const _cloudGroup = new THREE.Group();
_cloudGroup.visible = _skyFx.cloudsOn;
scene.add(_cloudGroup);
const _CLOUD_COUNT = 14;
const _CLOUD_DRIFT_SPEED = 1.5;     // m/s
const _CLOUD_RADIUS = 280;          // wrap distance from camera in X/Z
const _clouds = [];
for (let i = 0; i < _CLOUD_COUNT; i++) {
  const mat = new THREE.SpriteMaterial({
    map: _cloudTexture,
    transparent: true,
    depthWrite: false,
    opacity: 0.85,
  });
  const s = new THREE.Sprite(mat);
  s.position.set(
    (Math.random() - 0.5) * 2 * _CLOUD_RADIUS,
    80 + Math.random() * 60,
    (Math.random() - 0.5) * 2 * _CLOUD_RADIUS,
  );
  const scale = 60 + Math.random() * 40;
  s.scale.set(scale, scale * 0.5, 1);
  _cloudGroup.add(s);
  _clouds.push(s);
}

function _updateClouds(dt) {
  if (!_skyFx.cloudsOn) return;
  const camX = camera.position.x;
  const camZ = camera.position.z;
  for (const c of _clouds) {
    c.position.x += _CLOUD_DRIFT_SPEED * dt;
    if (c.position.x - camX > _CLOUD_RADIUS) c.position.x -= 2 * _CLOUD_RADIUS;
    const dz = c.position.z - camZ;
    if (dz >  _CLOUD_RADIUS) c.position.z -= 2 * _CLOUD_RADIUS;
    if (dz < -_CLOUD_RADIUS) c.position.z += 2 * _CLOUD_RADIUS;
  }
}

// Bird silhouettes — 16×8 pixel-art "M" with a 2-frame wing flap. NEAREST filter
// so the pixels stay crunchy at any size; small fixed pool, spawn-on-timer.
function _makeBirdTexture(frame) {
  const c = document.createElement('canvas');
  c.width = 16; c.height = 8;
  const ctx = c.getContext('2d');
  ctx.fillStyle = '#1a1a1a';
  if (frame === 0) {
    // Wings up — classic gull "M".
    ctx.fillRect(1, 4, 2, 2);
    ctx.fillRect(3, 3, 2, 2);
    ctx.fillRect(5, 2, 2, 2);
    ctx.fillRect(7, 3, 2, 2);
    ctx.fillRect(9, 2, 2, 2);
    ctx.fillRect(11, 3, 2, 2);
    ctx.fillRect(13, 4, 2, 2);
  } else {
    // Wings level.
    ctx.fillRect(1, 4, 14, 1);
    ctx.fillRect(7, 3, 2, 2);
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.generateMipmaps = false;
  return tex;
}
const _BIRD_FRAMES = [_makeBirdTexture(0), _makeBirdTexture(1)];
// Fallback flap cadence — only used when no music is playing. With music, the wing
// frame is derived from the beat index so every bird flaps in unison with the song.
const _BIRD_FLAP_FALLBACK = 0.25;

const _birdGroup = new THREE.Group();
_birdGroup.visible = _skyFx.birdsOn;
scene.add(_birdGroup);
const _BIRD_COUNT = 6;
const _birds = [];
let _birdSizeMult = 3.0;
function _applyBirdScale(b) {
  const s = b.userData.baseScale * _birdSizeMult;
  b.scale.set(s, s * 0.5, 1);
}
for (let i = 0; i < _BIRD_COUNT; i++) {
  const mat = new THREE.SpriteMaterial({ map: _BIRD_FRAMES[0], transparent: true, depthWrite: false });
  const s = new THREE.Sprite(mat);
  s.userData.baseScale = 4 + Math.random() * 2;
  _applyBirdScale(s);
  s.visible = false;
  s.userData.alive = false;
  s.userData.flapFrame = 0;
  s.userData.vx = 0;
  s.userData.vz = 0;
  _birdGroup.add(s);
  _birds.push(s);
}
let _birdSpawnTimer = 0;

function _spawnBird() {
  const idle = _birds.find((b) => !b.userData.alive);
  if (!idle) return;
  const camX = camera.position.x;
  const camZ = camera.position.z;
  const fromLeft = Math.random() < 0.5;
  const SPAWN_X = 250;
  const speed = 8 + Math.random() * 8;
  idle.userData.alive = true;
  idle.userData.flapFrame = 0;
  idle.userData.vx = fromLeft ? speed : -speed;
  idle.userData.vz = (Math.random() - 0.5) * 2;
  _applyBirdScale(idle);
  idle.position.set(
    camX + (fromLeft ? -SPAWN_X : SPAWN_X),
    50 + Math.random() * 40,
    camZ + (Math.random() - 0.5) * 2 * SPAWN_X,
  );
  idle.material.map = _BIRD_FRAMES[0];
  idle.material.needsUpdate = true;
  idle.visible = true;
}

function _updateBirds(dt) {
  if (!_skyFx.birdsOn) return;
  _birdSpawnTimer -= dt;
  if (_birdSpawnTimer <= 0) {
    _spawnBird();
    _birdSpawnTimer = 5 + Math.random() * 4;
  }
  // Beat-locked wing flap — frame derives from the global beat index so every bird
  // is in lockstep with the song. Falls back to a wall-clock cadence when no music
  // is playing so birds still animate (e.g. if visible before audio kicks in).
  let flapFrame;
  if (_musicStarted && audioCtx) {
    const elapsed = audioCtx.currentTime - _musicLoopStartTime;
    flapFrame = Math.floor(elapsed / MUSIC_BEAT) & 1;
  } else {
    flapFrame = Math.floor(performance.now() / 1000 / _BIRD_FLAP_FALLBACK) & 1;
  }
  const camX = camera.position.x;
  const DESPAWN_X = 320;
  for (const b of _birds) {
    if (!b.userData.alive) continue;
    b.position.x += b.userData.vx * dt;
    b.position.z += b.userData.vz * dt;
    if (b.userData.flapFrame !== flapFrame) {
      b.userData.flapFrame = flapFrame;
      b.material.map = _BIRD_FRAMES[flapFrame];
      b.material.needsUpdate = true;
    }
    if (Math.abs(b.position.x - camX) > DESPAWN_X) {
      b.userData.alive = false;
      b.visible = false;
    }
  }
}

// =========================================================
// Sky boombox — multi-part pixel-art sprite assembly that "plays" the song.
// Body, two speakers, cassette window with spinning reels, VU meter, four LEDs.
// Group sits at fixed world position; its rotation is updated to the camera's
// each frame so child offsets stay screen-aligned (left speaker is always to the
// camera's left, etc). Each child is a Sprite so it billboards individually too.
// =========================================================

function _bbCanvas(w, h) {
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const ctx = c.getContext('2d');
  ctx.imageSmoothingEnabled = false;
  return { c, ctx };
}
function _bbTex(c) {
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.generateMipmaps = false;
  return tex;
}
function _bbSprite(tex, opts = {}) {
  const mat = new THREE.SpriteMaterial({
    map: tex,
    transparent: true,
    depthWrite: false,
    color: opts.color || 0xffffff,
  });
  return new THREE.Sprite(mat);
}

function _makeBoomboxBodyTexture() {
  const { c, ctx } = _bbCanvas(192, 96);
  // Case body — dark plastic, slightly rounded by 1-px chamfer.
  ctx.fillStyle = '#181b22'; ctx.fillRect(2, 2, 188, 92);
  ctx.fillStyle = '#1f242c'; ctx.fillRect(0, 4, 192, 88);
  ctx.fillStyle = '#181b22'; ctx.fillRect(4, 0, 184, 96);
  // Top highlight + bottom shadow strip.
  ctx.fillStyle = '#2c333d'; ctx.fillRect(4, 4, 184, 3);
  ctx.fillStyle = '#0a0c10'; ctx.fillRect(4, 89, 184, 3);
  // Centre cassette deck recess (window will sit on top of this).
  ctx.fillStyle = '#0a0c10'; ctx.fillRect(64, 28, 64, 36);
  ctx.fillStyle = '#15181e'; ctx.fillRect(66, 30, 60, 32);
  // Side vents — pixel rows, left and right of the deck.
  ctx.fillStyle = '#0a0c10';
  for (let i = 0; i < 6; i++) { ctx.fillRect(14, 30 + i * 6, 36, 2); ctx.fillRect(142, 30 + i * 6, 36, 2); }
  // Handle indent on top.
  ctx.fillStyle = '#0a0c10'; ctx.fillRect(72, 0, 48, 6);
  ctx.fillStyle = '#3a414c'; ctx.fillRect(74, 2, 44, 2);
  return _bbTex(c);
}
function _makeBoomboxSpeakerTexture() {
  const { c, ctx } = _bbCanvas(64, 64);
  // Outer rim
  ctx.fillStyle = '#2c333d';
  ctx.beginPath(); ctx.arc(32, 32, 30, 0, Math.PI * 2); ctx.fill();
  // Cone
  ctx.fillStyle = '#0a0c10';
  ctx.beginPath(); ctx.arc(32, 32, 26, 0, Math.PI * 2); ctx.fill();
  // Mid ring
  ctx.fillStyle = '#1c2026';
  ctx.beginPath(); ctx.arc(32, 32, 18, 0, Math.PI * 2); ctx.fill();
  // Dust cap
  ctx.fillStyle = '#2c333d';
  ctx.beginPath(); ctx.arc(32, 32, 8, 0, Math.PI * 2); ctx.fill();
  // Specular hint
  ctx.fillStyle = '#5b6571';
  ctx.beginPath(); ctx.arc(27, 27, 3, 0, Math.PI * 2); ctx.fill();
  return _bbTex(c);
}
function _makeBoomboxReelTexture() {
  const { c, ctx } = _bbCanvas(32, 32);
  ctx.fillStyle = '#cfd6dc';
  ctx.beginPath(); ctx.arc(16, 16, 14, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = '#0a0c10';
  ctx.beginPath(); ctx.arc(16, 16, 4, 0, Math.PI * 2); ctx.fill();
  // Three spoke holes — visible spokes give the rotation something to read.
  ctx.fillStyle = '#0a0c10';
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2;
    const x = 16 + Math.cos(a) * 9;
    const y = 16 + Math.sin(a) * 9;
    ctx.beginPath(); ctx.arc(x, y, 2.5, 0, Math.PI * 2); ctx.fill();
  }
  return _bbTex(c);
}
function _makeBoomboxLedTexture() {
  const { c, ctx } = _bbCanvas(16, 16);
  ctx.fillStyle = '#ffffff';
  ctx.beginPath(); ctx.arc(8, 8, 6, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = '#cfd6dc';
  ctx.beginPath(); ctx.arc(6, 6, 2, 0, Math.PI * 2); ctx.fill();
  return _bbTex(c);
}
function _makeBoomboxVuTexture() {
  const { c, ctx } = _bbCanvas(64, 8);
  // White bar — colored via SpriteMaterial.color so we can tint at runtime.
  ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 1, 64, 6);
  ctx.fillStyle = '#cfd6dc'; ctx.fillRect(0, 0, 64, 1);
  return _bbTex(c);
}

const _BB_BODY_TEX    = _makeBoomboxBodyTexture();
const _BB_SPEAKER_TEX = _makeBoomboxSpeakerTexture();
const _BB_REEL_TEX    = _makeBoomboxReelTexture();
const _BB_LED_TEX     = _makeBoomboxLedTexture();
const _BB_VU_TEX      = _makeBoomboxVuTexture();

// Composite the boombox sprite parts (body + speakers + reels) into a single 2D canvas
// for the loading screen "now playing" panel. Two frames — A is the rest pose, B has
// the speakers slightly bumped out for a "thumping" effect. _updateLoadingSpeakerPulse
// swaps between them on each beat to give the boombox a 2-frame BPM-synced animation
// matching the kit's chunky pixel-art rhythm.
function _makeBoomboxCompositeUrl(spkSize, vuLit) {
  const W = 256, H = 128;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d');
  ctx.imageSmoothingEnabled = false;
  const bx = (W - 192) / 2, by = (H - 96) / 2;
  ctx.drawImage(_BB_BODY_TEX.image, bx, by);

  // VU meters above the cassette deck — chunky 7-segment colored bars on each side of
  // the deck recess. `vuLit` (0..1) controls how many segments are lit; the rest are
  // dimmed. Bright colors here are what brings the boombox to life on a dark loading
  // screen — without these the body is just dark grays.
  const vuColors = ['#39ff7a', '#39ff7a', '#a3ff39', '#ffd66b', '#ffd66b', '#ff8a39', '#ff5a3a'];
  const segCount = vuColors.length;
  const lit = Math.round(vuLit * segCount);
  const segW = 4, segH = 8, gap = 1;
  const drawVu = (cx, cy) => {
    // VU bar painted as N small horizontal segments running left-to-right.
    for (let i = 0; i < segCount; i++) {
      ctx.fillStyle = vuColors[i];
      ctx.globalAlpha = i < lit ? 1 : 0.18;
      ctx.fillRect(cx + i * (segW + gap), cy, segW, segH);
    }
    ctx.globalAlpha = 1;
  };
  // Two VU strips above each speaker grille
  drawVu(bx + 14, by + 14);
  drawVu(bx + 142, by + 14);

  // Cassette LEDs — three bright pixel-art dots above the deck (red/green/orange)
  // simulating the "RECORD/PLAY/PAUSE" indicators. These give the boombox warm color
  // accents in the otherwise dark face.
  const ledY = by + 18;
  const drawLed = (cx, color) => {
    ctx.fillStyle = color;
    ctx.beginPath(); ctx.arc(cx, ledY, 2.5, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.beginPath(); ctx.arc(cx - 0.5, ledY - 0.5, 1, 0, Math.PI * 2); ctx.fill();  // glint
  };
  drawLed(bx + 76,  '#ff5a3a');  // red — RECORD
  drawLed(bx + 96,  '#39ff7a');  // green — PLAY
  drawLed(bx + 116, '#ffd66b');  // amber — PAUSE

  // Speakers + reels on top of all the body decoration
  ctx.drawImage(_BB_SPEAKER_TEX.image, bx + 32 - spkSize / 2, by + 48 - spkSize / 2, spkSize, spkSize);
  ctx.drawImage(_BB_SPEAKER_TEX.image, bx + 160 - spkSize / 2, by + 48 - spkSize / 2, spkSize, spkSize);
  const reel = 22;
  ctx.drawImage(_BB_REEL_TEX.image, bx + 80 - reel / 2, by + 46 - reel / 2, reel, reel);
  ctx.drawImage(_BB_REEL_TEX.image, bx + 112 - reel / 2, by + 46 - reel / 2, reel, reel);
  return c.toDataURL();
}
const _BOOMBOX_FRAME_A = _makeBoomboxCompositeUrl(42, 0.35);  // resting — VU low
const _BOOMBOX_FRAME_B = _makeBoomboxCompositeUrl(50, 0.85);  // beat — speakers thumping, VU peaked
const _BOOMBOX_COMPOSITE_URL = _BOOMBOX_FRAME_A;

const _boomboxGroup = new THREE.Group();
_boomboxGroup.position.set(0, 90, -80);
_boomboxGroup.visible = false;
scene.add(_boomboxGroup);
let _boomboxSizeMult = 1.0;

// World-space size constants. Final on-screen scale = const × _boomboxSizeMult.
const _BB_BODY_W = 60, _BB_BODY_H = 30;
const _BB_SPK_R  = 12;        // speaker radius (so diameter = 24)
const _BB_CASS_W = 18, _BB_CASS_H = 9;
const _BB_REEL_R = 2.0;
const _BB_LED_R  = 0.8;
const _BB_VU_W   = 16, _BB_VU_H = 0.8;

const _bbBody = _bbSprite(_BB_BODY_TEX);
_bbBody.position.set(0, 0, 0);
_boomboxGroup.add(_bbBody);

const _bbSpkL = _bbSprite(_BB_SPEAKER_TEX);
_bbSpkL.position.set(-18, -2, 1);
_boomboxGroup.add(_bbSpkL);
const _bbSpkR = _bbSprite(_BB_SPEAKER_TEX);
_bbSpkR.position.set(18, -2, 1);
_boomboxGroup.add(_bbSpkR);

const _bbCassette = _bbSprite(_BB_BODY_TEX);  // body texture reused for the dark window inset
_bbCassette.material.color.setHex(0x000000);
_bbCassette.position.set(0, 1, 1);
_boomboxGroup.add(_bbCassette);

const _bbReelL = _bbSprite(_BB_REEL_TEX);
_bbReelL.position.set(-3.5, 1, 2);
_boomboxGroup.add(_bbReelL);
const _bbReelR = _bbSprite(_BB_REEL_TEX);
_bbReelR.position.set(3.5, 1, 2);
_boomboxGroup.add(_bbReelR);

// VU meters — one per channel, sit above the cassette deck. Tinted hot at zero
// energy to lukewarm at peak; scaleX on each frame to "dance" with the beat envelope.
const _bbVuL = _bbSprite(_BB_VU_TEX, { color: 0x39ff7a });
_bbVuL.position.set(-7, 8, 1);
_boomboxGroup.add(_bbVuL);
const _bbVuR = _bbSprite(_BB_VU_TEX, { color: 0x39ff7a });
_bbVuR.position.set(7, 8, 1);
_boomboxGroup.add(_bbVuR);

// LEDs — four small dots along the top edge. Light up one per quarter note so
// players see musical phrasing even peripherally.
const _bbLeds = [];
const _BB_LED_COLORS = [0xff3b6b, 0xffd66b, 0x39ff7a, 0x4ad9ff];
for (let i = 0; i < 4; i++) {
  const led = _bbSprite(_BB_LED_TEX, { color: _BB_LED_COLORS[i] });
  led.position.set(-9 + i * 6, 12, 1);
  _boomboxGroup.add(led);
  _bbLeds.push(led);
}

function _applyBoomboxScale() {
  const m = _boomboxSizeMult;
  _bbBody.scale.set(_BB_BODY_W * m, _BB_BODY_H * m, 1);
  // Speakers — base scale, modulated by beat in update.
  const sp = _BB_SPK_R * 2 * m;
  _bbSpkL.scale.set(sp, sp, 1);
  _bbSpkR.scale.set(sp, sp, 1);
  _bbCassette.scale.set(_BB_CASS_W * m, _BB_CASS_H * m, 1);
  const rr = _BB_REEL_R * 2 * m;
  _bbReelL.scale.set(rr, rr, 1);
  _bbReelR.scale.set(rr, rr, 1);
  _bbVuL.scale.set(_BB_VU_W * m, _BB_VU_H * m, 1);
  _bbVuR.scale.set(_BB_VU_W * m, _BB_VU_H * m, 1);
  const lr = _BB_LED_R * 2 * m;
  for (const led of _bbLeds) led.scale.set(lr, lr, 1);
  // Stash unmodulated speaker / VU base scales so the beat update can multiply them.
  _bbSpkL.userData.baseScale = sp;
  _bbSpkR.userData.baseScale = sp;
  _bbVuL.userData.baseScaleX = _BB_VU_W * m;
  _bbVuR.userData.baseScaleX = _BB_VU_W * m;
  _bbBody.userData.baseY = 0;
}
_applyBoomboxScale();

function _updateBoombox() {
  if (!_skyFx.boomboxOn) return;
  // Camera-align the group so child offsets read as screen-relative.
  _boomboxGroup.quaternion.copy(camera.quaternion);

  // Beat info — falls back to wall-clock when no music so the boombox still animates
  // on the home screen / level-select if it's ever visible there.
  let beatPhase, beatIdx, elapsed;
  if (_musicStarted && audioCtx) {
    elapsed = audioCtx.currentTime - _musicLoopStartTime;
    beatPhase = (elapsed % MUSIC_BEAT) / MUSIC_BEAT;
    beatIdx = Math.floor(elapsed / MUSIC_BEAT);
  } else {
    elapsed = performance.now() / 1000;
    const fakeBeat = 0.5;
    beatPhase = (elapsed % fakeBeat) / fakeBeat;
    beatIdx = Math.floor(elapsed / fakeBeat);
  }
  const pulse = Math.max(0, 1 - beatPhase * 3.3);
  const pulseSq = pulse * pulse;

  // Speakers — punch outward by 25% on the beat, decay between.
  const spkBoost = 1 + pulseSq * 0.25;
  _bbSpkL.scale.set(_bbSpkL.userData.baseScale * spkBoost, _bbSpkL.userData.baseScale * spkBoost, 1);
  _bbSpkR.scale.set(_bbSpkR.userData.baseScale * spkBoost, _bbSpkR.userData.baseScale * spkBoost, 1);

  // Body — subtle vertical bob, ±0.8m at full size.
  _bbBody.position.y = Math.sin(beatPhase * Math.PI) * 0.8 * _boomboxSizeMult;

  // Reels — continuous rotation. Rate keyed to BPM so faster songs spin faster.
  const beatPerSec = 1 / (_musicStarted && audioCtx ? MUSIC_BEAT : 0.5);
  const reelRot = elapsed * beatPerSec * Math.PI * 0.5;
  _bbReelL.material.rotation = reelRot;
  _bbReelR.material.rotation = -reelRot;

  // VU meter — width "dances" between 0.4× and 1.0× of base, with stereo offset
  // (right channel runs slightly behind so they don't lockstep).
  const vuL = 0.4 + pulseSq * 0.6;
  const vuR = 0.4 + Math.max(0, 1 - ((beatPhase + 0.15) % 1) * 3.3) ** 2 * 0.6;
  _bbVuL.scale.x = _bbVuL.userData.baseScaleX * vuL;
  _bbVuR.scale.x = _bbVuR.userData.baseScaleX * vuR;

  // LEDs — one lit per beat, cycling left→right around the bar (mod 4).
  const litIdx = beatIdx & 3;
  for (let i = 0; i < 4; i++) {
    _bbLeds[i].material.opacity = (i === litIdx) ? 1.0 : 0.35;
  }
}

// =========================================================
// Wall speakers — pixel-art cones mounted to the perimeter walls. Each stack is
// 3 cones, oriented to face inward. Built/torn down with the level bounds so every
// level gets a venue-appropriate rig: corners always, plus midpoints scaled to
// wall length. Cabinets / VU / lights deferred until the cone read feels right.
// =========================================================

// Cone texture — hand-painted pixel disc so the edges are crunchy, not smooth.
// `ctx.arc` always anti-aliases regardless of imageSmoothingEnabled, so we walk
// the bounding box and stamp 1×1 squares for every pixel inside the disc.
function _drawPixelDisc(ctx, cx, cy, r, color) {
  ctx.fillStyle = color;
  const r2 = r * r;
  for (let y = Math.floor(cy - r); y <= Math.ceil(cy + r); y++) {
    for (let x = Math.floor(cx - r); x <= Math.ceil(cx + r); x++) {
      const dx = x + 0.5 - cx, dy = y + 0.5 - cy;
      if (dx * dx + dy * dy <= r2) ctx.fillRect(x, y, 1, 1);
    }
  }
}
function _makeWallConeTexture() {
  // 32×32 base — chunky enough that NEAREST upscaling keeps pixels visible at 1.8m.
  const { c, ctx } = _bbCanvas(32, 32);
  _drawPixelDisc(ctx, 16, 16, 15.5, '#3a3f48'); // outer rim, charcoal
  _drawPixelDisc(ctx, 16, 16, 13,   '#7a8088'); // cone face, light grey
  _drawPixelDisc(ctx, 16, 16,  8,   '#4a4f58'); // inner ring, charcoal step
  _drawPixelDisc(ctx, 16, 16,  4,   '#9aa0a8'); // dust cap, light grey
  ctx.fillStyle = '#cfd6dc'; ctx.fillRect(13, 13, 1, 1);  // specular pixel
  return _bbTex(c);
}
const _SPK_CONE_TEX = _makeWallConeTexture();

const _speakerGroup = new THREE.Group();
_speakerGroup.visible = _skyFx.wallSpeakersOn;
scene.add(_speakerGroup);
const _speakers = [];        // flat list of { cone, baseScale } for the beat update
let _speakerSizeMult = 1.0;

const _SPK_CONE_W = 5.25;      // cone diameter at 1× size — single big speaker
// Wall thickness is 0.4m (BoxGeometry depth); inner face is 0.2m from wall centre.
// Cone sits at 0.21m so it hugs the inner face with a 0.01m bias against z-fight.
const _SPK_WALL_OFFSET = 0.21;

const _spkConeMat = new THREE.MeshBasicMaterial({
  map: _SPK_CONE_TEX, transparent: true, depthWrite: false,
});
const _SPK_CONE_GEO = new THREE.PlaneGeometry(1, 1);

// 8-bit VU meter — square panel matching the speaker footprint, 8 horizontal LED
// segments stacked bottom-to-top. Colours grade from green through yellow/orange
// to red, classic VU style. Lit segments fill from the bottom up to the current
// envelope; un-lit segments dim to ~12% of their colour for the "off LED" look.
const _METER_SEG_COLORS = [
  '#39ff7a', // green
  '#39ff7a',
  '#a3ff39', // green-yellow
  '#ffd66b', // yellow
  '#ffd66b',
  '#ff8a39', // orange
  '#ff5a3a', // red-orange
  '#ff3b6b', // hot pink-red (clip)
];
const _METER_SEGMENTS = _METER_SEG_COLORS.length;
const _METER_H = 5.25;            // panel height — matches the cone diameter
const _METER_ASPECT = 0.4;        // width / height — narrow VU strip
const _METER_FRAME_PCT = 0.08;    // outer frame fraction left for the panel border
const _METER_SEG_INSET = 0.92;    // segment width as fraction of inner panel width
const _METER_DIM_MUL   = 0.12;    // un-lit segment brightness multiplier

const _meters = [];               // { segments: [...], litColors: [...] }

function _makeMeterPanelTexture() {
  // Static dark panel with a chunky inner border. Hand-painted at 32px so the
  // bevel reads as a couple of crisp pixels at full size.
  const { c, ctx } = _bbCanvas(32, 32);
  ctx.fillStyle = '#0a0c10'; ctx.fillRect(0, 0, 32, 32);
  ctx.fillStyle = '#1f242c'; ctx.fillRect(2, 2, 28, 28);
  ctx.fillStyle = '#2c333d'; ctx.fillRect(2, 2, 28, 1);
  ctx.fillStyle = '#0a0c10'; ctx.fillRect(2, 29, 28, 1);
  ctx.fillStyle = '#15181e'; ctx.fillRect(3, 3, 26, 26);
  return _bbTex(c);
}
const _METER_PANEL_TEX = _makeMeterPanelTexture();
const _meterPanelMat = new THREE.MeshBasicMaterial({ map: _METER_PANEL_TEX });
const _METER_PANEL_GEO = new THREE.PlaneGeometry(1, 1);
const _METER_SEG_GEO = new THREE.PlaneGeometry(1, 1);

function _spawnSpeaker(x, z, fx, fz, wallHeight) {
  const yaw = Math.atan2(fx, fz);  // rotates plane normal from +Z to (fx, fz)
  const coneSize = _SPK_CONE_W * _speakerSizeMult;
  const px = x + fx * _SPK_WALL_OFFSET;
  const pz = z + fz * _SPK_WALL_OFFSET;
  const cone = new THREE.Mesh(_SPK_CONE_GEO, _spkConeMat.clone());
  cone.position.set(px, wallHeight / 2, pz);
  cone.rotation.y = yaw;
  cone.scale.set(coneSize, coneSize, 1);
  _speakerGroup.add(cone);
  _speakers.push({ cone, baseScale: coneSize });
}

function _spawnMeter(x, z, fx, fz, wallHeight) {
  const yaw = Math.atan2(fx, fz);
  const m = _speakerSizeMult;
  const H = _METER_H * m;
  const W = H * _METER_ASPECT;
  const px = x + fx * _SPK_WALL_OFFSET;
  const pz = z + fz * _SPK_WALL_OFFSET;
  const py = wallHeight / 2;

  // Background panel.
  const panel = new THREE.Mesh(_METER_PANEL_GEO, _meterPanelMat);
  panel.position.set(px, py, pz);
  panel.rotation.y = yaw;
  panel.scale.set(W, H, 1);
  _speakerGroup.add(panel);

  // Segments — 8 horizontal bars stacked vertically, slightly forward of the panel.
  // 85% segment height with the remaining 15% as inter-segment gap (panel shows through).
  const innerH = H * (1 - 2 * _METER_FRAME_PCT);
  const innerW = W * (1 - 2 * _METER_FRAME_PCT) * _METER_SEG_INSET;
  const segStride = innerH / _METER_SEGMENTS;
  const segH = segStride * 0.85;
  const fwdFx = fx * 0.01, fwdFz = fz * 0.01;
  const segments = [];
  const litColors = [];
  for (let i = 0; i < _METER_SEGMENTS; i++) {
    const litColor = new THREE.Color(_METER_SEG_COLORS[i]);
    const seg = new THREE.Mesh(_METER_SEG_GEO, new THREE.MeshBasicMaterial({ color: litColor }));
    const yOff = (i + 0.5 - _METER_SEGMENTS / 2) * segStride;
    seg.position.set(px + fwdFx, py + yOff, pz + fwdFz);
    seg.rotation.y = yaw;
    seg.scale.set(innerW, segH, 1);
    _speakerGroup.add(seg);
    segments.push(seg);
    litColors.push(litColor);
  }
  _meters.push({ segments, litColors });
}

function _clearSpeakers() {
  // Per-instance cloned materials need disposing; shared geo/textures stay alive.
  for (const sp of _speakers) sp.cone.material.dispose();
  for (const m of _meters) {
    for (const seg of m.segments) seg.material.dispose();
  }
  while (_speakerGroup.children.length) _speakerGroup.remove(_speakerGroup.children[0]);
  _speakers.length = 0;
  _meters.length = 0;
}

// Set by _buildBounds with the margin-adjusted wall positions (NOT the block AABB).
// Speakers attach to walls, so they must use this — _currentBoundsAABB would put
// them at the block area, floating in space marginSide metres off the wall.
let _currentWallBounds = null;

function _buildSpeakers() {
  _clearSpeakers();
  if (!_currentWallBounds) return;
  const a = _currentWallBounds;
  const walls = [
    { sx: a.minX, sz: a.maxZ, ex: a.maxX, ez: a.maxZ, fx:  0, fz: -1 }, // back  (+Z), faces -Z
    { sx: a.maxX, sz: a.minZ, ex: a.minX, ez: a.minZ, fx:  0, fz:  1 }, // front (-Z), faces +Z
    { sx: a.minX, sz: a.minZ, ex: a.minX, ez: a.maxZ, fx:  1, fz:  0 }, // left  (-X), faces +X
    { sx: a.maxX, sz: a.maxZ, ex: a.maxX, ez: a.minZ, fx: -1, fz:  0 }, // right (+X), faces -X
  ];
  // Opposite walls pair up by type so the room reads symmetrically:
  // back+front carry a single centred speaker each; left+right carry THREE meters
  // each, spread equally along the wall (at 25% / 50% / 75% of its length).
  // walls[] is [back, front, left, right].
  const meterTs = [0.25, 0.50, 0.75];
  for (let i = 0; i < walls.length; i++) {
    const w = walls[i];
    if (i < 2) {
      const px = (w.sx + w.ex) / 2;
      const pz = (w.sz + w.ez) / 2;
      _spawnSpeaker(px, pz, w.fx, w.fz, a.height);
    } else {
      for (const t of meterTs) {
        const px = w.sx + (w.ex - w.sx) * t;
        const pz = w.sz + (w.ez - w.sz) * t;
        _spawnMeter(px, pz, w.fx, w.fz, a.height);
      }
    }
  }
}

function _updateSpeakers() {
  if (!_skyFx.wallSpeakersOn) return;
  if (_speakers.length === 0 && _meters.length === 0) return;
  // Two pulses: speakers follow the song's bassDensity so they thump with the actual
  // bass hits; meters always follow the BPM (one pulse per quarter beat) so they read
  // as a consistent metronome regardless of how busy the bass line is. Both share the
  // same linear-decay envelope shape (pulse → 0 at 30% of the period, dormant after).
  let spkPulse, meterPulse;
  if (_musicStarted && audioCtx && _activeSong) {
    const elapsed = audioCtx.currentTime - _musicLoopStartTime;
    const density = _activeSong.bassDensity || 'normal';
    const periodBeats = density === 'sparse' ? 4
                      : density === 'medium' ? 2
                      : density === 'dense'  ? 0.5
                      : 1;
    const spkPeriodSec = periodBeats * MUSIC_BEAT;
    const spkPhase = (elapsed % spkPeriodSec) / spkPeriodSec;
    spkPulse = Math.max(0, 1 - spkPhase * 3.3);
    const meterPhase = (elapsed % MUSIC_BEAT) / MUSIC_BEAT;
    meterPulse = Math.max(0, 1 - meterPhase * 3.3);
  } else {
    // No music — wall-clock fallback so fixtures don't freeze on home screen.
    const phase = (performance.now() / 500) % 1;
    spkPulse = Math.max(0, 1 - phase * 3.3);
    meterPulse = spkPulse;
  }
  // Speakers — cone scale punches outward 30% on each bass hit (squared envelope
  // for a sharper attack).
  const boost = 1 + spkPulse * spkPulse * 0.30;
  for (const sp of _speakers) {
    const s = sp.baseScale * boost;
    sp.cone.scale.set(s, s, 1);
  }
  // Meters — segments fill from the bottom up to the current envelope. Lit
  // segments show their full colour; un-lit segments dim toward black for the
  // "off LED" look. Linear envelope so the fill drop reads smoothly.
  const filled = Math.ceil(meterPulse * _METER_SEGMENTS);
  for (const meter of _meters) {
    for (let i = 0; i < _METER_SEGMENTS; i++) {
      const isLit = i < filled;
      meter.segments[i].material.color
        .copy(meter.litColors[i])
        .multiplyScalar(isLit ? 1.0 : _METER_DIM_MUL);
    }
  }
}

// =========================================================
// Wall trim — architectural skirting + cornice + beat-pulsing LED strip. Reads
// as "venue interior" without competing visually with the smashable blocks.
// Built/torn down with the bounds (so it follows the level's wall geometry).
// =========================================================

const _WALL_SKIRT_H        = 0.5;   // dark band at the floor
const _WALL_CORNICE_H      = 0.3;   // light band at the ceiling
const _WALL_LED_H          = 0.15;  // strip just below the cornice
const _WALL_TRIM_THICKNESS = 0.5;   // wall is 0.4m; trim sticks 0.05m proud each side
const _WALL_LED_DIM        = 0.30;  // LED dim baseline between bass hits

const _wallSkirtMat   = new THREE.MeshStandardMaterial({ color: 0x2a3038, roughness: 0.92, metalness: 0.05 });
const _wallCorniceMat = new THREE.MeshStandardMaterial({ color: 0x8a96a8, roughness: 0.85, metalness: 0.05 });
const _WALL_LED_BASE_COLOR = new THREE.Color(0x4ad9ff);
const _wallLedMat = new THREE.MeshBasicMaterial({ color: _WALL_LED_BASE_COLOR.clone() });

const _wallTrimGroup = new THREE.Group();
_wallTrimGroup.visible = _skyFx.wallTrimOn;
scene.add(_wallTrimGroup);
const _wallLeds = [];

function _clearWallTrim() {
  for (const child of _wallTrimGroup.children) child.geometry.dispose();
  while (_wallTrimGroup.children.length) _wallTrimGroup.remove(_wallTrimGroup.children[0]);
  _wallLeds.length = 0;
}

function _buildWallTrim() {
  _clearWallTrim();
  if (!_currentWallBounds) return;
  const a = _currentWallBounds;
  const h = a.height;
  const cx = (a.minX + a.maxX) / 2;
  const cz = (a.minZ + a.maxZ) / 2;
  const w = a.maxX - a.minX;
  const d = a.maxZ - a.minZ;
  // Each entry: along-wall length, world position centre, axis flag (true = wall
  // runs along Z so the box geometry needs a 90° yaw to align).
  const walls = [
    { len: w, x: cx,      z: a.maxZ, isZ: false }, // back
    { len: w, x: cx,      z: a.minZ, isZ: false }, // front
    { len: d, x: a.minX,  z: cz,     isZ: true  }, // left
    { len: d, x: a.maxX,  z: cz,     isZ: true  }, // right
  ];
  for (const wl of walls) {
    // Skirting at floor.
    const skirtGeo = new THREE.BoxGeometry(wl.len, _WALL_SKIRT_H, _WALL_TRIM_THICKNESS);
    const skirt = new THREE.Mesh(skirtGeo, _wallSkirtMat);
    skirt.position.set(wl.x, _WALL_SKIRT_H / 2, wl.z);
    if (wl.isZ) skirt.rotation.y = Math.PI / 2;
    skirt.receiveShadow = true;
    _wallTrimGroup.add(skirt);
    // Cornice at ceiling.
    const corniceGeo = new THREE.BoxGeometry(wl.len, _WALL_CORNICE_H, _WALL_TRIM_THICKNESS);
    const cornice = new THREE.Mesh(corniceGeo, _wallCorniceMat);
    cornice.position.set(wl.x, h - _WALL_CORNICE_H / 2, wl.z);
    if (wl.isZ) cornice.rotation.y = Math.PI / 2;
    cornice.receiveShadow = true;
    _wallTrimGroup.add(cornice);
    // LED strip just below the cornice. MeshBasic so it ignores lighting and
    // reads as a glowing band; colour modulated each frame from the bass envelope.
    const ledGeo = new THREE.BoxGeometry(wl.len, _WALL_LED_H, _WALL_TRIM_THICKNESS);
    const led = new THREE.Mesh(ledGeo, _wallLedMat);
    led.position.set(wl.x, h - _WALL_CORNICE_H - _WALL_LED_H / 2, wl.z);
    if (wl.isZ) led.rotation.y = Math.PI / 2;
    _wallTrimGroup.add(led);
    _wallLeds.push(led);
  }
}

function _updateWallLeds() {
  if (!_skyFx.wallTrimOn || _wallLeds.length === 0) return;
  // Synced to the song's BPM (one pulse per quarter-note beat). Previously tracked the
  // bass-density envelope, which made the trim flash at different rates depending on the
  // song's bass authoring — sparse songs left the room feeling sluggish, dense songs felt
  // strobe-y. BPM sync keeps the trim consistent and reads as a steady metronome cue.
  let pulse;
  if (_musicStarted && audioCtx) {
    const elapsed = audioCtx.currentTime - _musicLoopStartTime;
    const phase = (elapsed % MUSIC_BEAT) / MUSIC_BEAT;
    pulse = Math.max(0, 1 - phase * 3.3);
  } else {
    const phase = (performance.now() / 500) % 1;
    pulse = Math.max(0, 1 - phase * 3.3);
  }
  // Material is shared across all 4 strips — set once per frame.
  const brightness = _WALL_LED_DIM + pulse * (1 - _WALL_LED_DIM);
  _wallLedMat.color.copy(_WALL_LED_BASE_COLOR).multiplyScalar(brightness);
}

// Sky beat pulse — modulates atmospheric rayleigh on each quarter note.
// Uses the same beatPhase math as _updateBlockBeatPulse for consistency.
function _updateSkyBeatPulse() {
  if (!_skyFx.beatPulseOn) {
    sky.material.uniforms['rayleigh'].value = _SKY_BASELINE_RAYLEIGH;
    return;
  }
  if (!_musicStarted || !audioCtx) return;
  const elapsed = audioCtx.currentTime - _musicLoopStartTime;
  const beatPhase = (elapsed % MUSIC_BEAT) / MUSIC_BEAT;
  const pulse = Math.max(0, 1 - beatPhase * 3.3);
  sky.material.uniforms['rayleigh'].value = _SKY_BASELINE_RAYLEIGH * (1 + pulse * pulse * 0.08);
}

// =========================================================
// First-person fists — chunky boxy hand + knuckles + sleeve
// =========================================================

// Fresnel rim light for the fist skin. The top-down FPS camera otherwise sees a flat back-of-hand; a thin
// cool-white glow on the grazing edges (rounded box corners, knuckles, thumb) separates the fist from the
// floor and gives it form. Injected into the Lambert shader so it rides ON TOP of the existing diffuse +
// heat/rainbow emissive without disturbing them. Added BEFORE tonemapping so it passes through ACES and
// reads soft instead of clipping to white. Uniforms are stashed on material.userData so the rim can be
// warm-tinted with the streak later if we want it as part of the heat ramp.
const _FIST_RIM_COLOR = new THREE.Color(0xbfe6ff);  // cool blue-white at rest
function _applyFistRim(material) {
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uRimColor = { value: _FIST_RIM_COLOR.clone() };
    shader.uniforms.uRimPower = { value: 3.0 };      // higher = thinner rim hugging the edge; keeps it off the flat top face (which read grey when washed)
    shader.uniforms.uRimStrength = { value: 0.55 };  // overall rim brightness
    shader.fragmentShader = shader.fragmentShader
      .replace('void main() {', 'uniform vec3 uRimColor;\nuniform float uRimPower;\nuniform float uRimStrength;\nvoid main() {')
      .replace('#include <tonemapping_fragment>',
        '\tfloat _rim = pow(1.0 - clamp(dot(normalize(normal), normalize(vViewPosition)), 0.0, 1.0), uRimPower) * uRimStrength;\n\tgl_FragColor.rgb += uRimColor * _rim;\n#include <tonemapping_fragment>');
    material.userData.rimUniforms = shader.uniforms;
  };
}

// `opts.forAvatar` builds the SAME fist for the third-person co-op avatar, minus the first-person-only
// machinery: a short forearm instead of the 0.6-deep off-screen sleeve, and no rocket flame. The rim
// shader IS kept (the avatar wants that shading) and `opts.skinColor` tints the hand to match the
// avatar's face. The default (FP) path is untouched. Spring rings stay built-in (no _hideBuiltInSpringParts).
function createFist(theme = 'plain', side = 1, opts = {}) {
  const forAvatar = !!opts.forAvatar;
  // Skin tint: avatar fists take the gentleman's face colour; FP fists keep the originals. Knuckles +
  // thumb derive as darker shades so the form still reads.
  const handHex = opts.skinColor ?? 0xd9a07a;
  const knuckHex = opts.skinColor ? new THREE.Color(opts.skinColor).multiplyScalar(0.86).getHex() : 0xc08560;
  const thumbHex = opts.skinColor ? new THREE.Color(opts.skinColor).multiplyScalar(0.96).getHex() : 0xcf926c;
  const sleeveHex = opts.sleeveColor ?? 0x3a4150;   // avatar: the jacket colour; FP: the glove sleeve grey-blue
  // Avatar skin/sleeve use the SAME StandardMaterial response as the face/body, so an identical hex actually
  // matches (Lambert renders the same colour noticeably browner). FP fists keep their original Lambert look.
  const skinMat = (hex) => forAvatar
    ? new THREE.MeshStandardMaterial({ color: hex, emissive: 0x000000, roughness: 0.6, metalness: 0.1 })
    : new THREE.MeshLambertMaterial({ color: hex, emissive: 0x000000 });
  const group = new THREE.Group();
  const materials = [];
  const themeParts = [];

  const hand = new THREE.Mesh(
    new RoundedBoxGeometry(0.20, 0.16, 0.24, 4, 0.015),  // subtly softened corners on the main fist block
    skinMat(handHex)
  );
  _applyFistRim(hand.material);
  group.add(hand);
  materials.push(hand.material);

  const knuckles = [];
  for (let i = 0; i < 4; i++) {
    const k = new THREE.Mesh(
      new THREE.SphereGeometry(0.035, 6, 4),   // ORIGINAL size — only the position moves
      skinMat(knuckHex)
    );
    k.position.set(-0.072 + i * 0.048, 0.05, -0.13);  // original placement, raised a smidge (0.045 → 0.05)
    _applyFistRim(k.material);
    group.add(k);
    knuckles.push(k);
    materials.push(k.material);
  }
  // Thumb (original size) on the inner side; side = +1 left / -1 right mirrors it to the correct hand.
  const thumb = new THREE.Mesh(
    new THREE.CapsuleGeometry(0.032, 0.042, 6, 12),  // smaller / less prominent
    skinMat(thumbHex)
  );
  thumb.position.set(side * 0.09, 0.0, -0.055);   // pulled back in a bit
  thumb.rotation.set(Math.PI / 2 * 0.78, 0, side * -0.5);
  _applyFistRim(thumb.material);
  group.add(thumb);
  materials.push(thumb.material);
  group.userData.thumb = thumb;
  group.userData.skinMatCount = materials.length;  // hand + knuckles + thumb = the "skin" set (warm/glow/rainbow)
  // Hand spans z ∈ [-0.12, 0.12]. Wrist band sits at z ∈ [0.12, 0.16]; sleeve at [0.16, 0.30].
  // No depth overlap = no z-fighting between the three elements.
  const wristBand = new THREE.Mesh(
    new THREE.BoxGeometry(0.21, 0.17, 0.04),
    new THREE.MeshLambertMaterial({ color: 0xfff4d6, emissive: 0x000000 })
  );
  wristBand.position.z = 0.14;
  group.add(wristBand);
  materials.push(wristBand.material);

  // Forearm — long sleeve box wrapped in a pivot subgroup so it can tilt up at the back
  // toward where the shoulder would be (instead of pointing straight back like a robot arm).
  // Subgroup origin sits at the wrist end (z=0.16) so the rotation pivots from the wrist,
  // and the sleeve mesh inside is centered along its own depth so the box geometry's center
  // aligns with the midpoint of the visible forearm. Depth tuned so the back end extends
  // past the camera near-clip plane and clips off-screen — that's what sells "arm attached
  // to a body" instead of "floating fist with a stub behind it."
  const FOREARM_DEPTH = forAvatar ? 0.34 : 0.60;  // FP sleeve runs off the near-clip; the avatar wants a real forearm
  const forearmPivot = new THREE.Group();
  forearmPivot.position.z = 0.16;
  forearmPivot.rotation.x = 0.20;  // back of sleeve tilts up ~11.5° toward the shoulder
  const sleeve = new THREE.Mesh(
    new THREE.BoxGeometry(0.18, 0.14, FOREARM_DEPTH),
    skinMat(sleeveHex)
  );
  sleeve.position.z = FOREARM_DEPTH / 2;  // sits in front of pivot, extending back along +z
  forearmPivot.add(sleeve);

  if (theme === 'spring') {
    const springMat = new THREE.MeshLambertMaterial({ color: 0xd7f7ff, emissive: 0x000000 });
    // FP view: a long coil that runs off-screen behind the camera (22 rings @ 0.16 = ~3.4m of spring).
    // Avatar view: that whole tail is on-screen, so compress it to wrap just the visible forearm.
    const springRings = forAvatar ? 8 : 22;
    const springSpacing = forAvatar ? (FOREARM_DEPTH - 0.02) / (springRings - 1) : 0.16;
    for (let i = 0; i < springRings; i++) {
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(0.145, 0.018, 8, 24),
        springMat
      );
      ring.position.z = 0.02 + i * springSpacing;
      ring.rotation.x = 0.04;
      forearmPivot.add(ring);
      themeParts.push(ring);
    }
  } else if (theme === 'hook') {
    const rocketMat = new THREE.MeshStandardMaterial({
      color: 0xffd66b,
      emissive: 0x2b1d05,
      emissiveIntensity: 0,
      metalness: 0.72,
      roughness: 0.22,
      envMap: springMetalEnvMap,
      envMapIntensity: 1.10
    });
    const bandMat = new THREE.MeshStandardMaterial({
      color: 0xfff4d6,
      emissive: 0x21190b,
      emissiveIntensity: 0,
      metalness: 0.62,
      roughness: 0.22,
      envMap: springMetalEnvMap,
      envMapIntensity: 1.05
    });
    const noseMat = new THREE.MeshStandardMaterial({
      color: 0xff5a3a,
      emissive: 0x300905,
      emissiveIntensity: 0,
      metalness: 0.56,
      roughness: 0.24,
      envMap: springMetalEnvMap,
      envMapIntensity: 0.95
    });
    const finMat = new THREE.MeshStandardMaterial({
      color: 0x6f7782,
      emissive: 0x090c10,
      emissiveIntensity: 0,
      metalness: 0.82,
      roughness: 0.20,
      envMap: springMetalEnvMap,
      envMapIntensity: 1.20
    });
    const nozzleMat = new THREE.MeshStandardMaterial({
      color: 0x2b2f38,
      emissive: 0x05070a,
      emissiveIntensity: 0,
      metalness: 0.90,
      roughness: 0.18,
      envMap: springMetalEnvMap,
      envMapIntensity: 1.25
    });
    const strapMat = new THREE.MeshStandardMaterial({
      color: 0x27303a,
      emissive: 0x030406,
      emissiveIntensity: 0,
      metalness: 0.55,
      roughness: 0.28,
      envMap: springMetalEnvMap,
      envMapIntensity: 0.75
    });
    const rocketGroup = new THREE.Group();
    rocketGroup.position.set(0.00, 0.114, -0.018);
    rocketGroup.userData.basePosition = rocketGroup.position.clone();
    group.add(rocketGroup);
    themeParts.push(rocketGroup);

    const rocketStrap = new THREE.Mesh(
      new THREE.BoxGeometry(0.118, 0.018, 0.035),
      strapMat
    );
    rocketStrap.position.set(0.00, -0.033, 0.015);
    rocketGroup.add(rocketStrap);

    const rocketBody = new THREE.Mesh(
      new THREE.CylinderGeometry(0.034, 0.034, 0.20, 12),
      rocketMat
    );
    rocketBody.rotation.x = Math.PI / 2;
    rocketBody.position.set(0.00, 0.00, 0.035);
    rocketBody.userData.pulseEmissiveHex = 0xffb000;
    rocketGroup.add(rocketBody);
    themeParts.push(rocketBody);

    const rocketBand = new THREE.Mesh(
      new THREE.CylinderGeometry(0.0355, 0.0355, 0.024, 12),
      bandMat
    );
    rocketBand.rotation.x = Math.PI / 2;
    rocketBand.position.set(0.00, 0.00, -0.030);
    rocketGroup.add(rocketBand);

    const rocketNose = new THREE.Mesh(
      new THREE.ConeGeometry(0.036, 0.078, 12),
      noseMat
    );
    rocketNose.rotation.x = -Math.PI / 2;
    rocketNose.position.set(0.00, 0.00, -0.104);
    rocketNose.userData.pulseEmissiveHex = 0xff3a00;
    rocketGroup.add(rocketNose);
    themeParts.push(rocketNose);

    for (const side of [-1, 1]) {
      const fin = new THREE.Mesh(
        new THREE.ConeGeometry(0.036, 0.072, 3),
        finMat
      );
      fin.position.set(side * 0.039, -0.014, 0.112);
      fin.rotation.set(Math.PI / 2, 0, side * 0.55);
      fin.scale.set(0.62, 0.92, 1.05);
      fin.userData.pulseEmissiveHex = 0xffb000;
      rocketGroup.add(fin);
      themeParts.push(fin);
    }

    const nozzle = new THREE.Mesh(
      new THREE.CylinderGeometry(0.028, 0.032, 0.026, 12),
      nozzleMat
    );
    nozzle.rotation.x = Math.PI / 2;
    nozzle.position.set(0.00, 0.00, 0.148);
    rocketGroup.add(nozzle);

    const flameLayers = forAvatar ? [] : [
      { color: 0xe9ffff, emissive: 0xc8ffff, radius: 0.014, length: 0.115, opacity: 0.82, width: 0.70, stretch: 1.15, speed: 0.095 },
      { color: 0x7be8ff, emissive: 0x25bfff, radius: 0.026, length: 0.160, opacity: 0.70, width: 1.00, stretch: 1.45, speed: 0.070 },
      { color: 0x2f8dff, emissive: 0x0a61ff, radius: 0.038, length: 0.185, opacity: 0.34, width: 1.22, stretch: 1.70, speed: 0.052 }
    ];
    for (const layer of flameLayers) {
      const flameMat = new THREE.MeshLambertMaterial({
        color: layer.color,
        emissive: layer.emissive,
        emissiveIntensity: 0,
        transparent: true,
        opacity: 0,
        depthWrite: false
      });
      const flame = new THREE.Mesh(
        new THREE.ConeGeometry(layer.radius, layer.length, 8),
        flameMat
      );
      flame.rotation.x = -Math.PI / 2;
      flame.position.set(0.00, 0.00, 0.152);
      flame.visible = false;
      flame.userData.isRocketFlame = true;
      flame.userData.flameLayer = layer;
      rocketGroup.add(flame);
      themeParts.push(flame);
    }
  }

  group.add(forearmPivot);
  materials.push(sleeve.material);

  group.userData.knuckles = knuckles;
  group.userData.hand = hand;
  group.userData.materials = materials;
  group.userData.theme = theme;
  group.userData.themeParts = themeParts;
  group.userData.wristBand = wristBand;
  group.userData.sleeve = sleeve;
  group.userData.forearmPivot = forearmPivot;
  // Cache the original color of each material so the storm-max rainbow tint can lerp from
  // it each frame and snap back exactly when the envelope decays. Without this, the original
  // hue would be lost on first tint and never recover.
  group.userData.originalColors = materials.map(m => m.color.clone());
  group.userData.lastAimQuat = null; // cached aim rotation; persists during decay so fists don't snap
  return group;
}

// Build a co-op avatar fist for a given glove + hand side, reusing the REAL fist model (FP-only bits
// stripped via forAvatar). Passed into mpInit so net/mp.js can wear the peer's actual gloves.
function buildAvatarFist(glove, side, skinColor, sleeveColor) {
  return createFist(gloveTheme(glove), side, { forAvatar: true, skinColor, sleeveColor });
}

const FIST_REST_L = { x: -0.30, y: -0.30, z: -0.55, rx: 0, ry:  0.30 };
const FIST_REST_R = { x:  0.30, y: -0.30, z: -0.55, rx: 0, ry: -0.30 };

let leftFist = createFist(gloveTheme(gloveForFist(0)), 1);
let rightFist = createFist(gloveTheme(gloveForFist(1)), -1);
camera.add(leftFist);
camera.add(rightFist);

// Camera-anchored spring visual for the left arm. The decorative rings built into the
// fist mesh are hidden; these rings are drawn from a fixed body-side origin to the moving
// fist so the spring always appears to come from the player.
if (gloveForFist(0) === GLOVE_SPRING) {
  for (const p of leftFist.userData.themeParts || []) p.visible = false;
}
if (gloveForFist(1) === GLOVE_SPRING) {
  for (const p of rightFist.userData.themeParts || []) p.visible = false;
}
const leftSpringVisual = new THREE.Group();
const leftSpringMat = new THREE.MeshStandardMaterial({
  color: 0xd7f7ff,
  emissive: 0x244f55,
  emissiveIntensity: 0,
  metalness: 0.82,
  roughness: 0.18,
  envMap: springMetalEnvMap,
  envMapIntensity: 1.35
});
const leftSpringRings = [];
const rightSpringRings = [];
for (let i = 0; i < 24; i++) {
  const ring = new THREE.Mesh(
    new THREE.TorusGeometry(0.145, 0.018, 8, 24),
    leftSpringMat
  );
  leftSpringVisual.add(ring);
  leftSpringRings.push(ring);
}
const rightSpringVisual = new THREE.Group();
for (let i = 0; i < 24; i++) {
  const ring = new THREE.Mesh(
    new THREE.TorusGeometry(0.145, 0.018, 8, 24),
    leftSpringMat
  );
  rightSpringVisual.add(ring);
  rightSpringRings.push(ring);
}
const leftSpringCuff = new THREE.Mesh(
  new THREE.TorusGeometry(0.135, 0.026, 10, 28),
  new THREE.MeshStandardMaterial({
    color: 0xd7f7ff,
    emissive: 0x244f55,
    emissiveIntensity: 0,
    metalness: 0.82,
    roughness: 0.18,
    envMap: springMetalEnvMap,
    envMapIntensity: 1.35
  })
);
leftSpringVisual.add(leftSpringCuff);
const rightSpringCuff = new THREE.Mesh(
  new THREE.TorusGeometry(0.135, 0.026, 10, 28),
  leftSpringCuff.material
);
rightSpringVisual.add(rightSpringCuff);
camera.add(leftSpringVisual);
camera.add(rightSpringVisual);
const _leftSpringAnchor = new THREE.Vector3(-0.22, -0.37, -0.34);
const _rightSpringAnchor = new THREE.Vector3(0.22, -0.37, -0.34);
const LEFT_SPRING_WRIST_GAP = 0.08;
const LEFT_SPRING_RING_OFFSET_X = -0.008;
const LEFT_SPRING_RING_OFFSET_Y = 0.017;
const LEFT_SPRING_BACK_LIFT_Y = 0.026;
const LEFT_SPRING_BACK_PULL_Z = 0.095;
const LEFT_SPRING_CHARGE_LIFT_Y = 0.03;  // small lift of the coil while charging; paired with an extra back ring (below) that covers the arm
const LEFT_SPRING_WAVE_AMP = 0.20;      // wobble amplitude (punch + settle share this); dialed back a touch from 0.25
const LEFT_SPRING_WAVE_SPEED = 0.045;
const LEFT_SPRING_WAVE_SPACING = 0.38;  // wavelength along the coil; SMALLER = longer wave (slightly longer per Rich)
const LEFT_SPRING_IDLE_PULLBACK = 0.7;  // at rest (no target) retract the coil toward the body so the fist sits proud out front
const LEFT_SPRING_RINGOUT_BEATS = 0.5;  // how long the wobble keeps jiggling + decaying after the arm settles (in beats)
const _leftSpringFrameAnchor = new THREE.Vector3();
const _leftSpringHand = new THREE.Vector3();
const _leftSpringTip = new THREE.Vector3();
const _leftSpringDir = new THREE.Vector3();
const _leftSpringWaveSide = new THREE.Vector3();
const _leftSpringWaveUp = new THREE.Vector3();
const _leftSpringQuat = new THREE.Quaternion();
const _rightSpringFrameAnchor = new THREE.Vector3();
const _rightSpringHand = new THREE.Vector3();
const _rightSpringTip = new THREE.Vector3();
const _rightSpringDir = new THREE.Vector3();
const _rightSpringWaveSide = new THREE.Vector3();
const _rightSpringWaveUp = new THREE.Vector3();
const _rightSpringQuat = new THREE.Quaternion();
const _zAxis = new THREE.Vector3(0, 0, 1);

function _disposeFistMesh(fist) {
  if (!fist) return;
  fist.traverse((obj) => {
    if (obj.geometry) obj.geometry.dispose();
    if (obj.material) {
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      for (const mat of mats) mat.dispose();
    }
  });
}

function _hideBuiltInSpringParts(fist, glove) {
  if (glove !== GLOVE_SPRING) return;
  for (const part of fist.userData.themeParts || []) part.visible = false;
}

function _setRuntimeGloveLoadout(loadout) {
  const next = [
    normalizeGlove(loadout && loadout[0], GLOVE_BARE),
    normalizeGlove(loadout && loadout[1], GLOVE_BARE)
  ];
  _runtimeGloveLoadout = next;
  camera.remove(leftFist);
  camera.remove(rightFist);
  _disposeFistMesh(leftFist);
  _disposeFistMesh(rightFist);
  leftFist = createFist(gloveTheme(next[0]), 1);
  rightFist = createFist(gloveTheme(next[1]), -1);
  _hideBuiltInSpringParts(leftFist, next[0]);
  _hideBuiltInSpringParts(rightFist, next[1]);
  camera.add(leftFist);
  camera.add(rightFist);
  if (_mp && _mp.setLoadout) _mp.setLoadout(next);   // co-op: tell the peer so their avatar re-equips
}

let activeFist = 1;                                // tracks last-fired fist (drives the energy beam origin)
const fistAnimStart = [-Infinity, -Infinity];      // per-fist punch animation start time
const fistAnimSpeed = [1, 1];                      // per-fist animation speed multiplier (1.0 normal, 1.25 charged)
const nextPunchAllowedByFist = [0, 0];             // per-fist cooldown end time (in seconds)
const missRecoveryUntil = [0, 0];                  // per-fist visual recovery end time (ms)
const FIST_RECOVERY_TIME = 0.13;                   // after impact, time to return to rest
const CHARGED_SPEED_MULT = 1.25;                   // charged punches arrive 20% faster than uncharged
const LEFT_ARM_REACH_FALLBACK_Z = -0.65;           // visual-only reach when no locked target distance exists
const LEFT_ARM_REACH_MAX_Z = -10.5;                // cap so very long locks don't throw the fist out of the scene
const LEFT_ARM_TARGET_VISUAL_GAP = 2.25;           // stop the visible fist/spring clearly in front of the block face so impact pop is visible
let leftArmReachExtraZ = LEFT_ARM_REACH_FALLBACK_Z;
let leftArmReachHasTarget = false;
let rightArmReachExtraZ = LEFT_ARM_REACH_FALLBACK_Z;
let rightArmReachHasTarget = false;
let leftFistIdleBobY = 0;
let leftFistIdleBobZ = 0;
let leftFistChargeX = 0;
let leftFistChargeY = 0;
let leftFistChargeZ = 0;
let rightFistIdleBobY = 0;
let rightFistIdleBobZ = 0;
let rightFistChargeX = 0;
let rightFistChargeY = 0;
let rightFistChargeZ = 0;

function leftArmReturnTime() {
  // Visual-only spring return: tied to a 32nd-note so it snaps back musically after impact.
  // Clamped so very fast/slow songs don't make it unreadable or sluggish.
  return Math.max(0.055, Math.min(0.105, MUSIC_BEAT / 8));
}

function fistAnimDelta(elapsed) {
  // Three phases: pullback → thrust → recovery. Returns offsets from rest pose.
  const pullbackEnd = IMPACT_DELAY * 0.35;
  const impact = IMPACT_DELAY;
  const recoveryEnd = IMPACT_DELAY + FIST_RECOVERY_TIME;
  if (elapsed < 0 || elapsed > recoveryEnd) return { dy: 0, dz: 0, drx: 0 };

  // 1) Pullback — fist comes back toward face, tilts up (winding up)
  if (elapsed < pullbackEnd) {
    const t = elapsed / pullbackEnd;
    return { dy: t * 0.04, dz: t * 0.10, drx: t * 0.5 };
  }
  // 2) Thrust — accelerating forward to peak at impact frame
  if (elapsed < impact) {
    const t = (elapsed - pullbackEnd) / (impact - pullbackEnd);
    const eased = t * t * (3 - 2 * t); // smoothstep
    return {
      dy: 0.04 + (-0.02 - 0.04) * eased,
      dz: 0.10 + (-0.45 - 0.10) * eased,
      drx: 0.5 + (-0.4 - 0.5) * eased,
    };
  }
  // 3) Recovery — ease back to rest
  const t = (elapsed - impact) / (recoveryEnd - impact);
  const eased = 1 - Math.pow(1 - t, 2);
  return {
    dy: -0.02 + 0.02 * eased,
    dz: -0.45 + 0.45 * eased,
    drx: -0.4 + 0.4 * eased,
  };
}

function springArmReachDelta(elapsed, reachExtraZ) {
  const impact = IMPACT_DELAY;
  const recoveryEnd = IMPACT_DELAY + leftArmReturnTime();
  if (elapsed < 0 || elapsed > recoveryEnd) return 0;
  if (elapsed < impact) {
    // Snap the visual arm reach out almost immediately, then hold near full extension
    // until the actual impact moment. Distance still comes from leftArmReachExtraZ.
    const t = Math.min(1, elapsed / Math.max(impact * 0.18, 0.001));
    const eased = 1 - Math.pow(1 - t, 3);
    return reachExtraZ * eased;
  }
  const t = (elapsed - impact) / (recoveryEnd - impact);
  // Brief hold, then quick beat-aligned snap back.
  const holdT = 0.10;
  if (t < holdT) return reachExtraZ;
  const rt = (t - holdT) / (1 - holdT);
  return reachExtraZ * Math.pow(1 - rt, 1.35);
}

function leftArmReachDelta(elapsed) {
  return springArmReachDelta(elapsed, leftArmReachExtraZ);
}

function _punchUseT(elapsed) {
  const impact = IMPACT_DELAY;
  const recoveryEnd = IMPACT_DELAY + FIST_RECOVERY_TIME;
  if (elapsed < 0 || elapsed > recoveryEnd) return 0;
  if (elapsed < impact) {
    const t = elapsed / impact;
    return t * t * (3 - 2 * t);
  }
  const t = (elapsed - impact) / (recoveryEnd - impact);
  return Math.pow(1 - t, 1.4);
}

function updateFistThemes(leftElapsed, rightElapsed) {
  const hasTarget = !!(lockedTarget || nearTarget);
  const ready = hasTarget ? 1 : 0;

  function updateSpringTheme(opts) {
    const reach = springArmReachDelta(opts.elapsed, opts.reachExtraZ);
    const use = opts.reachExtraZ !== 0 ? Math.min(1, Math.abs(reach / opts.reachExtraZ)) : 0;
    // "Extended" ONLY during the actual punch thrust (use spikes while reach ramps). Rest AND charge-hold
    // both keep the coil pulled back so the fist stays visible (charge reads as a loaded spring). NOT gated
    // on opts.hasReachTarget — that latches true after the first targeted punch and used to keep the coil
    // extended + cuff popped at rest. Thrust itself is reach-driven, so the punch animation is unchanged.
    const fullSpring = use > 0.08;
    opts.frameAnchor.copy(opts.anchor);
    opts.frameAnchor.x += opts.chargeX;
    opts.frameAnchor.y += opts.idleBobY;
    opts.frameAnchor.y += opts.chargeY;
    opts.frameAnchor.z += opts.idleBobZ;
    opts.frameAnchor.z += opts.chargeZ;
    opts.hand.copy(opts.fist.position);
    opts.dir.copy(opts.hand).sub(opts.frameAnchor);
    const handLen = opts.dir.length();
    if (handLen > 0.001) opts.dir.multiplyScalar(1 / handLen);
    const wristGap = Math.min(LEFT_SPRING_WRIST_GAP, handLen * 0.35);
    opts.tip.copy(opts.hand).addScaledVector(opts.dir, -wristGap);
    opts.dir.copy(opts.tip).sub(opts.frameAnchor);
    const springLen = opts.dir.length();
    if (springLen > 0.001) {
      opts.dir.multiplyScalar(1 / springLen);
      opts.quat.setFromUnitVectors(_zAxis, opts.dir);
    }
    const visibleCount = Math.min(opts.rings.length, Math.max(6, Math.ceil(springLen / 0.34)));
    // While charging, add ONE extra ring at the back (body end) so the coil covers the arm instead of the
    // arm clipping through it. Shifting the index by one when it's added keeps every existing ring in the
    // same place (identical tori), so it just appears with no pop.
    const chargeBackRing = opts.charge > 0.01 ? 1 : 0;
    opts.waveSide.set(opts.dir.z, 0, -opts.dir.x);
    if (opts.waveSide.lengthSq() < 0.0001) opts.waveSide.set(1, 0, 0);
    opts.waveSide.normalize();
    opts.waveUp.crossVectors(opts.waveSide, opts.dir);
    if (opts.waveUp.lengthSq() < 0.0001) opts.waveUp.set(0, 1, 0);
    opts.waveUp.normalize();
    // Wobble envelope. During the punch (extension + return) the amplitude follows sin(use·π) so it's
    // strongest through the transitions. After the arm settles it doesn't cut out — the spring keeps
    // jiggling and DECAYS to rest over LEFT_SPRING_RINGOUT_BEATS, a damped "boing... settle" finish. The
    // travelling wave phase keeps advancing through the ring-out, so the amplitude shrinks while it moves.
    const springMoveEnd = IMPACT_DELAY + leftArmReturnTime();
    const ringoutTime = LEFT_SPRING_RINGOUT_BEATS * MUSIC_BEAT;
    let waveWindow = 0;
    if (opts.elapsed >= 0 && opts.elapsed <= springMoveEnd) {
      waveWindow = 0.35 + 0.65 * Math.sin(use * Math.PI);
    } else if (opts.elapsed > springMoveEnd && opts.elapsed <= springMoveEnd + ringoutTime) {
      // Continuous from the 0.35 base at settle (sin(use·π)→0 as use→0), squared decay to 0 = quick drop then ease.
      const d = (opts.elapsed - springMoveEnd) / ringoutTime;
      waveWindow = 0.35 * (1 - d) * (1 - d);
    }
    const waveAmp = LEFT_SPRING_WAVE_AMP * waveWindow;
    const wavePhase = performance.now() * LEFT_SPRING_WAVE_SPEED;
    for (let i = 0; i < opts.rings.length; i++) {
      const ring = opts.rings[i];
      const visible = i < visibleCount + chargeBackRing;
      ring.visible = visible;
      if (!visible) continue;
      const t = fullSpring
        ? (i + 0.35) / (visibleCount + 0.45 + opts.charge * 1.4)
        : (i + (chargeBackRing ? -0.8 : 0.2)) / 7 * LEFT_SPRING_IDLE_PULLBACK;  // at rest: cluster toward the body; charging: index shifted back so an extra ring lands behind the arm
      ring.position.lerpVectors(opts.frameAnchor, opts.tip, t);
      ring.position.x += opts.side * LEFT_SPRING_RING_OFFSET_X;
      ring.position.y += LEFT_SPRING_RING_OFFSET_Y + LEFT_SPRING_BACK_LIFT_Y * Math.pow(1 - t, 1.6) + opts.charge * LEFT_SPRING_CHARGE_LIFT_Y;
      ring.position.z += LEFT_SPRING_BACK_PULL_Z * Math.pow(1 - t, 1.4);
      if (waveAmp > 0.0001) {
        const endFade = Math.sin(Math.PI * Math.min(1, Math.max(0, t)));
        const wave = Math.sin(i * LEFT_SPRING_WAVE_SPACING - wavePhase) * waveAmp * endFade;
        const lift = Math.cos(i * LEFT_SPRING_WAVE_SPACING - wavePhase * 0.85) * waveAmp * 0.35 * endFade;
        ring.position.addScaledVector(opts.waveSide, wave);
        ring.position.addScaledVector(opts.waveUp, lift);
      }
      ring.quaternion.copy(opts.quat);
      ring.scale.setScalar(1 + use * 0.08 - opts.charge * 0.06);
      ring.material.emissive.setHex(0x244f55);
      ring.material.emissiveIntensity = ready * 0.06 + use * 0.20 + opts.charge * 0.32;
    }
    // Cuff fully removed (Rich): the tip cuff read as a stray ring out front both at rest and during a
    // punch/charge. The coil rings + hand carry the look without it. Kept positioned (harmless) but hidden.
    opts.cuff.visible = false;
    opts.cuff.position.copy(opts.tip);
    opts.cuff.position.x += opts.side * LEFT_SPRING_RING_OFFSET_X;
    opts.cuff.position.y += LEFT_SPRING_RING_OFFSET_Y;
    opts.cuff.quaternion.copy(opts.quat);
    opts.cuff.scale.setScalar(1 + use * 0.04);
    opts.cuff.material.emissive.setHex(0x244f55);
    opts.cuff.material.emissiveIntensity = opts.charge * 0.16 + use * 0.10;
    const sleeve = opts.fist.userData.sleeve;
    const wrist = opts.fist.userData.wristBand;
    const forearmPivot = opts.fist.userData.forearmPivot;
    if (sleeve && wrist && forearmPivot) {
      const retract = use;
      forearmPivot.rotation.x = 0;
      sleeve.scale.z = Math.max(0.04, 1 - retract * 0.96);
      sleeve.position.z = 0.30 * sleeve.scale.z;
      sleeve.visible = retract < 0.96;
      wrist.visible = retract < 0.9;
    }
  }

  function hideSpringTheme(fist, rings, cuff) {
    for (const ring of rings) ring.visible = false;
    cuff.visible = false;
    const sleeve = fist.userData.sleeve;
    const wrist = fist.userData.wristBand;
    const forearmPivot = fist.userData.forearmPivot;
    if (sleeve && wrist && forearmPivot) {
      forearmPivot.rotation.x = 0.20;
      sleeve.scale.z = 1;
      sleeve.position.z = 0.30;
      sleeve.visible = true;
      wrist.visible = true;
    }
  }

  if (gloveForFist(0) === GLOVE_SPRING) {
    updateSpringTheme({
      side: 1, fist: leftFist, elapsed: leftElapsed, charge: displayedCharge[0] || 0,
      reachExtraZ: leftArmReachExtraZ, hasReachTarget: leftArmReachHasTarget,
      anchor: _leftSpringAnchor, rings: leftSpringRings, cuff: leftSpringCuff,
      frameAnchor: _leftSpringFrameAnchor, hand: _leftSpringHand, tip: _leftSpringTip,
      dir: _leftSpringDir, waveSide: _leftSpringWaveSide, waveUp: _leftSpringWaveUp,
      quat: _leftSpringQuat, idleBobY: leftFistIdleBobY, idleBobZ: leftFistIdleBobZ,
      chargeX: leftFistChargeX, chargeY: leftFistChargeY, chargeZ: leftFistChargeZ
    });
  } else {
    hideSpringTheme(leftFist, leftSpringRings, leftSpringCuff);
  }
  if (gloveForFist(1) === GLOVE_SPRING) {
    updateSpringTheme({
      side: -1, fist: rightFist, elapsed: rightElapsed, charge: displayedCharge[1] || 0,
      reachExtraZ: rightArmReachExtraZ, hasReachTarget: rightArmReachHasTarget,
      anchor: _rightSpringAnchor, rings: rightSpringRings, cuff: rightSpringCuff,
      frameAnchor: _rightSpringFrameAnchor, hand: _rightSpringHand, tip: _rightSpringTip,
      dir: _rightSpringDir, waveSide: _rightSpringWaveSide, waveUp: _rightSpringWaveUp,
      quat: _rightSpringQuat, idleBobY: rightFistIdleBobY, idleBobZ: rightFistIdleBobZ,
      chargeX: rightFistChargeX, chargeY: rightFistChargeY, chargeZ: rightFistChargeZ
    });
  } else {
    hideSpringTheme(rightFist, rightSpringRings, rightSpringCuff);
  }

  function updateRocketTheme(fist, elapsed, charge) {
    const hookUse = _punchUseT(elapsed);
    const hookParts = fist.userData.themeParts || [];
    for (let i = 0; i < hookParts.length; i++) {
      const part = hookParts[i];
      const isFlame = part.userData.isRocketFlame;
      if (isFlame) {
        const layer = part.userData.flameLayer || {};
        const launchFlare = Math.max(0, Math.sin(hookUse * Math.PI));
        const flameUse = Math.max(0.28, hookUse, charge * 0.4) + launchFlare * 0.22;
        part.visible = true;
        const flicker = 0.88 + 0.18 * Math.sin(performance.now() * (layer.speed || 0.07));
        const width = layer.width || 1;
        const stretch = layer.stretch || 1.45;
        part.scale.set(
          (0.48 + flameUse * 0.30) * width,
          (0.50 + flameUse * stretch) * flicker,
          (0.48 + flameUse * 0.30) * width
        );
        part.material.opacity = Math.min(layer.opacity || 0.7, flameUse * (layer.opacity || 0.7));
        part.material.emissiveIntensity = flameUse * 1.8;
        continue;
      }
      if (part.isGroup) {
        const basePosition = part.userData.basePosition || part.position;
        const launchFlare = Math.max(0, Math.sin(hookUse * Math.PI));
        const idleHum = 0.0024 * Math.sin(performance.now() * 0.035) + 0.0010 * Math.sin(performance.now() * 0.063);
        const launchKick = launchFlare * (0.0045 * Math.sin(performance.now() * 0.18));
        const vibe = idleHum + launchKick;
        part.rotation.x = vibe * 1.6;
        part.rotation.z = vibe;
        part.position.copy(basePosition);
        part.position.y += vibe * 0.55;
        continue;
      }
      part.material.emissive.setHex(part.userData.pulseEmissiveHex || 0xffb000);
      part.material.emissiveIntensity = charge * 0.25 + hookUse * 0.35;
      part.scale.setScalar(1 + charge * 0.025 + hookUse * 0.04);
    }
  }
  if (gloveForFist(0) === GLOVE_ROCKET) updateRocketTheme(leftFist, leftElapsed, displayedCharge[0] || 0);
  if (gloveForFist(1) === GLOVE_ROCKET) updateRocketTheme(rightFist, rightElapsed, displayedCharge[1] || 0);
}

// aimFactor: 0..1, smooths to 1 when locked. Drives beam pulse during a committed punch.
let aimFactor = 0;
const AIM_SMOOTH_RATE = 14;

// Charge mechanic — hold to charge, release to punch. Full charge = 1-hit destroy + radius cascade.
// Left mouse drives left fist, right mouse drives right fist. State is per-fist, so both can
// charge / fire simultaneously — true one-twos and dual charged hooks are possible.
const charging = [false, false];
const chargeStartTime = [0, 0];
const chargeAmount = [0, 0];        // precise current charge per fist
const displayedCharge = [0, 0];     // smoothed for visuals (so glow doesn't snap on release)
const CHARGE_DURATION = 1.0;       // seconds to reach full charge
const CHARGE_THRESHOLD = 0.85;     // ≥ this on release = super punch
const CHARGE_DISPLAY_RATE = 12;    // smoothing rate for visual decay after release
const SUCK_GRACE = 0.3;            // charge level (≈300ms) before debris starts being sucked in — keeps quick clicks from triggering suction
const SUCK_ORBIT_RADIUS = 1.0;     // base radius chips orbit at while charging
const SUCK_ORBIT_SPEED = 5.0;      // rad/s — how fast the orbit rotates
const SUCK_ORBIT_FORWARD = 1.5;    // distance ahead of the fist (camera-forward) where the orbit center sits
const SUCK_ORBIT_SIDEWAYS = 1.0;   // how far each fist's orbit shifts to its own side. Kept equal to RADIUS so the L+R rings just kiss at the camera-forward midline (under the reticle), not overlap or gap.
const SUCK_GATHER_DURATION = 0.1;  // seconds chips collapse to fist center before launch fires
const SUCK_DISPERSE_DELAY = 0.2;   // seconds after launch before chip-chip collision re-enables — lets chips spread out so they don't all crash into each other in one frame
// Collision groups: orbiting chips don't collide with each other (otherwise they bunch into a mound).
// Bit 14 carved off as the "orbiting" group; filter excludes it. Default = collide with everything.
const ORBIT_COLLISION_GROUPS = 0x4000BFFF;
// Currency chips on the floor (bit 13). Filter = bit 0 + bit 9 so they collide with floor/walls/blocks
// but NOT with each other and NOT with armed chip-storm projectiles. Means coins can rest on
// top of structures, while chip-storms fly through coin piles unhindered.
const DEBRIS_GROUPS = 0x20000201;
// Aesthetic rubble chunks (bit 11). Filter = bit 0 too, BUT blocks filter out bit 11 (see
// addBlock) — so aesthetic chunks collide only with floor + walls, never with blocks or chips
// or armed projectiles. Big perf win: each break can spawn 4-11 chunks, and on a 95-block
// level that's ~1000 bodies that no longer have to collision-test against every standing block.
const AESTHETIC_CHUNK_GROUPS = 0x08000001;
// Block colliders: membership = bit 9 ONLY (0x0200) so the invisible upper wall extension can
// filter them out (tall toppling blocks pass through the roofless portion and get culled by
// _checkLevelEnd's escape margin). Filter still excludes bit 11 (aesthetic chunks).
const BLOCK_COLLISION_GROUPS = 0x0200F7FF;
// Armed chip-storm projectiles (bit 12): filter = bit 0 + bit 9 — they collide with
// blocks/floor/walls, never with debris or other armed chips. So when chip 1 breaks the front
// block, chip 2 flies straight through the cloud of broken-block aesthetic chunks behind it.
const ARMED_PROJECTILE_GROUPS = 0x10000201;
// Invisible upper wall extension: collides with everything EXCEPT bit 9 (blocks). Means high
// lunges and stray coins bounce off the roofless portion, while tall toppling blocks pass through.
const UPPER_WALL_GROUPS = 0xFFFFFDFF;
const pendingLaunchTime = [-Infinity, -Infinity];  // when (sec) the fist released and gather began
const pendingLaunchCharge = [0, 0];                // charge amount captured at release
const pendingLaunchTimingMult = [1, 1];            // legacy — kept so existing call paths compile
const pendingLaunchReleaseTier = ['good', 'good']; // release timing tier ('perfect' = trails on launch)
// Combined timing tier (click AND release) used for break-loot count scaling. miss/good/perfect.
const pendingLaunchComboTier = ['good', 'good'];
const pendingLaunchStreakBonus = [1, 1];           // streak bonus at release — applied per break-loot coin
const pendingLaunchSuppressCoins = [false, false]; // one-shot BACK ON BEAT recovery suppresses storm break-loot
// Charged-punch destroy radius scales with whether one or both fists are firing simultaneously.
const CHARGED_RADIUS_SOLO = 1.8;
const CHARGED_RADIUS_DUAL = 3.0;
const DUAL_CHARGE_WINDOW = 0.15;   // seconds — both charged punches within this window count as dual
const lastChargedPunchTime = [-Infinity, -Infinity];
// Charge color ramp: warm yellow → red-orange → white-hot
const _C_CHARGE_LOW  = new THREE.Color(0xffe055);
const _C_CHARGE_MID  = new THREE.Color(0xff5a3a);
const _C_CHARGE_HIGH = new THREE.Color(0xfff0d0);
const _aimHelper = new THREE.Object3D();
const _vTmp = new THREE.Vector3();
const _qTmp = new THREE.Quaternion();
const _qRest = new THREE.Quaternion();
const _qAim = new THREE.Quaternion();
const _qAnim = new THREE.Quaternion();
const _xAxis = new THREE.Vector3(1, 0, 0);
const _glowColor = new THREE.Color();

function poseFist(fist, rest, dy, dz, drx) {
  fist.position.set(rest.x, rest.y + dy, rest.z + dz);

  // Keep the rest orientation regardless of lock — only the punch animation tilts the fist.
  _qRest.setFromEuler(new THREE.Euler(rest.rx, rest.ry, 0));
  _qAnim.setFromAxisAngle(_xAxis, drx);
  fist.quaternion.copy(_qRest).multiply(_qAnim);
}

function updateCharge(dt) {
  const now = performance.now() / 1000;
  const smooth = 1 - Math.exp(-CHARGE_DISPLAY_RATE * dt);
  for (let i = 0; i < 2; i++) {
    if (charging[i]) {
      chargeAmount[i] = Math.min((now - chargeStartTime[i]) / CHARGE_DURATION, 1);
    }
    displayedCharge[i] += (chargeAmount[i] - displayedCharge[i]) * smooth;
  }
}

function updateFists(dt) {
  // Camera was moved/rotated this frame — refresh its world matrix so beam math sees current state.
  camera.updateMatrixWorld(true);

  // Smooth aim toward 1 when locked, 0 otherwise (frame-rate independent).
  const targetAim = lockedTarget ? 1 : 0;
  aimFactor += (targetAim - aimFactor) * (1 - Math.exp(-AIM_SMOOTH_RATE * dt));

  // Each fist animates independently. Animation time is scaled by fistAnimSpeed so charged
  // punches play their full curve in less wall-clock time (impact lands sooner).
  const now = performance.now() / 1000;
  const animMax = IMPACT_DELAY + FIST_RECOVERY_TIME;
  const leftAnimMax = IMPACT_DELAY + leftArmReturnTime();
  const leftElapsed  = (now - fistAnimStart[0]) * fistAnimSpeed[0];
  const rightElapsed = (now - fistAnimStart[1]) * fistAnimSpeed[1];
  const leftDelta  = (leftElapsed  >= 0 && leftElapsed  <= leftAnimMax) ? fistAnimDelta(leftElapsed)  : { dy: 0, dz: 0, drx: 0 };
  const rightDelta = (rightElapsed >= 0 && rightElapsed <= animMax) ? fistAnimDelta(rightElapsed) : { dy: 0, dz: 0, drx: 0 };
  if (gloveForFist(0) === GLOVE_SPRING) {
    leftDelta.dz += leftArmReachDelta(leftElapsed);
    leftDelta.drx *= 0.15; // spring jab should translate, not whip the whole coil around
  }
  if (gloveForFist(1) === GLOVE_SPRING) {
    rightDelta.dz += springArmReachDelta(rightElapsed, rightArmReachExtraZ);
    rightDelta.drx *= 0.15; // spring jab should translate, not whip the whole coil around
  }
  poseFist(leftFist,  FIST_REST_L, leftDelta.dy,  leftDelta.dz,  leftDelta.drx);
  poseFist(rightFist, FIST_REST_R, rightDelta.dy, rightDelta.dz, rightDelta.drx);

  leftFistChargeX = 0;
  leftFistChargeY = 0;
  leftFistChargeZ = 0;
  rightFistChargeX = 0;
  rightFistChargeY = 0;
  rightFistChargeZ = 0;

  // Charge wind-up — both fists pull back, rise, and swing outward as charge builds.
  if (displayedCharge[0] > 0.001) {
    const c = displayedCharge[0];
    leftFistChargeX = -c * 0.05;   // dialled back (was 0.10) so both loaded coils don't crowd the corners
    leftFistChargeY = c * 0.06;    // dialled back (was 0.12) so they don't climb up into the frame
    leftFistChargeZ = c * 0.04;    // dialled back (was 0.08) so the coil doesn't balloon near the camera
    leftFist.position.x += leftFistChargeX; // out to the left
    leftFist.position.y += leftFistChargeY; // up
    leftFist.position.z += leftFistChargeZ; // back toward camera
  }
  if (displayedCharge[1] > 0.001) {
    const c = displayedCharge[1];
    rightFistChargeX = c * 0.05;   // dialled back (was 0.10) so both loaded coils don't crowd the corners
    rightFistChargeY = c * 0.06;   // dialled back (was 0.12) so they don't climb up into the frame
    rightFistChargeZ = c * 0.04;   // dialled back (was 0.08) so the coil doesn't balloon near the camera
    rightFist.position.x += rightFistChargeX; // out to the right
    rightFist.position.y += rightFistChargeY; // up
    rightFist.position.z += rightFistChargeZ; // back toward camera
  }

  // Subtle hover — each fist breathes independently when at rest. The Y (up-down)
  // component locks to the music's quarter-note pulse so the fists feel like they're
  // drumming with the beat; phase-shifted so each fist's TROUGH (the "strike" low
  // point) lands ON a beat. L troughs at beat 0 / 2 / ..., R offset by one beat so
  // it troughs at beat 1 / 3 / ..., together alternating strikes every beat for a
  // drumming cadence. Curve shape uses a power > 1 so the fists HOLD briefly at the
  // peak (anticipation) and trough (strike-landed), reading as physical weight
  // rather than a pure metronome sine. Z (forward-back) stays wall-clock so the
  // pair doesn't read as a single rigid unit.
  let beatPhaseY = 0;
  if (_musicStarted && audioCtx) {
    const elapsed = audioCtx.currentTime - _musicLoopStartTime;
    const period = MUSIC_BEAT * 2;  // one full L bob cycle per 2 beats
    if (elapsed >= 0) beatPhaseY = (elapsed % period) / period;
  }
  const tHover = performance.now() / 1000;
  const HOVER_FREQ_Z = 0.7;
  const FIST_BOB_AMP = 0.012;
  const phaseRadY = beatPhaseY * 2 * Math.PI;
  const shape = (c) => Math.sign(c) * Math.pow(Math.abs(c), 1.5);
  leftFistIdleBobY = shape(-Math.cos(phaseRadY)) * FIST_BOB_AMP;
  leftFistIdleBobZ = Math.cos(tHover * HOVER_FREQ_Z * 2 * Math.PI) * 0.005;
  rightFistIdleBobY = shape(-Math.cos(phaseRadY + Math.PI)) * FIST_BOB_AMP;
  rightFistIdleBobZ = Math.cos(tHover * HOVER_FREQ_Z * 2 * Math.PI + Math.PI / 3) * 0.005;
  leftFist.position.y  += leftFistIdleBobY;
  rightFist.position.y += rightFistIdleBobY;
  leftFist.position.z  += leftFistIdleBobZ;
  rightFist.position.z += rightFistIdleBobZ;
  // Drive the peer avatar's idle bob from OUR beat phase (normalized -1..1). The songs are synced and the
  // BPM is fixed, so this lands the avatar's bob exactly in step with our own fists. Avatar picks its amp.
  if (_mp && _mp.setIdleBob) _mp.setIdleBob(leftFistIdleBobY / FIST_BOB_AMP, rightFistIdleBobY / FIST_BOB_AMP);

  // Miss recovery: the offending hand springs up, outward, and back toward the camera.
  // This is presentation only; the block body is never nudged or activated by a miss.
  for (let i = 0; i < 2; i++) {
    const remaining = missRecoveryUntil[i] - performance.now();
    if (remaining <= 0) continue;
    const t = Math.min(1, remaining / MISS_RECOVERY_MS);
    const eased = Math.sin(t * Math.PI * 0.5);
    const fist = i === 0 ? leftFist : rightFist;
    if (i === 0) {
      // Left spring miss: compress/snap back along the spring line instead of kicking
      // the hand off-axis. Keeps the existing miss lockout + red tint intact.
      fist.position.x = FIST_REST_L.x - 0.03 * eased;
      fist.position.y = FIST_REST_L.y - 0.03 * eased;
      fist.position.z = FIST_REST_L.z + 0.16 * eased;
      _qRest.setFromEuler(new THREE.Euler(FIST_REST_L.rx, FIST_REST_L.ry, 0));
      fist.quaternion.copy(_qRest);
      continue;
    }
    const side = i === 0 ? -1 : 1;
    fist.position.x += side * 0.18 * eased;
    fist.position.y += 0.24 * eased;
    fist.position.z += 0.18 * eased;
    _qAnim.setFromEuler(new THREE.Euler(-0.65 * eased, side * 0.25 * eased, side * 0.35 * eased));
    fist.quaternion.multiply(_qAnim);
  }

  updateFistThemes(leftElapsed, rightElapsed);

  // Refresh fist matrices after we just changed their position/quaternion.
  leftFist.updateMatrixWorld(true);
  rightFist.updateMatrixWorld(true);

  updateFistGlow();
  // Note: updateEnergyBeam is NOT called here — it runs in the render block, after the camera's
  // wind-up offset/pitch are applied, so beam start points stay anchored to the fists on screen.
}

function updateFistGlow() {
  // Reset both fists' emissive, then apply per-fist based on each fist's own charge level.
  for (const fist of [leftFist, rightFist]) {
    for (const mat of fist.userData.materials) {
      mat.emissiveIntensity = 0;
    }
  }
  for (let i = 0; i < 2; i++) {
    const c = displayedCharge[i];
    if (c < 0.001) continue;

    // Yellow → red-orange → white-hot
    if (c < 0.5) _glowColor.copy(_C_CHARGE_LOW).lerp(_C_CHARGE_MID, c * 2);
    else         _glowColor.copy(_C_CHARGE_MID).lerp(_C_CHARGE_HIGH, (c - 0.5) * 2);

    const pulse = c >= 0.95 ? 0.85 + 0.15 * Math.sin(performance.now() * 0.025) : 1;
    const bodyIntensity = c * 0.7 * pulse;
    const knuckleIntensity = bodyIntensity * 1.6;

    const fist = i === 0 ? leftFist : rightFist;
    for (const mat of fist.userData.materials) {
      mat.emissive.copy(_glowColor);
      mat.emissiveIntensity = bodyIntensity;
    }
    for (const k of fist.userData.knuckles) {
      k.material.emissiveIntensity = knuckleIntensity;
    }
  }
}

// Energy beams — one per fist, thick lines with additive blending so they pop in the PBR scene.
function createBeam() {
  const geo = new LineSegmentsGeometry();
  geo.setPositions([0, 0, 0, 0, 0, 0]); // placeholder, updated each frame
  const mat = new LineMaterial({
    color: 0xff5a3a,
    linewidth: 4,
    transparent: true,
    opacity: 0,
    depthTest: true, // segments behind walls/blocks are clipped naturally
    // NormalBlending so the yellow↔red slow pulse renders as actual yellow / red instead
    // of washing to white over bright surfaces. Was AdditiveBlending until 2026-05-16.
  });
  mat.resolution.set(window.innerWidth, window.innerHeight);
  const line = new LineSegments2(geo, mat);
  line.renderOrder = 1002; // above the highlight glow shell
  scene.add(line);
  return { geo, mat };
}
const beamL = createBeam();
const beamR = createBeam();

const _beamFistPos = new THREE.Vector3();
const _beamCamForward = new THREE.Vector3();
// Scratch color for the per-frame yellow↔red beat tween — separate from the highlight's
// _C_HL_TMP so the two functions can run in the same frame without clobbering each other.
const _beamColorTmp = new THREE.Color();
// Beam tip mode. Default OFF preserves the original cellCenterOnBlock behaviour (tip snaps
// to the block-centre X/Z + 1m-cell Y, which sits inside the block volume). When ON, the tip
// terminates at the actual ray-OBB surface entry point — visually correct for huge blocks
// where the centre is deep inside, slides as you sweep aim across wide blocks. Admin toggle.
let _beamTipAtSurface = true;

// Contact ring — additive billboarded sprite at the beam-meet point on the locked target.
// Pulses with the same phase as the beams so the lock reads as "armed" right where the next
// smash will land. Admin-toggleable (default ON).
let _contactRingOn = true;
// Centre-screen beat ring. Declared up here (alongside the other admin toggles) because the
// visual-fx panel registers a checkbox that reads its initial value at module-load time —
// declaring it later was a TDZ trap that halted init.
let _beatRingOn = false;
// When ON, impactRing fires the new pixel-art surface-flat flash. When OFF, the original
// expanding yellow billboard ring fires. Default ON.
let _impactFlashV2 = true;
function _makeContactRingTexture() {
  // Chunky pixel-art ring: small canvas, hard edges, NearestFilter on the texture so the
  // pixels render blocky in-world. Reads cleanly against any background and matches the
  // game's pixel-art aesthetic instead of looking like a soft modern glow.
  const size = 24;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d');
  const cx = (size - 1) / 2;
  const cy = (size - 1) / 2;
  const innerR = 7;
  const outerR = 10;
  const img = ctx.createImageData(size, size);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = x - cx;
      const dy = y - cy;
      const r = Math.sqrt(dx * dx + dy * dy);
      const i = (y * size + x) * 4;
      img.data[i] = 255;
      img.data[i + 1] = 255;
      img.data[i + 2] = 255;
      img.data[i + 3] = (r >= innerR && r <= outerR) ? 255 : 0;
    }
  }
  ctx.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(canvas);
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}
const _contactRingMat = new THREE.MeshBasicMaterial({
  map: _makeContactRingTexture(),
  color: 0xff5a3a,
  transparent: true,
  opacity: 0,
  // NormalBlending so the slow yellow↔red pulse renders as actual colour rather than
  // washing to white over bright surfaces. Was AdditiveBlending until 2026-05-16.
  depthTest: true,
  depthWrite: false,
  side: THREE.DoubleSide,
});
const _contactRing = new THREE.Mesh(new THREE.PlaneGeometry(0.4, 0.4), _contactRingMat);
_contactRing.renderOrder = 1003;
scene.add(_contactRing);

// Pick the face of the block the world hit-point lies on, returning the outward normal in
// world space. The face is whichever local axis has the largest normalized coordinate.
const _faceLocalHit = new THREE.Vector3();
const _faceLocalNormal = new THREE.Vector3();
function _faceNormalAt(block, worldHitPoint) {
  _faceLocalHit.copy(worldHitPoint);
  block.worldToLocal(_faceLocalHit);
  const p = block.geometry.parameters;
  const ax = Math.abs(_faceLocalHit.x) / (p.width / 2);
  const ay = Math.abs(_faceLocalHit.y) / (p.height / 2);
  const az = Math.abs(_faceLocalHit.z) / (p.depth / 2);
  if (ax >= ay && ax >= az) _faceLocalNormal.set(Math.sign(_faceLocalHit.x) || 1, 0, 0);
  else if (ay >= az)        _faceLocalNormal.set(0, Math.sign(_faceLocalHit.y) || 1, 0);
  else                      _faceLocalNormal.set(0, 0, Math.sign(_faceLocalHit.z) || 1);
  return _faceLocalNormal.transformDirection(block.matrixWorld);
}
const _contactRingLookTarget = new THREE.Vector3();
function updateEnergyBeam() {
  // Beams + block highlight stay visible during charge whenever there's a target, so the
  // player can read the beat pulse on the block AND see chips converging there. The CENTER
  // reticle, however, only shows in SPRAY mode — when there's no target during a charge.
  // With a target locked, beams converge to that target's aim point; adding a center reticle
  // creates "am I firing forward or at the block?" confusion. Reticle is purely the "you'll
  // spray camera-forward when you release" cue now.
  const charging0 = charging[0] && chargeAmount[0] > SUCK_GRACE;
  const charging1 = charging[1] && chargeAmount[1] > SUCK_GRACE;
  const target = lockedTarget || nearTarget;
  // Gated on controls.isLocked so the reticle only shows during actual gameplay — without this
  // the precision branch below kept it visible over menus / the How to Play modal whenever Aim
  // Assist was off (the render loop runs this every frame regardless of screen).
  // With Aim Assist OFF (precision), the player aims everything by hand and only the block
  // directly under the crosshair targets — so a persistent reticle is needed to aim with.
  // With Aim Assist ON, keep the original behaviour: the reticle is purely the "you'll spray
  // camera-forward when you release" charge cue (shown only while charging without a target).
  const reticleVisible = controls.isLocked && (!_aimAssist || (!target && (charging0 || charging1)));
  _reticle.style.opacity = reticleVisible ? '0.5' : '0';

  // Compute the shared yellow↔red tween up here (before the no-target early-return) so
  // the reticle can read the same colour as the beams + block outline. Slow cos⁴ pulse
  // keeps reticle / beams / outline all on the same downbeat cadence. Storm-max neon
  // rainbow override applies the same way it always has.
  const beatPulse = _slowBeatPulse();
  _beamColorTmp.copy(_C_HL_YELLOW).lerp(_C_HL_RED, beatPulse);
  if (_stormMaxIntensity > 0) {
    const hue = ((performance.now() / 1000 * 90) % 360) / 360;
    _stormMaxTmpColor.setHSL(hue, 1, 0.6);
    _beamColorTmp.lerp(_stormMaxTmpColor, _stormMaxIntensity);
  }

  // Reticle is pure CSS, so paint its border + dot + glow by hand each frame whenever
  // it's visible. Skip the work entirely when hidden.
  if (reticleVisible) {
    const r = Math.round(_beamColorTmp.r * 255);
    const g = Math.round(_beamColorTmp.g * 255);
    const b = Math.round(_beamColorTmp.b * 255);
    const rgb = `rgb(${r},${g},${b})`;
    _reticle.style.borderColor = rgb;
    _reticle.style.boxShadow = `0 0 4px rgba(${r},${g},${b},0.6)`;
    _reticleDot.style.background = rgb;
  }

  if (!target) {
    beamL.mat.opacity = 0;
    beamR.mat.opacity = 0;
    _contactRingMat.opacity = 0;
    return;
  }
  // Beam opacity: matches the outline's pattern (0.75 + 0.25 * slow pulse). Was a
  // separate sin breathing under AdditiveBlending, but with NormalBlending the wide sin
  // swing (0.20→0.90) reads as on/off flicker. Tying opacity to the slow pulse here
  // keeps the beam consistently visible while letting the COLOR carry the rhythm.
  const pulse = 0.75 + 0.25 * beatPulse;
  const baseAlpha = lockedTarget ? aimFactor : 0.4;
  const opacity = pulse * baseAlpha;

  // Both fists converge on the cell the player is aiming at — for cubes this is the centre,
  // for cuboids it's the specific 1m cell the ray hits. Falls back to closest-point if not aimed at.
  camera.getWorldDirection(_beamCamForward);
  const aimHit = rayHitBlock(target, camera.position, _beamCamForward);
  // Surface mode: tip = actual ray-OBB entry point on the block face.
  // Default mode: tip = cell-centre (block-centre X/Z, 1m-cell Y), which sits inside the block.
  const tip = aimHit
    ? (_beamTipAtSurface ? aimHit : cellCenterOnBlock(target, aimHit))
    : closestPointOnBlock(target, camera.position);

  for (const [fist, beam] of [[leftFist, beamL], [rightFist, beamR]]) {
    fist.getWorldPosition(_beamFistPos);
    beam.geo.setPositions([
      _beamFistPos.x, _beamFistPos.y, _beamFistPos.z,
      tip.x, tip.y, tip.z,
    ]);
    beam.mat.opacity = opacity;
    beam.mat.color.copy(_beamColorTmp);
  }

  // Contact ring at the beam meet-point. Same opacity envelope as the beams so the lock
  // reads as one coherent visual. Lays flat on the block face (decal-style) by orienting the
  // plane to the face normal and offsetting a hair off the surface to avoid z-fighting.
  if (_contactRingOn && aimHit) {
    const normal = _faceNormalAt(target, aimHit);
    _contactRing.position.copy(aimHit).addScaledVector(normal, 0.012);
    _contactRingLookTarget.copy(_contactRing.position).add(normal);
    _contactRing.lookAt(_contactRingLookTarget);
    _contactRingMat.opacity = opacity;
    _contactRingMat.color.copy(_beamColorTmp);
  } else {
    _contactRingMat.opacity = 0;
  }
}

// Floor + perimeter walls are built per-level by the bounds system (see _buildBounds below).
// Materials are shared; their colors get retinted per-level by applyScene().
//
// Both maps are GRAYSCALE so applyScene's per-scene `color` still tints them (MeshStandard does map ×
// color). FLOOR = procedural BRUSHED METAL (bright base keeps the scene colour + brightness; directional
// grain = texture, no panels/seams) tiled per world-units. WALL = vertical gradient (darker at the floor
// → lighter up) so the slabs read with form instead of flat.
function _makeVGradientTex(bottomL, topL) {
  const c = document.createElement('canvas'); c.width = 4; c.height = 256;
  const x = c.getContext('2d');
  const g = x.createLinearGradient(0, 256, 0, 0);  // bottom → top
  g.addColorStop(0, `rgb(${bottomL},${bottomL},${bottomL})`);
  g.addColorStop(1, `rgb(${topL},${topL},${topL})`);
  x.fillStyle = g; x.fillRect(0, 0, 4, 256);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}
function _makeBrushedMetalTex() {
  const S = 512;
  const c = document.createElement('canvas'); c.width = c.height = S;
  const x = c.getContext('2d');
  x.fillStyle = '#e6e6e6'; x.fillRect(0, 0, S, S);             // mid base (bump mid-level)
  // Directional brushed grain — SHORTER, varied streaks (not full-width) so no line runs forever, which
  // is the ruler-drawn look we're avoiding. Wrapped in X so the tile still seams cleanly.
  const hstreak = (x0, y, len, style, lw) => {
    x.strokeStyle = style; x.lineWidth = lw;
    x.beginPath(); x.moveTo(x0, y); x.lineTo(x0 + len, y); x.stroke();
    if (x0 + len > S) { x.beginPath(); x.moveTo(x0 - S, y); x.lineTo(x0 + len - S, y); x.stroke(); }  // wrap
  };
  for (let i = 0; i < 1700; i++) {
    const y = Math.random() * S;
    const len = 60 + Math.random() * 300;
    const g = 190 + ((Math.random() * 40) | 0) - 20;   // 170..230
    const a = (0.05 + Math.random() * 0.07).toFixed(3);
    hstreak(Math.random() * S, y + 0.5, len, `rgba(${g},${g},${g},${a})`, Math.random() < 0.15 ? 2 : 1);
  }
  // A scatter of faint cross-scratches / scuffs (kept inland from the edges so they don't reveal tiling),
  // to break the purely-directional uniformity the way real worn metal does.
  for (let i = 0; i < 60; i++) {
    const cx = 24 + Math.random() * (S - 48), cy = 24 + Math.random() * (S - 48);
    const dx = (Math.random() - 0.5) * 26, dy = (Math.random() - 0.5) * 10;
    const lv = Math.random() < 0.5 ? 78 : 238;
    x.strokeStyle = `rgba(${lv},${lv},${lv},${(0.05 + Math.random() * 0.06).toFixed(3)})`;
    x.lineWidth = 1;
    x.beginPath(); x.moveTo(cx, cy); x.lineTo(cx + dx, cy + dy); x.stroke();
  }
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  return t;
}
const _FLOOR_TILE = 6;  // metres per texture tile (repeat set in _buildBounds from this)
// The grain drives ONLY a bumpMap (no albedo map), so the floor colour stays clean + flat and the brushed
// look comes purely from micro-relief catching the directional sun + sky reflection (glints/shifts as you
// move) — no painted streaks. Repeat is set on the texture itself in _buildBounds. bumpScale tunes depth.
const _floorGrainTex = _makeBrushedMetalTex();
const _floorMaterial = new THREE.MeshStandardMaterial({
  color: 0x6e7080, roughness: 0.63, metalness: 0.30,
  bumpMap: _floorGrainTex, bumpScale: 1.9,
});
const _wallMaterial  = new THREE.MeshStandardMaterial({ color: 0x4f5a70, roughness: 0.88, metalness: 0.05, map: _makeVGradientTex(196, 255) });

// =========================================================
// Scene presets — each level optionally declares a `scene` field
// (e.g. "outdoor-grass") that picks an atmospheric/floor preset.
// applyScene() is called at the start of buildFromSpec.
// =========================================================
const SCENE_PRESETS = {
  default: {
    turbidity: 6, rayleigh: 2, mieCoefficient: 0.005, mieDirectionalG: 0.8,
    sunElevation: 35, sunAzimuth: 140,
    sunColor: 0xfff4d6, sunIntensity: 2.6,
    hemiSky: 0xbcd4ff, hemiGround: 0x3a2a1e, hemiIntensity: 0.35,
    floor: 0x6e7080, wall: 0x4f5a70,
    skirt: 0x2a3038, cornice: 0x8a96a8, led: 0x4ad9ff,
  },
  'outdoor-grass': {
    turbidity: 3, rayleigh: 1.5, mieCoefficient: 0.005, mieDirectionalG: 0.8,
    sunElevation: 50, sunAzimuth: 140,
    sunColor: 0xfff4d6, sunIntensity: 3.2,
    hemiSky: 0xc8e6ff, hemiGround: 0x6e9a3a, hemiIntensity: 0.55,
    floor: 0x6dbb3c, wall: 0x4a8a28,
    skirt: 0x1f3a14, cornice: 0xd8e8a0, led: 0xa8ff48,
  },
  'outdoor-snow': {
    turbidity: 4, rayleigh: 2.5, mieCoefficient: 0.003, mieDirectionalG: 0.8,
    sunElevation: 30, sunAzimuth: 140,
    sunColor: 0xfffefa, sunIntensity: 2.8,
    hemiSky: 0xe2f0ff, hemiGround: 0x8aa2b8, hemiIntensity: 0.7,
    floor: 0xeaf2f9, wall: 0xa6bccc,
    skirt: 0x4a5a70, cornice: 0xf4faff, led: 0x80e8ff,
  },
  'outdoor-tropical': {
    turbidity: 8, rayleigh: 1.0, mieCoefficient: 0.005, mieDirectionalG: 0.85,
    sunElevation: 60, sunAzimuth: 100,
    sunColor: 0xfff0a0, sunIntensity: 3.4,
    hemiSky: 0xfff2c0, hemiGround: 0xb89060, hemiIntensity: 0.7,
    floor: 0xf2d68a, wall: 0xb8945a,
    skirt: 0x4a3018, cornice: 0xfff0c0, led: 0xffd040,
  },
  'outdoor-sunset': {
    turbidity: 8, rayleigh: 1.2, mieCoefficient: 0.008, mieDirectionalG: 0.85,
    sunElevation: 14, sunAzimuth: 250,
    sunColor: 0xffc070, sunIntensity: 3.0,
    hemiSky: 0xffb898, hemiGround: 0xa0623a, hemiIntensity: 0.65,
    floor: 0xc88450, wall: 0x946038,
    skirt: 0x401820, cornice: 0xffd0a0, led: 0xff5048,
  },
  'outdoor-night': {
    turbidity: 4, rayleigh: 0.5, mieCoefficient: 0.002, mieDirectionalG: 0.9,
    sunElevation: -10, sunAzimuth: 80,
    sunColor: 0x9eb6ff, sunIntensity: 0.6,
    hemiSky: 0x2a3550, hemiGround: 0x101830, hemiIntensity: 0.4,
    floor: 0x1a2030, wall: 0x0e1422,
    skirt: 0x05080f, cornice: 0x4a5878, led: 0xb060ff,
  },
  'outdoor-storm': {
    turbidity: 12, rayleigh: 0.8, mieCoefficient: 0.012, mieDirectionalG: 0.6,
    sunElevation: 30, sunAzimuth: 200,
    sunColor: 0xc0c8d0, sunIntensity: 2.4,
    hemiSky: 0x9aa2b0, hemiGround: 0x4a5060, hemiIntensity: 0.75,
    floor: 0x6a7280, wall: 0x4a5060,
    skirt: 0x252a32, cornice: 0xc8d0d8, led: 0xf8f048,
  },
  'outdoor-volcanic': {
    turbidity: 16, rayleigh: 0.5, mieCoefficient: 0.018, mieDirectionalG: 0.7,
    sunElevation: 14, sunAzimuth: 200,
    sunColor: 0xff6028, sunIntensity: 2.8,
    hemiSky: 0xb05030, hemiGround: 0x5a2818, hemiIntensity: 0.85,
    floor: 0x4a2818, wall: 0x32180c,
    skirt: 0x10080a, cornice: 0x988078, led: 0xff4818,
  },
  'outdoor-desert': {
    turbidity: 10, rayleigh: 0.8, mieCoefficient: 0.010, mieDirectionalG: 0.85,
    sunElevation: 70, sunAzimuth: 140,
    sunColor: 0xfff5b0, sunIntensity: 3.4,
    hemiSky: 0xf0d090, hemiGround: 0xb88858, hemiIntensity: 0.7,
    floor: 0xdcb478, wall: 0x9a7048,
    skirt: 0x4a3018, cornice: 0xfff0c0, led: 0xff8838,
  },
  'outdoor-cherry-blossom': {
    turbidity: 4, rayleigh: 1.2, mieCoefficient: 0.005, mieDirectionalG: 0.85,
    sunElevation: 45, sunAzimuth: 140,
    sunColor: 0xfff0e0, sunIntensity: 3.0,
    hemiSky: 0xffd0e0, hemiGround: 0xe8a8c8, hemiIntensity: 0.7,
    floor: 0xf8c8d8, wall: 0xc888a0,
    skirt: 0x5a2840, cornice: 0xffe8f0, led: 0xff70b8,
  },
  'outdoor-autumn': {
    turbidity: 6, rayleigh: 1.4, mieCoefficient: 0.008, mieDirectionalG: 0.85,
    sunElevation: 35, sunAzimuth: 200,
    sunColor: 0xffc080, sunIntensity: 3.0,
    hemiSky: 0xffb880, hemiGround: 0xa84028, hemiIntensity: 0.7,
    floor: 0xd4622a, wall: 0x9a4218,
    skirt: 0x401810, cornice: 0xffe098, led: 0xff8028,
  },
  'outdoor-aurora': {
    turbidity: 4, rayleigh: 1.5, mieCoefficient: 0.005, mieDirectionalG: 0.85,
    sunElevation: 25, sunAzimuth: 80,
    sunColor: 0xa0e8c8, sunIntensity: 2.8,
    hemiSky: 0x40d8b8, hemiGround: 0x9050c8, hemiIntensity: 0.85,
    floor: 0x5fa898, wall: 0x3a7868,
    skirt: 0x1a2840, cornice: 0xb8f0d8, led: 0xff48b8,
  },
  'outdoor-candy': {
    turbidity: 3, rayleigh: 1.2, mieCoefficient: 0.005, mieDirectionalG: 0.85,
    sunElevation: 60, sunAzimuth: 140,
    sunColor: 0xffe0f0, sunIntensity: 3.4,
    hemiSky: 0xff80c0, hemiGround: 0x40d0e0, hemiIntensity: 0.85,
    floor: 0x40d0e0, wall: 0xff80c0,
    skirt: 0x6020a0, cornice: 0xfff0f8, led: 0x40e8ff,
  },
  'outdoor-savanna': {
    turbidity: 8, rayleigh: 1.0, mieCoefficient: 0.008, mieDirectionalG: 0.85,
    sunElevation: 60, sunAzimuth: 140,
    sunColor: 0xfff0a0, sunIntensity: 3.4,
    hemiSky: 0xffe080, hemiGround: 0xb88a3a, hemiIntensity: 0.7,
    floor: 0xd8a850, wall: 0xa07830,
    skirt: 0x402818, cornice: 0xfff0a0, led: 0xffc028,
  },
};

// Very subtle distance fog — tinted per-scene to the sky/atmosphere colour (hemiSky) so the far end of an
// arena hazes gently into the horizon. EXPONENTIAL (FogExp2) so one density works across any arena size
// (a fixed near/far range only engaged in long corridors, not wide shallow rooms). Near play stays clear;
// the haze grows with distance. Raise FOG_DENSITY for a thicker haze, lower it toward ~0.004 for subtler.
const FOG_DENSITY = 0.006;
let _activeSceneName = 'default';
function applyScene(name) {
  _activeSceneName = (SCENE_PRESETS[name] ? name : 'default');
  const preset = SCENE_PRESETS[name] || SCENE_PRESETS.default;
  const u = sky.material.uniforms;
  u.turbidity.value = preset.turbidity;
  u.rayleigh.value = preset.rayleigh;
  u.mieCoefficient.value = preset.mieCoefficient;
  u.mieDirectionalG.value = preset.mieDirectionalG;
  _SKY_BASELINE_RAYLEIGH = preset.rayleigh;
  const phi = THREE.MathUtils.degToRad(90 - preset.sunElevation);
  const theta = THREE.MathUtils.degToRad(preset.sunAzimuth);
  sunDir.setFromSphericalCoords(1, phi, theta);
  u.sunPosition.value.copy(sunDir);
  sun.position.copy(sunDir).multiplyScalar(40);
  sun.color.setHex(preset.sunColor);
  sun.intensity = preset.sunIntensity;
  _hemiLight.color.setHex(preset.hemiSky);
  _hemiLight.groundColor.setHex(preset.hemiGround);
  _hemiLight.intensity = preset.hemiIntensity;
  // Subtle per-scene distance haze, tinted to the sky colour so the far end recedes into atmosphere.
  if (scene.fog && scene.fog.isFogExp2) { scene.fog.color.setHex(preset.hemiSky); scene.fog.density = FOG_DENSITY; }
  else scene.fog = new THREE.FogExp2(preset.hemiSky, FOG_DENSITY);
  _floorMaterial.color.setHex(preset.floor);
  _wallMaterial.color.setHex(preset.wall);
  // Trim colours follow the scene so skirt/cornice/LED harmonise with the wall.
  // The LED material is mutated each frame from _WALL_LED_BASE_COLOR; updating
  // the base here is enough for the new colour to show up on the next pulse.
  _wallSkirtMat.color.setHex(preset.skirt);
  _wallCorniceMat.color.setHex(preset.cornice);
  _WALL_LED_BASE_COLOR.setHex(preset.led);
  if (scene.environment) scene.environment.dispose();
  scene.environment = pmremGenerator.fromScene(sky, 0.04).texture;
}

// Physics world — debris piles, and Phase 4 will wire smashable blocks into this too.
const physicsWorld = new RAPIER.World({ x: 0, y: -30, z: 0 });
const collisionEvents = new RAPIER.EventQueue(true);
const PHYSICS_DT = 1 / 60;
let physicsAccum = 0;

// Rubble-click throttling
// Tuned 2026-05-02 for L10 audio: per-body cooldown raised so each chunk clicks at most ~5×/s,
// min velocity raised so settling/quiet bounces stay silent. These two together cut the source
// rubble-click rate from ~63/s (audio-thread underrun territory) toward ~20/s.
const RUBBLE_COOLDOWN_MS = 200;
const RUBBLE_MIN_VEL = 3.5;
const RUBBLE_MAX_PER_FRAME = 5;
const lastRubbleByBody = new Map();
// Body-handle → chip lookup so the collision handler can pick coin-clink vs rubble-click per chip.
const _chipByBodyHandle = new Map();
// Body-handle → block lookup so projectile chips can damage blocks they collide with.
const _blockByBodyHandle = new Map();
// Queue of {block, chip} impacts captured during drainCollisionEvents — processed AFTER drain
// so we don't mutate the physics world (setBodyType, removeRigidBody) while Rapier is iterating.
const _pendingProjectileImpacts = [];

// Snapshot every dynamic body's pre-step transform so renders between physics steps can interpolate.
function snapshotBodyTransforms() {
  for (const block of blocks) {
    if (!block.userData.dynamic) continue;
    const ud = block.userData;
    const t = ud.body.translation();
    const r = ud.body.rotation();
    if (!ud.prevPos) {
      ud.prevPos = new THREE.Vector3();
      ud.prevQuat = new THREE.Quaternion();
    }
    ud.prevPos.set(t.x, t.y, t.z);
    ud.prevQuat.set(r.x, r.y, r.z, r.w);
  }
  for (const chip of debris) {
    const ud = chip.userData;
    const t = ud.body.translation();
    const r = ud.body.rotation();
    if (!ud.prevPos) {
      ud.prevPos = new THREE.Vector3();
      ud.prevQuat = new THREE.Quaternion();
    }
    ud.prevPos.set(t.x, t.y, t.z);
    ud.prevQuat.set(r.x, r.y, r.z, r.w);
  }
}

function stepPhysics(dt) {
  physicsAccum += dt;
  let steps = 0;
  while (physicsAccum >= PHYSICS_DT && steps < 4) {
    snapshotBodyTransforms();
    physicsWorld.step(collisionEvents);
    physicsAccum -= PHYSICS_DT;
    steps++;
  }
  if (physicsAccum > PHYSICS_DT * 4) physicsAccum = 0;

  // Drain collision events into rubble clicks. Cooldown per body + per-frame cap keeps it cheap.
  let played = 0;
  const now = performance.now();
  collisionEvents.drainCollisionEvents((c1, c2, started) => {
    if (!started) return;
    _collisionEventsThisFrame++;

    const col1 = physicsWorld.getCollider(c1);
    const col2 = physicsWorld.getCollider(c2);
    if (!col1 || !col2) return;
    const b1 = col1.parent();
    const b2 = col2.parent();
    if (!b1 && !b2) return;

    const h1 = b1 ? b1.handle : -1;
    const h2 = b2 ? b2.handle : -1;
    const chip1 = _chipByBodyHandle.get(h1);
    const chip2 = _chipByBodyHandle.get(h2);

    // Chip-storm damage runs FIRST, before any sound-related gates — so a volley striking a stack
    // collects every armed chip even when the rubble-click rate-limit/cooldown skips audio.
    const nowSec = now / 1000;
    const armed1 = chip1 && chip1.userData.projectileUntil > nowSec ? chip1 : null;
    const armed2 = chip2 && chip2.userData.projectileUntil > nowSec ? chip2 : null;
    const armedChip = armed1 || armed2;
    if (armedChip) {
      const otherHandle = (armedChip === chip1) ? h2 : h1;
      const targetBlock = _blockByBodyHandle.get(otherHandle);
      if (targetBlock) {
        if (armedChip.userData.canDamageBlocks !== false) {
          _projectileHitsThisFrame++;
          // Defer the actual hit processing — applyProjectileHit mutates the physics world
          // (unanchorBlock setBodyType, breakBlock removeRigidBody) and Rapier panics if we
          // mutate during drainCollisionEvents. Queue it; we'll flush after drain returns.
          _pendingProjectileImpacts.push({ block: targetBlock, chip: armedChip });
          armedChip.userData.pendingCollect = true;
        } else {
          // Off-beat fired coins still physically hit/bounce off the target, but they do not
          // damage, break, shockwave, or vanish into the HUD. Disarm them and return their
          // collider to normal coin behavior so the failed release is visible in-world.
          if (armedChip.userData.collider) armedChip.userData.collider.setCollisionGroups(DEBRIS_GROUPS);
          armedChip.userData.pendingCollect = false;
        }
        armedChip.userData.projectileUntil = 0;  // spent
      }
    }

    // Sound-related gates (rate limit / cooldown / min velocity) only block AUDIO from here on.
    if (played >= RUBBLE_MAX_PER_FRAME) { _audioRubbleDropped++; return; }
    if (now - (lastRubbleByBody.get(h1) ?? 0) < RUBBLE_COOLDOWN_MS) return;
    if (now - (lastRubbleByBody.get(h2) ?? 0) < RUBBLE_COOLDOWN_MS) return;

    let speed = 0;
    if (b1 && b1.bodyType() === RAPIER.RigidBodyType.Dynamic) {
      const v = b1.linvel(); speed = Math.max(speed, Math.hypot(v.x, v.y, v.z));
    }
    if (b2 && b2.bodyType() === RAPIER.RigidBodyType.Dynamic) {
      const v = b2.linvel(); speed = Math.max(speed, Math.hypot(v.x, v.y, v.z));
    }
    if (speed < RUBBLE_MIN_VEL) return;

    const intensity = Math.min(speed / 9, 1);
    // If either body is a currency chip, play coin clink. Otherwise pick the click variant by the chip's source material.

    const involvesCoin = (chip1 && chip1.userData.isCurrency) || (chip2 && chip2.userData.isCurrency);
    const sourceChip = chip1 || chip2;
    const soundPos = sourceChip ? sourceChip.position : null;
    // Subtle quantization — snap collision sounds forward to the next 16th-note boundary so the
    // mass of bouncing chips and coins pulls onto the music's rhythm grid. Worst-case delay ~125ms
    // (one 16th); average ~60ms — at the edge of perception, but the aggregate feel is tighter.
    const snapped = _snapToNextSixteenth(audioCtx.currentTime);
    // Position is intentionally null for these high-rate sounds: each panner allocation also
    // wires a tap into the reverb convolver, and at L9/L10 cascade rates that reverb input
    // count overruns the audio thread (symptoms: bit-crushed crackle, then full audio dropout).
    // Smashes still go positional because they're already capped at 2/frame.
    if (involvesCoin) {
      const coinChip = (chip1 && chip1.userData.isCurrency) ? chip1 : chip2;
      playCoinClink(intensity, null, coinChip ? coinChip.userData.coinNote : null, snapped);
    } else {
      playRubbleClick(intensity, null, sourceChip ? sourceChip.userData.material : null, snapped);
    }
    if (h1 >= 0) lastRubbleByBody.set(h1, now);
    if (h2 >= 0) lastRubbleByBody.set(h2, now);
    played++;
  });

  // Flush deferred projectile impacts now that the collision event drain has returned.
  // Safe to mutate the physics world here (unanchor, breakBlock, removeRigidBody, etc.).
  if (_pendingProjectileImpacts.length > 0) {
    for (const { block, chip } of _pendingProjectileImpacts) {
      applyProjectileHit(block, chip);
    }
    _pendingProjectileImpacts.length = 0;
  }

  // Dislodged-block self-destruct scan (dev feel-test for the co-op "move = pop" rule).
  if (_dislodgePop) _scanDislodgedPops();
}

// Pop dislodged (dynamic) blocks once they come to rest. Master-gated by _dislodgePop in
// stepPhysics; _dislodgePopGroundOnly narrows it to blocks that settled ON the floor.
// A dynamic block is, by definition, one the player already knocked loose (blocks spawn fixed),
// so "settled dynamic block" == "dislodged block that has stopped moving".
const _DISLODGE_REST_LIN = 0.45;   // m/s — below this the block is treated as stopped
const _DISLODGE_REST_ANG = 0.7;    // rad/s — and not tumbling
const _DISLODGE_REST_HOLD_MS = 220; // must stay at rest this long before it pops
const _DISLODGE_GROUND_EPS = 0.15; // m — lowest corner this close to the floor counts as "hit the ground"
const _dislodgeQuat = new THREE.Quaternion();
const _dislodgeCorner = new THREE.Vector3();
const _dislodgePopDir = new THREE.Vector3(0, 1, 0);
function _blockLowestCornerY(block) {
  const p = block.geometry.parameters;
  const hx = p.width / 2, hy = p.height / 2, hz = p.depth / 2;
  const q = block.userData.body.rotation();
  const t = block.userData.body.translation();
  _dislodgeQuat.set(q.x, q.y, q.z, q.w);
  let minY = Infinity;
  for (let sx = -1; sx <= 1; sx += 2)
    for (let sy = -1; sy <= 1; sy += 2)
      for (let sz = -1; sz <= 1; sz += 2) {
        _dislodgeCorner.set(sx * hx, sy * hy, sz * hz).applyQuaternion(_dislodgeQuat);
        if (_dislodgeCorner.y < minY) minY = _dislodgeCorner.y;
      }
  return t.y + minY;
}
const _dislodgeVictims = [];
function _scanDislodgedPops() {
  const now = performance.now();
  _dislodgeVictims.length = 0;
  // Effective fall threshold: the gauntlet's giant towers want a bigger drop than campaign-scale
  // structures, but an explicit ?poph= always wins (so tuning stays possible anywhere).
  const minFall = (!_dislodgeMinFallExplicit && _currentLevel === 'gauntlet')
    ? _GAUNTLET_DISLODGE_FALL : _dislodgeMinFall;
  for (const block of blocks) {
    const ud = block.userData;
    if (!ud.dynamic || !ud.body) { ud.restSince = undefined; continue; }
    // Peer-owned blocks (any MP mode) are destroyed by authoritative 'd' events, never by our local
    // scan — otherwise the two clients would destroy different blocks and desync the kill set.
    if (ud.ownedRemote) { ud.restSince = undefined; continue; }

    if (_dislodgePopGroundOnly) {
      // Shatter only on a real DROP-AND-SLAM. Two gates the block must clear:
      //   1. Fall height: it must have descended at least _dislodgeMinFall metres from the highest
      //      point it reached while loose. This is the strict discriminator — a block jostled or
      //      shoved inside a collapsing pile never actually rises/falls far, so it is excluded no
      //      matter how its speed spikes. Base blocks already on the floor never qualify either.
      //   2. Impact speed: it must still be moving down fast at contact, so a block that drifted
      //      down gently from height does not pop. The contact this step can already have killed the
      //      downward velocity, so we use the fastest of this frame and last (captured pre-contact).
      const lowest = _blockLowestCornerY(block);
      if (ud.dislodgeApexY === undefined || lowest > ud.dislodgeApexY) ud.dislodgeApexY = lowest;
      const fellHeight = ud.dislodgeApexY - lowest;
      const v = ud.body.linvel();
      const fallSpeed = -Math.min(v.y, ud.lastDislodgeVy ?? 0);
      ud.lastDislodgeVy = v.y;
      if (lowest <= _DISLODGE_GROUND_EPS
          && fellHeight >= minFall
          && fallSpeed >= _dislodgeImpactMin) {
        _dislodgeVictims.push(block);
      }
      continue;
    }

    // Settle-anywhere (move = pop): wait until the block has actually come to rest anywhere.
    const v = ud.body.linvel();
    const w = ud.body.angvel();
    const atRest = Math.hypot(v.x, v.y, v.z) < _DISLODGE_REST_LIN
      && Math.hypot(w.x, w.y, w.z) < _DISLODGE_REST_ANG;
    if (!atRest) { ud.restSince = undefined; continue; }
    if (ud.restSince === undefined) { ud.restSince = now; continue; }
    if (now - ud.restSince < _DISLODGE_REST_HOLD_MS) continue;
    _dislodgeVictims.push(block);
  }
  // Pop after the scan so breakBlock's splice/cascade doesn't mutate the array mid-iteration.
  for (const block of _dislodgeVictims) _popDislodgedBlock(block);
}

// Destroy a dislodged block as a satisfying shatter that PAYS OUT. Ground-break removes the
// old "smash the fallen rubble for coins" loop, so without this the level's coin budget is
// starved and goals become unreachable. We drop the block's coins (physical, collectable, and
// auto-banked after their floor lifetime if not grabbed) plus a rubble + impact-FX burst, all
// thrown UP and out so the block bursts into the air instead of sinking where it landed.
function _popDislodgedBlock(block) {
  const pos = block.position.clone();
  const mat = block.userData.material;
  // Visual shatter + FX only. perCoinScore 0 makes spawnChunks skip its OWN coin drop, which
  // matters because that path clamps to +1 per block under Rhythm Assist (the default) and was
  // starving the level's coin budget. We pay the real value ourselves, below, mode-independent.
  spawnChunks(pos, _dislodgePopDir, mat, 0, false, 1, 1.3, block, 'good');
  breakBlock(block, _dislodgePopDir, 6, true);  // upwardBurst = true
  // Pay the block's full material value (crate 1 / brick 3 / metal 5) as physical, collectable
  // coins that burst upward. Auto-bank after their floor lifetime if not grabbed. This is the loot
  // the player used to earn smashing the fallen rubble by hand, restored for ground-break.
  const value = BLOCK_BASELINE_COINS[mat] || 1;
  _spawnDebrisBatch(pos, _dislodgePopDir, value, [0.3, 0.5], [4, 9], true, mat, 1);
  playCoinTinkle(value, pos);
  _popCoinAmountAtWorld(value, pos, _popColorForTier('good'));
}

// Click-start blip — short percussive tone that fires on every mousedown so the player can
// audibly gauge their timing the instant they press, even when not aimed at a target. Pitch
// encodes the timing tier (perfect = bright high, good = clear mid, miss = dull low).
function playClickStart(tier) {
  if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)({ latencyHint: 'interactive' });
  _ensureAudioGraph();
  const t0 = audioCtx.currentTime;
  const freq = tier === 'perfect' ? 1320 : tier === 'good' ? 660 : 180;
  const basePeak = tier === 'perfect' ? 0.16 : tier === 'good' ? 0.13 : 0.10;
  const peak = basePeak * _clickShape.level;
  const len = tier === 'miss' ? 0.10 : 0.06;

  const osc = audioCtx.createOscillator();
  osc.type = tier === 'miss' ? 'square' : 'triangle';
  osc.frequency.value = freq;
  const env = audioCtx.createGain();
  env.gain.setValueAtTime(0.001, t0);
  env.gain.linearRampToValueAtTime(peak, t0 + 0.002);
  env.gain.exponentialRampToValueAtTime(0.001, t0 + len);
  osc.connect(env).connect(_sfxBus || audioCtx.destination);
  osc.start(t0); osc.stop(t0 + len + 0.02);
}

// Whoosh — fires at click, swells through the charge window, ends as impact lands.
// Bandpass filter sweeping UP (180→1500Hz) gives the rising-pitch "fist accelerating" feel.
function playWhoosh() {
  if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)({ latencyHint: 'interactive' });
  _ensureAudioGraph();
  const t0 = audioCtx.currentTime;
  const len = IMPACT_DELAY;
  const sr = audioCtx.sampleRate;

  const buf = audioCtx.createBuffer(1, Math.max(1, Math.floor(sr * len)), sr);
  const data = buf.getChannelData(0);
  const STEP = 1 / 5;
  for (let i = 0; i < data.length; i++) {
    const v = (Math.random() * 2 - 1);
    data[i] = Math.round(v / STEP) * STEP;
  }
  const src = audioCtx.createBufferSource();
  src.buffer = buf;

  const bp = audioCtx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.setValueAtTime(180, t0);
  bp.frequency.exponentialRampToValueAtTime(1500, t0 + len);
  bp.Q.value = 5;

  const env = audioCtx.createGain();
  env.gain.setValueAtTime(0.001, t0);
  env.gain.exponentialRampToValueAtTime(0.22, t0 + len * 0.85);
  env.gain.exponentialRampToValueAtTime(0.001, t0 + len);

  src.connect(bp).connect(env).connect(_sfxBus || audioCtx.destination);
  src.start(t0); src.stop(t0 + len + 0.02);
}

// Snap a time forward to the next 16th-note boundary in the music. Subtle (max ~125ms delay)
// quantization for collision sounds — pulls them onto the rhythm grid without obvious lag.
function _snapToNextSixteenth(t) {
  if (!_musicStarted || !audioCtx) return t;
  const sixteenthDur = MUSIC_BEAT / 4;
  const elapsed = t - _musicLoopStartTime;
  if (elapsed < 0) return t;
  const nextSlot = Math.ceil(elapsed / sixteenthDur);
  return _musicLoopStartTime + nextSlot * sixteenthDur;
}

// Snap a time forward to the next QUARTER-NOTE beat. Coarser than the 16th snap: used for the co-op
// PEER's smash + jab so their hits land on the main pulse the observer hears. A 16th snap let peer
// hits sit on off-beat subdivisions, so a flurry read as a syncopated "gallop"; quarter-note snap
// forces everything onto the beat (up to ~1 beat of delay, but always in time). See _mpApplyPunch.
function _snapToNextBeat(t) {
  if (!_musicStarted || !audioCtx) return t;
  const elapsed = t - _musicLoopStartTime;
  if (elapsed < 0) return t;
  const nextBeat = Math.ceil(elapsed / MUSIC_BEAT);
  return _musicLoopStartTime + nextBeat * MUSIC_BEAT;
}

// The current position WITHIN the beat, 0..1 (0 = on the beat). Raw song time (no per-player audio-offset
// calibration) so it's the objective musical position of the moment. Sent with a co-op punch so the
// observer can reproduce the SAME off-beat feel a whole number of beats later (see _mpApplyPunch).
function _currentSongBeatPhase() {
  if (!_musicStarted || !audioCtx) return 0;
  const elapsed = audioCtx.currentTime - _musicLoopStartTime;
  return elapsed >= 0 ? (elapsed % MUSIC_BEAT) / MUSIC_BEAT : 0;
}

// Stone click — gritty 3-bit noise lowpass, the original "rubble click" feel.
function _playStoneClick(intensity, position, startTime) {
  const t0 = startTime !== undefined ? startTime : audioCtx.currentTime;
  const len = 0.018 + Math.random() * 0.022;
  const sr = audioCtx.sampleRate;
  const buf = audioCtx.createBuffer(1, Math.max(1, Math.floor(sr * len)), sr);
  const data = buf.getChannelData(0);
  const STEP = 1 / 5;
  for (let i = 0; i < data.length; i++) {
    const env = Math.pow(1 - i / data.length, 1.6);
    const v = (Math.random() * 2 - 1) * env;
    data[i] = Math.round(v / STEP) * STEP;
  }
  const src = audioCtx.createBufferSource();
  src.buffer = buf;
  const lp = audioCtx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 220 + Math.random() * 480;
  lp.Q.value = 2;
  const g = audioCtx.createGain();
  g.gain.value = intensity * 0.22;
  src.connect(lp).connect(g).connect(_spatialDestination(position));
  src.start(t0); src.stop(t0 + len);
}

// Wood tap — bandpassed mid-range click, drier than stone.
function _playWoodClick(intensity, position, startTime) {
  const t0 = startTime !== undefined ? startTime : audioCtx.currentTime;
  const len = 0.018 + Math.random() * 0.020;
  const sr = audioCtx.sampleRate;
  const buf = audioCtx.createBuffer(1, Math.max(1, Math.floor(sr * len)), sr);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    const env = Math.pow(1 - i / data.length, 1.8);
    data[i] = (Math.random() * 2 - 1) * env;
  }
  const src = audioCtx.createBufferSource();
  src.buffer = buf;
  const bp = audioCtx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = _pickScaleNote(580, 1000);  // wood tap, active song scale
  bp.Q.value = 2.5;
  const g = audioCtx.createGain();
  g.gain.value = intensity * 0.18;
  src.connect(bp).connect(g).connect(_spatialDestination(position));
  src.start(t0); src.stop(t0 + len);
}

// Metal tink — bright bandpassed transient + brief tonal accent for "ringing shard" character.
function _playMetalClick(intensity, position, startTime) {
  const t0 = startTime !== undefined ? startTime : audioCtx.currentTime;
  const len = 0.022 + Math.random() * 0.020;
  const sr = audioCtx.sampleRate;
  const buf = audioCtx.createBuffer(1, Math.max(1, Math.floor(sr * len)), sr);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    const env = Math.pow(1 - i / data.length, 2);
    data[i] = (Math.random() * 2 - 1) * env;
  }
  const dest = _spatialDestination(position);
  const src = audioCtx.createBufferSource();
  src.buffer = buf;
  const bp = audioCtx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = _pickScaleNote(1800, 2400);  // metal tink, active song scale
  bp.Q.value = 3;
  const g = audioCtx.createGain();
  g.gain.value = intensity * 0.13;
  src.connect(bp).connect(g).connect(dest);
  src.start(t0); src.stop(t0 + len);
  // Brief tonal accent — sine at ~2kHz, quick decay
  const osc = audioCtx.createOscillator();
  const oscEnv = audioCtx.createGain();
  osc.type = 'sine';
  osc.frequency.value = _pickScaleNote(1900, 2300);  // tonal accent, active song scale
  oscEnv.gain.setValueAtTime(0.001, t0);
  oscEnv.gain.linearRampToValueAtTime(intensity * 0.05, t0 + 0.002);
  oscEnv.gain.exponentialRampToValueAtTime(0.001, t0 + 0.04);
  osc.connect(oscEnv).connect(dest);
  osc.start(t0); osc.stop(t0 + 0.06);
}

// Material-aware landing click — wood, metal, or stone (default for brick / unknown).
function playRubbleClick(intensity, position, material, startTime) {
  if (!audioCtx) return;
  _audioRubbleCalls++;
  if (material === 'crate') _playWoodClick(intensity, position, startTime);
  else if (material === 'metal') _playMetalClick(intensity, position, startTime);
  else _playStoneClick(intensity, position, startTime);
}

// Player-vs-wall AABBs (faster than a Rapier query). Refilled by _buildBounds on every level.
const _wallAABBs = [];

// =========================================================
// Bounds system — per-level floor + 4 perimeter walls (visual + physics), auto-fit to the level's
// block AABB plus configurable margins. Single source of truth: changing the bounds rebuilds both
// the visuals and the colliders together, so they can never drift out of sync.
// =========================================================

// Default margins around the union of (placed blocks) ∪ (player spawn). Forward = -Z (where the
// player looks); Behind = +Z; Side = ±X. Generous forward by default so corridor levels have
// room to lunge past the last block.
const BOUNDS_DEFAULTS = { marginForward: 12, marginBehind: 4, marginSide: 8, height: 8 };

// Per-level overrides. Each field optional → falls back to the default. Mutated live by the
// admin sliders so tweaks while playing stick (until reload).
const LEVEL_BOUNDS = {
  // Sandbox — open exploration, generous margins so the world feels big.
  sandbox: { marginForward: 14, marginBehind: 10, marginSide: 12 },
  // L5 open arena — structures all around player, square room feels right.
  level5:  { marginForward: 6,  marginBehind: 6,  marginSide: 6 },
  // L6 single set-piece — tight chamber.
  level6:  { marginForward: 8,  marginBehind: 6,  marginSide: 6 },
  // L7 spiral arena — roughly square room around the spiral footprint.
  level7:  { marginForward: 8,  marginBehind: 6,  marginSide: 8 },
  // L10 capstone arena — three big pyramids span x ∈ ±24 and z up to -34, so margins are extra
  // generous to keep the room feeling like a playground, not a box hugging the structures.
  level10: { marginForward: 10, marginBehind: 8, marginSide: 8 },
  // L1-L4 corridor levels use defaults.
  // Gauntlet showcase - long forward run, wide so the camera + strafing have air.
  gauntlet: { marginForward: 12, marginBehind: 8, marginSide: 14 },
};

function _resolveBounds(levelId) {
  return { ...BOUNDS_DEFAULTS, ...(LEVEL_BOUNDS[levelId] || {}) };
}

// Compute the axis-aligned box enclosing all placed blocks AND the player spawn point.
function _aabbOfBlocksAndSpawn() {
  // Player spawn is (0, 1.65, 6) - see _resetRunStats.
  let minX = -1, maxX = 1, minZ = -1, maxZ = 6;
  for (const b of blocks) {
    const p = b.geometry.parameters;
    const hw = p.width / 2, hd = p.depth / 2;
    if (b.position.x - hw < minX) minX = b.position.x - hw;
    if (b.position.x + hw > maxX) maxX = b.position.x + hw;
    if (b.position.z - hd < minZ) minZ = b.position.z - hd;
    if (b.position.z + hd > maxZ) maxZ = b.position.z + hd;
  }
  return { minX, maxX, minZ, maxZ };
}

// Build the room from a margins object. Returns the {minX, maxX, minZ, maxZ, height} actually used.
let _boundsMeshes = [];   // floor + 4 walls (THREE meshes)
let _boundsBodies = [];   // ground + 4 wall rigid bodies (Rapier)
// AABB snapshot captured at level-build time. Slider tweaks resize from this snapshot — otherwise
// the room would shrink as blocks get smashed during play.
let _currentBoundsAABB = null;

function _clearBounds() {
  for (const m of _boundsMeshes) {
    scene.remove(m);
    m.geometry.dispose();
  }
  _boundsMeshes.length = 0;
  for (const body of _boundsBodies) physicsWorld.removeRigidBody(body);
  _boundsBodies.length = 0;
  _wallAABBs.length = 0;
  _clearSpeakers();
  _clearWallTrim();
}

function _buildBounds(margins) {
  const aabb = _currentBoundsAABB || _aabbOfBlocksAndSpawn();
  const minX = aabb.minX - margins.marginSide;
  const maxX = aabb.maxX + margins.marginSide;
  const minZ = aabb.minZ - margins.marginForward;  // forward = -Z (deeper into level)
  const maxZ = aabb.maxZ + margins.marginBehind;   // behind = +Z (behind player)
  const h = margins.height;
  const w = maxX - minX;
  const d = maxZ - minZ;
  const cx = (minX + maxX) / 2;
  const cz = (minZ + maxZ) / 2;

  // Floor (visual). Brushed-metal bump repeat mapped to world-units so grain scale is constant across arenas.
  _floorGrainTex.repeat.set(w / _FLOOR_TILE, d / _FLOOR_TILE);
  const floor = new THREE.Mesh(new THREE.PlaneGeometry(w, d), _floorMaterial);
  floor.rotation.x = -Math.PI / 2;
  floor.position.set(cx, 0, cz);
  floor.receiveShadow = true;
  scene.add(floor);
  _boundsMeshes.push(floor);

  // Floor (physics) — thin cuboid 1m thick centred at y=-0.5 so its top sits at y=0.
  const ground = physicsWorld.createRigidBody(
    RAPIER.RigidBodyDesc.fixed().setTranslation(cx, -0.5, cz)
  );
  physicsWorld.createCollider(RAPIER.ColliderDesc.cuboid(w / 2, 0.5, d / 2), ground);
  _boundsBodies.push(ground);

  // 4 walls — each one visual mesh + physics collider. Rotated walls (left/right) use the
  // same box geometry, rotated 90° around Y so the 0.4m-thick face points inward.
  // wallSpec: [length, x, z, ry, halfX, halfZ, posX, posZ] — but easier to just build inline.
  // Wall is built as TWO stacked Rapier colliders so the invisible upper extension can let
  // tall toppling blocks pass through (they get culled by _checkLevelEnd's escape margin)
  // while still catching coins, chips, debris, and the player.
  //   - Lower (0..h): visible portion, default collision groups, collides with everything.
  //   - Upper (h..UPPER_TOP): invisible extension, UPPER_WALL_GROUPS excludes blocks (bit 9).
  // Player pushout is hand-rolled via _wallAABBs, so the player gets a single full-height
  // AABB entry — Rapier groups are irrelevant for the player kinematic body.
  const UPPER_TOP = 200;
  const upperH = UPPER_TOP - h;
  const buildWall = (axis, length, posX, posZ) => {
    const isZAxis = axis === 'z';
    const meshGeoW = length;
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(meshGeoW, h, 0.4), _wallMaterial);
    mesh.position.set(posX, h / 2, posZ);
    mesh.rotation.y = isZAxis ? Math.PI / 2 : 0;
    mesh.receiveShadow = true;
    scene.add(mesh);
    _boundsMeshes.push(mesh);

    // Physics half-extents — for X-axis wall (front/back), thickness is on Z; for Z-axis (sides), on X.
    const hx = isZAxis ? 0.2 : length / 2;
    const hz = isZAxis ? length / 2 : 0.2;

    // Lower (visible-height) collider — default groups, collides with everything.
    const lowerBody = physicsWorld.createRigidBody(
      RAPIER.RigidBodyDesc.fixed().setTranslation(posX, h / 2, posZ)
    );
    physicsWorld.createCollider(RAPIER.ColliderDesc.cuboid(hx, h / 2, hz), lowerBody);
    _boundsBodies.push(lowerBody);

    // Upper (invisible) collider — filters out dynamic blocks so tall structures pass through.
    const upperBody = physicsWorld.createRigidBody(
      RAPIER.RigidBodyDesc.fixed().setTranslation(posX, h + upperH / 2, posZ)
    );
    const upperCollider = physicsWorld.createCollider(
      RAPIER.ColliderDesc.cuboid(hx, upperH / 2, hz), upperBody
    );
    upperCollider.setCollisionGroups(UPPER_WALL_GROUPS);
    _boundsBodies.push(upperBody);

    // Player pushout reads from _wallAABBs — keep the full-height entry so lunges still can't escape.
    _wallAABBs.push({ x: posX, y: UPPER_TOP / 2, z: posZ, hx, hy: UPPER_TOP / 2, hz });
  };
  buildWall('x', w, cx, maxZ);  // back wall (behind player, +Z)
  buildWall('x', w, cx, minZ);  // front wall (deep end, -Z)
  buildWall('z', d, minX, cz);  // left side
  buildWall('z', d, maxX, cz);  // right side
  _currentWallBounds = { minX, maxX, minZ, maxZ, height: h };
  _buildSpeakers();
  _buildWallTrim();
}

function _rebuildBoundsForLevel(levelId) {
  _clearBounds();
  _currentBoundsAABB = _aabbOfBlocksAndSpawn();   // freeze AABB so slider tweaks don't shrink the room as blocks break
  _buildBounds(_resolveBounds(levelId));
  if (_refreshBoundsSliders) _refreshBoundsSliders(levelId);
}

// Slider refresh hook — set by the admin panel after sliders are created.
let _refreshBoundsSliders = null;

// =========================================================
// Player body — sphere collider at camera height. A sphere avoids the constant floor-contact
// problem a tall capsule has, and short blocks (under chest level) are naturally steppable.
// =========================================================
const PLAYER_RADIUS = 0.4;

const playerBody = physicsWorld.createRigidBody(
  RAPIER.RigidBodyDesc.kinematicPositionBased()
    .setTranslation(0, 1.65, 6)
);
const playerCollider = physicsWorld.createCollider(
  RAPIER.ColliderDesc.ball(PLAYER_RADIUS),
  playerBody
);
// Push the player sphere out of an axis-aligned box if they're overlapping it.
function pushOutFromAABB(cx, cy, cz, hx, hy, hz) {
  // Use actual camera Y so flying above a block stops triggering lateral pushout from its XZ footprint.
  const px = camera.position.x, py = camera.position.y, pz = camera.position.z;
  const closestX = Math.max(cx - hx, Math.min(px, cx + hx));
  const closestY = Math.max(cy - hy, Math.min(py, cy + hy));
  const closestZ = Math.max(cz - hz, Math.min(pz, cz + hz));
  const dx = px - closestX, dy = py - closestY, dz = pz - closestZ;
  const distSq = dx * dx + dy * dy + dz * dz;
  const r = PLAYER_RADIUS;
  if (distSq >= r * r) return;
  const dist = Math.sqrt(distSq);
  if (dist < 0.0001) {
    // Player center is INSIDE the AABB — happens when a fast frame tunnels into a big block,
    // or a block spawns/topples around the player. The original code skipped this, which left
    // the player permanently stuck inside the geometry. Recover by finding the nearest X/Z face
    // and pushing out laterally. Y is intentionally excluded so we don't teleport the player to
    // the top of a tall block, matching the lateral-only behaviour of the grazing-case push.
    const dxLeft  = (cx - hx) - px;     // negative — distance to -X face
    const dxRight = (cx + hx) - px;     // positive — distance to +X face
    const dzBack  = (cz - hz) - pz;     // negative — distance to -Z face
    const dzFront = (cz + hz) - pz;     // positive — distance to +Z face
    const penX = Math.abs(dxLeft) < Math.abs(dxRight) ? dxLeft : dxRight;
    const penZ = Math.abs(dzBack) < Math.abs(dzFront) ? dzBack : dzFront;
    if (Math.abs(penX) <= Math.abs(penZ)) {
      camera.position.x += penX + Math.sign(penX) * r;
    } else {
      camera.position.z += penZ + Math.sign(penZ) * r;
    }
    return;
  }
  const factor = (r - dist) / dist;
  camera.position.x += dx * factor;
  camera.position.z += dz * factor;
}

function resolvePlayerCollision() {
  for (const w of _wallAABBs) {
    pushOutFromAABB(w.x, w.y, w.z, w.hx, w.hy, w.hz);
  }
  for (const b of blocks) {
    if (b.userData.dynamic) continue; // toppled blocks are walkthrough rubble
    const bp = b.geometry.parameters;
    pushOutFromAABB(
      b.position.x, b.position.y, b.position.z,
      bp.width / 2, bp.height / 2, bp.depth / 2
    );
  }
}

// Smashable blocks. Mirrors HitsToBreak/HitsTaken/OriginalColor attributes from PunchSmashClient.
const blocks = [];
const CRACK_DARKEN_PER_HIT = 0.7; // matches the V-mult feel of _getCrackedDisplayColor

// Per-material base texture painters — chunky pixel art, Minecraft-tier fidelity.
// Cached: each material gets one base canvas, copied per-block so cracks are independent.
const TEXTURE_SIZE = 64;
const _baseTextureCanvases = {};

function paintCrateTexture(ctx, w, h) {
  // 4 vertical planks with slight color variation + dark plank edges + horizontal grain streaks.
  ctx.fillStyle = '#d9a04a';
  ctx.fillRect(0, 0, w, h);
  const plankCount = 4;
  const plankWidth = w / plankCount;
  for (let i = 0; i < plankCount; i++) {
    const v = 0.78 + Math.random() * 0.22;
    ctx.fillStyle = `rgb(${Math.floor(217 * v)}, ${Math.floor(160 * v)}, ${Math.floor(74 * v)})`;
    ctx.fillRect(i * plankWidth, 0, plankWidth, h);
  }
  ctx.strokeStyle = 'rgba(50, 28, 12, 0.7)';
  ctx.lineWidth = 1;
  for (let i = 1; i < plankCount; i++) {
    const x = Math.floor(i * plankWidth);
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, h);
    ctx.stroke();
  }
  ctx.strokeStyle = 'rgba(80, 50, 25, 0.35)';
  for (let i = 0; i < 8; i++) {
    const y = Math.floor(Math.random() * h);
    const x1 = Math.random() * w;
    const len = 6 + Math.random() * 18;
    ctx.beginPath();
    ctx.moveTo(x1, y);
    ctx.lineTo(x1 + len, y);
    ctx.stroke();
  }
}

function paintBrickTexture(ctx, w, h) {
  // Mortar background, then offset brick rows on top.
  ctx.fillStyle = '#3a2820';
  ctx.fillRect(0, 0, w, h);
  const brickH = 8, brickW = 16, mortar = 1;
  for (let row = 0; row * brickH < h; row++) {
    const y = row * brickH;
    const xOffset = (row % 2) * (brickW / 2);
    for (let col = -1; col * brickW + xOffset < w; col++) {
      const x = col * brickW + xOffset;
      const v = 0.85 + Math.random() * 0.3;
      const r = Math.floor(196 * v), g = Math.floor(104 * v), b = Math.floor(79 * v);
      ctx.fillStyle = `rgb(${r},${g},${b})`;
      ctx.fillRect(x + mortar, y + mortar, brickW - mortar * 2, brickH - mortar * 2);
    }
  }
}

function paintMetalTexture(ctx, w, h) {
  // Silver base + cooler grain hatching + bright rivets at corners — reads as polished metal.
  ctx.fillStyle = '#9a9ea6';
  ctx.fillRect(0, 0, w, h);
  ctx.strokeStyle = 'rgba(60, 65, 75, 0.4)';
  ctx.lineWidth = 1;
  for (let x = -h; x < w; x += 4) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x + h, h);
    ctx.stroke();
  }
  const inset = 6;
  const rivets = [[inset, inset], [w - inset, inset], [inset, h - inset], [w - inset, h - inset]];
  for (const [rx, ry] of rivets) {
    const grad = ctx.createRadialGradient(rx - 1, ry - 1, 0, rx, ry, 3);
    grad.addColorStop(0, '#e0e2e8');
    grad.addColorStop(0.6, '#7a7c84');
    grad.addColorStop(1, '#3a3c44');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(rx, ry, 3, 0, Math.PI * 2);
    ctx.fill();
  }
}

function getBaseTextureCanvas(material) {
  if (_baseTextureCanvases[material]) return _baseTextureCanvases[material];
  const canvas = document.createElement('canvas');
  canvas.width = TEXTURE_SIZE;
  canvas.height = TEXTURE_SIZE;
  const ctx = canvas.getContext('2d');
  if (material === 'crate') paintCrateTexture(ctx, TEXTURE_SIZE, TEXTURE_SIZE);
  else if (material === 'brick') paintBrickTexture(ctx, TEXTURE_SIZE, TEXTURE_SIZE);
  else if (material === 'metal') paintMetalTexture(ctx, TEXTURE_SIZE, TEXTURE_SIZE);
  else { ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, TEXTURE_SIZE, TEXTURE_SIZE); }
  _baseTextureCanvases[material] = canvas;
  return canvas;
}

function makeCrackCanvas(material) {
  const canvas = document.createElement('canvas');
  canvas.width = TEXTURE_SIZE;
  canvas.height = TEXTURE_SIZE;
  canvas.getContext('2d').drawImage(getBaseTextureCanvas(material), 0, 0);
  return canvas;
}

// Shared CanvasTexture per material — used by all undamaged blocks of that material.
// First damage to a block swaps its map to a per-block crack texture (see _paintBlockDamage).
// Saves 200+ canvas allocations + GPU uploads per big-level load.
const _sharedBaseTextures = {};
function getSharedBaseTexture(material) {
  if (_sharedBaseTextures[material]) return _sharedBaseTextures[material];
  const tex = new THREE.CanvasTexture(getBaseTextureCanvas(material));
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  _sharedBaseTextures[material] = tex;
  return tex;
}

// Lazily promote a block from the shared base texture to its own per-block crack canvas/texture
// on first damage, then paint the damage. After this the block's texture is independent and
// further paintDamage calls just update its private canvas + flag needsUpdate. Each block also
// picks a stable crack style on first damage so subsequent hits paint in the same style.
function _paintBlockDamage(block, hitIndex) {
  const ud = block.userData;
  if (!ud.crackCanvas) {
    const c = document.createElement('canvas');
    c.width = TEXTURE_SIZE;
    c.height = TEXTURE_SIZE;
    c.getContext('2d').drawImage(getBaseTextureCanvas(ud.material), 0, 0);
    ud.crackCanvas = c;
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.magFilter = THREE.NearestFilter;
    tex.minFilter = THREE.NearestFilter;
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    ud.crackTexture = tex;
    block.material.map = tex;
    block.material.needsUpdate = true;
    ud.crackStyle = _CRACK_STYLES[Math.floor(Math.random() * _CRACK_STYLES.length)];
  }
  paintDamage(ud.crackCanvas, hitIndex, ud.material, ud.crackStyle);
  ud.crackTexture.needsUpdate = true;
  // Co-op: mirror this crack to the peer (suppressed when WE are applying a peer's crack — see
  // _mpApplyDamage, which sets _netRemoteContext to block the echo).
  if (_mp && !_netRemoteContext && ud.netId !== undefined) {
    _mp.send({ t: 'dmg', id: ud.netId, ht: hitIndex });
  }
}

// Scale BoxGeometry UVs per face so the texture tiles instead of stretching across non-cubic blocks.
// One texture tile per meter; tile scale per face equals the face's two world dimensions.
const TEXTURE_TILE_PER_METER = 1.0;
function applyTilingUVs(geometry, w, h, d) {
  const uv = geometry.attributes.uv;
  const t = TEXTURE_TILE_PER_METER;
  // Three.js BoxGeometry face order: +x, -x, +y, -y, +z, -z. 4 UVs per face.
  const faceScales = [
    [d * t, h * t], [d * t, h * t],   // ±x faces map to (z, y)
    [w * t, d * t], [w * t, d * t],   // ±y faces map to (x, z)
    [w * t, h * t], [w * t, h * t],   // ±z faces map to (x, y)
  ];
  for (let f = 0; f < 6; f++) {
    const [su, sv] = faceScales[f];
    for (let v = 0; v < 4; v++) {
      const idx = f * 4 + v;
      uv.setXY(idx, uv.getX(idx) * su, uv.getY(idx) * sv);
    }
  }
  uv.needsUpdate = true;
}

// Metal gets dents instead of cracks — looks like a hammer struck and left a dimple.
function paintDents(canvas, hitIndex) {
  const ctx = canvas.getContext('2d');
  const w = canvas.width, h = canvas.height;
  const dents = 1 + (Math.random() < 0.5 ? 1 : 0);
  const sizeBoost = Math.min(hitIndex, 4) * 0.3;
  for (let i = 0; i < dents; i++) {
    const cx = 6 + Math.random() * (w - 12);
    const cy = 6 + Math.random() * (h - 12);
    const r = 3.5 + Math.random() * 3.5 + sizeBoost;
    // Concave shading — dark interior fades to invisible at the rim.
    const grad = ctx.createRadialGradient(cx + 1, cy + 1, 0, cx, cy, r);
    grad.addColorStop(0, 'rgba(15, 17, 22, 0.9)');
    grad.addColorStop(0.5, 'rgba(45, 48, 55, 0.55)');
    grad.addColorStop(1, 'rgba(80, 83, 90, 0)');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    // Bright highlight on the opposite-light side — metal pushed up around the rim.
    const hx = cx - 1.4, hy = cy - 1.4;
    const hgrad = ctx.createRadialGradient(hx, hy, 0, hx, hy, r * 0.55);
    hgrad.addColorStop(0, 'rgba(225, 228, 235, 0.45)');
    hgrad.addColorStop(1, 'rgba(225, 228, 235, 0)');
    ctx.fillStyle = hgrad;
    ctx.beginPath();
    ctx.arc(hx, hy, r * 0.55, 0, Math.PI * 2);
    ctx.fill();
  }
}

// Material-aware damage painter. Metal gets dents; crate/brick pick from 4 crack styles.
// Each block picks a stable style on first damage (stored in userData) so subsequent hits
// on the same block paint in the same style — gives variety across blocks without making
// any single block look like a chaotic mix of patterns.
const _CRACK_STYLES = ['web', 'lightning', 'star', 'shatter'];
function paintDamage(canvas, hitIndex, material, style) {
  if (material === 'metal') paintDents(canvas, hitIndex);
  else paintCrack(canvas, hitIndex, style || 'web');
}

function paintCrack(canvas, hitIndex, style) {
  const ctx = canvas.getContext('2d');
  const w = canvas.width, h = canvas.height;
  const lineWidth = 1.2 + Math.min(hitIndex, 4) * 0.5;
  const opacity = Math.min(0.4 + Math.min(hitIndex, 5) * 0.12, 0.9);
  ctx.strokeStyle = `rgba(0, 0, 0, ${opacity})`;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.lineWidth = lineWidth;
  if (style === 'lightning') _paintCrackLightning(ctx, w, h);
  else if (style === 'star')  _paintCrackStar(ctx, w, h);
  else if (style === 'shatter') _paintCrackShatter(ctx, w, h);
  else _paintCrackWeb(ctx, w, h);
}

// Spider-web — classic radial branches from 1-2 origins, with side forks. Densest pattern.
function _paintCrackWeb(ctx, w, h) {
  const origins = 1 + (Math.random() < 0.55 ? 1 : 0);
  for (let o = 0; o < origins; o++) {
    const cx = w * (0.2 + Math.random() * 0.6);
    const cy = h * (0.2 + Math.random() * 0.6);
    const branches = 7 + Math.floor(Math.random() * 5); // 7-11 main branches
    for (let i = 0; i < branches; i++) {
      const baseAngle = (i / branches) * Math.PI * 2 + (Math.random() - 0.5) * 0.6;
      let x = cx, y = cy;
      ctx.beginPath();
      ctx.moveTo(x, y);
      const segments = 4 + Math.floor(Math.random() * 4);
      for (let s = 0; s < segments; s++) {
        const segLen = 6 + Math.random() * 14;
        const angle = baseAngle + (Math.random() - 0.5) * 0.5;
        x += Math.cos(angle) * segLen;
        y += Math.sin(angle) * segLen;
        ctx.lineTo(x, y);
      }
      ctx.stroke();
      if (Math.random() < 0.7) {
        const branchStart = 6 + Math.random() * 12;
        const branchAngle = baseAngle + (Math.random() < 0.5 ? -1 : 1) * (Math.PI / 4 + Math.random() * Math.PI / 4);
        let bx = cx + Math.cos(baseAngle) * branchStart;
        let by = cy + Math.sin(baseAngle) * branchStart;
        ctx.beginPath();
        ctx.moveTo(bx, by);
        const sLen = 8 + Math.random() * 16;
        bx += Math.cos(branchAngle) * sLen;
        by += Math.sin(branchAngle) * sLen;
        ctx.lineTo(bx, by);
        if (Math.random() < 0.45) {
          const subAngle = branchAngle + (Math.random() < 0.5 ? -1 : 1) * (0.5 + Math.random() * 0.4);
          const subLen = 5 + Math.random() * 10;
          bx += Math.cos(subAngle) * subLen;
          by += Math.sin(subAngle) * subLen;
          ctx.lineTo(bx, by);
        }
        ctx.stroke();
      }
    }
  }
}

// Lightning — 1-2 jagged top-to-bottom bolts with a couple of horizontal forks. Linear feel.
function _paintCrackLightning(ctx, w, h) {
  const bolts = 1 + (Math.random() < 0.5 ? 1 : 0);
  for (let b = 0; b < bolts; b++) {
    const startX = Math.random() * w;
    const startY = Math.random() * h * 0.3;
    const endX = startX + (Math.random() - 0.5) * w * 0.4;
    const endY = h - Math.random() * h * 0.3;
    let x = startX, y = startY;
    ctx.beginPath();
    ctx.moveTo(x, y);
    const segments = 6 + Math.floor(Math.random() * 4);
    for (let s = 1; s <= segments; s++) {
      const t = s / segments;
      const tx = startX + (endX - startX) * t + (Math.random() - 0.5) * 10;
      const ty = startY + (endY - startY) * t + (Math.random() - 0.5) * 6;
      ctx.lineTo(tx, ty);
      x = tx; y = ty;
    }
    ctx.stroke();
    const forks = 1 + (Math.random() < 0.6 ? 1 : 0);
    for (let f = 0; f < forks; f++) {
      const t = 0.3 + Math.random() * 0.5;
      const fx = startX + (endX - startX) * t;
      const fy = startY + (endY - startY) * t;
      ctx.beginPath();
      ctx.moveTo(fx, fy);
      const flen = 8 + Math.random() * 14;
      const angle = (Math.random() - 0.5) * Math.PI * 0.7;
      ctx.lineTo(fx + Math.cos(angle) * flen, fy + Math.sin(angle) * flen);
      ctx.stroke();
    }
  }
}

// Star burst — clean radial rays from one impact point, like a bullet hole.
function _paintCrackStar(ctx, w, h) {
  const cx = w * (0.3 + Math.random() * 0.4);
  const cy = h * (0.3 + Math.random() * 0.4);
  const rays = 8 + Math.floor(Math.random() * 5); // 8-12 rays
  for (let r = 0; r < rays; r++) {
    const angle = (r / rays) * Math.PI * 2 + (Math.random() - 0.5) * 0.4;
    const len = 12 + Math.random() * 18;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + Math.cos(angle) * len, cy + Math.sin(angle) * len);
    ctx.stroke();
  }
}

// Shatter — concentric arcs + radial rays from a centre. Reads like glass impact.
function _paintCrackShatter(ctx, w, h) {
  const cx = w * (0.3 + Math.random() * 0.4);
  const cy = h * (0.3 + Math.random() * 0.4);
  const arcs = 2 + Math.floor(Math.random() * 2);
  for (let a = 0; a < arcs; a++) {
    const r = 8 + a * 9 + Math.random() * 4;
    const startA = Math.random() * Math.PI * 2;
    const span = Math.PI * (0.8 + Math.random() * 0.7);
    ctx.beginPath();
    ctx.arc(cx, cy, r, startA, startA + span);
    ctx.stroke();
  }
  const rays = 4 + Math.floor(Math.random() * 3);
  for (let r = 0; r < rays; r++) {
    const angle = (r / rays) * Math.PI * 2 + (Math.random() - 0.5) * 0.4;
    const len = 12 + Math.random() * 16;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + Math.cos(angle) * len, cy + Math.sin(angle) * len);
    ctx.stroke();
  }
}

// Shared dark edge material for all blocks — soft pencil outline so single cuboids read as one entity, not a stack of cubes.
const _blockEdgeMaterial = new THREE.LineBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.4 });

// Material library — visual + physical traits per durability tier.
// Crate (1 hit) is light wood, brick (2 hits) is the standard medium, metal (3 hits) is the heavy tier.
const MATERIALS = {
  crate: { color: 0xd9a04a, hits: 1, density: 0.6, roughness: 0.85, metalness: 0.0  },
  brick: { color: 0xc4684f, hits: 2, density: 1.2, roughness: 0.82, metalness: 0.05 },
  metal: { color: 0x9a9ea6, hits: 3, density: 2.0, roughness: 0.4,  metalness: 0.7  },
};

function createBlockVisualMesh(w, h, d, material = 'brick') {
  const m = MATERIALS[material] || MATERIALS.brick;
  const sharedTex = getSharedBaseTexture(material);
  const geometry = new THREE.BoxGeometry(w, h, d);
  applyTilingUVs(geometry, w, h, d);
  const block = new THREE.Mesh(
    geometry,
    new THREE.MeshStandardMaterial({ color: 0xffffff, map: sharedTex, roughness: m.roughness, metalness: m.metalness })
  );
  block.castShadow = true;
  block.receiveShadow = true;
  block.add(new THREE.LineSegments(new THREE.EdgesGeometry(geometry), _blockEdgeMaterial));
  return block;
}

function addBlock({ size, position, material, rotationY = 0 }) {
  const m = MATERIALS[material] || MATERIALS.brick;  // unknown/missing material → safe default
  const [w, h, d] = size;
  // All undamaged blocks of the same material share one CanvasTexture. First damage promotes
  // the block to its own per-block crack canvas via _paintBlockDamage. Saves a lot of canvas
  // allocations + GPU uploads when a 200+ block level loads.
  const sharedTex = getSharedBaseTexture(material);
  const geometry = new THREE.BoxGeometry(w, h, d);
  applyTilingUVs(geometry, w, h, d);
  // material.color stays white so the texture canvas shows its baked colors unmodified.
  // The crack-darken pass multiplies material.color by a fade factor - that uniformly dims the texture.
  const block = new THREE.Mesh(
    geometry,
    new THREE.MeshStandardMaterial({ color: 0xffffff, map: sharedTex, roughness: m.roughness, metalness: m.metalness })
  );
  block.castShadow = true;
  block.receiveShadow = true;
  // Soft pencil outline along the block's 12 edges — disambiguates a single cuboid from a stack of cubes.
  block.add(new THREE.LineSegments(new THREE.EdgesGeometry(geometry), _blockEdgeMaterial));
  block.userData.crackCanvas = null;   // lazily created on first damage
  block.userData.crackTexture = null;  // lazily created on first damage
  // UGC builds apply a sub-millimeter golden-ratio jitter so coplanar LLM-emitted faces
  // don't z-fight. Off by default; buildFromSpec switches it on for the UGC level only.
  let _px = position[0], _py = position[1], _pz = position[2];
  if (_ugcJitterIndex >= 0) {
    const i = _ugcJitterIndex++;
    const G = 0.6180339887;
    const E = 0.0005; // 0.5mm max offset per axis — invisible, breaks all coplanar ties.
    _px += ((i * G)         % 1) * E;
    _py += ((i * G * G)     % 1) * E;
    _pz += ((i * G * G * G) % 1) * E;
  }
  block.position.set(_px, _py, _pz);
  if (rotationY !== 0) block.rotation.y = rotationY;
  block.userData.hitsToBreak = m.hits;
  block.userData.hitsTaken = 0;
  block.userData.originalColor = new THREE.Color(0xffffff);
  block.userData.material = material;
  block.userData.supporters = new Set();
  block.userData.dependents = new Set();
  block.userData.permanentAnchor = false;

  // Start fixed (anchored) — stable stacks until first hit, mirrors IMPACT_PUSH_UNANCHOR_IF_NEEDED.
  const bodyDesc = RAPIER.RigidBodyDesc.fixed().setTranslation(position[0], position[1], position[2]);
  if (rotationY !== 0) {
    bodyDesc.setRotation({ x: 0, y: Math.sin(rotationY / 2), z: 0, w: Math.cos(rotationY / 2) });
  }
  const body = physicsWorld.createRigidBody(bodyDesc);
  const blockCollider = physicsWorld.createCollider(
    RAPIER.ColliderDesc.cuboid(w / 2, h / 2, d / 2)
      .setFriction(0.8)
      .setRestitution(0.05)
      .setDensity(m.density)
      .setActiveEvents(RAPIER.ActiveEvents.COLLISION_EVENTS),
    body
  );
  // Filter out aesthetic chunks (bit 11): block colliders don't even check chunks against the
  // structure. Saves a lot of broad-phase work when a 95-block layout sprays 1000+ chunks around.
  blockCollider.setCollisionGroups(BLOCK_COLLISION_GROUPS);
  block.userData.body = body;
  block.userData.dynamic = false;
  _blockByBodyHandle.set(body.handle, block);

  scene.add(block);
  // Flush matrixWorld immediately. Without this, blocks added during level entry have a
  // stale identity matrixWorld until the renderer's next traversal updates it — which means
  // rayHitBlock (which transforms the ray via block.matrixWorld.invert()) treats the block
  // as if it were sitting at the origin. The first updateNear after build then picks blocks
  // based on the ghost-at-origin AABB instead of their real positions, and hysteresis locks
  // the wrong target for the rest of the level. Forcing the matrix here makes the very
  // first updateNear see correct positions.
  block.updateMatrixWorld(true);
  // Deterministic net id: builders run identically on every client, so the Nth block built gets
  // the same id everywhere. That's what lets a peer's "block N died" event find the same block.
  block.userData.netId = _netSeq++;
  _netById.set(block.userData.netId, block);
  blocks.push(block);
  return block;
}

// Topple constants ported from PunchConfigClient defaults
const IMPACT_PUSH_STRENGTH = 14;
const IMPACT_PUSH_UP = 0.25;
const IMPACT_PUSH_RANDOM = 0.12;
const IMPACT_PUSH_CAP = 45;
const NEARBY_RADIUS = 12;
const NEARBY_MAX_PARTS = 200; // big enough that even the 140-block pyramid cascades fully
const NEARBY_MULT = 0.35;

// Block-death style toggle (admin). When `_ghostDeath.enabled` is true, killed blocks become
// non-colliding dynamic bodies that fly off on the hit's impulse, fade over GHOST_DEATH_FADE_MS,
// and dispose. When false, the original 160ms shrink-and-fade-in-place runs. Toggle in admin
// to revert if the new behavior turns out wrong. Declared here (not next to breakBlock further
// down) because the admin panel setup references it during module load.
const _ghostDeath = { enabled: true };
const GHOST_DEATH_HOLD_MS = 300;   // full-opacity flight time before fade starts
const GHOST_DEATH_FADE_MS = 700;   // linear opacity fade-out duration after the hold
const _dyingBlocks = [];

// Storm power tuning knobs. The slider values are MAX targets reached at streak tier 50+.
// Lower streaks scale toward those maxes. `forceOn` skips the streak ramp and applies the
// max values immediately — useful for tuning without holding a streak.
const _stormPower = {
  forceOn: false,
  aoeRadiusMax: 2.5,            // m at streak 50+. 0 to disable AOE entirely.
  aoeDamageMax: 3,              // damage per AOE neighbour at streak 50+.
  shockwaveRadiusMultMax: 1.5,  // shockwave radius mult at streak 50+ (baseline 1.0).
  chunkImpulseMultMax: 2.3,     // chunk push-speed mult at streak 50+ (baseline 1.0).
};

// Streak-based fraction of max storm power. 0 below tier 5, ramps to 1.0 at tier 50+.
function _stormPowerFactor() {
  if (_stormPower.forceOn) return 1.0;
  const s = _streak;
  if (s >= 50) return 1.0;
  if (s >= 20) return 0.75;
  if (s >= 10) return 0.5;
  if (s >= 5)  return 0.25;
  return 0;
}

// Assist-mode auto-tap blast scalar. Unlike _stormPowerFactor (capped at 1.0 @ streak 50, balanced
// for the chip-storm), this is a CONTINUOUS curve that KEEPS GROWING past 50 so god-tier punches
// feel genuinely explosive, and ramps smoothly so the build is felt at every tier. Drives the
// assist-only AOE/topple/shake in applyHit (decoupled from _stormPower so normal mode is untouched).
//   streak  <5  -> 0 (no blast, plain tap)
//   streak 5..50 -> 0.3 → 1.0 (visible even at the first tier)
//   streak 50+   -> keeps climbing +1 per 25 streak, capped at 3.0 (~streak 100 = max chaos)
const EASY_BLAST_AOE_RADIUS = 2.8;  // metres at blast 1.0 (streak 50); multiplied by the scalar (so >50 exceeds this)
const EASY_BLAST_AOE_DAMAGE = 3;    // per-neighbour damage at blast 1.0; multiplied by the scalar
function _easyTapBlast() {
  const s = _streak;
  if (s < 5) return 0;
  if (s <= 50) return 0.3 + 0.7 * ((s - 5) / 45);
  return Math.min(3, 1 + (s - 50) / 25);
}

// Assist DAMAGE curve — decoupled from the spectacle curve above. This is the only dial that controls
// how much the blast actually KILLS. It holds at zero until the streak is deep, then ramps in, so a tap
// clears only the block it lands on through the early/mid streak (the charge storm is the multi-clear
// tool meanwhile) and the big multi-block clears arrive "far far later". Spectacle (shake, debris,
// soundscape) still rides _easyTapBlast from streak 5 — power is FELT early, destruction LANDS late.
const EASY_DAMAGE_MIN_STREAK = 35;    // no AOE kills below this
const EASY_DAMAGE_FULL_STREAK = 80;   // full AOE (1.0 scalar) by here
function _easyDamageBlast() {
  const s = _streak;
  if (s < EASY_DAMAGE_MIN_STREAK) return 0;
  return Math.min(1, (s - EASY_DAMAGE_MIN_STREAK) / (EASY_DAMAGE_FULL_STREAK - EASY_DAMAGE_MIN_STREAK));
}

// Assist destruction SOUNDSCAPE gate + build curve. The audio sections hold off until this streak
// (the visual blast/AOE still starts earlier at streak 5 — kept, Rich liked it), then build in
// gradually so the intensity doesn't slam on. Returns a 0..1 loudness scalar: subdued at the gate,
// full by ~streak 50.
const EASY_SECTION_MIN_STREAK = 20;
function _easySectionIntensity(streak) {
  if (streak < EASY_SECTION_MIN_STREAK) return 0;
  return 0.35 + 0.65 * Math.min(1, (streak - EASY_SECTION_MIN_STREAK) / 30);  // 0.35 @ 20 → 1.0 @ 50
}

function unanchorBlock(block) {
  if (block.userData.dynamic) return;

  // Multiplayer ownership: whoever FIRST sends a block dynamic owns it and will stream its motion.
  // The cascade below recurses through unanchorBlock, so dependents inherit the same ownership pass.
  if (_mpEnabled && _netRemoteContext) {
    // We're replaying a peer's event (their death/unanchor cascaded into this block). The PEER owns it.
    block.userData.owned = false;
    block.userData.ownedRemote = true;
    if (_MP_MODE === 'cause') {
      // Cause-based: peer collapses run on our OWN local physics. Make it dynamic + awake; the synced
      // impulse (in _mpApplyUnanchor) gives it the same kick, then physics carries it (divergent, smooth).
      block.userData.body.setBodyType(RAPIER.RigidBodyType.Dynamic, true);
      block.userData.dynamic = true;
    }
    // stream mode: dynamic stays false; body stays Fixed (kinematic) — the streamed motion drives it.
  } else {
    block.userData.body.setBodyType(RAPIER.RigidBodyType.Dynamic, true);
    block.userData.dynamic = true;
    if (_mpEnabled) {
      block.userData.owned = true;
      block.userData.ownedRemote = false;
      block.userData.netNeedsUnanchor = true;
    }
  }

  for (const S of block.userData.supporters) {
    S.userData.dependents.delete(block);
  }
  block.userData.supporters.clear();

  for (const D of block.userData.dependents) {
    D.userData.supporters.delete(block);
    if (D.userData.supporters.size === 0 && !D.userData.permanentAnchor) {
      unanchorBlock(D);
    }
  }
  block.userData.dependents.clear();
}

// World-aligned XZ half-extents — accounts for Y rotation so rotated beams get a sensible footprint.
function getWorldXZHalfExtents(block) {
  const { width, depth } = block.geometry.parameters;
  const ry = block.rotation.y;
  if (ry === 0) return [width / 2, depth / 2];
  const c = Math.abs(Math.cos(ry));
  const s = Math.abs(Math.sin(ry));
  return [(width * c + depth * s) / 2, (width * s + depth * c) / 2];
}

// A rests on B when B is below A, A's bottom is at or below B's top, and their XZ footprints overlap.
function isSupportedBy(A, B) {
  if (B.position.y >= A.position.y) return false;
  const aBottom = A.position.y - A.geometry.parameters.height / 2;
  const bTop    = B.position.y + B.geometry.parameters.height / 2;
  if (aBottom > bTop + 0.1) return false;
  const [ahx, ahz] = getWorldXZHalfExtents(A);
  const [bhx, bhz] = getWorldXZHalfExtents(B);
  const overlapX = (ahx + bhx) - Math.abs(A.position.x - B.position.x);
  const overlapZ = (ahz + bhz) - Math.abs(A.position.z - B.position.z);
  if (overlapX <= 0 || overlapZ <= 0) return false;
  const area = Math.min(overlapX, ahx * 2, bhx * 2) * Math.min(overlapZ, ahz * 2, bhz * 2);
  return area >= 0.05;
}

function buildSupportGraph() {
  for (let i = 0; i < blocks.length; i++) {
    const A = blocks[i];
    for (let j = 0; j < blocks.length; j++) {
      if (i === j) continue;
      const B = blocks[j];
      if (isSupportedBy(A, B)) {
        A.userData.supporters.add(B);
        B.userData.dependents.add(A);
      }
    }
  }
  // Anything still without supporters after the build is either ground-resting or
  // a manually-placed floating decoration — either way, the cascade should never drop it.
  for (const block of blocks) {
    if (block.userData.supporters.size === 0) {
      block.userData.permanentAnchor = true;
    }
  }
}

function applyImpulseToBlock(block, impactPos, forward, strengthMult) {
  unanchorBlock(block);
  const body = block.userData.body;

  const f = new THREE.Vector3(forward.x, 0, forward.z);
  if (f.lengthSq() < 0.0001) f.set(0, 0, -1); else f.normalize();

  const dir = f.multiplyScalar(0.85)
    .add(new THREE.Vector3(0, IMPACT_PUSH_UP, 0))
    .add(new THREE.Vector3(
      (Math.random() - 0.5) * 2 * IMPACT_PUSH_RANDOM,
      (Math.random() - 0.5) * 2 * IMPACT_PUSH_RANDOM,
      (Math.random() - 0.5) * 2 * IMPACT_PUSH_RANDOM,
    ))
    .normalize();

  // Density-aware velocity change — heavier materials resist knockback so metal tips while crate flies.
  // Without this, all materials get the same dv since impulse cancels mass in v = impulse/mass.
  const density = (MATERIALS[block.userData.material] || MATERIALS.brick).density;
  const dv = Math.min(IMPACT_PUSH_STRENGTH * strengthMult / density, IMPACT_PUSH_CAP);
  const mass = body.mass();
  const impulse = dir.multiplyScalar(dv * mass);

  body.applyImpulseAtPoint(
    { x: impulse.x, y: impulse.y, z: impulse.z },
    { x: impactPos.x, y: impactPos.y, z: impactPos.z },
    true
  );

  // MP re-kick: this owned block got an ADDITIONAL shove after its initial unanchor 'u' was already
  // sent. The one-shot 'u' can't carry follow-up impulses, so the peer's copy misses the extra kick and
  // big explosive collapses drift apart. Just FLAG it here — the actual velocity read + network send
  // happen later in _mpSyncOwnedBlocks, at a physics-safe point. Do NOT read/mutate the body or send
  // from this hot topple/AOE path: doing that caused a Rapier "recursive use / unsafe aliasing" freeze.
  if (_mpEnabled && _MP_MODE === 'cause' && !_netRemoteContext
      && block.userData.owned && !block.userData.netNeedsUnanchor
      && !block.userData.netDead && block.userData.netId !== undefined) {
    block.userData.netReKicked = true;
  }
}

// AABB overlap check — finds blocks essentially touching the target. Used by charged punch cascade.
function findTouchingBlocks(targetBlock, threshold = 0.15) {
  const out = [];
  const tp = targetBlock.geometry.parameters;
  const tc = targetBlock.position;
  for (const other of blocks) {
    if (other === targetBlock) continue;
    const op = other.geometry.parameters;
    const dx = Math.abs(other.position.x - tc.x) - (tp.width  + op.width)  / 2;
    const dy = Math.abs(other.position.y - tc.y) - (tp.height + op.height) / 2;
    const dz = Math.abs(other.position.z - tc.z) - (tp.depth  + op.depth)  / 2;
    if (dx <= threshold && dy <= threshold && dz <= threshold) out.push(other);
  }
  return out;
}

// Per-material break-topple config — when a block of this material breaks, surrounding blocks
// inside `radius` get an impulse scaled by `strength`. Defaults: crate=clean break (no shockwave),
// brick=moderate kick, metal=big meaty cascade. Mutable from the admin sliders.
// Blunt collateral topple on break, keyed to the BREAKING block's material (the support graph handles
// the *structural* collapse separately — dependents fall when a supporter is removed). This is the
// "weight" cue: crate breaks clean (feels light), brick nudges its neighbours, metal disturbs a bit
// more (feels heavy). Metal was radius 9 / strength 1.0, which reached across a whole structure and
// slumped the entire thing into rubble from one hit. Pulled back to a LOCAL disturbance (a notch above
// brick) so carving into a metal section crumbles that part, not the whole build, while the support
// graph still drops anything that genuinely loses its footing.
const BREAK_TOPPLE = {
  crate: { radius: 0,   strength: 0   },
  brick: { radius: 4,   strength: 0.6 },
  metal: { radius: 5,   strength: 0.7 },
};

// AOE damage from a chip-storm break. Damages blocks within radius by a fixed amount.
// Breaks via AOE do NOT trigger another AOE — keeps the cascade one level deep so a
// 30-chip volley can't spiral into runaway chain reactions. AOE-broken blocks award
// coins via spawnChunks so high-streak chain destruction is reward-positive.
function _applyChipAOEDamage(impactPos, forward, radius, damage, perCoinScore, tierMult, impulseMult, tier = 'good') {
  const tally = { crate: 0, brick: 0, metal: 0 };  // per-material kill counts (assist destruction soundscape)
  if (radius <= 0 || damage <= 0) return tally;
  let count = 0;
  // Snapshot the iteration set — breakBlock mutates `blocks`, and we don't want to
  // process newly-spawned chunks (none should appear here, but safer to snapshot).
  const candidates = [];
  for (const other of blocks) {
    if (count >= NEARBY_MAX_PARTS) break;
    if (other.position.distanceTo(impactPos) <= radius) {
      candidates.push(other);
      count++;
    }
  }
  for (const other of candidates) {
    if (!blocks.includes(other)) continue;
    other.userData.hitsTaken += damage;
    const ht = other.userData.hitsTaken;
    if (ht >= other.userData.hitsToBreak) {
      if (tally[other.userData.material] !== undefined) tally[other.userData.material]++;
      // Pay out coins for AOE-killed blocks so chain destruction stays reward-positive.
      // Uses the same chip-storm payout path as the originating chip's break.
      spawnChunks(other.position.clone(), forward, other.userData.material, perCoinScore, true, tierMult, impulseMult, other, tier);
      // AOE-killed block: impulse radiates from the AOE source through this block.
      const aoeKill = new THREE.Vector3(other.position.x - impactPos.x, 0.2, other.position.z - impactPos.z);
      if (aoeKill.lengthSq() < 0.0001) aoeKill.set(forward.x, 0.2, forward.z);
      breakBlock(other, aoeKill, 9);
    } else {
      _paintBlockDamage(other, ht);
      const c = other.userData.originalColor;
      const darken = Math.pow(CRACK_DARKEN_PER_HIT, ht);
      other.material.color.setRGB(c.r * darken, c.g * darken, c.b * darken);
    }
  }
  return tally;
}

function applyNearbyTopple(impactPos, forward, exclude, scale = 1, radiusOverride = null) {
  // Two modes:
  //   - radiusOverride passed → caller is doing a fixed shockwave (e.g. chip-storm cinematic);
  //     ignore material config entirely so it stays consistent.
  //   - no override → look up the breaking block's material and use its radius+strength.
  let radius, matStrength;
  if (radiusOverride !== null) {
    radius = radiusOverride;
    matStrength = 1.0;
  } else {
    const cfg = exclude && exclude.userData.material ? BREAK_TOPPLE[exclude.userData.material] : null;
    radius = cfg ? cfg.radius : NEARBY_RADIUS;
    matStrength = cfg ? cfg.strength : 1.0;
  }
  if (radius <= 0 || matStrength <= 0) return;
  let count = 0;
  for (const other of blocks) {
    if (other === exclude) continue;
    if (count >= NEARBY_MAX_PARTS) break;
    if (other.position.distanceTo(impactPos) <= radius) {
      applyImpulseToBlock(other, impactPos, forward, NEARBY_MULT * scale * matStrength);
      count++;
    }
  }
}

// Reused temporaries for interpolated sync (avoids per-frame allocation)
const _interpVec = new THREE.Vector3();
const _interpQuat = new THREE.Quaternion();

function syncBlocks() {
  // Interpolate between pre-step snapshot and current physics state, smoothing the 60→display rate gap.
  const alpha = Math.min(physicsAccum / PHYSICS_DT, 1);
  for (const block of blocks) {
    if (!block.userData.dynamic) continue;
    const ud = block.userData;
    const t = ud.body.translation();
    const r = ud.body.rotation();
    if (ud.prevPos) {
      _interpVec.set(t.x, t.y, t.z);
      block.position.lerpVectors(ud.prevPos, _interpVec, alpha);
      _interpQuat.set(r.x, r.y, r.z, r.w);
      block.quaternion.copy(ud.prevQuat).slerp(_interpQuat, alpha);
    } else {
      block.position.set(t.x, t.y, t.z);
      block.quaternion.set(r.x, r.y, r.z, r.w);
    }
  }
}

// =========================================================
// Targeting — score-based picker + Near highlight + Lock-at-swing-start
// Mirrors PunchTargetingClient.SelectBestTarget + PunchHighlightClient
// =========================================================

let nearTarget = null;
let lockedTarget = null;
let nearHighlight = null;
const NEAR_COLOR = 0xffe055;
const LOCK_COLOR = 0xff5a3a;

// Ray-vs-OBB: where (if anywhere) does the player's forward ray hit this block?
// Transforms the ray into the block's local space so rotated blocks (diagonal beams) work too.
const _rayLocal = new THREE.Ray();
const _localBox = new THREE.Box3();
const _hitLocal = new THREE.Vector3();
const _invMatrix = new THREE.Matrix4();
function rayHitBlock(block, origin, forward) {
  _invMatrix.copy(block.matrixWorld).invert();
  _rayLocal.origin.copy(origin).applyMatrix4(_invMatrix);
  _rayLocal.direction.copy(forward).transformDirection(_invMatrix);
  const p = block.geometry.parameters;
  const hx = p.width / 2, hy = p.height / 2, hz = p.depth / 2;
  _localBox.min.set(-hx, -hy, -hz);
  _localBox.max.set(hx, hy, hz);
  if (!_rayLocal.intersectBox(_localBox, _hitLocal)) return null;
  return _hitLocal.clone().applyMatrix4(block.matrixWorld);
}

// Snap a world-space point to a vertical-cell within this block — Y subdivides into 1m-cells
// (so the player can aim at the top vs bottom of a tall column for climbing), but X and Z
// snap to the block centre so horizontal aim sweeps don't make the beam tip jump between
// left/right halves of a wide block. Used by the energy beam visual.
function cellCenterOnBlock(block, hitPoint) {
  const local = block.worldToLocal(hitPoint.clone());
  const p = block.geometry.parameters;
  const numCellsY = Math.max(1, Math.round(p.height));
  const cellSizeY = p.height / numCellsY;
  const halfY = p.height / 2;
  const idxY = Math.floor((local.y + halfY) / cellSizeY);
  const clampedY = Math.max(0, Math.min(numCellsY - 1, idxY));
  const cellY = -halfY + (clampedY + 0.5) * cellSizeY;
  return block.localToWorld(new THREE.Vector3(0, cellY, 0));
}

function closestPointOnBlock(block, worldPoint) {
  // Oriented-box clamp: world point → local → clamp to half-extents → back to world.
  const local = block.worldToLocal(worldPoint.clone());
  const p = block.geometry.parameters;
  const hx = p.width / 2, hy = p.height / 2, hz = p.depth / 2;
  local.x = Math.max(-hx, Math.min(hx, local.x));
  local.y = Math.max(-hy, Math.min(hy, local.y));
  local.z = Math.max(-hz, Math.min(hz, local.z));
  return block.localToWorld(local);
}

function fistCanTarget(fistIndex, block) {
  if (!block || !blocks.includes(block)) return false;
  const forward = new THREE.Vector3();
  camera.getWorldDirection(forward);
  const aimPoint = rayHitBlock(block, camera.position, forward)
                || closestPointOnBlock(block, camera.position);
  return aimPoint.distanceTo(camera.position) <= gloveConfig(fistIndex).lockRange;
}

function scoreBlock(block, originPos, forward) {
  // Targeting alignment used to be HYBRID: block-center X/Z + ray-entry Y. Block-center X/Z
  // was stable horizontally, but it broke when the player aimed at the EDGE of a wide flat
  // block — the block's center sat far from the aim line, alignment scored poorly, and a
  // smaller block whose center happened to sit on the aim line would win despite being
  // further away. With _rayAimTargeting on, the scoring point follows the actual ray-hit
  // (or closest-on-block when ray misses), so the block you're pointing at always scores
  // with near-perfect alignment regardless of its center offset. Hysteresis still prevents
  // adjacent-block flicker. Toggle off to compare against the legacy hybrid scoring.
  // Range and score use SURFACE distance: a giant block whose center is 25m away can still
  // have its surface 1m from the player, and that block must remain targetable.
  const hit = rayHitBlock(block, originPos, forward);
  const closest = hit || closestPointOnBlock(block, originPos);
  const scoringPoint = _rayAimTargeting
    ? closest.clone()
    : new THREE.Vector3(block.position.x, closest.y, block.position.z);
  const toScoring = scoringPoint.sub(originPos);
  const scoringLen = toScoring.length();
  if (scoringLen < 0.001) return { dist: 0, score: 0, closest, align: 1 };
  const align = forward.dot(toScoring.divideScalar(scoringLen));
  const dist = closest.distanceTo(originPos);
  if (align < HIGHLIGHT_ALIGN_MIN) return { dist, score: Infinity, closest, align };
  // Weight by aim quality. A well-aimed distant target beats a closer off-aim one.
  // (1.05 - align)³ aggressively favors targets near the centre of view; distance only matters as a tiebreaker.
  const m = 1.05 - align;
  const aimPenalty = m * m * m;
  return { dist, score: dist * aimPenalty, closest, align };
}

// Reused color objects for the highlight tween. Constant names kept (YELLOW / RED) for
// minimal churn — they now hold WHITE (off-beat base) and BRIGHT GREEN (downbeat peak).
// Green at peak pops hard against the warm block palette (beige / brown / red brick).
const _C_HL_YELLOW = new THREE.Color(0xffffff);  // off-beat / approach colour
const _C_HL_RED    = new THREE.Color(0xff3a3a);  // on-beat peak colour — vivid red
const _C_HL_TMP    = new THREE.Color();

// Shared "SMASH NOW!" textures. Two variants cross-faded by the beat-phase:
//   • WHITE — visible during the approach (cycle ramp-up). Clean white letters with a
//     dark stroke so the text reads against any block colour.
//   • COLOR — visible only at the peak (downbeat). Yellow letters with red + dark
//     strokes for the "STRIKE!" warning-label moment.
// Canvas is 6:1 aspect (768×128) so the long "SMASH NOW!" has comfortable horizontal
// padding. Per-face plane geometry below matches this aspect to keep the text un-squished.
function _paintSmashNowTex(mode) {
  const W = 768, H = 128;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d');
  ctx.clearRect(0, 0, W, H);
  ctx.font = 'bold 72px "Pixelify Sans", "Press Start 2P", system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';
  if (mode === 'color') {
    // Red letter fill with a thick dark stroke. Red matches the highlight family's
    // downbeat peak colour (_C_HL_RED) so the visual ensemble all reads as one accent
    // at the strike moment.
    ctx.lineWidth = 10; ctx.strokeStyle = '#0a0820';
    ctx.strokeText('SMASH NOW!', W / 2, H / 2);
    ctx.fillStyle = '#ff3a3a';
    ctx.fillText('SMASH NOW!', W / 2, H / 2);
  } else {
    // White approach style: single dark stroke + white fill.
    ctx.lineWidth = 10; ctx.strokeStyle = '#0a0820';
    ctx.strokeText('SMASH NOW!', W / 2, H / 2);
    ctx.fillStyle = '#ffffff';
    ctx.fillText('SMASH NOW!', W / 2, H / 2);
  }
  const tex = new THREE.CanvasTexture(c);
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearFilter;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
let _smashNowTexWhite = null;
let _smashNowTexColor = null;
// Linger-gate state — tracks when the current target highlight became active so the
// SMASH NOW! text only starts appearing after the player has been hovering on a target
// for SMASH_NOW_SILENT_MS, then ramps in over SMASH_NOW_RAMP_MS. Mirrors the audio
// tick's "confident players never hear/see it; only lingerers get the assist" pattern.
let _smashNowLockStartMs = -1;
let _smashNowLastHighlight = null;
function _getSmashNowTexWhite() {
  if (!_smashNowTexWhite) _smashNowTexWhite = _paintSmashNowTex('white');
  return _smashNowTexWhite;
}
function _getSmashNowTexColor() {
  if (!_smashNowTexColor) _smashNowTexColor = _paintSmashNowTex('color');
  return _smashNowTexColor;
}

function setNearTarget(block) {
  if (block === nearTarget) return;
  if (nearHighlight && nearTarget) {
    nearTarget.remove(nearHighlight);
    nearHighlight.traverse(o => {
      if (o.geometry) o.geometry.dispose();
      if (o.material) o.material.dispose();
    });
    nearHighlight = null;
  }
  nearTarget = block;
  if (!block) return;

  nearHighlight = new THREE.Group();

  // Thick edges via LineSegments2 — screen-space pixel width, real thick lines.
  const edges = new THREE.EdgesGeometry(block.geometry);
  const segmentsGeom = new LineSegmentsGeometry();
  segmentsGeom.setPositions(edges.attributes.position.array);
  edges.dispose();
  const lineMat = new LineMaterial({
    color: 0xffe055,
    linewidth: 6, // in screen-space pixels
    transparent: true,
    opacity: 1.0,
    depthTest: true, // back-side edges are occluded by the block itself
  });
  lineMat.resolution.set(window.innerWidth, window.innerHeight);
  const lineSegs = new LineSegments2(segmentsGeom, lineMat);
  lineSegs.renderOrder = 1001;
  lineSegs.scale.setScalar(1.015);
  nearHighlight.add(lineSegs);

  // Inner glow box — additive translucent shell.
  const glowGeo = block.geometry.clone();
  const glowMat = new THREE.MeshBasicMaterial({
    color: 0xffe055,
    transparent: true,
    opacity: 0.20,
    side: THREE.BackSide,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const glowMesh = new THREE.Mesh(glowGeo, glowMat);
  glowMesh.scale.setScalar(1.04);
  glowMesh.renderOrder = 1000;
  nearHighlight.add(glowMesh);

  // Approach face-squares — one thick wireframe square per face of the brick, growing
  // outward from the face centre across the beat phase and reaching the face's exact
  // edges at the downbeat. Replaces an earlier corner-bracket halo; squares solve two
  // problems at once. (1) Perfect touch: dimensions are baked from the face's actual
  // width/height, so the corners land on the silhouette by geometric guarantee instead
  // of relying on a fudge factor. (2) Resolution feel: "growing outward to fill the
  // face" naturally peaks AT the edge rather than imploding to nothing, so the beat
  // moment is a clean visual landing.
  block.geometry.computeBoundingSphere();
  const _brickRadius = (block.geometry.boundingSphere && block.geometry.boundingSphere.radius) || 1.0;
  // Read brick half-extents. Most bricks are BoxGeometry so parameters are present; fall
  // back to boundingBox for any non-Box (or wrapped) geometry.
  let _bw, _bh, _bd;
  if (block.geometry.parameters && block.geometry.parameters.width != null) {
    _bw = block.geometry.parameters.width;
    _bh = block.geometry.parameters.height;
    _bd = block.geometry.parameters.depth;
  } else {
    block.geometry.computeBoundingBox();
    const _bb = block.geometry.boundingBox;
    _bw = _bb.max.x - _bb.min.x;
    _bh = _bb.max.y - _bb.min.y;
    _bd = _bb.max.z - _bb.min.z;
  }
  // Tiny offset along each face's outward normal so the square sits just in front of the
  // face and doesn't z-fight the brick texture. depthTest:true keeps back-facing squares
  // correctly occluded by the brick itself (cleaner than depthTest:false, which would
  // overlay every square at once and look like a mess of crossed lines).
  const _FACE_EPS = 0.005;
  const _FACE_LW = 8;  // screen-space pixels — thicker than the bracket version so the square reads as a deliberate target frame on the face
  const _makeFaceSquare = (faceW, faceH) => {
    const w2 = faceW / 2;
    const h2 = faceH / 2;
    // Unit-cycle square (corners at ±w2, ±h2 in the local XY plane); 4 edges = 8 vertices.
    const pos = [
      -w2,  h2, 0,   w2,  h2, 0,
       w2,  h2, 0,   w2, -h2, 0,
       w2, -h2, 0,  -w2, -h2, 0,
      -w2, -h2, 0,  -w2,  h2, 0,
    ];
    const geo = new LineSegmentsGeometry();
    geo.setPositions(pos);
    const mat = new LineMaterial({
      color: 0xffe055,
      linewidth: _FACE_LW,
      transparent: true,
      opacity: 0.9,
      depthTest: true,  // back faces hidden by the brick — see comment above
    });
    mat.resolution.set(window.innerWidth, window.innerHeight);
    const m = new LineSegments2(geo, mat);
    m.renderOrder = 1003;
    return m;
  };
  // Per-face SMASH NOW! text — TWO coplanar plane meshes: a WHITE variant that ramps up
  // during the approach, and a COLOUR variant that flashes at the beat peak. Their
  // opacities sum to a single visibility curve, but the colour mesh has a sharper spike
  // (narrow window around the downbeat) so most of the cycle reads white and the actual
  // strike moment reads as the yellow/red warning fill. Canvas aspect is 6:1; plane
  // height = width/6. Width is clamped by both face dimensions so the text fits even on
  // narrow or short faces.
  const _smashTexWhite = _getSmashNowTexWhite();
  const _smashTexColor = _getSmashNowTexColor();
  const _TEXT_EPS = _FACE_EPS * 1.4;  // sit a hair above the wireframe square to avoid coplanar fight
  const _TEXT_ASPECT = 6;             // canvas is 768×128
  const _makeTextMesh = (tex, faceW, faceH, renderOrder) => {
    const textW = Math.min(faceW * 0.85, faceH * _TEXT_ASPECT * 0.85);
    const textH = textW / _TEXT_ASPECT;
    const geo = new THREE.PlaneGeometry(textW, textH);
    const mat = new THREE.MeshBasicMaterial({
      map: tex,
      transparent: true,
      opacity: 0,           // updated per frame
      depthTest: true,
      side: THREE.DoubleSide,
    });
    const m = new THREE.Mesh(geo, mat);
    m.renderOrder = renderOrder;
    return m;
  };
  const _makeFaceText = (faceW, faceH) => ({
    white: _makeTextMesh(_smashTexWhite, faceW, faceH, 1005),
    color: _makeTextMesh(_smashTexColor, faceW, faceH, 1006),  // colour on top so peak reads cleanly
  });
  // Build all 6 face squares + their matched SMASH NOW text planes. Each lives in the
  // brick's local frame so it follows any brick rotation/translation naturally — no
  // per-frame billboard math needed.
  const _faceSquares = [];
  const _faceTextsWhite = [];
  const _faceTextsColor = [];
  const _addFaceSquare = (faceW, faceH, pos, rot) => {
    const sq = _makeFaceSquare(faceW, faceH);
    const txt = _makeFaceText(faceW, faceH);
    sq.position.copy(pos);
    // Both text meshes share the face's pos / rot. They're coplanar — render order on
    // the colour mesh wins the depth tie-break at the same depth.
    txt.white.position.copy(pos);
    txt.color.position.copy(pos);
    if (rot) {
      sq.rotation.copy(rot);
      txt.white.rotation.copy(rot);
      txt.color.rotation.copy(rot);
    }
    _faceSquares.push(sq);
    _faceTextsWhite.push(txt.white);
    _faceTextsColor.push(txt.color);
  };
  const _v = (x, y, z) => new THREE.Vector3(x, y, z);
  const _e = (x, y, z) => new THREE.Euler(x, y, z);
  // +Z, -Z faces: local XY plane already faces ±Z, so width=brick width, height=brick height.
  _addFaceSquare(_bw, _bh, _v(0, 0,  _bd / 2 + _FACE_EPS), null);
  _addFaceSquare(_bw, _bh, _v(0, 0, -_bd / 2 - _FACE_EPS), _e(0, Math.PI, 0));
  // ±X faces: rotate around Y so the local XY plane faces ±X. Local-X maps to world-Z,
  // local-Y maps to world-Y, so the local width is the brick's depth.
  _addFaceSquare(_bd, _bh, _v( _bw / 2 + _FACE_EPS, 0, 0), _e(0,  Math.PI / 2, 0));
  _addFaceSquare(_bd, _bh, _v(-_bw / 2 - _FACE_EPS, 0, 0), _e(0, -Math.PI / 2, 0));
  // ±Y faces: rotate around X so the local XY plane faces ±Y. Local-X maps to world-X,
  // local-Y maps to world-Z (after rotation), so width=brick width, height=brick depth.
  _addFaceSquare(_bw, _bd, _v(0,  _bh / 2 + _FACE_EPS, 0), _e(-Math.PI / 2, 0, 0));
  _addFaceSquare(_bw, _bd, _v(0, -_bh / 2 - _FACE_EPS, 0), _e( Math.PI / 2, 0, 0));
  for (const sq of _faceSquares) {
    sq.visible = _approachHaloOn;
    nearHighlight.add(sq);
  }
  for (const txt of _faceTextsWhite) {
    txt.visible = _approachHaloOn;
    nearHighlight.add(txt);
  }
  for (const txt of _faceTextsColor) {
    txt.visible = _approachHaloOn;
    nearHighlight.add(txt);
  }

  nearHighlight.userData.lineMat = lineMat;
  nearHighlight.userData.glowMat = glowMat;
  nearHighlight.userData.faceSquares = _faceSquares;
  nearHighlight.userData.faceTextsWhite = _faceTextsWhite;
  nearHighlight.userData.faceTextsColor = _faceTextsColor;
  nearHighlight.userData.brickRadius = _brickRadius;
  block.add(nearHighlight);
}

// Highlight outline thickness — base + boost-on-beat. Linewidth is screen-space pixels;
// keeping the peak under ~14 avoids the LineMaterial's anti-aliasing fringe getting weird.
const HIGHLIGHT_LINEWIDTH_BASE = 4;
const HIGHLIGHT_LINEWIDTH_BOOST = 8;
// Per-beat pulse, cos² shape. Still used internally by the music-engine's per-beat
// triggers / block-pulse-fx that need every-beat granularity, but the highlight system
// has moved off this onto the slow pulse below.
function _beatTargetPulse() {
  const elapsed = _beatElapsed();
  if (elapsed !== null) {
    const beatPhase = (elapsed % MUSIC_BEAT) / MUSIC_BEAT;
    const c = 0.5 + 0.5 * Math.cos(beatPhase * Math.PI * 2);
    return c * c;
  }
  return 0.5 + 0.5 * Math.sin(performance.now() / 1000 * 4);
}
// Slow pulse on a 2-beat cycle with a cos⁴ shape — sharper peak than cos², most of the
// cycle near zero. Shared by the block outline + glow + face squares (via the highlight
// system), the energy beams + contact ring (target locked), and the charge reticle
// (target-less spray mode). One source of truth so every yellow↔red cue tweens together
// at the downbeat cadence the playtest pass settled on (2026-05-16). Tune the exponent
// at the bottom of the function if the peak feels too narrow or too soft.
function _slowBeatPulse() {
  const elapsed = _beatElapsed();
  if (elapsed !== null) {
    const period = MUSIC_BEAT * 2;
    const phase = (elapsed % period) / period;
    const c = 0.5 + 0.5 * Math.cos(phase * Math.PI * 2);
    const c2 = c * c;
    return c2 * c2;
  }
  return 0.5 + 0.5 * Math.sin(performance.now() / 1000 * 2);
}
function updateHighlightPulse() {
  if (!nearHighlight || !nearHighlight.userData) return;
  // Highlight stays visible during charge now — under the release-lock model, the highlight
  // IS the contract for what the chip-storm will fire at, plus the beat-locked pulse gives
  // the player the rhythm cue for WHEN to release. Beam still hides during charge (it's a
  // swing-time indicator, not relevant here) but the block outline pulse stays.
  const lineMat = nearHighlight.userData.lineMat;
  const glowMat = nearHighlight.userData.glowMat;
  // Beat-synced pulse from the shared helper — same value the beams + contact ring read,
  // so the outline, beams, and ring all tween yellow↔red and pulse together.
  // Shared slow pulse (cos⁴ over 2-beat cycle) drives the outline/glow + face-square
  // color, the beams/contact ring color, and the reticle color from one helper.
  const pulse = _slowBeatPulse();
  // bracketPhase (linear 0→1 over the same 2-beat cycle) is used below for the face
  // squares' growth animation, which needs continuous travel rather than a pulse curve.
  let bracketPhase = 0;
  const bracketElapsed = _beatElapsed();
  if (bracketElapsed !== null) {
    const period = MUSIC_BEAT * 2;
    bracketPhase = (bracketElapsed % period) / period;
  } else {
    bracketPhase = (performance.now() / 1000 / 1.0) % 1;
  }
  // Color tween: yellow base off-beat, red snap on the downbeat. Drives line color, line
  // opacity, line thickness, glow color/opacity, and (via _C_HL_TMP copy) the brackets +
  // overlay colors, so the whole highlight ensemble breathes as one coherent half-speed cue.
  _C_HL_TMP.copy(_C_HL_YELLOW).lerp(_C_HL_RED, pulse);
  // Storm-max override: same neon-rainbow treatment as the beams. Drives the block outline
  // color, the glow shell color, and (because the contact ring + edge line both read from
  // _C_HL_TMP) the targeting visuals as a coherent rainbow cue at god-mode.
  if (_stormMaxIntensity > 0) {
    const hue = ((performance.now() / 1000 * 75) % 360) / 360;  // slightly slower than beams for layered shimmer
    _stormMaxTmpColor.setHSL(hue, 1, 0.6);
    _C_HL_TMP.lerp(_stormMaxTmpColor, _stormMaxIntensity);
  }
  const lockBoost = lockedTarget ? 1.5 : 1.0;

  if (lineMat) {
    lineMat.color.copy(_C_HL_TMP);
    lineMat.opacity = 0.75 + 0.25 * pulse;
    lineMat.linewidth = HIGHLIGHT_LINEWIDTH_BASE + pulse * HIGHLIGHT_LINEWIDTH_BOOST;
  }
  if (glowMat) {
    glowMat.color.copy(_C_HL_TMP);
    glowMat.opacity = 0.18 * pulse * lockBoost;
  }

  // Face squares — one wireframe square per brick face, growing outward from the face
  // centre to the face edges across the 2-beat downbeat cycle. The downbeat impact moment
  // lands at the perimeter, framing the brick. Lives in the brick's local frame and
  // follows brick transforms naturally; no billboard math needed.
  const faceSquares = nearHighlight.userData.faceSquares;
  const faceTextsWhite = nearHighlight.userData.faceTextsWhite;
  const faceTextsColor = nearHighlight.userData.faceTextsColor;
  if (faceSquares && faceSquares.length) {
    // Assist mode: hide the converging face-square + "SMASH NOW!" timing cue entirely. The player isn't
    // timing the hit (the game auto-fires on the beat), so a "release on the downbeat" cue is misleading.
    // The outline/glow pulse + reticle still mark the target. (The else branch below hides them each frame.)
    if (_approachHaloOn && !_easyMode) {
      // Square travel is DELAYED until the second half of the 2-beat cycle so players
      // don't reactively tap on the first sign of motion. From phase 0 → 0.5 the square
      // sits dormant at min scale / low opacity; from phase 0.5 → 1.0 (the back beat
      // into the downbeat) it grows out to the face edges. Compresses the visible
      // approach to one beat instead of two — tighter window, less early-firing.
      const SQUARE_MIN_T = 0.05;
      const squareApproach = Math.max(0, (bracketPhase - 0.5) * 2);  // 0 across first half, 0→1 across second half
      const t = SQUARE_MIN_T + (1.0 - SQUARE_MIN_T) * squareApproach;
      const opacity = 0.10 + 0.90 * squareApproach * squareApproach;
      for (const sq of faceSquares) {
        sq.visible = true;
        // Scale ONLY in the square's local XY plane (its in-face dimensions). Local Z is
        // the face normal and must stay 1 so the square doesn't lift off / sink into the
        // face as it grows.
        sq.scale.set(t, t, 1);
        sq.material.color.copy(_C_HL_TMP);
        sq.material.opacity = opacity;
        sq.material.resolution.set(window.innerWidth, window.innerHeight);
      }
      // SMASH NOW! text — TWO layers cross-faded by the beat phase.
      //
      // Base visibility uses a LATE-RAMPING curve (cos⁴ raised to TEXT_CURVE_POW > 1) so
      // the text stays mostly invisible across the cycle and ramps up quickly in the last
      // moment before the peak. No phase lead — the peak is centred on the downbeat.
      //
      // The two layers split that visibility:
      //   • COLOUR (yellow/red warning fill): a narrow spike at the peak (TEXT_PEAK_POW).
      //     Only visible right at the beat moment.
      //   • WHITE (clean approach style): the remainder of the base visibility. Visible
      //     during the ramp-up and any post-peak tail where the colour spike has faded
      //     but the base curve is still non-zero.
      // Together they sum to the base visibility, so the total opacity is constant but
      // the colour swap reads as "text was white, now it's the warning colour" at the beat.
      if (faceTextsWhite && faceTextsWhite.length) {
        const TEXT_CURVE_POW = 3.5;    // base visibility — higher power = text stays near zero
                                        //   for most of the cycle and only ramps up in the last
                                        //   moment before the downbeat
        const TEXT_PEAK_POW  = 8;      // colour spike — narrow window around the peak only
        const SMASH_NOW_SILENT_MS = 800;   // no text at all for the first 0.8s of lock
        const SMASH_NOW_RAMP_MS   = 1500;  // then linger-gate ramps 0→1 over 1.5s
        // Reset the lock-start clock when the active highlight changes (new target or
        // re-acquire after losing). nearHighlight is a fresh THREE.Group per setNearTarget,
        // so identity comparison is a reliable change-detect.
        if (nearHighlight !== _smashNowLastHighlight) {
          _smashNowLockStartMs = performance.now();
          _smashNowLastHighlight = nearHighlight;
        }
        const sinceLockMs = performance.now() - _smashNowLockStartMs;
        let lingerGate = 0;
        if (sinceLockMs >= SMASH_NOW_SILENT_MS) {
          lingerGate = Math.min(1, (sinceLockMs - SMASH_NOW_SILENT_MS) / SMASH_NOW_RAMP_MS);
        }
        let cos4;
        const textElapsed = _beatElapsed();
        if (textElapsed !== null) {
          const period = MUSIC_BEAT * 2;
          const lp = ((textElapsed % period) + period) % period / period;
          const lc = 0.5 + 0.5 * Math.cos(lp * Math.PI * 2);
          const lc2 = lc * lc;
          cos4 = lc2 * lc2;
        } else {
          cos4 = pulse;  // wall-clock fallback
        }
        const baseOpacity = Math.pow(cos4, TEXT_CURVE_POW) * lingerGate;
        const colorBlend  = Math.pow(cos4, TEXT_PEAK_POW);
        const whiteOpacity = baseOpacity * (1 - colorBlend);
        const colorOpacity = baseOpacity * colorBlend;
        for (const txt of faceTextsWhite) {
          txt.visible = true;
          txt.material.opacity = whiteOpacity;
        }
        for (const txt of faceTextsColor) {
          txt.visible = true;
          txt.material.opacity = colorOpacity;
        }
      }
    } else {
      for (const sq of faceSquares) sq.visible = false;
      if (faceTextsWhite) for (const txt of faceTextsWhite) txt.visible = false;
      if (faceTextsColor) for (const txt of faceTextsColor) txt.visible = false;
    }
  }
}

function updateNear() {
  // While locked, freeze the near target so the player doesn't get flicker mid-swing.
  if (lockedTarget) return;

  const origin = camera.position;
  const forward = new THREE.Vector3();
  camera.getWorldDirection(forward);
  const targetRange = maxEquippedTargetRange();

  // Aim assist OFF — precision mode. Only a block the crosshair ray actually pierces can
  // be a target; among those the nearest surface hit wins (you can't target something
  // behind another block). No cone, no aim-quality scoring, no auto-face (gated at the
  // firePunch callsite). The player aims everything by hand.
  if (!_aimAssist) {
    let precisionBlock = null;
    let nearestHit = Infinity;
    for (const block of blocks) {
      const hit = rayHitBlock(block, origin, forward);
      if (!hit) continue;
      const d = hit.distanceTo(origin);
      if (d > targetRange) continue;
      if (d < nearestHit) { nearestHit = d; precisionBlock = block; }
    }
    setNearTarget(precisionBlock);
    return;
  }

  let bestBlock = null;
  let bestScore = Infinity;
  for (const block of blocks) {
    const data = scoreBlock(block, origin, forward);
    if (data.dist > targetRange) continue;
    if (data.score < bestScore) {
      bestBlock = block;
      bestScore = data.score;
    }
  }

  let curScore = Infinity;
  if (nearTarget && blocks.includes(nearTarget)) {
    const data = scoreBlock(nearTarget, origin, forward);
    if (data.dist <= targetRange) curScore = data.score;
  }

  // Hysteresis (relative): switch when the new candidate's score is meaningfully better — 30% lower.
  // Relative scaling keeps the rule sane regardless of the raw score magnitude.
  let shouldSwitch = false;
  if (!nearTarget || curScore === Infinity) shouldSwitch = true;
  else if (bestBlock !== nearTarget && bestScore < curScore * 0.7) shouldSwitch = true;

  if (shouldSwitch) setNearTarget(bestBlock);
}

function lockNearAsTarget() {
  if (!nearTarget) return;
  lockedTarget = nearTarget;
  // Color is animated by updateHighlightPulse; lock state just brightens the glow via lockBoost.
}

function clearLock() {
  lockedTarget = null;
}

// =========================================================
// Structure helpers
// =========================================================

// Maps a primary structure material to a "harder" tier — for slabs/caps that should be sturdier than the base.
function strongerMaterial(m) {
  if (m === 'crate') return 'brick';
  if (m === 'brick') return 'metal';
  return 'metal';
}

function tower(x, z, count, blockSize, material) {
  const half = blockSize / 2;
  for (let i = 0; i < count; i++) {
    addBlock({
      size: [blockSize, blockSize, blockSize],
      position: [x, half + i * blockSize, z],
      material,
    });
  }
}

function brickWall(x, z, cols, rows, blockSize, material, alongAxis = 'x') {
  const half = blockSize / 2;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const offset = (c - (cols - 1) / 2) * blockSize;
      const px = alongAxis === 'x' ? x + offset : x;
      const pz = alongAxis === 'z' ? z + offset : z;
      addBlock({
        size: [blockSize, blockSize, blockSize],
        position: [px, half + r * blockSize, pz],
        material,
      });
    }
  }
}

function pyramid(x, z, base, blockSize, material) {
  const half = blockSize / 2;
  for (let layer = 0; layer < base; layer++) {
    const rowSize = base - layer;
    for (let i = 0; i < rowSize; i++) {
      for (let j = 0; j < rowSize; j++) {
        const ox = (i - (rowSize - 1) / 2) * blockSize;
        const oz = (j - (rowSize - 1) / 2) * blockSize;
        addBlock({
          size: [blockSize, blockSize, blockSize],
          position: [x + ox, half + layer * blockSize, z + oz],
          material,
        });
      }
    }
  }
}

// Jenga-style alternating-direction tower. Each layer = 3 long blocks side-by-side,
// next layer rotated 90°, so each block above rests on three crossing blocks below.
function jengaTower(x, z, layers, blockLen, blockThick, material) {
  const half = blockThick / 2;
  for (let l = 0; l < layers; l++) {
    const y = half + l * blockThick;
    const horiz = l % 2 === 0;
    for (let i = 0; i < 3; i++) {
      const off = (i - 1) * blockThick;
      const position = horiz ? [x, y, z + off] : [x + off, y, z];
      const size = horiz
        ? [blockLen, blockThick, blockThick]
        : [blockThick, blockThick, blockLen];
      addBlock({ size, position, material });
    }
  }
}

// Tiered shelf — pairs of short pillars with planks across them, stacked vertically.
// Knocking a pillar at any tier drops everything above it.
function shelf(x, z, tiers, material) {
  const PILLAR_W = 0.8, PILLAR_H = 1.0, PILLAR_DX = 2.0;
  const PLANK_W = 5.0, PLANK_H = 0.3, PLANK_D = 1.5;
  const tierH = PILLAR_H + PLANK_H;
  for (let t = 0; t < tiers; t++) {
    const baseY = t * tierH;
    addBlock({ size: [PILLAR_W, PILLAR_H, PILLAR_W], position: [x - PILLAR_DX, baseY + PILLAR_H / 2, z], material });
    addBlock({ size: [PILLAR_W, PILLAR_H, PILLAR_W], position: [x + PILLAR_DX, baseY + PILLAR_H / 2, z], material });
    addBlock({ size: [PLANK_W, PLANK_H, PLANK_D], position: [x, baseY + PILLAR_H + PLANK_H / 2, z], material });
  }
}

function bridge(x, z, span, blocksPerPillar, blockSize, material) {
  const halfSpan = span / 2;
  const halfBlock = blockSize / 2;
  for (let i = 0; i < blocksPerPillar; i++) {
    addBlock({
      size: [blockSize, blockSize, blockSize],
      position: [x - halfSpan, halfBlock + i * blockSize, z],
      material,
    });
    addBlock({
      size: [blockSize, blockSize, blockSize],
      position: [x + halfSpan, halfBlock + i * blockSize, z],
      material,
    });
  }
  // Slab on top — slightly larger than span so it overhangs the pillars; bumped to a stronger material.
  addBlock({
    size: [span + blockSize, blockSize * 0.7, blockSize * 1.2],
    position: [x, blocksPerPillar * blockSize + halfBlock * 0.7, z],
    material: strongerMaterial(material),
  });
}

// =========================================================
// Building-style helpers for the materials demo room
// =========================================================

// Loose pile of crates of varied size in a corner — fast tier-1 demolition test.
function crateStack(x, z) {
  // 2×2 base
  for (let i = 0; i < 2; i++) for (let j = 0; j < 2; j++) {
    addBlock({ size: [1.0, 1.0, 1.0], position: [x + (i - 0.5), 0.5, z + (j - 0.5)], material: 'crate' });
  }
  // Mid layer — 2 offset crates resting across the base
  addBlock({ size: [1.0, 1.0, 1.0], position: [x - 0.4, 1.5, z + 0.0], material: 'crate' });
  addBlock({ size: [0.8, 0.8, 0.8], position: [x + 0.4, 1.4, z + 0.4], material: 'crate' });
  // Top — single crate
  addBlock({ size: [0.7, 0.7, 0.7], position: [x - 0.1, 2.35, z - 0.1], material: 'crate' });
}

// Brick wall reinforced with metal blocks at structural corners + center top.
function reinforcedWall(x, z, cols, rows) {
  const blockSize = 1.0, half = 0.5;
  const reinforced = new Set([
    `0,0`, `${cols - 1},0`,                                   // bottom corners
    `0,${rows - 1}`, `${cols - 1},${rows - 1}`,               // top corners
    `${Math.floor(cols / 2)},${rows - 1}`,                    // top centre
  ]);
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const offset = (c - (cols - 1) / 2) * blockSize;
      const isMetal = reinforced.has(`${c},${r}`);
      addBlock({
        size: [blockSize, blockSize, blockSize],
        position: [x + offset, half + r * blockSize, z],
        material: isMetal ? 'metal' : 'brick',
      });
    }
  }
}

// Hollow vault chamber with loose crates inside — front face left open so the goodies are visible.
function vaultChamber(x, z) {
  // 3×3×3 hollow cube of metal blocks. Front face (smallest z toward player) is open.
  for (let i = 0; i < 3; i++) {
    for (let j = 0; j < 3; j++) {
      for (let k = 0; k < 3; k++) {
        const onShell = i === 0 || i === 2 || j === 0 || j === 2 || k === 0 || k === 2;
        if (!onShell) continue;
        const isOpenFace = k === 2 && i > 0 && i < 2 && j > 0 && j < 2;  // skip middle of front face
        if (isOpenFace) continue;
        addBlock({
          size: [1.0, 1.0, 1.0],
          position: [x + (i - 1), 0.5 + j, z + (k - 1)],
          material: 'metal',
        });
      }
    }
  }
  // Loose crates lying inside — visible through the open face
  addBlock({ size: [0.6, 0.6, 0.6], position: [x - 0.3, 1.30, z + 0.0], material: 'crate' });
  addBlock({ size: [0.6, 0.6, 0.6], position: [x + 0.3, 1.30, z + 0.0], material: 'crate' });
  addBlock({ size: [0.5, 0.5, 0.5], position: [x + 0.0, 1.85, z - 0.3], material: 'crate' });
}

// Tall brick column capped with a single metal block. Knocking the bottom drops the cap satisfyingly.
function brickTower(x, z, height) {
  for (let i = 0; i < height; i++) {
    addBlock({ size: [1.0, 1.0, 1.0], position: [x, 0.5 + i * 1.0, z], material: 'brick' });
  }
  addBlock({ size: [1.4, 0.6, 1.4], position: [x, height + 0.3, z], material: 'metal' });
}

// Two brick pillars + heavy metal crossbeam = portal / arch gate, all three materials in dialogue.
function gate(x, z) {
  const W = 4, H = 4;
  addBlock({ size: [1.0, H, 1.0], position: [x - W / 2, H / 2, z], material: 'brick' });
  addBlock({ size: [1.0, H, 1.0], position: [x + W / 2, H / 2, z], material: 'brick' });
  addBlock({ size: [W + 1.0, 0.7, 1.0], position: [x, H + 0.35, z], material: 'metal' });
}

// Mixed-material rubble — a small pile of mixed scrap on the ground.
function rubbleHeap(x, z) {
  addBlock({ size: [1.5, 0.8, 1.5], position: [x, 0.4, z], material: 'brick' });
  addBlock({ size: [0.8, 0.8, 0.8], position: [x + 0.7, 0.4, z + 0.5], material: 'crate' });
  addBlock({ size: [0.6, 0.6, 0.6], position: [x - 0.6, 0.3, z + 0.3], material: 'crate' });
  addBlock({ size: [0.7, 0.5, 0.7], position: [x + 0.2, 1.05, z + 0.0], material: 'metal' });
  addBlock({ size: [0.5, 0.5, 0.5], position: [x - 0.4, 0.25, z - 0.5], material: 'crate' });
}

// =========================================================
// Layout — wrapped in a function so we can rebuild it on level retry.
// =========================================================
function buildPracticeLayout(mode = 'spring') {
  applyScene('outdoor-grass');
  const material = mode === 'rocket' ? 'brick' : 'crate';
  addBlock({ size: [1.1, 1.1, 1.1], position: [0, 0.55, -6.5], material });
  addBlock({ size: [1.2, 1.2, 1.2], position: [-4.8, 0.60, -10.5], material: 'brick' });
  addBlock({ size: [1.2, 1.2, 1.2], position: [4.8, 0.60, -10.5], material: 'brick' });
  if (mode === 'dual') {
    addBlock({ size: [1.0, 1.0, 1.0], position: [-2.6, 0.50, -15.0], material: 'crate' });
    addBlock({ size: [1.0, 1.0, 1.0], position: [2.6, 0.50, -16.5], material: 'metal' });
  } else if (mode === 'rocket') {
    addBlock({ size: [1.0, 2.0, 1.0], position: [0, 1.0, -16.0], material: 'brick' });
  } else {
    addBlock({ size: [1.0, 1.0, 1.0], position: [0, 0.50, -16.0], material: 'metal' });
  }
}

function buildSandboxLayout() {
// Forward arc — what player sees first (camera looks toward -Z)
pyramid(-8, -10, 4, 1.0, 'brick');                        // 30 blocks, 4m base
tower(8, -10, 7, 1.2, 'brick');                           // tall column
brickWall(0, -18, 6, 4, 1.0, 'brick', 'x');               // 6×4 brick wall
brickWall(-16, -10, 4, 4, 1.0, 'brick', 'z');             // perpendicular wall

// Mid range — bridge to the right
bridge(13, 4, 4, 3, 1.0, 'brick');

// Behind / left — pillar forest of varied heights
addBlock({ size: [0.8, 5.0, 0.8], position: [-10, 2.50,  4], material: 'brick' });
addBlock({ size: [0.8, 3.5, 0.8], position: [-12, 1.75,  6], material: 'brick' });
addBlock({ size: [0.8, 6.0, 0.8], position: [ -9, 3.00,  9], material: 'brick' });
addBlock({ size: [0.8, 4.0, 0.8], position: [-13, 2.00, 11], material: 'brick' });
addBlock({ size: [0.8, 5.5, 0.8], position: [-11, 2.75, 14], material: 'brick' });

// Behind player — scattered "debris field" of small blocks
addBlock({ size: [0.7, 0.7, 0.7], position: [-3, 0.35, 10], material: 'crate' });
addBlock({ size: [0.5, 0.5, 0.5], position: [ 3, 0.25, 12], material: 'crate' });
addBlock({ size: [1.0, 0.5, 2.0], position: [ 5, 0.25, 15], material: 'crate' });
addBlock({ size: [2.0, 1.0, 1.0], position: [-2, 0.50, 17], material: 'brick' });
addBlock({ size: [0.8, 0.8, 0.8], position: [ 0, 0.40, 13], material: 'crate' });

// Right — dense cluster of small cubes packed together
for (let i = 0; i < 3; i++) {
  for (let j = 0; j < 3; j++) {
    for (let k = 0; k < 2; k++) {
      addBlock({
        size: [0.7, 0.7, 0.7],
        position: [17 + (i - 1) * 0.72, 0.35 + j * 0.72, -2 + (k - 0.5) * 0.72],
        material: 'crate',
      });
    }
  }
}

// =========================================================
// Second room (z ∈ [-80, -25]) — through the doorway. HUGE pyramid + courtyard frame.
// =========================================================
pyramid(0, -52, 9, 1.0, 'metal'); // 285 blocks, 9m base, 9m tall

// Courtyard pillars — 4 corners around the pyramid
const pillarPositions = [
  [-9.5, -43.5], // NW (front-left)
  [+9.5, -43.5], // NE (front-right)
  [-9.5, -60.5], // SW (back-left)
  [+9.5, -60.5], // SE (back-right)
];
for (const [px, pz] of pillarPositions) {
  addBlock({ size: [1.0, 9.0, 1.0], position: [px, 4.5, pz], material: 'metal' });
}

// Suspended cuboid beam between two points in the xz plane (y is the height).
function addBeam(fromX, fromZ, toX, toZ, y, thickness, material) {
  const dx = toX - fromX, dz = toZ - fromZ;
  const length = Math.hypot(dx, dz);
  const angle = Math.atan2(-dz, dx); // rotation around Y so the box's local +X aligns with from→to
  addBlock({
    size: [length, thickness, thickness],
    position: [(fromX + toX) / 2, y, (fromZ + toZ) / 2],
    material, rotationY: angle,
  });
}

// Diagonal beams from each pillar to the corresponding pyramid base corner
const BEAM_Y_LOW = 5.2;
const BEAM_Y_HIGH = 8.4;
const PYR_HALF = 4.5; // pyramid base half-extent
addBeam(-9.5, -43.5, -PYR_HALF, -47.5, BEAM_Y_LOW, 0.6, 'metal'); // NW pillar → pyr NW corner
addBeam( 9.5, -43.5,  PYR_HALF, -47.5, BEAM_Y_LOW, 0.6, 'metal'); // NE
addBeam(-9.5, -60.5, -PYR_HALF, -56.5, BEAM_Y_LOW, 0.6, 'metal'); // SW
addBeam( 9.5, -60.5,  PYR_HALF, -56.5, BEAM_Y_LOW, 0.6, 'metal'); // SE

// Perimeter beams between adjacent pillars at the top — forms a frame above the courtyard
addBlock({ size: [19, 0.7, 0.7], position: [0, BEAM_Y_HIGH, -43.5], material: 'metal' });
addBlock({ size: [19, 0.7, 0.7], position: [0, BEAM_Y_HIGH, -60.5], material: 'metal' });
addBlock({ size: [0.7, 0.7, 17], position: [-9.5, BEAM_Y_HIGH, -52], material: 'metal' });
addBlock({ size: [0.7, 0.7, 17], position: [ 9.5, BEAM_Y_HIGH, -52], material: 'metal' });

// Wide back-wall stack
brickWall(0, -77, 8, 5, 1.0, 'metal', 'x');

// =========================================================
// Third room (z ∈ [-135, -80]) — stacked-not-joined structures to test the support graph cascade.
// =========================================================

// Jenga tower — multi-supporter test. Each upper block crosses 3 below; needs all 3 gone to fall.
jengaTower(-12, -90, 9, 3.0, 0.5, 'brick');

// Tiered shelf — single-chain cascade. Knock any pillar, everything above drops.
shelf(0, -90, 4, 'brick');

// Cantilever — single-supporter test. One cube holds a beam jutting out.
addBlock({ size: [1.5, 1.0, 1.5], position: [12, 0.5, -90], material: 'brick' });
addBlock({ size: [4.0, 0.4, 1.0], position: [13.5, 1.2, -90], material: 'brick' });

// Tall narrow tower — straight vertical cascade.
tower(-15, -110, 14, 0.9, 'brick');

// Stacked-pillar plus crossbeam test — two separate towers holding a long beam between them.
tower(-5, -108, 5, 1.0, 'brick');
tower(5, -108, 5, 1.0, 'brick');
addBlock({ size: [12, 0.6, 1.2], position: [0, 5.3, -108], material: 'metal' });
// And a smaller block sitting on top of the crossbeam, mid-span.
addBlock({ size: [1.0, 1.0, 1.0], position: [0, 6.1, -108], material: 'brick' });

// Teetering box pile — varied-size cascade with offset stacking.
addBlock({ size: [2.2, 1.2, 2.2], position: [12, 0.6, -118], material: 'brick' });
addBlock({ size: [1.6, 1.0, 1.6], position: [12.4, 1.7, -118.3], material: 'brick' });
addBlock({ size: [1.2, 0.8, 1.2], position: [11.7, 2.6, -117.7], material: 'brick' });
addBlock({ size: [0.7, 0.7, 0.7], position: [11.5, 3.35, -117.5], material: 'crate' });

// Two parallel tall towers with a gap — visual contrast for cascade locality.
tower(-10, -125, 9, 0.9, 'brick');
tower(-6, -125, 9, 0.9, 'brick');

// Step pyramid in the back — small enough to demolish but big enough to feel cascading.
pyramid(8, -128, 5, 1.0, 'brick');

// =========================================================
// Fourth room (z ∈ [-190, -135]) — materials demo: showcase of building helpers using crate/brick/metal.
// =========================================================

// Front-left: a casual pile of crates — quick tier-1 demolition test.
crateStack(-13, -145);

// Front-centre: brick wall with metal corners + centre-top cap — readable mixed-material structure.
reinforcedWall(0, -148, 7, 4);

// Front-right: vault chamber with crates inside — tier-3 effort, tier-1 reward.
vaultChamber(13, -148);

// Mid-left: tall brick column capped by a single metal block — knock the bottom, the cap drops dramatically.
brickTower(-13, -163, 8);

// Mid-right: another vault chamber, set further back so player can flank it.
brickTower(13, -163, 6);

// Back-centre: arch gate — two brick pillars joined by a heavy metal crossbeam.
gate(0, -176);

// Back-corners: small mixed rubble heaps for atmosphere.
rubbleHeap(-15, -178);
rubbleHeap(15, -178);
} // end buildSandboxLayout

// =========================================================
// Level 1 — first proper level. Teaches the core loop: tap on beat → smash → coin.
// Pure crate, every block individually placed on the floor with at least 2m of space — nothing
// touches anything else, so breaks don't cascade or topple neighbours. Each smash is its own
// rhythmic event. ~24 crates wrapping the player in a 360° arc. No stacks, no brick, no metal.
// BASE_YIELD = block count → 1★ 0.5× · 2★ 1.0× · 3★ 1.5× (auto-derived).
// =========================================================
function buildLevel1Layout() {
  // Forward-receding line — 21 crates marching down the corridor at x=0. Player walks/lunges
  // forward through the parade, tapping each on the beat. Like a rhythm-game lane in 3D.
  // No stacking, ~1m gap between cubes — clean breaks, no neighbour topple.
  // Alternating short (2m) / tall (4m) solid cuboids — every other beat the silhouette changes.
  // Halved 2026-05-20 to match the halved duration (sections 4→2 on 2026-05-16) so the goal
  // threshold (derived from base yield over standing blocks) reflects what's reachable.
  const FOOTPRINT = 1.5;        // x-depth and z-depth of each slab
  const SHORT_H = 2;            // short-block height
  const TALL_H = 4;             // tall-block height
  const COUNT = 21;
  const SPACING = 2.5;          // 1m gap edge-to-edge
  const START_Z = -3;           // first block a few metres in front of spawn (player at z=6)
  for (let i = 0; i < COUNT; i++) {
    // Alternate short/tall so each punch has a slightly different vertical aim.
    const h = (i % 2 === 0) ? SHORT_H : TALL_H;
    // Double-width slabs (X axis) so no horizontal aim is needed in the very first level — the
    // player just looks straight ahead and taps. Steering/accuracy is saved for later levels.
    addBlock({
      size: [FOOTPRINT * 2, h, FOOTPRINT],
      position: [0, h / 2, START_Z - i * SPACING],
      material: 'crate',
    });
  }
}

// =========================================================
// Level 2 — same forward-receding corridor as L1, but every 3rd block is a brick (2 hits, 3 coins).
// Pattern: crate · crate · brick · crate · crate · brick · …  Each 3-block group spans 1+1+2 = 4
// hits = exactly one 4/4 bar at one tap per beat. Bricks stand TALL so the visual rhythm matches
// the audio rhythm — short, short, TALL, short, short, TALL.
// Halved to 32 blocks 2026-05-20 to match the halved duration (sections 4→2 on 2026-05-16)
// so the goal threshold reflects what's reachable in the shorter run.
// =========================================================
// Level 2 — one running-bond brick wall facing the player. 40 bricks (8 wide x 5 tall) laid
// in a real masonry stagger: alternate courses shift half a brick, with a small mortar gap
// between bricks. All brick = the 2-hit mechanic taught across a single solid structure.
// BASE_YIELD = 40 brick x 3 = 120. Stars set explicitly in LEVEL_CONFIG.
function buildLevel2Layout() {
  const BW = 2.2;          // brick width (along the wall, X) — decent, easy-to-read size
  const BH = 1.1;          // brick height (Y)
  const BD = 1.0;          // brick depth (wall thickness, Z)
  const GAP = 0.06;        // mortar line so faces don't touch (no z-fighting) and rows read as courses
  const COLS = 8;
  const ROWS = 5;          // 8 x 5 = 40 bricks
  const PITCH_X = BW + GAP;
  const PITCH_Y = BH + GAP;
  const WALL_Z = -6;       // out in front of the player's spawn (~15% closer than the original -7)
  const baseX = -((COLS - 1) / 2) * PITCH_X;
  for (let r = 0; r < ROWS; r++) {
    const offset = (r % 2 === 1) ? PITCH_X / 2 : 0;  // running bond: every other course shifts half a brick
    for (let c = 0; c < COLS; c++) {
      addBlock({
        size: [BW, BH, BD],
        position: [baseX + c * PITCH_X + offset, BH / 2 + r * PITCH_Y, WALL_Z],
        material: 'brick',
      });
    }
  }
}

// =========================================================
// Level 3 — pure steering practice. 64 tall crates alternating left/right of centre with 8m
// in Z between them. Each is a single tap. Gentle turn-per-smash; the lunge auto-takes you
// to the next block once you flick aim across the centerline.
// 64 crates. BASE_YIELD = 64. 1★ 64 · 2★ 96 · 3★ 128.
// =========================================================
function buildLevel3Layout() {
  const SIZE = 1.5;
  const X_OFFSET = 1.5;           // one block-width off-axis — gentle swing
  const COUNT = 64;
  const FIRST_Z = -5;
  const SPACING_Z = 8;            // a smidge tighter than the original 10m — gentle turn per smash
  const h = SIZE * 2;             // tall silhouettes — same shape as the previous brick layout
  for (let i = 0; i < COUNT; i++) {
    const x = (i % 2 === 0) ? -X_OFFSET : X_OFFSET;
    const z = FIRST_Z - i * SPACING_Z;
    addBlock({
      size: [SIZE, h, SIZE],
      position: [x, h / 2, z],
      material: 'crate',
    });
  }
}

// =========================================================
// Level 4 (shown as "Level 5") — VERTICAL CLIMB. The world's first "you can go UP" level: by
// the teaching progression the player reaches here with dual ROCKET gloves (loadout idx 4 =
// [rocket, rocket]), so punching a block above launches them upward. A column of 30 flat crate
// blocks climbs straight into the sky. Block TOPS are spaced ~90% of TARGET_RANGE apart (the
// spacing that felt right); each block is extended UPWARD so it nearly meets the one above, with
// a small gap left at the bottom of each (and under the first, off the ground) — a tall target
// to aim at while rising instead of a thin pad to miss. Punch a block to launch up to the next.
// Purpose: teach upward launch with the rocket fists.
// 30 crates. BASE_YIELD ≈ 30.
// =========================================================
function buildLevel4Layout() {
  const COUNT = 30;
  const PITCH = TARGET_RANGE * 0.9; // ~11.7m between block tops — the spacing that felt right
  const BOTTOM_GAP = 2.5;           // space left under each block (and under the first, off the ground)
  const H = PITCH - BOTTOM_GAP;     // ~9.2m — each block is extended upward to nearly meet the one above
  const X = 0;
  const Z = -6;                     // a touch in front of spawn so the first launch reads forward+up
  for (let i = 0; i < COUNT; i++) {
    const top = PITCH * (i + 1);    // top of block i sits where the old flat pad did
    addBlock({
      size: [4, H, 4],
      position: [X, top - H / 2, Z],  // addBlock takes the center
      material: 'crate',
    });
  }
}

// =========================================================
// Level 5 — open-arena sandbox. After four corridor levels, the world opens up: structures
// scattered around the spawn at varied angles and distances, no fixed path. Player rotates
// 360° to find them, smashes in any order. Uses ONLY the materials taught so far (crate +
// brick) — no metal, no charge required. Consolidates rhythm + steering as a free-form
// playground before the charge mechanic is introduced in L6.
// ~63 blocks. BASE_YIELD ≈ 26 + 111 = 137. 1★ 137 · 2★ 206 · 3★ 274.
// =========================================================
function buildLevel5Layout() {
  // Forward — big crate pyramid centerpiece
  pyramid(0, -10, 3, 1.5, 'crate');
  // Right — brick wall (the "first thing the player sees if they look right")
  brickWall(11, 0, 4, 2, 1.5, 'brick', 'z');
  // Left — small crate stack
  crateStack(-11, 0);
  // NE far — jenga-style brick tower; topples dramatically
  jengaTower(11, -10, 4, 2.5, 0.5, 'brick');
  // NW far — tiered brick shelf
  shelf(-11, -10, 3, 'brick');
  // Behind-right — small crate pile (atmosphere filler)
  rubbleCratePile(8, 14);
  // Behind-left — brick column trio
  for (let i = 0; i < 3; i++) {
    addBlock({ size: [1.0, 3.0, 1.0], position: [-10 + i * 1.5, 1.5, 13], material: 'brick' });
  }
  // Behind-centre — short brick wall, low so player can see other structures over it
  brickWall(0, 16, 3, 1, 1.5, 'brick', 'x');
}

// Helper — small mixed-size crate pile for atmospheric clutter (only crates, no metal).
function rubbleCratePile(x, z) {
  addBlock({ size: [1.5, 1.5, 1.5], position: [x,        0.75, z       ], material: 'crate' });
  addBlock({ size: [1.0, 1.0, 1.0], position: [x + 1.0,  0.5,  z + 0.5 ], material: 'crate' });
  addBlock({ size: [0.8, 0.8, 0.8], position: [x - 0.7,  0.4,  z + 0.4 ], material: 'crate' });
  addBlock({ size: [0.7, 0.7, 0.7], position: [x + 0.2,  1.7,  z + 0.0 ], material: 'crate' });  // sits on top of the big one
  addBlock({ size: [0.6, 0.6, 0.6], position: [x - 0.4,  0.3,  z - 0.6 ], material: 'crate' });
}

// =========================================================
// Level 6 — charge intro, single big set-piece. One large brick pyramid topples first (drops a
// sea of coins on the ground), then a 4-tier metal pyramid behind it. Player charge-sucks the
// coin trail and chip-storms through the metal pyramid.
//   - 5-tier BRICK pyramid (base 5×5): 25 + 16 + 9 + 4 + 1 = 55 bricks
//   - 4-tier METAL pyramid (base 4×4): 16 + 9 + 4 + 1 = 30 metals
// 85 blocks. BASE_YIELD = 165 + 150 = 315. 1★ 315 · 2★ 472 · 3★ 630.
// =========================================================
function buildLevel6Layout() {
  const SIZE = 1.5;
  const BRICK_PYRAMID_BASE = 5;
  const BRICK_PYRAMID_DEPTH = BRICK_PYRAMID_BASE * SIZE;  // 7.5m footprint
  const BRICK_PYRAMID_CENTER_Z = -10;
  const METAL_PYRAMID_BASE = 4;
  const METAL_PYRAMID_DEPTH = METAL_PYRAMID_BASE * SIZE;  // 6m footprint
  const STACK_GAP = 14;              // gap between brick pyramid back face and metal pyramid front face
  pyramid(0, BRICK_PYRAMID_CENTER_Z, BRICK_PYRAMID_BASE, SIZE, 'brick');
  const metalCenterZ = BRICK_PYRAMID_CENTER_Z - BRICK_PYRAMID_DEPTH / 2 - STACK_GAP - METAL_PYRAMID_DEPTH / 2;
  pyramid(0, metalCenterZ, METAL_PYRAMID_BASE, SIZE, 'metal');
}

// =========================================================
// Level 7 — domino spiral. 44 single-block pillars (1×8×1, brick + metal in a 2:1 pattern) wound
// in a 4-turn spiral from radius 10m → 2m around centre (0, -10). Per-step angular spacing kept
// the same as the 22-tower / 2-turn original by doubling both count and turns together — adjacent
// towers ~3.4m apart along the path, AND ~2m radially between rings. Both inside brick
// BREAK_TOPPLE radius (4m default), so cascades chain across rings, not just along the path.
// Lesson: physics gives you free spectacle — you tap one pillar, several fall. 1★ requires
// tapping ~all 44 towers within the song.
// 44 blocks (30 brick + 14 metal). BASE_YIELD = 90 + 70 = 160. 1★ 160 · 2★ 240 · 3★ 320.
// =========================================================
function buildLevel7Layout() {
  const TOWER_COUNT = 44;
  const TURNS = 4;
  const R_OUTER = 10;
  const R_INNER = 2;
  const CENTER_Z = -10;
  const TOWER_HEIGHT = 8;
  // Outside-in: t=0 is outer edge (radius=R_OUTER), t=1 is centre (radius=R_INNER).
  // Player walks the spiral from outside in. Clockwise winding — the outer arm sweeps round to
  // the right of the player as they enter, then curves toward the centre.
  for (let i = 0; i < TOWER_COUNT; i++) {
    const t = i / (TOWER_COUNT - 1);
    const r = R_OUTER + (R_INNER - R_OUTER) * t;
    const theta = -t * TURNS * Math.PI * 2;  // negative → clockwise as viewed from above
    const x = r * Math.cos(theta);
    const z = CENTER_Z + r * Math.sin(theta);
    // Pattern B B M B B M … — every 3rd tower is metal (positions 2, 5, 8, …).
    const material = (i % 3 === 2) ? 'metal' : 'brick';
    addBlock({
      size: [1, TOWER_HEIGHT, 1],
      position: [x, TOWER_HEIGHT / 2, z],
      material,
    });
  }
}

// =========================================================
// Level 8 — pattern + crescendo. Forward-receding corridor of cubes (1:1:1) following the
// 5-block musical pattern crate · crate · brick · crate · metal, repeated 12 times. Each cube
// is 0.15m bigger than the previous (linear +10% of base), starting at player-size 1.5m and
// ending at 10.35m. Constant 1.0m edge-to-edge gap between cubes — small cubes feel tight,
// huge late cubes loom over the corridor and force the player to look up. Lessons: musical
// 5-against-4 phrasing (pattern doesn't divide bar length) and vertical aim.
// 60 blocks = 36 crate + 12 brick + 12 metal. BASE_YIELD = 36 + 36 + 60 = 132.
// 1★ 132 · 2★ 198 · 3★ 264.
// =========================================================
function buildLevel8Layout() {
  const COUNT = 60;
  const FIRST_SIZE = 1.5;          // cube 0 ≈ player height
  const SIZE_STEP = FIRST_SIZE * 0.05;  // +5% of FIRST_SIZE per cube
  const GAP = FIRST_SIZE * 3;      // edge-to-edge spacing — three first-cubes wide (4.5m), constant
  const START_Z = -3;              // first cube centre, like L1/L2
  const PATTERN = ['crate', 'crate', 'brick', 'crate', 'metal'];
  let z = START_Z;
  for (let i = 0; i < COUNT; i++) {
    const size = FIRST_SIZE + SIZE_STEP * i;
    if (i > 0) {
      const prevSize = FIRST_SIZE + SIZE_STEP * (i - 1);
      z -= prevSize / 2 + GAP + size / 2;
    }
    addBlock({
      size: [size, size, size],
      position: [0, size / 2, z],
      material: PATTERN[i % PATTERN.length],
    });
  }
}

// =========================================================
// Level 9 — ammo loop (extended). Six metal structures alternated with six varied crate fields:
// M C M C M C M C M C M C. Pyramid gates are now solid 3D stepped pyramids (3×3 + 2×2 + 1 = 14
// cubes, 4.5m × 4.5m × 4.5m); wall and staircase stay flat (6 cubes each). Lesson: coins are
// ammo — smash metal → coins drop → charge to suck them in → chip-storm the crate field ahead.
// Each pyramid drops 70 chips (massive ammo dump) before its crate field; flat gates drop 30.
//   - Gates cycle P-W-S twice: 2 pyramids (28) + 2 walls (12) + 2 staircases (12) = 52 metals
//   - Six unique fields ~14-16 crates each: scatter · rows · forest · diagonal · clusters · pillars
//   - 86 crates total
// 138 blocks. BASE_YIELD = 260 + 86 = 346. 1★ 346 · 2★ 519 · 3★ 692.
// =========================================================
function buildLevel9Layout() {
  const CRATE_SIZE = 1.5;
  const METAL_SIZE = 1.5;
  const SECTION_GAP = 4;            // edge-to-edge gap between gates and crate fields
  const m = METAL_SIZE;
  const y0 = m / 2, y1 = y0 + m, y2 = y1 + m;

  const placeMetal = (x, y, z) => addBlock({
    size: [m, m, m], position: [x, y, z], material: 'metal',
  });
  const placeCrate = (x, z, tall) => {
    const h = tall ? CRATE_SIZE * 2 : CRATE_SIZE;
    addBlock({
      size: [CRATE_SIZE, h, CRATE_SIZE],
      position: [x, h / 2, z],
      material: 'crate',
    });
  };

  // 3D pyramid — solid stepped 3-tier (3×3 base + 2×2 mid + 1 apex = 14 cubes). 4.5m on every axis.
  const buildPyramid3D = (gateZ) => {
    for (const dx of [-m, 0, m])
      for (const dz of [-m, 0, m])
        placeMetal(dx, y0, gateZ + dz);
    for (const dx of [-m / 2, m / 2])
      for (const dz of [-m / 2, m / 2])
        placeMetal(dx, y1, gateZ + dz);
    placeMetal(0, y2, gateZ);
  };
  // Wall (flat, 1 deep) — 3×2 rectangular slab, 6 cubes.
  const buildWallGate = (gateZ) => {
    placeMetal(-m, y0, gateZ); placeMetal(0, y0, gateZ); placeMetal(m, y0, gateZ);
    placeMetal(-m, y1, gateZ); placeMetal(0, y1, gateZ); placeMetal(m, y1, gateZ);
  };
  // Staircase (flat, 1 deep) — slopes up to one side, 3+2+1 = 6 cubes.
  const buildStaircaseGate = (gateZ) => {
    placeMetal(-m, y0, gateZ); placeMetal(0, y0, gateZ); placeMetal(m, y0, gateZ);
    placeMetal(0, y1, gateZ); placeMetal(m, y1, gateZ);
    placeMetal(m, y2, gateZ);
  };
  // Gate descriptors carry their z-extent (depth) so positioning math knows how far to advance.
  const gates = [
    { depth: 3 * m, build: buildPyramid3D },
    { depth: m,     build: buildWallGate },
    { depth: m,     build: buildStaircaseGate },
  ];

  // Six unique crate fields. Each entry = [x, dz, tall]. dz < 0 = deeper. 'tall' = 3m slab.
  // Adjacent crates stay 1.5m apart minimum on z OR offset enough on x to avoid overlap.
  const SCATTER = [
    [-3,    0,   false], [ 3,    0,   true ],
    [ 0,   -2,   false],
    [-2,   -3.5, true ], [ 2,   -3.5, false],
    [-3,   -5,   false], [ 1,   -5,   false],
    [-2,   -7,   false], [ 2,   -7,   true ],
    [ 0,   -9,   false], [ 3,   -9,   false],
    [-1,  -10.5, false],
    [ 3,  -11.5, false], [-3, -12,   true ],
  ];
  const ROWS = [
    [-3,  0,   false], [-1,  0,   true ], [1,  0,   false], [3,  0,   true ],
    [-3, -2.5, true ], [-1, -2.5, false], [1, -2.5, true ], [3, -2.5, false],
    [-3, -5,   false], [-1, -5,   true ], [1, -5,   false], [3, -5,   true ],
    [-3, -7.5, true ], [-1, -7.5, false], [1, -7.5, true ], [3, -7.5, false],
  ];
  const FOREST = [
    [-3,    0,    true ], [ 3,    0,    true ],
    [ 0,   -1.5,  false],
    [-1.5, -3,    false], [ 1.5, -3,    false],
    [-3,   -4.5,  false], [ 3,   -4.5,  true ],
    [ 0,   -6,    true ],
    [-1.5, -7.5,  false], [ 1.5, -7.5,  true ],
    [-3,   -9,    false], [ 3,   -9,    false],
    [-1.5,-10.5,  true ], [ 1.5,-10.5,  false],
  ];
  // Diagonal — snake weaving from front-left across to back-right and partway back.
  const DIAGONAL = [
    [-3,  0,    true ],
    [-2, -1.5,  false],
    [-1, -3,    true ],
    [ 0, -4.5,  false],
    [ 1, -6,    true ],
    [ 2, -7.5,  false],
    [ 3, -9,    true ],
    [ 2, -10.5, false],
    [ 1, -12,   true ],
    [ 0, -13.5, false],
    [-1, -15,   true ],
    [-2, -16.5, false],
    [-3, -18,   true ],
    [ 3, -18,   false],
  ];
  // Clusters — three small groups stepping front-left → centre → back-right.
  const CLUSTERS = [
    [-3,    0,   false], [-1,    0,   true ],
    [-3,   -1.5, true ], [-1,   -1.5, false],
    [ 0,   -3.5, true ],
    [-1,   -5,   false], [ 1,   -5,   false],
    [ 0,   -6.5, true ],
    [-1,   -8,   false], [ 1,   -8,   false],
    [ 1,   -9.5, false], [ 3,   -9.5, true ],
    [ 1,  -11,   true ], [ 3,  -11,   false],
  ];
  // Pillar room — 4 corner tall pillars + grid of singles inside, 6m deep.
  const PILLAR_ROOM = [
    [-3,  0,   true ], [ 0,    0,   false], [ 3,    0,   true ],
    [-3, -6,   true ], [ 3,   -6,   true ],
    [-1.5, -1.5, false], [ 0,    -1.5, false], [ 1.5, -1.5, false],
    [-1.5, -3,   true ], [ 0,    -3,   false], [ 1.5, -3,   true ],
    [-1.5, -4.5, false], [ 0,    -4.5, false], [ 1.5, -4.5, false],
  ];
  const fields = [SCATTER, ROWS, FOREST, DIAGONAL, CLUSTERS, PILLAR_ROOM];

  // Compose: 6 sections, M C M C M C M C M C M C. Gates cycle P-W-S twice across the 6 sections.
  let z = -3;
  for (let i = 0; i < 6; i++) {
    const gate = gates[i % 3];
    gate.build(z);
    const fieldStartZ = z - gate.depth / 2 - SECTION_GAP - CRATE_SIZE / 2;
    for (const [x, dz, tall] of fields[i]) placeCrate(x, fieldStartZ + dz, tall);
    if (i < 5) {
      const fieldDepth = -Math.min(...fields[i].map(([, dz]) => dz));
      const nextGate = gates[(i + 1) % 3];
      z = fieldStartZ - fieldDepth - CRATE_SIZE / 2 - SECTION_GAP - nextGate.depth / 2;
    }
  }
}

// =========================================================
// Level 10 — capstone arena (huge pyramids edition). Three big-cube pyramids dominate the room:
// a 9-row crate pyramid (285 crates at 2m cubes, 18m tall) at back centre, a 6-row brick pyramid
// (91 bricks at 2m, 12m tall) mid-left, a 3-row metal pyramid (14 metals at 3m, 9m tall) mid-right.
// Three "huge cube on tall cuboid" set-pieces stand around the playground for cascade drama.
// All other sandbox-style structures (jenga, shelf, towers, reinforced wall, atmosphere) kept.
//   Metals 19 (95) · Bricks 191 (573) · Crates 308 (308)
// 518 blocks. BASE_YIELD = 976. 1★ 976 · 2★ 1464 · 3★ 1952.
// =========================================================
function buildLevel10Layout() {
  // ---- THREE BIG PYRAMIDS ----
  // Back-centre — 9-row crate pyramid, 2m cubes → 18m × 18m base, 18m tall. 285 crates.
  pyramid(0, -25, 9, 2.0, 'crate');
  // Mid-left — 6-row brick pyramid, 2m cubes → 12m × 12m base, 12m tall. 91 bricks.
  pyramid(-18, -8, 6, 2.0, 'brick');
  // Mid-right — 3-row metal pyramid, 3m cubes → 9m × 9m base, 9m tall. 14 metals.
  pyramid(18, -8, 3, 3.0, 'metal');

  // ---- HUGE CUBES BALANCED ON TALL CUBOIDS ----
  // Each pair = a thin tall column with a giant cube perched on top. When the column breaks
  // (or topples from a chip-storm), the cube falls dramatically.
  // Pair 1 — brick column at (-8, 5), 6m tall, 3×3×3 crate on top.
  addBlock({ size: [1.5, 6, 1.5], position: [-8, 3, 5], material: 'brick' });
  addBlock({ size: [3, 3, 3],     position: [-8, 7.5, 5], material: 'crate' });
  // Pair 2 — brick column at (8, 5), 6m tall, 3×3×3 brick boulder on top.
  addBlock({ size: [1.5, 6, 1.5], position: [8, 3, 5], material: 'brick' });
  addBlock({ size: [3, 3, 3],     position: [8, 7.5, 5], material: 'brick' });
  // Pair 3 — taller column behind player, 4×4×4 crate on top.
  addBlock({ size: [1.5, 7, 1.5], position: [0, 3.5, 12], material: 'brick' });
  addBlock({ size: [4, 4, 4],     position: [0, 9, 12], material: 'crate' });

  // ---- Reinforced wall (front-close) ----
  reinforcedWall(0, -3, 4, 3);

  // ---- Cascade structures ----
  jengaTower(-12, -18, 12, 3.0, 0.5, 'brick');   // mid-left jenga, 36 bricks
  shelf( 12, -18,  5, 'brick');                  // mid-right shelf, 15 bricks

  // ---- Tall corner towers ----
  tower(-22, -28, 16, 1.0, 'brick');   // back-left, 16-block column
  tower( 22, -28, 14, 1.0, 'brick');   // back-right, 14-block column

  // ---- Front-close detail ----
  crateStack(11, -1);                            // 7 crates, right close
  for (let i = 0; i < 3; i++) {                  // brick column trio, left close
    addBlock({ size: [1.0, 4.0, 1.0], position: [-12 + i * 1.5, 2.0, -1], material: 'brick' });
  }

  // ---- Behind player — 360° atmosphere ----
  rubbleCratePile( 10, 14);
  rubbleCratePile(-10, 14);
  // Pillar forest of varied-height brick towers.
  addBlock({ size: [0.8, 5.0, 0.8], position: [-14, 2.50,  8], material: 'brick' });
  addBlock({ size: [0.8, 6.0, 0.8], position: [ 14, 3.00, 10], material: 'brick' });
  addBlock({ size: [0.8, 4.0, 0.8], position: [-17, 2.00, 12], material: 'brick' });
  addBlock({ size: [0.8, 7.0, 0.8], position: [ 17, 3.50, 14], material: 'brick' });
  // Stacked crate pairs to break up dead space.
  addBlock({ size: [1.5, 1.5, 1.5], position: [-4, 0.75, 14], material: 'crate' });
  addBlock({ size: [1.5, 1.5, 1.5], position: [-4, 2.25, 14], material: 'crate' });
  addBlock({ size: [1.5, 1.5, 1.5], position: [ 4, 0.75, 14], material: 'crate' });
  addBlock({ size: [1.5, 1.5, 1.5], position: [ 4, 2.25, 14], material: 'crate' });
}

// Dispatcher — picks the layout matching the level id. Falls back to sandbox.
// buildFromSpec — renders a level-spec JSON (web/src/levels/*.json) into the world.
// v0 supports `spiral`, `line`, and `rawBlocks`. More primitives land as needed.
//
// `opts.microJitter` — UGC-only switch. The LLM regularly emits blocks that share a face
// or fully overlap, which causes Three.js z-fighting (flickering coplanar surfaces). When
// this flag is on, addBlock applies a sub-millimeter golden-ratio offset per-block so no
// two faces are ever exactly coplanar. Curated worlds were authored carefully and don't
// need it; running the jitter on them would just add invisible seams between flush blocks.
function buildFromSpec(spec, opts = {}) {
  if (opts.microJitter) _ugcJitterIndex = 0;
  try {
    applyScene(spec.scene || 'default');
    for (const s of spec.structures || []) {
      if      (s.type === 'spiral')    _buildSpiralFromSpec(s);
      else if (s.type === 'line')      _buildLineFromSpec(s);
      else if (s.type === 'pixelWall') _buildPixelWallFromSpec(s);
      else if (s.type === 'voxel')     _buildVoxelFromSpec(s);
      else console.warn('[buildFromSpec] unknown structure type:', s.type);
    }
    for (const b of _normalizeRawBlocks(spec.rawBlocks)) {
      addBlock(b);
    }
  } finally {
    _ugcJitterIndex = -1;
  }
}
// -1 = jitter off (curated levels). 0+ = jitter on; addBlock reads + increments per call.
let _ugcJitterIndex = -1;

// Gemini sometimes returns `rawBlocks` as an array of group-wrappers
// (`{ comment, blocks: [...] }`) — usually one wrapper per creature in creatures-mode
// scenes. The schema expects a flat array of block objects. Normalize either shape into
// a flat list of valid blocks, dropping anything malformed with a console warning. This
// keeps existing community records playable; the server now also normalizes on save.
function _normalizeRawBlocks(rawBlocks) {
  if (!Array.isArray(rawBlocks)) return [];
  const out = [];
  for (const item of rawBlocks) {
    if (!item) continue;
    if (Array.isArray(item.size) && Array.isArray(item.position)) {
      out.push(item);
    } else if (Array.isArray(item.blocks)) {
      for (const inner of item.blocks) {
        if (inner && Array.isArray(inner.size) && Array.isArray(inner.position)) {
          out.push(inner);
        } else {
          console.warn('[buildFromSpec] dropping malformed inner block:', inner);
        }
      }
    } else {
      console.warn('[buildFromSpec] dropping malformed rawBlock entry:', item);
    }
  }
  return out;
}

function _buildVoxelFromSpec(s) {
  const [cx, cz] = s.centerXZ;
  const v = s.voxelSize ?? 1.0;
  const baseY = s.baseY ?? 0;
  const shape = s.shape;
  const matMap = s.materialMap;
  if (!shape || shape.length === 0) return;
  const H = shape.length;
  const D = shape[0]?.length ?? 0;
  const W = shape[0]?.[0]?.length ?? 0;
  if (D === 0 || W === 0) return;
  for (let y = 0; y < H; y++) {
    for (let z = 0; z < D; z++) {
      const row = shape[y][z];
      for (let x = 0; x < W; x++) {
        const material = matMap[row[x]];
        if (!material) continue;
        const wx = cx + (x - (W - 1) / 2) * v;
        const wy = baseY + (y + 0.5) * v;
        const wz = cz + (z - (D - 1) / 2) * v;
        addBlock({ size: [v, v, v], position: [wx, wy, wz], material });
      }
    }
  }
}

function _buildPixelWallFromSpec(s) {
  const [cx, cz] = s.centerXZ;
  const px = s.pixelSize ?? 1.0;
  const thickness = s.thickness ?? 1.0;
  const baseY = s.baseY ?? 0;
  const grid = s.grid;
  const matMap = s.materialMap;
  const rows = grid.length;
  if (rows === 0) return;
  const cols = grid[0].length;
  for (let r = 0; r < rows; r++) {
    const row = grid[r];
    for (let c = 0; c < cols; c++) {
      const material = matMap[row[c]];
      if (!material) continue;
      const x = cx + (c - (cols - 1) / 2) * px;
      const y = baseY + ((rows - 1 - r) + 0.5) * px;
      addBlock({ size: [px, px, thickness], position: [x, y, cz], material });
    }
  }
}

function _buildSpiralFromSpec(s) {
  const sign = s.direction === 'cw' ? -1 : 1;
  const [cx, , cz] = s.center;
  for (let i = 0; i < s.count; i++) {
    const t = i / (s.count - 1);
    const r = s.rOuter + (s.rInner - s.rOuter) * t;
    const theta = sign * t * s.turns * Math.PI * 2;
    const x = cx + r * Math.cos(theta);
    const z = cz + r * Math.sin(theta);
    const material = s.materialPattern[i % s.materialPattern.length];
    addBlock({ size: s.blockSize, position: [x, s.blockYCenter, z], material });
  }
}

function _buildLineFromSpec(s) {
  const [sx, sz] = s.startXZ;
  const [ax, az] = s.advanceXZ;
  // Two ways to vary per-block: (a) `pattern` array of fully-described blocks
  // (each with its own blockSize, material, y) cycling per position, or
  // (b) shared blockSize/material with optional materialPattern + yCurve.
  // Use (a) when blocks differ across multiple dimensions (W2-L2 brick-tall
  // vs crate-ground vs crate-sky); use (b) for simpler cases.
  if (s.pattern) {
    for (let i = 0; i < s.count; i++) {
      const p = s.pattern[i % s.pattern.length];
      const x = (p.x !== undefined) ? p.x : sx + ax * i;
      const z = (p.z !== undefined) ? p.z : sz + az * i;
      addBlock({ size: p.blockSize, position: [x, p.y, z], material: p.material });
    }
    return;
  }
  const palette = s.materialPattern || [s.material];
  const yFn = _makeYCurveFn(s.yCurve, s.count);
  for (let i = 0; i < s.count; i++) {
    const x = sx + ax * i;
    const z = sz + az * i;
    const y = yFn(i);
    const material = palette[i % palette.length];
    addBlock({ size: s.blockSize, position: [x, y, z], material });
  }
}

// Returns Y(i) for i in [0, count). Phase named so the JSON never holds radians.
function _makeYCurveFn(curve, count) {
  if (!curve || curve.type === 'constant') {
    const y = curve ? curve.y : 0;
    return () => y;
  }
  if (curve.type === 'sine') {
    const centre = (curve.min + curve.max) / 2;
    const amp = (curve.max - curve.min) / 2;
    const omega = (curve.cycles * 2 * Math.PI) / count;
    const phaseByName = {
      'min':             -Math.PI / 2,
      'max':              Math.PI / 2,
      'center-rising':    0,
      'center-falling':   Math.PI,
    };
    const phase = phaseByName[curve.start] ?? 0;
    return (i) => centre + amp * Math.sin(omega * i + phase);
  }
  if (curve.type === 'alternating') {
    const values = curve.values;
    return (i) => values[i % values.length];
  }
  console.warn('[buildFromSpec] unknown yCurve type:', curve.type);
  return () => 0;
}

// W1 scenes — hardcoded levels don't go through buildFromSpec so they need
// their scene applied here. W2/W3 levels declare scene in their JSON specs
// and applyScene fires inside buildFromSpec.
const W1_SCENES = {
  level1:  'outdoor-grass',
  level2:  'outdoor-snow',
  level3:  'outdoor-tropical',
  level4:  'outdoor-sunset',
  level5:  'outdoor-grass',
  level6:  'outdoor-snow',
  level7:  'outdoor-tropical',
  level8:  'outdoor-sunset',
  level9:  'outdoor-grass',
  level10: 'outdoor-sunset',
};

// Techno backing track for the gauntlet showcase — lifted from the UGC level "a gauntlet, but
// it's 100 walls wide and tall but really thin" (motorik/distorted techno). Its generation was
// missing a BPM, so we set 130. Same song-schema the UGC engine authors, plays via applySong.
const gauntletSong = {"id":"gauntlet-of-a-hundred-walls","vibe":"mechanical","motifMode":"chord","drumKit":"distorted","drumPattern":"motorik","bassPattern":[0,3,0,3,0,3,0,3],"scaleHz":[43.65,46.25,51.91,58.27,61.74,69.3,77.78,87.31,92.5,103.83,116.54,123.47,138.59,155.56,174.61,185,207.65,233.08,246.94,277.18,311.13,349.23,369.99,415.3,466.16,493.88,554.37,622.25],"chords":[{"r":174.61,"t":207.65,"f":261.63,"o":349.23,"s":311.13,"bars":8}],"leadMotif":[[0,2,1,4,0,2,1,4]],"leadMotifBusy":[[0,4,2,4,1,4,0,4,2,4]],"leadMotifVar":[[0,1,2,1,0,4,2,4]],"leadMotifVarBusy":[[0,1,2,4,0,1,2,4,3,4]],"leadMotifBuild":[[0,1,2,3,4,3,2,1]],"leadMotifBuildBusy":[[0,4,1,4,2,4,3,4,2,4]],"sections":[null,null,null,{"delayFeedback":0.3},{"transpose":5},{"padVolume":0.7},null,{"leadVolume":1.3,"drumVolume":1.2,"delayFeedback":0.4}],"bpm":130};

// =========================================================
// Demolition Gauntlet — filming / showcase level (NOT part of the campaign). Eight SINGLE-material
// towers drawn from different inspirations (maths + architecture), with deliberately varied block
// sizes and heights, arranged shortest -> tallest down a volcanic corridor and alternating L/R for
// a slalom sweep. Built for dual SPRING gloves: the player stays planted and reach-punches each
// base; the tall ones fell from the base. Reached via ?gauntlet, no timer (sections Infinity),
// disco backing track. Every tower is a connected mass so a base hit cascades it down.
// =========================================================
function buildGauntletLayout() {
  applyScene('outdoor-volcanic');
  _gauntletJenga(0, -8);        // 1 · brick · ~12m  · jenga (the callback)
  _gauntletHanoi(-5, -22);      // 2 · crate · ~18m  · Tower of Hanoi (block-size gradient)
  _gauntletLeaning(5, -36);     // 3 · brick · ~22m  · Leaning Tower of Pisa
  _gauntletZiggurat(-5, -50);   // 4 · brick · ~25m  · stepped ziggurat
  _gauntletHelix(6, -64);       // 5 · crate · ~45m  · spiral staircase (helix)
  _gauntletHyperboloid(-6, -78);// 6 · metal · ~55m  · hyperboloid cooling tower (Shukhov)
  _gauntletObelisk(6, -92);     // 7 · metal · ~60m  · obelisk / monolith
  _gauntletDNA(0, -106);        // 8 · metal · ~70m  · DNA double helix (finale)
}

// 1) Jenga — brick, medium cross-stacked planks. Classic chaotic topple.
function _gauntletJenga(x, z) { jengaTower(x, z, 12, 4.0, 1.0, 'brick'); }

// 2) Tower of Hanoi — crate discs shrinking top-up. Pure block-size showcase; scatters as it goes.
function _gauntletHanoi(x, z) {
  const widths = [5.4, 4.8, 4.2, 3.6, 3.0, 2.4, 1.8, 1.2, 0.8];
  const H = 2.0; let y = 0;
  for (const w of widths) { addBlock({ size: [w, H, w], position: [x, y + H / 2, z], material: 'crate' }); y += H; }
}

// 3) Leaning Tower of Pisa — brick cubes, each layer nudged so the stack tilts ~2.3m off vertical.
function _gauntletLeaning(x, z) {
  const S = 1.6, N = 14, LEAN = 0.18;
  for (let i = 0; i < N; i++) addBlock({ size: [S, S, S], position: [x + i * LEAN, S / 2 + i * S, z], material: 'brick' });
}

// 4) Ziggurat — brick, large stepped tiers (Mesopotamian / Mayan). Crumbles tier by tier.
function _gauntletZiggurat(x, z) {
  const S = 2.5, tiers = 5;  // sides 5,4,3,2,1 — two block-layers per tier
  for (let t = 0; t < tiers; t++) {
    const side = tiers - t;
    for (let layer = 0; layer < 2; layer++) {
      const y = (t * 2 + layer) * S + S / 2;
      for (let i = 0; i < side; i++) for (let j = 0; j < side; j++) {
        addBlock({ size: [S, S, S], position: [x + (i - (side - 1) / 2) * S, y, z + (j - (side - 1) / 2) * S], material: 'brick' });
      }
    }
  }
}

// 5) Spiral staircase — crate steps winding a central column (helix). Unwinds when the core drops.
function _gauntletHelix(x, z) {
  for (let i = 0; i < 46; i++) addBlock({ size: [0.9, 1.0, 0.9], position: [x, 0.5 + i, z], material: 'crate' });
  const steps = 56, R = 1.3, rise = 0.8, dA = Math.PI / 6;
  for (let i = 0; i < steps; i++) {
    const a = i * dA;
    addBlock({ size: [1.8, 0.5, 1.8], position: [x + Math.cos(a) * R, 0.6 + i * rise, z + Math.sin(a) * R], material: 'crate', rotationY: a });
  }
}

// 6) Hyperboloid cooling tower — metal, twisted waisted rings (a curved surface from a stack of
// straight runs, Shukhov-style). Airy, not dense; shatters into a spark-storm.
function _gauntletHyperboloid(x, z) {
  const H = 55, dy = 2.5, waist = 1.8, flare = 2.4, perRing = 6;
  for (let y = 0; y < H; y += dy) {
    const t = (y - H / 2) / (H / 2);          // -1..1, 0 at the waist
    const r = waist + flare * t * t;          // narrowest in the middle
    const twist = y * (Math.PI / 22);
    for (let k = 0; k < perRing; k++) {
      const a = twist + k * (2 * Math.PI / perRing);
      addBlock({ size: [0.8, dy, 0.8], position: [x + Math.cos(a) * r, y + dy / 2, z + Math.sin(a) * r], material: 'metal', rotationY: a });
    }
  }
}

// 7) Obelisk / monolith — metal, a few massive tapering slabs + a pyramidion cap. Falls like a tree.
function _gauntletObelisk(x, z) {
  const slabs = 10, slabH = 5.5;
  for (let i = 0; i < slabs; i++) {
    const w = 3.6 - i * 0.14;                 // taper 3.6 -> ~2.3
    addBlock({ size: [w, slabH, w], position: [x, slabH / 2 + i * slabH, z], material: 'metal' });
  }
  const capY = slabs * slabH, capLayers = 4;
  for (let l = 0; l < capLayers; l++) {
    const cw = 2.2 * (1 - l / capLayers);
    addBlock({ size: [cw, 1.3, cw], position: [x, capY + 0.65 + l * 1.3, z], material: 'metal' });
  }
}

// 8) DNA double helix — metal, two intertwined strands joined by rungs (maths / biology). Finale;
// the twin spirals peel apart as it comes down.
function _gauntletDNA(x, z) {
  const H = 70, dy = 1.3, R = 2.0, turn = Math.PI / 9;   // ~20° per step
  const steps = Math.floor(H / dy);
  for (let i = 0; i < steps; i++) {
    const a = i * turn, y = dy / 2 + i * dy;
    addBlock({ size: [0.8, dy, 0.8], position: [x + Math.cos(a) * R, y, z + Math.sin(a) * R], material: 'metal' });
    addBlock({ size: [0.8, dy, 0.8], position: [x - Math.cos(a) * R, y, z - Math.sin(a) * R], material: 'metal' });
    if (i % 5 === 0) addBlock({ size: [2 * R, 0.6, 0.6], position: [x, y, z], material: 'metal', rotationY: a });  // rung
  }
}

function _buildLevelLayout(id) {
  // Reset the deterministic net-id sequence so both clients number this level's blocks identically.
  _netSeq = 0;
  _netById.clear();
  if (W1_SCENES[id]) applyScene(W1_SCENES[id]);
  if (_isPracticeLevel(id)) buildPracticeLayout(_practiceMode || id.replace('practice-', '') || 'spring');
  else if (id === 'level1') buildLevel1Layout();
  else if (id === 'level2') buildLevel2Layout();
  else if (id === 'level3') buildLevel3Layout();
  else if (id === 'level4') buildLevel4Layout();
  else if (id === 'level5') buildLevel5Layout();
  else if (id === 'level6') buildLevel6Layout();
  else if (id === 'level7') {
    if (USE_L7_SPEC) buildFromSpec(w1L7Spec);
    else             buildLevel7Layout();
  }
  else if (id === 'level8') buildLevel8Layout();
  else if (id === 'level9') buildLevel9Layout();
  else if (id === 'level10') buildLevel10Layout();
  else if (id === 'level11') buildFromSpec(w2L1Spec);
  else if (id === 'level12') buildFromSpec(w2L2Spec);
  else if (id === 'level13') buildFromSpec(w2L3Spec);
  else if (id === 'level14') buildFromSpec(w2L4Spec);
  else if (id === 'level15') buildFromSpec(w2L5Spec);
  else if (id === 'level16') buildFromSpec(w2L6Spec);
  else if (id === 'level17') buildFromSpec(w2L7Spec);
  else if (id === 'level18') buildFromSpec(w2L8Spec);
  else if (id === 'level19') buildFromSpec(w2L9Spec);
  else if (id === 'level20') buildFromSpec(w2L10Spec);
  else if (id === 'level21') buildFromSpec(w3L1Spec);
  else if (id === 'level22') buildFromSpec(w3L2Spec);
  else if (id === 'level23') buildFromSpec(w3L3Spec);
  else if (id === 'level24') buildFromSpec(w3L4Spec);
  else if (id === 'level25') buildFromSpec(w3L5Spec);
  else if (id === 'level26') buildFromSpec(w3L6Spec);
  else if (id === 'level27') buildFromSpec(w3L7Spec);
  else if (id === 'level28') buildFromSpec(w3L8Spec);
  else if (id === 'level29') buildFromSpec(w3L9Spec);
  else if (id === 'level30') buildFromSpec(w3L10Spec);
  else if (id === 'level31') buildFromSpec(testSmileysSpec);
  else if (id === 'level32') buildFromSpec(testTempleSpec);
  else if (id === 'level33') buildFromSpec(testGraveyardSpec);
  else if (id === 'level34') buildFromSpec(testMusicBoxSpec);
  else if (id === 'ugc-current') buildFromSpec(_ugcLevelSpec, { microJitter: true });
  else if (id === 'gauntlet') buildGauntletLayout();
  else                       buildSandboxLayout();
}

// UGC level state — when the player generates a level via the Create page, the level + song
// specs from the server live in these globals. The 'ugc-current' level dispatch pulls from
// _ugcLevelSpec, and LEVEL_CONFIG['ugc-current'].songSpec gets mutated to point at _ugcSongSpec.
let _ugcLevelSpec = null;
let _ugcSongSpec = null;
let _ugcId = null;
let _ugcPrompt = null;
// Creator nickname for the currently-loaded UGC level, set by _playUgcById from /play and
// by _generateUgcLevel from the local nickname. Used by the ready page and the post-clear
// leaderboard panel so "by X" can render without a re-fetch. null = anonymous.
let _ugcCreatorName = null;

buildSandboxLayout();
buildSupportGraph();
_rebuildBoundsForLevel('sandbox');

const controls = new PointerLockControls(camera, renderer.domElement);
const lockHint = document.getElementById('lock-hint');

// =========================================================
// Level definitions + level select.
// Sandbox = the open playground (song loops, no win/fail).
// Level 1 onwards = "real" levels (slice 2 will add win/fail/stars).
// =========================================================
const LEVELS = [
  { id: 'sandbox', name: 'Sandbox', tip: 'No timer, no goals. Practice your timing.',                        isSandbox: true },
  { id: 'level1',  name: 'Level 1', tip: 'Smash on the beat when the target is red.',                        isSandbox: false },
  { id: 'level2',  name: 'Level 2', tip: 'Bricks need 2 hits. Smash on 2 separate beats when the target is red.', isSandbox: false },
  { id: 'level3',  name: 'Level 4', tip: 'Blocks swing left and right. Auto-aim catches each one — just smash to the beat.', isSandbox: false },
  { id: 'level4',  name: 'Level 5', tip: 'Rocket fists! Aim UP at the pad above and smash to launch yourself higher. Climb to the top.',  isSandbox: false },
  { id: 'level5',  name: 'Level 3', tip: 'Open arena. Smash any structure to the beat.',                     isSandbox: false },
  { id: 'level6',  name: 'Level 6', tip: 'Hold to draw coins to your fists. Release on the beat to launch an explosive storm.', isSandbox: false },
  { id: 'level7',  name: 'Level 7', tip: 'Smash one pillar on the beat and watch the rest topple. Fire coins to clear the stage in seconds.', isSandbox: false },
  { id: 'level8',  name: 'Level 8', tip: 'Cubes grow so visual feedback gets harder to see. Listen and feel the rhythm to stay on beat.', isSandbox: false },
  { id: 'level9',  name: 'Level 9', tip: 'Coins destroy metal in one hit. Charge and fire coins to finish the level in seconds.', isSandbox: false },
  { id: 'level10', name: 'Level 10', tip: "Final stage. This is a huge arena. Bring everything you've learned together. If you reach a streak of 50, your coins deal extreme explosive damage to multiple blocks.", isSandbox: false },
  { id: 'level11', name: 'Level 1',  tip: 'Aim up to hit blocks above you and you will launch into the air.', isSandbox: false },
  { id: 'level12', name: 'Level 2',  tip: 'After you launch into the air, time your next two smashes to destroy the brick wall.', isSandbox: false },
  { id: 'level13', name: 'Level 4',  tip: 'Now bricks in the sky are set apart from each other... but auto-aim catches them, so you can smash without much manual aiming while in the air.', isSandbox: false },
  { id: 'level14', name: 'Level 5',  tip: "A spooky ghost and his house. Aim high to launch upwards, you can even get onto the roof. Lots of bricks here, so charge and fire coins from the air to clear it quickly.", isSandbox: false },
  { id: 'level15', name: 'Level 7',  tip: 'Try scaling the tower and unleashing coins at the metal blocks on top to clear the level more quickly.', isSandbox: false },
  { id: 'level16', name: 'Level 6',  tip: "Try farming coins from the coffee and take the smile off this guy's face!", isSandbox: false },
  { id: 'level17', name: 'Level 3',  tip: 'Aim above you to hit the blocks in the sky. Can you maintain your rhythm in the sky without touching the ground?', isSandbox: false },
  { id: 'level18', name: 'Level 8',  tip: "This one's massive. Scale upwards by smashing to the beat. Smashing metal blocks causes more explosive damage than crates. Get big streaks to clear the level faster.", isSandbox: false },
  { id: 'level19', name: 'Level 9',  tip: 'An icy castle made from cold steel. Coins will clear this level faster than punching.', isSandbox: false },
  { id: 'level20', name: 'Level 10', tip: 'Climb the walls to reach the alien. Fire coins to bring him down to earth!', isSandbox: false },
  { id: 'level21', name: 'Level 1',  tip: 'Weave between gates. Lunge into each pole as your aim swings left and right.', isSandbox: false },
  { id: 'level22', name: 'Level 3',  tip: "Follow the snake's curve. Read ahead and lunge into the next bend.", isSandbox: false },
  { id: 'level23', name: 'Level 4',  tip: 'Read the combo: jab, jab, cross. Time bricks for the power beats.', isSandbox: false },
  { id: 'level24', name: 'Level 6',  tip: 'Balls float high and low. Alternate aim up and down to keep the beat.', isSandbox: false },
  { id: 'level25', name: 'Level 2',  tip: 'Six tall towers. Punch the base, then work upward without breaking rhythm.', isSandbox: false },
  { id: 'level26', name: 'Level 7',  tip: 'Lamps alternate sides. Let your aim swing rhythmically left and right.', isSandbox: false },
  { id: 'level27', name: 'Level 5',  tip: 'Three jenga towers stretching skyward. Smash low layers to topple the rest.', isSandbox: false },
  { id: 'level28', name: 'Level 8',  tip: 'Stay on the road. Pyramids are scenery. Punch the brick beats.', isSandbox: false },
  { id: 'level29', name: 'Level 9',  tip: 'Smash through the wall first. Towers behind are scenery, chip what you can reach.', isSandbox: false },
  { id: 'level30', name: 'Level 10', tip: 'Race the road of fire. Chip lava pillars on the way. Crack the crater at the end.', isSandbox: false },
  { id: 'level31', name: 'TEST 1',   tip: 'UGC test: "a row of 3 different smiley faces" (Gemini 3.1 Pro).',          isSandbox: false },
  { id: 'level32', name: 'TEST 2',   tip: 'UGC test: "a giant ancient temple made of huge stone blocks" (Gemini 3.1 Pro).', isSandbox: false },
  { id: 'level33', name: 'TEST 3',   tip: 'UGC test: "a dark haunted graveyard with rows of crooked headstones" (Gemini 3.1 Pro). Heads up: oversized.', isSandbox: false },
  { id: 'level34', name: 'TEST 4',   tip: 'UGC test: "the inside of a music box" (Gemini 3.1 Pro). Abstract prompt.',                isSandbox: false },
];
let _currentLevel = null;
let _practiceMode = null;
let _nextEntryForcedLoadout = null;

function _isPracticeLevel(id = _currentLevel) {
  return typeof id === 'string' && id.startsWith('practice-');
}


function _practiceLoadoutForMode(mode) {
  if (mode === 'rocket') return [GLOVE_ROCKET, GLOVE_BARE];
  if (mode === 'double-spring') return [GLOVE_SPRING, GLOVE_SPRING];
  if (mode === 'double-rocket') return [GLOVE_ROCKET, GLOVE_ROCKET];
  if (mode === 'dual') return [GLOVE_SPRING, GLOVE_ROCKET];
  return [GLOVE_SPRING, GLOVE_BARE];
}

function _teachingLoadoutForLevel(levelId) {
  const world1 = WORLDS.find(w => w.id === 'world1');
  const idx = world1 ? world1.levelIds.indexOf(levelId) : -1;
  if (idx === 0) return [GLOVE_BARE, GLOVE_BARE];
  if (idx === 1) return [GLOVE_SPRING, GLOVE_BARE];
  if (idx === 2) return [GLOVE_SPRING, GLOVE_SPRING];
  if (idx === 3) return [GLOVE_ROCKET, GLOVE_BARE];
  if (idx === 4) return [GLOVE_ROCKET, GLOVE_ROCKET];
  return null;
}

function _activeLoadoutForLevel(levelId) {
  if (_gloveParam) return gloveLoadout;
  if (levelId === 'gauntlet') return [GLOVE_SPRING, GLOVE_SPRING];  // showcase level always plays with dual springs
  const progression = _loadProgressionState();
  if (progression.fullLoadoutFreedom) {
    return _sanitizeGloveLoadoutForProgression(gloveLoadout, progression);
  }
  return _teachingLoadoutForLevel(levelId) || _sanitizeGloveLoadoutForProgression(gloveLoadout, progression);
}

function _consumeForcedLoadoutForLevel(levelId) {
  if (!_nextEntryForcedLoadout || _nextEntryForcedLoadout.levelId !== levelId) return null;
  const loadout = _nextEntryForcedLoadout.loadout;
  _nextEntryForcedLoadout = null;
  return loadout;
}

// Worlds — top-level grouping of levels. Each world holds 10 levels. World 2+ shown on the
// home screen as "coming soon" teasers until built out.
const WORLDS = [
  {
    id: 'world1',
    name: 'POP',
    levelIds: ['level1', 'level2', 'level5', 'level3', 'level4', 'level6', 'level7', 'level8', 'level9', 'level10'],
    status: 'active',
  },
  {
    id: 'world2',
    name: '',
    levelIds: ['level11', 'level12', 'level17', 'level13', 'level14', 'level16', 'level15', 'level18', 'level19', 'level20'],
    status: 'active',
    songSpec: w2FlyingSong,
  },
  {
    id: 'world3',
    name: '',
    levelIds: ['level21', 'level25', 'level22', 'level23', 'level27', 'level24', 'level26', 'level28', 'level29', 'level30'],
    status: 'active',
  },
  {
    id: 'world4',
    name: 'UGC',
    levelIds: ['level31', 'level32', 'level33', 'level34'],
    status: 'active',
    // Set to false (or delete) to bring this world back on the home page.
    hidden: true,
  },
];
let _currentWorld = null;  // null = world select; world id = level select for that world

// Per-level progress persistence — best stars + best score, saved to localStorage on level end
// and read by the select-card render. Falls back to {0,0} if storage isn't available.
const _DEV_UNLOCK_KEY = 'smash-dev-unlock-all';
let _devUnlockAll = (() => {
  try { return localStorage.getItem(_DEV_UNLOCK_KEY) === '1'; } catch (e) { return false; }
})();
const _DEV_UNLOCK_REWARD_TEST_KEY = 'smash-dev-unlock-reward-test';
let _devUnlockRewardTest = (() => {
  try { return localStorage.getItem(_DEV_UNLOCK_REWARD_TEST_KEY) === '1'; } catch (e) { return false; }
})();
const _PROGRESS_KEY = 'smash-progress-v1';
function _loadAllProgress() {
  try { return JSON.parse(localStorage.getItem(_PROGRESS_KEY) || '{}'); } catch (e) { return {}; }
}
function _loadLevelProgress(id) {
  const data = _loadAllProgress();
  return data[id] || { stars: 0, best: 0 };
}
function _saveLevelProgress(id, stars, score) {
  try {
    const data = _loadAllProgress();
    const prev = data[id] || { stars: 0, best: 0 };
    data[id] = { stars: Math.max(prev.stars, stars), best: Math.max(prev.best, Math.round(score)) };
    localStorage.setItem(_PROGRESS_KEY, JSON.stringify(data));
  } catch (e) { /* localStorage blocked — ignore, in-memory progress only this session */ }
}

// First-time World 1 auto-start gate. The W1 tile click auto-starts level1 instead
// of opening the W1 level select the first time a user picks World 1. The flag is
// set the moment level1 is started (so a player who begins level1 via any path
// — even a deep link, dev console, or the old flow after a cache-busted deploy —
// is treated as "already saw the W1 level select" on subsequent visits). Onboarding
// reduction: removed the friction of picking level1 from a 10-card grid on the
// first visit. Tied to a per-browser flag because there's no auth; clearing
// localStorage puts the user back into the first-time path, which is the natural
// definition of "new device / new player".
const _W1_EVER_STARTED_KEY = 'smash-w1-ever-started-v1';
function _hasEverStartedW1() {
  try { return localStorage.getItem(_W1_EVER_STARTED_KEY) === '1'; }
  catch (e) { return false; }  // localStorage blocked — treat as not-yet-started (auto-start the first time)
}
function _markW1EverStarted() {
  try { localStorage.setItem(_W1_EVER_STARTED_KEY, '1'); }
  catch (e) { /* ignore — next visit will treat them as first-time again */ }
}

function _isWorldComplete(world) {
  if (!world || !world.levelIds || world.levelIds.length === 0) return false;
  return world.levelIds.every((levelId) => (_loadLevelProgress(levelId).stars || 0) >= 1);
}

function _worldPrereq(world) {
  const idx = WORLDS.indexOf(world);
  if (idx <= 0) return null;
  for (let i = idx - 1; i >= 0; i--) {
    const prev = WORLDS[i];
    if (!prev.hidden && prev.status === 'active') return prev;
  }
  return null;
}

function _isWorldUnlocked(world) {
  if (_devUnlockAll) return true;
  if (!world || world.status !== 'active') return false;
  const prereq = _worldPrereq(world);
  return !prereq || _isWorldComplete(prereq);
}

function _world1CompletedCount() {
  const world1 = WORLDS.find(w => w.id === 'world1');
  if (!world1) return 0;
  let count = 0;
  for (const levelId of world1.levelIds) {
    if ((_loadLevelProgress(levelId).stars || 0) < 1) break;
    count++;
  }
  return count;
}

function _loadProgressionState() {
  const world1Completed = _devUnlockAll ? 5 : _world1CompletedCount();
  const springSlots = world1Completed >= 2 ? 2 : (world1Completed >= 1 ? 1 : 0);
  const rocketSlots = world1Completed >= 4 ? 2 : (world1Completed >= 3 ? 1 : 0);
  const unlockedGloves = new Set([GLOVE_BARE]);
  if (springSlots > 0) unlockedGloves.add(GLOVE_SPRING);
  if (rocketSlots > 0) unlockedGloves.add(GLOVE_ROCKET);
  return {
    completedWorlds: WORLDS.filter(w => _isWorldComplete(w)).map(w => w.id),
    unlockedGloves,
    springSlots,
    rocketSlots,
    fullLoadoutFreedom: _devUnlockAll || world1Completed >= 5
  };
}

function _allowedGlovesForSlot(slot, state = _loadProgressionState()) {
  const gloves = [GLOVE_BARE];
  if (state.unlockedGloves.has(GLOVE_SPRING)) gloves.push(GLOVE_SPRING);
  if (state.unlockedGloves.has(GLOVE_ROCKET)) gloves.push(GLOVE_ROCKET);
  if (state.fullLoadoutFreedom) return gloves;
  const other = gloveLoadout[1 - slot];
  return gloves.filter((glove) => {
    if (glove === GLOVE_BARE) return true;
    const limit = glove === GLOVE_SPRING ? state.springSlots : state.rocketSlots;
    if (limit >= 2) return other === GLOVE_BARE || other === glove;
    return limit >= 1 && other !== glove;
  });
}

function _sanitizeGloveLoadoutForProgression(loadout, state = _loadProgressionState()) {
  const next = [
    state.unlockedGloves.has(loadout[0]) ? loadout[0] : GLOVE_BARE,
    state.unlockedGloves.has(loadout[1]) ? loadout[1] : GLOVE_BARE
  ];
  if (!state.fullLoadoutFreedom) {
    if (next[0] !== GLOVE_BARE && next[1] !== GLOVE_BARE && next[0] !== next[1]) next[1] = GLOVE_BARE;
    for (const glove of [GLOVE_SPRING, GLOVE_ROCKET]) {
      const limit = glove === GLOVE_SPRING ? state.springSlots : state.rocketSlots;
      const count = next.filter(g => g === glove).length;
      if (count > limit) next[1] = GLOVE_BARE;
    }
  }
  return next;
}

function _gloveLabel(glove) {
  if (glove === GLOVE_SPRING) return 'Spring';
  if (glove === GLOVE_ROCKET) return 'Rocket';
  return 'Bare';
}

function _gloveShortLabel(glove) {
  if (glove === GLOVE_SPRING) return 'SPR';
  if (glove === GLOVE_ROCKET) return 'RKT';
  return 'BARE';
}

function _gloveAccentColor(glove) {
  if (glove === GLOVE_SPRING) return '#70e8ff';
  if (glove === GLOVE_ROCKET) return '#ff7a2a';
  return '#ffe100';
}

function _gloveComboLabel(loadout) {
  const left = normalizeGlove(loadout && loadout[0], GLOVE_BARE);
  const right = normalizeGlove(loadout && loadout[1], GLOVE_BARE);
  if (left === right && left !== GLOVE_BARE) return `Double ${_gloveLabel(left)}s`;
  return `${_gloveLabel(left)} Left + ${_gloveLabel(right)} Right`;
}

function _buildEquipGlovesPanel({ compact = false } = {}) {
  const loadoutState = _loadProgressionState();
  const sanitizedLoadout = _sanitizeGloveLoadoutForProgression(gloveLoadout, loadoutState);
  let pendingLoadout = [...sanitizedLoadout];
  const loadoutChangedByProgress = sanitizedLoadout[0] !== gloveLoadout[0] || sanitizedLoadout[1] !== gloveLoadout[1];
  if (loadoutChangedByProgress && !_gloveParam) {
    _saveGloveLoadout(sanitizedLoadout);
    gloveLoadout[0] = sanitizedLoadout[0];
    gloveLoadout[1] = sanitizedLoadout[1];
  }
  const panel = document.createElement('div');
  panel.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:16px;'
    + `padding:${compact ? '12px 16px' : '24px 28px'};background:var(--col-bg-panel);border:4px solid var(--col-ink);`
    + 'box-shadow:6px 6px 0 var(--col-ink);font-family:var(--font-display);'
    + 'text-transform:uppercase;color:var(--col-ink);max-width:780px;width:min(780px,92vw);';
  const title = document.createElement('div');
  title.textContent = 'Equip Gloves';
  title.style.cssText = `font-size:${compact ? '14px' : '22px'};color:var(--col-pink);`
    + '-webkit-text-stroke:2px var(--col-ink);paint-order:stroke;text-align:center;';
  if (compact) panel.appendChild(title);

  const comboTitle = document.createElement('div');
  comboTitle.style.cssText = 'height:54px;max-width:min(660px,100%);display:flex;align-items:center;justify-content:center;'
    + 'font-size:clamp(15px,2.7vw,25px);line-height:1.05;color:var(--col-pink);'
    + '-webkit-text-stroke:1.4px var(--col-ink);paint-order:stroke;text-align:center;text-wrap:balance;'
    + 'overflow:hidden;';
  panel.appendChild(comboTitle);

  const previewSlot = document.createElement('div');
  previewSlot.style.cssText = `width:100%;display:flex;justify-content:center;height:${compact ? '210px' : '300px'};`;
  panel.appendChild(previewSlot);

  let preview = null;
  const renderPreview = () => {
    if (preview && preview._disposePreview) preview._disposePreview();
    previewSlot.textContent = '';
    preview = _makeUnlockRewardModel({
      kind: 'freedom',
      previewLoadout: [...pendingLoadout],
      previewMuted: true,
      previewEquipLayout: true
    });
    preview.style.width = 'min(100%,620px)';
    preview.style.height = compact ? '210px' : '300px';
    preview.style.minHeight = compact ? '190px' : '300px';
    preview.style.filter = 'drop-shadow(0 14px 10px rgba(0,0,0,0.30))';
    previewSlot.appendChild(preview);
  };

  const slots = document.createElement('div');
  slots.style.cssText = 'display:grid;grid-template-columns:repeat(2,minmax(220px,1fr));gap:14px;width:100%;';
  const optionButtons = [[], []];
  const updateVisualSelection = () => {
    pendingLoadout = _sanitizeGloveLoadoutForProgression(pendingLoadout, loadoutState);
    comboTitle.textContent = _gloveComboLabel(pendingLoadout);
    for (let slot = 0; slot < optionButtons.length; slot++) {
      for (const button of optionButtons[slot]) {
        const selected = button.dataset.glove === pendingLoadout[slot];
        const accent = _gloveAccentColor(button.dataset.glove);
        button.style.background = selected ? accent : 'white';
        button.style.transform = selected ? 'translate(2px,2px)' : 'translate(0,0)';
        button.style.boxShadow = selected ? '1px 1px 0 var(--col-ink)' : '3px 3px 0 var(--col-ink)';
        button.style.opacity = button.disabled ? '0.42' : '1';
      }
    }
    renderPreview();
  };
  const makeSlot = (slot, label) => {
    const wrap = document.createElement('div');
    wrap.style.cssText = 'display:flex;flex-direction:column;gap:10px;padding:12px;background:white;'
      + 'border:3px solid var(--col-ink);box-shadow:3px 3px 0 var(--col-ink);';
    const text = document.createElement('span');
    text.textContent = label;
    text.style.cssText = 'font-size:11px;color:var(--col-pink);';
    const choices = document.createElement('div');
    choices.style.cssText = 'display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px;';
    const allowed = _allowedGlovesForSlot(slot, loadoutState);
    for (const glove of [GLOVE_BARE, GLOVE_SPRING, GLOVE_ROCKET]) {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.glove = glove;
      button.disabled = !allowed.includes(glove);
      button.style.cssText = 'height:72px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:5px;'
        + 'border:3px solid var(--col-ink);box-shadow:3px 3px 0 var(--col-ink);cursor:pointer;'
        + 'font-family:var(--font-display);color:var(--col-ink);transition:transform 80ms ease,box-shadow 80ms ease,background 120ms ease;';
      const badge = document.createElement('span');
      badge.textContent = _gloveShortLabel(glove);
      badge.style.cssText = 'display:flex;align-items:center;justify-content:center;min-width:44px;height:26px;'
        + `background:${_gloveAccentColor(glove)};border:2px solid var(--col-ink);font-size:10px;`;
      const name = document.createElement('span');
      name.textContent = allowed.includes(glove) ? _gloveLabel(glove) : 'Locked';
      name.style.cssText = 'font-size:10px;line-height:1;';
      button.appendChild(badge);
      button.appendChild(name);
      const selectGlove = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (button.disabled) return;
        pendingLoadout[slot] = glove;
        updateVisualSelection();
      };
      button.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
      };
      button.onpointerdown = selectGlove;
      choices.appendChild(button);
      optionButtons[slot].push(button);
    }
    wrap.appendChild(text);
    wrap.appendChild(choices);
    return wrap;
  };
  slots.appendChild(makeSlot(0, 'Left Hand'));
  slots.appendChild(makeSlot(1, 'Right Hand'));
  panel.appendChild(slots);
  const status = document.createElement('div');
  status.style.cssText = 'font-family:ui-monospace,monospace;font-size:12px;line-height:1.5;text-transform:none;text-align:center;color:#27303a;';
  const springStatus = loadoutState.springSlots >= 2 ? 'Double Springs unlocked' : (loadoutState.springSlots >= 1 ? 'Spring unlocked' : 'Spring unlocks after Level 1');
  const rocketStatus = loadoutState.rocketSlots >= 2 ? 'Double Rockets unlocked' : (loadoutState.rocketSlots >= 1 ? 'Rocket unlocked' : 'Rocket unlocks after Level 3');
  const fullStatus = loadoutState.fullLoadoutFreedom ? 'Mix and match unlocked' : 'Mix and match unlocks after Level 5';
  status.textContent = `${springStatus} · ${rocketStatus} · ${fullStatus}`;
  panel.appendChild(status);
  if (_gloveParam) {
    const dev = document.createElement('div');
    dev.textContent = 'URL override active';
    dev.style.cssText = 'font-size:10px;color:var(--col-pink);';
    panel.appendChild(dev);
  } else {
    const actions = document.createElement('div');
    actions.style.cssText = 'display:flex;gap:10px;flex-wrap:wrap;justify-content:center;';
    const done = document.createElement('button');
    done.className = 'ss-btn ss-btn--primary';
    done.textContent = 'Done';
    done.onclick = (e) => {
      e.stopPropagation();
      const fixed = _sanitizeGloveLoadoutForProgression(pendingLoadout, loadoutState);
      gloveLoadout[0] = fixed[0];
      gloveLoadout[1] = fixed[1];
      _saveGloveLoadout(fixed);
      _setRuntimeGloveLoadout(fixed);
      if (preview && preview._disposePreview) preview._disposePreview();
      if (_gloveParam) {
        const url = new URL(location.href);
        url.searchParams.delete('gloves');
        history.replaceState(null, '', url.toString());
      }
      _homeView = 'world-select';
      _currentWorld = null;
      _renderHome();
    };
    actions.appendChild(done);
    panel.appendChild(actions);
  }
  updateVisualSelection();
  return panel;
}

function _progressionRewardFromChange(before, after) {
  if ((before.springSlots || 0) < 1 && (after.springSlots || 0) >= 1) return _unlockRewardForWorld1Tier(1);
  if ((before.springSlots || 0) < 2 && (after.springSlots || 0) >= 2) return _unlockRewardForWorld1Tier(2);
  if ((before.rocketSlots || 0) < 1 && (after.rocketSlots || 0) >= 1) return _unlockRewardForWorld1Tier(3);
  if ((before.rocketSlots || 0) < 2 && (after.rocketSlots || 0) >= 2) return _unlockRewardForWorld1Tier(4);
  if (!before.fullLoadoutFreedom && after.fullLoadoutFreedom) {
    return _unlockRewardForWorld1Tier(5);
  }
  return null;
}

function _unlockRewardForWorld1Tier(tier) {
  if (tier === 1) {
    return {
      kind: 'glove',
      glove: GLOVE_SPRING,
      previewLoadout: [GLOVE_SPRING, GLOVE_BARE],
      nextLoadout: [GLOVE_SPRING, GLOVE_BARE],
      title: 'SPRING FIST UNLOCKED',
      body: 'Smash far away blocks'
    };
  }
  if (tier === 2) {
    return {
      kind: 'glove',
      glove: GLOVE_SPRING,
      previewLoadout: [GLOVE_SPRING, GLOVE_SPRING],
      nextLoadout: [GLOVE_SPRING, GLOVE_SPRING],
      title: 'DOUBLE SPRINGS UNLOCKED',
      body: 'Reach with both hands'
    };
  }
  if (tier === 3) {
    return {
      kind: 'glove',
      glove: GLOVE_ROCKET,
      previewLoadout: [GLOVE_ROCKET, GLOVE_BARE],
      nextLoadout: [GLOVE_ROCKET, GLOVE_BARE],
      title: 'ROCKET FIST UNLOCKED',
      body: 'Launch yourself into blocks'
    };
  }
  if (tier === 4) {
    return {
      kind: 'glove',
      glove: GLOVE_ROCKET,
      previewLoadout: [GLOVE_ROCKET, GLOVE_ROCKET],
      nextLoadout: [GLOVE_ROCKET, GLOVE_ROCKET],
      title: 'DOUBLE ROCKETS UNLOCKED',
      body: 'Blast through with both hands'
    };
  }
  if (tier === 5) {
    return {
      kind: 'freedom',
      previewLoadout: [GLOVE_SPRING, GLOVE_ROCKET],
      nextLoadout: [GLOVE_SPRING, GLOVE_ROCKET],
      forceNextLoadout: true,
      title: 'MIX & MATCH UNLOCKED',
      body: 'Choose a glove for each hand'
    };
  }
  return null;
}

function _unlockRewardForLevelWorld(levelId) {
  const world1 = WORLDS.find(w => w.id === 'world1');
  const idx = world1 ? world1.levelIds.indexOf(levelId) : -1;
  if (idx >= 0) return _unlockRewardForWorld1Tier(idx + 1);
  return null;
}

function _unlockRewardForClear(levelId, before, after) {
  const reward = _devUnlockRewardTest ? _unlockRewardForLevelWorld(levelId) : _progressionRewardFromChange(before, after);
  if (reward) {
    reward.nextLevelId = _nextLevelAfter(levelId);
    if (reward.forceNextLoadout && reward.nextLevelId && reward.nextLoadout) {
      _nextEntryForcedLoadout = {
        levelId: reward.nextLevelId,
        loadout: [...reward.nextLoadout]
      };
    }
  }
  return reward;
}

function _nextLevelAfter(levelId) {
  for (const world of WORLDS) {
    const idx = world.levelIds.indexOf(levelId);
    if (idx >= 0 && idx < world.levelIds.length - 1) return world.levelIds[idx + 1];
  }
  return null;
}

// Replace the "click to start" overlay with the home/world-select container. All view
// content (world select OR level select for a chosen world) is rendered into _homeContent
// by _renderHome(), which branches on _currentWorld. The kit classes do all the styling.
lockHint.textContent = '';
lockHint.classList.add('ss-home-bg');
// IMPORTANT: index.html's `#lock-hint` rule sets `display:flex; align-items:center;
// justify-content:center;` — meant for the original "click to start" overlay. We must
// explicitly override `display` here, otherwise the flex centring clips the top of long
// home pages and breaks scroll-up. Setting `display:block` disengages the flex layout so
// the content flows naturally from top with padding + overflow scroll.
lockHint.style.cssText = 'position:fixed;inset:0;display:block;pointer-events:auto;background:var(--col-bg-deep);'
  + 'padding:60px 20px 40px;overflow-y:auto;overflow-x:hidden;';

const _homeContent = document.createElement('div');
_homeContent.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:28px;width:100%;max-width:1400px;margin:0 auto;'
  + 'transition:opacity 180ms ease-out;';

// Pulsing pixel speaker shown during the brief level-loading transition. Same disc layers
// as the wall-speaker cone (`_makeWallConeTexture`) so it reads as part of the kit. The
// pulse syncs to the music's beat phase via _updateLoadingSpeakerPulse() in the render loop
// — when the new song's BPM applies (after restartMusic), the pulse rate adopts it too.
const _loadingSpeakerEl = (() => {
  const c = document.createElement('canvas');
  c.width = 64; c.height = 64;
  const ctx = c.getContext('2d');
  ctx.imageSmoothingEnabled = false;
  _drawPixelDisc(ctx, 32, 32, 31, '#3a3f48');
  _drawPixelDisc(ctx, 32, 32, 26, '#7a8088');
  _drawPixelDisc(ctx, 32, 32, 16, '#4a4f58');
  _drawPixelDisc(ctx, 32, 32,  8, '#9aa0a8');
  ctx.fillStyle = '#cfd6dc'; ctx.fillRect(26, 26, 2, 2);
  c.style.cssText = 'width:128px;height:128px;image-rendering:pixelated;transform:scale(0.85);';
  return c;
})();

// 8-segment VU meter for the loading screen — same colour ramp + dim-when-off behaviour
// as the in-level wall meters (_makeMeter), rendered in HTML so it can sit beside the
// loading speaker. Two of these flank the speaker on the loading / generating screens.
const _LOADING_METER_COLORS = ['#39ff7a', '#39ff7a', '#a3ff39', '#ffd66b', '#ffd66b', '#ff8a39', '#ff5a3a', '#ff3b6b'];
function _makeLoadingMeter() {
  const wrap = document.createElement('div');
  wrap.style.cssText = 'display:flex;flex-direction:column-reverse;gap:2px;padding:4px 3px;background:#15181e;border:2px solid #0a0c10;width:14px;height:96px;box-sizing:border-box;';
  const segs = [];
  for (let i = 0; i < _LOADING_METER_COLORS.length; i++) {
    const seg = document.createElement('div');
    seg.style.cssText = `flex:1;background:${_LOADING_METER_COLORS[i]};opacity:0.12;`;
    wrap.appendChild(seg);
    segs.push(seg);
  }
  return { el: wrap, segs };
}
const _loadingMeterL = _makeLoadingMeter();
const _loadingMeterR = _makeLoadingMeter();
// Container holds [meter | boombox | meter]. The boombox replaces the old plain speaker
// disc — same role (visual indicator that music is playing) but more on-brand for the
// kit. Uses a 2-frame BPM-synced animation (frames A/B alternated by the pulse update).
const _loadingBoomboxEl = document.createElement('div');
_loadingBoomboxEl.style.cssText = 'width:200px;height:96px;background-size:contain;background-repeat:no-repeat;background-position:center;'
  + 'image-rendering:pixelated;image-rendering:crisp-edges;'
  + `background-image:url(${_BOOMBOX_FRAME_A});`;
const _loadingSpeakerWrap = document.createElement('div');
_loadingSpeakerWrap.style.cssText = 'display:flex;align-items:center;justify-content:center;gap:14px;';
_loadingSpeakerWrap.appendChild(_loadingMeterL.el);
_loadingSpeakerWrap.appendChild(_loadingBoomboxEl);
_loadingSpeakerWrap.appendChild(_loadingMeterR.el);
function _updateLoadingSpeakerPulse() {
  if (!_loadingSpeakerWrap.parentNode) return;  // detached → not visible, skip work
  // Pulse on every beat at the active song's BPM. Density-aware variants (every 4 beats
  // for sparse, every 8th for dense) felt out of sync with what the player expects on a
  // loading screen — they want a steady "song is playing" tick. Falls back to a 120 BPM
  // wall clock when no music is active yet.
  let pulse;
  if (_musicStarted && audioCtx && _activeSong) {
    const elapsed = audioCtx.currentTime - _musicLoopStartTime;
    if (elapsed < 0) {
      pulse = 0;
    } else {
      const phase = (elapsed % MUSIC_BEAT) / MUSIC_BEAT;
      pulse = Math.max(0, 1 - phase * 3.3);
    }
  } else {
    // No music yet — 120 BPM wall-clock fallback (60/120 = 0.5s per beat).
    const phase = (performance.now() / 500) % 1;
    pulse = Math.max(0, 1 - phase * 3.3);
  }
  // Boombox 2-frame animation — swap to FRAME_B when the beat is hitting hard, FRAME_A
  // otherwise. Threshold 0.5 gives a clean snap-in/snap-out feel matching the kit's
  // chunky pixel-art rhythm (steps(2) animation philosophy).
  const beatHigh = pulse > 0.5;
  _loadingBoomboxEl.style.backgroundImage = `url(${beatHigh ? _BOOMBOX_FRAME_B : _BOOMBOX_FRAME_A})`;
  // Meters — fill from bottom up to the current envelope, same logic as in-level VUs.
  // Linear pulse → integer segment count; lit segments at full opacity, others dimmed.
  const filled = Math.ceil(pulse * _LOADING_METER_COLORS.length);
  for (const meter of [_loadingMeterL, _loadingMeterR]) {
    for (let i = 0; i < meter.segs.length; i++) {
      meter.segs[i].style.opacity = i < filled ? '1' : '0.12';
    }
  }
}
lockHint.appendChild(_homeContent);

// =====================================================================
// Home background field. A sparse scatter of iso silhouettes behind
// the world-select view. Pool = UGC levels (from /community) + built-in
// worlds 1-3 (level1-30, served from local curated-silhouettes.json so
// the Worker isn't hit for these). Sampled once per session (49 cards),
// placed on a 7x7 stretcher-bond grid, wrapped in a div that CSS-
// animates down-left along the iso angle with a crossfade at the loop
// boundary.
// =====================================================================

const _homeBgField = document.createElement('div');
// position:fixed so cards stay anchored to the viewport when lockHint
// scrolls. z-index:-1 sinks the field below lockHint's bg color but
// above its background, so the field is visible behind _homeContent.
// pointer-events:none so the field never blocks world-tile / featured
// row clicks. overflow:hidden so a placed card never escapes the box.
_homeBgField.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:-1;overflow:hidden;';
lockHint.appendChild(_homeBgField);

// null = not yet fetched this session; array (possibly empty) = sample
// of levels to scatter in the field. Stable for the rest of the visit.
let _homeBgSample = null;

async function _ensureHomeBgSample() {
  if (_homeBgSample !== null) return;
  try {
    const res = await fetch(`${UGC_SERVER_URL}/community?limit=200`);
    if (!res.ok) throw new Error(`server returned ${res.status}`);
    const data = await res.json();
    const ugcLevels = Array.isArray(data.levels) ? data.levels : [];

    // Curated levels from built-in worlds 1-3. These silhouettes live
    // in curatedSilhouettes (web/src/curated-silhouettes.json) — the
    // worker's /silhouette/:id endpoint only serves UGC, so we pre-
    // populate _ugcSilhouetteDataCache for these ids. _fetchUgcSilhouette
    // checks that cache first and resolves immediately, so the painting
    // path needs no per-source branching. WORLDS is defined further
    // down the file but this reference resolves at call time, so the
    // forward reference is safe.
    const curatedIds = new Set();
    for (const w of WORLDS) {
      if (w.id === 'world1' || w.id === 'world2' || w.id === 'world3') {
        for (const id of w.levelIds) curatedIds.add(id);
      }
    }
    const curatedLevels = [];
    for (const id of curatedIds) {
      const entry = curatedSilhouettes[id];
      if (!entry || !entry.silhouette) continue;
      _ugcSilhouetteDataCache.set(id, entry.silhouette);
      curatedLevels.push({ id, scene: entry.scene || 'outdoor-grass' });
    }

    if (ugcLevels.length === 0 && curatedLevels.length === 0) {
      _homeBgSample = []; return;
    }
    // Exclude the featured-row ids so the same silhouettes don't appear in
    // both the foreground Featured row and the background field. The featured
    // row (_populateFeaturedRow) shows the 4 newest community levels, and
    // /community returns createdAt desc, so the first 4 ugcLevels here are
    // exactly those cards. Keep this in sync with _populateFeaturedRow's
    // slice(0, 4) if that count ever changes.
    const featuredSet = new Set(ugcLevels.slice(0, 4).map(l => l.id));
    const candidates = [...ugcLevels, ...curatedLevels].filter(l => !featuredSet.has(l.id));
    // Fisher-Yates shuffle. The order of this array is what changes
    // per session; the underlying set of available levels is the same.
    for (let i = candidates.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
    }
    _homeBgSample = candidates.slice(0, 49);
  } catch (e) {
    // Local dev with no UGC server running, or a 503. Empty sample
    // leaves the field empty rather than blocking the home page.
    console.warn('[home-bg] failed to load community levels:', e);
    _homeBgSample = [];
  }
}

function _rectsOverlap(a, b) {
  return !(a.x + a.w <= b.x || b.x + b.w <= a.x || a.y + a.h <= b.y || b.y + b.h <= a.y);
}

const _HOME_BG_CARD_W = 128;
const _HOME_BG_CARD_H = 80;
const _HOME_BG_CARD_OPACITY = 0.6;
const _HOME_BG_PLACE_ATTEMPTS = 200;
const _HOME_BG_SCROLL_DURATION_MS = 45_000;

function _layoutHomeBgField() {
  _homeBgField.textContent = '';
  // Tear down the previous keyframes style element if any, so the
  // document head doesn't accumulate dead @keyframes blocks across
  // re-layouts (resize, navigating back to home, etc.).
  if (_homeBgField._keyframeStyle) {
    _homeBgField._keyframeStyle.remove();
    _homeBgField._keyframeStyle = null;
  }
  if (!_homeBgSample || _homeBgSample.length === 0) return;

  // Eager pre-fetch. The painting function (_paintLazyUgcSilhouette)
  // already triggers the fetch at call time, but the wrapper animation
  // is moving by the time the fetch resolves — so the duplicates that
  // scroll into view later in the loop are at risk of being empty for
  // a beat if the network is slow. Calling _fetchUgcSilhouette for
  // every unique id here (deduped via the in-flight memoization) makes
  // the intent explicit and the fetch starts at layout time, not at
  // first visibility. Safe no-op when the data is already cached.
  const seenIds = new Set();
  for (const lvl of _homeBgSample) {
    if (seenIds.has(lvl.id)) continue;
    seenIds.add(lvl.id);
    _fetchUgcSilhouette(lvl.id);
  }

  const vw = window.innerWidth;

  // 7x7 grid with 2:1 cells. cellH = cellW/2 matches the 2:1 dimetric
  // iso ground ratio used elsewhere, so the scroll vector (cellW, cellH)
  // lands exactly on the iso "down-left" axis at any viewport aspect
  // (the 1:2 screen ratio between horizontal and vertical travel is the
  // iso Y axis in 2:1 dimetric). 7x7 = 49 cells matches the home-bg
  // sample size exactly, so every level gets a slot.
  const COLS = 7;
  const ROWS = 7;
  const cellW = vw / COLS;
  const cellH = cellW / 2; // 2:1 iso ground tile ratio

  // Build the cells and shuffle
  const cells = [];
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) cells.push({ r, c });
  }
  for (let i = cells.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [cells[i], cells[j]] = [cells[j], cells[i]];
  }

  // First pass: compute each card's jittered position. Storing the
  // computed position lets the duplicate copy (second pass below) reuse
  // the exact same jitter offsets — critical for the seamless loop,
  // since the duplicate must overlay the original perfectly when the
  // animation snaps back to (0, 0).
  // Stretcher bond: every odd row is offset by half a cell so the
  // cards interlock like bricks. The amount is cellW/2 (= 137 at
  // 1920px viewport) — this centers each odd-row card between two
  // even-row cards at the same column index. The vertical gap
  // (cellH - cardH = ~57px after the row offset) keeps the cards
  // from visually overlapping despite the horizontal interlock.
  // The +cellW/2 shift on the rightmost column pushes cards ~62px
  // past the viewport edge; parent overflow:hidden clips them.
  const STRETCHER_OFFSET = cellW / 2;
  const cardData = [];
  for (let i = 0; i < _homeBgSample.length && i < cells.length; i++) {
    const lvl = _homeBgSample[i];
    const cell = cells[i];
    const jitterX = (Math.random() - 0.5) * cellW * 0.3;
    const jitterY = (Math.random() - 0.5) * cellH * 0.3;
    const rowOffsetX = (cell.r % 2 === 1) ? STRETCHER_OFFSET : 0;
    const cardX = cell.c * cellW + (cellW - _HOME_BG_CARD_W) / 2 + rowOffsetX + jitterX;
    const cardY = cell.r * cellH + (cellH - _HOME_BG_CARD_H) / 2 + jitterY;
    cardData.push({ lvl, x: cardX, y: cardY });
  }

  // Wrapper. This div is the thing the CSS animation transforms, so
  // it spans the parent and gets translated by exactly one cell period
  // (-cellW, +cellH) per loop — the iso "down-left" direction.
  // will-change:transform hints the compositor to put it on its own GPU
  // layer for smooth animation. The wrapper itself doesn't clip; the
  // parent _homeBgField has overflow:hidden so cards outside the
  // viewport are clipped.
  const wrapper = document.createElement('div');
  wrapper.className = 'home-bg-scroll';
  wrapper.style.cssText = 'position:absolute;left:0;top:0;'
    + 'width:100%;height:100%;'
    + 'will-change:transform;';

  // Place one set of cards. The wrapper has NO duplicate copy: a
  // second copy offset by (+cellW, -cellH) was the seamless-loop trick
  // when the snap itself was visible, but it lands in cells that
  // already have their own original card — anti-diagonal collision,
  // two different silhouettes stacking on top of each other. The
  // cap on non-overlapping cards with that offset is the count of
  // anti-diagonals (13 for 7x7), so we can't have a dense field
  // with duplicates. The crossfade at the loop boundary now does
  // the work the duplicates used to do.
  //
  // During the scroll, the leftmost cards exit the viewport and
  // the rightmost stay in view. The crossfade hides the reset.
  for (const data of cardData) {
    const card = document.createElement('div');
    // No background, no border, no shadow — just the iso block
    // silhouette at the field opacity. image-rendering:pixelated
    // keeps the block edges crisp at 128x80 display size. hideFloor
    // on the renderer drops the floor parallelogram so each card is
    // just the block art.
    card.style.cssText = `position:absolute;left:${data.x}px;top:${data.y}px;`
      + `width:${_HOME_BG_CARD_W}px;height:${_HOME_BG_CARD_H}px;`
      + 'background-color:transparent;'
      + 'box-sizing:border-box;background-size:contain;background-repeat:no-repeat;background-position:center;'
      + `image-rendering:pixelated;opacity:${_HOME_BG_CARD_OPACITY};`;
    _paintLazyUgcSilhouette(card, data.lvl.id, data.lvl.scene, `homebg-${data.lvl.id}`, { hideFloor: true });
    wrapper.appendChild(card);
  }
  _homeBgField.appendChild(wrapper);

  // Keyframes injected with this layout's actual cellW/cellH. Unique
  // animation name per layout so a new animation cleanly replaces the
  // old one even if the keyframe values changed (resize).
  //
  // The crossfade at the loop boundary: opacity dips to 0 in the last
  // 0.6% of the cycle and rises back to 1 in the first 0.6% of the
  // next cycle. The loop snap itself is at 100% = 0%, where opacity
  // is 0 on both sides — the cards are invisible for the instant of
  // the snap, so the eye doesn't catch the wrapper teleport. The
  // fade itself is 0.36s at 60s (just long enough to feel like a
  // single exhale, short enough to not register as a "blink").
  //
  // Linear timing is required: any easing would pause the visible
  // motion at the loop boundary, making the snap back momentarily
  // visible.
  //
  // prefers-reduced-motion halts the animation entirely for users
  // with vestibular sensitivities.
  const fadePct = 0.6;
  const animId = `home-bg-scroll-${Date.now().toString(36)}`;
  const styleEl = document.createElement('style');
  styleEl.textContent = `@keyframes ${animId} {`
    + ` 0% { transform: translate3d(0, 0, 0); opacity: 0; }`
    + ` ${fadePct}% { transform: translate3d(${-cellW * fadePct / 100}px, ${cellH * fadePct / 100}px, 0); opacity: 1; }`
    + ` ${100 - fadePct}% { transform: translate3d(${-cellW * (100 - fadePct) / 100}px, ${cellH * (100 - fadePct) / 100}px, 0); opacity: 1; }`
    + ` 100% { transform: translate3d(${-cellW}px, ${cellH}px, 0); opacity: 0; }`
    + `} @media (prefers-reduced-motion: reduce) { .home-bg-scroll { animation: none !important; } }`;
  document.head.appendChild(styleEl);
  _homeBgField._keyframeStyle = styleEl;
  wrapper.style.animation = `${animId} ${_HOME_BG_SCROLL_DURATION_MS}ms linear infinite`;
}

function _populateHomeBgField() {
  _ensureHomeBgSample().then(_layoutHomeBgField);
}

function _clearHomeBgField() {
  _homeBgField.textContent = '';
}

// User Guide UI — fixed top-right pill button + modal overlay. Built once at module load
// and lives across page renders so home/community/create/world-select all share the same
// instance. Button is appended to lockHint so it auto-hides during gameplay (lockHint
// gets display:none on pointer-lock acquired). Modal lives on document.body so it can
// overlay any view without being clipped by lockHint's overflow.
// Easy mode — auto-smash assist. The game auto-fires a synthetic tap on every kick+snare onset
// at whatever block you're aimed at, timing tier pinned to GOOD, and manual clicks are inert.
// You can't miss and can't fail (you pass), but auto only ever scores good (flat +1 per block),
// so you cap around 1 star; perfects (and 2-3 stars) require playing manually with assist off.
// Aimed at the L1 insta-quit: no timing window, so audio lag + reaction time stop mattering.
// Hoisted here (not near the punch handlers where it's used) so the ASSIST toggle button below
// can read _easyMode at module load without a temporal-dead-zone crash.
// Persisted so the menu toggle survives reloads; ?easy=1 still force-enables it for a page load.
// Defaults ON for players who haven't chosen (no stored value) — same as Aim Assist — so new
// players get the friendliest setup; an explicit OFF choice ('0') is still respected.
const _EASY_MODE_KEY = 'smash-easy-mode-v1';
let _easyMode = new URLSearchParams(location.search).has('easy')
  || (() => { try { const v = localStorage.getItem(_EASY_MODE_KEY); return v === null ? true : v === '1'; } catch (e) { return true; } })();
// Race fairness: ?assist=on|off forces rhythm assist identically for every racer (it changes the coin
// economy, so a mixed race is meaningless). Overrides the saved/URL preference for the race page load.
if (_RACE_ON && _RACE_ASSIST !== null) _easyMode = _RACE_ASSIST;
function _setEasyMode(on) {
  _easyMode = !!on;
  try { localStorage.setItem(_EASY_MODE_KEY, _easyMode ? '1' : '0'); } catch (e) { /* storage blocked - session-only */ }
}
// Aim assist — separate from rhythm assist (_easyMode). ON (default) = forgiving cone
// acquisition + auto-face swing onto the picked block. OFF = precision: only the block
// directly under the crosshair targets, and the camera never auto-swings. Read in
// updateNear (acquisition) and at the firePunch auto-face callsite. Default ON.
const _AIM_ASSIST_KEY = 'smash-aim-assist-v1';
let _aimAssist = (() => { try { const v = localStorage.getItem(_AIM_ASSIST_KEY); return v === null ? true : v === '1'; } catch (e) { return true; } })();
function _setAimAssist(on) {
  _aimAssist = !!on;
  try { localStorage.setItem(_AIM_ASSIST_KEY, _aimAssist ? '1' : '0'); } catch (e) { /* storage blocked - session-only */ }
}
let _autoFiringNow = false;       // true while dispatching a synthetic auto-smash — skip timing grading
const _easyOnsetTimes = [];       // kick+snare onset times captured live from the drum scheduler
let _easyAltFist = 0;             // alternate fists for tap/ring audio variety
let _easyLastOnsetT = 0;          // audioCtx time of the last auto-punch onset — drives the countdown pill's pacing
let _easyWhiffs = 0;              // consecutive whiffed onsets in the current no-target stretch; 1 is forgiven, 2nd breaks
let _easyNoTargetStart = 0;       // audioCtx time the current no-target stretch began (0 = have a target) — pill pacing
// Assist streak decay: a bar-length grace window from the last landed hit. Stable within a song
// (one bar, not the volatile gap between onsets) so dense fills can't wrongly break it and slow
// songs aren't punished. Replaces the old per-onset whiff model.
let _easyStreakGraceUntil = 0;          // audioCtx time the streak fades at (0 = not yet armed); refreshed on every hit
const EASY_STREAK_GRACE_BARS = 1;       // grace window length in bars — consistent across all streak levels
// Assist charge: right button engages a bar-synced coin-suck loop. Coins vacuum into orbit over a
// fixed phrase, then auto-fire on the downbeat at the phrase end — no timing skill, the release is
// always on the beat. Re-arms each phrase while held. Drives charging[1] + pendingLaunch* directly.
let _easyPunchHeld = false;       // left mouse button currently held — gates the auto-punch stream
let _easyChargeStormCoins = false; // true only while a charge-storm break spawns loot — those coins keep full material × streak; all other assist breaks are flat +1

const _userGuideBtn = document.createElement('button');
_userGuideBtn.type = 'button';
_userGuideBtn.textContent = '? HOW TO PLAY';
// Position is set on _topRightBar below — the bar holds Discord and HOW TO PLAY
// side by side, fixed at top-right, content-aware (follows the 1400px content edge
// on wide viewports instead of the viewport's far right).
_userGuideBtn.style.cssText = 'font-family:var(--font-display);font-size:11px;letter-spacing:1px;'
  + 'padding:10px 14px;background:rgba(20,16,40,0.85);color:#cfd6dc;'
  + 'border:2px solid #555;cursor:pointer;text-transform:uppercase;pointer-events:auto;';
_userGuideBtn.addEventListener('mousedown', (e) => e.stopPropagation());
_userGuideBtn.addEventListener('mouseenter', () => {
  _userGuideBtn.style.background = 'rgba(40,32,80,0.95)';
  _userGuideBtn.style.borderColor = 'var(--col-cyan, #00E5FF)';
  _userGuideBtn.style.color = 'var(--col-cyan, #00E5FF)';
});
_userGuideBtn.addEventListener('mouseleave', () => {
  _userGuideBtn.style.background = 'rgba(20,16,40,0.85)';
  _userGuideBtn.style.borderColor = '#555';
  _userGuideBtn.style.color = '#cfd6dc';
});

// Top-right bar — fixed flex container that holds Discord + HOW TO PLAY side by side
// at the spot HOW TO PLAY used to occupy alone. Discord now rides next to the guide
// button on every page (it used to be a tiny 16px icon in the home-page footer only).
const _topRightBar = document.createElement('div');
_topRightBar.style.cssText = 'position:fixed;top:18px;right:max(18px, calc((100vw - 1400px) / 2 + 18px));z-index:50;'
  + 'display:flex;gap:10px;align-items:center;';

// Discord button — pill matching HOW TO PLAY's style, with the pixel-art Discord
// icon bumped from 16px (footer link) to 28px so it reads at top-right scale. Same
// hover treatment as the guide button: cyan border + brighter background on hover.
const _discordBtn = document.createElement('a');
_discordBtn.href = 'https://discord.gg/YvSxxrD7jj';
_discordBtn.target = '_blank';
_discordBtn.rel = 'noopener noreferrer';
_discordBtn.style.cssText = 'font-family:var(--font-display);font-size:11px;letter-spacing:1px;'
  + 'padding:10px 14px;background:rgba(20,16,40,0.85);color:#cfd6dc;'
  + 'border:2px solid #555;cursor:pointer;text-transform:uppercase;pointer-events:auto;'
  + 'text-decoration:none;display:inline-flex;align-items:center;gap:8px;';
// _DISCORD_ICON_URL is declared with `const` near the home-footer setup later in
// this file (~line 9271) — referencing it from here would hit the temporal dead
// zone and kill the whole module. _paintDiscordIcon is a hoisted function
// declaration, so we can call it directly to get the data URL.
const _discordIconUrl = _paintDiscordIcon();
const _discordIcon = document.createElement('span');
_discordIcon.style.cssText = 'display:inline-block;width:28px;height:28px;background-image:url(' + _discordIconUrl + ');'
  + 'background-size:contain;background-repeat:no-repeat;image-rendering:pixelated;flex:0 0 auto;';
_discordBtn.appendChild(_discordIcon);
const _discordText = document.createElement('span');
_discordText.textContent = 'Discord';
_discordBtn.appendChild(_discordText);
_discordBtn.addEventListener('mousedown', (e) => e.stopPropagation());
_discordBtn.addEventListener('mouseenter', () => {
  _discordBtn.style.background = 'rgba(40,32,80,0.95)';
  _discordBtn.style.borderColor = 'var(--col-cyan, #00E5FF)';
  _discordBtn.style.color = 'var(--col-cyan, #00E5FF)';
});
_discordBtn.addEventListener('mouseleave', () => {
  _discordBtn.style.background = 'rgba(20,16,40,0.85)';
  _discordBtn.style.borderColor = '#555';
  _discordBtn.style.color = '#cfd6dc';
});
// Assist toggle displays register a refresher here so a change made ANYWHERE (the home
// controls row OR the How to Play mode chips) updates every visible toggle live, instead of
// leaving stale "ON/OFF" text behind. The set is cleared + repopulated each home render.
const _assistToggleRefreshers = new Set();
function _notifyAssistToggleUIs() {
  for (const fn of [..._assistToggleRefreshers]) fn();
}

// Assist toggles live on the home screen now (see _renderHome's controls row), not the
// top-right bar. _makeAssistToggle builds a kit-styled pill that mirrors the Equip Gloves
// button it sits beside: lit cyan when ON, dim when OFF, label "<name>: ON/OFF". Fresh
// instances are created per home render (the home content is rebuilt each time).
function _makeAssistToggle(labelText, getOn, setOn) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'ss-btn ss-btn--secondary';
  function refresh() {
    const on = getOn();
    btn.textContent = labelText + ': ' + (on ? 'ON' : 'OFF');
    btn.style.background = on ? 'var(--col-cyan, #00E5FF)' : 'rgba(20,16,40,0.85)';
    btn.style.color = on ? 'var(--col-ink, #1a1a2e)' : '#cfd6dc';
  }
  refresh();
  _assistToggleRefreshers.add(refresh);
  btn.addEventListener('mousedown', (e) => e.stopPropagation());
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    setOn(!getOn());
    _notifyAssistToggleUIs();  // update this button + any other visible assist toggle
  });
  return btn;
}

// The player-controls strip (Rhythm/Aim assist + Equip Gloves) that used to sit on the home page.
// It now rides the surfaces where you are about to pick something to play (the per-world level grid
// and the Community page), so the front door can lead with Multiplayer instead. The ? HOW TO PLAY
// modal keeps live copies of the assist toggles as the universal fallback (UGC deep-links, first run).
function _buildPlayerControls() {
  const row = document.createElement('div');
  row.style.cssText = 'display:flex;gap:10px;align-items:center;justify-content:center;flex-wrap:wrap;margin:0 0 16px;';
  row.addEventListener('mousedown', (e) => e.stopPropagation());
  row.appendChild(_makeAssistToggle('Rhythm Assist', () => _easyMode, _setEasyMode));
  row.appendChild(_makeAssistToggle('Aim Assist', () => _aimAssist, _setAimAssist));
  if (_loadProgressionState().fullLoadoutFreedom) {
    const equipBtn = document.createElement('button');
    equipBtn.type = 'button';
    equipBtn.className = 'ss-btn ss-btn--secondary';
    equipBtn.style.background = 'var(--col-green)';
    equipBtn.textContent = 'Equip Gloves';
    equipBtn.addEventListener('mousedown', (e) => e.stopPropagation());
    equipBtn.onclick = (e) => { e.stopPropagation(); _homeView = 'gloves'; _currentWorld = null; _renderHome(); };
    row.appendChild(equipBtn);
  }
  return row;
}

// Home-page Multiplayer hero (Layout A): a full-width band under the tagline, above the world tiles,
// that makes online play the loudest thing on the page. Cyan keeps the established Multiplayer colour.
// A left text column (headline + blurb + PLAY) balances a VS scene on the right (two mascots facing off,
// reusing the existing /mascot.png asset), so the band reads as "you vs your friends" and never sparse.
// A NEW! badge overlaps the top corner. Whole band is clickable. Campaign + community stay below as the
// solo path (a cold visitor with no friend online can still act on those).
function _buildMpHero() {
  const hero = document.createElement('div');
  hero.style.cssText = 'position:relative;display:flex;align-items:center;justify-content:space-between;gap:18px;flex-wrap:wrap;'
    + 'width:min(720px, 94vw);margin:16px auto 6px;padding:16px 24px;box-sizing:border-box;'
    + 'background:var(--col-cyan, #00E5FF);border:4px solid var(--col-ink, #0a0820);'
    + 'box-shadow:6px 6px 0 var(--col-ink, #0a0820);border-radius:12px;cursor:pointer;'
    + 'transition:transform 130ms ease, box-shadow 130ms ease;';
  hero.addEventListener('mousedown', (e) => e.stopPropagation());

  // Left column — headline, blurb, PLAY. Width-capped so the blurb wraps to a tidy block instead of
  // stretching to one long line (which is what shoved PLAY around and left the right side empty before).
  const left = document.createElement('div');
  left.style.cssText = 'display:flex;flex-direction:column;align-items:flex-start;gap:9px;text-align:left;max-width:360px;min-width:0;';
  // Headline row: MULTIPLAYER + a live "ONLINE" pill so it's obvious these are internet matches with a
  // friend (share a link), not same-screen local play.
  const head = document.createElement('div');
  head.style.cssText = 'display:flex;align-items:center;gap:10px;flex-wrap:wrap;';
  const h = document.createElement('div');
  h.style.cssText = 'font-family:var(--font-display);font-size:22px;letter-spacing:1px;color:#fff;'
    + '-webkit-text-stroke:3.5px var(--col-ink, #0a0820);paint-order:stroke;text-transform:uppercase;line-height:1.1;';
  h.textContent = 'Multiplayer';
  const onlineTag = document.createElement('div');
  onlineTag.style.cssText = 'display:inline-flex;align-items:center;gap:5px;font-family:var(--font-display);'
    + 'font-size:10px;letter-spacing:1px;color:#fff;background:var(--col-ink, #0a0820);border-radius:5px;'
    + 'padding:4px 7px;text-transform:uppercase;';
  const dot = document.createElement('span');
  dot.style.cssText = 'width:8px;height:8px;border-radius:50%;background:var(--col-green, #43d675);'
    + 'box-shadow:0 0 0 2px rgba(67,214,117,0.4);flex:0 0 auto;';
  onlineTag.append(dot, document.createTextNode('Online'));
  head.append(h, onlineTag);
  const sub = document.createElement('div');
  // Same face as the CREATE / DISCOVER tile subtext (Jersey 25) so the home page's supporting copy rhymes.
  sub.style.cssText = 'font-family:"Jersey 25", var(--font-body, ui-monospace), monospace;font-size:23px;line-height:1.08;'
    + 'letter-spacing:0.5px;color:var(--col-ink, #0a0820);';
  sub.textContent = 'You vs your friends! Most smashes or coins wins. Up to 4 players.';
  const play = document.createElement('button');
  play.type = 'button';
  play.className = 'ss-btn';
  play.style.cssText = 'background:var(--col-green, #43d675);color:#fff;-webkit-text-stroke:2px var(--col-ink);paint-order:stroke;'
    + 'font-family:var(--font-display);font-size:16px;padding:13px 26px;white-space:nowrap;margin-top:2px;';
  play.textContent = 'PLAY ▶';
  left.append(head, sub, play);

  // Right column — the VS scene: FOUR mascots (up to 4 players) squaring up around a chunky VS. Two on
  // each side face inward toward the VS; a slight stagger + overlap reads as a lively free-for-all scrum
  // (everyone competing, most smashes wins) rather than two tidy teams, and the fuller cluster fills what
  // was an empty band. Reuses /mascot.png; the game's avatar is this same top-hatted gent.
  const vs = document.createElement('div');
  vs.style.cssText = 'display:flex;align-items:center;flex:0 0 auto;';
  // flip=true faces left (for the right-hand pair); lift staggers the crowd; z layers the overlap; lean is
  // the px each mascot crowds toward the VS on hover (outer pair leans further for a lively scrum).
  const mascots = [];
  const mascot = (flip, lift, z, lean) => {
    const img = document.createElement('img');
    img.src = '/mascot.png'; img.alt = '';
    const flipS = flip ? ' scaleX(-1)' : '';
    const base = 'translate(0px,' + lift + 'px)' + flipS;
    const hover = 'translate(' + lean + 'px,' + (lift - 3) + 'px)' + flipS;   // crowd inward + a little hop
    img.style.cssText = 'width:62px;height:62px;image-rendering:pixelated;position:relative;margin:0 -7px;'
      + 'z-index:' + z + ';transform:' + base + ';transition:transform 170ms cubic-bezier(0.34,1.56,0.64,1);';
    mascots.push({ img, base, hover });
    return img;
  };
  const vsTxt = document.createElement('div');
  vsTxt.style.cssText = 'font-family:var(--font-display);font-size:24px;color:#fff;position:relative;z-index:5;margin:0 3px;'
    + '-webkit-text-stroke:3px var(--col-ink, #0a0820);paint-order:stroke;'
    + 'transition:transform 170ms cubic-bezier(0.34,1.56,0.64,1);';
  vsTxt.textContent = 'VS';
  vs.append(mascot(false, 5, 1, 7), mascot(false, -3, 2, 3), vsTxt, mascot(true, -3, 2, -3), mascot(true, 5, 1, -7));

  // NEW! badge — pixel starburst-style pill overlapping the top corner. Yellow to echo the brand title.
  const badge = document.createElement('div');
  badge.textContent = 'NEW!';
  badge.style.cssText = 'position:absolute;top:-13px;right:-11px;transform:rotate(9deg);'
    + 'font-family:var(--font-display);font-size:13px;letter-spacing:1px;color:var(--col-ink, #0a0820);'
    + 'background:var(--col-bg-panel, #FFE600);border:3px solid var(--col-ink, #0a0820);'
    + 'box-shadow:3px 3px 0 var(--col-ink, #0a0820);padding:5px 9px;border-radius:6px;'
    + 'text-transform:uppercase;pointer-events:none;';

  // Hover responsiveness — matches the kit's chunky-shadow idiom (like the level cards): the whole panel
  // lifts up-left with a deeper drop shadow, and the mascots crowd in toward the VS which pops, selling
  // the "squaring up" energy. Everything eases back on leave.
  hero.addEventListener('mouseenter', () => {
    hero.style.transform = 'translate(-3px, -3px)';
    hero.style.boxShadow = '9px 9px 0 var(--col-ink, #0a0820)';
    for (const m of mascots) m.img.style.transform = m.hover;
    vsTxt.style.transform = 'scale(1.2) rotate(-4deg)';
  });
  hero.addEventListener('mouseleave', () => {
    hero.style.transform = 'translate(0, 0)';
    hero.style.boxShadow = '6px 6px 0 var(--col-ink, #0a0820)';
    for (const m of mascots) m.img.style.transform = m.base;
    vsTxt.style.transform = 'none';
  });

  const open = (e) => { e.stopPropagation(); _fireAnalytics('home_cta_click', { levelId: 'multiplayer' }); _openMpModal(); };
  play.onclick = open;
  hero.onclick = open;
  hero.append(left, vs, badge);
  return hero;
}

_topRightBar.appendChild(_discordBtn);
_topRightBar.appendChild(_userGuideBtn);
lockHint.appendChild(_topRightBar);

// Fixed back-to-home button in the top-left of the viewport — mirrors HOW TO PLAY on
// the right. Visible on subpages (community, create, world view, ready); hidden on
// the home page itself where the greeting takes that spot. Replaces the inline back
// buttons that used to live in each subpage's header.
const _backBtn = document.createElement('button');
_backBtn.type = 'button';
_backBtn.textContent = '◀ HOME';
_backBtn.style.cssText = 'position:fixed;top:8px;left:max(18px, calc((100vw - 1400px) / 2 + 18px));z-index:50;'
  + 'font-family:var(--font-display);font-size:11px;letter-spacing:1px;'
  + 'padding:10px 14px;background:var(--col-pink, #FF3B6B);color:var(--col-ink, #1a1a2e);'
  + 'border:3px solid var(--col-ink, #1a1a2e);box-shadow:3px 3px 0 var(--col-ink, #0a0820);'
  + 'cursor:pointer;text-transform:uppercase;display:none;';
_backBtn.addEventListener('mousedown', (e) => e.stopPropagation());
_backBtn.addEventListener('mouseenter', () => { _backBtn.style.background = 'var(--col-bg-panel)'; });
_backBtn.addEventListener('mouseleave', () => { _backBtn.style.background = 'var(--col-pink, #FF3B6B)'; });
_backBtn.onclick = (e) => {
  e.stopPropagation();
  _currentWorld = null;
  _homeView = 'world-select';
  _renderHome();
};
lockHint.appendChild(_backBtn);

// Personal greeting in the top-left of the viewport — mirrors the HOW TO PLAY button
// position on the right. Fixed so it doesn't take vertical space in the page flow.
// Hidden when no nickname is set (first-time visitors don't see "Welcome back").
const _greetingEl = document.createElement('div');
_greetingEl.style.cssText = 'position:fixed;top:8px;left:max(18px, calc((100vw - 1400px) / 2 + 18px));z-index:50;'
  + 'font-family:var(--font-display);font-size:14px;letter-spacing:2px;'
  + 'color:#fff;-webkit-text-stroke:2px var(--col-ink, #1a1a2e);paint-order:stroke;'
  + 'padding:6px 14px;text-transform:uppercase;pointer-events:none;display:none;';
lockHint.appendChild(_greetingEl);

function _refreshGreeting() {
  const name = (_loadPlayerName() || '').trim();
  // Only on the home (world-select) view. Hide on community / create / world-view so
  // the greeting doesn't compete with those pages' back-to-home button.
  const onHome = !_currentWorld && _homeView === 'world-select';
  if (name && onHome) {
    _greetingEl.textContent = `Welcome back, ${name}!`;
    _greetingEl.style.display = 'block';
  } else {
    _greetingEl.style.display = 'none';
  }
  // Inverse — back button visible on subpages, hidden on home (where greeting lives).
  _backBtn.style.display = onHome ? 'none' : 'block';
}

// Modal — built lazily the first time it's opened so the home page paints fast on
// initial load. Same controls/tips content as the old inline panel, plus an X close,
// Esc-to-close, and click-backdrop-to-close.
const _userGuideModal = document.createElement('div');
_userGuideModal.style.cssText = 'position:fixed;inset:0;z-index:60;display:none;'
  + 'align-items:center;justify-content:center;padding:40px;'
  + 'background:rgba(0,0,0,0.7);pointer-events:auto;';
document.body.appendChild(_userGuideModal);

// Mouse glyph for the controls help: a rounded body with two top buttons + a divider; the
// active button (left/right) fills with the cyan accent so a binding reads at a glance.
// Supersampled then downscaled for crisp edges. Cached per side (neutral/left/right).
let _mouseIconCache = null;
function _paintMouseIcon(litSide) {
  const S = 4, W = 28, H = 40;
  const c = document.createElement('canvas');
  c.width = W * S; c.height = H * S;
  const ctx = c.getContext('2d');
  ctx.scale(S, S);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  const ink = '#0a0820', light = '#eef1f5', accent = '#00E5FF';
  const body = () => { ctx.beginPath(); ctx.roundRect(4, 3, 20, 34, [11, 11, 10, 10]); };
  body(); ctx.fillStyle = light; ctx.fill();
  if (litSide) {
    ctx.save(); body(); ctx.clip();
    ctx.fillStyle = accent;
    if (litSide === 'left') ctx.fillRect(3, 2, 11, 14);
    else ctx.fillRect(14, 2, 12, 14);
    ctx.restore();
  }
  body(); ctx.strokeStyle = ink; ctx.lineWidth = 3; ctx.stroke();
  ctx.lineWidth = 2.4;
  ctx.beginPath(); ctx.moveTo(14, 3.5); ctx.lineTo(14, 16); ctx.stroke();  // button divider
  ctx.beginPath(); ctx.moveTo(5, 16); ctx.lineTo(23, 16); ctx.stroke();    // button base line
  return c.toDataURL();
}
function _getMouseIcon(side) {
  if (!_mouseIconCache) {
    _mouseIconCache = {
      neutral: _paintMouseIcon(null),
      left: _paintMouseIcon('left'),
      right: _paintMouseIcon('right'),
    };
  }
  return _mouseIconCache[side || 'neutral'];
}

// =========================================================
// Level 1 coach card — a small, reactive, MODE-AWARE controls hint shown only on Level 1.
// It is not a blocking modal (pointer-events:none, sits at the bottom of the screen) and it
// advances itself as the player acts, so it teaches by responding rather than dumping text:
//   approach -> "walk up to / aim at a block"  (advances when a block is acquired = nearTarget)
//   smash    -> "hold / tap to smash it"        (advances on the first landed hit, via applyHit)
//   done     -> shrinks to a tiny reminder chip for the rest of the level.
// Wording follows the player's LIVE assist choices (Aim Assist -> walk vs aim; Rhythm Assist ->
// hold vs tap), the same idea as _buildHowtoColumns, so it never lies about the scheme actually
// in use. Shown every time Level 1 is played; lifecycle tied to _setupLevel('level1') (arms it)
// + the pointer-lock show/hide. Appended to document.body (not lockHint, which hides in-game).
// =========================================================
(function _installCoachStyles() {
  const st = document.createElement('style');
  st.textContent =
    '@keyframes ss-coach-in{0%{opacity:0;transform:translateX(-50%) translateY(14px) scale(0.9);}100%{opacity:1;transform:translateX(-50%) translateY(0) scale(1);}}'
    + '@keyframes ss-coach-pop{0%{transform:translateX(-50%) scale(1);}40%{transform:translateX(-50%) scale(1.1);}100%{transform:translateX(-50%) scale(1);}}'
    + '@keyframes ss-coach-bob{0%,100%{transform:translateY(0);}50%{transform:translateY(-3px);}}'
    + '.ss-coach-bob{animation:ss-coach-bob 0.9s ease-in-out infinite;}';
  document.head.appendChild(st);
})();

const _coachCard = document.createElement('div');
_coachCard.style.cssText = 'position:fixed;left:50%;bottom:96px;transform:translateX(-50%);z-index:55;'
  + 'display:none;flex-direction:column;align-items:center;gap:8px;pointer-events:none;'
  + 'background:#ffffff;border:3px solid var(--col-ink,#1a1a2e);box-shadow:4px 4px 0 var(--col-ink,#1a1a2e);'
  + 'border-radius:6px;padding:12px 18px;max-width:min(86vw,360px);';
document.body.appendChild(_coachCard);

let _coachActive = false;
let _coachPhase = 'approach';   // 'approach' | 'smash' | 'done'
let _coachSeenSmash = false;
let _coachMinimizeTimer = null;
let _coachApproachShownAt = 0;
// Minimum time the "walk up to a block" step stays up before a target-acquire can advance it.
// L1 spawns the player ~9m from the first crate (inside the 13m auto-aim cone), so without this
// the block is acquired on frame one and the approach step would never actually read.
const _COACH_MIN_APPROACH_MS = 2000;

function _coachKeycap(label) {
  const k = document.createElement('span');
  k.textContent = label;
  k.style.cssText = 'display:inline-flex;align-items:center;justify-content:center;'
    + 'font-family:var(--font-display);font-size:10px;letter-spacing:1px;text-transform:uppercase;'
    + 'min-width:14px;height:24px;padding:0 8px;color:var(--col-ink);background:#e7eaf0;'
    + 'border:3px solid var(--col-ink);border-radius:4px;box-shadow:0 2px 0 var(--col-ink);';
  return k;
}
function _coachMouse(side, bob) {
  const img = document.createElement('img');
  img.src = _getMouseIcon(side);
  img.alt = '';
  img.style.cssText = 'height:34px;width:auto;display:block;';
  if (bob) img.className = 'ss-coach-bob';
  return img;
}
function _coachWord(text) {
  const h = document.createElement('span');
  h.textContent = text;
  h.style.cssText = 'font-family:var(--font-body);font-size:11px;color:var(--col-ink);opacity:0.7;';
  return h;
}

function _coachRender() {
  _coachCard.replaceChildren();

  // Minimized reminder — just the verb + mouse glyph, small and out of the way.
  if (_coachPhase === 'done') {
    _coachCard.style.padding = '7px 12px';
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;align-items:center;gap:7px;';
    const verb = document.createElement('span');
    verb.style.cssText = 'font-family:var(--font-display);font-size:11px;letter-spacing:1px;text-transform:uppercase;color:var(--col-ink);';
    verb.textContent = _easyMode ? 'Hold to smash' : 'Tap to smash';
    row.appendChild(verb);
    row.appendChild(_coachMouse('left', false));
    _coachCard.appendChild(row);
    return;
  }

  _coachCard.style.padding = '12px 18px';
  const head = document.createElement('div');
  head.style.cssText = 'font-family:var(--font-display);font-size:15px;letter-spacing:1px;text-transform:uppercase;'
    + 'color:var(--col-ink);text-align:center;';
  const tokRow = document.createElement('div');
  tokRow.style.cssText = 'display:flex;align-items:center;justify-content:center;gap:6px;flex-wrap:wrap;';

  if (_coachPhase === 'approach') {
    // Aim Assist ON: auto-targets, so the action is just to get near. OFF: line up the crosshair.
    head.textContent = _aimAssist ? 'Walk up to a block' : 'Aim at a block';
    tokRow.appendChild(_coachKeycap('W'));
    tokRow.appendChild(_coachKeycap('A'));
    tokRow.appendChild(_coachKeycap('S'));
    tokRow.appendChild(_coachKeycap('D'));
    if (!_aimAssist) {
      tokRow.appendChild(_coachMouse('neutral', true));
      tokRow.appendChild(_coachWord('look'));
    }
  } else {  // 'smash' — Rhythm Assist ON: hold to auto-smash. OFF: tap (a click breaks a crate).
    head.textContent = _easyMode ? 'Hold to smash it!' : 'Tap to smash it!';
    tokRow.appendChild(_coachMouse('left', true));
    tokRow.appendChild(_coachWord(_easyMode ? 'hold' : 'tap'));
    const orSep = document.createElement('span');
    orSep.textContent = 'or';
    orSep.style.cssText = 'font-family:var(--font-body);font-size:11px;color:var(--col-ink);opacity:0.55;margin:0 1px;';
    tokRow.appendChild(orSep);
    tokRow.appendChild(_coachKeycap('Space'));
  }
  _coachCard.appendChild(head);
  _coachCard.appendChild(tokRow);
}

// Arm the coach for a fresh Level 1 run. Called from _setupLevel; non-L1 levels disarm it.
function _coachReset() {
  if (_coachMinimizeTimer) { clearTimeout(_coachMinimizeTimer); _coachMinimizeTimer = null; }
  _coachActive = true;
  _coachPhase = 'approach';
  _coachSeenSmash = false;
}
function _coachShow() {
  if (!_coachActive) return;
  if (_coachPhase === 'approach') _coachApproachShownAt = performance.now();
  _coachRender();
  _coachCard.style.display = 'flex';
  _coachCard.style.animation = 'ss-coach-in 0.32s cubic-bezier(0.34,1.56,0.64,1) both';
}
function _coachHide() {
  _coachCard.style.display = 'none';
}
function _coachDisarm() {
  _coachActive = false;
  if (_coachMinimizeTimer) { clearTimeout(_coachMinimizeTimer); _coachMinimizeTimer = null; }
  _coachHide();
}
// approach -> smash, fired from the render loop when a block gets acquired.
function _coachToSmash() {
  if (!_coachActive || _coachPhase !== 'approach') return;
  _coachPhase = 'smash';
  _coachRender();
  _coachCard.style.animation = 'ss-coach-pop 0.3s ease-out both';
}
// First landed hit on this level — celebrate, then minimize to a small persistent reminder.
function _coachNoteSmash() {
  if (!_coachActive || _coachSeenSmash) return;
  _coachSeenSmash = true;
  _coachCard.replaceChildren();
  _coachCard.style.padding = '12px 18px';
  const nice = document.createElement('div');
  nice.style.cssText = 'font-family:var(--font-display);font-size:16px;letter-spacing:1px;text-transform:uppercase;color:var(--col-ink);text-align:center;';
  nice.textContent = 'Nice! Keep going';
  _coachCard.appendChild(nice);
  _coachCard.style.animation = 'ss-coach-pop 0.34s ease-out both';
  _coachMinimizeTimer = setTimeout(() => {
    _coachMinimizeTimer = null;
    if (!_coachActive) return;
    _coachPhase = 'done';
    _coachRender();
  }, 1100);
}

// Shared CONTROLS + HOW TO PLAY columns builder. Used by the help modal and by the
// always-on inline panel on the level-select page. Mode-aware (reads the assist toggles).
function _buildHowtoColumns(rebuild) {
  // MODE-AWARE. Reads the live assist toggles (_easyMode = Rhythm Assist, _aimAssist) and
  // renders the matching scheme, so the controls never lie about the player's chosen mode.
  // Both surfaces (help modal + inline level-select panel) rebuild from this on open, so they
  // always reflect the current toggle state. Layout: two mode chips along the top, then
  // CONTROLS organised by FUNCTION (Move | Smash) with mouse+key bindings combined on one
  // line, then TIPS. The modal layers BLOCKS + MOVES underneath via _buildHowtoExtras.
  const container = document.createElement('div');
  container.style.cssText = 'display:flex;flex-direction:column;gap:16px;width:100%;align-items:center;';

  // Toggling a chip flips the real assist state (single source of truth) then rebuilds so the
  // chips AND the mode-aware controls update. Modal passes a full-panel rebuild (to refresh the
  // BLOCKS/MOVES extras too); without one we replace just this columns block in place.
  const doRebuild = rebuild || (() => container.replaceWith(_buildHowtoColumns()));

  // Mode chips — compact pills showing BOTH assist states with a SHORT adaptive line, and now
  // CLICKABLE: tapping one flips that mode right here. Styled like the home-screen toggles
  // (cyan on, dim off) so the player connects them.
  const makeModeChip = (label, on, onDesc, offDesc, setter) => {
    const chip = document.createElement('button');
    chip.type = 'button';
    chip.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:3px;font:inherit;'
      + 'padding:8px 16px;border:3px solid var(--col-ink);box-shadow:2px 2px 0 var(--col-ink);'
      + 'min-width:150px;text-align:center;cursor:pointer;'
      + (on ? 'background:var(--col-cyan, #00E5FF);color:var(--col-ink, #1a1a2e);'
            : 'background:rgba(20,16,40,0.85);color:#cfd6dc;');
    const name = document.createElement('div');
    name.style.cssText = 'font-family:var(--font-display);font-size:10px;letter-spacing:1px;text-transform:uppercase;white-space:nowrap;';
    name.textContent = `${label}: ${on ? 'ON' : 'OFF'}`;
    const desc = document.createElement('div');
    desc.style.cssText = 'font-family:var(--font-body);font-size:10px;line-height:1.3;white-space:nowrap;';
    desc.textContent = on ? onDesc : offDesc;
    chip.appendChild(name);
    chip.appendChild(desc);
    chip.addEventListener('mousedown', (e) => e.stopPropagation());
    chip.addEventListener('mouseenter', () => { chip.style.transform = 'translate(-1px,-1px)'; chip.style.boxShadow = '3px 3px 0 var(--col-ink)'; });
    chip.addEventListener('mouseleave', () => { chip.style.transform = ''; chip.style.boxShadow = '2px 2px 0 var(--col-ink)'; });
    chip.addEventListener('click', (e) => { e.stopPropagation(); setter(!on); _notifyAssistToggleUIs(); doRebuild(); });
    return chip;
  };
  const modeRow = document.createElement('div');
  modeRow.style.cssText = 'display:flex;justify-content:center;align-items:stretch;gap:12px;flex-wrap:wrap;';
  modeRow.appendChild(makeModeChip('Rhythm Assist', _easyMode, 'We smash in time', 'You time each hit', _setEasyMode));
  modeRow.appendChild(makeModeChip('Aim Assist', _aimAssist, 'Auto-aims for you', 'You aim yourself', _setAimAssist));
  container.appendChild(modeRow);

  // CONTROLS — contained in a chunky kit card so it reads as one designed block (not a list
  // floating on green). Organised by function (Move/Aim | Smash); each binding shows pixel
  // input tokens (mouse glyph + keycaps) so it's scannable, not a wall of text. Mode-aware.
  const controlsSection = document.createElement('div');
  controlsSection.style.cssText = 'display:flex;flex-direction:column;align-items:center;width:100%;';
  const controlsTitle = document.createElement('div');
  controlsTitle.className = 'ss-howto__col-title';
  controlsTitle.style.cssText += 'text-align:center;margin-bottom:8px;';
  controlsTitle.textContent = 'CONTROLS';
  controlsSection.appendChild(controlsTitle);

  // Pixel input tokens.
  const keycap = (label) => {
    const k = document.createElement('span');
    k.textContent = label;
    k.style.cssText = 'display:inline-flex;align-items:center;justify-content:center;'
      + 'font-family:var(--font-display);font-size:9px;letter-spacing:1px;text-transform:uppercase;'
      + 'min-width:13px;height:21px;padding:0 7px;color:var(--col-ink);background:#e7eaf0;'
      + 'border:3px solid var(--col-ink);border-radius:4px;box-shadow:0 2px 0 var(--col-ink);';
    return k;
  };
  const mouseTok = (side) => {
    const img = document.createElement('img');
    img.src = _getMouseIcon(side);
    img.alt = '';
    img.style.cssText = 'height:28px;width:auto;display:block;flex:0 0 auto;';
    return img;
  };
  const sep = () => {
    const s = document.createElement('span');
    s.textContent = '/';
    s.style.cssText = 'font-family:var(--font-display);font-size:11px;color:var(--col-ink);opacity:0.45;margin:0 1px;';
    return s;
  };
  const hint = (text) => {
    const h = document.createElement('span');
    h.textContent = text;
    h.style.cssText = 'font-family:var(--font-body);font-size:10px;color:var(--col-ink);opacity:0.7;margin-left:2px;';
    return h;
  };

  const grid = document.createElement('div');
  grid.style.cssText = 'display:grid;grid-template-columns:max-content max-content;'
    + 'column-gap:16px;row-gap:9px;align-items:center;justify-content:center;width:100%;';
  let _firstHeader = true;
  const addHeader = (text) => {
    const wrap = document.createElement('div');
    wrap.style.cssText = 'grid-column:1 / -1;display:flex;align-items:center;gap:10px;'
      + `margin-top:${_firstHeader ? 0 : 12}px;margin-bottom:1px;`;
    const line = () => { const l = document.createElement('div'); l.style.cssText = 'flex:1;height:3px;background:var(--col-ink);opacity:0.2;'; return l; };
    const lbl = document.createElement('span');
    lbl.style.cssText = 'font-family:var(--font-display);font-size:10px;letter-spacing:2px;'
      + 'color:var(--col-ink);text-transform:uppercase;opacity:0.85;';
    lbl.textContent = text;
    wrap.appendChild(line()); wrap.appendChild(lbl); wrap.appendChild(line());
    grid.appendChild(wrap);
    _firstHeader = false;
  };
  const addRow = (action, tokens) => {
    const a = document.createElement('div');
    a.style.cssText = 'justify-self:end;font-family:var(--font-display);font-size:11px;'
      + 'letter-spacing:1px;color:var(--col-ink);text-transform:uppercase;';
    a.textContent = action;
    const cell = document.createElement('div');
    cell.style.cssText = 'justify-self:start;display:flex;align-items:center;gap:5px;flex-wrap:wrap;';
    for (const t of tokens) cell.appendChild(t);
    grid.appendChild(a);
    grid.appendChild(cell);
  };

  addHeader('Move / Aim');
  addRow('Move', [keycap('W'), keycap('A'), keycap('S'), keycap('D')]);
  addRow('Aim', [mouseTok('neutral'), hint('move')]);
  addRow('Sprint', [keycap('E'), hint('hold')]);

  // Smash swaps with Rhythm Assist: normal = two fists + charge; assist = hold to auto-smash
  // + right hand charges both fists.
  addHeader('Smash');
  if (_easyMode) {
    addRow('Auto-Smash', [mouseTok('left'), sep(), keycap('Space'), hint('hold')]);
    addRow('Charge & Fire Coins', [mouseTok('right'), sep(), keycap('R-Alt'), hint('hold')]);
  } else {
    addRow('Left Fist', [mouseTok('left'), sep(), keycap('Space')]);
    addRow('Right Fist', [mouseTok('right'), sep(), keycap('R-Alt')]);
    addRow('Charge & Fire Coins', [mouseTok('neutral'), hint('hold either')]);
  }

  const card = document.createElement('div');
  // White polaroid-style inset so it stands out from the yellow panel (the dark-tint version
  // just read as muddy yellow). Matches the kit's white panels inside yellow cards elsewhere.
  card.style.cssText = 'background:#ffffff;border:3px solid var(--col-ink);'
    + 'box-shadow:4px 4px 0 var(--col-ink);border-radius:4px;padding:16px 24px 18px;'
    + 'width:100%;max-width:420px;box-sizing:border-box;';
  card.appendChild(grid);
  controlsSection.appendChild(card);

  // Fine print under the card: menu key + the one-line trackpad tip (kept because the failure
  // is invisible — a dropped trackpad click looks identical to a mistimed one).
  const footer = document.createElement('div');
  footer.style.cssText = 'font-family:var(--font-body);font-size:10px;color:var(--col-ink);'
    + 'opacity:0.65;text-align:center;margin-top:10px;line-height:1.5;max-width:380px;';
  footer.innerHTML = 'Menu: <b>ESC</b><br>Laptop trackpad? Use SPACE / RIGHT ALT to smash.';
  controlsSection.appendChild(footer);

  container.appendChild(controlsSection);

  // TIPS section — high-level "what to do" pointers. The lead tip adapts to Rhythm Assist
  // (in assist the game handles timing, so "smash on beat" would mislead).
  const tipsSection = document.createElement('div');
  tipsSection.className = 'ss-howto__col';
  const tipsTitle = document.createElement('div');
  tipsTitle.className = 'ss-howto__col-title';
  tipsTitle.style.textAlign = 'center';
  tipsTitle.textContent = 'TIPS';
  tipsSection.appendChild(tipsTitle);
  const tipsList = document.createElement('div');
  tipsList.className = 'ss-howto__tips';
  const leadTip = _easyMode
    ? 'Aim at a block and hold to smash it, we handle the timing'
    : 'Aim at a block and smash it on the beat for the most coins';
  for (const tipText of [
    leadTip,
    'The level ends when you smash all the blocks',
    'Hit the coin goal to unlock the next level',
    'Build streaks for big multipliers',
    'Earn 3 stars on every level',
  ]) {
    const tip = document.createElement('div');
    tip.className = 'ss-howto__tip';
    const bullet = document.createElement('span');
    bullet.className = 'ss-howto__tip-bullet';
    bullet.textContent = '★';
    tip.appendChild(bullet);
    tip.appendChild(document.createTextNode(tipText));
    tipsList.appendChild(tip);
  }
  tipsSection.appendChild(tipsList);
  container.appendChild(tipsSection);

  return container;
}

// Modal-only extras: BLOCKS table (hits + coin yield per material), one-line scoring
// summary, and a small MOVES tip set covering the charge mechanic + metal cascade. NOT
// included in the always-on inline panel — that one stays the 2-column quick reference.
function _buildHowtoExtras() {
  const wrap = document.createElement('div');
  wrap.style.cssText = 'display:flex;flex-direction:column;gap:18px;margin-top:24px;'
    + 'border-top:4px solid var(--col-ink, #0a0820);padding-top:20px;';

  // BLOCKS row — 3 chunky material-tinted chips. Background colors mirror the in-game
  // texture base colors (paintCrateTexture/Brick/Metal) so players see the same tan /
  // red / silver they recognize from the level. Ink-colored text reads on all three.
  const blocksSection = document.createElement('div');
  const blocksTitle = document.createElement('div');
  blocksTitle.className = 'ss-howto__col-title';
  blocksTitle.style.textAlign = 'center';
  blocksTitle.textContent = 'BLOCKS';
  blocksSection.appendChild(blocksTitle);

  const blocksRow = document.createElement('div');
  blocksRow.style.cssText = 'display:flex;gap:12px;margin-top:8px;justify-content:center;';
  for (const { name, color, hits, coins } of [
    { name: 'CRATE', color: '#d9a04a', hits: 1, coins: 1 },
    { name: 'BRICK', color: '#c4684f', hits: 2, coins: 3 },
    { name: 'METAL', color: '#9a9ea6', hits: 3, coins: 5 },
  ]) {
    const chip = document.createElement('div');
    chip.style.cssText = `flex:1;max-width:170px;background:${color};`
      + 'border:var(--border-chunky, 4px solid #0a0820);'
      + 'box-shadow:var(--shadow-hard-sm, 3px 3px 0 #0a0820);'
      + 'padding:12px 8px;text-align:center;';
    const nm = document.createElement('div');
    nm.style.cssText = 'font-family:var(--font-display);font-size:14px;letter-spacing:2px;'
      + 'color:var(--col-ink, #0a0820);text-transform:uppercase;'
      + 'text-shadow:1px 1px 0 rgba(255,255,255,0.25);';
    nm.textContent = name;
    chip.appendChild(nm);
    const hitsLine = document.createElement('div');
    hitsLine.style.cssText = 'font-family:var(--font-display);font-size:10px;letter-spacing:1px;'
      + 'color:var(--col-ink, #0a0820);margin-top:6px;';
    hitsLine.textContent = `${hits} HIT${hits > 1 ? 'S' : ''}`;
    chip.appendChild(hitsLine);
    const coinsLine = document.createElement('div');
    coinsLine.style.cssText = 'font-family:var(--font-display);font-size:10px;letter-spacing:1px;'
      + 'color:var(--col-ink, #0a0820);margin-top:2px;';
    coinsLine.textContent = `${coins} COIN${coins > 1 ? 'S' : ''}`;
    chip.appendChild(coinsLine);
    blocksRow.appendChild(chip);
  }
  blocksSection.appendChild(blocksRow);

  wrap.appendChild(blocksSection);

  // MOVES — three high-leverage tips for mechanics that aren't obvious from playing.
  // Kept short; the full manual is a separate, future surface.
  const movesSection = document.createElement('div');
  const movesTitle = document.createElement('div');
  movesTitle.className = 'ss-howto__col-title';
  movesTitle.style.textAlign = 'center';
  movesTitle.textContent = 'MOVES';
  movesSection.appendChild(movesTitle);

  const movesList = document.createElement('div');
  movesList.className = 'ss-howto__tips';
  movesList.style.marginTop = '8px';
  // Charge moves adapt to Rhythm Assist: in assist the charge is the right hand and arms
  // both fists, and the player isn't timing the release (on-beat just pays a bonus).
  const moveTips = _easyMode
    ? [
        'Hold Right Mouse / Right Alt to charge, floor coins suck toward both fists',
        'Release to fire them at your target; an on-beat release pays a bonus',
        'Streaks multiply coins: x1.25 (5) · x1.5 (10) · x2 (20) · x3 (50)',
      ]
    : [
        'Hold a fist button to charge, floor coins suck toward your fist',
        'Release on the beat to fire them at your target for bigger payouts',
        'Streaks multiply coins: x1.25 (5) · x1.5 (10) · x2 (20) · x3 (50)',
      ];
  for (const tipText of moveTips) {
    const tip = document.createElement('div');
    tip.className = 'ss-howto__tip';
    const bullet = document.createElement('span');
    bullet.className = 'ss-howto__tip-bullet';
    bullet.textContent = '★';
    tip.appendChild(bullet);
    tip.appendChild(document.createTextNode(tipText));
    movesList.appendChild(tip);
  }
  movesSection.appendChild(movesList);
  wrap.appendChild(movesSection);

  return wrap;
}

function _buildUserGuideModalContent() {
  const panel = document.createElement('div');
  panel.className = 'ss-howto';
  panel.style.cssText = 'position:relative;max-width:720px;width:100%;max-height:80vh;overflow-y:auto;';

  const title = document.createElement('div');
  // Match the kit's column-title treatment (pink + dark stroke via paint-order) so the
  // title reads cleanly against the green panel. Bumped size 14px (vs 11px col-titles)
  // so it sits at panel-heading weight, not column-heading weight.
  title.style.cssText = 'font-family:var(--font-display);font-size:14px;letter-spacing:3px;'
    + 'color:var(--col-pink, #FF3B6B);'
    + '-webkit-text-stroke:2px var(--col-ink);paint-order:stroke;'
    + 'text-align:center;margin:0 0 18px;text-transform:uppercase;';
  title.textContent = 'How to Play';
  panel.appendChild(title);

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.textContent = '✕';
  // Dark ink on transparent panel — invisible. Use a kit-style chunky button: dark ink
  // background, light label, chunky border. Reads as "close" at a glance against green.
  closeBtn.style.cssText = 'position:absolute;top:8px;right:8px;font-family:var(--font-display);'
    + 'font-size:18px;width:36px;height:36px;background:var(--col-ink, #1a1a2e);color:#fff;'
    + 'border:3px solid var(--col-ink, #1a1a2e);box-shadow:var(--shadow-hard-sm);cursor:pointer;'
    + 'padding:0;line-height:1;';
  closeBtn.addEventListener('mousedown', (e) => e.stopPropagation());
  closeBtn.onclick = (e) => { e.stopPropagation(); _userGuideModal.style.display = 'none'; };
  panel.appendChild(closeBtn);

  // Pass the full-panel rebuild so a chip toggle also refreshes the modal-only BLOCKS/MOVES extras.
  panel.appendChild(_buildHowtoColumns(_rebuildUserGuideModal));
  panel.appendChild(_buildHowtoExtras());

  // Stop click-on-panel from closing (only backdrop click closes).
  panel.addEventListener('mousedown', (e) => e.stopPropagation());
  panel.addEventListener('click', (e) => e.stopPropagation());

  _userGuideModal.appendChild(panel);
}
// Rebuild the whole help modal in place — used on open AND when a mode chip is toggled, so the
// chips, the mode-aware controls, and the BLOCKS/MOVES extras all refresh together.
function _rebuildUserGuideModal() {
  _userGuideModal.replaceChildren();
  _buildUserGuideModalContent();
}
_userGuideModal.addEventListener('click', (e) => {
  e.stopPropagation();
  _userGuideModal.style.display = 'none';
});
_userGuideModal.addEventListener('mousedown', (e) => e.stopPropagation());
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && _userGuideModal.style.display !== 'none') {
    _userGuideModal.style.display = 'none';
  }
});
_userGuideBtn.onclick = (e) => {
  e.stopPropagation();
  // Rebuild fresh on every open so the mode-aware content reflects the CURRENT assist toggles
  // (was built once + cached, which left the mode chips/controls stale after a toggle flip).
  _rebuildUserGuideModal();
  _userGuideModal.style.display = 'flex';
};

// Top-level entry: branches on _currentWorld. null → world select; world id → level grid.
// Home-screen view state. 'world-select' is the default; 'create' is the UGC prompt page.
// World-level views are gated by _currentWorld being set, so they don't need a flag here.
let _homeView = 'world-select';

// Tracks the LAST major view rendered so we can fade-in only on actual view changes
// (home → community → world etc.), not on intra-view re-renders (filter / sort pill
// clicks, etc. — those would otherwise blink awkwardly on every click).
let _prevHomeViewKey = null;
function _homeViewKey() {
  if (_homeView === 'create') return 'create';
  if (_homeView === 'community') return 'community';
  if (_homeView === 'gloves') return 'gloves';
  if (_currentWorld) return `world:${_currentWorld}`;
  return 'home';
}

// Co-op room code: short + unambiguous (no 0/O/1/I/L), so it's easy to read off a shared link.
function _makeRoomCode() {
  const alphabet = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
  let s = '';
  for (let i = 0; i < 5; i++) s += alphabet[Math.floor(Math.random() * alphabet.length)];
  return s;
}

// Curated multiplayer arena pool (starter set, 2026-07-01). `boot` is the URL bootstrap that enters the
// arena on load (?gauntlet / ?level=<id> / ?play=<id>); `arena` is the id the referee/HUD carries. Both
// players launch the same query so they build the identical structure.
const _RACE_ARENAS = [
  { label: 'Gauntlet',         boot: 'gauntlet',      arena: 'gauntlet' },
  { label: 'Grand Finale',     boot: 'level=level10', arena: 'level10' },
  { label: 'Ten Tall Towers',  boot: 'play=nmxv9m',   arena: 'nmxv9m' },
  { label: 'Towering Ascent',  boot: 'play=533w24',   arena: '533w24' },
  { label: 'Sandstone Kingdom', boot: 'play=3pzuef',  arena: '3pzuef' },
  { label: 'Giraffe Jamboree', boot: 'play=6gwfai',   arena: '6gwfai' },
  { label: 'Iron Giants',      boot: 'play=akznvz',   arena: 'akznvz' },
];

// Multiplayer front-end. A proper room flow with a lobby: pick arena/mode/assist, CREATE a room (or JOIN
// one by code), gather in a lobby that shows who's connected, then the HOST hits Start and everyone
// launches into the SAME arena together. The lobby uses a thin relay connection (mpLobby: presence +
// launch only). Start broadcasts the canonical game query; every client navigates to it, taking the exact
// same URL load path (?boot&mp=CODE&race=...&arena=...&assist=...) that runs the full game + referee.
let _coopModal = null;
let _mpLobby = null;   // active lobby transport while the modal is in its lobby view

function _mpPlayerName() {
  try { const n = localStorage.getItem('smash-player-name-v1'); if (n && n.trim()) return n.trim().slice(0, 16); } catch { /* no storage */ }
  return 'Guest' + Math.floor(Math.random() * 90 + 10);
}
function _mpBuildQuery(arena, mode, assist, loadout, code) {
  let q = `${arena.boot}&mp=${code}`;
  if (mode !== 'off') q += `&race=${mode}&arena=${arena.arena}&assist=${assist ? 'on' : 'off'}&loadout=${loadout || 'spring-spring'}`;
  return q;
}
function _mpModeLabel(mode) { return mode === 'smashes' ? 'Race · Most Smashes' : mode === 'coins' ? 'Race · Most Coins' : 'Co-op'; }
// Host-chosen race loadout options — the gloves are a power ladder, so everyone runs the same pair.
const _MP_LOADOUTS = [
  { val: 'spring-spring', label: 'Dual Spring' },
  { val: 'rocket-rocket', label: 'Dual Rocket' },
  { val: 'spring-rocket', label: 'Spring + Rocket' },
  { val: 'bare-bare', label: 'Bare Fists' },
];
function _mpLoadoutLabel(v) { const o = _MP_LOADOUTS.find((l) => l.val === v); return o ? o.label : 'Dual Spring'; }

function _openMpModal(joinCode = null) {
  if (_MP_PROD_HIDDEN) return;   // MP hidden on the public prod host while the freeze bug is chased
  const st = { view: joinCode ? 'lobby' : 'setup', arena: _RACE_ARENAS[0], mode: 'smashes',
    assist: !!_easyMode, loadout: 'spring-spring', code: joinCode || null, isHost: false, presence: [] };

  if (!_coopModal) {
    _coopModal = document.createElement('div');
    _coopModal.style.cssText = 'position:fixed;inset:0;display:none;align-items:center;justify-content:center;'
      + 'z-index:1600;background:rgba(26,16,64,0.6);pointer-events:auto;';
    _coopModal.addEventListener('mousedown', (e) => { if (e.target === _coopModal) _closeCoopModal(); });
  }

  const card = document.createElement('div');
  card.style.cssText = 'background:var(--col-bg-panel);border:6px solid var(--col-cyan);'
    + 'box-shadow:6px 6px 0 var(--col-ink);padding:26px 30px 22px;display:flex;flex-direction:column;'
    + 'gap:14px;min-width:340px;max-width:460px;';

  // The card is the kit's vibrant-yellow panel, so everything is INK (dark navy) for contrast, matching
  // the world tiles / level cards. Title is brand-pink with an ink stroke so it reads on yellow.
  const _title = (t) => { const d = document.createElement('div'); d.textContent = t;
    d.style.cssText = 'font:800 24px/1 var(--font-display, system-ui),sans-serif;color:var(--col-pink);'
      + '-webkit-text-stroke:2px var(--col-ink);paint-order:stroke;letter-spacing:1px;text-align:center;'; return d; };
  const _blurb = (t) => { const d = document.createElement('div'); d.textContent = t;
    d.style.cssText = 'font:600 16px/1.45 system-ui,sans-serif;color:var(--col-ink);text-align:center;opacity:0.95;'; return d; };
  const _btn = (t, primary) => { const b = document.createElement('button'); b.className = 'ss-btn' + (primary ? '' : ' ss-btn--secondary');
    if (primary) { b.style.background = 'var(--col-green)'; b.style.color = 'var(--col-ink)'; } b.textContent = t; return b; };
  // Segmented control: options [{label,val}]; getVal()/onPick(val). Inactive chips are ink-outlined on
  // the yellow; the active chip fills cyan. Ink text throughout so both states stay legible.
  const _segRow = (labelText, options, getVal, onPick, boxed, captionFor) => {
    // `boxed` wraps the pills in a dashed ink container so a multi-row group (arena / gloves) reads as one
    // set instead of loose buttons drifting under the label. `captionFor(val)` renders a live caption to
    // the right of the pills (e.g. the goal explanation), refreshed on each selection.
    const wrap = document.createElement('div'); wrap.style.cssText = 'display:flex;align-items:' + (boxed ? 'flex-start' : 'center') + ';gap:8px;';
    const lab = document.createElement('div'); lab.textContent = labelText;
    lab.style.cssText = 'font:800 12px/1 system-ui,sans-serif;color:var(--col-ink);width:94px;flex:0 0 94px;letter-spacing:0.3px;white-space:nowrap;' + (boxed ? 'padding-top:10px;' : '');
    wrap.appendChild(lab);
    const groupFlex = boxed ? '1 1 auto' : (captionFor ? '0 0 auto' : '1 1 auto');
    const group = document.createElement('div'); group.style.cssText = 'display:flex;flex-wrap:wrap;gap:6px;flex:' + groupFlex + ';'
      + (boxed ? 'border:2px dashed rgba(10,8,32,0.45);border-radius:8px;padding:8px;' : '');
    const btns = [];
    let cap = null;
    if (captionFor) { cap = document.createElement('div'); cap.style.cssText = 'font:800 12.5px/1.2 system-ui,sans-serif;color:var(--col-ink);opacity:0.8;flex:1 1 auto;'; }
    const paint = () => { for (const b of btns) { const on = b._val === getVal();
      b.style.background = on ? 'var(--col-cyan)' : '#fff';
      b.style.boxShadow = on ? '2px 2px 0 var(--col-ink)' : 'none'; }
      if (cap) cap.textContent = captionFor(getVal()); };
    for (const opt of options) {
      const b = document.createElement('button'); b._val = opt.val; b.textContent = opt.label;
      b.style.cssText = 'padding:6px 11px;border:2px solid var(--col-ink);border-radius:6px;cursor:pointer;'
        + 'font:800 12px/1 system-ui,sans-serif;color:var(--col-ink);transition:background 0.1s;';
      b.onclick = (e) => { e.stopPropagation(); onPick(opt.val); paint(); };
      group.appendChild(b); btns.push(b);
    }
    wrap.appendChild(group); if (cap) wrap.appendChild(cap); paint(); return wrap;
  };

  // ---- lobby lifecycle ----
  const closeLobby = () => { if (_mpLobby) { _mpLobby.close(); _mpLobby = null; } };
  const navigateToGame = (q) => { closeLobby(); location.href = `${location.pathname}?${q}`; };
  const startLobby = () => {
    closeLobby();
    const room = st.code;
    // Dynamic import keeps net/mp.js (and its three addon) out of the initial bundle for non-MP players.
    import('./net/mp.js').then(({ mpLobby }) => {
      if (st.view !== 'lobby' || st.code !== room) return;   // user left / changed room before it loaded
      _mpLobby = mpLobby({
        url: _MP_RELAY, room, name: _mpPlayerName(), isHost: st.isHost,
        onPresence: (list) => { st.presence = list; if (st.view === 'lobby') render(); },
        onLaunch: (m) => { if (m && m.q) navigateToGame(m.q); },   // host started — everyone follows
        onStatus: () => { /* lobby is best-effort; presence reflects reality */ },
      });
    });
  };
  const hostStart = () => {
    const q = _mpBuildQuery(st.arena, st.mode, st.assist, st.loadout, st.code);
    if (_mpLobby) _mpLobby.launch({ q });   // tell the others
    setTimeout(() => navigateToGame(q), 120);  // let the launch frame flush before we navigate away
  };

  // ---- views ----
  function renderSetup() {
    card.appendChild(_title('MULTIPLAYER'));
    card.appendChild(_blurb('Online multiplayer for up to 4 players. Create a room and share the link with friends.'));
    const controls = document.createElement('div');
    controls.style.cssText = 'display:flex;flex-direction:column;gap:9px;padding:12px 0;border-top:2px solid rgba(10,8,32,0.25);border-bottom:2px solid rgba(10,8,32,0.25);';
    controls.appendChild(_segRow('Goal', [
      { label: 'Smashes', val: 'smashes' }, { label: 'Coins', val: 'coins' },
    ], () => st.mode, (v) => { st.mode = v; }, false, (v) => v === 'coins' ? 'Most coins win!' : 'Most smashes win!'));
    controls.appendChild(_segRow('Arena', _RACE_ARENAS.map((a, i) => ({ label: a.label, val: i })),
      () => _RACE_ARENAS.indexOf(st.arena), (v) => { st.arena = _RACE_ARENAS[v]; }, true));
    // Rhythm Assist is a MATCH RULE — it changes the coin economy, so the host forces it for everyone.
    controls.appendChild(_segRow('Rhythm Assist', [{ label: 'On', val: 1 }, { label: 'Off', val: 0 }],
      () => (st.assist ? 1 : 0), (v) => { st.assist = !!v; }, false, () => 'same for everyone'));
    // Aim Assist is PERSONAL — rules-neutral (it only changes how you target), so each player keeps their
    // own. This toggle just sets the host's own local preference; joiners set theirs in the lobby.
    controls.appendChild(_segRow('Aim Assist', [{ label: 'On', val: 1 }, { label: 'Off', val: 0 }],
      () => (_aimAssist ? 1 : 0), (v) => _setAimAssist(!!v), false, () => 'just you'));
    // Gloves: host picks one loadout for the whole race (fairness — everyone runs the same power tier).
    controls.appendChild(_segRow('Gloves', _MP_LOADOUTS.map((l) => ({ label: l.label, val: l.val })),
      () => st.loadout, (v) => { st.loadout = v; }, true));
    card.appendChild(controls);
    const row = document.createElement('div'); row.style.cssText = 'display:flex;gap:10px;justify-content:center;flex-wrap:wrap;margin-top:2px;';
    const createBtn = _btn('Create Room', true);
    createBtn.onclick = async (e) => { e.stopPropagation(); if (!_loadPlayerName()) await _promptPlayerName(); st.code = _makeRoomCode(); st.isHost = true; st.view = 'lobby'; startLobby(); render(); };
    const joinBtn = _btn('Join a Room', false);
    joinBtn.onclick = (e) => { e.stopPropagation(); st.view = 'join'; render(); };
    const cancelBtn = _btn('Cancel', false);
    cancelBtn.onclick = (e) => { e.stopPropagation(); _closeCoopModal(); };
    row.append(createBtn, joinBtn, cancelBtn);
    card.appendChild(row);
  }

  function renderJoin() {
    card.appendChild(_title('JOIN A ROOM'));
    card.appendChild(_blurb('Enter the room code your friend shared with you.'));
    const input = document.createElement('input');
    input.type = 'text'; input.maxLength = 5; input.placeholder = 'CODE';
    input.style.cssText = 'text-align:center;letter-spacing:6px;text-transform:uppercase;padding:12px;border:3px solid var(--col-ink);'
      + 'background:rgba(255,255,255,0.65);color:var(--col-ink);font:800 22px/1 monospace;border-radius:6px;';
    input.addEventListener('input', () => { input.value = input.value.toUpperCase().replace(/[^A-Z0-9]/g, ''); });
    card.appendChild(input);
    const row = document.createElement('div'); row.style.cssText = 'display:flex;gap:10px;justify-content:center;margin-top:2px;';
    const joinBtn = _btn('Join', true);
    const doJoin = async () => { const c = input.value.trim().toUpperCase(); if (c.length < 4) { input.style.borderColor = 'var(--col-pink, #ff3b6b)'; return; }
      if (!_loadPlayerName()) await _promptPlayerName();
      st.code = c; st.isHost = false; st.view = 'lobby'; startLobby(); render(); };
    joinBtn.onclick = (e) => { e.stopPropagation(); doJoin(); };
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter') doJoin(); });
    const backBtn = _btn('Back', false);
    backBtn.onclick = (e) => { e.stopPropagation(); st.view = 'setup'; render(); };
    row.append(joinBtn, backBtn);
    card.appendChild(row);
    setTimeout(() => input.focus(), 30);
  }

  function renderLobby() {
    card.appendChild(_title('LOBBY'));
    // Room code + share link
    const codeWrap = document.createElement('div');
    codeWrap.style.cssText = 'text-align:center;font:800 30px/1 monospace;color:var(--col-ink);letter-spacing:8px;'
      + 'background:rgba(255,255,255,0.55);border:3px solid var(--col-ink);border-radius:8px;padding:8px 0;box-shadow:3px 3px 0 var(--col-ink);';
    codeWrap.textContent = st.code;
    card.appendChild(codeWrap);
    const link = `${location.origin}${location.pathname}?join=${st.code}`;
    const linkRow = document.createElement('div'); linkRow.style.cssText = 'display:flex;gap:8px;align-items:stretch;';
    const linkField = document.createElement('input');
    linkField.type = 'text'; linkField.readOnly = true; linkField.value = link;
    linkField.style.cssText = 'flex:1 1 auto;min-width:0;padding:9px 11px;border:2px solid var(--col-ink);background:rgba(255,255,255,0.6);color:var(--col-ink);font:600 12px/1 monospace;border-radius:6px;';
    linkField.addEventListener('focus', () => linkField.select());
    const copyBtn = _btn('Copy', false); copyBtn.style.cssText += ';padding:8px 14px;';
    copyBtn.onclick = async (e) => { e.stopPropagation();
      try { await navigator.clipboard.writeText(link); copyBtn.textContent = 'Copied!'; }
      catch { linkField.focus(); linkField.select(); }
      setTimeout(() => { copyBtn.textContent = 'Copy'; }, 1600); };
    linkRow.append(linkField, copyBtn);
    card.appendChild(linkRow);
    // Config summary
    const cfg = document.createElement('div');
    cfg.style.cssText = 'text-align:center;font:800 13px/1.4 system-ui,sans-serif;color:var(--col-ink);padding:8px 0;border-top:2px solid rgba(10,8,32,0.25);border-bottom:2px solid rgba(10,8,32,0.25);';
    cfg.textContent = st.mode === 'off'
      ? `${st.arena.label}  ·  ${_mpModeLabel(st.mode)}`
      : `${st.arena.label}  ·  ${_mpModeLabel(st.mode)}  ·  Assist ${st.assist ? 'On' : 'Off'}  ·  ${_mpLoadoutLabel(st.loadout)}`;
    if (!st.isHost) cfg.textContent = 'Waiting in room — the host picks the arena & mode.';
    card.appendChild(cfg);
    // Presence list
    const listTitle = document.createElement('div'); listTitle.textContent = `PLAYERS (${st.presence.length})`;
    listTitle.style.cssText = 'font:800 12px/1 system-ui,sans-serif;color:var(--col-ink);letter-spacing:0.5px;opacity:0.75;';
    card.appendChild(listTitle);
    const list = document.createElement('div'); list.style.cssText = 'display:flex;flex-direction:column;gap:5px;';
    if (st.presence.length === 0) { const d = document.createElement('div'); d.textContent = 'connecting…';
      d.style.cssText = 'font:600 13px/1 system-ui,sans-serif;color:var(--col-ink);opacity:0.6;'; list.appendChild(d); }
    for (const p of st.presence) {
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;align-items:center;gap:8px;padding:7px 10px;border:2px solid var(--col-ink);border-radius:6px;background:rgba(255,255,255,0.5);';
      const dot = document.createElement('div'); dot.style.cssText = 'width:10px;height:10px;border-radius:50%;background:var(--col-green,#7be36b);border:1.5px solid var(--col-ink);flex:0 0 auto;';
      const nm = document.createElement('div'); nm.textContent = p.name + (p.self ? ' (you)' : '');
      nm.style.cssText = 'flex:1 1 auto;font:800 14px/1 system-ui,sans-serif;color:var(--col-ink);';
      row.append(dot, nm);
      if (p.host) { const h = document.createElement('div'); h.textContent = 'HOST';
        h.style.cssText = 'font:800 10px/1 system-ui,sans-serif;color:var(--col-ink);background:var(--col-cyan);border:2px solid var(--col-ink);padding:3px 6px;border-radius:4px;letter-spacing:0.5px;'; row.appendChild(h); }
      list.appendChild(row);
    }
    card.appendChild(list);
    // Per-player Aim Assist — rules-neutral, so every player (host + joiners) sets their own here before
    // the race starts. Binds straight to the local _aimAssist preference the game already reads at launch.
    const yours = document.createElement('div'); yours.style.cssText = 'padding-top:8px;border-top:2px solid rgba(10,8,32,0.25);';
    yours.appendChild(_segRow('Aim Assist', [{ label: 'On', val: 1 }, { label: 'Off', val: 0 }],
      () => (_aimAssist ? 1 : 0), (v) => _setAimAssist(!!v), false, () => 'your setting'));
    card.appendChild(yours);
    // Actions
    const row = document.createElement('div'); row.style.cssText = 'display:flex;gap:10px;justify-content:center;margin-top:4px;align-items:center;';
    if (st.isHost) {
      const ready = st.presence.length >= 2;
      const startBtn = _btn(ready ? 'Start' : 'Waiting for a friend…', true);
      startBtn.disabled = !ready; startBtn.style.opacity = ready ? '1' : '0.5'; startBtn.style.cursor = ready ? 'pointer' : 'default';
      startBtn.onclick = (e) => { e.stopPropagation(); if (ready) hostStart(); };
      row.appendChild(startBtn);
    } else {
      const wait = document.createElement('div'); wait.textContent = 'Waiting for the host to start…';
      wait.style.cssText = 'font:800 14px/1.3 system-ui,sans-serif;color:var(--col-ink);text-align:center;';
      row.appendChild(wait);
    }
    const leaveBtn = _btn('Leave', false);
    leaveBtn.onclick = (e) => { e.stopPropagation(); closeLobby(); st.view = 'setup'; st.presence = []; render(); };
    row.appendChild(leaveBtn);
    card.appendChild(row);
  }

  function render() {
    card.innerHTML = '';
    if (st.view === 'setup') renderSetup();
    else if (st.view === 'join') renderJoin();
    else renderLobby();
  }

  _coopModal.innerHTML = '';
  _coopModal.appendChild(card);
  if (joinCode) startLobby();   // opened from a ?join=CODE link — drop straight into that room's lobby
  render();
  if (!_coopModal.isConnected) document.body.appendChild(_coopModal);
  _coopModal.style.display = 'flex';
}
function _closeCoopModal() {
  if (_mpLobby) { _mpLobby.close(); _mpLobby = null; }
  if (_coopModal) _coopModal.style.display = 'none';
}

function _renderHome() {
  const key = _homeViewKey();
  const viewChanged = _prevHomeViewKey !== key;
  _prevHomeViewKey = key;

  // Re-check the greeting on each render in case the nickname was set/changed since
  // last paint (e.g. after the player accepts the nickname modal during their first
  // generate or score submission).
  _refreshGreeting();

  if (viewChanged) {
    // Fade-out → render → fade-in. CSS transition on _homeContent's opacity (added at
    // module init) handles the tween; we just flip opacity:0 → 1 with a forced
    // double-rAF in between so the browser has a frame to register the starting state.
    _homeContent.style.opacity = '0';
  }
  _homeContent.textContent = '';
  // Drop assist-toggle refreshers from the view we are leaving; whichever view builds the controls
  // strip below repopulates the set. (The strip now lives on the world-grid + Community pages.)
  _assistToggleRefreshers.clear();
  if (_homeView === 'create') _renderCreatePage();
  else if (_homeView === 'community') _renderCommunityPage();
  else if (_homeView === 'gloves') _renderEquipGlovesPage();
  else if (_currentWorld) _renderWorldLevels();
  else _renderWorldSelect();

  if (viewChanged) {
    requestAnimationFrame(() => requestAnimationFrame(() => {
      _homeContent.style.opacity = '1';
    }));
  }

  // Background field of community silhouettes lives only behind the
  // world-select view. Cleared on every other view (gloves / create /
  // community / per-world level select) so the field never crowds a
  // different layout. First render fires the async sample fetch; later
  // renders reuse the cached sample + re-randomize positions.
  if (key === 'home') {
    _populateHomeBgField();
  } else {
    _clearHomeBgField();
  }
}

// =========================================================
// UGC Create page — prompt input → server generate → load level into game.
// In dev (vite dev) UGC_SERVER_URL falls back to the local Node prototype on :8787.
// In prod (vite build) it reads VITE_UGC_SERVER_URL from web/.env.production, which
// points at the deployed Cloudflare Worker.
// =========================================================
const UGC_SERVER_URL = import.meta.env.VITE_UGC_SERVER_URL || 'http://localhost:8787';

// Analytics — fires events to the Worker's Analytics Engine dataset. Disabled in dev
// (import.meta.env.PROD) so local testing doesn't pollute the prod dataset. Anon ID is
// a localStorage UUID, opaque, no PII. Event types are validated server-side; this
// client is the source of truth for which fields go where.
//
// All event types funnel through _fireAnalytics(type, extras). Extras can carry:
//   levelId  - string, for level_start / level_clear / ugc_play
//   status   - string, for level_clear ('pass' / 'fail')
//   stars    - number, for level_clear
//   score    - number, for level_clear
// The Worker maps these onto AE's blob/double slots so a single SQL query can read them.
const _ANALYTICS_ANON_ID_KEY = 'smash-anon-id-v1';
function _loadOrCreateAnonId() {
  try {
    let id = localStorage.getItem(_ANALYTICS_ANON_ID_KEY);
    if (!id) {
      id = (crypto.randomUUID && crypto.randomUUID()) || Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem(_ANALYTICS_ANON_ID_KEY, id);
    }
    return id;
  } catch { return null; }
}
const _ANALYTICS_ANON_ID = import.meta.env.PROD ? _loadOrCreateAnonId() : null;
function _fireAnalytics(type, extras = {}) {
  if (!_ANALYTICS_ANON_ID) return;  // dev mode or localStorage unavailable
  // Worker/AE pipeline (game telemetry, retention, cohort math).
  fetch(`${UGC_SERVER_URL}/analytics/session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ anonId: _ANALYTICS_ANON_ID, type, ...extras }),
    keepalive: true,  // survive navigation if the player clicks away fast
  }).catch(() => {});  // silent — analytics failure should never break the site
  // GA4 mirror (Google Analytics dashboard). `extras` flows through as GA4 event
  // params (levelId, stars, score, etc.) and can be promoted to custom dimensions
  // in the GA4 admin UI. `gtag` is defined globally by the snippet in index.html;
  // the typeof check keeps this a no-op in dev/SSR.
  if (typeof gtag === 'function') gtag('event', type, extras);
}
// One session_start per page load.
_fireAnalytics('session_start');
// Heartbeat every 60s while the page is visible. Session-length math = distinct minute
// buckets per user per day on the Worker side; gaps in heartbeats are interpreted as
// session boundaries. document.visibilityState gate stops heartbeats when the tab is
// backgrounded so idle tabs don't inflate the playtime numbers.
if (_ANALYTICS_ANON_ID) {
  setInterval(() => {
    if (document.visibilityState === 'visible') _fireAnalytics('session_heartbeat');
  }, 60_000);
}

// Cloudflare Turnstile invisible widget — gates POST /generate against scripted abuse of
// the Gemini quota. Site key is public and ships in the bundle; the matching secret lives
// on the Worker. Falls back to CF's documented "always passes" test key in dev so local
// Vite still works without adding localhost to the prod widget's hostname allowlist.
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';
let _turnstileWidgetId = null;
let _turnstileToken = null;
let _turnstileHost = null;

// Render the invisible widget once and let it grab a token in the background. Safe to call
// repeatedly: re-renders only if the script hasn't loaded yet, otherwise no-op.
function _ensureTurnstileWidget() {
  if (_turnstileWidgetId !== null) return;
  if (!_turnstileHost) {
    _turnstileHost = document.createElement('div');
    _turnstileHost.id = 'cf-turnstile-host';
    _turnstileHost.style.cssText = 'position:fixed;left:-9999px;top:0;';
    document.body.appendChild(_turnstileHost);
  }
  if (!window.turnstile || typeof window.turnstile.render !== 'function') {
    return;
  }
  try {
    _turnstileWidgetId = window.turnstile.render(_turnstileHost, {
      sitekey: TURNSTILE_SITE_KEY,
      // Note: `size: 'invisible'` is no longer accepted by Turnstile's render API —
      // invisible behavior is now configured on the site key itself in the Cloudflare
      // dashboard. The host element is positioned at left:-9999px so the widget stays
      // out of view regardless of how the site key is configured.
      callback: (token) => { _turnstileToken = token; },
      'error-callback': () => { _turnstileToken = null; },
      'expired-callback': () => { _turnstileToken = null; },
    });
  } catch (e) {
    console.warn('[turnstile] render failed', e);
  }
}

// Resolve a Turnstile token for the next /generate. Returns null if Turnstile is
// unreachable for ~10s; caller should surface a friendly error rather than retry forever.
async function _getTurnstileToken() {
  _ensureTurnstileWidget();
  if (_turnstileToken) return _turnstileToken;
  if (_turnstileWidgetId === null) {
    for (let i = 0; i < 30 && _turnstileWidgetId === null; i++) {
      await new Promise(r => setTimeout(r, 100));
      _ensureTurnstileWidget();
    }
    if (_turnstileWidgetId === null) return null;
  }
  try { window.turnstile.execute(_turnstileWidgetId); } catch {}
  for (let i = 0; i < 100; i++) {
    if (_turnstileToken) return _turnstileToken;
    await new Promise(r => setTimeout(r, 100));
  }
  return null;
}

// Last-used prompt version. Persists across Create-page renders so the player's choice
// sticks if they go back and try again.
let _ugcPromptVersion = 'creatures';

// Last-used MUSIC variant — independent of the level variant. 'default' uses the schema
// as-is; 'freeform' grants the LLM license to depart from pop conventions for music.
let _ugcMusicVersion = 'pruned';

// Community-page filter: 'all' (default) or 'mine' (only ids this browser has generated).
let _communityFilter = 'all';
// Community-page sort: 'newest' / 'plays' / 'score'. Applied client-side on whatever
// the server returned for /community — works within the fetched window.
let _communitySort = 'newest';

// Pending prompt for the Create page input — used by Remix to pre-fill from a community card.
let _pendingCreatePrompt = '';

// Player nickname, prompted once on first UGC level clear and reused for all future score
// submissions. localStorage-backed so it persists across sessions on this browser. No
// server-side identity (we don't have accounts) — the name is purely a label on the
// leaderboard so friends can recognize each other's scores during the test.
const _PLAYER_NAME_KEY = 'smash-player-name-v1';
function _loadPlayerName() {
  try { return localStorage.getItem(_PLAYER_NAME_KEY) || ''; } catch { return ''; }
}
function _savePlayerName(name) {
  try { localStorage.setItem(_PLAYER_NAME_KEY, name); } catch {}
}

// Foolproof reset path: visit ?reset=1 and the page wipes nickname + progress + UGC ledger
// before anything else runs. Strips the query param from the URL so a refresh doesn't keep
// re-clearing. Ran here at module load before any code reads localStorage.
(() => {
  try {
    const sp = new URLSearchParams(location.search);
    if (sp.has('reset')) {
      localStorage.removeItem('smash-player-name-v1');
      localStorage.removeItem('smash-progress-v1');
      localStorage.removeItem('smash-my-ugc-v1');
      sp.delete('reset');
      const newQS = sp.toString();
      history.replaceState(null, '', location.pathname + (newQS ? '?' + newQS : ''));
      console.log('[reset] wiped local nickname + progress + mine ledger');
    }
  } catch {}
})();

// Local "I made this" ledger. Without server-side accounts, we just remember which UGC ids
// were generated from this browser so the Community page can offer a "MINE" filter.
const _MY_UGC_KEY = 'smash-my-ugc-v1';
function _loadMyUgcIds() {
  try { return JSON.parse(localStorage.getItem(_MY_UGC_KEY) || '[]'); } catch { return []; }
}
function _addMyUgcId(id) {
  try {
    const ids = _loadMyUgcIds();
    if (!ids.includes(id)) ids.push(id);
    localStorage.setItem(_MY_UGC_KEY, JSON.stringify(ids));
  } catch { /* localStorage blocked — ignore */ }
}

// Build a shareable URL for a UGC level. Produces e.g. https://soundssmashing.com/?play=abc123
function _shareUrlForUgc(id) {
  return `${location.origin}${location.pathname}?play=${encodeURIComponent(id)}`;
}

// Client-side prompt moderation — first-line defense before the network call. Same
// blocklist as the server's BANNED_NAME_SUBSTRINGS so the user gets immediate inline
// feedback for obvious cases without round-tripping. Server still validates as the
// source of truth (a hostile client could bypass this).
const _CLIENT_BANNED_SUBSTRINGS = [
  'nigger', 'nigga', 'chink', 'spic', 'kike', 'gook', 'wetback', 'beaner',
  'wop', 'kraut', 'paki', 'raghead', 'sandnigger', 'coon', 'jiggaboo',
  'faggot', 'fag', 'tranny', 'dyke', 'shemale',
  'rape', 'rapist', 'pedo', 'pedophile', 'molest', 'incest',
  'fuck', 'shit', 'cunt', 'bitch', 'asshole', 'whore', 'slut', 'bastard',
  'dick', 'cock', 'pussy', 'twat', 'wank', 'jerkoff',
  'hitler', 'nazi', 'heil', 'kkk',
];
function _normalizePromptForFilter(s) {
  return s.toLowerCase()
    .replace(/0/g, 'o').replace(/1/g, 'i').replace(/3/g, 'e').replace(/4/g, 'a')
    .replace(/5/g, 's').replace(/7/g, 't').replace(/8/g, 'b').replace(/@/g, 'a')
    .replace(/\$/g, 's').replace(/!/g, 'i').replace(/[^a-z]/g, '');
}
function _isPromptAllowedClient(prompt) {
  const norm = _normalizePromptForFilter(prompt);
  for (const word of _CLIENT_BANNED_SUBSTRINGS) {
    if (norm.includes(word)) return false;
  }
  return true;
}

// Loading-state copy — rotated during the 30-60s Gemini wait on the Create page.
// Status messages tell the player what's happening (sense of progress, even if
// fictional). Tips educate while they wait — especially valuable for first-timers
// from a YouTuber drop who haven't internalised what makes a good prompt yet.
const _LOADING_STATUS_MESSAGES = [
  'asking the AI to dream up blocks…',
  'painting the iso scenes…',
  'composing your beat…',
  'stacking the bricks…',
  'tuning the music…',
  'gluing it all together…',
  'almost ready…',
];
// Loading-music state — borrowed UGC song that plays while Gemini cooks. Tracks the
// fetched-and-displayed metadata + the mute state so the toggle can flip volume.
let _loadingMusicMuted = false;
function _setLoadingMusicMuted(muted) {
  _loadingMusicMuted = muted;
  if (audioCtx && _musicGain) {
    const target = muted ? 0.0001 : (typeof _MUSIC_VOLUME_TARGET !== 'undefined' ? _MUSIC_VOLUME_TARGET : 0.6);
    try { _musicGain.gain.linearRampToValueAtTime(target, audioCtx.currentTime + 0.15); } catch {}
  }
}

// Nickname-prompt modal — shown once on a player's first UGC level clear so their score
// goes onto the leaderboard with a name they (and their friends) recognize. Returns a
// Promise that resolves with the entered name, or '' if the player cancels (in which case
// we skip submission for this run; we'll re-ask next time). Built lazily on first call.
let _nicknameModal = null;
function _promptPlayerName(errorText = '') {
  return new Promise((resolve) => {
    if (!_nicknameModal) {
      // z-index sits ABOVE the clear-screen overlay (z=1001) so the prompt is visible
      // when it pops at level end. With the old z=80 the modal opened *behind* the clear
      // screen and silently waited for input it never got, dropping the score submission.
      const overlay = document.createElement('div');
      overlay.style.cssText = 'position:fixed;inset:0;z-index:1100;display:none;'
        + 'align-items:center;justify-content:center;padding:40px;'
        + 'background:rgba(0,0,0,0.75);pointer-events:auto;';
      document.body.appendChild(overlay);

      const panel = document.createElement('div');
      panel.style.cssText = 'background:var(--col-green);border:var(--border-chunky);'
        + 'box-shadow:var(--shadow-hard-sm);padding:28px 32px;max-width:440px;width:100%;'
        + 'display:flex;flex-direction:column;gap:14px;align-items:center;text-align:center;';

      const title = document.createElement('div');
      title.style.cssText = 'font-family:var(--font-display);font-size:14px;letter-spacing:3px;'
        + 'color:var(--col-pink);'
        + '-webkit-text-stroke:2px var(--col-ink);paint-order:stroke;'
        + 'text-transform:uppercase;';
      title.textContent = 'Pick a Nickname';
      panel.appendChild(title);

      const blurb = document.createElement('div');
      blurb.style.cssText = 'font-family:var(--font-body, ui-monospace), monospace;'
        + 'font-size:13px;color:var(--col-ink);opacity:0.85;line-height:1.4;';
      blurb.textContent = 'Your name will appear publicly on every level you create or score on.';
      panel.appendChild(blurb);

      const input = document.createElement('input');
      input.type = 'text';
      input.maxLength = 24;
      input.placeholder = 'e.g. alex';
      input.style.cssText = 'width:100%;padding:12px 16px;font:18px var(--font-body, ui-monospace), monospace;'
        + 'background:#fff;color:var(--col-ink);border:var(--border-chunky);'
        + 'box-shadow:var(--shadow-hard-sm);outline:none;text-align:center;';
      input.addEventListener('mousedown', (e) => e.stopPropagation());
      // Strip non-printable / non-ASCII chars as the player types so emoji, homoglyphs,
      // and zero-width chars don't sneak into the leaderboard. The server enforces the
      // same rule but stripping at input time gives instant feedback.
      input.addEventListener('input', () => {
        const cleaned = input.value.replace(/[^\x20-\x7e]/g, '');
        if (cleaned !== input.value) input.value = cleaned;
      });
      panel.appendChild(input);

      // Inline error slot — server-side rejections (e.g. blocked names) display here so
      // the player can correct without losing the modal flow.
      const errorMsg = document.createElement('div');
      errorMsg.style.cssText = 'font-family:var(--font-body, ui-monospace), monospace;'
        + 'font-size:12px;color:var(--col-pink, #FF3B6B);min-height:14px;letter-spacing:0.5px;'
        + 'font-weight:bold;';
      panel.appendChild(errorMsg);

      const btnRow = document.createElement('div');
      btnRow.style.cssText = 'display:flex;gap:12px;margin-top:4px;';
      const okBtn = document.createElement('button');
      okBtn.type = 'button';
      okBtn.className = 'ss-btn ss-btn--primary';
      okBtn.textContent = 'SAVE';
      // SAVE is disabled while the input is empty — a nickname is required to proceed.
      // No SKIP button: name is mandatory for UGC create + score submission so the
      // creator/leaderboard attribution always lands on a real player. Visual feedback
      // (greyed-out button, not-allowed cursor) telegraphs the gate before the player
      // tries to click.
      const setOkEnabled = (enabled) => {
        okBtn.disabled = !enabled;
        okBtn.style.opacity = enabled ? '1' : '0.45';
        okBtn.style.cursor = enabled ? 'pointer' : 'not-allowed';
      };
      setOkEnabled(false);
      btnRow.appendChild(okBtn);
      panel.appendChild(btnRow);

      panel.addEventListener('mousedown', (e) => e.stopPropagation());
      panel.addEventListener('click', (e) => e.stopPropagation());
      overlay.appendChild(panel);

      _nicknameModal = { overlay, input, errorMsg, okBtn, setOkEnabled, resolve: null };
      okBtn.addEventListener('mousedown', (e) => e.stopPropagation());
      // Re-evaluate the enabled state on every input change. The existing input handler
      // already strips non-ASCII chars; this adds the empty-check on top.
      input.addEventListener('input', () => {
        setOkEnabled(input.value.trim().length > 0);
      });
      okBtn.onclick = (e) => {
        e.stopPropagation();
        const name = input.value.trim();
        if (!name) { input.focus(); return; }
        _savePlayerName(name);
        _nicknameModal.overlay.style.display = 'none';
        const r = _nicknameModal.resolve; _nicknameModal.resolve = null;
        if (r) r(name);
      };
      // Enter submits (if non-empty); Escape no longer dismisses since name is required.
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); okBtn.click(); }
      });
    }
    _nicknameModal.input.value = _loadPlayerName();
    _nicknameModal.setOkEnabled(_nicknameModal.input.value.trim().length > 0);
    _nicknameModal.errorMsg.textContent = errorText || '';
    _nicknameModal.resolve = resolve;
    _nicknameModal.overlay.style.display = 'flex';
    setTimeout(() => _nicknameModal.input.focus(), 50);
  });
}

// Submit a level-clear score to the UGC server. Resolves the player's nickname first,
// prompting once if not set. If the server rejects the name (e.g. profanity blocklist),
// re-prompts with an error message so the player can pick a different one. Silent
// failure on network errors — leaderboard isn't gameplay-critical and we don't want a
// server outage to block the clear screen.
async function _submitUgcScore(ugcId, score) {
  let name = _loadPlayerName();
  let errorText = '';
  // Retry loop: if the server rejects with a name_blocked code, we re-prompt with the
  // error so the player can correct it. Capped at a few attempts to avoid spinning forever.
  for (let attempt = 0; attempt < 4; attempt++) {
    if (!name) {
      if (attempt === 0) console.log(`[score] no player name yet — prompting (level=${ugcId}, score=${score})`);
      name = await _promptPlayerName(errorText);
      if (!name) {
        console.log(`[score] name prompt was skipped/empty — submission cancelled`);
        return null;
      }
    }
    console.log(`[score] submitting: level=${ugcId} score=${score} name="${name}"`);
    try {
      const res = await fetch(`${UGC_SERVER_URL}/score/${encodeURIComponent(ugcId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, score }),
      });
      if (res.status === 400) {
        const data = await res.json().catch(() => ({}));
        if (data.code === 'name_blocked') {
          // Wipe the saved name (it's not allowed) and re-prompt with the error visible.
          console.warn(`[score] name "${name}" rejected by server — re-prompting`);
          _savePlayerName('');
          errorText = 'Name not allowed. Try another.';
          name = '';
          continue;
        }
        console.warn(`[score] server returned 400 (${data.error || 'unknown'}) for level=${ugcId}`);
        return null;
      }
      if (!res.ok) {
        console.warn(`[score] server returned ${res.status} for level=${ugcId}`);
        return null;
      }
      const data = await res.json();
      const topScores = Array.isArray(data.topScores) ? data.topScores : null;
      console.log(`[score] submission OK — topScores:`, topScores);
      // The clear-screen leaderboard reveal phase awaits this promise directly and runs the
      // celebration animation off the result; no separate live-update path needed.
      return topScores;
    } catch (e) {
      console.warn(`[score] submission failed for level=${ugcId}:`, e);
      return null;
    }
  }
  // Fell through the retry loop without success — too many name rejections in a row.
  return null;
}

// Module-level handle for the post-clear leaderboard surface so the async score-submit
// callback can find + update it after submission completes.
let _clearScreenLeaderboardSection = null;

function _renderLeaderboardBody(section, topScores) {
  const list = Array.isArray(topScores) ? topScores : [];
  section.body.textContent = '';
  if (list.length === 0) {
    section.body.textContent = 'Be the first to set a score!';
    return;
  }
  // Top entry — pulled out as the headline number with name + pixel-art trophy.
  const top = list[0];
  const head = document.createElement('div');
  head.style.cssText = 'font-family:var(--font-display);font-size:18px;letter-spacing:2px;'
    + 'color:var(--col-gold, #ffd66b);'
    + '-webkit-text-stroke:2px var(--col-ink);paint-order:stroke;'
    + 'text-transform:uppercase;margin:2px 0 6px;display:flex;align-items:center;gap:10px;';
  const trophy = document.createElement('span');
  trophy.style.cssText = `display:inline-block;width:24px;height:24px;`
    + `background-image:url(${_trophyUrl});background-size:contain;background-repeat:no-repeat;image-rendering:pixelated;`;
  head.appendChild(trophy);
  head.appendChild(document.createTextNode(`${top.score}  ${top.name}`));
  section.body.appendChild(head);
  // Lower entries on a tighter line each — kit display font, smaller and dimmer than #1.
  for (let i = 1; i < list.length; i++) {
    const e = list[i];
    const row = document.createElement('div');
    row.style.cssText = 'font-family:var(--font-display);font-size:10px;letter-spacing:1.5px;'
      + 'color:#cfd6dc;opacity:0.7;text-transform:uppercase;';
    row.textContent = `#${i + 1}  ${e.score}  ${e.name}`;
    section.body.appendChild(row);
  }
  // "You scored X" line — only if we know the player's score and they're not already on top.
  const youScore = section.yourScore;
  if (typeof youScore === 'number' && youScore > 0) {
    const onTop = top && top.score === youScore && top.name === _loadPlayerName();
    if (!onTop) {
      const you = document.createElement('div');
      you.style.cssText = 'font-family:var(--font-display);font-size:10px;letter-spacing:1.5px;'
        + 'color:var(--col-cyan, #00E5FF);margin-top:4px;text-transform:uppercase;';
      you.textContent = `YOU · ${youScore}`;
      section.body.appendChild(you);
    }
  }
}

// Populate the clear-screen leaderboard surface. Fetches /play/:id since the latest
// topScores live there. If a score-submit is in-flight the server may still be writing,
// but the live-update hook in _submitUgcScore will refresh the section once it resolves.
async function _populateClearScreenLeaderboard(section) {
  try {
    const res = await fetch(`${UGC_SERVER_URL}/play/${encodeURIComponent(section.ugcId)}`);
    if (!res.ok) throw new Error('fetch failed');
    const data = await res.json();
    const topScores = Array.isArray(data.topScores) ? data.topScores : [];
    if (_clearScreenLeaderboardSection !== section) return; // screen replaced before fetch returned
    _renderLeaderboardBody(section, topScores);
  } catch {
    if (_clearScreenLeaderboardSection !== section) return;
    section.body.textContent = '(leaderboard unavailable)';
  }
}

// Determine the celebration "case" by diffing before/after topScores against the player.
// Returns one of: 'knockout', 'first-record', 'own-best', 'placed', 'off-board', 'no-pass'.
function _classifyLeaderboardEvent(before, after, yourName, yourScore) {
  if (!Array.isArray(after) || after.length === 0) return 'no-pass';  // submit failed / didn't pass
  const top = after[0];
  const newOnTop = top && top.name === yourName && top.score === yourScore;
  const wasOnBoard = (before || []).some(e => e.name === yourName);
  const wasOnTop = before && before.length > 0 && before[0].name === yourName;
  const youOnBoard = after.some(e => e.name === yourName && e.score === yourScore);
  if (newOnTop && (!before || before.length === 0)) return 'first-record';
  if (newOnTop && wasOnTop) return 'own-best';
  if (newOnTop) return 'knockout';
  if (youOnBoard) return 'placed';
  return 'off-board';
}

// Run the leaderboard reveal animation. Awaits before-fetch + submit-promise, classifies
// the event, then renders an appropriate celebration. Uses the existing music beat grid
// for sound timing so chimes land on a downbeat. Two abort signals: isStale() means the
// clear screen was torn down (bail entirely); skipped() means the player tapped to fast-
// forward (skip animations but still render the final state).
async function _runLeaderboardCelebration(section, isStale, skipped) {
  // Wait for the before-fetch (already kicked off when the panel was built).
  if (section.beforeFetch) { try { await section.beforeFetch; } catch {} }
  // Reveal the panel; pre-fill body with the before-scores so the player isn't staring
  // at an empty box during the submit-promise await.
  section.wrap.style.display = 'flex';
  _renderLeaderboardBody(section, section.beforeScores || []);
  if (isStale()) return;

  // If there's no submit promise, the player didn't pass — leave the before-scores up.
  if (!section.submitPromise) return;

  // Wait for the submit to resolve so we know the after-state.
  let after = null;
  try { after = await section.submitPromise; } catch {}
  if (isStale()) return;
  // Submit failed — fall back to before-scores with no celebration.
  if (!Array.isArray(after)) return;

  const event = _classifyLeaderboardEvent(section.beforeScores || [], after, section.yourName, section.yourScore);
  // If the player tapped to skip during submit-await, render final state with no anim.
  if (skipped()) { _renderLeaderboardBody(section, after); return; }
  await _animateLeaderboardEvent(section, section.beforeScores || [], after, event, isStale, skipped);
}

// Helper: clear the body and play the celebration animation for the classified event.
// All animations honor isStale() (cancelled screen — bail) and skipped() (fast-forward).
async function _animateLeaderboardEvent(section, before, after, event, isStale, skipped) {
  const abort = () => isStale() || skipped();
  const beatMs = MUSIC_BEAT * 1000;
  section.body.textContent = '';

  // Tagline shown above the entries — varies by event type. Slides up + fades in.
  const tagText = {
    'knockout':     'NEW LEADER!',
    'first-record': 'FIRST RECORD!',
    'own-best':     'NEW BEST!',
    'placed':       null,
    'off-board':    null,
  }[event];
  if (tagText) {
    const tag = document.createElement('div');
    tag.style.cssText = 'font-family:var(--font-display);font-size:14px;letter-spacing:3px;'
      + 'color:var(--col-pink, #FF3B6B);'
      + '-webkit-text-stroke:2px var(--col-ink);paint-order:stroke;'
      + 'text-transform:uppercase;margin-bottom:4px;';
    tag.textContent = tagText;
    tag.animate(
      [{ transform: 'translateY(8px) scale(0.7)', opacity: 0 },
       { transform: 'translateY(0) scale(1.0)',   opacity: 1 }],
      { duration: 320, easing: 'cubic-bezier(0.2, 0.9, 0.3, 1)', fill: 'forwards' }
    );
    section.body.appendChild(tag);
    // Tag appears with a small chime tied to the beat grid.
    if (audioCtx) {
      const t = audioCtx.currentTime + 0.02;
      playCoinClink(0.55, null, 880,  t);
      playCoinClink(0.45, null, 1318, t + 0.08);
    }
  }

  // Knockout: render previous leader's name first (in greyed-out top position), pause briefly
  // so the player reads it, then strike it through and replace with the player's row. The
  // older leader gets struck with a pink line in time with a beat.
  if (event === 'knockout' && before.length > 0) {
    const oldTop = before[0];
    const knockedRow = document.createElement('div');
    knockedRow.style.cssText = 'font-family:var(--font-display);font-size:18px;letter-spacing:2px;'
      + 'color:#888;text-transform:uppercase;position:relative;display:flex;align-items:center;gap:10px;';
    const oldTrophy = document.createElement('span');
    oldTrophy.style.cssText = `display:inline-block;width:24px;height:24px;`
      + `background-image:url(${_trophyUrl});background-size:contain;background-repeat:no-repeat;`
      + `image-rendering:pixelated;filter:grayscale(0.85) brightness(0.7);`;
    knockedRow.appendChild(oldTrophy);
    knockedRow.appendChild(document.createTextNode(`${oldTop.score}  ${oldTop.name}`));
    section.body.appendChild(knockedRow);
    // Strikethrough — a pink line that scales from x=0 to x=1, simulating it being drawn.
    const strike = document.createElement('div');
    strike.style.cssText = 'position:absolute;top:50%;left:0;right:0;height:3px;'
      + 'background:var(--col-pink, #FF3B6B);transform-origin:left;transform:scaleX(0);';
    knockedRow.appendChild(strike);
    await _sleep(abort() ? 0 : 500);
    if (abort()) return;
    if (audioCtx) playSmashSound('metal', 0.6, false, null);
    strike.animate(
      [{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }],
      { duration: 260, easing: 'cubic-bezier(0.4, 0, 0.7, 1)', fill: 'forwards' }
    );
    await _sleep(abort() ? 0 : 320);
    if (abort()) return;
    // Fade the knocked entry to faint grey so attention shifts to the new top.
    knockedRow.animate(
      [{ opacity: 1 }, { opacity: 0.35 }],
      { duration: 200, fill: 'forwards' }
    );
    await _sleep(abort() ? 0 : 220);
    if (abort()) return;
  }

  // New leader entry — gold + bold + larger. Slides in from below with a slight scale-up.
  // Custom pixel-art trophy in front of the score (replaces the 🏆 emoji which doesn't
  // sit well next to the kit's pixel typography).
  const newTop = after[0];
  const headRow = document.createElement('div');
  headRow.style.cssText = 'font-family:var(--font-display);font-size:18px;letter-spacing:2px;'
    + 'color:var(--col-gold, #ffd66b);'
    + '-webkit-text-stroke:2px var(--col-ink);paint-order:stroke;'
    + 'text-transform:uppercase;margin:2px 0 6px;display:flex;align-items:center;gap:10px;';
  const headTrophy = document.createElement('span');
  headTrophy.style.cssText = `display:inline-block;width:24px;height:24px;`
    + `background-image:url(${_trophyUrl});background-size:contain;background-repeat:no-repeat;image-rendering:pixelated;`;
  headRow.appendChild(headTrophy);
  headRow.appendChild(document.createTextNode(`${newTop.score}  ${newTop.name}`));
  section.body.appendChild(headRow);
  if (event === 'knockout' || event === 'first-record' || event === 'own-best') {
    headRow.animate(
      [{ transform: 'translateY(14px) scale(0.85)', opacity: 0 },
       { transform: 'translateY(0) scale(1.08)',    opacity: 1, offset: 0.7 },
       { transform: 'translateY(0) scale(1.0)',     opacity: 1 }],
      { duration: 480, easing: 'cubic-bezier(0.2, 0.9, 0.3, 1)', fill: 'forwards' }
    );
    if (audioCtx) {
      const t = audioCtx.currentTime + 0.04;
      // Rising arpeggio chime — A-C-E like the star-stamp triad, lands on top.
      playCoinClink(0.6, null,  880, t);
      playCoinClink(0.6, null, 1046, t + 0.10);
      playCoinClink(0.7, null, 1318, t + 0.20);
    }
  }

  await _sleep(abort() ? 0 : 200);
  if (abort()) return;

  // Render the rest of the top entries (#2, #3) in the kit display font, smaller than the
  // headline #1 row. Same letter-spacing and uppercase treatment so the leaderboard reads
  // as one type family rather than display-font-on-top + monospace-below.
  for (let i = 1; i < after.length; i++) {
    const e = after[i];
    const row = document.createElement('div');
    row.style.cssText = 'font-family:var(--font-display);font-size:10px;letter-spacing:1.5px;'
      + 'color:#cfd6dc;opacity:0.7;text-transform:uppercase;';
    const isYou = e.name === section.yourName && e.score === section.yourScore;
    if (isYou && event === 'placed') {
      row.style.color = 'var(--col-cyan, #00E5FF)';
      row.style.opacity = '1';
    }
    row.textContent = `#${i + 1}  ${e.score}  ${e.name}`;
    section.body.appendChild(row);
    if (isYou && event === 'placed' && audioCtx) {
      playCoinClink(0.45, null, 1046, audioCtx.currentTime + 0.02);
    }
  }

  // Off-board: show the player's score below the list with a cyan call-out.
  if (event === 'off-board' && typeof section.yourScore === 'number') {
    const you = document.createElement('div');
    you.style.cssText = 'font-family:var(--font-display);font-size:10px;letter-spacing:1.5px;'
      + 'color:var(--col-cyan, #00E5FF);margin-top:4px;text-transform:uppercase;';
    you.textContent = `YOU · ${section.yourScore}`;
    section.body.appendChild(you);
    if (audioCtx) playCoinClink(0.4, null, 880, audioCtx.currentTime + 0.02);
  }
}

// Curated seed prompts the SURPRISE ME button rotates through. Mixed across creature
// scenes, architectural setpieces, atmospheric vignettes, and explicit-genre scenes —
// chosen to demonstrate the range of what the system can do.
const _UGC_SURPRISE_PROMPTS = [
  // Creatures + scenes
  'a farmyard with giant chickens, pigs, and a sleeping cat',
  'a herd of robot elephants marching across a desert plain',
  'cute glow-in-the-dark mushroom creatures in a moonlit forest',
  'a coral reef of cuboid fish swimming around a treasure chest',
  'a flock of pixel-art penguins on an icy cliff',
  'three dinosaurs of different sizes roaring at the sun',
  'an army of tiny robot ants carrying a giant donut',
  'a dragon nesting on a mountain of glowing eggs',
  // Architecture / structures
  'a haunted Victorian mansion with crooked chimneys',
  'a giant Eiffel tower at sunset, with smaller buildings around it',
  'a Mayan temple deep in the jungle with stone steps climbing up',
  'a futuristic city of leaning skyscrapers connected by sky bridges',
  'a windswept lighthouse on a rocky island',
  'a samurai castle on a cherry-blossom hill',
  'a row of beach huts with palm trees and a long pier',
  'an oil rig surrounded by seagulls flying around it',
  // Music + culture mashups
  'cyberpunk samba carnival on Mars with neon dancers',
  'a robot DJ spinning chicago house in a warehouse rave',
  'rasta robots under palm trees listening to dub reggae',
  'a hipster jazz quartet of robots playing in a smoky lounge',
  'a death metal band of robots in a giant volcanic forge',
  'a trap-music dragon counting silver coins on a mountain',
  'a country line-dance of cowboy robots at sundown',
  'a brass big band of robots on a beachside stage',
  'a drum-and-bass robot rave in a neon underground tunnel',
  'a bossa nova tea ceremony with origami robots',
  'flamenco bull-dancers in a sunset arena',
  // Atmospheric / cinematic
  'a giant dinosaur towering over a tiny village',
  'a haunted graveyard at dawn with floating ghosts',
  'a meadow of trees with a master sword stuck in a stone',
  'a desert oasis with crystal palms and a sleeping camel',
  'an underwater temple with mermaid statues',
  'a snowy alpine cabin with smoke curling from the chimney',
  'a candy land of giant lollipops and gummy bears',
  'an aurora-lit ice palace with crystal towers',
  // Pure scale / spectacle
  'one absolutely gigantic mecha-dragon dwarfing the camera',
  'a thousand tiny robot ants marching toward us',
  'a field of exactly seventeen lopsided pumpkins',
  'a single huge oil rig out at sea',
  'an infinity of stacked skyscrapers reaching into the sky',
  'twenty Crystal towers each made of stacked metal blocks',
  // Whimsical / playful
  'a giant cake with candles and tiny party robots',
  'a robot proposing on one knee with floating love hearts',
  'a beach volleyball game with giant pixel balls',
  'a chess board with pieces twice as tall as the king',
  'a circus tent with stripey poles and a clown robot juggling',
  'a vending machine the size of a house in a tiny park',
  // Fantasy / mythological
  'a phoenix rising from a pile of glowing coals',
  'a stone-circle ruins with druid robots chanting',
  'a kraken tentacle bursting through a wooden ship deck',
  'a wizard tower with a glowing orb at the top',
  'a unicorn herd grazing in a flower meadow',
  // Transport / vehicles
  'a giant pirate ship with billowing sails on rough seas',
  'a steampunk train chugging through a mountain pass',
  'a UFO hovering over a cornfield with abducted cows',
  'a hot-air balloon festival rising at dawn',
  // Genre + setting hybrids (the engine seems to like these)
  'a haunted forest with synthwave glow-mushrooms',
  'a polynesian beach party with reggae robot dancers',
  'a goth-rock graveyard at midnight with bat creatures',
  'a happy hardcore gabba robot moshpit',
  'an industrial techno warehouse with strobing pillars',
  // Music-genre forward — each prompt names a specific style so the freeform-music
  // mode reaches for genre-appropriate BPM, drum kit, scale, swing, patterns.
  'a smooth jazz quartet of robots playing in a velvet lounge',
  'a bebop jazz cellar with tiny saxophone-playing rats',
  'a 90s drum-and-bass robot rave under a freeway overpass',
  'a deep house warehouse party with strobing robot DJs',
  'a Detroit techno factory floor with conveyor-belt dancers',
  'a UK garage two-step robot crew on a London estate',
  'a lo-fi hip-hop study session with a robot reading by a window',
  'a boom-bap rap battle between two graffiti-covered robots',
  'a trap-music dragon counting silver in a neon throne room',
  'a synthwave neon racetrack with chrome robot cars',
  'a vaporwave shopping mall with palm trees and pastel robots',
  'a 70s funk band of robots in glittering jumpsuits',
  'a Motown soul stage with sequined backup-singer robots',
  'a Saturday-night disco floor with a giant mirror ball',
  'a punk rock dive bar full of moshing spiky-haired robots',
  'a classic rock arena with stadium-light banks and big amps',
  'a heavy metal forge with anvils and headbanging blacksmith robots',
  'a doom-metal monastery with chanting hooded figures',
  'a black-metal pine forest at midnight with corpse-paint robots',
  'a prog-rock mountaintop with a robot wizard at a synth bank',
  'a country line-dance saloon with hat-tipping cowboy robots',
  'a bluegrass front-porch jam with banjo and washboard robots',
  'a celtic seaside village with bagpipe robots and stone walls',
  'a sea-shanty pirate galley with rowdy mug-clinking robots',
  'a gospel church of singing robots with a stained-glass sun',
  'a reggae beach bar with hammocks and palm-tree speakers',
  'a dub-reggae studio with rastas mixing a dubplate',
  'a ska third-wave parking-lot show with horn-section robots',
  'a samba street parade through colorful Brazilian rooftops',
  'a bossa nova cafe with potted plants and acoustic robots',
  'a salsa dance club with shimmering disco lamps',
  'a flamenco bull-ring at sunset with stamping dancers',
  'a mariachi plaza fountain with sombrero-wearing robots',
  'a K-pop stage with mirrored backdrops and synchronised robots',
  'a Bollywood courtyard with whirling sari-clad dancers',
  'a baroque chamber-music court with powdered-wig robots',
  'a symphonic concert hall with a giant pipe-organ robot',
  'a choir of cathedral robots under stained-glass windows',
  'an opera-house balcony with a robot diva mid-aria',
  'a 90s rave under a motorway with glow-stick robots',
  'a hardstyle festival main stage with mega-pyrotechnics',
  'a jungle (drum-and-bass) warehouse rave with junglist robots',
  'a dubstep wobble-bass cave with neon stalactites',
  'a footwork battle in a Chicago park with sneaker-tapping robots',
  'an afrobeats rooftop garden with djembes and bright textiles',
  'a city-pop Tokyo arcade rooftop with neon palm trees',
  'a glitch-IDM laboratory with malfunctioning android scientists',
  'a math-rock attic studio with robots playing odd-time signatures',
  'a post-rock cliffside lighthouse with crashing waves',
  'a shoegaze fog-drenched bedroom with reverbed amp stacks',
  'an ambient temple of sleeping robots and slow-burning incense',
  'a circus calliope of steam-organ robots blasting tunes',
  'a polka-band Bavarian beer hall with accordion robots',
  'a tango Buenos Aires alleyway with cobblestones and a streetlamp',
  'a Klezmer wedding tent with clarinet robots and dancing chairs',
];

// Friendly display labels for the design + music pills. Internal IDs are unchanged
// (server still receives 'full' / 'creatures' / etc.); only the UI text differs.
const _UGC_VERSION_LABELS = {
  full:      'Standard',
  lean:      'Light',
  freeform:  'Architecture',
  creatures: 'Creatures',
};
const _UGC_MUSIC_LABELS = {
  default:      'Classic 8 Bit',
  freeform:     'Legacy Modern Retro',
  experimental: 'Experimental Modern Retro',
  pruned:       'Modern Retro',
};

// One-line descriptions shown below each pill row, updated as the active selection
// changes. Friendly tone so users can pick deliberately.
const _UGC_VERSION_DESC = {
  full:      'Versatile default. Voxel structures plus rawBlocks.',
  lean:      'Cubes only. Good for rhythmical platforming.',
  freeform:  'Buildings, vehicles, monuments built from varied cuboids.',
  creatures: 'Cuboid creatures with anatomy and symmetry rules, scattered across the level.',
};
const _UGC_MUSIC_DESC = {
  default:      'Chip-tune leaning, close to the original game soundtrack feel.',
  freeform:     'Wide license: unusual modes, swing, drum kits, effects, modulation.',
  experimental: 'Same wide license plus stricter motif-quality rules. Testing whether it produces more variety than Modern Retro.',
  pruned:       'Wider creativity, unusual modes, swing, drum kits, effects, modulation.',
};

// Per-pill visibility — admin toggles in the dashboard flip these off to hide pills
// from the Create page. Current ship: ALL prompt-version pills hidden (the page silently
// uses `_ugcPromptVersion = 'creatures'` as the only mode), and music is trimmed to two
// pills (Classic 8 Bit + Modern Retro = pruned). Legacy Modern Retro and Experimental
// stay registered so admins can re-enable them for A/B work without code changes.
const _ugcPillVisible = {
  full:      false,
  lean:      false,
  freeform:  false,
  creatures: false,
  // Music
  defaultMusic:      true,
  freeformMusic:     false,
  experimentalMusic: false,
  prunedMusic:       true,
};

function _renderCreatePage() {
  // Warm up the Turnstile widget so it has a token ready by the time the user clicks
  // GENERATE. Idempotent — only renders the first time.
  _ensureTurnstileWidget();
  // Match the kit: world-view header (back + title) + caption + content stack. Tighter
  // vertical gap than the default (28px → 14px) so the page reads as a single coherent
  // panel rather than a stack of disconnected forms.
  const view = document.createElement('div');
  view.className = 'ss-world-view';
  view.style.gap = '14px';

  // Header dropped — the global fixed top-left HOME button replaces the inline back
  // button, and the cyan caption below carries the page identity instead of a separate
  // CREATE A LEVEL title.
  const caption = document.createElement('div');
  caption.style.cssText = 'font-family:var(--font-display);font-size:22px;letter-spacing:2px;'
    + 'color:var(--col-cyan, #00E5FF);'
    + '-webkit-text-stroke:3px var(--col-ink);paint-order:stroke;'
    + 'text-transform:uppercase;text-align:center;margin-top:36px;';
  caption.textContent = 'Make a smashing musical world!';
  view.appendChild(caption);

  // Prompt row: chunky polaroid-framed input + a "?" surprise block on the right. The
  // ?-block replaces the old SURPRISE ME button — clicking it rolls a random prompt into
  // the field. Sitting next to the input keeps the "type or roll" affordance at the same
  // moment of decision instead of buried in a button row below.
  const promptRow = document.createElement('div');
  promptRow.style.cssText = 'display:flex;align-items:stretch;gap:8px;width:720px;max-width:92vw;';

  const input = document.createElement('input');
  input.type = 'text';
  input.placeholder = 'a haunted forest with glowing mushrooms';
  input.maxLength = 200;
  input.style.cssText = 'flex:1 1 auto;padding:16px 20px;font:22px "Jersey 25", var(--font-body, ui-monospace), monospace;background:#fff;color:var(--col-ink, #1a1a2e);border:var(--border-chunky, 4px solid #1a1a2e);box-shadow:var(--shadow-hard-sm, 4px 4px 0 #1a1a2e);outline:none;text-align:left;min-width:0;letter-spacing:0.5px;';
  input.addEventListener('mousedown', (e) => e.stopPropagation());
  input.addEventListener('click', (e) => e.stopPropagation());
  // Placeholder visibility relies on default browser behavior: shows when the field is
  // empty, hides as soon as the user types. The previous focus/blur swap manually
  // cleared the placeholder on focus — but the page auto-focuses on entry, so the
  // placeholder never appeared.
  if (_pendingCreatePrompt) {
    input.value = _pendingCreatePrompt;
    _pendingCreatePrompt = '';
  }
  promptRow.appendChild(input);

  const surpriseBlock = document.createElement('button');
  surpriseBlock.type = 'button';
  surpriseBlock.title = 'Surprise me!';
  surpriseBlock.style.cssText = 'flex:0 0 64px;font-family:var(--font-display);font-size:36px;'
    + 'color:var(--col-ink, #1a1a2e);background:var(--col-bg-panel);'
    + 'border:var(--border-chunky, 4px solid #1a1a2e);box-shadow:var(--shadow-hard-sm, 4px 4px 0 #1a1a2e);'
    + 'cursor:pointer;display:flex;align-items:center;justify-content:center;line-height:1;'
    + 'transition:background-color 80ms steps(2);';
  surpriseBlock.textContent = '?';
  surpriseBlock.addEventListener('mousedown', (e) => e.stopPropagation());
  surpriseBlock.addEventListener('mouseenter', () => { surpriseBlock.style.background = 'var(--col-cyan, #00E5FF)'; });
  surpriseBlock.addEventListener('mouseleave', () => { surpriseBlock.style.background = 'var(--col-bg-panel)'; });
  promptRow.appendChild(surpriseBlock);

  view.appendChild(promptRow);

  // Design selector — friendly Standard / Light / Architecture / Creatures labels map
  // to internal API IDs (full / lean / freeform / creatures). Pill row is centered
  // (no inline left-label) so the active selection sits under the input naturally.
  // Description line below updates with the active selection.
  // Filter to only the pills enabled in the admin dashboard. Hidden ones drop out of
  // the row entirely so the remaining pills re-centre cleanly. If the current selection
  // refers to a hidden pill, fall back to the first visible option.
  const VERSION_OPTIONS = ['creatures', 'full', 'lean', 'freeform'].filter(v => _ugcPillVisible[v]);
  const MUSIC_VISIBILITY_KEY = { default: 'defaultMusic', freeform: 'freeformMusic', experimental: 'experimentalMusic', pruned: 'prunedMusic' };
  const MUSIC_OPTIONS   = ['freeform', 'experimental', 'pruned', 'default'].filter(v => _ugcPillVisible[MUSIC_VISIBILITY_KEY[v]]);
  if (VERSION_OPTIONS.length && !VERSION_OPTIONS.includes(_ugcPromptVersion)) _ugcPromptVersion = VERSION_OPTIONS[0];
  if (MUSIC_OPTIONS.length   && !MUSIC_OPTIONS.includes(_ugcMusicVersion))     _ugcMusicVersion   = MUSIC_OPTIONS[0];

  const versionRow = document.createElement('div');
  versionRow.style.cssText = 'display:flex;gap:10px;align-items:center;flex-wrap:wrap;justify-content:center;';
  const pills = {};
  for (const v of VERSION_OPTIONS) {
    const pill = _makeVersionPill(_UGC_VERSION_LABELS[v] || v);
    pill.onclick = (e) => { e.stopPropagation(); _ugcPromptVersion = v; refreshPills(); };
    pills[v] = pill;
    versionRow.appendChild(pill);
  }
  if (VERSION_OPTIONS.length) view.appendChild(versionRow);

  const versionDesc = document.createElement('div');
  versionDesc.style.cssText = 'font-family:"Jersey 25", var(--font-body, ui-monospace), monospace;font-size:16px;color:#cfd6dc;opacity:0.85;letter-spacing:0.5px;text-align:center;max-width:560px;line-height:1.25;';
  if (VERSION_OPTIONS.length) view.appendChild(versionDesc);

  // Music selector — friendly Classic 8 Bit / Modern Retro labels. Same centred pill row.
  const musicRow = document.createElement('div');
  musicRow.style.cssText = 'display:flex;gap:10px;align-items:center;flex-wrap:wrap;justify-content:center;';
  const musicPills = {};
  for (const v of MUSIC_OPTIONS) {
    const pill = _makeVersionPill(_UGC_MUSIC_LABELS[v] || v);
    pill.onclick = (e) => { e.stopPropagation(); _ugcMusicVersion = v; refreshPills(); };
    musicPills[v] = pill;
    musicRow.appendChild(pill);
  }
  if (MUSIC_OPTIONS.length) view.appendChild(musicRow);

  const musicDesc = document.createElement('div');
  musicDesc.style.cssText = 'font-family:"Jersey 25", var(--font-body, ui-monospace), monospace;font-size:16px;color:#cfd6dc;opacity:0.85;letter-spacing:0.5px;text-align:center;max-width:560px;line-height:1.25;';
  if (MUSIC_OPTIONS.length) view.appendChild(musicDesc);

  function refreshPills() {
    for (const v of VERSION_OPTIONS) _setPillActive(pills[v], _ugcPromptVersion === v);
    for (const v of MUSIC_OPTIONS)   _setPillActive(musicPills[v], _ugcMusicVersion === v);
    versionDesc.textContent = _UGC_VERSION_DESC[_ugcPromptVersion] || '';
    musicDesc.textContent   = _UGC_MUSIC_DESC[_ugcMusicVersion] || '';
  }
  refreshPills();

  // Big magenta GENERATE CTA — matches the home page CREATE tile's primary-action style
  // (pink bg, white text + ink stroke, chunky border + drop shadow, hover-swap to yellow).
  // SURPRISE ME moved into the prompt row as the ?-block, so this row only carries the
  // primary action.
  const generateBtn = document.createElement('button');
  generateBtn.type = 'button';
  generateBtn.style.cssText = 'padding:18px 56px;font-family:var(--font-display);font-size:22px;letter-spacing:2px;text-transform:uppercase;'
    + 'color:#fff;-webkit-text-stroke:2px var(--col-ink, #1a1a2e);paint-order:stroke;'
    + 'background:var(--col-pink, #ff3b6b);border:4px solid var(--col-ink, #1a1a2e);'
    + 'box-shadow:5px 5px 0 var(--col-ink, #0a0820);cursor:pointer;'
    + 'transition:transform 60ms steps(1), box-shadow 60ms steps(1), background-color 80ms steps(2);';
  generateBtn.textContent = 'GENERATE ▸';
  generateBtn.addEventListener('mousedown', (e) => e.stopPropagation());
  generateBtn.addEventListener('mouseenter', () => { generateBtn.style.background = 'var(--col-bg-panel)'; });
  generateBtn.addEventListener('mouseleave', () => { generateBtn.style.background = 'var(--col-pink, #ff3b6b)'; });
  view.appendChild(generateBtn);

  // Status / error display.
  const status = document.createElement('div');
  status.className = 'ss-home__caption';
  status.style.minHeight = '16px';
  status.style.color = 'var(--col-pink, #ff6e6e)';
  status.style.fontFamily = '"Jersey 25", var(--font-body), monospace';
  status.style.fontSize = '16px';
  status.style.letterSpacing = '0.5px';
  view.appendChild(status);

  _homeContent.appendChild(view);

  // Wire up
  generateBtn.onclick = (e) => {
    e.stopPropagation();
    const prompt = input.value.trim();
    if (!prompt) { status.textContent = 'type a prompt first'; return; }
    _generateUgcLevel(prompt, status);
  };
  surpriseBlock.onclick = (e) => {
    e.stopPropagation();
    // Pull a random prompt from the curated pool, but avoid re-picking whatever's
    // currently in the input (so consecutive clicks always change the result).
    const current = input.value.trim();
    let pick = current;
    let tries = 0;
    while (pick === current && tries < 5) {
      pick = _UGC_SURPRISE_PROMPTS[Math.floor(Math.random() * _UGC_SURPRISE_PROMPTS.length)];
      tries++;
    }
    input.value = pick;
    input.focus();
  };
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') generateBtn.click();
  });

  // Auto-focus the input so the player can type immediately.
  setTimeout(() => input.focus(), 50);
}

// =========================================================
// UGC Community page — list recent levels from the server, click to play.
// =========================================================

async function _renderCommunityPage() {
  // Reuse the world-view layout (header with back + title, then a content grid below).
  // lockHint's parent uses `justify-content: center`, which traps the top of long content.
  // We work around that by capping the inner list's height to ~60vh and giving it its own
  // scrollbar — header stays anchored, list scrolls within the page.
  const view = document.createElement('div');
  view.className = 'ss-world-view';

  // Title only — back button is now the global fixed top-left HOME button.
  const title = document.createElement('div');
  title.className = 'ss-world-view__title';
  title.textContent = 'COMMUNITY';
  title.style.marginTop = '36px';
  view.appendChild(title);

  // Player controls (Rhythm/Aim assist + Equip Gloves) so community levels are played with the same
  // at-a-glance settings as the campaign, without sending players back to a separate settings screen.
  view.appendChild(_buildPlayerControls());

  // ALL / MINE filter pills.
  const filterRow = document.createElement('div');
  filterRow.style.cssText = 'display:flex;gap:8px;align-items:center;margin-bottom:12px;';
  filterRow.addEventListener('mousedown', (e) => e.stopPropagation());
  const allPill  = _makeVersionPill('ALL');
  const minePill = _makeVersionPill('MINE');
  filterRow.appendChild(allPill);
  filterRow.appendChild(minePill);
  view.appendChild(filterRow);
  function refreshFilterPills() {
    _setPillActive(allPill,  _communityFilter === 'all');
    _setPillActive(minePill, _communityFilter === 'mine');
  }
  allPill.onclick  = (e) => { e.stopPropagation(); _communityFilter = 'all';  _renderHome(); };
  minePill.onclick = (e) => { e.stopPropagation(); _communityFilter = 'mine'; _renderHome(); };
  refreshFilterPills();

  // Sort pills — newest / top plays / top score / most stars. Applied client-side on
  // the levels array before rendering. Stars sort uses local progress (per-browser).
  const sortRow = document.createElement('div');
  sortRow.style.cssText = 'display:flex;gap:8px;align-items:center;margin-bottom:12px;flex-wrap:wrap;justify-content:center;';
  sortRow.addEventListener('mousedown', (e) => e.stopPropagation());
  const sortPills = {
    newest: _makeVersionPill('NEWEST'),
    plays:  _makeVersionPill('TOP PLAYS'),
    score:  _makeVersionPill('TOP SCORE'),
  };
  for (const [key, pill] of Object.entries(sortPills)) {
    pill.onclick = (e) => { e.stopPropagation(); _communitySort = key; _renderHome(); };
    sortRow.appendChild(pill);
  }
  view.appendChild(sortRow);
  function refreshSortPills() {
    for (const [key, pill] of Object.entries(sortPills)) {
      _setPillActive(pill, _communitySort === key);
    }
  }
  refreshSortPills();

  // List container — flows into the page-level scroll on `lockHint`. Was previously a
  // capped inner scroll region (max-height:60vh + overflow-y:auto) which created a
  // nested scrollbar inside the page, giving the whole thing a "frames-from-1999" feel.
  // Removing the cap lets cards fill the page and scroll as one document.
  const listWrap = document.createElement('div');
  listWrap.style.cssText = 'width:100%;display:flex;justify-content:center;padding:0 24px;';
  listWrap.addEventListener('mousedown', (e) => e.stopPropagation());
  const list = document.createElement('div');
  // 252px = 240 card + 6px border × 2 (content-box sizing). auto-fill wraps responsively.
  // max-width caps the grid at 5 cards wide so the row stays centered on wide viewports.
  list.style.cssText = 'display:grid;grid-template-columns:repeat(auto-fill, 252px);gap:16px;align-content:start;justify-content:center;width:100%;max-width:1324px;';
  listWrap.appendChild(list);
  view.appendChild(listWrap);

  _homeContent.appendChild(view);

  // Loading placeholder.
  const placeholder = document.createElement('div');
  placeholder.className = 'ss-home__caption';
  placeholder.style.gridColumn = '1 / -1';
  placeholder.style.padding = '24px';
  placeholder.style.textAlign = 'center';
  placeholder.textContent = 'loading…';
  list.appendChild(placeholder);

  // Fetch the recent-levels list. limit=500 matches the server cap so we get the full
  // visible window — the page is a true "browse all" surface now.
  let levels = [];
  try {
    const res = await fetch(`${UGC_SERVER_URL}/community?limit=500`);
    if (!res.ok) throw new Error(`server returned ${res.status}`);
    const data = await res.json();
    levels = Array.isArray(data.levels) ? data.levels : [];
  } catch (e) {
    placeholder.textContent = `couldn't load levels: ${e.message}`;
    return;
  }

  list.textContent = '';

  // MINE filter — keep ids in the per-browser ledger OR levels authored under the
  // player's current nickname. The nickname match auto-heals cases where localStorage
  // was wiped (`?reset=1`, admin reset) or the level was generated from a different
  // browser/session, so the user still sees their work as long as the creatorName
  // matches their nickname. Soft attribution; case-insensitive trim compare.
  if (_communityFilter === 'mine') {
    const mineIds = new Set(_loadMyUgcIds());
    const myName = (_loadPlayerName() || '').trim().toLowerCase();
    levels = levels.filter(l => {
      if (mineIds.has(l.id)) return true;
      if (myName && (l.creatorName || '').trim().toLowerCase() === myName) return true;
      return false;
    });
  }

  // Sort.
  if (_communitySort === 'plays') {
    levels = [...levels].sort((a, b) => (b.playCount || 0) - (a.playCount || 0));
  } else if (_communitySort === 'score') {
    levels = [...levels].sort((a, b) => {
      const sa = a.topScores?.[0]?.score || 0;
      const sb = b.topScores?.[0]?.score || 0;
      return sb - sa;
    });
  }
  // 'newest' is the server's default order (createdAt desc), no client sort needed.

  if (levels.length === 0) {
    // Empty state — friendly text + a chunky magenta CREATE call-to-action so the user
    // has a clear next step instead of a dead end. CTA matches the home page CREATE
    // tile half styling (pink bg, white text + ink stroke).
    const empty = document.createElement('div');
    empty.style.cssText = 'grid-column:1 / -1;display:flex;flex-direction:column;align-items:center;gap:24px;padding:48px;';
    const text = document.createElement('div');
    text.className = 'ss-home__caption';
    text.style.textAlign = 'center';
    text.textContent = _communityFilter === 'mine'
      ? "you haven't made any levels yet"
      : 'no levels yet — be the first';
    empty.appendChild(text);
    const createBtn = document.createElement('button');
    createBtn.type = 'button';
    createBtn.textContent = 'CREATE';
    createBtn.style.cssText = 'padding:18px 44px;font-family:var(--font-display);font-size:22px;letter-spacing:2px;text-transform:uppercase;'
      + 'color:#fff;-webkit-text-stroke:2px var(--col-ink, #1a1a2e);paint-order:stroke;'
      + 'background:var(--col-pink, #FF3B6B);border:4px solid var(--col-ink, #1a1a2e);'
      + 'box-shadow:5px 5px 0 var(--col-ink, #0a0820);cursor:pointer;'
      + 'transition:transform 60ms steps(1), box-shadow 60ms steps(1);';
    createBtn.addEventListener('mousedown', (e) => e.stopPropagation());
    createBtn.addEventListener('mouseenter', () => {
      createBtn.style.background = 'var(--col-bg-panel)';
    });
    createBtn.addEventListener('mouseleave', () => {
      createBtn.style.background = 'var(--col-pink, #FF3B6B)';
    });
    createBtn.onclick = (e) => { e.stopPropagation(); _homeView = 'create'; _renderHome(); };
    empty.appendChild(createBtn);
    list.appendChild(empty);
    return;
  }

  // IntersectionObserver — defers each card's silhouette render until it scrolls within
  // 200px of the viewport. With 40+ cards (and growing), rendering all silhouettes
  // up-front froze the page; lazy loading keeps initial paint snappy. Cards re-use the
  // same observer; each unobserves itself after its silhouette is set.
  const silhObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const target = entry.target;
      const lvl = target._lvl;
      if (lvl) {
        _paintLazyUgcSilhouette(target, lvl.id, lvl.scene, `comm-${lvl.id}`);
      }
      silhObserver.unobserve(target);
    }
  }, { rootMargin: '200px' });

  for (const lvl of levels) {
    list.appendChild(_buildCommunityCard(lvl, silhObserver));
  }
}

// Mix two #rrggbb hex colours by linear interpolation. amt=0 → first colour, amt=1 → second.
function _mixHex(a, b, amt) {
  const pa = parseInt(a.slice(1), 16), pb = parseInt(b.slice(1), 16);
  const ar = (pa >> 16) & 0xff, ag = (pa >> 8) & 0xff, ab = pa & 0xff;
  const br = (pb >> 16) & 0xff, bg = (pb >> 8) & 0xff, bb = pb & 0xff;
  const r = Math.round(ar + (br - ar) * amt);
  const g = Math.round(ag + (bg - ag) * amt);
  const bl = Math.round(ab + (bb - ab) * amt);
  return '#' + ((r << 16) | (g << 8) | bl).toString(16).padStart(6, '0');
}
// Card panel background — softened scene-floor colour so cards read as belonging to their
// world's palette instead of being bright-white interrupts. Mix is 60% white so structures
// still pop while the tint is unmistakable.
function _silhouettePanelColor(sceneName) {
  const floor = _SCENE_FLOOR_COLOR[sceneName] || '#6e7080';
  return _mixHex(floor, '#ffffff', 0.6);
}

// Scene → floor colour map. Pulled from the engine's SCENE_PRESETS so silhouette
// floors match the in-game ground colour the player will actually see.
const _SCENE_FLOOR_COLOR = {
  'outdoor-grass':          '#6dbb3c',
  'outdoor-snow':           '#eaf2f9',
  'outdoor-tropical':       '#f2d68a',
  'outdoor-sunset':         '#c88450',
  'outdoor-night':          '#1a2030',
  'outdoor-storm':          '#6a7280',
  'outdoor-volcanic':       '#4a2818',
  'outdoor-desert':         '#dcb478',
  'outdoor-cherry-blossom': '#f8c8d8',
  'outdoor-autumn':         '#d4622a',
  'outdoor-aurora':         '#5fa898',
  'outdoor-candy':          '#40d0e0',
  'outdoor-savanna':        '#d8a850',
};
// Per-material face shades — top (lighter), front (canonical block colour), side (darker).
// Iso convention: top is hit most by ambient light, side is in shadow, front sits between.
// Each material gets its own trio so silhouettes also encode "what you'll be smashing".
const _SILH_FACE_COLORS = {
  // [top, front, side]
  brick: ['#de8470', '#c4684f', '#88483a'],
  crate: ['#f3c372', '#d9a04a', '#97703a'],
  metal: ['#c4c8d2', '#9a9ea6', '#6c6f76'],
};

// Render a server-computed iso silhouette into a canvas. The server emits canvas-pixel
// space polygons (already painters-sorted): a floor parallelogram + per-block face quads
// (top/front/side). We anti-alias-fill each polygon and stroke a thin dark outline so
// individual blocks are visible even when adjacent same-material faces touch.
// Client-side silhouette computation — same logic as `tools/ugc-server.mjs::computeSilhouette`
// but takes a pre-extracted block array (each block: {x, y, z, w, h, d, material}). Used by
// the one-shot dumper for curated levels (since we capture blocks AFTER they're built into
// the live `blocks[]` array, not from a level spec). Output shape matches the server's so
// `_renderSilhouetteToDataUrl` consumes it without changes.
function _computeSilhouetteFromBlocks(blocks) {
  if (!blocks || blocks.length === 0) return null;

  let minX = Infinity, maxX = -Infinity;
  let minY = Infinity, maxY = -Infinity;
  let minZ = Infinity, maxZ = -Infinity;
  for (const b of blocks) {
    if (b.x - b.w / 2 < minX) minX = b.x - b.w / 2;
    if (b.x + b.w / 2 > maxX) maxX = b.x + b.w / 2;
    if (b.y - b.h / 2 < minY) minY = b.y - b.h / 2;
    if (b.y + b.h / 2 > maxY) maxY = b.y + b.h / 2;
    if (b.z - b.d / 2 < minZ) minZ = b.z - b.d / 2;
    if (b.z + b.d / 2 > maxZ) maxZ = b.z + b.d / 2;
  }
  const floorY = Math.min(0, minY);
  const floor = {
    x: (minX + maxX) / 2, y: floorY, z: (minZ + maxZ) / 2,
    w: maxX - minX, h: 0.01, d: maxZ - minZ,
  };
  const PX = 12;

  let minSx = Infinity, maxSx = -Infinity, minSy = Infinity, maxSy = -Infinity;
  const expandBbox = (b) => {
    for (let i = 0; i < 8; i++) {
      const x = b.x + ((i & 1) ? b.w / 2 : -b.w / 2);
      const y = b.y + ((i & 2) ? b.h / 2 : -b.h / 2);
      const z = b.z + ((i & 4) ? b.d / 2 : -b.d / 2);
      const sx = (x - z) * PX;
      const sy = ((x + z) * 0.5 - y) * PX;
      if (sx < minSx) minSx = sx;
      if (sx > maxSx) maxSx = sx;
      if (sy < minSy) minSy = sy;
      if (sy > maxSy) maxSy = sy;
    }
  };
  for (const b of blocks) expandBbox(b);
  expandBbox(floor);

  const W = Math.max(1, Math.ceil(maxSx - minSx));
  const H = Math.max(1, Math.ceil(maxSy - minSy));
  const project = (x, y, z) => [(x - z) * PX - minSx, ((x + z) * 0.5 - y) * PX - minSy];

  const floorPoly = [
    project(floor.x - floor.w / 2, floor.y + floor.h / 2, floor.z - floor.d / 2),
    project(floor.x + floor.w / 2, floor.y + floor.h / 2, floor.z - floor.d / 2),
    project(floor.x + floor.w / 2, floor.y + floor.h / 2, floor.z + floor.d / 2),
    project(floor.x - floor.w / 2, floor.y + floor.h / 2, floor.z + floor.d / 2),
  ];
  const sorted = blocks.slice().sort((a, b) => (a.x + a.z + a.y) - (b.x + b.z + b.y));
  const faces = [];
  for (const b of sorted) {
    const m = b.material === 'crate' ? 'c' : b.material === 'metal' ? 'm' : 'b';
    const xMin = b.x - b.w / 2, xMax = b.x + b.w / 2;
    const yMin = b.y - b.h / 2, yMax = b.y + b.h / 2;
    const zMin = b.z - b.d / 2, zMax = b.z + b.d / 2;
    const t = [project(xMin, yMax, zMin), project(xMax, yMax, zMin), project(xMax, yMax, zMax), project(xMin, yMax, zMax)];
    const f = [project(xMin, yMin, zMax), project(xMax, yMin, zMax), project(xMax, yMax, zMax), project(xMin, yMax, zMax)];
    const s = [project(xMax, yMin, zMin), project(xMax, yMin, zMax), project(xMax, yMax, zMax), project(xMax, yMax, zMin)];
    const flat = (q) => [q[0][0], q[0][1], q[1][0], q[1][1], q[2][0], q[2][1], q[3][0], q[3][1]];
    faces.push({ m, f: 0, p: flat(t) });
    faces.push({ m, f: 1, p: flat(f) });
    faces.push({ m, f: 2, p: flat(s) });
  }
  return { w: W, h: H, floor: floorPoly, faces };
}

// One-shot dumper — runs from the browser console. For each curated level id, builds the
// level into the world, captures `blocks[]`, runs the silhouette compute, and accumulates
// results. At the end, downloads a JSON file you can drop into `web/src/curated-silhouettes.json`.
// Intended to be run ONCE per level-design change. Since World 1 layouts are stable
// ("never touching them again"), re-running is rare. Exposed as `window._dumpCuratedSilhouettes`
// so it survives module reloads.
window._dumpCuratedSilhouettes = function () {
  const levelIds = [];
  for (const w of WORLDS) {
    if (w.hidden) continue;
    for (const id of w.levelIds) levelIds.push(id);
  }
  // Save current state so we can restore.
  const wasRunActive = _runActive;
  _runActive = false;

  const out = {};
  for (const id of levelIds) {
    try {
      _clearWorld();
      _buildLevelLayout(id);
      // Extract block geometry from the in-game blocks[] array. Each `block` is a
      // THREE.Mesh with .position and .geometry.parameters (BoxGeometry width/height/depth).
      const captured = blocks
        .filter(b => b && b.geometry && b.geometry.parameters)
        .map(b => ({
          x: b.position.x, y: b.position.y, z: b.position.z,
          w: b.geometry.parameters.width,
          h: b.geometry.parameters.height,
          d: b.geometry.parameters.depth,
          material: b.userData?.material || 'brick',
        }));
      const silh = _computeSilhouetteFromBlocks(captured);
      // applyScene fires during _buildLevelLayout and updates _activeSceneName, so we can
      // capture the scene that was active at the moment of build. The silhouette renderer
      // uses this to colour the floor parallelogram (e.g. outdoor-grass → green).
      out[id] = { silhouette: silh, scene: _activeSceneName, blockCount: captured.length };
      console.log(`[silh] ${id}: ${captured.length} blocks → ${silh ? `${silh.w}x${silh.h}px, ${silh.faces.length / 3} cells` : 'null'}`);
    } catch (e) {
      console.warn(`[silh] ${id} FAILED:`, e);
      out[id] = null;
    }
  }
  _clearWorld();
  _runActive = wasRunActive;

  // Download as JSON file.
  const blob = new Blob([JSON.stringify(out, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'curated-silhouettes.json';
  a.click();
  URL.revokeObjectURL(url);
  console.log(`[silh] dump complete (${Object.keys(out).length} levels). Save the downloaded file to web/src/curated-silhouettes.json`);
  return out;
};

// Memoize silhouette data URLs by cache key. The same level renders identically every
// time, and toDataURL on a 900×500 canvas is ~30-80ms each — without caching, the home
// page (30 levels) and the level cards re-render the same canvases on every nav.
const _silhouetteCache = new Map();

// UGC silhouette data fetched from the Worker's /silhouette/:id endpoint. Keyed by ugc id.
// Values are silhouette objects or null (404 / not yet available — never refetch). The
// Worker sets immutable Cache-Control, so the browser HTTP cache covers reloads; this map
// covers within-session repeat reads.
const _ugcSilhouetteDataCache = new Map();
// Promise-cache so concurrent observers asking for the same id only kick one fetch.
const _ugcSilhouetteInFlight = new Map();
function _fetchUgcSilhouette(id) {
  if (_ugcSilhouetteDataCache.has(id)) return Promise.resolve(_ugcSilhouetteDataCache.get(id));
  if (_ugcSilhouetteInFlight.has(id)) return _ugcSilhouetteInFlight.get(id);
  const p = (async () => {
    try {
      const res = await fetch(`${UGC_SERVER_URL}/silhouette/${id}`);
      if (!res.ok) { _ugcSilhouetteDataCache.set(id, null); return null; }
      const data = await res.json();
      const silh = data?.silhouette || null;
      _ugcSilhouetteDataCache.set(id, silh);
      return silh;
    } catch {
      _ugcSilhouetteDataCache.set(id, null);
      return null;
    } finally {
      _ugcSilhouetteInFlight.delete(id);
    }
  })();
  _ugcSilhouetteInFlight.set(id, p);
  return p;
}
// Render-and-paint helper. Handles fetch + render + assignment in one call, with the
// rendered-cache fast path. targetEl can be any element with a backgroundImage style.
// `options.hideFloor` skips the floor parallelogram; `options.scale` overrides the
// default 0.7 render scale.
function _paintLazyUgcSilhouette(targetEl, ugcId, sceneName, cacheKey, options) {
  const hideFloor = !!(options && options.hideFloor);
  const scale = options && options.scale;
  if (cacheKey && _silhouetteCache.has(cacheKey)) {
    targetEl.style.backgroundImage = `url(${_silhouetteCache.get(cacheKey)})`;
    return;
  }
  _fetchUgcSilhouette(ugcId).then(silh => {
    if (!silh) return;
    const url = _renderSilhouetteToDataUrl(silh, sceneName, cacheKey, scale, hideFloor);
    if (url) targetEl.style.backgroundImage = `url(${url})`;
  });
}

function _renderSilhouetteToDataUrl(silh, sceneName, cacheKey, scaleOverride, hideFloor) {
  if (cacheKey && _silhouetteCache.has(cacheKey)) return _silhouetteCache.get(cacheKey);
  if (!silh || !silh.w || !silh.h || !Array.isArray(silh.faces)) return null;
  // Hi-res-but-not-smoothed pass: render close to full resolution then let CSS upscale
  // with `image-rendering: pixelated`. Polygon fills inside the canvas keep their
  // anti-aliased detail, but the small CSS upscale produces crisp non-blurred edges
  // rather than the fully-smoothed look of true full-res rendering.
  const PIXEL_SCALE = scaleOverride || 0.7;
  const c = document.createElement('canvas');
  c.width  = Math.max(1, Math.round(silh.w * PIXEL_SCALE));
  c.height = Math.max(1, Math.round(silh.h * PIXEL_SCALE));
  const ctx = c.getContext('2d');
  ctx.imageSmoothingEnabled = false;
  ctx.scale(PIXEL_SCALE, PIXEL_SCALE);

  const floorColor = _SCENE_FLOOR_COLOR[sceneName] || '#6e7080';

  // 1) Floor parallelogram first — sits underneath everything. Skipped when
  // hideFloor is set: the home-page background field uses iso silhouettes
  // without the platform under the blocks (just the block art on a
  // transparent card). Card callers (community, featured, hero) keep the
  // floor so the silhouette reads as a full scene.
  if (!hideFloor && silh.floor && silh.floor.length === 4) {
    ctx.fillStyle = floorColor;
    ctx.beginPath();
    ctx.moveTo(silh.floor[0][0], silh.floor[0][1]);
    for (let i = 1; i < 4; i++) ctx.lineTo(silh.floor[i][0], silh.floor[i][1]);
    ctx.closePath();
    ctx.fill();
  }

  // 2) Block face polygons in painters order — fill with face shade, stroke 1px dark
  // outline so the iso voxel edges are crisp and adjacent same-material faces don't fuse.
  // Per-face fill+stroke (not split into passes) so a closer block's fill correctly hides
  // a farther block's stroke where they overlap. A separated stroke pass would leak back-
  // block outlines through closer block fills, making the silhouette look transparent.
  ctx.lineWidth = 1;
  ctx.lineJoin = 'miter';
  for (const face of silh.faces) {
    const matKey = face.m === 'c' ? 'crate' : face.m === 'm' ? 'metal' : 'brick';
    const colors = _SILH_FACE_COLORS[matKey];
    const p = face.p;  // [x0,y0, x1,y1, x2,y2, x3,y3]
    ctx.fillStyle = colors[face.f];
    ctx.strokeStyle = colors[2];  // material's darkest shade for the outline
    ctx.beginPath();
    ctx.moveTo(p[0], p[1]);
    ctx.lineTo(p[2], p[3]);
    ctx.lineTo(p[4], p[5]);
    ctx.lineTo(p[6], p[7]);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }
  const dataUrl = c.toDataURL();
  if (cacheKey) _silhouetteCache.set(cacheKey, dataUrl);
  return dataUrl;
}

// Material colours for the proportion strip. Match the engine's MATERIALS palette so
// the strip reads as "this is what the level is made of" using familiar block colours.
const _MATERIAL_STRIPE_COLOR = {
  crate: '#d9a04a',  // warm wood
  brick: '#c4684f',  // red brick
  metal: '#9a9ea6',  // silver
};

// Pixel-perfect material tiles for the proportion strip. The in-game 64×64 textures
// don't downscale cleanly to ~20px strip height — detail loss + aliasing make them look
// glitchy. These dedicated tiny tiles are painted at the actual strip pixel scale, so
// every tile pixel is a literal display pixel.
//
// Tile dimensions chosen so one full tile fits the 20px strip-content height vertically
// and tiles repeat cleanly across the segment width:
//   crate:  10×20 px (vertical wood plank)
//   brick:  16×20 px (offset brick rows)
//   metal:  12×20 px (riveted metal plate)
function _paintCrateStripTile(ctx, w, h) {
  ctx.imageSmoothingEnabled = false;
  // Base wood
  ctx.fillStyle = '#d9a04a'; ctx.fillRect(0, 0, w, h);
  // Vertical grain — two darker streaks
  ctx.fillStyle = '#b8843a';
  ctx.fillRect(2, 0, 1, h);
  ctx.fillRect(6, 0, 1, h);
  // Plank top edge — dark
  ctx.fillStyle = '#8c5e1a'; ctx.fillRect(0, 0, w, 1);
  // Plank bottom edge — slightly dark (read as plank seam below)
  ctx.fillStyle = '#a87528'; ctx.fillRect(0, h - 1, w, 1);
}
function _paintBrickStripTile(ctx, w, h) {
  ctx.imageSmoothingEnabled = false;
  ctx.fillStyle = '#c4684f'; ctx.fillRect(0, 0, w, h);
  const halfH = Math.floor(h / 2);
  const halfW = Math.floor(w / 2);
  // Mortar — dark lines between bricks. Vertical mortar offsets between top + bottom rows.
  ctx.fillStyle = '#5a2818';
  ctx.fillRect(0, halfH - 1, w, 1);                      // horizontal mortar (mid)
  ctx.fillRect(0, 0, 1, halfH - 1);                       // top-row vertical at x=0
  ctx.fillRect(halfW, halfH, 1, h - halfH);               // bottom-row vertical at x=mid (offset)
  // Subtle highlight along the top of each brick — reads as 3D bevel
  ctx.fillStyle = '#d2785f';
  ctx.fillRect(1, 1, w - 1, 1);
  ctx.fillRect(0, halfH + 1, halfW, 1);
  ctx.fillRect(halfW + 1, halfH + 1, w - halfW - 1, 1);
}
function _paintMetalStripTile(ctx, w, h) {
  ctx.imageSmoothingEnabled = false;
  ctx.fillStyle = '#9a9ea6'; ctx.fillRect(0, 0, w, h);
  // Top edge brighter, bottom darker → subtle bevel
  ctx.fillStyle = '#b6bac2'; ctx.fillRect(0, 0, w, 1);
  ctx.fillStyle = '#71757d'; ctx.fillRect(0, h - 1, w, 1);
  // Rivets at corners — 2×2 dark with a 1px highlight pixel for shine
  const rivet = (x, y) => {
    ctx.fillStyle = '#3a3d44'; ctx.fillRect(x, y, 2, 2);
    ctx.fillStyle = '#d4d8de'; ctx.fillRect(x, y, 1, 1);
  };
  rivet(1, 1);
  rivet(w - 3, 1);
  rivet(1, h - 3);
  rivet(w - 3, h - 3);
}
let _materialPatternUrlCache = null;
function _getMaterialPatternUrls() {
  if (_materialPatternUrlCache) return _materialPatternUrlCache;
  const build = (paint, w, h) => {
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    paint(c.getContext('2d'), w, h);
    return c.toDataURL();
  };
  _materialPatternUrlCache = {
    crate: build(_paintCrateStripTile, 10, 20),
    brick: build(_paintBrickStripTile, 16, 20),
    metal: build(_paintMetalStripTile, 12, 20),
  };
  return _materialPatternUrlCache;
}
const _MATERIAL_TILE_SIZE_CSS = {
  crate: '10px 20px',
  brick: '16px 20px',
  metal: '12px 20px',
};

// Shared score-badge builder for community + featured cards. Renders the leaderboard top
// score with the player's name (truncated) and the local personal best stacked beneath
// it. Falls back to the existing BEST/NEW behaviour when no top score exists yet.
function _buildScoreBadge(topScores, personalBest) {
  const top = Array.isArray(topScores) && topScores.length > 0 ? topScores[0] : null;
  if (top) {
    const wrap = document.createElement('div');
    wrap.style.cssText = 'display:flex;flex-direction:column;gap:2px;margin-top:0;';
    // Top score line: pixel-art trophy icon replaces the "TOP ·" label so the row reads
    // visually instead of as text. Trophy + score + name on a single flex row.
    const topLine = document.createElement('div');
    topLine.className = 'ss-level__best';
    topLine.style.cssText = 'margin-top:0;font-size:11px;letter-spacing:1px;display:flex;align-items:center;gap:6px;';
    const trophy = document.createElement('span');
    // The text in this badge has no descenders being used, so its optical centre sits in
    // the upper half of the line box. Lift the trophy a couple of px so its visible mass
    // aligns with the text's optical centre rather than the geometric line centre.
    // 4-direction 1px ink drop-shadows give the gold trophy the same dark-outline treatment
    // as the kit's outlined text — without it, gold-on-yellow card bg has no contrast and the
    // icon disappears into the background.
    trophy.style.cssText = `display:inline-block;width:16px;height:16px;`
      + `background-image:url(${_trophyUrl});background-size:contain;background-repeat:no-repeat;`
      + `image-rendering:pixelated;flex:0 0 auto;transform:translateY(-2px);`
      + `filter:drop-shadow(1px 0 0 var(--col-ink, #1a1a2e)) drop-shadow(-1px 0 0 var(--col-ink, #1a1a2e)) drop-shadow(0 1px 0 var(--col-ink, #1a1a2e)) drop-shadow(0 -1px 0 var(--col-ink, #1a1a2e));`;
    topLine.appendChild(trophy);
    const truncName = (top.name || '').slice(0, 10);
    // Single space between score and name keeps the row compact; the double-space looked
    // disjointed at this font size.
    topLine.appendChild(document.createTextNode(`${top.score} ${truncName}`));
    wrap.appendChild(topLine);
    if (personalBest > 0) {
      const youLine = document.createElement('div');
      youLine.style.cssText = 'font-family:var(--font-display);font-size:9px;letter-spacing:1px;'
        + 'color:var(--col-ink, #1a1a2e);opacity:0.7;text-transform:uppercase;';
      youLine.textContent = `YOU · ${personalBest}`;
      wrap.appendChild(youLine);
    }
    return wrap;
  }
  const badge = document.createElement('div');
  badge.style.marginTop = '0';
  if (personalBest > 0) {
    badge.className = 'ss-level__best';
    badge.textContent = `BEST · ${personalBest}`;
  } else {
    badge.className = 'ss-level__new';
    badge.textContent = '★ NEW ★';
  }
  return badge;
}

// Hover-revealed play icon overlay shared by featured + community cards. A chunky pixel
// triangle in cyan with a 1px ink stroke + drop-shadow (via CSS filter), centered over
// the silhouette polaroid. pointer-events:none so the icon is purely visual — clicks still
// hit the card's play handler underneath. shape-rendering="crispEdges" keeps the polygon
// pixel-perfect at the upscaled display size.
function _buildCardPlayIcon() {
  const wrap = document.createElement('div');
  wrap.className = 'ss-card-play';
  wrap.innerHTML = '<svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" shape-rendering="crispEdges">'
    + '<polygon points="9,5 27,16 9,27" fill="#00E5FF" stroke="#0a0820" stroke-width="2" stroke-linejoin="miter"/>'
    + '</svg>';
  return wrap;
}

function _buildCommunityCard(lvl, silhObserver) {
  // Polaroid-style compact card matching the home-page featured cards (same idiom, same
  // size) but with REMIX + COPY LINK buttons tucked under the score badge so the action
  // affordances stay visible. Was a 640×230 horizontal panel; switched to the polaroid
  // grid format during the home-page unification pass so the community page reads as the
  // "browse all of the same kind of thing" version of the home featured row.
  const progress = _loadLevelProgress(`ugc-${lvl.id}`);

  const card = document.createElement('div');
  card.className = 'ss-level';
  // Border defaults to the card's yellow bg so it's invisible at rest, then swaps to
  // cyan on hover to make the community-content affordance unmistakable. Layout stays
  // constant since the 6px border occupies the same space either way.
  card.style.cssText = `width:240px;height:300px;padding:0;gap:0;flex-direction:column;overflow:hidden;cursor:pointer;`
    + `background-color:var(--col-bg-panel);position:relative;`  // position:relative anchors the absolute-positioned Report link in the bottom-right
    + `border:6px solid var(--col-bg-panel);box-shadow:5px 5px 0 var(--col-ink, #0a0820);`
    + `transition:border-color 80ms steps(2);`;
  card.addEventListener('mousedown', (e) => e.stopPropagation());
  card.addEventListener('mouseenter', () => { card.style.borderColor = 'var(--col-cyan, #00E5FF)'; });
  card.addEventListener('mouseleave', () => { card.style.borderColor = 'var(--col-bg-panel)'; });

  // Title section — sits ABOVE the silhouette. Reserves 2 lines of vertical space so all
  // cards have the same title-area height regardless of title length, keeping silhouettes
  // aligned across the grid.
  const titleSection = document.createElement('div');
  titleSection.style.cssText = 'padding:12px 12px 0;flex:0 0 auto;';
  const name = document.createElement('div');
  // Jersey 25 for UGC card titles — clean retro digital pixel font, designed for legible
  // headers. Used here only on community + featured cards; rest of the app stays on
  // Silkscreen (the body font). AA disabled so the pixel grid renders crisp.
  name.style.cssText = 'font-family:"Jersey 25", var(--font-body, ui-monospace), monospace;font-size:18px;line-height:1.1;letter-spacing:0.5px;font-weight:400;color:var(--col-ink, #1a1a2e);-webkit-text-stroke:0;'
    + '-webkit-font-smoothing:none;-moz-osx-font-smoothing:grayscale;font-smooth:never;'
    + 'display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;text-overflow:ellipsis;'
    + 'min-height:calc(1.1em * 2);';
  name.textContent = (lvl.title && lvl.title.trim()) || lvl.prompt;
  titleSection.appendChild(name);
  card.appendChild(titleSection);

  // Polaroid silhouette panel — silhouette render is DEFERRED via IntersectionObserver
  // (passed by _renderCommunityPage). Cards offscreen don't burn render cycles on first
  // paint; the observer fills bg-image when each card scrolls within rootMargin.
  const silhPanel = document.createElement('div');
  silhPanel.style.cssText = `position:relative;margin:8px 12px 0;flex:0 0 110px;image-rendering:pixelated;`
    + `background-color:#ffffff;border:3px solid var(--col-ink);box-shadow:3px 3px 0 var(--col-ink);`
    + `box-sizing:border-box;background-size:contain;background-repeat:no-repeat;background-position:center bottom;`;
  silhPanel._lvl = lvl;
  if (silhObserver) {
    silhObserver.observe(silhPanel);
  } else {
    _paintLazyUgcSilhouette(silhPanel, lvl.id, lvl.scene, `comm-${lvl.id}`);
  }
  silhPanel.appendChild(_buildCardPlayIcon());
  card.appendChild(silhPanel);

  // Middle section — meta, stars, score badge. Sized to fill remaining vertical space
  // between silhouette and the action footer below. Variable content (1- vs 2-line badge)
  // is absorbed here; overflow:hidden quietly clips if a card's badge runs long.
  const middle = document.createElement('div');
  middle.style.cssText = 'flex:1 1 auto;padding:10px 12px 8px;display:flex;flex-direction:column;gap:4px;min-height:0;overflow:hidden;';

  const meta = document.createElement('div');
  meta.style.cssText = 'font-family:"Jersey 25", var(--font-body, ui-monospace), monospace;font-size:14px;color:var(--col-ink, #1a1a2e);opacity:0.7;letter-spacing:0.5px;';
  const creatorName = (lvl.creatorName && lvl.creatorName.trim()) || 'anonymous';
  const plays = lvl.playCount || 0;
  meta.textContent = plays > 0
    ? `by ${creatorName} · ${_formatTimeAgo(lvl.createdAt)} · ▶ ${plays}`
    : `by ${creatorName} · ${_formatTimeAgo(lvl.createdAt)}`;
  middle.appendChild(meta);

  const starRow = document.createElement('div');
  starRow.className = 'ss-level__stars';
  starRow.style.fontSize = '14px';
  for (let i = 1; i <= 3; i++) {
    const span = document.createElement('span');
    span.className = i <= progress.stars ? 'ss-level__stars-on' : 'ss-level__stars-off';
    span.textContent = i <= progress.stars ? '★' : '☆';
    starRow.appendChild(span);
  }
  middle.appendChild(starRow);

  const badge = _buildScoreBadge(lvl.topScores, progress.best);
  middle.appendChild(badge);
  card.appendChild(middle);

  // Action footer — its OWN flex item at the bottom of the card, not nested in the middle
  // section. flex:0 0 auto + fixed padding means buttons sit at a constant Y across every
  // card regardless of how long the score badge above is. This was the missing fix: when
  // actions lived inside `middle`, content overflow on 2-line badges pushed buttons closer
  // to the cyan border than 1-line badges. Now actions are independent.
  const actions = document.createElement('div');
  actions.style.cssText = 'flex:0 0 auto;display:flex;gap:6px;padding:0 12px 12px;';
  const mkAction = (label, variant, onClick) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `ss-btn ss-btn--${variant}`;
    // Keep the kit's primary (cyan) / secondary (pink) default bg colors. Add white text
    // with thick ink stroke (matches the home page CREATE/BROWSE tile titles). On hover,
    // both bgs swap to cyan — same hover-swap pattern as the home page CTA halves.
    btn.style.cssText = 'flex:1 1 0;font-size:9px;padding:6px 4px;letter-spacing:1px;'
      + 'white-space:nowrap;'
      + 'color:#fff;-webkit-text-stroke:2px var(--col-ink, #1a1a2e);paint-order:stroke;'
      + 'transition:background-color 80ms steps(2);';
    btn.textContent = label;
    btn.onclick = (e) => { e.stopPropagation(); onClick(btn); };
    btn.onmousedown = (e) => e.stopPropagation();
    const defaultBg = variant === 'primary' ? 'var(--col-cyan, #00E5FF)' : 'var(--col-pink, #FF3B6B)';
    const hoverBg = 'var(--col-bg-panel)';
    btn.addEventListener('mouseenter', () => { btn.style.background = hoverBg; });
    btn.addEventListener('mouseleave', () => { btn.style.background = defaultBg; });
    return btn;
  };
  actions.appendChild(mkAction('REMIX', 'primary', () => {
    _pendingCreatePrompt = lvl.prompt;
    _homeView = 'create';
    _renderHome();
  }));
  actions.appendChild(mkAction('COPY LINK', 'secondary', async (btn) => {
    const url = _shareUrlForUgc(lvl.id);
    const original = btn.textContent;
    try {
      await navigator.clipboard.writeText(url);
      btn.textContent = 'COPIED!';
    } catch {
      btn.textContent = 'COPY FAILED';
    }
    setTimeout(() => { btn.textContent = original; }, 1400);
  }));
  card.appendChild(actions);

  // Tiny "Report" link in the bottom-right of the card. Deliberately small + unstyled so
  // it doesn't compete with REMIX/COPY LINK. One-click confirm + post; toast on success.
  // No reason input — keep it frictionless to surface bad levels.
  const reportLink = document.createElement('button');
  reportLink.type = 'button';
  reportLink.textContent = 'Report';
  reportLink.style.cssText = 'position:absolute;bottom:4px;right:8px;background:transparent;'
    + 'border:none;color:var(--col-ink);opacity:0.45;font-family:var(--font-body);'
    + 'font-size:10px;cursor:pointer;padding:2px 4px;letter-spacing:0.5px;';
  reportLink.addEventListener('mouseenter', () => { reportLink.style.opacity = '0.9'; });
  reportLink.addEventListener('mouseleave', () => { reportLink.style.opacity = '0.45'; });
  reportLink.onmousedown = (e) => e.stopPropagation();
  reportLink.onclick = async (e) => {
    e.stopPropagation();
    if (reportLink.dataset.armed !== '1') {
      // First click — arm with confirm prompt. 3s auto-revert.
      reportLink.dataset.armed = '1';
      const orig = reportLink.textContent;
      reportLink.textContent = 'Report? Click again';
      reportLink.style.opacity = '1';
      reportLink.style.color = 'var(--col-pink)';
      setTimeout(() => {
        if (reportLink.dataset.armed === '1') {
          reportLink.dataset.armed = '0';
          reportLink.textContent = orig;
          reportLink.style.color = 'var(--col-ink)';
          reportLink.style.opacity = '0.45';
        }
      }, 3000);
      return;
    }
    // Second click — post.
    reportLink.dataset.armed = '2';  // mark consumed so the timeout doesn't reset display
    reportLink.textContent = 'Reporting...';
    try {
      await fetch(`${UGC_SERVER_URL}/report/${encodeURIComponent(lvl.id)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      reportLink.textContent = 'Reported';
    } catch {
      reportLink.textContent = 'Report failed';
    }
    setTimeout(() => {
      reportLink.dataset.armed = '0';
      reportLink.textContent = 'Report';
      reportLink.style.color = 'var(--col-ink)';
      reportLink.style.opacity = '0.45';
    }, 2000);
  };
  card.appendChild(reportLink);

  card.onclick = (e) => {
    e.stopPropagation();
    _playUgcById(lvl.id);
  };
  return card;
}

// Compact featured-level card for the home page. 240×280 vertical layout: silhouette on
// top (175px tall, full width), then title + stars + best/new badge underneath. Click the
// card to play. Reuses _buildCommunityCard's data shape so it can consume /community
// response objects directly. No REMIX/SHARE buttons — those live on the community page.
function _buildFeaturedCard(lvl) {
  const progress = _loadLevelProgress(`ugc-${lvl.id}`);
  const card = document.createElement('div');
  card.className = 'ss-level';
  // Yellow background to match level cards + world tiles — unifies the page's visual
  // language. Border defaults to the card's yellow bg so it's invisible at rest, then
  // swaps to cyan on hover to surface the community-content affordance. Layout stays
  // constant since the 6px border occupies the same space either way.
  card.style.cssText = `width:220px;height:270px;padding:0;gap:0;flex-direction:column;overflow:hidden;cursor:pointer;`
    + `background-color:var(--col-bg-panel);`
    + `border:6px solid var(--col-bg-panel);box-shadow:5px 5px 0 var(--col-ink, #0a0820);`
    + `transition:border-color 80ms steps(2);`;
  card.addEventListener('mousedown', (e) => e.stopPropagation());
  card.addEventListener('mouseenter', () => { card.style.borderColor = 'var(--col-cyan, #00E5FF)'; });
  card.addEventListener('mouseleave', () => { card.style.borderColor = 'var(--col-bg-panel)'; });

  // Silhouette panel (top) — wrapped in a white polaroid frame with a chunky dark border
  // so the silhouette reads as a framed crafted artifact, matching the world-tile + level-
  // card treatment elsewhere on the page. Background-image is painted in via
  // _paintLazyUgcSilhouette so the home page doesn't block on a per-card fetch.
  const silhPanel = document.createElement('div');
  silhPanel.style.cssText = `position:relative;margin:12px 12px 0;flex:0 0 122px;image-rendering:pixelated;`
    + `background-color:#ffffff;border:3px solid var(--col-ink);box-shadow:3px 3px 0 var(--col-ink);`
    + `box-sizing:border-box;background-size:contain;background-repeat:no-repeat;background-position:center bottom;`;
  _paintLazyUgcSilhouette(silhPanel, lvl.id, lvl.scene, `feat-${lvl.id}`);
  silhPanel.appendChild(_buildCardPlayIcon());
  card.appendChild(silhPanel);

  // Inner (bottom): title + stars + badge.
  const inner = document.createElement('div');
  inner.style.cssText = 'flex:1 1 auto;padding:10px 12px 12px;display:flex;flex-direction:column;gap:4px;min-height:0;overflow:hidden;';

  const name = document.createElement('div');
  name.className = 'ss-level__name';
  // Clamp to 2 lines with ellipsis + reserve 2 lines of vertical space so all cards have
  // the same title-area height regardless of how long the actual title is. Without this
  // reservation, 1-line titles + 3-line titles produced different content heights, which
  // made the badges below sit at different vertical positions across the row.
  // Jersey 25 for UGC card titles — clean retro digital pixel font, designed for legible
  // headers. Used here only on community + featured cards; rest of the app stays on
  // Silkscreen (the body font). AA disabled so the pixel grid renders crisp.
  name.style.cssText = 'font-family:"Jersey 25", var(--font-body, ui-monospace), monospace;font-size:18px;line-height:1.1;letter-spacing:0.5px;font-weight:400;color:var(--col-ink, #1a1a2e);-webkit-text-stroke:0;'
    + '-webkit-font-smoothing:none;-moz-osx-font-smoothing:grayscale;font-smooth:never;'
    + 'display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;text-overflow:ellipsis;'
    + 'min-height:calc(1.1em * 2);';
  name.textContent = (lvl.title && lvl.title.trim()) || lvl.prompt;
  inner.appendChild(name);

  // Creator + time-ago meta line. Mirrors community-card style minus the play count
  // (home featured row is now "latest worlds" — recency is what matters here, popularity
  // can be discovered on the community page).
  const creatorLine = document.createElement('div');
  creatorLine.style.cssText = 'font-family:"Jersey 25", var(--font-body, ui-monospace), monospace;font-size:14px;color:var(--col-ink, #1a1a2e);opacity:0.7;letter-spacing:0.5px;';
  const featuredCreator = (lvl.creatorName && lvl.creatorName.trim()) || 'anonymous';
  creatorLine.textContent = `by ${featuredCreator} · ${_formatTimeAgo(lvl.createdAt)}`;
  inner.appendChild(creatorLine);

  const starRow = document.createElement('div');
  starRow.className = 'ss-level__stars';
  starRow.style.fontSize = '14px';
  for (let i = 1; i <= 3; i++) {
    const span = document.createElement('span');
    span.className = i <= progress.stars ? 'ss-level__stars-on' : 'ss-level__stars-off';
    span.textContent = i <= progress.stars ? '★' : '☆';
    starRow.appendChild(span);
  }
  inner.appendChild(starRow);

  const badge = _buildScoreBadge(lvl.topScores, progress.best);
  badge.style.marginTop = 'auto';
  inner.appendChild(badge);
  card.appendChild(inner);

  card.onclick = (e) => {
    e.stopPropagation();
    _fireAnalytics('featured_card_click', { levelId: lvl.id });
    _playUgcById(lvl.id);
  };
  return card;
}

// Combined CTA tile that sits as the rightmost slot in the featured row. Same dimensions
// as a featured card (280×320) split vertically: top half is the CREATE action (pink),
// bottom half is BROWSE ALL (cyan). Each half is independently clickable. Sits in the
// featured row so the primary UGC actions are at the same scroll position as the curated
// showcase — no longer below-the-fold like the old standalone CTA buttons.
function _buildCreateBrowseTile(browseSubtitle) {
  const card = document.createElement('div');
  // No `ss-level` className: that class adds a `:hover` rule that lifts the WHOLE card on
  // any child hover, which would make both halves bounce together. We want each half to
  // animate independently, so this tile owns its own styling top to bottom.
  // Sized to match the featured cards' OUTER visual size (220 + 6px cyan border × 2 = 232).
  // The CTA tile has no border so we add the equivalent dimensions directly. Without this
  // match the CTA tile rendered 12px shorter + narrower than the featured cards next to it.
  card.style.cssText = 'width:232px;height:282px;padding:0;display:flex;flex-direction:column;overflow:hidden;cursor:default;'
    + 'box-shadow:5px 5px 0 var(--col-ink, #0a0820);'
    + 'background-color:var(--col-bg-panel);';
  card.addEventListener('mousedown', (e) => e.stopPropagation());

  const mkHalf = (label, subtitle, bgColor, onClick) => {
    const half = document.createElement('div');
    half.style.cssText = 'flex:1 1 0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:2px;'
      + 'padding:12px 16px;text-align:center;'
      + `background:${bgColor};color:var(--col-ink, #1a1a2e);cursor:pointer;`
      + 'transition:background-color 60ms steps(2);';
    const title = document.createElement('div');
    title.style.cssText = 'font-family:var(--font-display);font-size:22px;letter-spacing:2px;text-transform:uppercase;'
      + '-webkit-text-stroke:3.5px var(--col-ink, #1a1a2e);paint-order:stroke;color:#fff;';
    title.textContent = label;
    const sub = document.createElement('div');
    sub.style.cssText = 'font-family:"Jersey 25", var(--font-body, ui-monospace), monospace;font-size:22px;line-height:1.05;'
      + 'opacity:0.9;letter-spacing:0.5px;color:var(--col-ink, #1a1a2e);';
    sub.textContent = subtitle;
    half.appendChild(title);
    half.appendChild(sub);
    half.addEventListener('mousedown', (e) => e.stopPropagation());
    // Hover swaps the half's background to the kit's signature panel yellow so the
    // affordance is unmissable. Leave restores the original colour. The faint brightness
    // tint we used before was barely visible against the saturated pink/cyan.
    half.addEventListener('mouseenter', () => { half.style.background = 'var(--col-bg-panel)'; });
    half.addEventListener('mouseleave', () => { half.style.background = bgColor; });
    half.onclick = (e) => { e.stopPropagation(); onClick(); };
    return half;
  };

  const createHalf = mkHalf('Create', 'Make a musical world',
    'var(--col-pink, #FF3B6B)',
    () => { _fireAnalytics('home_cta_click', { levelId: 'create' }); _homeView = 'create'; _renderHome(); });
  const browseHalf = mkHalf('Discover', browseSubtitle || 'All community worlds',
    'var(--col-cyan, #00E5FF)',
    () => { _fireAnalytics('home_cta_click', { levelId: 'community' }); _homeView = 'community'; _renderHome(); });

  // Thin dark divider between the two halves so the visual split reads cleanly.
  const divider = document.createElement('div');
  divider.style.cssText = 'flex:0 0 4px;background:var(--col-ink, #1a1a2e);';

  card.appendChild(createHalf);
  card.appendChild(divider);
  card.appendChild(browseHalf);
  return card;
}

// Async populator for the home page's featured row. Fetches the community list once, takes
// the 4 newest levels (/community returns createdAt desc), and appends compact cards
// into the placeholder container created by _renderWorldSelect. If the list is empty (cold
// deploy, or the fetch failed), the placeholder is removed
// so the page collapses cleanly. Tolerates fetch failure by silently leaving the row hidden.
async function _populateFeaturedRow(captionEl, rowEl) {
  // Now shows the most recent N community levels — this row is "Latest worlds by the
  // community" rather than a curated pick. /community returns levels sorted by
  // createdAt desc, so the first 4 are the newest. Fetch limit is set above the
  // worker's INDEX_MAX cap (200) so the response length reflects the true total.
  let levels = [];
  try {
    const res = await fetch(`${UGC_SERVER_URL}/community?limit=500`);
    if (!res.ok) throw new Error(`server returned ${res.status}`);
    const data = await res.json();
    levels = Array.isArray(data.levels) ? data.levels : [];
  } catch {
    captionEl?.remove();
    rowEl.remove();
    return;
  }
  if (!levels.length) {
    captionEl?.remove();
    rowEl.remove();
    return;
  }
  // Take 4 most recent and render. The 5th tile (CREATE/DISCOVER CTA) always appends.
  // The CTA tile's DISCOVER subtitle gets the exact community count baked in —
  // surfaces total community size at the natural CTA moment rather than as a stat
  // hanging off the caption.
  for (const lvl of levels.slice(0, 4)) {
    rowEl.appendChild(_buildFeaturedCard(lvl));
  }
  const ctaTile = _buildCreateBrowseTile(`${levels.length} community worlds`);
  rowEl.appendChild(ctaTile);
}

function _formatTimeAgo(timestamp) {
  const sec = Math.floor((Date.now() - timestamp) / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

async function _playUgcById(id) {
  // Quick fetch (~100ms) keeps user activation alive, so we can _enterLevel directly without
  // a "ready" page. Show a brief loading state in case the fetch is slow.
  _homeContent.textContent = '';
  _homeContent.appendChild(_loadingSpeakerWrap);
  const loading = document.createElement('div');
  loading.style.cssText = 'color:#cfd6dc;font:14px ui-monospace,monospace;letter-spacing:2px;opacity:0.7;margin-top:18px;';
  loading.textContent = 'loading…';
  _homeContent.appendChild(loading);

  try {
    const res = await fetch(`${UGC_SERVER_URL}/play/${encodeURIComponent(id)}`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `server returned ${res.status}`);
    }
    const data = await res.json();
    const { level, song, prompt } = data;
    _ugcLevelSpec = level;
    _ugcSongSpec = song;
    _ugcId = id;
    _ugcPrompt = prompt;
    _ugcCreatorName = data.creatorName || null;
    LEVEL_CONFIG['ugc-current'].songSpec = song;
    _fireAnalytics('ugc_play', { levelId: id });
    _homeView = 'world-select';
    _currentLevel = null;  // bypass resume shortcut
    _runActive = false;
    _enterLevel('ugc-current');
  } catch (e) {
    console.error('[UGC play]', e);
    _homeContent.textContent = '';
    _renderCommunityPage();
    // After re-rendering, append the error
    const err = document.createElement('div');
    err.style.cssText = 'color:#ff6e6e;font:14px ui-monospace,monospace;text-align:center;margin-top:14px;';
    err.textContent = `couldn't load level: ${e.message}`;
    _homeContent.appendChild(err);
  }
}

function _makeVersionPill(label) {
  const pill = document.createElement('button');
  pill.textContent = label;
  // Kit-aligned: Press Start 2P (font-display), 2px border, generous padding. Default
  // (inactive) styling sits here; active styling is applied via _setPillActive which
  // swaps to a solid cyan fill + ink-stroked white text — visually pops vs. the dim
  // outline of inactive pills.
  pill.style.cssText = 'padding:9px 16px;font-family:var(--font-display, ui-monospace), monospace;font-size:11px;letter-spacing:1px;'
    + 'background:transparent;color:#888;border:2px solid #555;border-radius:14px;cursor:pointer;text-transform:uppercase;'
    + 'transition:background-color 80ms steps(2);';
  pill.addEventListener('mousedown', (e) => e.stopPropagation());
  return pill;
}

// Pill active/inactive styling — keeps the visual contract consistent across the
// community filter pills, community sort pills, and the Create page version+music pills.
// Active = solid cyan bg with white text + chunky ink stroke. Inactive = transparent
// with dim outline.
function _setPillActive(pill, active) {
  if (active) {
    pill.style.background = 'var(--col-cyan, #00E5FF)';
    pill.style.color = '#fff';
    pill.style.webkitTextStroke = '2px var(--col-ink, #1a1a2e)';
    pill.style.paintOrder = 'stroke';
    pill.style.borderColor = 'var(--col-ink, #1a1a2e)';
  } else {
    pill.style.background = 'transparent';
    pill.style.color = '#888';
    pill.style.webkitTextStroke = '0';
    pill.style.paintOrder = 'normal';
    pill.style.borderColor = '#555';
  }
}

function _makeUgcBtn(label, color) {
  const btn = document.createElement('button');
  btn.textContent = label;
  btn.style.cssText = `padding:14px 28px;font:18px "Fugaz One", system-ui, sans-serif;letter-spacing:2px;background:rgba(20,20,28,0.8);color:${color};border:2px solid ${color};border-radius:6px;cursor:pointer;`;
  btn.addEventListener('mousedown', (e) => e.stopPropagation());
  btn.addEventListener('mouseenter', () => { btn.style.background = color; btn.style.color = '#000'; });
  btn.addEventListener('mouseleave', () => { btn.style.background = 'rgba(20,20,28,0.8)'; btn.style.color = color; });
  return btn;
}

// Build the Create page's loading panel — bobbing mascot, prompt echo, rotating status
// + tip lines, and a "now playing" footer that gets populated when the borrowed UGC song
// resolves. Returns control handles so the caller can update the now-playing line and
// stop the rotation timers when generation completes.
function _showCreateLoadingPanel(prompt) {
  _homeContent.textContent = '';
  const panel = document.createElement('div');
  panel.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:18px;padding:36px 20px 24px;width:100%;max-width:640px;margin:0 auto;';

  // Reuse the Level Ready page card layout — yellow card, cyan border, white polaroid
  // silhouette panel inside, meta + prompt below. The polaroid runs a construction
  // animation in place of a silhouette image. When generation completes, the Ready
  // page is the SAME layout but populated with the real silhouette + title.
  const card = document.createElement('div');
  card.style.cssText = 'width:320px;max-width:92vw;padding:0;display:flex;flex-direction:column;'
    + 'background-color:var(--col-bg-panel);border:6px solid var(--col-cyan, #00E5FF);'
    + 'box-shadow:6px 6px 0 var(--col-ink, #0a0820);box-sizing:border-box;';

  const polaroid = document.createElement('div');
  polaroid.style.cssText = 'margin:14px 14px 0;flex:0 0 180px;background:#ffffff;'
    + 'border:3px solid var(--col-ink, #1a1a2e);box-shadow:3px 3px 0 var(--col-ink, #0a0820);'
    + 'box-sizing:border-box;position:relative;overflow:hidden;';
  const blockCanvas = document.createElement('canvas');
  blockCanvas.width = 280; blockCanvas.height = 158;
  blockCanvas.style.cssText = 'width:100%;height:100%;image-rendering:pixelated;display:block;';
  polaroid.appendChild(blockCanvas);
  card.appendChild(polaroid);

  // Inner content (title pending + meta + prompt echo) — mirrors the Ready page
  // structure so the layout doesn't shift when generation completes.
  const inner = document.createElement('div');
  inner.style.cssText = 'padding:14px 16px 18px;display:flex;flex-direction:column;gap:8px;';

  const cardTitle = document.createElement('div');
  cardTitle.style.cssText = 'font-family:"Jersey 25", var(--font-body), monospace;font-size:22px;line-height:1.1;letter-spacing:0.5px;color:var(--col-ink, #1a1a2e);opacity:0.5;'
    + '-webkit-font-smoothing:none;-moz-osx-font-smoothing:grayscale;font-smooth:never;';
  cardTitle.textContent = 'Untitled, for now…';
  inner.appendChild(cardTitle);

  const metaLine = document.createElement('div');
  metaLine.style.cssText = 'font-family:"Jersey 25", var(--font-body), monospace;font-size:14px;color:var(--col-ink, #1a1a2e);opacity:0.7;letter-spacing:0.5px;';
  const loadingCreator = (_loadPlayerName() || 'anonymous').trim() || 'anonymous';
  metaLine.textContent = `by ${loadingCreator}  ·  just a moment…`;
  inner.appendChild(metaLine);

  const promptEcho = document.createElement('div');
  promptEcho.style.cssText = 'font-family:"Jersey 25", var(--font-body), monospace;font-size:14px;color:var(--col-ink, #1a1a2e);opacity:0.6;letter-spacing:0.4px;line-height:1.3;margin-top:4px;';
  promptEcho.textContent = `"${prompt}"`;
  inner.appendChild(promptEcho);

  card.appendChild(inner);
  panel.appendChild(card);

  // Construction animation — reuses the iso silhouette renderer so the loading
  // polaroid matches the visual idiom of the eventual ready-page silhouette. Random
  // chunky blocks (crate/brick/metal) accumulate every ~600ms, recompose into an iso
  // projection that fits + centers in the canvas, and reset after ~14 blocks. The
  // result feels like a level being progressively built block by block.
  const constructionBlocks = [];
  const renderConstruction = () => {
    const ctx = blockCanvas.getContext('2d');
    ctx.imageSmoothingEnabled = false;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, blockCanvas.width, blockCanvas.height);
    if (!constructionBlocks.length) return;
    const silh = _computeSilhouetteFromBlocks(constructionBlocks);
    if (!silh) return;
    const padding = 14;
    const scale = Math.min((blockCanvas.width - padding * 2) / silh.w, (blockCanvas.height - padding * 2) / silh.h);
    const offsetX = (blockCanvas.width - silh.w * scale) / 2;
    const offsetY = (blockCanvas.height - silh.h * scale) / 2;
    ctx.save();
    ctx.translate(offsetX, offsetY);
    ctx.scale(scale, scale);
    if (silh.floor && silh.floor.length === 4) {
      ctx.fillStyle = '#e0e2ea';
      ctx.beginPath();
      ctx.moveTo(silh.floor[0][0], silh.floor[0][1]);
      for (let i = 1; i < 4; i++) ctx.lineTo(silh.floor[i][0], silh.floor[i][1]);
      ctx.closePath(); ctx.fill();
    }
    ctx.lineWidth = 1;
    for (const face of silh.faces) {
      const matKey = face.m === 'c' ? 'crate' : face.m === 'm' ? 'metal' : 'brick';
      const colors = _SILH_FACE_COLORS[matKey];
      const p = face.p;
      ctx.fillStyle = colors[face.f];
      ctx.strokeStyle = colors[2];
      ctx.beginPath();
      ctx.moveTo(p[0], p[1]); ctx.lineTo(p[2], p[3]);
      ctx.lineTo(p[4], p[5]); ctx.lineTo(p[6], p[7]);
      ctx.closePath();
      ctx.fill(); ctx.stroke();
    }
    ctx.restore();
  };
  // 4×5 pixel font for the loading phrases. Each phrase spells out letter-by-letter
  // via beat-driven block additions. Phrase is picked at random on panel open and on
  // each cycle reset, so consecutive generations show different words.
  const LETTER_BITMAPS = {
    A: ['.XX.', 'X..X', 'XXXX', 'X..X', 'X..X'],
    C: ['.XXX', 'X...', 'X...', 'X...', '.XXX'],
    D: ['XXX.', 'X..X', 'X..X', 'X..X', 'XXX.'],
    E: ['XXXX', 'X...', 'XXX.', 'X...', 'XXXX'],
    G: ['.XXX', 'X...', 'X.XX', 'X..X', '.XXX'],
    H: ['X..X', 'X..X', 'XXXX', 'X..X', 'X..X'],
    I: ['XXXX', '.XX.', '.XX.', '.XX.', 'XXXX'],
    L: ['X...', 'X...', 'X...', 'X...', 'XXXX'],
    M: ['X..X', 'XXXX', 'XXXX', 'X..X', 'X..X'],
    N: ['X..X', 'XX.X', 'X.XX', 'X..X', 'X..X'],
    O: ['.XX.', 'X..X', 'X..X', 'X..X', '.XX.'],
    R: ['XXX.', 'X..X', 'XXX.', 'X.X.', 'X..X'],
    S: ['.XXX', 'X...', '.XX.', '...X', 'XXX.'],
    T: ['XXXX', '.XX.', '.XX.', '.XX.', '.XX.'],
    W: ['X..X', 'X..X', 'X..X', 'XXXX', 'X..X'],
    Y: ['X..X', 'X..X', '.XX.', '.X..', '.X..'],
  };
  const LOADING_PHRASES = [
    'GENERATING',
    'NOT LONG NOW',
    'HANG TIGHT',
    'NEARLY THERE',
    'ALMOST READY',
  ];
  const buildSequenceFor = (phrase) => {
    const seq = [];
    const blockSize = 2.4;
    const charSpacing = blockSize;
    const charWidth = 4 * blockSize;
    const totalW = phrase.length * charWidth + (phrase.length - 1) * charSpacing;
    const xOffset = -totalW / 2;
    const materials = ['crate', 'brick', 'metal'];
    for (let i = 0; i < phrase.length; i++) {
      const letter = phrase[i];
      // Spaces (and any unknown char) just advance position without adding pixels.
      if (letter === ' ' || !LETTER_BITMAPS[letter]) continue;
      const bmp = LETTER_BITMAPS[letter];
      const charX = xOffset + i * (charWidth + charSpacing);
      const mat = materials[i % materials.length];
      for (let row = 0; row < bmp.length; row++) {
        for (let col = 0; col < bmp[row].length; col++) {
          if (bmp[row][col] === 'X') {
            seq.push({
              x: charX + col * blockSize + blockSize / 2,
              y: (bmp.length - 1 - row) * blockSize + blockSize / 2,
              z: 0,
              w: blockSize,
              h: blockSize,
              d: blockSize,
              material: mat,
            });
          }
        }
      }
    }
    return seq;
  };
  const pickRandomPhrase = () => LOADING_PHRASES[Math.floor(Math.random() * LOADING_PHRASES.length)];
  let constructionSequence = buildSequenceFor(pickRandomPhrase());

  let constructionStep = 0;
  const addConstructionBlock = () => {
    if (!polaroid.isConnected) return;
    if (constructionStep >= constructionSequence.length) {
      // Phrase fully spelled — pick a new random phrase and start the cycle over so
      // long generations don't get stuck on the same word.
      constructionBlocks.length = 0;
      constructionStep = 0;
      constructionSequence = buildSequenceFor(pickRandomPhrase());
    }
    constructionBlocks.push(constructionSequence[constructionStep]);
    constructionStep++;
    renderConstruction();
  };
  addConstructionBlock();  // first pixel so the polaroid isn't blank

  // Beat-aligned block additions — when music is playing, add one block per beat at
  // the active song's BPM. Fast songs build levels fast; slow songs build slow. When
  // music isn't started yet (brief pre-music window or fetch failure), a 700ms
  // fallback timer keeps adding blocks so the polaroid never sits empty.
  let lastConstructionBeatIdx = -1;
  let constructionRaf = null;
  const constructionBeatTick = () => {
    if (!polaroid.isConnected) { constructionRaf = null; return; }
    if (_musicStarted && audioCtx && _activeSong) {
      const elapsed = audioCtx.currentTime - _musicLoopStartTime;
      if (elapsed >= 0) {
        // Divide by half-beat so the construction adds twice per beat (2× the original
        // rate). Faster reveal feels more dynamic + lets longer phrases spell out within
        // a single generation.
        const beatIdx = Math.floor(elapsed / (MUSIC_BEAT / 2));
        // Detect backwards-jump (music engine called restartMusic, resetting
        // _musicLoopStartTime to "now"). Without this guard, lastConstructionBeatIdx
        // would still hold a high value from the previous music session and the
        // animation would silently stop until new music re-accumulated to that beat —
        // which never happens within a single loading panel.
        if (beatIdx < lastConstructionBeatIdx) lastConstructionBeatIdx = -1;
        if (beatIdx > lastConstructionBeatIdx) {
          lastConstructionBeatIdx = beatIdx;
          addConstructionBlock();
        }
      }
    }
    constructionRaf = requestAnimationFrame(constructionBeatTick);
  };
  constructionRaf = requestAnimationFrame(constructionBeatTick);
  const constructionFallback = setInterval(() => {
    if (!_musicStarted) addConstructionBlock();
  }, 350);

  // Status (rotating, cyan kit-style)
  const status = document.createElement('div');
  status.style.cssText = 'font:13px var(--font-display);color:var(--col-cyan, #00E5FF);text-transform:uppercase;letter-spacing:2px;text-align:center;min-height:18px;'
    + '-webkit-text-stroke:2px var(--col-ink, #1a1a2e);paint-order:stroke;transition:opacity 200ms;';
  status.textContent = _LOADING_STATUS_MESSAGES[0];
  panel.appendChild(status);

  // Compact music footer — "Listen while you wait" tag + scaled mini boombox +
  // attribution + mute + shuffle. Subtle top divider so it reads as a footer not a hero.
  const footer = document.createElement('div');
  footer.style.cssText = 'margin-top:24px;padding:14px 0 0;display:flex;flex-direction:column;align-items:center;gap:10px;'
    + 'border-top:1px solid #2a2a4a;width:100%;max-width:480px;';
  const npLabel = document.createElement('div');
  npLabel.style.cssText = 'font:10px var(--font-display);color:#6c6c8a;letter-spacing:2px;text-transform:uppercase;';
  npLabel.textContent = '♫ Listen while you wait';
  footer.appendChild(npLabel);

  const miniRow = document.createElement('div');
  miniRow.style.cssText = 'display:flex;align-items:center;gap:14px;flex-wrap:wrap;justify-content:center;';
  _loadingSpeakerWrap.style.transform = 'scale(0.5)';
  _loadingSpeakerWrap.style.transformOrigin = 'center';
  _loadingSpeakerWrap.style.margin = '-32px -64px';
  miniRow.appendChild(_loadingSpeakerWrap);

  const npAndControls = document.createElement('div');
  npAndControls.style.cssText = 'display:flex;flex-direction:column;align-items:flex-start;gap:6px;';
  const npText = document.createElement('div');
  npText.className = 'np-text';
  npText.style.cssText = 'font:14px "Jersey 25", var(--font-body), monospace;color:#cfd6dc;letter-spacing:0.5px;text-align:left;max-width:240px;opacity:0;transition:opacity 600ms;';
  npAndControls.appendChild(npText);

  const ctrlRow = document.createElement('div');
  ctrlRow.style.cssText = 'display:flex;align-items:center;gap:8px;';
  const muteBtn = document.createElement('button');
  muteBtn.type = 'button';
  muteBtn.style.cssText = 'background:transparent;border:1px solid #555;color:#888;padding:3px 10px;font:10px var(--font-body);cursor:pointer;letter-spacing:0.5px;display:none;';
  muteBtn.textContent = 'mute';
  muteBtn.addEventListener('mousedown', (e) => e.stopPropagation());
  muteBtn.onclick = (e) => {
    e.stopPropagation();
    const next = !_loadingMusicMuted;
    _setLoadingMusicMuted(next);
    muteBtn.textContent = next ? 'unmute' : 'mute';
  };
  ctrlRow.appendChild(muteBtn);

  const shuffleBtn = document.createElement('button');
  shuffleBtn.type = 'button';
  shuffleBtn.style.cssText = 'background:transparent;border:1px solid #555;color:#888;padding:3px 10px;font:10px var(--font-body);cursor:pointer;letter-spacing:0.5px;display:none;';
  shuffleBtn.textContent = '↻ shuffle';
  shuffleBtn.addEventListener('mousedown', (e) => e.stopPropagation());
  shuffleBtn.onclick = (e) => {
    e.stopPropagation();
    // Re-trigger the music fetch with a brief pause so the song change feels deliberate
    // rather than a hard cut. Sequence:
    //   1) hide UI immediately + fade audio down
    //   2) wait ~500ms (silent moment)
    //   3) fetch new random song, applySong + restartMusic, fade audio back up
    npText.style.opacity = '0';
    muteBtn.style.display = 'none';
    shuffleBtn.style.display = 'none';
    if (audioCtx && _musicGain) {
      try { _musicGain.gain.linearRampToValueAtTime(0.0001, audioCtx.currentTime + 0.18); } catch {}
    }
    setTimeout(() => {
      _startLoadingMusic({ nowPlayingText: npText, muteButton: muteBtn, shuffleButton: shuffleBtn, mascot: null });
    }, 500);
  };
  ctrlRow.appendChild(shuffleBtn);
  npAndControls.appendChild(ctrlRow);

  miniRow.appendChild(npAndControls);
  footer.appendChild(miniRow);
  panel.appendChild(footer);

  _homeContent.appendChild(panel);

  // Rotation — status every 4s. Starts after a beat so the initial state
  // has a moment to read.
  let statusIdx = 0;
  const rotate = (el, list, getIdx, setIdx) => {
    el.style.opacity = '0';
    setTimeout(() => {
      const next = (getIdx() + 1) % list.length;
      setIdx(next);
      el.textContent = list[next];
      el.style.opacity = '1';
    }, 200);
  };
  const statusInterval = setInterval(() => rotate(status, _LOADING_STATUS_MESSAGES, () => statusIdx, (i) => statusIdx = i), 4000);

  return {
    nowPlayingText: npText,
    muteButton: muteBtn,
    shuffleButton: shuffleBtn,
    mascot: null,  // no longer in the panel; _startLoadingMusic's BPM sync just no-ops
    cleanup: () => {
      clearInterval(statusInterval);
      clearInterval(constructionFallback);
      if (constructionRaf) cancelAnimationFrame(constructionRaf);
      _loadingSpeakerWrap.style.transform = '';
      _loadingSpeakerWrap.style.margin = '';
    },
  };
}

// Borrow a random UGC song to play during the loading state. Fetches /community for a
// pool of recent levels, picks one at random, then fetches /play/:id?countPlay=false to
// get the song spec without inflating the borrowed level's playCount. Silent no-op if
// /community is empty or any fetch fails — loading state still works without music.
async function _startLoadingMusic(panelHandles) {
  try {
    const commRes = await fetch(`${UGC_SERVER_URL}/community?limit=40`);
    if (!commRes.ok) return;
    const commData = await commRes.json();
    const levels = Array.isArray(commData.levels) ? commData.levels : [];
    if (!levels.length) return;
    const pick = levels[Math.floor(Math.random() * levels.length)];

    const playRes = await fetch(`${UGC_SERVER_URL}/play/${encodeURIComponent(pick.id)}?countPlay=false`);
    if (!playRes.ok) return;
    const playData = await playRes.json();
    if (!playData.song) return;

    applySong(playData.song);
    if (!_musicStarted) {
      startMusic();
    } else {
      // Already playing — force a restart so the new song's chord progression takes
      // effect immediately instead of after the current loop finishes.
      restartMusic();
    }
    _setLoadingMusicMuted(false);  // ensure not muted from a prior loading session

    if (panelHandles) {
      // Sync the mascot's bob animation duration to the song's BPM so he dances in time.
      // 2-step animation spans 2 beats: duration = 2 × (60 / bpm) seconds. Fallback to
      // 120 BPM if the song spec doesn't carry one.
      if (panelHandles.mascot) {
        const bpm = (playData.song.bpm) || 120;
        const beatSeconds = 60 / bpm;
        panelHandles.mascot.style.animationDuration = `${beatSeconds * 2}s`;
      }
      if (panelHandles.nowPlayingText) {
        const creator = (playData.creatorName || 'anonymous');
        const title = (playData.title || playData.prompt || 'untitled').slice(0, 40);
        panelHandles.nowPlayingText.textContent = `"${title}" by ${creator}`;
        panelHandles.nowPlayingText.style.opacity = '0.85';
      }
      if (panelHandles.muteButton) panelHandles.muteButton.style.display = 'inline-block';
      if (panelHandles.shuffleButton) panelHandles.shuffleButton.style.display = 'inline-block';
    }
  } catch {
    // Silent — loading panel works fine without music.
  }
}

async function _generateUgcLevel(prompt, statusEl) {
  // CLIENT-SIDE MODERATION — block obvious slurs/profanity before any network call so
  // the user gets immediate inline feedback. Server still validates as source of truth.
  if (!_isPromptAllowedClient(prompt)) {
    if (statusEl) statusEl.textContent = "let's keep it friendly — try a different prompt";
    return;
  }

  // Resolve creator nickname BEFORE showing the loading state — prompts the modal only
  // if no name is set yet. Returning creators reuse their saved name silently. With the
  // modal's SKIP button removed (and SAVE gated on a non-empty input), first-time
  // creators can no longer publish anonymously even via the prompt.
  let creatorName = _loadPlayerName();
  if (!creatorName) {
    creatorName = await _promptPlayerName();
  }

  // Show the new loading panel + start the borrowed UGC music in the background.
  const panelHandles = _showCreateLoadingPanel(prompt);
  _startLoadingMusic(panelHandles);

  // Resolve a Turnstile token before hitting /generate. The widget normally has one ready
  // by now (it warmed up when the Create page rendered).
  const turnstileToken = await _getTurnstileToken();
  if (!turnstileToken) {
    panelHandles.cleanup();
    _renderCreatePage();
    const input = _homeContent.querySelector('input');
    if (input) input.value = prompt;
    const status = _homeContent.querySelector('div:last-child');
    if (status) status.textContent = `verification failed — refresh and try again`;
    return;
  }

  try {
    const res = await fetch(`${UGC_SERVER_URL}/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prompt, version: _ugcPromptVersion, musicVersion: _ugcMusicVersion, turnstileToken, creatorName: creatorName || undefined }),
    });
    _turnstileToken = null;
    if (_turnstileWidgetId !== null && window.turnstile) {
      try { window.turnstile.reset(_turnstileWidgetId); } catch {}
    }
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      // Friendly, human copy per known error code, each with a clear next step. Unknown codes fall
      // through to the generic catch below. Every branch shows the same clean error card (no more
      // re-rendering the form and hunting for a status line), and keeps the prompt for a retry/edit.
      const byCode = {
        prompt_blocked:   { headline: "Let's keep it friendly", message: "That prompt has words we can't use. Try describing your world a different way.", canRetry: false },
        title_blocked:    { headline: 'That got a bit weird', message: "The AI made something we couldn't keep. Give it another go, or tweak your prompt." },
        name_blocked:     { headline: 'Nickname not allowed', message: "Your nickname isn't allowed. Change it from the home screen, then come back.", canRetry: false },
        turnstile_failed: { headline: 'Quick check failed', message: "We couldn't confirm you're human. Refresh the page and try again.", refresh: true },
        rate_limited:     { headline: 'Whoa, slow down!', message: "You've made a lot of levels this hour. Take a short break and try again later.", canRetry: false },
        daily_cap:        { headline: 'Taking a breather', message: "We've hit today's limit for new levels. Come back tomorrow to make more!", canRetry: false },
      };
      const cfg = byCode[err.code];
      if (cfg) { panelHandles.cleanup(); _showCreateErrorPage(prompt, cfg); return; }
      throw new Error(err.error || `server returned ${res.status}`);
    }
    const { id, level, song, silhouette, scene, title } = await res.json();
    _fireAnalytics('ugc_generate', { levelId: id });
    panelHandles.cleanup();
    _ugcLevelSpec = level;
    _ugcSongSpec = song;
    _ugcId = id;
    _ugcPrompt = prompt;
    _ugcCreatorName = creatorName || null;
    _addMyUgcId(id);
    LEVEL_CONFIG['ugc-current'].songSpec = song;
    _renderUgcReadyPage(prompt, { silhouette, scene, title, createdAt: Date.now() });
  } catch (e) {
    console.error('[UGC generate]', e);
    panelHandles.cleanup();
    // Map network failures + raw 5xx / bad-JSON errors to a calm, human message with a next step.
    // The technical detail stays in the console; the user sees a clean card, not a broken form.
    const msg = e?.message || '';
    let headline = 'Something went wrong';
    let message = 'The AI is having trouble right now. Give it a moment and try again.';
    if (/network|failed to fetch|networkerror|TypeError/i.test(msg)) {
      headline = "Can't reach the server";
      message = 'Check your internet connection, then give it another go.';
    } else if (/gemini|generated spec failed validation|invalid JSON|non-JSON|validation/i.test(msg)) {
      headline = 'The AI hiccuped';
      message = "That didn't come out right. It usually works on the next try, so give it another go.";
    }
    _showCreateErrorPage(prompt, { headline, message });
  }
}

// Clean, friendly error surface for a failed generation. Replaces the old approach (re-render the whole
// Create form, then drop a terse line into a fragile `div:last-child` selector that often missed and left
// the page looking broken). Shows a calm kit card: what happened + a clear next step. `refresh` swaps the
// primary action to a page reload (for the human-check failure); `canRetry:false` hides Try Again for
// errors a retry can't fix (blocked prompt, rate/daily cap). Edit Prompt always returns to the form with
// the prompt preserved.
function _showCreateErrorPage(prompt, { headline, message, canRetry = true, refresh = false }) {
  _homeContent.textContent = '';
  const view = document.createElement('div');
  view.className = 'ss-world-view';
  view.style.gap = '18px';

  const card = document.createElement('div');
  card.style.cssText = 'width:440px;max-width:92vw;margin-top:52px;padding:28px 30px 32px;box-sizing:border-box;'
    + 'display:flex;flex-direction:column;align-items:center;gap:14px;text-align:center;'
    + 'background-color:var(--col-bg-panel);border:4px solid var(--col-ink, #0a0820);'
    + 'box-shadow:6px 6px 0 var(--col-ink, #0a0820);';

  const h = document.createElement('div');
  h.style.cssText = 'font-family:var(--font-display);font-size:24px;letter-spacing:1px;color:var(--col-pink, #FF3B6B);'
    + '-webkit-text-stroke:2px var(--col-ink, #0a0820);paint-order:stroke;text-transform:uppercase;line-height:1.15;';
  h.textContent = headline;

  const m = document.createElement('div');
  m.style.cssText = 'font-family:"Jersey 25", var(--font-body, ui-monospace), monospace;font-size:22px;line-height:1.2;'
    + 'letter-spacing:0.5px;color:var(--col-ink, #0a0820);max-width:360px;';
  m.textContent = message;

  const actions = document.createElement('div');
  actions.style.cssText = 'display:flex;gap:12px;flex-wrap:wrap;justify-content:center;margin-top:6px;';
  const mkBtn = (label, bg, run) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'ss-btn';
    b.style.cssText = 'padding:13px 24px;font-family:var(--font-display);font-size:16px;letter-spacing:1px;text-transform:uppercase;'
      + 'color:#fff;-webkit-text-stroke:2px var(--col-ink, #0a0820);paint-order:stroke;background:' + bg + ';white-space:nowrap;';
    b.textContent = label;
    b.addEventListener('mousedown', (e) => e.stopPropagation());
    b.onclick = (e) => { e.stopPropagation(); run(); };
    return b;
  };
  const editBtn = mkBtn('Edit Prompt', 'var(--col-cyan, #00E5FF)', () => { _pendingCreatePrompt = prompt; _renderCreatePage(); });
  if (refresh) {
    actions.append(mkBtn('Refresh Page', 'var(--col-green, #43d675)', () => location.reload()), editBtn);
  } else if (canRetry) {
    actions.append(mkBtn('Try Again', 'var(--col-green, #43d675)', () => _generateUgcLevel(prompt)), editBtn);
  } else {
    actions.append(editBtn);
  }

  card.append(h, m, actions);
  view.appendChild(card);
  _homeContent.appendChild(view);
}

function _renderUgcReadyPage(prompt, meta) {
  _homeContent.textContent = '';

  const view = document.createElement('div');
  view.className = 'ss-world-view';
  view.style.gap = '20px';

  const title = document.createElement('div');
  title.className = 'ss-world-view__title';
  title.textContent = 'Your level is ready!';
  view.appendChild(title);

  // Polaroid-style card mirroring the community/featured card idiom but bigger and
  // hero-positioned — this is THE level you just made, so it gets prominent treatment.
  // Yellow card bg, cyan border (community/UGC color), white polaroid silhouette panel
  // inside, title + creator + prompt echo below.
  const card = document.createElement('div');
  card.style.cssText = 'width:320px;max-width:92vw;padding:0;display:flex;flex-direction:column;'
    + 'background-color:var(--col-bg-panel);border:6px solid var(--col-cyan, #00E5FF);'
    + 'box-shadow:6px 6px 0 var(--col-ink, #0a0820);box-sizing:border-box;';

  // Polaroid silhouette panel — uses the just-generated level's silhouette returned by
  // /generate. Cached by id so re-renders don't re-rasterise.
  const silhPanel = document.createElement('div');
  silhPanel.style.cssText = 'margin:14px 14px 0;flex:0 0 180px;'
    + 'background-color:#ffffff;border:3px solid var(--col-ink);box-shadow:3px 3px 0 var(--col-ink);'
    + 'background-size:contain;background-repeat:no-repeat;background-position:center bottom;'
    + 'box-sizing:border-box;image-rendering:pixelated;';
  if (meta && meta.silhouette) {
    const url = _renderSilhouetteToDataUrl(meta.silhouette, meta.scene, `ready-${_ugcId}`);
    if (url) silhPanel.style.backgroundImage = `url(${url})`;
  }
  card.appendChild(silhPanel);

  // Inner content — title (LLM-generated), meta (by Creator · just now), prompt echo.
  const inner = document.createElement('div');
  inner.style.cssText = 'padding:14px 16px 18px;display:flex;flex-direction:column;gap:8px;';

  const cardTitle = document.createElement('div');
  cardTitle.style.cssText = 'font-family:"Jersey 25", var(--font-body, ui-monospace), monospace;font-size:22px;line-height:1.1;letter-spacing:0.5px;font-weight:400;color:var(--col-ink, #1a1a2e);'
    + '-webkit-font-smoothing:none;-moz-osx-font-smoothing:grayscale;font-smooth:never;';
  cardTitle.textContent = (meta && meta.title) || (_ugcLevelSpec && _ugcLevelSpec.title) || 'Untitled';
  inner.appendChild(cardTitle);

  const metaLine = document.createElement('div');
  metaLine.style.cssText = 'font-family:"Jersey 25", var(--font-body, ui-monospace), monospace;font-size:14px;color:var(--col-ink, #1a1a2e);opacity:0.7;letter-spacing:0.5px;';
  const readyCreator = (_ugcCreatorName && _ugcCreatorName.trim()) || 'anonymous';
  metaLine.textContent = `by ${readyCreator}  ·  just now`;
  inner.appendChild(metaLine);

  // Prompt echo in a slightly more decorative italic-style display so it's clearly the
  // user's words ("you typed this") rather than the LLM's title or the meta line.
  const promptEcho = document.createElement('div');
  promptEcho.style.cssText = 'font-family:"Jersey 25", var(--font-body, ui-monospace), monospace;font-size:14px;color:var(--col-ink, #1a1a2e);opacity:0.6;letter-spacing:0.4px;line-height:1.3;margin-top:4px;';
  promptEcho.textContent = `"${prompt}"`;
  inner.appendChild(promptEcho);

  card.appendChild(inner);
  view.appendChild(card);

  // Action row below the card — PLAY is the primary chunky magenta CTA matching the
  // home Create button. NEW PROMPT is a quieter secondary text-style link.
  const playBtn = document.createElement('button');
  playBtn.type = 'button';
  playBtn.style.cssText = 'padding:18px 56px;font-family:var(--font-display);font-size:22px;letter-spacing:2px;text-transform:uppercase;'
    + 'color:#fff;-webkit-text-stroke:2px var(--col-ink, #1a1a2e);paint-order:stroke;'
    + 'background:var(--col-pink, #ff3b6b);border:4px solid var(--col-ink, #1a1a2e);'
    + 'box-shadow:5px 5px 0 var(--col-ink, #0a0820);cursor:pointer;'
    + 'transition:background-color 80ms steps(2);';
  playBtn.textContent = 'PLAY ▸';
  playBtn.addEventListener('mousedown', (e) => e.stopPropagation());
  playBtn.addEventListener('mouseenter', () => { playBtn.style.background = 'var(--col-bg-panel)'; });
  playBtn.addEventListener('mouseleave', () => { playBtn.style.background = 'var(--col-pink, #ff3b6b)'; });
  view.appendChild(playBtn);

  const newLink = document.createElement('button');
  newLink.type = 'button';
  newLink.style.cssText = 'background:transparent;border:none;color:#9c9cb8;cursor:pointer;'
    + 'font-family:"Jersey 25", var(--font-body), monospace;font-size:15px;letter-spacing:0.5px;'
    + 'padding:6px 12px;text-decoration:underline;';
  newLink.textContent = '← new prompt';
  newLink.addEventListener('mousedown', (e) => e.stopPropagation());
  newLink.addEventListener('mouseenter', () => { newLink.style.color = 'var(--col-cyan, #00E5FF)'; });
  newLink.addEventListener('mouseleave', () => { newLink.style.color = '#9c9cb8'; });
  view.appendChild(newLink);

  _homeContent.appendChild(view);

  playBtn.onclick = (e) => {
    e.stopPropagation();
    _homeView = 'world-select';
    _currentLevel = null;
    _runActive = false;
    _enterLevel('ugc-current');
  };
  newLink.onclick = (e) => {
    e.stopPropagation();
    _homeView = 'create';
    _renderHome();
  };
}

// Pixel-art eighth note ♪ flanking the home-screen brand. Painted procedurally (16x16 base,
// scaled up via CSS image-rendering: pixelated). Body color is parameterized so we can
// paint two notes — cyan for the left (matching SOUNDS) and gold for the right (matching
// SMASHING) — tying the decoration directly to the title's color story. Navy outline like
// the title text; on dark bg the outline disappears against the bg and the bright body
// reads as a clean shape, same trick as the wordmark.
function _paintBrandNote(bodyColor) {
  const c = document.createElement('canvas');
  c.width = 16; c.height = 16;
  const ctx = c.getContext('2d');
  // No outline — solid colored pixel shape. On the dark home backdrop, the bright body
  // (cyan or gold) is itself the high-contrast edge. Outlines in navy disappeared into
  // the bg; outlines in white "felt out of place" against the kit. Solid is cleanest.
  const palette = { '.': null, B: bodyColor };
  const grid = [
    '................',
    '.........BBBB...',
    '........BBBBBB..',
    '........BBBBBB..',
    '........BBBBB...',
    '........BBB.....',
    '.........BB.....',
    '.........BB.....',
    '.........BB.....',
    '.........BB.....',
    '....BBBB.BB.....',
    '..BBBBBBBBB.....',
    '.BBBBBBBBBB.....',
    '.BBBBBBBBB......',
    '.BBBBBBBB.......',
    '..BBBBBB........',
  ];
  for (let y = 0; y < 16; y++) {
    for (let x = 0; x < 16; x++) {
      const col = palette[grid[y][x]];
      if (col) { ctx.fillStyle = col; ctx.fillRect(x, y, 1, 1); }
    }
  }
  return c.toDataURL();
}
const _brandNoteCyanUrl = _paintBrandNote('#00E5FF');
const _brandNoteGoldUrl = _paintBrandNote('#FFE600');

// 16×16 pixel-art gold trophy used in the leaderboard headline row, replacing the 🏆
// emoji. Hand-pixelled to fit the kit aesthetic (sharp 1px edges, ink outline, electric
// gold body with a single highlight + shadow column for read-at-glance volume). Shows
// classic two-handle cup → narrow waist → stem → wide base.
// Tiny 8-bit pixel-art icons for the home page footer (Discord, GitHub). Painted
// procedurally to 12×12 canvases — same approach as the trophy. Used as inline-block
// background-image elements next to the link text.
function _paintDiscordIcon() {
  const c = document.createElement('canvas');
  c.width = 16; c.height = 16;
  const ctx = c.getContext('2d');
  const palette = { '.': null, B: '#5865f2', W: '#ffffff' };
  const grid = [
    '................',
    '...BBBBBBBBBB...',
    '..BBBBBBBBBBBB..',
    '.BBBBBBBBBBBBBB.',
    '.BBWWBBBBBBWWBB.',
    '.BBWWBBBBBBWWBB.',
    '.BBBBBBBBBBBBBB.',
    '.BBBBBBBBBBBBBB.',
    '.BBBBBBBBBBBBBB.',
    '..BBBBBBBBBBBB..',
    '...BBBBBBBBBB...',
    '...BBB..........',
    '...BB...........',
    '..BB............',
    '..B.............',
    '................',
  ];
  for (let y = 0; y < 16; y++) {
    for (let x = 0; x < 16; x++) {
      const col = palette[grid[y][x]];
      if (col) { ctx.fillStyle = col; ctx.fillRect(x, y, 1, 1); }
    }
  }
  return c.toDataURL();
}
function _paintGithubIcon() {
  const c = document.createElement('canvas');
  c.width = 16; c.height = 16;
  const ctx = c.getContext('2d');
  const palette = { '.': null, O: '#cfd6dc', W: '#ffffff' };
  // Octocat-y shape: circle silhouette with ear notches at the top + an iconic
  // tail extending out of the bottom-left. White interior so the silhouette reads.
  const grid = [
    '................',
    '....OOOOOOOO....',
    '...OOOOOOOOOO...',
    '..OO.OOOO.OO.O..',
    '..OOOOOOOOOOOO..',
    '.OOWWWWWWWWWWOO.',
    '.OOWWOWWWWOWWOO.',
    '.OOWWWWWWWWWWOO.',
    '.OOWWWWWWWWWWOO.',
    '.OOWWWWWWWWWWOO.',
    '..OOWWWWWWWWOO..',
    '...OOOOOOOOOO...',
    '...OO.OOOO.OO...',
    '..OO..OOOO..OO..',
    '.OOO............',
    '..OO............',
  ];
  for (let y = 0; y < 16; y++) {
    for (let x = 0; x < 16; x++) {
      const col = palette[grid[y][x]];
      if (col) { ctx.fillStyle = col; ctx.fillRect(x, y, 1, 1); }
    }
  }
  return c.toDataURL();
}
const _DISCORD_ICON_URL = _paintDiscordIcon();
// GitHub icon now uses a hand-crafted PNG dropped into web/public/. The procedural
// _paintGithubIcon function is kept above for reference but unused — feel free to
// delete if you decide the asset stays.
const _GITHUB_ICON_URL = '/github.png';

function _paintTrophy() {
  const c = document.createElement('canvas');
  c.width = 16; c.height = 16;
  const ctx = c.getContext('2d');
  const palette = {
    '.': null,
    O: '#1a1a2e',  // ink outline (col-ink)
    G: '#FFE600',  // electric gold (col-gold)
    H: '#fffce0',  // highlight pale gold
    S: '#a06b00',  // dark gold shadow
  };
  const grid = [
    '................',
    '..OOOOOOOOOOOO..',  // 1: cup rim outline
    '.OGGGGGGGGGGGGO.',  // 2: rim interior gold
    'OOGGGGGGGGGGGGOO',  // 3: handles START outboard, cup full width
    'O.OGGGGGGGGGGO.O',  // 4: handle HOLE on each side (the . pixel) — reads as a loop
    'O.OGGGGGGGGGGO.O',  // 5
    'O.OGGGGGGGGGGO.O',  // 6
    'OOGGGGGGGGGGGGOO',  // 7: handles END
    '.OGGGGGGGGGGGGO.',  // 8: cup body alone
    '..OOOOOOOOOOOO..',  // 9: cup bottom seam
    '....OOGGGGOO....',  // 10: stem shoulder, narrow with gold core
    '.....OGGGGO.....',  // 11
    '......OGGO......',  // 12: narrowest stem
    '....OOOOOOOO....',  // 13: base shoulder
    '.OOGGGGGGGGGGOO.',  // 14: base body gold
    '.OOOOOOOOOOOOOO.',  // 15: base bottom
  ];
  for (let y = 0; y < 16; y++) {
    for (let x = 0; x < 16; x++) {
      const col = palette[grid[y][x]];
      if (col) { ctx.fillStyle = col; ctx.fillRect(x, y, 1, 1); }
    }
  }
  return c.toDataURL();
}
const _trophyUrl = _paintTrophy();

function _renderWorldSelect() {
  // Brand title — big SOUNDS / SMASHING! stacked, with the kit's stroke + paint-order treatment.
  const brand = document.createElement('div');
  brand.className = 'ss-brand';
  // Pixel-art ♪ flanks the title on both sides, alternating bob via CSS animation-delay.
  // Left note is cyan (matches SOUNDS), right note is gold (matches SMASHING) — colors
  // pulled directly from the wordmark so the decoration ties to the brand's color story.
  const noteL = document.createElement('div');
  noteL.className = 'ss-brand__note ss-brand__note--left';
  noteL.style.backgroundImage = `url(${_brandNoteCyanUrl})`;
  const noteR = document.createElement('div');
  noteR.className = 'ss-brand__note ss-brand__note--right';
  noteR.style.backgroundImage = `url(${_brandNoteGoldUrl})`;
  const text = document.createElement('div');
  text.className = 'ss-brand__text';
  const line1 = document.createElement('div');
  line1.className = 'ss-brand__line1';
  line1.textContent = 'SOUNDS';
  const line2 = document.createElement('div');
  line2.className = 'ss-brand__line2';
  line2.textContent = 'SMASHING!';
  text.appendChild(line1);
  text.appendChild(line2);
  brand.appendChild(noteL);
  brand.appendChild(text);
  brand.appendChild(noteR);

  _homeContent.appendChild(brand);

  // Tagline under the brand — tells cold visitors what the game IS in one breath. Pink
  // for kit colour cohesion. Press Start 2P at modest size so it sits as a subtitle.
  const tagline = document.createElement('div');
  tagline.style.cssText = 'font-family:var(--font-display);font-size:18px;letter-spacing:2px;'
    + 'color:var(--col-pink, #FF3B6B);'
    + '-webkit-text-stroke:3px var(--col-ink);paint-order:stroke;'
    + 'text-transform:uppercase;text-align:center;margin-top:-12px;';
  tagline.textContent = 'Smash to the rhythm!';
  _homeContent.appendChild(tagline);

  // Multiplayer hero — the front door leads with online racing (Layout A). The assist + Equip Gloves
  // controls that used to share this strip now live on the world-grid + Community pages (the surfaces
  // where you pick what to play); the ? HOW TO PLAY modal keeps the assist toggles as a global fallback.
  if (!_MP_PROD_HIDDEN) _homeContent.appendChild(_buildMpHero());

  // World tiles row. Hidden worlds (e.g. world4 while UGC is in flux) are skipped here so
  // they don't appear on the home page; their levels are still reachable via direct id if
  // anything in code references them.
  const worldsRow = document.createElement('div');
  worldsRow.className = 'ss-worlds';
  for (const world of WORLDS) {
    if (world.hidden) continue;
    const worldUnlocked = _isWorldUnlocked(world);
    const prereqWorld = _worldPrereq(world);
    const tile = document.createElement('div');
    tile.className = 'ss-world' + (world.status === 'soon' || !worldUnlocked ? ' ss-world--soon' : '');

    const name = document.createElement('div');
    name.className = 'ss-world__name';
    name.textContent = `WORLD ${WORLDS.indexOf(world) + 1}`;
    tile.appendChild(name);

    // Hero silhouette inside the tile — one signature level per world. Picked for
    // distinctive shape (not gameplay quality) so the home page promises "hand-crafted
    // iso content lives here" without spoiling all 10 levels' surprises. Easy to swap.
    if (world.status === 'active') {
      const heroId = _WORLD_HERO_LEVEL[world.id];
      const entry = heroId && curatedSilhouettes[heroId];
      if (entry && entry.silhouette) {
        const heroWrap = document.createElement('div');
        heroWrap.className = 'ss-world__hero';
        const heroFront = document.createElement('div');
        heroFront.className = 'ss-world__hero-front';
        const img = document.createElement('img');
        img.src = _renderSilhouetteToDataUrl(entry.silhouette, entry.scene, `hero-${heroId}`, 0.4);
        img.alt = '';
        heroFront.appendChild(img);
        heroWrap.appendChild(heroFront);
        tile.appendChild(heroWrap);
      }
    }

    if (world.status === 'active' && worldUnlocked) {
      const stars = _getWorldStars(world);
      const totalStars = world.levelIds.length * 3;
      const progress = document.createElement('div');
      progress.className = 'ss-world__progress';
      const starIcon = document.createElement('span');
      starIcon.className = 'ss-world__progress-star';
      starIcon.textContent = '★';
      const text = document.createElement('span');
      text.textContent = `${stars} / ${totalStars}`;
      progress.appendChild(starIcon);
      progress.appendChild(text);
      tile.appendChild(progress);

      tile.onclick = (e) => {
        e.stopPropagation();
        _fireAnalytics('world_tile_click', { levelId: world.id });
        // First-time World 1 flow: skip the level select, auto-start level1.
        // The W1 level select is fine for returning players who want to pick a
        // specific level (replay, three-star chase, etc.), but for the very
        // first click the friction of "pick level1 from a 10-card grid" is
        // pure drop-off. The localStorage flag is set when level1 starts, so
        // any path into level1 (deep link, dev, etc.) flips the user to the
        // returning-player flow on the next visit.
        // Not in an mp room: a co-op/race player who lands on home must NOT get yanked into solo level1
        // (that's how a first-timer ended up in the wrong level instead of joining the shared session).
        if (world.id === 'world1' && !_hasEverStartedW1() && !_MP_ROOM) {
          _fireAnalytics('first_time_world1_auto_start', { levelId: 'world1' });
          _enterLevel('level1');
          return;
        }
        _currentWorld = world.id;
        _renderHome();
      };
    } else if (world.status === 'active' && !worldUnlocked) {
      const stamp = document.createElement('div');
      stamp.className = 'ss-world__soon-stamp';
      const prereqIndex = prereqWorld ? WORLDS.indexOf(prereqWorld) + 1 : 1;
      stamp.textContent = `COMPLETE WORLD ${prereqIndex}`;
      tile.appendChild(stamp);
    } else {
      const stamp = document.createElement('div');
      stamp.className = 'ss-world__soon-stamp';
      stamp.textContent = 'COMING SOON';
      tile.appendChild(stamp);
    }

    // Mascot on every active world tile — hidden by default, slides + fades in on hover.
    // Universal "ready to go" affordance so every world feels alive, not just whichever
    // contains the next unstarred level. Wrapper div owns the slide/fade transition so it
    // doesn't compete with the bob animation on the sprite itself.
    if (world.status === 'active' && worldUnlocked) {
      const mascotWrap = document.createElement('div');
      mascotWrap.className = 'ss-world__mascot-wrap';
      const mascot = document.createElement('div');
      mascot.className = 'ss-world__mascot';
      mascotWrap.appendChild(mascot);
      tile.appendChild(mascotWrap);
    }

    worldsRow.appendChild(tile);
  }
  _homeContent.appendChild(worldsRow);

  // Featured UGC row — sits between the worlds and the sandbox link. Caption labels the
  // row as community content; the CREATE/BROWSE tile inside the row does the inviting.
  const featuredCaption = document.createElement('div');
  featuredCaption.style.cssText = 'font-family:var(--font-display);font-size:18px;letter-spacing:2px;'
    + 'color:var(--col-cyan, #00E5FF);'
    + '-webkit-text-stroke:3px var(--col-ink);paint-order:stroke;'
    + 'text-transform:uppercase;text-align:center;margin-top:8px;';
  featuredCaption.textContent = 'Latest worlds by the community';
  _homeContent.appendChild(featuredCaption);

  const featuredRow = document.createElement('div');
  featuredRow.style.cssText = 'display:flex;gap:14px;flex-wrap:wrap;justify-content:center;align-items:flex-start;';
  _homeContent.appendChild(featuredRow);
  _populateFeaturedRow(featuredCaption, featuredRow);

  // (CREATE + BROWSE actions now live as the 4th tile in the featured row — see
  // _buildCreateBrowseTile. The standalone CTA button row below the featured section was
  // dropped because the in-row tile keeps the actions at the same scroll position as the
  // featured cards, never below the fold.)

  // Sandbox link — secondary meta-mode, demoted to a small text link below the featured row.
  const sandbox = document.createElement('div');
  sandbox.className = 'ss-home__sandbox';
  sandbox.textContent = '◇  Free play sandbox  ◇';
  sandbox.onclick = (e) => { e.stopPropagation(); _enterLevel('sandbox'); };
  _homeContent.appendChild(sandbox);

  // Footer — quiet credits + GitHub link at the bottom of the page. Reads as a
  // real product, not a demo. Same Jersey 25 + small dim styling as community meta.
  // (Discord moved to the top-right bar next to HOW TO PLAY — see _topRightBar.)
  const footer = document.createElement('div');
  footer.style.cssText = 'margin-top:32px;padding:24px 0 12px;display:flex;flex-direction:column;align-items:center;gap:6px;'
    + 'font-family:"Jersey 25", var(--font-body), monospace;font-size:13px;color:#6c6c8a;letter-spacing:0.5px;';
  const credit = document.createElement('div');
  credit.textContent = 'made by Donkeytonk';
  footer.appendChild(credit);
  const links = document.createElement('div');
  links.style.cssText = 'display:flex;gap:14px;align-items:center;';
  const mkLink = (label, href, iconUrl) => {
    const a = document.createElement('a');
    a.href = href;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.style.cssText = 'color:#9c9cb8;text-decoration:none;border-bottom:1px solid transparent;'
      + 'transition:color 80ms,border-color 80ms;display:inline-flex;align-items:center;gap:6px;';
    if (iconUrl) {
      const icon = document.createElement('span');
      icon.style.cssText = `display:inline-block;width:16px;height:16px;background-image:url(${iconUrl});`
        + 'background-size:contain;background-repeat:no-repeat;image-rendering:pixelated;flex:0 0 auto;';
      a.appendChild(icon);
    }
    const text = document.createElement('span');
    text.textContent = label;
    a.appendChild(text);
    a.addEventListener('mousedown', (e) => e.stopPropagation());
    a.addEventListener('mouseenter', () => { a.style.color = 'var(--col-cyan, #00E5FF)'; a.style.borderBottomColor = 'var(--col-cyan, #00E5FF)'; });
    a.addEventListener('mouseleave', () => { a.style.color = '#9c9cb8'; a.style.borderBottomColor = 'transparent'; });
    return a;
  };
  links.appendChild(mkLink('GitHub', 'https://github.com/donkeytonk', _GITHUB_ICON_URL));
  footer.appendChild(links);
  _homeContent.appendChild(footer);

  // Help / controls live in the persistent USER GUIDE button (top-right of viewport,
  // built once at module load — see `_userGuideBtn` near the lockHint setup). The inline
  // toggle + howto panel that used to sit at the end of this view were removed when the
  // modal replaced them, so the home page ends quietly with the sandbox link.
}

function _renderWorldLevels() {
  const world = WORLDS.find(w => w.id === _currentWorld);
  if (!world) { _currentWorld = null; _renderWorldSelect(); return; }
  if (!_isWorldUnlocked(world)) { _currentWorld = null; _renderWorldSelect(); return; }

  const view = document.createElement('div');
  view.className = 'ss-world-view';

  // Title only — back button is now the global fixed top-left HOME button.
  const title = document.createElement('div');
  title.className = 'ss-world-view__title';
  title.textContent = `WORLD ${WORLDS.indexOf(world) + 1}`;
  title.style.marginTop = '36px';
  view.appendChild(title);

  // Player controls (Rhythm/Aim assist + Equip Gloves) sit right where you pick a level to play.
  view.appendChild(_buildPlayerControls());

  // Active-level cursor: the first level in this world the player hasn't passed yet.
  // The mascot character sits on this card. null once the world is fully cleared.
  const activeLevelId = _nextLevelInWorld(world);

  // Snake layout — the path winds 1→2→3→4→5 across the top row, then drops down to
  // 6 (under 5) and travels back left as 6→7→8→9→10. Visually that means level 10 sits
  // under level 1, level 9 under level 2, etc. The DOM order to produce this in a
  // 5-column grid is: row 1 forward, row 2 reversed.
  const ids = world.levelIds;
  const rowSize = 5;
  const snakeOrder = [];
  for (let i = 0; i < ids.length; i++) {
    const row = Math.floor(i / rowSize);
    const colInRow = i % rowSize;
    // Even rows go forward, odd rows reversed. Fits any 5×N grid; works for 10-level worlds.
    snakeOrder.push(row % 2 === 0 ? i : (row * rowSize + (rowSize - 1 - colInRow)));
  }

  const grid = document.createElement('div');
  grid.className = 'ss-levels';
  for (const idx of snakeOrder) {
    const lvlId = ids[idx];
    const lvl = LEVELS.find(l => l.id === lvlId);
    if (!lvl) continue;
    grid.appendChild(_buildLevelCard(lvl, lvl.id === activeLevelId));
  }
  view.appendChild(grid);

  // Always-on HOW TO PLAY panel embedded at the bottom of the level-select page. The
  // top-right ?HOW TO PLAY modal still works globally; this inline copy makes the
  // controls + tips visible by default while the player is browsing levels in a world.
  const howto = document.createElement('div');
  howto.className = 'ss-howto';
  howto.style.marginTop = '32px';
  howto.appendChild(_buildHowtoColumns());
  view.appendChild(howto);

  _homeContent.appendChild(view);
}

// Find the song spec for a level. Resolution order:
//   1. LEVEL_CONFIG[levelId].songSpec — per-level override
//   2. WORLDS[w].songSpec — per-world default
//   3. DEFAULT_SONG — engine default (A minor 120 BPM)
function _songForLevel(levelId) {
  const cfg = LEVEL_CONFIG[levelId];
  if (cfg && cfg.songSpec) return cfg.songSpec;
  for (const w of WORLDS) {
    if (w.levelIds.indexOf(levelId) !== -1) return w.songSpec || DEFAULT_SONG;
  }
  return DEFAULT_SONG;
}

// Sequential unlock, world-aware: first level of each world is always open;
// subsequent levels in a world unlock once the previous level in that world
// has ≥1 star. Sandbox is always open. Admin "unlock all" overrides everything.
// Returns the next level id the player has yet to pass within the given world — used to
// position the level-select mascot character on the current point of progression. Returns
// null if every level in the world is already passed (mascot hides in that case).
function _nextLevelInWorld(world) {
  if (!world || !world.levelIds) return null;
  for (const lvlId of world.levelIds) {
    const p = _loadLevelProgress(lvlId);
    if ((p.stars || 0) < 1) return lvlId;
  }
  return null;
}

// First non-hidden, active world that still has an unstarred level. Used by the home
// page to position the mascot on the world the player should play next. Null if every
// active world is fully cleared.
// Per-world hero silhouette shown on the home tile. Picked for distinctive SHAPE (not
// gameplay quality) so the home page hints at the world's flavor without spoiling all
// 10 levels. Swap any id below to retune.
const _WORLD_HERO_LEVEL = {
  world1: 'level5',   // open arena disk — clean recognizable circular silhouette
  world2: 'level18',  // HUGE PIKACHU creature — most iconic silhouette in the game
  world3: 'level28',  // desert pyramids — strong geometric triangle profile
};

function _isLevelUnlocked(levelId) {
  if (_devUnlockAll) return true;
  if (_isPracticeLevel(levelId)) return true;
  if (levelId === 'sandbox') return true;
  for (const w of WORLDS) {
    const idx = w.levelIds.indexOf(levelId);
    if (idx === -1) continue;
    if (!_isWorldUnlocked(w)) return false;
    if (idx === 0) return true;
    const prev = _loadLevelProgress(w.levelIds[idx - 1]);
    return (prev.stars || 0) >= 1;
  }
  return true;
}

function _buildLevelCard(lvl, isActiveLevel = false) {
  const progress = _loadLevelProgress(lvl.id);
  const unlocked = _isLevelUnlocked(lvl.id);
  const isResume = unlocked && _currentLevel === lvl.id;

  const card = document.createElement('div');
  card.className = 'ss-level' + (unlocked ? '' : ' ss-level--locked') + (isResume ? ' ss-level--resume' : '');
  // Match the community + featured card affordance — white default, kit panel-yellow on
  // hover, plus a thick cyan outline ring (5px) so the active card pops against neighbours.
  // The base .ss-level class fills in white via CSS; the hover swap is JS so we don't
  // have to fight the CSS class' inherited :hover lift.
  if (unlocked) {
    card.addEventListener('mouseenter', () => {
      card.style.backgroundColor = 'var(--col-bg-panel)';
      // Match the kit's dark chunky border to the yellow bg so it visually disappears,
      // letting the cyan outline sit flush against the yellow with no dark gap between.
      // No layout shift since border-width stays the same.
      card.style.borderColor = 'var(--col-bg-panel)';
      card.style.outline = '5px solid var(--col-cyan, #00E5FF)';
      card.style.outlineOffset = '0';
    });
    card.addEventListener('mouseleave', () => {
      card.style.backgroundColor = '';
      card.style.borderColor = '';
      card.style.outline = '';
      card.style.outlineOffset = '';
    });
  }
  // Mascot anchor — when this is the next level to play, the character stands tucked
  // into the bottom-right corner overflowing slightly below the card so he doesn't cover
  // the centered NEW / BEST badge or the Pro Tip text. `image-rendering: pixelated` keeps
  // the sprite's pixel grid crisp at the upscaled display size.
  if (isActiveLevel) {
    card.style.position = 'relative';
    const mascot = document.createElement('div');
    // bottom:-16 hangs only 16px below the card edge so he doesn't reach into the next
    // row of level cards. will-change:transform + GPU layer (translateZ(0)) eliminates
    // the subpixel-flicker that appeared during the bob animation.
    mascot.style.cssText = 'position:absolute;width:64px;height:64px;right:-12px;bottom:-6px;'
      + 'background-image:url(/mascot.png);background-size:contain;background-repeat:no-repeat;'
      + 'pointer-events:none;image-rendering:pixelated;'
      + 'will-change:transform;transform:translateZ(0);'
      + 'animation:ss-mascot-bob 1.6s steps(2) infinite;z-index:2;';
    card.appendChild(mascot);
  }

  // Title row: LEVEL N + optional RESUME pill + ?-tooltip help icon (which carries the
  // gameplay tip on hover, freeing the body of the card for the iso silhouette preview).
  const titleRow = document.createElement('div');
  titleRow.className = 'ss-level__title-row';
  const name = document.createElement('div');
  name.className = 'ss-level__name';
  name.textContent = lvl.name.toUpperCase();
  titleRow.appendChild(name);
  const titleRight = document.createElement('div');
  titleRight.style.cssText = 'display:flex;align-items:center;gap:6px;flex-shrink:0;';
  if (isResume) {
    const pill = document.createElement('div');
    pill.className = 'ss-level__resume';
    pill.textContent = 'RESUME';
    titleRight.appendChild(pill);
  }
  if (unlocked && lvl.tip) {
    const help = document.createElement('div');
    help.className = 'ss-level__help';
    help.textContent = '?';
    const tip = document.createElement('div');
    tip.className = 'ss-level__help-tip';
    const prefix = document.createElement('span');
    prefix.className = 'ss-level__tip-prefix';
    prefix.textContent = 'PRO TIP:';
    tip.appendChild(prefix);
    tip.appendChild(document.createTextNode(lvl.tip));
    help.appendChild(tip);
    help.onclick = (e) => e.stopPropagation();
    titleRight.appendChild(help);
  }
  if (titleRight.children.length) titleRow.appendChild(titleRight);
  card.appendChild(titleRow);

  const starRow = document.createElement('div');
  starRow.className = 'ss-level__stars';
  for (let i = 1; i <= 3; i++) {
    const span = document.createElement('span');
    if (i <= progress.stars) {
      span.className = 'ss-level__stars-on';
      span.textContent = '★';
    } else {
      span.className = 'ss-level__stars-off';
      span.textContent = '☆';
    }
    starRow.appendChild(span);
  }
  card.appendChild(starRow);

  // Iso silhouette preview (unlocked) or unlock-prompt text (locked). Silhouette data
  // baked at build-time via window._dumpCuratedSilhouettes — see curated-silhouettes.json.
  // Re-run the dumper if a curated level layout changes.
  if (unlocked) {
    const entry = curatedSilhouettes[lvl.id];
    if (entry && entry.silhouette) {
      const silhWrap = document.createElement('div');
      silhWrap.className = 'ss-level__silh';
      const img = document.createElement('img');
      img.src = _renderSilhouetteToDataUrl(entry.silhouette, entry.scene, `lvl-${lvl.id}`);
      img.alt = '';
      silhWrap.appendChild(img);
      card.appendChild(silhWrap);
    }
  } else {
    const tip = document.createElement('div');
    tip.className = 'ss-level__tip';
    // Resolve the prereq's WORLD-LOCAL display name (e.g. "Level 1" in world 2) instead
    // of subtracting 1 from the global id. The id-derived number ("Clear LEVEL 11
    // first." for world 2's level 2) didn't match the card labels the player saw on the
    // grid and read as confusing. Mirrors the unlock rule in _isLevelUnlocked: prereq is
    // the previous level in this world's play order.
    let prereqName = 'previous level';
    for (const w of WORLDS) {
      const idx = w.levelIds.indexOf(lvl.id);
      if (idx > 0) {
        const prereq = LEVELS.find(L => L.id === w.levelIds[idx - 1]);
        if (prereq) prereqName = prereq.name.toUpperCase();
        break;
      }
    }
    tip.textContent = `Clear ${prereqName} first.`;
    card.appendChild(tip);
  }

  // Bottom badge: BEST score / ★ NEW ★ for unlocked, LOCKED for locked.
  if (!unlocked) {
    const lockBadge = document.createElement('div');
    lockBadge.className = 'ss-level__lock';
    lockBadge.textContent = '🔒 LOCKED';
    card.appendChild(lockBadge);
  } else if (progress.best > 0) {
    const best = document.createElement('div');
    best.className = 'ss-level__best';
    best.textContent = `BEST · ${progress.best}`;
    card.appendChild(best);
  } else {
    const newBadge = document.createElement('div');
    newBadge.className = 'ss-level__new';
    newBadge.textContent = '★ NEW ★';
    card.appendChild(newBadge);
  }

  if (unlocked) {
    card.onclick = (e) => { e.stopPropagation(); _enterLevel(lvl.id); };
  }
  return card;
}

function _getWorldStars(world) {
  let total = 0;
  for (const lvlId of world.levelIds) {
    const p = _loadLevelProgress(lvlId);
    total += p.stars || 0;
  }
  return total;
}

// Per-level config — sections is how many ~16s music sections the level lasts before clear screen.
// Sections are now 8 bars (~16s at 120 BPM); doubled from previous 16-bar (~32s) so total level
// durations are preserved. With sections > 8 the song's 8-section arc wraps and replays.
// Sandbox uses Infinity → song loops, no clear screen, no fail.
const LEVEL_CONFIG = {
  sandbox: { sections: Infinity },
  gauntlet: { sections: Infinity, songSpec: gauntletSong },   // showcase/filming level - no timer, motorik techno track
  level1:  { sections: 2, stars: { one: 17, two: 24, three: 38 } },   // ~32s — W1-L1, 21 crates
  level2:  { sections: 2, songSpec: w1L2MultihitSong, stars: { one: 50, two: 90, three: 140 } },  // ~32s — W1-L2, 40-brick wall (yield 120); stars are a first pass, playtest
  level3:  { sections: 4, songSpec: w1L3SteeringSong },  // ~64s — playful pop, steering swings
  level4:  { sections: 5, songSpec: w1L4StrideSong },  // ~80s — shown "Level 5", vertical climb, 30 crates (baseYield 30 → formula stars 24/45/90)
  level5:  { sections: 6, songSpec: w1L5OpenSong },    // ~96s — calm pop, open arena
  level6:  { sections: 6, songSpec: w1L6ChargeSong },   // ~96s — triumphant pop, charge intro
  level7:  { sections: 4, songSpec: w1L7DominoSong },   // ~64s — playful pop, domino spiral
  level8:  { sections: 4, songSpec: w1L8GrowSong },     // ~64s — bright pop, growing-cubes crescendo
  level9:  { sections: 6, songSpec: w1L9EngineSong },   // ~96s — dance pop, chip-storm engine loop
  level10: { sections: 10, songSpec: w1L10CapstoneSong },  // ~160s — triumphant pop finale, capstone arena
  level11: { sections: 2,  songSpec: w2ScaryHorrorSong, stars: { one: 17, two: 24, three: 38 } },     // ~32s — W2-L1, 21 crates
  level12: { sections: 3,  songSpec: w2FranticChaseSong, stars: { one: 22, two: 38, three: 58 } },    // ~48s — W2-L2, 14 crate + 7 brick
  level13: { sections: 2,  songSpec: w2CalmMeditationSong, stars: { one: 25, two: 42, three: 65 } },  // ~32s — W2-L3, 16 crate + 8 brick
  level14: { sections: 8, songSpec: w2RobotFactorySong },   // ~128s — W2-L4 ghost-in-haunted-house, robot-factory song (UGC test)
  level15: { sections: 6,  songSpec: w2AncientTempleSong },     // ~96s — W2-L5 + ancient temple song
  level16: { sections: 6,  songSpec: w2RainyCityNightSong },    // ~96s — W2-L6 + rainy city at night song
  level17: { sections: 4,  songSpec: w2AlienDancePartySong },   // ~64s — W2-L7 + alien dance party song
  level18: { sections: 12, songSpec: w2CheekyFishDuckSong },  // ~192s — W2-L8 HUGE PIKACHU + cheeky fish/duck song (vibe-preset UGC test)
  level19: { sections: 8,  songSpec: w2IceCastleCrystalSong },   // ~128s — W2-L9 ice castle (combined level+song UGC test)
  level20: { sections: 12, songSpec: w2AscendingToStarsSong },   // ~192s — W2-L10 wall-climb-to-aliens + ascending to the stars song
  level21: { sections: 4,  songSpec: w3SkiSlalomSong },          // ~64s  — W3-L1 ski slalom (combined UGC test)
  level22: { sections: 4,  songSpec: w3SnakeSlitherGrassSong },  // ~64s  — W3-L2 snake slithering (combined UGC test)
  level23: { sections: 4,  songSpec: w3BoxingComboSong },        // ~64s  — W3-L3 boxing combo
  level24: { sections: 4,  songSpec: w3TennisSong },             // ~64s  — W3-L4 tennis rally
  level25: { sections: 4,  songSpec: w3ThreeTowersSong },        // ~64s  — W3-L5 three tall towers
  level26: { sections: 4,  songSpec: w3StreetLightsSong },       // ~64s  — W3-L6 street lights
  level27: { sections: 4,  songSpec: w3JengaTowersSong },        // ~64s  — W3-L7 jenga towers
  level28: { sections: 4,  songSpec: w3DesertPyramidsSong },     // ~64s  — W3-L8 desert pyramids
  level29: { sections: 4,  songSpec: w3BricksTowersSong },       // ~64s  — W3-L9 bricks and towers
  level30: { sections: 4,  songSpec: w3VolcanoFinaleSong },      // ~64s  — W3-L10 volcano finale
  level31: { sections: 4,  songSpec: testSmileysSong },          // ~64s  — TEST 1 (Gemini smileys)
  level32: { sections: 4,  songSpec: testTempleSong },           // ~64s  — TEST 2 (Gemini temple)
  level33: { sections: 4,  songSpec: testGraveyardSong },        // ~64s  — TEST 3 (Gemini graveyard, oversized)
  level34: { sections: 4,  songSpec: testMusicBoxSong },         // ~64s  — TEST 4 (Gemini music box, abstract)
  'ugc-current': { sections: 4, songSpec: null },                // mutated when player generates a UGC level
  'practice-spring': { sections: Infinity },
  'practice-rocket': { sections: Infinity },
  'practice-dual': { sections: Infinity },
};

// Tracks whether the inline buildSandboxLayout() at startup is still pristine. Once a fresh run
// begins (or the player switches levels), we tear-down + rebuild and flip this off.
let _initialLayoutBuilt = true;
// Is a run currently live (between _enterLevel start and _endLevel)? Resume-via-Esc keeps this true.
let _runActive = false;

// Tear down all blocks + debris + transient targeting/charge state. Used before rebuilding for retry.
function _clearWorld() {
  setNearTarget(null);
  lockedTarget = null;
  for (const block of blocks) {
    if (block.userData.body) {
      _blockByBodyHandle.delete(block.userData.body.handle);
      physicsWorld.removeRigidBody(block.userData.body);
    }
    scene.remove(block);
    block.geometry.dispose();
    block.material.dispose();
    if (block.userData.crackTexture) block.userData.crackTexture.dispose();
  }
  blocks.length = 0;
  for (const chip of debris) _removeChip(chip);
  debris.length = 0;
  charging[0] = charging[1] = false;
  _pendingShockwave[0] = _pendingShockwave[1] = false;
}

function _resetRunStats() {
  _score = 0;
  _scoreDisplayed = 0;        // reset the rolling count-up so a new level starts clean at 0
  _scoreRollLastText = '';
  _streak = 0;
  _rhythmRecoveryDebt = false;
  _lastStreakTier = 0;
  _runLongestStreak = 0;
  _lastScoredPunchMs[0] = 0;
  _lastScoredPunchMs[1] = 0;
  _runPerfectHits = 0;
  _runTotalTimingHits = 0;
  _coinScoreSpan.textContent = '0';
  _coinBarFill.style.width = '0%';
  _updateStreakHud();
  // Reset player to the spawn point + facing forward at the start of every fresh run. Without
  // the rotation reset, finishing a run while looking sideways/behind (chasing rubble in a big
  // arena) leaves you facing that way at the start of the next level.
  // Race: the spawn X is a UNIQUE side-by-side slot the referee hands out at gate-open (rgo) so two
  // players can't land on the same spot. It isn't known yet here (level entry precedes rgo), so start
  // provisionally at centre and let _raceOnGo snap us to the assigned slot during the (input-locked)
  // countdown. _raceApplySpawn maps slot/count to an evenly-spread, centred X.
  if (_RACE_ON) { _raceSpawnSlot = -1; _raceSpawnCount = 0; }
  _raceApplySpawn();
}

// Place the player at the current spawn X (centre for solo/non-race or before the referee assigns a
// slot; the referee-assigned unique slot once rgo lands). Spread is centred: slot i of n -> (i-(n-1)/2)*2.
function _raceApplySpawn() {
  let x = 0;
  if (_RACE_ON && _raceSpawnSlot >= 0 && _raceSpawnCount > 0) {
    x = (_raceSpawnSlot - (_raceSpawnCount - 1) / 2) * 2;
  }
  camera.position.set(x, 1.65, 6);
  camera.rotation.set(0, 0, 0);
  moveVelocity.set(0, 0, 0);
  playerBody.setNextKinematicTranslation({ x, y: 1.65, z: 6 });
}

// Compute base yield + star thresholds + duration for the level. Run AFTER blocks exist.
function _setupLevel(id) {
  const cfg = LEVEL_CONFIG[id] || LEVEL_CONFIG.sandbox;
  _fireAnalytics('level_start', { levelId: id });
  // Stamp the first-time World 1 gate the moment level1 is started. Covers the
  // W1 tile auto-start (primary path) AND any other path into level1 (deep link,
  // dev console, the W1 level select if a returning player revisits before the
  // flag is set, etc.). Once set, the user is treated as "already saw the W1
  // level select" and the auto-start is bypassed on subsequent W1 clicks.
  if (id === 'level1') _markW1EverStarted();
  // Level 1 controls coach — arm it for L1 (shown on the next pointer-lock), disarm elsewhere.
  if (id === 'level1') _coachReset(); else _coachDisarm();
  _levelBaseYield = _computeBaseYield();
  // Star thresholds — rebalanced 2026-05-13 alongside the "BAD = no coins" rule.
  // BASE_YIELD = max coins from breaking every block at "good" timing (no perfect, no time bonus).
  // Under the new rule, miss-tier hits drop zero coins, so 100% good timing is required to hit
  // 1.0× base. Floor lowered to 0.8× so casual play with a few miss-tier hits can still pass 1★.
  // Ceilings raised because the bonus stack (streak ≤ 2× + storm-max 3× + perfect 2×) can multiply
  // base by ~6× on hot runs — old 2.0× ceiling was trivial under the new bonuses.
  // Per-level coin thresholds: if a level's config has an explicit `stars` object, use those
  // numbers directly. Otherwise fall back to the formula based on baseYield. Direct numbers
  // are the right call when a level's block count doesn't predict how achievable the goals
  // actually feel (e.g. short levels can't accumulate streak so the formula over-shoots).
  if (cfg.stars) {
    _levelStarThresholds = { one: cfg.stars.one, two: cfg.stars.two, three: cfg.stars.three };
  } else {
    _levelStarThresholds = {
      one:   Math.round(_levelBaseYield * 0.8),  // pass = ~80% good-timing hits (some miss room for casual)
      two:   Math.round(_levelBaseYield * 1.5),  // solid good timing + small streaks
      three: Math.round(_levelBaseYield * 3.0),  // sustained streaks, some perfects, storm-max territory
    };
  }
  _hudTarget = _levelStarThresholds.one || 600;  // bar fills toward 1★ goal — end of bar = level cleared
  _setHudGoal(cfg.sections === Infinity ? null : _levelStarThresholds);
  _levelDurationSec = cfg.sections === Infinity ? Infinity : cfg.sections * MUSIC_LOOP_DURATION;
  _levelStartAudio = audioCtx ? audioCtx.currentTime : 0;
  _levelEnded = false;
  // Show the goal banner on every scored level (curated + UGC). Sandbox skips because it
  // has no clear gate. Banner reuses the same shrink-into-HUD animation regardless of
  // level — the visual cue is helpful even on repeat plays, since per-level coin targets
  // vary as the player rolls through different songs/structures.
  if (cfg.sections !== Infinity) {
    _showGoalIntro(_levelStarThresholds.one);
  }
  _runActive = true;
  _allBlocksClearedAt = -Infinity;  // reset so retry doesn't carry over a stale "cleared" timer
  _endTriggered = false;
  // Music time resets on level entry — clear slot tracking so the first press is admitted.
  _lastConsumedSlot = -1;
  _slotDualPressUsed = -1;
  _lastPressTime[0] = _lastPressTime[1] = -Infinity;
  pendingLaunchSuppressCoins[0] = pendingLaunchSuppressCoins[1] = false;
  _endTriggeredAtMs = 0;
  _endReason = null;
  _endingCashedOut = false;
  // Hard-reset the in-flight coin state and bump the epoch so any coins still cashing in from the
  // PREVIOUS run can't add to this level's score (the bug where starting a new level fast left the
  // old run's coins bleeding into the new total). The epoch guard makes their callbacks no-ops.
  _pendingCoinAwards = 0;
  _pendingCoinValue = 0;
  _coinAwardEpoch++;
  // Race: entering the arena. Lock input until the referee opens the start gate, and clear any prior-
  // round race state so a retry / re-entry starts a clean race. _raceTick then signals ready + runs the
  // countdown once we're connected.
  if (_RACE_ON) {
    _raceLocked = true;
    _raceStarted = false;
    _raceOver = false;
    _raceEndSent = false;
    _raceReadySent = false;
    _raceGoAt = 0;
    _raceEmptyAt = -1;
    _raceBoard = [];        // clear last round's standings so a rematch HUD starts from zero
    _raceTotalBlocks = blocks.length;   // the arena is built by now — this is the goal the bars fill toward
    _raceHideBanner();
    _raceHideResultActions();
    _raceRenderHud();       // reflect the cleared board (hides the panel until the first smash)
  }
  // MP boot: the arena is now built — reveal it (two frames to let it paint) so the sandbox backdrop
  // never showed through. No-op after the first level of a session (the curtain only exists at boot).
  if (_mpBootCurtain) requestAnimationFrame(() => requestAnimationFrame(_hideMpBootCurtain));
}

// Restart music from section 0 — used on retry. Mirrors skipToNextSection but anchored at idx 0.
function restartMusic() {
  if (!_musicStarted || !audioCtx) {
    startMusic();
    return;
  }
  if (_musicTimeoutId !== null) { clearTimeout(_musicTimeoutId); _musicTimeoutId = null; }
  if (_musicGain) _musicGain.disconnect();
  _musicGain = audioCtx.createGain();
  _musicGain.gain.value = _MUSIC_VOLUME_TARGET;
  _musicGain.connect(_masterBus);
  // Rebuild reverb + delay sends from the new _musicGain. Without this the effects chain
  // is left wired to the disconnected old gain, so replays sound bone dry even when the
  // song spec has reverbWetness > 0. (`skipToNextSection` does the same dance.)
  _buildMusicEffects();
  _applyMusicEffectsFromSong();
  _musicLoopStartTime = audioCtx.currentTime + 0.05;
  _musicNextStart = audioCtx.currentTime + 0.05;
  _musicLoopIndex = 0;
  _easyOnsetTimes.length = 0;  // fresh timeline -> drop any onsets queued against the old one
  _scheduleNextLoop();
}

// Multiplayer music sync: re-anchor our song clock to `pos` (the host's continuous seconds-since-song-
// start) so a joining player drops into the SAME part of the song the existing player is hearing. We
// rewind our loop-start to put us at `pos`, then re-run the section we're now inside from its real start
// — _noteIsPast skips the onsets already behind the clock, so only the remainder of the bar sounds and
// the next section lines up on its boundary. (No latency compensation yet; fine on a LAN/local tabs, a
// little late over the open internet — a later increment can fold in half-RTT.)
function _resyncMusicTo(pos) {
  if (!audioCtx || !Number.isFinite(pos) || pos < 0) return;
  if (!_musicStarted) return;   // not in a level yet — we'll pull a fresh position via 'mreq' on entry
  if (_musicTimeoutId !== null) { clearTimeout(_musicTimeoutId); _musicTimeoutId = null; }
  // Swap _musicGain so the notes we ALREADY queued against the old (unsynced) timeline are cut — they
  // were scheduled ahead in Web Audio and would otherwise keep playing OVER the re-anchored timeline,
  // smearing two beat grids together (sounds like "different beats"). Same dance restartMusic does.
  if (_musicGain) _musicGain.disconnect();
  _musicGain = audioCtx.createGain();
  _musicGain.gain.value = _MUSIC_VOLUME_TARGET;
  _musicGain.connect(_masterBus);
  _buildMusicEffects();
  _applyMusicEffectsFromSong();
  const SECTION_DUR = MUSIC_LOOP_DURATION;
  _musicLoopStartTime = audioCtx.currentTime - pos;        // our song clock now reads `pos`
  const sectionIdx = Math.floor(pos / SECTION_DUR);
  _musicLoopIndex = sectionIdx;
  _musicNextStart = _musicLoopStartTime + sectionIdx * SECTION_DUR;  // start of the section we're inside (in the past)
  _easyOnsetTimes.length = 0;
  _scheduleNextLoop();
}

function _enterLevel(id, opts = {}) {
  const enteringPractice = _isPracticeLevel(id);
  if (enteringPractice) {
    _practiceMode = id.replace('practice-', '') || 'spring';
    _setRuntimeGloveLoadout(_practiceLoadoutForMode(_practiceMode));
  } else if (_RACE_ON && _RACE_LOADOUT) {
    // Race fairness: every player runs the host-chosen loadout, overriding their own saved loadout for
    // the race only (their save is untouched). Must win here — _activeLoadoutForLevel would otherwise
    // pull each player's personal/teaching loadout and desync the matchup.
    _practiceMode = null;
    _setRuntimeGloveLoadout(_RACE_LOADOUT);
  } else {
    _practiceMode = null;
    _setRuntimeGloveLoadout(_consumeForcedLoadoutForLevel(id) || _activeLoadoutForLevel(id));
  }
  // `force` bypasses the progression gate — used by intentional deep-links (?level=/?gauntlet) and races,
  // where a joiner may not have unlocked the arena in their own campaign but must still enter it.
  if (!opts.force && !_isLevelUnlocked(id)) {
    const owningWorld = WORLDS.find(w => w.levelIds.includes(id));
    if (owningWorld && !_isWorldUnlocked(owningWorld)) _currentWorld = null;
    _renderHome();
    return;
  }
  // `restart` forces a full rebuild even when we're "already in" this level — the race rematch needs this:
  // at a race finish _runActive is still true and _levelEnded is still false, so without the guard the
  // resume shortcut below would just re-lock the pointer and the rematch would never rebuild or re-arm.
  const isResume = !opts.restart && _currentLevel === id && _runActive && !_levelEnded;
  if (isResume) {
    _currentLevel = id;
    _requestPointerLock();
    return;
  }
  // Replace the home panel content with the pulsing pixel speaker so the click feels
  // responsive without exposing the previous level's scene. lockHint stays visible covering
  // the canvas; the lock-event handler hides it once the new world is built and lock acquires.
  if (lockHint) {
    _homeContent.textContent = '';
    _homeContent.appendChild(_loadingSpeakerWrap);
  }
  // Latch the requested level so a fast second click cancels the first deferred rebuild.
  _pendingEntryLevel = id;
  requestAnimationFrame(() => {
    if (_pendingEntryLevel !== id) return;  // superseded by a newer click
    _pendingEntryLevel = null;
    try {
      // Fresh run: rebuild world unless this is the very first sandbox click on the pristine layout.
      const skipRebuild = id === 'sandbox' && _initialLayoutBuilt && !_runActive;
      if (!skipRebuild) {
        _clearWorld();
        _buildLevelLayout(id);
        buildSupportGraph();
        _rebuildBoundsForLevel(id);
      }
      _initialLayoutBuilt = false;
      _resetRunStats();
      _currentLevel = id;
      // Apply the level's world song spec before the music starts/restarts. World 1 levels
      // (and sandbox) fall through to DEFAULT_SONG. World 2 levels get whatever spec is
      // attached to the world (currently authored from a UGC prompt).
      applySong(_songForLevel(id));
      // Music: first time → startMusic; subsequent fresh starts → restart from section 0.
      if (!_musicStarted) startMusic();
      else restartMusic();
      // _setupLevel needs audioCtx set, which startMusic ensures.
      _setupLevel(id);
      // Multiplayer: reveal the shared scene the moment it's built, so a window that isn't being actively
      // driven (a joiner who hasn't clicked to capture the mouse yet, or either player pre-GO) SPECTATES
      // the live arena instead of sitting behind the home overlay — which reads as a blank screen. The
      // pointer-lock 'lock' handler normally does this reveal, but it only fires once a click acquires
      // lock, which never comes on an idle spectating window. The "click anywhere to aim" hint invites
      // the one gesture that captures the mouse.
      if (_MP_ROOM) {
        lockHint.style.display = 'none';
        document.body.classList.add('in-game');
        if (_mpSpectateBadge) _mpSpectateBadge.style.display = 'block';
      }
      // Lock at the end so the cursor capture lines up with the freshly-built scene's first
      // render. The pointerlockerror listener handles the Chrome exit-cooldown via 1.3s retry.
      _requestPointerLock();
    } catch (e) {
      // Restore home on any rebuild failure so the user isn't stranded staring at the
      // pulsing speaker. The console error pinpoints what threw.
      console.error('[level entry] rebuild failed:', e);
      if (lockHint) lockHint.style.display = 'block';
      document.body.classList.remove('in-game');
      _renderHome();
    }
  });
}
let _pendingEntryLevel = null;

// Pointer-lock retry helper. Chrome blocks lock requests for ~1.25s after Esc-exit (the
// "exit cooldown"). If the user clicks a level card during that window the lock fails
// silently and they have to click again. We track our intent (_wantLock) and auto-retry
// once after 1.3s if the browser fires pointerlockerror.
let _wantLock = false;
let _pointerLockRetryPending = false;
function _requestPointerLock() {
  if (controls.isLocked) return;
  _wantLock = true;
  try { controls.lock(); } catch (_) { /* swallow; pointerlockerror handler retries */ }
}

// Fired when the song's final section ends — show the clear screen with stars + bonuses.
function _endLevel() {
  if (_levelEnded) return;
  _levelEnded = true;
  _runActive = false;
  // Bank any currency chips still sitting on the floor — score is final once the run ends.
  _flushPhysicalCoinsToScore();
  // Fold in coins that are still FLYING to the counter (their setTimeout score-add hasn't landed
  // yet). Without this, a big ground-break cash-out that outlasts the hard timeout would end the
  // level with those coins uncounted — the player would fail with their coins still in the air.
  // Bump the epoch so the in-flight award callbacks become no-ops and don't double-add.
  if (_pendingCoinValue > 0) _score += _pendingCoinValue;
  _pendingCoinValue = 0;
  _pendingCoinAwards = 0;
  _coinAwardEpoch++;
  const baseScore = Math.round(_score);
  // Pass is decided by COINS ALONE — time bonus only contributes to higher stars, never to passing.
  const passed = baseScore >= _levelStarThresholds.one;
  // Time bonus: scales with leftover song time, capped at the pass threshold's worth of coins.
  // A perfect cleared-instantly run earns ≈ +1★ in time bonus on top of base coins.
  const elapsedSec = audioCtx ? (audioCtx.currentTime - _levelStartAudio) : _levelDurationSec;
  const leftoverFrac = passed ? Math.max(0, (_levelDurationSec - elapsedSec) / _levelDurationSec) : 0;
  const timeBonus = Math.round(leftoverFrac * _levelStarThresholds.one);
  const totalScore = baseScore + timeBonus;
  // Stars: must pass on coins first; total score (coins + time bonus) determines 2★/3★ tier.
  let stars = 0;
  if (passed) {
    stars = 1;
    if (totalScore >= _levelStarThresholds.two)   stars = 2;
    if (totalScore >= _levelStarThresholds.three) stars = 3;
  }
  _score = totalScore;
  _coinScoreSpan.textContent = String(totalScore);
  // Persist best stars + best score so the level-select cards can show progress next session.
  // For UGC levels the slot id 'ugc-current' is shared, so key the progress by the actual
  // community level id (`ugc-<shortid>`) instead.
  const progressKey = (_currentLevel === 'ugc-current' && _ugcId) ? `ugc-${_ugcId}` : _currentLevel;
  const progressionBefore = _loadProgressionState();
  _saveLevelProgress(progressKey, stars, totalScore);
  const progressionAfter = _loadProgressionState();
  const unlockReward = passed ? _unlockRewardForClear(progressKey, progressionBefore, progressionAfter) : null;
  // Kick off the score submission BEFORE building the clear screen so we can pass the
  // promise into the leaderboard reveal phase. The reveal phase awaits the promise and
  // animates a knockout/first-record celebration based on the before-vs-after diff.
  // Submit only happens on a passing UGC run; for everything else the promise stays null.
  const submitPromise = (passed && _currentLevel === 'ugc-current' && _ugcId)
    ? _submitUgcScore(_ugcId, totalScore)
    : null;
  _showClearScreen({ stars, baseScore, timeBonus, totalScore, endReason: _endReason, submitPromise, unlockReward });
  _fireAnalytics('level_clear', {
    levelId: _currentLevel || '',
    status: passed ? 'pass' : 'fail',
    stars,
    score: totalScore,
  });
  if (controls.isLocked) controls.unlock();
}

// Beat-locked target-block pulse — every quarter-note the current near/locked target
// emissive-flashes. Sharp attack (full at phase 0), quick decay by ~30% of beat so each
// pulse reads as a snap, not a smear. Was previously applied world-wide and competed
// with itself for visibility; scoping to just the current target makes the rhythm cue
// unambiguous ("hit THIS block on THIS beat") and lets us crank intensity since only
// one block flashes at a time. Non-target blocks have emissive forced to 0 to reset
// any flash they had under the prior world-wide behavior.
let BLOCK_BEAT_PULSE_INTENSITY = 0.04;
function _updateBlockBeatPulse() {
  if (!_musicStarted || !audioCtx) return;
  const elapsed = audioCtx.currentTime - _musicLoopStartTime;
  const beatPhase = (elapsed % MUSIC_BEAT) / MUSIC_BEAT;  // 0 at beat onset, climbs to 1 by next beat
  const pulse = Math.max(0, 1 - beatPhase * 3.3);
  const intensity = pulse * pulse * BLOCK_BEAT_PULSE_INTENSITY;
  const target = lockedTarget || nearTarget;
  for (const b of blocks) {
    if (!b.userData.body) continue;  // skip blocks mid-break-animation
    if (b === target) {
      b.material.emissive.setRGB(intensity, intensity, intensity);
    } else {
      b.material.emissive.setRGB(0, 0, 0);
    }
  }
}

// Track whether a level's time is up. Called every tick. Two end conditions:
//   - Time-up: song's allotted duration has elapsed.
//   - Content-up: all blocks broken (with a small delay so the final break finishes).
// Once the end is triggered we cash out floor coins (animated) and wait for every flying
// coin to land before firing _endLevel — so the player sees every coin before the clear screen.
let _allBlocksClearedAt = -Infinity;
// Hard timeout — once the trigger fires, the clear screen waits at most this long for chips
// to resolve. After that, any remaining floor chips get sync-flushed by _endLevel. Prevents
// the screen from hanging indefinitely when something stays stuck (orbiting / settling chips).
const END_HARD_TIMEOUT_MS = 5000;
function _exitPracticeRoom() {
  _runActive = false;
  _levelEnded = true;
  _practiceMode = null;
  _setRuntimeGloveLoadout(_currentLevel ? _activeLoadoutForLevel(_currentLevel) : gloveLoadout);
  _coinHudGroup.style.display = 'none';
  _streakBadge.style.display = 'none';
  _coachDisarm();
  _hudSpeakerFloat.style.display = 'none';
  _hudSpeakerFloatRight.style.display = 'none';
  _currentLevel = null;
  _currentWorld = null;
  _homeView = 'world-select';
  _renderHome();
  lockHint.style.display = 'block';
  document.body.classList.remove('in-game');
  if (controls.isLocked) controls.unlock();
}

function _checkLevelEnd() {
  if (_RACE_ON) {
    _raceTick();   // signal-ready / GO countdown driver (runs every frame while racing)
    // Race finish = arena emptied. Checked HERE, independent of level duration, because race arenas like
    // the gauntlet are endless (_levelDurationSec === Infinity) and would otherwise return early below,
    // never reaching the normal blocks===0 end path. Whoever hits zero first fires rend; the referee then
    // finalizes for everyone (rres), so a stray unreachable block on the other screen can't hang the race.
    if (_raceStarted && !_raceEndSent && _runActive && !_levelEnded) {
      // Smashes mode ends the moment a player secures the MAJORITY of blocks (more than half — they can
      // no longer be caught), rather than waiting for the whole arena to be cleared. The referee finalizes
      // on rend with the top score as winner, which is that player. Coins mode (and the rare smashes tie
      // that never reaches majority) still ends on the arena emptying below.
      if (_RACE_METRIC === 'smashes' && _raceTotalBlocks > 0) {
        const goal = Math.floor(_raceTotalBlocks / 2) + 1;
        const top = _raceBoard.reduce((m, r) => Math.max(m, r.smashes || 0), 0);
        if (top >= goal) { _raceEndSent = true; if (_mp) _mp.send({ t: 'rend' }); }
      }
      if (!_raceEndSent) {
        if (blocks.length === 0) {
          if (_raceEmptyAt < 0) _raceEmptyAt = performance.now();
          else if (performance.now() - _raceEmptyAt >= 400) { _raceEndSent = true; if (_mp) _mp.send({ t: 'rend' }); }
        } else {
          _raceEmptyAt = -1;
        }
      }
    }
  }
  if (!_runActive || _levelEnded) return;
  if (_levelDurationSec === Infinity) {
    if (!_isPracticeLevel()) {
      _blocksLeftEl.style.display = 'none';
      return;  // sandbox never ends
    }
    if (blocks.length > 0) {
      _blocksLeftEl.textContent = `${blocks.length} TARGET${blocks.length === 1 ? '' : 'S'} LEFT`;
      _blocksLeftEl.style.display = 'block';
      _allBlocksClearedAt = -Infinity;
      return;
    }
    _blocksLeftEl.style.display = 'none';
    if (_allBlocksClearedAt === -Infinity) _allBlocksClearedAt = performance.now();
    if (performance.now() - _allBlocksClearedAt >= 500) _exitPracticeRoom();
    return;
  }
  if (!audioCtx) return;

  // Auto-cull "lost" blocks — anything that's tunneled below the floor or escaped well past
  // the arena walls is unreachable for the player and would otherwise prevent the level from
  // ever ending. Use a generous margin past the auto-fitted bounds so we only nuke true escapees,
  // not blocks resting against the wall edge.
  if (_currentBoundsAABB) {
    const CULL_MARGIN = 15;
    const minX = _currentBoundsAABB.minX - CULL_MARGIN;
    const maxX = _currentBoundsAABB.maxX + CULL_MARGIN;
    const minZ = _currentBoundsAABB.minZ - CULL_MARGIN;
    const maxZ = _currentBoundsAABB.maxZ + CULL_MARGIN;
    for (let i = blocks.length - 1; i >= 0; i--) {
      const b = blocks[i];
      const p = b.position;
      if (p.y < -10 || p.x < minX || p.x > maxX || p.z < minZ || p.z > maxZ) {
        if (b.userData.body) {
          _blockByBodyHandle.delete(b.userData.body.handle);
          physicsWorld.removeRigidBody(b.userData.body);
        }
        scene.remove(b);
        b.geometry.dispose();
        b.material.dispose();
        if (b.userData.crackTexture) b.userData.crackTexture.dispose();
        blocks.splice(i, 1);
      }
    }
  }

  // Update the "X blocks left" HUD. Hide once we hit zero — the end-trigger sequence handles UX.
  if (blocks.length > 0) {
    _blocksLeftEl.textContent = `${blocks.length} BLOCK${blocks.length === 1 ? '' : 'S'} LEFT`;
    _blocksLeftEl.style.display = 'block';
  } else {
    _blocksLeftEl.style.display = 'none';
  }

  if (!_endTriggered) {
    // Only end condition: every block is gone — broken AND any toppled rubble cleared.
    // (Previously this counted only anchored blocks, which let the level end while toppled
    //  rubble was still lying around. Players want to smash the rubble too.)
    if (blocks.length === 0) {
      if (_allBlocksClearedAt === -Infinity) _allBlocksClearedAt = performance.now();
      if (performance.now() - _allBlocksClearedAt >= 400) {
        _endTriggered = true;
        _endTriggeredAtMs = performance.now();
        _endReason = 'cleared';
      }
    } else {
      _allBlocksClearedAt = -Infinity;
    }
  }

  if (_endTriggered) {
    // Race: the arena emptied (or ran out of time). Tell the referee to finalize — the broadcast result
    // (rres) drives the end overlay for everyone. Skip the normal solo clear-screen entirely.
    if (_RACE_ON) {
      if (!_raceEndSent) { _raceEndSent = true; if (_mp) _mp.send({ t: 'rend' }); }
      return;
    }
    // Cash out floor chips ONCE at trigger — animated fly-to-counter. New chips that drop
    // after this (e.g. from post-trigger charge releases) intentionally don't get instant-
    // scooped; they sit until the hard-timeout sync-flush. That preserves the in-game feel
    // of dropping coins normally during a final flurry.
    if (!_endingCashedOut) {
      _endingCashedOut = true;
      _cashOutPhysicalCoins();
    }
    // Hard timeout: 5s after trigger, force-end regardless of pending state. _endLevel's
    // sync flush will scoop any leftover chips into the score.
    if (performance.now() - _endTriggeredAtMs > END_HARD_TIMEOUT_MS) { _endLevel(); return; }
    if (_pendingCoinAwards > 0) return;  // coins still in the air — hold the clear screen
    // Only block on chips that are FREE on the floor (not orbiting a fist mid-charge, not
    // queued for collection). orbiting/pendingCollect chips resolve themselves shortly and
    // shouldn't hang the clear screen.
    for (const c of debris) {
      const ud = c.userData;
      if (ud.isCurrency && !ud.orbiting && !ud.pendingCollect) return;
    }
    _endLevel();
  }
}

// =========================================================
// Clear screen — shown after _endLevel. Stars + breakdown + Retry / Level Select buttons.
// =========================================================
const _clearScreen = document.createElement('div');
_clearScreen.style.cssText = 'position:fixed;inset:0;display:none;align-items:center;justify-content:center;'
  + 'background:rgba(26,16,64,0.82);pointer-events:auto;z-index:1001;';
document.body.appendChild(_clearScreen);

// Active clear-screen star elements, captured in _showClearScreen and cleared on hide.
// _updateHudBeatAnchors reads this to apply a subtle scale pulse to the LIT stars on
// the beat — quieter than pulsing the whole panel, focused on the celebration moment.
let _activeClearStars = null;
function _hideClearScreen() {
  _clearScreen.style.display = 'none';
  _clearScreenRevealToken++;  // invalidate any in-flight reveal callbacks
  _clear3DConfetti();          // tear down any cubes still in the air
  _clearScreenLeaderboardSection = null;  // stop async score-submit callbacks updating a hidden screen
  _activeClearStars = null;
}

// Reveal sequence state. _revealToken increments on every show/hide so async phases
// from a previous show abandon themselves instead of writing into a torn-down panel.
let _clearScreenSkipped = false;
let _clearScreenRevealToken = 0;
const _sleep = (ms) => new Promise(r => setTimeout(r, ms));

function _mkClearButton(label, primary, onClick) {
  const b = document.createElement('button');
  b.textContent = label;
  b.className = 'ss-btn ' + (primary ? 'ss-btn--primary' : 'ss-btn--secondary');
  b.onclick = (e) => { e.stopPropagation(); onClick(); };
  return b;
}

function _renderEquipGlovesPage() {
  if (!_loadProgressionState().fullLoadoutFreedom && !_devUnlockAll) {
    _homeView = 'world-select';
    _renderHome();
    return;
  }
  const view = document.createElement('div');
  view.className = 'ss-world-view';
  const header = document.createElement('div');
  header.className = 'ss-world-view__header';
  const back = document.createElement('button');
  back.className = 'ss-world-view__back';
  back.textContent = 'BACK';
  back.onclick = (e) => {
    e.stopPropagation();
    _homeView = 'world-select';
    _renderHome();
  };
  const title = document.createElement('div');
  title.className = 'ss-world-view__title';
  title.textContent = 'EQUIP GLOVES';
  header.appendChild(back);
  header.appendChild(title);
  view.appendChild(header);

  const caption = document.createElement('div');
  caption.className = 'ss-home__caption';
  caption.textContent = 'Choose a glove for each hand, then press Done.';
  view.appendChild(caption);

  view.appendChild(_buildEquipGlovesPanel());
  _homeContent.appendChild(view);
}

function _openEquipGlovesFromClearScreen() {
  _hideClearScreen();
  lockHint.style.display = 'block';
  document.body.classList.remove('in-game');
  _currentWorld = null;
  _homeView = 'gloves';
  _renderHome();
}

function _makeUnlockRewardModel(reward) {
  const host = document.createElement('div');
  host.style.cssText = 'width:min(76vw,620px);height:min(42vw,320px);min-height:210px;'
    + 'position:relative;display:flex;align-items:center;justify-content:center;'
    + 'z-index:2;'
    + 'border:4px solid rgba(255,225,0,0.38);box-shadow:0 0 34px rgba(112,232,255,0.26),inset 0 0 40px rgba(255,255,255,0.08);'
    + 'background:radial-gradient(circle at 50% 45%,#fff27a 0,#ffe100 52%,#c99300 100%);'
    + 'filter:drop-shadow(0 22px 18px rgba(0,0,0,0.44));';

  const previewScene = new THREE.Scene();
  const previewCamera = new THREE.PerspectiveCamera(32, 620 / 320, 0.01, 100);

  const previewRenderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
  previewRenderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  previewRenderer.setSize(620, 320, false);
  previewRenderer.setClearColor(0x000000, 0);
  previewRenderer.domElement.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block;';
  host.appendChild(previewRenderer.domElement);

  previewRenderer.outputColorSpace = THREE.SRGBColorSpace;
  previewScene.environment = springMetalEnvMap;
  previewScene.add(new THREE.AmbientLight(0xfff0a8, 0.85));
  previewScene.add(new THREE.HemisphereLight(0xffffff, 0x2a3a58, 2.1));
  const key = new THREE.DirectionalLight(0xffffff, 4.2);
  key.position.set(1.4, 2.8, 2.2);
  previewScene.add(key);
  const rim = new THREE.DirectionalLight(0x8fffff, 2.6);
  rim.position.set(-2.4, 1.4, 1.8);
  previewScene.add(rim);
  const warmFill = new THREE.DirectionalLight(0xff7a2a, 1.4);
  warmFill.position.set(0.5, -0.2, 2.8);
  previewScene.add(warmFill);

  function poseEquipPreviewFist(previewFist, glove, side, x, y, t, shake) {
    if (!previewFist) return;
    const isSpring = glove === GLOVE_SPRING;
    const isRocket = glove === GLOVE_ROCKET;
    previewFist.scale.setScalar(isSpring ? 1.18 : 1.28);
    previewFist.position.x = x + Math.sin(t * 4.2 + (side > 0 ? Math.PI : 0)) * 0.012;
    previewFist.position.y = y - shake * 0.030;
    previewFist.position.z = 0;
    previewFist.rotation.set(
      Math.PI / 2 - shake * 0.016,
      0,
      0
    );
    if (previewFist.userData.forearmPivot) {
      previewFist.userData.forearmPivot.rotation.x = 0;
    }
    if (isRocket) {
      for (const part of previewFist.userData.themeParts || []) {
        part.visible = true;
        if (part.userData && part.userData.basePosition) {
          part.position.copy(part.userData.basePosition);
          part.position.y += 0.015;
          part.position.z -= 0.045;
        }
      }
    }
  }

  const previewLoadout = Array.isArray(reward?.previewLoadout)
    ? [
        normalizeGlove(reward.previewLoadout[0], GLOVE_BARE),
        normalizeGlove(reward.previewLoadout[1], GLOVE_BARE)
      ]
    : [reward && reward.glove ? reward.glove : GLOVE_BARE, GLOVE_BARE];
  const previewMuted = !!reward?.previewMuted;
  const previewEquipLayout = !!reward?.previewEquipLayout;
  const isDualPreview = previewEquipLayout || previewLoadout[1] !== GLOVE_BARE;
  const leftPreviewGlove = previewLoadout[0];
  const rightPreviewGlove = previewLoadout[1];
  const theme = previewEquipLayout ? 'equip' : (isDualPreview ? 'dual' : gloveTheme(leftPreviewGlove));
  const leftHasSpring = leftPreviewGlove === GLOVE_SPRING;
  const rightHasSpring = rightPreviewGlove === GLOVE_SPRING;
  const previewHasSpring = leftHasSpring || rightHasSpring;
  const previewSpringMat = previewHasSpring ? leftSpringMat.clone() : null;
  const previewSpringCuffMat = previewHasSpring ? leftSpringCuff.material.clone() : null;
  const previewSpringRings = [];
  const previewSpringVisual = new THREE.Group();
  if (leftHasSpring) {
    for (let i = 0; i < 24; i++) {
      const ring = new THREE.Mesh(new THREE.TorusGeometry(0.145, 0.018, 8, 24), previewSpringMat);
      previewSpringVisual.add(ring);
      previewSpringRings.push(ring);
    }
    const cuff = new THREE.Mesh(new THREE.TorusGeometry(0.135, 0.026, 10, 28), previewSpringCuffMat);
    previewSpringVisual.userData.cuff = cuff;
    previewSpringVisual.add(cuff);
    previewScene.add(previewSpringVisual);
  }
  const previewSpringRingsRight = [];
  const previewSpringVisualRight = new THREE.Group();
  if (rightHasSpring) {
    const rightMat = previewSpringMat.clone();
    const rightCuffMat = previewSpringCuffMat.clone();
    for (let i = 0; i < 24; i++) {
      const ring = new THREE.Mesh(new THREE.TorusGeometry(0.145, 0.018, 8, 24), rightMat);
      previewSpringVisualRight.add(ring);
      previewSpringRingsRight.push(ring);
    }
    const cuff = new THREE.Mesh(new THREE.TorusGeometry(0.135, 0.026, 10, 28), rightCuffMat);
    previewSpringVisualRight.userData.cuff = cuff;
    previewSpringVisualRight.add(cuff);
    previewScene.add(previewSpringVisualRight);
  }
  const blockGroup = new THREE.Group();
  const block = createBlockVisualMesh(0.58, isDualPreview ? 0.58 : 0.92, 0.18, 'brick');
  blockGroup.add(block);
  blockGroup.position.set(isDualPreview ? 0.44 : 1.05, isDualPreview ? 0.36 : -0.02, 0);
  previewScene.add(blockGroup);
  const rocketBlockGroup = new THREE.Group();
  if (isDualPreview) {
    const rocketBlock = createBlockVisualMesh(0.58, 0.58, 0.18, 'brick');
    rocketBlockGroup.add(rocketBlock);
    rocketBlockGroup.position.set(2.05, -0.38, 0);
    previewScene.add(rocketBlockGroup);
  }

  const hasRocketPreview = leftPreviewGlove === GLOVE_ROCKET || rightPreviewGlove === GLOVE_ROCKET;
  const speedLineMat = (theme === 'hook' || theme === 'dual' || (theme === 'equip' && hasRocketPreview))
    ? new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.82 })
    : null;
  const speedLines = [];
  const previewRocketFlames = [];
  if (theme === 'hook' || theme === 'dual' || (theme === 'equip' && hasRocketPreview)) {
    for (let i = 0; i < 14; i++) {
      const line = new THREE.Mesh(new THREE.BoxGeometry(0.42 + (i % 4) * 0.08, 0.02, 0.01), speedLineMat);
      line.position.set(-1.3, -0.34 + (i % 5) * 0.12, -0.24);
      previewScene.add(line);
      speedLines.push(line);
    }
  }

  const fist = createFist(leftHasSpring ? 'plain' : gloveTheme(leftPreviewGlove));
  fist.scale.setScalar(leftHasSpring ? 1.42 : 1.55);
  fist.rotation.set(0.04, -Math.PI / 2, 0.02);
  fist.position.set(leftPreviewGlove === GLOVE_ROCKET ? -0.48 : -1.03, isDualPreview ? 0.31 : (leftPreviewGlove === GLOVE_ROCKET ? -0.01 : 0.015), 0);
  if ((leftHasSpring || leftPreviewGlove === GLOVE_ROCKET) && fist.userData.forearmPivot) {
    fist.userData.forearmPivot.rotation.x = 0;
  }
  previewScene.add(fist);
  const rightFistPreview = isDualPreview ? createFist(rightHasSpring ? 'plain' : gloveTheme(rightPreviewGlove)) : null;
  if (rightFistPreview) {
    rightFistPreview.scale.setScalar(rightHasSpring ? 1.32 : 1.32);
    rightFistPreview.rotation.set(0.02, -Math.PI / 2, 0.02);
    rightFistPreview.position.set(rightPreviewGlove === GLOVE_SPRING ? -1.18 : -0.48, -0.32, 0);
    if (rightFistPreview.userData.forearmPivot) rightFistPreview.userData.forearmPivot.rotation.x = 0;
    previewScene.add(rightFistPreview);
  }
  previewCamera.position.set(0, 0.12, 4.25);
  previewCamera.lookAt(previewEquipLayout ? 0 : 0.08, -0.02, 0);

  let disposed = false;
  let wasConnected = false;
  const startedAt = performance.now();
  let lastPreviewHitCycle = -1;
  let lastDualPreviewHitBeat = -1;
  let lastEquipLeftHitCycle = -1;
  let lastEquipRightHitCycle = -1;
  const previewPunchProgress = (phase) => {
    if (phase >= 0.74) {
      const u = (phase - 0.74) / 0.26;
      return 1 - Math.pow(1 - u, 4);
    }
    if (phase < 0.12) return 1;
    const u = Math.min(1, (phase - 0.12) / 0.42);
    return Math.max(0, 1 - u * u * (3 - 2 * u));
  };
  const renderPreview = () => {
    if (disposed) return;
    if (!host.isConnected) {
      if (wasConnected && host._disposePreview) {
        host._disposePreview();
        return;
      }
      requestAnimationFrame(renderPreview);
      return;
    }
    wasConnected = true;
    const t = (performance.now() - startedAt) / 1000;
    const rawMusicT = (_musicStarted && audioCtx) ? (audioCtx.currentTime - _musicLoopStartTime) : null;
    const beatT = (theme === 'equip' && rawMusicT !== null && rawMusicT >= 0)
      ? rawMusicT
      : (_beatElapsed() ?? t);
    const previewPeriod = Math.max(0.25, MUSIC_BEAT * 2);
    const previewCycle = Math.floor(beatT / previewPeriod);
    const loop = ((beatT % previewPeriod) + previewPeriod) % previewPeriod / previewPeriod;
    const punchT = previewPunchProgress(loop);
    const dualBeatIndex = Math.floor(beatT / MUSIC_BEAT);
    const dualBeatPhase = ((beatT % MUSIC_BEAT) + MUSIC_BEAT) % MUSIC_BEAT / MUSIC_BEAT;
    if (!previewMuted && theme === 'dual' && dualBeatPhase < 0.12 && dualBeatIndex !== lastDualPreviewHitBeat) {
      lastDualPreviewHitBeat = dualBeatIndex;
      playSmashSound('brick', 0.78, false, null);
    } else if (!previewMuted && theme !== 'dual' && loop < 0.12 && previewCycle !== lastPreviewHitCycle) {
      lastPreviewHitCycle = previewCycle;
      playSmashSound('brick', 0.78, false, null);
    }
    let springEffectT = punchT;
    let rocketEffectT = punchT;
    if (theme === 'equip') {
      const leftLoop = loop;
      const rightLoop = (((beatT - MUSIC_BEAT) % previewPeriod) + previewPeriod) % previewPeriod / previewPeriod;
      const leftCycle = Math.floor(beatT / previewPeriod);
      const rightCycle = Math.floor((beatT - MUSIC_BEAT) / previewPeriod);
      const leftT = previewPunchProgress(leftLoop);
      const rightT = previewPunchProgress(rightLoop);
      const leftShake = Math.max(0, leftT - 0.86) / 0.14;
      const rightShake = Math.max(0, rightT - 0.86) / 0.14;
      springEffectT = leftT;
      rocketEffectT = rightT;

      const leftIsRocket = leftPreviewGlove === GLOVE_ROCKET;
      const rightIsRocket = rightPreviewGlove === GLOVE_ROCKET;
      const leftIsBare = leftPreviewGlove === GLOVE_BARE;
      const rightIsBare = rightPreviewGlove === GLOVE_BARE;
      const springRestY = -1.08;
      const bareRestY = 0.34;
      const bareTravel = 0.12;
      const rocketRestY = 0.02;
      const rocketBlockStartY = 1.34;
      const rocketBlockTravel = 1.04;
      const leftFistY = leftIsRocket ? rocketRestY : (leftIsBare ? bareRestY + leftT * bareTravel : springRestY + leftT * 1.38);
      const rightFistY = rightIsRocket ? rocketRestY : (rightIsBare ? bareRestY + rightT * bareTravel : springRestY + rightT * 1.38);
      const leftBlockY = leftIsRocket
        ? rocketBlockStartY - leftT * rocketBlockTravel + Math.cos(t * 54) * 0.010 * leftShake
        : 0.82 + Math.cos(t * 54) * 0.010 * leftShake;
      const rightBlockY = rightIsRocket
        ? rocketBlockStartY - rightT * rocketBlockTravel + Math.cos(t * 54) * 0.010 * rightShake
        : 0.82 + Math.cos(t * 54) * 0.010 * rightShake;

      const leftColumnX = -0.88;
      const rightColumnX = 0.88;

      poseEquipPreviewFist(fist, leftPreviewGlove, -1, leftColumnX, leftFistY, t, leftShake);
      blockGroup.position.x = leftColumnX + Math.sin(t * 60) * 0.012 * leftShake;
      blockGroup.position.y = leftBlockY;

      if (rightFistPreview) {
        poseEquipPreviewFist(rightFistPreview, rightPreviewGlove, 1, rightColumnX, rightFistY, t, rightShake);
      }
      rocketBlockGroup.position.x = rightColumnX + Math.sin(t * 60) * 0.012 * rightShake;
      rocketBlockGroup.position.y = rightBlockY;

      const playEquipBrickHit = (side, soundPos) => {
        const pitch = smashPitchMultiplier(1, 1);
        playSmashSound('brick', pitch, side === 1, soundPos);
      };
      if (leftLoop < 0.12 && leftCycle !== lastEquipLeftHitCycle) {
        lastEquipLeftHitCycle = leftCycle;
        playEquipBrickHit(0, blockGroup.position.clone());
      }
      if (rightLoop < 0.12 && rightCycle !== lastEquipRightHitCycle) {
        lastEquipRightHitCycle = rightCycle;
        playEquipBrickHit(1, rocketBlockGroup.position.clone());
      }

      for (let i = 0; i < speedLines.length; i++) {
        const line = speedLines[i];
        const lineUsesLeft = leftPreviewGlove === GLOVE_ROCKET && (rightPreviewGlove !== GLOVE_ROCKET || i % 2 === 0);
        const lineT = lineUsesLeft ? leftT : rightT;
        const source = lineUsesLeft ? fist : rightFistPreview;
        const travel = ((t * 5.2 + i * 0.09) % 1);
        line.visible = !!source && lineT > 0.04 && lineT < 1;
        if (!line.visible) continue;
        line.rotation.z = Math.PI / 2;
        line.position.x = source.position.x - 0.18 + ((i % 4) * 0.12);
        line.position.y = source.position.y - 0.64 - travel * 0.72;
        line.position.z = -0.42 + (i % 3) * 0.03;
        line.scale.x = 0.75 + lineT * 1.65;
        line.material.opacity = (0.36 + lineT * 0.46) * Math.max(0, 1 - travel * 0.85);
      }
    } else if (theme === 'dual') {
      const leftLoop = loop;
      const rightLoop = (((beatT - MUSIC_BEAT) % previewPeriod) + previewPeriod) % previewPeriod / previewPeriod;
      const leftT = previewPunchProgress(leftLoop);
      const rightT = previewPunchProgress(rightLoop);
      springEffectT = leftT;
      rocketEffectT = rightT;
      const leftShake = Math.max(0, leftT - 0.86) / 0.14;
      const rightShake = Math.max(0, rightT - 0.86) / 0.14;
      if (leftPreviewGlove === GLOVE_ROCKET) {
        fist.position.x = -0.52 + leftT * 0.90 - leftShake * 0.030;
        fist.position.y = 0.31 + Math.sin(t * 18) * 0.004 * (0.3 + leftT);
        fist.rotation.x = 0.01 - leftShake * 0.016;
      } else {
        fist.position.x = -1.18 + leftT * 1.10;
        fist.position.y = 0.36 + Math.sin(t * 12) * 0.004 * leftT;
        fist.rotation.x = 0.012 - leftT * 0.008;
      }
      if (leftPreviewGlove === GLOVE_ROCKET) {
        blockGroup.position.x = 2.12 - leftT * 1.74 + Math.sin(t * 65) * 0.012 * leftShake;
        blockGroup.position.y = 0.36 + Math.cos(t * 58) * 0.007 * leftShake;
      } else {
        blockGroup.position.x = 0.44;
        blockGroup.position.y = 0.36;
      }
      if (rightFistPreview) {
        if (rightPreviewGlove === GLOVE_SPRING) {
          rightFistPreview.position.x = -1.18 + rightT * 1.10;
          rightFistPreview.position.y = -0.38 + Math.sin(t * 12) * 0.004 * rightT;
          rightFistPreview.rotation.x = 0.012 - rightT * 0.008;
        } else {
          rightFistPreview.position.x = -0.48 + rightT * 0.90 - rightShake * 0.030;
          rightFistPreview.position.y = -0.38 + Math.sin(t * 18) * 0.004 * (0.3 + rightT);
          rightFistPreview.rotation.x = 0.01 - rightShake * 0.016;
        }
      }
      if (rightPreviewGlove === GLOVE_ROCKET) {
        rocketBlockGroup.position.x = 2.12 - rightT * 1.74 + Math.sin(t * 65) * 0.012 * rightShake;
        rocketBlockGroup.position.y = -0.38 + Math.cos(t * 58) * 0.007 * rightShake;
      } else {
        rocketBlockGroup.position.x = 0.44;
        rocketBlockGroup.position.y = -0.38;
      }
      for (let i = 0; i < speedLines.length; i++) {
        const line = speedLines[i];
        const travel = ((t * 5.2 + i * 0.09) % 1);
        const lineUsesLeft = leftPreviewGlove === GLOVE_ROCKET && (rightPreviewGlove !== GLOVE_ROCKET || i % 2 === 0);
        const lineT = lineUsesLeft ? leftT : rightT;
        line.visible = lineT > 0.04 && lineT < 1;
        const originX = lineUsesLeft ? fist.position.x : (rightFistPreview ? rightFistPreview.position.x : -0.48);
        const originY = lineUsesLeft ? fist.position.y : (rightFistPreview ? rightFistPreview.position.y : -0.38);
        line.position.x = originX - 0.34 - travel * 1.20;
        line.position.y = originY - 0.24 + ((i * 0.08) % 0.46);
        line.position.z = -0.28 + (i % 3) * 0.04;
        line.scale.x = 0.75 + lineT * 1.8;
        line.material.opacity = (0.40 + lineT * 0.48) * Math.max(0, 1 - travel * 0.85);
      }
    } else if (theme === 'hook') {
      const impactShake = Math.max(0, punchT - 0.86) / 0.14;
      fist.position.x = -0.48 - impactShake * 0.035;
      fist.position.y = -0.01 + Math.sin(t * 18) * 0.004 * (0.3 + punchT);
      fist.rotation.x = 0.01 - impactShake * 0.018;
      blockGroup.position.x = 2.28 - punchT * 1.82 + Math.sin(t * 65) * 0.014 * impactShake;
      blockGroup.position.y = -0.02 + Math.cos(t * 58) * 0.008 * impactShake;
      for (let i = 0; i < speedLines.length; i++) {
        const line = speedLines[i];
        const travel = ((t * 5.2 + i * 0.09) % 1);
        line.visible = punchT > 0.04 && punchT < 1;
        line.position.x = fist.position.x - 0.34 - travel * 1.35;
        line.position.y = fist.position.y - 0.28 + ((i * 0.11) % 0.56);
        line.position.z = -0.28 + (i % 3) * 0.04;
        line.scale.x = 0.9 + punchT * 2.2;
        line.material.opacity = (0.42 + punchT * 0.50) * Math.max(0, 1 - travel * 0.85);
      }
    } else {
      fist.position.x = -1.10 + punchT * 1.88;
      fist.position.y = 0.015 + Math.sin(t * 12) * 0.006 * punchT;
      fist.rotation.x = 0.015 - punchT * 0.01;
      blockGroup.position.x = 1.05;
      blockGroup.position.y = -0.02;
    }
    const drawPreviewSpring = (visual, rings, springFist, springPreviewT, anchorX, anchorOverride = null, tipOverride = null) => {
      if (!visual || !rings || rings.length === 0 || !springFist) return;
      const anchor = anchorOverride ? anchorOverride.clone() : new THREE.Vector3(anchorX, springFist.position.y + 0.012, 0);
      const tip = tipOverride ? tipOverride.clone() : new THREE.Vector3(springFist.position.x - 0.16, springFist.position.y + 0.012, 0);
      const dir = tip.clone().sub(anchor);
      const springLen = dir.length();
      if (springLen > 0.001) dir.multiplyScalar(1 / springLen);
      const quat = new THREE.Quaternion().setFromUnitVectors(_zAxis, dir);
      const waveSide = new THREE.Vector3(dir.z, 0, -dir.x);
      if (waveSide.lengthSq() < 0.0001) waveSide.set(1, 0, 0);
      waveSide.normalize();
      const waveUp = new THREE.Vector3().crossVectors(waveSide, dir);
      if (waveUp.lengthSq() < 0.0001) waveUp.set(0, 1, 0);
      waveUp.normalize();
      const visibleCount = Math.min(rings.length, Math.max(6, Math.ceil(springLen / 0.12)));
      const waveAmp = LEFT_SPRING_WAVE_AMP * 0.45 * Math.sin(springPreviewT * Math.PI);
      const wavePhase = performance.now() * LEFT_SPRING_WAVE_SPEED;
      for (let i = 0; i < rings.length; i++) {
        const ring = rings[i];
        ring.visible = i < visibleCount;
        if (!ring.visible) continue;
        const rt = (i + 0.35) / (visibleCount + 0.45);
        ring.position.lerpVectors(anchor, tip, rt);
        ring.position.y += LEFT_SPRING_RING_OFFSET_Y + LEFT_SPRING_BACK_LIFT_Y * Math.pow(1 - rt, 1.6);
        ring.position.z += LEFT_SPRING_BACK_PULL_Z * Math.pow(1 - rt, 1.4);
        const endFade = Math.sin(Math.PI * Math.min(1, Math.max(0, rt)));
        const wave = Math.sin(i * LEFT_SPRING_WAVE_SPACING - wavePhase) * waveAmp * endFade;
        const lift = Math.cos(i * LEFT_SPRING_WAVE_SPACING - wavePhase * 0.85) * waveAmp * 0.35 * endFade;
        ring.position.addScaledVector(waveSide, wave);
        ring.position.addScaledVector(waveUp, lift);
        ring.quaternion.copy(quat);
        ring.scale.setScalar(1 + springPreviewT * 0.08);
        ring.material.emissive.setHex(0x244f55);
        ring.material.emissiveIntensity = 0.08 + springPreviewT * 0.18;
      }
      const cuff = visual.userData.cuff;
      if (cuff) {
        cuff.visible = true;
        cuff.position.copy(tip);
        cuff.position.y += LEFT_SPRING_RING_OFFSET_Y;
        cuff.quaternion.copy(quat);
        cuff.scale.setScalar(1 + springPreviewT * 0.04);
        cuff.material.emissive.setHex(0x244f55);
        cuff.material.emissiveIntensity = 0.06 + springPreviewT * 0.10;
      }
    };
    if (leftHasSpring) {
      if (previewEquipLayout) {
        drawPreviewSpring(
          previewSpringVisual,
          previewSpringRings,
          fist,
          springEffectT,
          0,
          new THREE.Vector3(fist.position.x, -1.42, 0),
          new THREE.Vector3(fist.position.x, fist.position.y - 0.16, 0)
        );
      } else {
        drawPreviewSpring(previewSpringVisual, previewSpringRings, fist, isDualPreview ? springEffectT : punchT, isDualPreview ? -2.65 : -3.05);
      }
    }
    if (rightHasSpring) {
      if (previewEquipLayout) {
        drawPreviewSpring(
          previewSpringVisualRight,
          previewSpringRingsRight,
          rightFistPreview,
          rocketEffectT,
          0,
          new THREE.Vector3(rightFistPreview.position.x, -1.42, 0),
          new THREE.Vector3(rightFistPreview.position.x, rightFistPreview.position.y - 0.16, 0)
        );
      } else {
        drawPreviewSpring(previewSpringVisualRight, previewSpringRingsRight, rightFistPreview, isDualPreview ? rocketEffectT : punchT, -2.65);
      }
    }
    if (leftPreviewGlove === GLOVE_ROCKET || rightPreviewGlove === GLOVE_ROCKET) {
      const pulseRocket = (rocketFist, rocketPreviewT) => {
        for (const part of rocketFist?.userData.themeParts || []) {
          if (!part.userData.isRocketFlame) continue;
          const layer = part.userData.flameLayer || {};
          const phase = layer.phase || 0;
          const pulse = 1.08 + rocketPreviewT * 0.95 + Math.sin(t * 15 + phase) * 0.12;
          part.scale.setScalar((layer.baseScale || 1) * pulse);
          if (part.material && part.material.opacity !== undefined) {
            part.material.opacity = Math.min(0.78, 0.34 + rocketPreviewT * 0.26);
          }
        }
      };
      if (leftPreviewGlove === GLOVE_ROCKET) pulseRocket(fist, theme === 'dual' ? springEffectT : punchT);
      if (rightPreviewGlove === GLOVE_ROCKET) pulseRocket(rightFistPreview, theme === 'dual' ? rocketEffectT : punchT);
    }
    previewRenderer.render(previewScene, previewCamera);
    requestAnimationFrame(renderPreview);
  };
  renderPreview();

  host._disposePreview = () => {
    disposed = true;
    previewScene.traverse((obj) => {
      if (obj.geometry) obj.geometry.dispose();
      if (obj.material) {
        const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
        for (const mat of mats) {
          if (mat === _blockEdgeMaterial) continue;
          mat.dispose();
        }
      }
    });
    previewRenderer.dispose();
  };
  return host;
}

function _practiceModeForReward(reward) {
  if (!reward) return 'spring';
  const loadout = reward.previewLoadout || [];
  if (loadout[0] === GLOVE_SPRING && loadout[1] === GLOVE_SPRING) return 'double-spring';
  if (loadout[0] === GLOVE_ROCKET && loadout[1] === GLOVE_ROCKET) return 'double-rocket';
  if (reward.kind === 'freedom') return 'dual';
  if (reward.glove === GLOVE_ROCKET) return 'rocket';
  return 'spring';
}

function _startPracticeFromReward(rewardBox, prize, reward) {
  const mode = _practiceModeForReward(reward);
  if (prize && prize._disposePreview) prize._disposePreview();
  rewardBox.remove();
  _hideClearScreen();
  lockHint.style.display = 'none';
  document.body.classList.add('in-game');
  _enterLevel(`practice-${mode}`);
}

function _rewardPrimaryButtonLabel(reward) {
  if (!reward || !reward.nextLevelId) return 'Continue';
  const loadout = reward.nextLoadout || reward.previewLoadout || [];
  if (loadout[0] === GLOVE_SPRING && loadout[1] === GLOVE_ROCKET) return 'Play Next Level With Spring + Rocket';
  if (loadout[0] === GLOVE_SPRING && loadout[1] === GLOVE_SPRING) return 'Play Next Level With Double Springs';
  if (loadout[0] === GLOVE_ROCKET && loadout[1] === GLOVE_ROCKET) return 'Play Next Level With Double Rockets';
  if (reward.kind === 'freedom') return 'Play Next Level With Your Loadout';
  if (loadout.includes(GLOVE_SPRING)) return 'Play Next Level With Spring Fist';
  if (loadout.includes(GLOVE_ROCKET)) return 'Play Next Level With Rocket Fist';
  return 'Play Next Level';
}

function _playNextFromReward(rewardBox, prize, reward, resolve) {
  const nextLevelId = reward && reward.nextLevelId;
  if (!nextLevelId) { resolve(); return; }
  if (prize && prize._disposePreview) prize._disposePreview();
  rewardBox.remove();
  _hideClearScreen();
  lockHint.style.display = 'none';
  document.body.classList.add('in-game');
  _enterLevel(nextLevelId);
  resolve();
}

function _showUnlockRewardPanel(root, reward) {
  return new Promise((resolve) => {
    if (!reward) { resolve(); return; }
    const isRocket = reward.glove === GLOVE_ROCKET;
    const accent = isRocket ? '#ff5b2e' : '#70e8ff';
    const glow = isRocket ? 'rgba(255,91,46,0.55)' : 'rgba(112,232,255,0.55)';
    const rewardBox = document.createElement('div');
    rewardBox.style.cssText = 'position:fixed;inset:0;z-index:2000;display:flex;flex-direction:column;'
      + 'align-items:center;justify-content:center;gap:18px;padding:24px;text-align:center;'
      + 'background:radial-gradient(circle at 50% 42%,' + glow + ' 0,rgba(9,10,18,0.94) 34%,rgba(5,6,12,0.98) 100%);'
      + 'border:0;box-shadow:inset 0 0 0 8px var(--col-ink);';
    const kicker = document.createElement('div');
    kicker.className = 'ss-heading';
    kicker.style.cssText = 'font-size:clamp(32px,7vw,88px);line-height:0.9;color:#ffe100;'
      + 'text-shadow:5px 5px 0 var(--col-ink),0 0 26px ' + glow + ';';
    kicker.textContent = reward.title;

    const prize = _makeUnlockRewardModel(reward);

    const title = document.createElement('div');
    title.className = 'ss-heading';
    title.style.cssText = 'font-size:clamp(22px,4vw,44px);color:#ff3b7f;text-shadow:4px 4px 0 var(--col-ink);';
    title.textContent = reward.body;
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:12px;flex-wrap:wrap;justify-content:center;margin-top:4px;';
    const goHome = () => {
      if (prize._disposePreview) prize._disposePreview();
      rewardBox.remove();
      _hideClearScreen();
      lockHint.style.display = 'block';
      document.body.classList.remove('in-game');
      _currentWorld = null;
      _homeView = 'world-select';
      _renderHome();
      resolve();
    };
    const playNext = () => _playNextFromReward(rewardBox, prize, reward, resolve);
    row.appendChild(_mkClearButton(_rewardPrimaryButtonLabel(reward), true, playNext));
    row.appendChild(_mkClearButton('Home', false, goHome));
    rewardBox.appendChild(kicker);
    rewardBox.appendChild(prize);
    rewardBox.appendChild(title);
    rewardBox.appendChild(row);
    root.appendChild(rewardBox);
  });
}

function _showClearScreen({ stars, baseScore, timeBonus, totalScore, endReason, submitPromise, unlockReward = null }) {
  _clearScreen.textContent = '';
  _clearScreenSkipped = false;
  _clearScreenRevealToken++;
  const myToken = _clearScreenRevealToken;
  const passed = stars >= 1;

  const panel = document.createElement('div');
  panel.className = 'ss-panel';
  panel.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:22px;min-width:420px;'
    + 'position:relative;z-index:2;';

  // Heading + subtitle as a tight stack. Subtitle reuses the 1★ threshold for the fail message
  // so the player sees the exact number they need to beat next time.
  const headBlock = document.createElement('div');
  headBlock.className = 'ss-headblock';
  const heading = document.createElement('div');
  heading.className = 'ss-heading' + (passed ? '' : ' ss-heading--muted');
  heading.textContent = passed ? 'level cleared' : 'level failed';
  const subheading = document.createElement('div');
  subheading.className = 'ss-subheading';
  if (passed) {
    subheading.textContent = 'Smashing! You hit the goal';
  } else if (endReason === 'cleared') {
    subheading.textContent = `All blocks down. Hit the beat for more coins. Need ${_levelStarThresholds.one}`;
  } else {
    subheading.textContent = `Song ran out. Keep smashing for ${_levelStarThresholds.one}`;
  }
  headBlock.appendChild(heading);
  headBlock.appendChild(subheading);
  panel.appendChild(headBlock);

  // Stars start hidden (all empty). Earned ones get stamped in during the reveal.
  const starRow = document.createElement('div');
  starRow.className = 'ss-stars';
  const starEls = [];
  for (let i = 0; i < 3; i++) {
    const s = document.createElement('span');
    s.className = 'ss-stars__off';
    s.textContent = '☆';
    s.style.transformOrigin = 'center';
    s.style.display = 'inline-block';  // needed for transform on span
    starRow.appendChild(s);
    starEls.push(s);
  }
  panel.appendChild(starRow);
  // Expose to the HUD beat-anchor updater so lit stars pulse on the quarter beat.
  _activeClearStars = starEls;

  // Table is built with placeholders so the row layout doesn't shift during reveal.
  // Coins value starts at 0 and tickers up; the other rows show "—" until stamped.
  const table = document.createElement('div');
  table.className = 'ss-table';
  const valueEls = {};
  const rows = [
    ['Coins',      'coins', '0',  false],
    ['Time bonus', 'time',  '—',  false],
    ['Total',      'total', '—',  true],
  ];
  for (const [label, key, placeholder, isTotal] of rows) {
    const l = document.createElement('div');
    l.className = 'ss-table__cell ss-table__cell--label';
    l.textContent = label;
    const v = document.createElement('div');
    v.className = 'ss-table__cell ss-table__cell--val';
    v.textContent = placeholder;
    if (isTotal) {
      l.classList.add('ss-table__cell--total');
      v.classList.add('ss-table__cell--total');
    }
    table.appendChild(l);
    table.appendChild(v);
    valueEls[key] = v;
  }
  panel.appendChild(table);

  const goals = document.createElement('div');
  goals.className = 'ss-goals';
  const goalsLabel = document.createElement('span');
  goalsLabel.className = 'ss-goals__label';
  goalsLabel.textContent = 'Goals';
  goals.appendChild(goalsLabel);
  const goalData = [
    [1, _levelStarThresholds.one],
    [2, _levelStarThresholds.two],
    [3, _levelStarThresholds.three],
  ];
  for (const [n, score] of goalData) {
    const item = document.createElement('span');
    item.className = 'ss-goals__item';
    const stars = document.createElement('span');
    stars.className = 'ss-goals__stars';
    stars.textContent = '★'.repeat(n);
    const sc = document.createElement('span');
    sc.className = 'ss-goals__score';
    sc.textContent = String(score);
    item.appendChild(stars);
    item.appendChild(sc);
    goals.appendChild(item);
  }
  panel.appendChild(goals);

  // UGC leaderboard surface — only on UGC levels. Hidden until the existing clear-screen
  // reveal sequence completes; then it animates in with a celebration based on the diff
  // between before-submit and after-submit topScores. Hidden so it doesn't compete with
  // the score reveal beats.
  _clearScreenLeaderboardSection = null;
  if (_currentLevel === 'ugc-current' && _ugcId) {
    const lb = document.createElement('div');
    // display:none initially — _runClearScreenReveal flips this on as a final phase.
    lb.style.cssText = 'display:none;flex-direction:column;align-items:center;gap:6px;'
      + 'padding:14px 22px;background:var(--col-ink, #1a1a2e);border:3px solid var(--col-ink, #1a1a2e);'
      + 'box-shadow:var(--shadow-hard-sm);min-width:340px;max-width:90vw;';
    const lbTitle = document.createElement('div');
    lbTitle.style.cssText = 'font-family:var(--font-display);font-size:11px;letter-spacing:2px;'
      + 'color:var(--col-gold, #ffd66b);text-transform:uppercase;';
    lbTitle.textContent = 'Leaderboard';
    lb.appendChild(lbTitle);
    // Creator attribution — small line under the title so the player sees who they just
    // played a level by. Falls back to "anonymous" for legacy / opted-out creators.
    const lbByLine = document.createElement('div');
    lbByLine.style.cssText = 'font-family:ui-monospace,monospace;font-size:11px;color:#cfd6dc;opacity:0.6;letter-spacing:0.5px;';
    const lbCreator = (_ugcCreatorName && _ugcCreatorName.trim()) || 'anonymous';
    lbByLine.textContent = `by ${lbCreator}`;
    lb.appendChild(lbByLine);
    const lbBody = document.createElement('div');
    lbBody.style.cssText = 'font-family:ui-monospace,monospace;font-size:13px;color:#cfd6dc;text-align:center;min-height:60px;display:flex;flex-direction:column;align-items:center;gap:4px;';
    lb.appendChild(lbBody);
    panel.appendChild(lb);
    _clearScreenLeaderboardSection = {
      wrap: lb, body: lbBody, ugcId: _ugcId,
      yourScore: totalScore,
      yourName: _loadPlayerName(),
      beforeScores: null,    // captured by the BEFORE fetch below
      submitPromise,         // resolves to topScores AFTER submission, or null on no-submit
      beforeFetch: null,     // promise that resolves once beforeScores is captured
    };
    // BEFORE fetch — snapshot the pre-submit leaderboard so the celebration phase can diff.
    _clearScreenLeaderboardSection.beforeFetch = (async (sec) => {
      try {
        const res = await fetch(`${UGC_SERVER_URL}/play/${encodeURIComponent(sec.ugcId)}`);
        if (!res.ok) throw new Error('fetch failed');
        const data = await res.json();
        sec.beforeScores = Array.isArray(data.topScores) ? data.topScores : [];
      } catch {
        sec.beforeScores = [];
      }
    })(_clearScreenLeaderboardSection);
  }

  const buttonRow = document.createElement('div');
  buttonRow.className = 'ss-disabled';  // unlocked at the end of the reveal sequence
  buttonRow.style.cssText = 'display:flex;gap:18px;margin-top:6px;transition:opacity 200ms steps(3),filter 200ms steps(3);';
  buttonRow.appendChild(_mkClearButton('Retry',        true,  () => { _hideClearScreen(); _retryLevel(); }));
  if (_loadProgressionState().fullLoadoutFreedom) {
    buttonRow.appendChild(_mkClearButton('Change Gloves', false, _openEquipGlovesFromClearScreen));
  }
  buttonRow.appendChild(_mkClearButton('Level Select', false, () => { _hideClearScreen(); _renderHome(); lockHint.style.display = 'block'; document.body.classList.remove('in-game'); }));
  panel.appendChild(buttonRow);

  // Click anywhere on the backdrop while the reveal is running = snap to final state.
  // After buttons unlock, this still flips the flag but no in-flight phase reads it.
  _clearScreen.onclick = () => { _clearScreenSkipped = true; };

  _clearScreen.appendChild(panel);
  _clearScreen.style.display = 'flex';

  _runClearScreenReveal({ myToken, stars, baseScore, timeBonus, totalScore, valueEls, starEls, buttonRow, panel, unlockReward });
}

// ---- Reveal sequence ----
// Phases: coin rain → coin ticker (with arpeggio) → time bonus stamp → total stamp →
// star stamps (one per earned star) → unlock buttons. Each phase honors _clearScreenSkipped
// and _clearScreenRevealToken so a stale reveal from a prior show abandons cleanly.
// Per-star material + scale note. Ascending A minor triad (A5 → C6 → E6) so the three
// stamps form a "ta-DA!" arpeggio resolution. Each material's natural smash timbre carries
// the character; the layered coin clink pins the exact pitch onto the song's scale.
const _STAR_BEATS = [
  { material: 'crate', noteHz: 880.00 },   // A5 — root
  { material: 'brick', noteHz: 1046.50 },  // C6 — minor third
  { material: 'metal', noteHz: 1318.51 },  // E6 — fifth
];

// Snap to the next beat boundary so the rest of the sequence rides on the music grid.
async function _waitForNextBeat() {
  if (!_musicStarted || !audioCtx) return;
  const elapsed = audioCtx.currentTime - _musicLoopStartTime;
  const offset = MUSIC_BEAT - (elapsed % MUSIC_BEAT);
  await _sleep(offset * 1000);
}

async function _runClearScreenReveal({ myToken, stars, baseScore, timeBonus, totalScore, valueEls, starEls, buttonRow, panel, unlockReward = null }) {
  const isStale = () => myToken !== _clearScreenRevealToken;
  const skipped = () => _clearScreenSkipped || isStale();
  const beatMs = MUSIC_BEAT * 1000;  // 500ms at 120 BPM (falls back to fixed ms if music isn't running)

  // Align the whole sequence to the song grid by waiting for the next beat boundary.
  await _waitForNextBeat();
  if (isStale()) return;

  // Phase 1: coin rain. Spawn over 1 beat (last coin spawned at beat 1). Coins fall ~650-950ms
  // so the last one is absorbed by ~beat 3. We then hold a ~0.5-beat pause before the ticker
  // so the rain visibly "completes" before the next phase begins.
  _spawnCoinRain(panel, myToken, 36, beatMs);
  await _sleep(beatMs * 3.5);
  if (isStale()) return;

  // Phase 2: ticker climbs Coins over 1 beat with the arpeggio crescendo.
  if (!skipped()) playChargeRelease(1.0, null);
  await _animateTicker(valueEls.coins, 0, baseScore, beatMs, myToken);
  if (isStale()) return;

  // Phase 3: time bonus stamp on the next beat.
  await _sleep(skipped() ? 0 : beatMs);
  if (isStale()) return;
  if (!skipped()) playSmashSound('brick', 1.0, false, null);
  _stampValue(valueEls.time, String(timeBonus), skipped());

  // Phase 4: total stamp on the next beat.
  await _sleep(skipped() ? 0 : beatMs);
  if (isStale()) return;
  if (!skipped()) playSmashSound('brick', 0.7, true, null);
  _stampValue(valueEls.total, String(totalScore), skipped());

  // Phase 5: drumroll pause, then one star per beat (crate → brick → metal, A-C-E ascending).
  await _sleep(skipped() ? 0 : beatMs * 2);
  if (isStale()) return;
  for (let i = 0; i < stars; i++) {
    if (isStale()) return;
    if (!skipped()) {
      const beat = _STAR_BEATS[i];
      playSmashSound(beat.material, 1.0, false, null);
      // Layer a clean scale-note clink so the pitch reads melodically over the smash timbre.
      if (audioCtx) playCoinClink(0.7, null, beat.noteHz, audioCtx.currentTime + 0.005);
    }
    starEls[i].className = 'ss-stars__on';
    starEls[i].textContent = '★';
    _stampElement(starEls[i], skipped());
    if (!skipped()) _burstConfetti(_STAR_BEATS[i].material);
    if (i < stars - 1) await _sleep(skipped() ? 0 : beatMs);
  }

  // Phase 6: leaderboard reveal — only for UGC levels (section was set up in _showClearScreen).
  // Awaits before-fetch + submit-promise, then plays the appropriate celebration.
  // Phase 6 happens before the buttons unlock so the player watches the celebration before
  // tapping RETRY/Level Select. Skip-on-click cuts this phase short instantly — the helper
  // gets isStale (bail) and skipped (fast-forward) as separate signals.
  if (!isStale() && _clearScreenLeaderboardSection) {
    await _sleep(skipped() ? 0 : beatMs);
    if (!isStale()) await _runLeaderboardCelebration(_clearScreenLeaderboardSection, isStale, skipped);
  }

  // Phase 7: progression reward reveal, if this clear unlocked a new glove/loadout tier.
  if (!isStale() && unlockReward) {
    await _sleep(skipped() ? 0 : beatMs);
    if (!isStale()) {
      if (!skipped()) {
        if (audioCtx) playCoinClink(1.1, null, 1318.51, audioCtx.currentTime + 0.005);
        _burstConfetti(unlockReward.glove === GLOVE_ROCKET ? 'metal' : 'crate');
      }
      await _showUnlockRewardPanel(_clearScreen, unlockReward);
    }
  }

  // Phase 8: unlock buttons one beat after the leaderboard celebration/reward check.
  if (isStale()) return;
  await _sleep(skipped() ? 0 : beatMs);
  if (isStale()) return;
  buttonRow.classList.remove('ss-disabled');
}

function _spawnCoinRain(panel, myToken, count, spawnDurMs) {
  const startedAt = performance.now();
  let spawned = 0;
  function tick() {
    if (myToken !== _clearScreenRevealToken) return;  // stale show — bail
    if (_clearScreenSkipped) return;                  // skip — stop spawning new coins
    const elapsed = performance.now() - startedAt;
    const target = Math.min(count, Math.floor((elapsed / spawnDurMs) * count));
    while (spawned < target) {
      _spawnCoin(panel);
      spawned++;
    }
    if (elapsed < spawnDurMs) requestAnimationFrame(tick);
  }
  tick();
}

// Each coin spawns above the screen aligned with the panel's horizontal range, falls toward
// a random point in the upper portion of the panel, then fades+shrinks as it "lands" — reads
// as the coin being absorbed into the window rather than passing through to the screen edge.
function _spawnCoin(panel) {
  const rect = panel.getBoundingClientRect();
  // Spawn within panel x-range with a small inset so coins don't graze the chunky border.
  const xInset = 12;
  const spawnX = rect.left + xInset + Math.random() * (rect.width - 2 * xInset);
  // Target landing y: top portion of the panel (top 55%).
  const landY = rect.top + 8 + Math.random() * (rect.height * 0.55);
  const fallDist = landY + 30;  // coin starts at top:-30
  const drift = (Math.random() - 0.5) * 40;  // small horizontal sway during fall
  const rotEnd = (Math.random() - 0.5) * 540;

  const coin = document.createElement('div');
  coin.className = 'ss-coin';
  coin.style.left = (spawnX - 13) + 'px';  // 13 = half coin width (26px)
  coin.style.top = '-30px';
  _clearScreen.appendChild(coin);

  const dur = 650 + Math.random() * 300;
  const anim = coin.animate([
    { transform: 'translate(0, 0) rotate(0deg) scale(1)', opacity: 1 },
    { transform: `translate(${drift * 0.5}px, ${fallDist * 0.75}px) rotate(${rotEnd * 0.75}deg) scale(1)`, opacity: 1, offset: 0.75 },
    { transform: `translate(${drift}px, ${fallDist}px) rotate(${rotEnd}deg) scale(0.4)`, opacity: 0, offset: 1 },
  ], { duration: dur, easing: 'cubic-bezier(0.4, 0, 0.7, 1)', fill: 'forwards' });
  anim.onfinish = () => coin.remove();

  // Quiet clink on ~30% of coins, timed near landing so the sound matches the fade.
  if (Math.random() < 0.3) {
    setTimeout(() => { if (!_clearScreenSkipped) playCoinClink(0.35, null); }, dur * 0.75);
  }
}

async function _animateTicker(el, from, to, durMs, myToken) {
  const start = performance.now();
  return new Promise((resolve) => {
    function step() {
      if (myToken !== _clearScreenRevealToken) { resolve(); return; }
      if (_clearScreenSkipped) { el.textContent = String(to); resolve(); return; }
      const t = Math.min(1, (performance.now() - start) / durMs);
      const eased = 1 - Math.pow(1 - t, 2);  // ease-out quad
      el.textContent = String(Math.floor(from + (to - from) * eased));
      if (t < 1) requestAnimationFrame(step);
      else { el.textContent = String(to); resolve(); }
    }
    step();
  });
}

function _stampValue(el, finalText, skipAnim) {
  el.textContent = finalText;
  if (skipAnim) return;
  el.animate([
    { transform: 'scale(1.7) rotate(-3deg)', opacity: 0 },
    { transform: 'scale(0.92) rotate(1deg)', opacity: 1, offset: 0.55 },
    { transform: 'scale(1) rotate(0deg)',    opacity: 1 },
  ], { duration: 240, easing: 'cubic-bezier(0.4,0,0.2,1)' });
}

function _stampElement(el, skipAnim) {
  if (skipAnim) return;
  el.animate([
    { transform: 'scale(2.4) rotate(-14deg)', opacity: 0 },
    { transform: 'scale(0.85) rotate(5deg)',  opacity: 1, offset: 0.55 },
    { transform: 'scale(1) rotate(0deg)',     opacity: 1 },
  ], { duration: 300, easing: 'cubic-bezier(0.4,0,0.2,1)' });
}

// =========================================================
// 3D confetti — dedicated Three.js overlay scene that erupts real textured cubes from each
// star's on-screen position. Owns its own renderer/camera/canvas (transparent, z-index above
// the clear-screen panel) so the cubes render IN FRONT of the UI. Camera is sized so 1 world
// unit ≈ 1 screen pixel at z=0, which lets us spawn at exact star pixel coordinates.
// Lazy-initialized on first burst; particles self-cleanup on lifetime expiry.
// =========================================================
const _CONFETTI_PER_BURST = 24;
const _CONFETTI_GRAVITY = 1400;     // world-units / s² ≈ pixels / s²
const _CONFETTI_CUBE_PX = 30;       // edge length in screen pixels
let _conf3DRenderer = null;
let _conf3DScene = null;
let _conf3DCamera = null;
let _conf3DCanvas = null;
let _conf3DRafId = 0;
let _conf3DLastTime = 0;
const _conf3DParticles = [];        // { mesh, velocity, angVel, life, totalLife }
const _conf3DBoxGeom = new THREE.BoxGeometry(1, 1, 1);
const _conf3DMaterials = {};

function _init3DConfetti() {
  if (_conf3DRenderer) return;
  _conf3DRenderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
  _conf3DRenderer.setPixelRatio(window.devicePixelRatio);
  _conf3DCanvas = _conf3DRenderer.domElement;
  // Set positioning BEFORE setSize. setSize writes inline style.width/height in CSS px;
  // if we wrote style.cssText AFTER setSize, those dims would be wiped and the canvas
  // would fall back to its intrinsic drawing-buffer size (innerWidth × DPR), shifting the
  // world origin off-screen on HiDPI displays. Reordering keeps both correct.
  _conf3DCanvas.style.cssText = 'position:absolute;left:0;top:0;pointer-events:none;z-index:1;display:none;';
  _conf3DRenderer.setSize(window.innerWidth, window.innerHeight);
  // z-index 1 = above the dim backdrop, BEHIND the panel (z-index 2). Cubes spawn at world
  // (0,0) which projects to canvas center = panel center, and radiate symmetrically.
  _clearScreen.appendChild(_conf3DCanvas);

  _conf3DScene = new THREE.Scene();
  // Camera: distance set so 1 world unit = 1 px at z=0 plane.
  const fov = 35;
  const dist = window.innerHeight / (2 * Math.tan(THREE.MathUtils.degToRad(fov) / 2));
  _conf3DCamera = new THREE.PerspectiveCamera(fov, window.innerWidth / window.innerHeight, 0.1, dist * 4);
  _conf3DCamera.position.set(0, 0, dist);
  _conf3DCamera.lookAt(0, 0, 0);

  // Lighting — directional key + fill so the textured faces show shading as cubes tumble.
  _conf3DScene.add(new THREE.AmbientLight(0xffffff, 0.55));
  const key = new THREE.DirectionalLight(0xffffff, 1.0);
  key.position.set(2, 4, 5);
  _conf3DScene.add(key);

  window.addEventListener('resize', () => {
    if (!_conf3DRenderer) return;
    _conf3DRenderer.setSize(window.innerWidth, window.innerHeight);
    const d = window.innerHeight / (2 * Math.tan(THREE.MathUtils.degToRad(fov) / 2));
    _conf3DCamera.aspect = window.innerWidth / window.innerHeight;
    _conf3DCamera.position.z = d;
    _conf3DCamera.updateProjectionMatrix();
  });
}

function _get3DConfettiMaterial(material) {
  if (_conf3DMaterials[material]) return _conf3DMaterials[material];
  const tex = new THREE.CanvasTexture(getBaseTextureCanvas(material));
  tex.colorSpace = THREE.SRGBColorSpace;
  const mat = new THREE.MeshStandardMaterial({
    map: tex,
    roughness: material === 'metal' ? 0.45 : 0.85,
    metalness: material === 'metal' ? 0.55 : 0.0,
  });
  _conf3DMaterials[material] = mat;
  return mat;
}

function _burstConfetti(material) {
  _init3DConfetti();
  // Re-attach the canvas every burst — _showClearScreen wipes _clearScreen.textContent at the
  // top of each clear, which orphans the canvas after level 1. appendChild is a no-op when the
  // node is already a child, so this only does work on levels 2+.
  _clearScreen.appendChild(_conf3DCanvas);
  _conf3DCanvas.style.display = 'block';

  // Bursts spawn at the top of the screen (centered horizontally). Cubes pour downward
  // through the panel area — hidden behind it, emerging from its sides and bottom edges.
  // wy = innerHeight/2 - 50 keeps origin just inside the viewport top.
  const wx = 0;
  const wy = window.innerHeight / 2 - 50;
  const mat = _get3DConfettiMaterial(material);

  for (let i = 0; i < _CONFETTI_PER_BURST; i++) {
    const mesh = new THREE.Mesh(_conf3DBoxGeom, mat);
    mesh.scale.setScalar(_CONFETTI_CUBE_PX);
    mesh.position.set(wx, wy, 0);
    mesh.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI);

    // Spawn is at the top of the screen, so radial-with-downward-bias gives a "pouring
    // fountain from above" feel — cubes spread laterally but mostly fall toward the panel.
    const angle = Math.random() * Math.PI * 2;
    const speed = 280 + Math.random() * 340;     // px/s
    const vx = Math.cos(angle) * speed;
    const vy = Math.sin(angle) * speed - 250;    // -250 = downward bias from top
    const vz = (Math.random() - 0.5) * 120;      // mild z spread for depth feel
    const angVel = new THREE.Vector3(
      (Math.random() - 0.5) * Math.PI * 8,
      (Math.random() - 0.5) * Math.PI * 8,
      (Math.random() - 0.5) * Math.PI * 5,
    );

    _conf3DScene.add(mesh);
    _conf3DParticles.push({
      mesh,
      velocity: new THREE.Vector3(vx, vy, vz),
      angVel,
      life: 0,
      totalLife: 1.2 + Math.random() * 0.5,
    });
  }

  if (!_conf3DRafId) {
    _conf3DLastTime = performance.now();
    _conf3DRafId = requestAnimationFrame(_tick3DConfetti);
  }
}

function _tick3DConfetti(now) {
  const dt = Math.min(0.05, (now - _conf3DLastTime) / 1000);
  _conf3DLastTime = now;

  for (let i = _conf3DParticles.length - 1; i >= 0; i--) {
    const p = _conf3DParticles[i];
    p.life += dt;
    if (p.life >= p.totalLife) {
      _conf3DScene.remove(p.mesh);
      _conf3DParticles.splice(i, 1);
      continue;
    }
    p.velocity.y -= _CONFETTI_GRAVITY * dt;
    p.mesh.position.x += p.velocity.x * dt;
    p.mesh.position.y += p.velocity.y * dt;
    p.mesh.position.z += p.velocity.z * dt;
    p.mesh.rotation.x += p.angVel.x * dt;
    p.mesh.rotation.y += p.angVel.y * dt;
    p.mesh.rotation.z += p.angVel.z * dt;
    // Shrink-fade in the last 25% of life — opacity isn't per-mesh on a shared material.
    const t = p.life / p.totalLife;
    if (t > 0.75) {
      const fade = 1 - (t - 0.75) / 0.25;
      p.mesh.scale.setScalar(_CONFETTI_CUBE_PX * fade);
    }
  }

  _conf3DRenderer.render(_conf3DScene, _conf3DCamera);

  if (_conf3DParticles.length > 0) {
    _conf3DRafId = requestAnimationFrame(_tick3DConfetti);
  } else {
    _conf3DRafId = 0;
    _conf3DCanvas.style.display = 'none';
  }
}

function _clear3DConfetti() {
  for (const p of _conf3DParticles) _conf3DScene && _conf3DScene.remove(p.mesh);
  _conf3DParticles.length = 0;
  if (_conf3DRafId) {
    cancelAnimationFrame(_conf3DRafId);
    _conf3DRafId = 0;
  }
  if (_conf3DCanvas) _conf3DCanvas.style.display = 'none';
}

function _retryLevel() {
  if (!_currentLevel) return;
  _setRuntimeGloveLoadout(_activeLoadoutForLevel(_currentLevel));
  _clearWorld();
  _buildLevelLayout(_currentLevel);
  buildSupportGraph();
  _rebuildBoundsForLevel(_currentLevel);
  _resetRunStats();
  restartMusic();
  _setupLevel(_currentLevel);
  _requestPointerLock();
}

_renderHome();

// Showcase launch: ?gauntlet drops straight into the Demolition Gauntlet filming level with
// dual Rocket gloves. Strip the param so refresh/back doesn't re-fire. Pointer lock engages on
// the first click (the lock hint covers the canvas until then).
if (new URLSearchParams(location.search).has('gauntlet')) {
  history.replaceState(null, '', location.pathname);
  _enterLevel('gauntlet', { force: true });
}

// Campaign-level deep-link: ?level=<id> drops straight into a curated campaign level (e.g. level10 for the
// W1 L10 race arena), bypassing the world/level-select AND the progression gate (force) so a co-op/race
// joiner who hasn't unlocked it in their own campaign can still enter the shared arena. Strip the param so
// refresh/back doesn't re-fire.
{
  const _lvlParam = new URLSearchParams(location.search).get('level');
  if (_lvlParam && LEVEL_CONFIG[_lvlParam]) {
    history.replaceState(null, '', location.pathname);
    _enterLevel(_lvlParam, { force: true });
  }
}

// ?join=<code> — opened from a lobby share link. Drop straight into the multiplayer modal's lobby for
// that room (as a joiner), where the host's Start will launch everyone into the arena. Strip the param.
{
  const _joinCode = new URLSearchParams(location.search).get('join');
  if (_joinCode && !_MP_PROD_HIDDEN) {
    history.replaceState(null, '', location.pathname);
    _openMpModal(_joinCode.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 5));
  }
}

// ===========================================================================================
// Multiplayer co-op (slice 2) — authoritative collapse streaming. The acting client OWNS the blocks
// it dislodges and STREAMS their motion (~15Hz): an 'unanchor' (create the falling block), a run of
// 'motion' batches (the actual collapse trajectory), then a 'settle' (final resting pose); deaths
// ride the existing 'd' event. The peer does NOT run its own physics for owned-remote blocks — it
// renders the streamed transforms (kinematic replay). We tried trigger+settle alone first: it can't
// reproduce a collapse, because the receiver only gets a zero-velocity start snapshot, so the cascade
// blocks just sit as a stable stack and never fall. Streaming the motion is what makes the tower fall
// identically on both screens.
// ===========================================================================================
const _MP_REST_LIN = 0.4;     // m/s — below this (with low spin) an owned block counts as resting
const _MP_REST_ANG = 0.6;     // rad/s
const _MP_REST_HOLD_MS = 200; // must stay at rest this long before we send its settle pose
const _MP_MOTION_MS = 50;        // 20Hz collapse streaming cadence (smaller steps = smoother on the peer)
// Render-behind buffer: the peer's blocks are drawn ~this far in the PAST, interpolating between the two
// most recent real samples. A late sample (jitter spike) then makes a block GLIDE across the gap instead
// of stalling and snapping. Only applies to peer-owned blocks, never your own, so play is unaffected.
const _MP_INTERP_DELAY = 100;

// Wire compression. Positions ride as centimetre integers (x100), rotation components as milli-units
// (x1000). That trims the fat full-precision JSON floats ~3x with no visible difference, so more of a
// collapse fits through a constrained link. Encode on send (_mpEncP/_mpEncQ), decode on apply (/100, /1000).
const _MP_POS = 100, _MP_ROT = 1000;
const _mpEncP = (v) => Math.round(v * _MP_POS);
const _mpEncQ = (v) => Math.round(v * _MP_ROT);

// ---- MP re-kick sync (follow-up collapse shoves) ----
// The one-shot 'u' unanchor carries a block's FIRST kick only. Later shoves from an ongoing collapse
// (topple, chip AOE, storm) never reached the peer, so big explosive hits looked mushier on the observer's
// screen and structures diverged more than the strict-events model needs. We resync those follow-up kicks,
// but ONLY from physics-safe points: the SEND reads velocity in _mpSyncOwnedBlocks (post stepPhysics), the
// RECEIVE queues here and applies in _mpDrainPendingKicks (also post-step). NEVER read/mutate a Rapier body
// from the topple/AOE hot path (applyImpulseToBlock) or straight from the network callback — an earlier
// version did exactly that and triggered Rapier's "recursive use of an object ... unsafe aliasing in rust"
// error, which leaks a borrow and PERMANENTLY poisons the physics world (every later frame throws at the
// player kinematic move -> hard freeze, no recovery). Absolute velocity is sent (not a delta) so a stale or
// duplicate 'k' can't compound. Live controls for diagnosis: set window._reKickEnabled=false to disable
// without a redeploy; read window._reKickStats (esp. .lastOp and .lastError) if a freeze ever recurs.
const _reKickStats = { sent: 0, applied: 0, queued: 0, dropped: 0, lastOp: null, lastError: null };
const _pendingKicks = [];
if (typeof window !== 'undefined') {
  window._reKickStats = _reKickStats;
  if (window._reKickEnabled === undefined) window._reKickEnabled = true;
}
function _reKickOn() { return typeof window === 'undefined' || window._reKickEnabled !== false; }

// Receiver: queue ONLY — no Rapier touch here (this can fire from the network path). Applied later.
function _mpApplyKick(msg) {
  if (!_reKickOn()) return;
  _pendingKicks.push(msg);
  _reKickStats.queued++;
  // Bound the queue so a burst / stall can never grow it without limit; drop oldest (staler) first.
  if (_pendingKicks.length > 512) { _pendingKicks.shift(); _reKickStats.dropped++; }
}

// Applier: runs once per frame from the tick loop, AFTER stepPhysics/syncBlocks, where the physics world
// is guaranteed not borrowed. Sets the peer-owned block's velocity to the owner's latest (idempotent).
function _mpDrainPendingKicks() {
  if (!_pendingKicks.length) return;
  for (let i = 0; i < _pendingKicks.length; i++) {
    const msg = _pendingKicks[i];
    const b = _netById.get(msg.id);
    if (!b) continue;
    const ud = b.userData;
    if (!ud || ud.netDead || !ud.body || !ud.ownedRemote || !ud.dynamic) continue;
    if (!msg.lv || !msg.av) continue;
    try {
      ud.body.setLinvel({ x: msg.lv[0] / _MP_POS, y: msg.lv[1] / _MP_POS, z: msg.lv[2] / _MP_POS }, true);
      ud.body.setAngvel({ x: msg.av[0] / _MP_ROT, y: msg.av[1] / _MP_ROT, z: msg.av[2] / _MP_ROT }, true);
      if (ud.body.wakeUp) ud.body.wakeUp();
      if (ud.netSettled) ud.netSettled = false;   // owner re-shoved a settled block — resume simulating it
      _reKickStats.applied++;
      _reKickStats.lastOp = { kind: 'apply', netId: msg.id, t: performance.now() };
    } catch (e) {
      // Should never happen from this safe point; if it does, capture EVERYTHING so we can confirm the
      // re-kick as the cause of a freeze, then rethrow into the tick's mp-tick guard (skips the frame).
      _reKickStats.lastError = { kind: 'apply', netId: msg.id, msg: (e && e.message) || String(e), lv: msg.lv, av: msg.av, t: performance.now() };
      console.error('[re-kick] APPLY threw for block', msg.id, '— world may be poisoned. stats:', _reKickStats, e);
      _pendingKicks.length = 0;
      throw e;
    }
  }
  _pendingKicks.length = 0;
}

// ---- deferred network Rapier mutations ----
// Every peer event that mutates the physics world (death, unanchor, streamed motion/settle) is QUEUED
// here and applied from the tick loop (_mpApplyDeferred), NEVER straight from the WebSocket onmessage
// callback. Applying them from onmessage let a freed/updated body collide with the physics step or a
// follow-on system mid-borrow, which panics Rapier ("recursive use of an object ... unsafe aliasing in
// rust") and PERMANENTLY poisons the world -> every later frame throws at the player move -> hard freeze.
// This mirrors the pattern the code already uses for projectile impacts (_pendingProjectileImpacts) and
// re-kicks (_pendingKicks): mutate the world only at the one safe point per frame.
const _pendingNetRapier = [];
function _mpDrainNetRapier() {
  if (!_pendingNetRapier.length) return;
  const ops = _pendingNetRapier;
  for (let i = 0; i < ops.length; i++) {
    const msg = ops[i];
    _phase = 'net-rapier:' + (msg && msg.t) + (msg && msg.id != null ? '#' + msg.id : '');
    try {
      if (msg.t === 'd') _mpApplyDeath(msg);
      else if (msg.t === 'u') _mpApplyUnanchor(msg);
      else if (msg.t === 'm') _mpApplyMotion(msg);
      else if (msg.t === 's') _mpApplySettle(msg);
    } catch (e) { _mpLoopError(e, 'net-rapier:' + (msg && msg.t)); }   // contain a bad op, keep the world alive
  }
  ops.length = 0;
}
// Runs once per frame from the tick, at the safe post-step point. Rematch (a full arena rebuild) and the
// referee-assigned spawn also land here, since they too mutate Rapier and were previously fired from onmessage.
function _mpApplyDeferred() {
  if (_raceRematchPending) {
    _raceRematchPending = false;
    _pendingNetRapier.length = 0;   // ops for the finished round are stale once we rebuild
    _phase = 'rematch';
    if (_currentLevel) { try { _enterLevel(_currentLevel, { force: true, restart: true }); } catch (e) { _mpLoopError(e, 'rematch'); } }
    return;   // the rebuild re-arms the round; nothing else to apply this frame
  }
  if (_raceSpawnApply) { _raceSpawnApply = false; _phase = 'spawn'; try { _raceApplySpawn(); } catch (e) { _mpLoopError(e, 'spawn'); } }
  _mpDrainNetRapier();
  if (_raceClearPending) { _raceClearPending = false; _phase = 'race-clear'; try { _raceClearLeftoverBlocks(); } catch (e) { _mpLoopError(e, 'race-clear'); } }
}

// Route an incoming peer event to the right handler. Rapier-mutating events are QUEUED (see above); the
// rest (cosmetic FX, music sync, race control) are safe to run straight from the network callback.
function _mpOnNet(msg) {
  const t = msg.t;
  if (t === 'd' || t === 'u' || t === 'm' || t === 's') {
    _pendingNetRapier.push(msg);
    if (_pendingNetRapier.length > 4096) _pendingNetRapier.shift();   // never grow unbounded on a stall
    return;
  }
  if (t === 'k') _mpApplyKick(msg);                 // follow-up collapse shove on an owned block (already queued)
  else if (t === 'pn') _mpApplyPunch(msg);          // peer avatar punch (cosmetic, no Rapier)
  else if (t === 'cs') _mpApplyCoinStorm(msg);      // a peer launched a charged coin volley (cosmetic)
  else if (t === 'orb') _mpApplyOrbit(msg);         // a peer's charge-gather ring update (cosmetic)
  else if (t === 'dmg') _mpApplyDamage(msg);        // a peer cracked a block — mirror the damage texture only
  else if (t === 'peer-join') _mpSendMusicPos();    // a peer joined — offer our song position
  else if (t === 'mreq') _mpSendMusicPos();         // a peer entered a level and is asking for it
  else if (t === 'msync') _resyncMusicTo(msg.pos);  // adopt the session's song position
  else if (t === 'rcfg') { /* referee ack of the race config; nothing to do client-side */ }
  else if (t === 'rgo') _raceOnGo(msg.at, msg.slot, msg.slots);  // start gate opened (+ our unique spawn slot)
  else if (t === 'rsc') _raceOnStandings(msg.board);   // live standings from the referee
  else if (t === 'rres') _raceOnResult(msg.board, msg.winners, msg.reason);  // final result
  else if (t === 'rmatch') _raceRematch();           // a peer hit Play Again — restart together
}

// ---- competitive race: client-side glue (referee lives in the relay) ----
// (_raceGoAt / _raceBanner / _raceHud are declared up near _RACE_ON — hoisted early so _setupLevel's
//  race branch can't hit their temporal dead zone if the ?gauntlet bootstrap enters a level at load.)
function _raceEnsureBanner() {
  if (_raceBanner) return;
  _raceBanner = document.createElement('div');
  _raceBanner.style.cssText = 'position:fixed;top:20%;left:50%;transform:translateX(-50%);z-index:1400;'
    + 'display:none;text-align:center;font-family:var(--font-display),system-ui,sans-serif;'
    + 'color:#fff;-webkit-text-stroke:4px #16161f;paint-order:stroke;letter-spacing:2px;'
    + 'text-shadow:0 4px 0 rgba(0,0,0,0.4);pointer-events:none;';
  document.body.appendChild(_raceBanner);
}
function _raceShowBanner(html, big) {
  _raceEnsureBanner();
  _raceBanner.innerHTML = html;
  _raceBanner.style.fontSize = big ? '84px' : '40px';
  _raceBanner.style.display = 'block';
}
function _raceHideBanner() { if (_raceBanner) _raceBanner.style.display = 'none'; }

// Result-screen actions (Play Again / Leave). Separate from the banner because the banner is
// pointer-events:none — these need clicks. Shown under the result banner once the referee declares a
// result; the cursor is freed (controls.unlock) in _raceOnResult so they're clickable.
let _raceResultActions = null;
function _raceEnsureResultActions() {
  if (_raceResultActions) return;
  _raceResultActions = document.createElement('div');
  // Sit BELOW the result banner (which anchors at top:20% and grows downward as big, often two-line
  // text) so the Play Again / Leave buttons never overlap the WINNER / LOSER message.
  _raceResultActions.style.cssText = 'position:fixed;top:62%;left:50%;transform:translateX(-50%);z-index:1450;'
    + 'display:none;gap:12px;pointer-events:auto;';
  const again = document.createElement('button');
  again.textContent = '▶ PLAY AGAIN'; again.className = 'ss-btn';
  again.style.cssText += ';background:var(--col-green,#43d675);font-size:18px;padding:12px 22px;';
  again.onclick = (e) => { e.stopPropagation(); _raceRequestRematch(again); };
  const leave = document.createElement('button');
  leave.textContent = 'LEAVE'; leave.className = 'ss-btn ss-btn--secondary';
  leave.style.cssText += ';font-size:18px;padding:12px 22px;';
  leave.onclick = (e) => { e.stopPropagation(); location.href = location.pathname; };  // strip race params → home
  _raceResultActions.append(again, leave);
  document.body.appendChild(_raceResultActions);
}
function _raceShowResultActions(canRematch) {
  _raceEnsureResultActions();
  const again = _raceResultActions.firstChild;
  // No rematch when we're the only one left (opponent_left) — the start gate needs 2 players.
  again.style.display = canRematch ? '' : 'none';
  again.textContent = '▶ PLAY AGAIN'; again.disabled = false; again.style.opacity = '1';
  _raceResultActions.style.display = 'flex';
}
function _raceHideResultActions() { if (_raceResultActions) _raceResultActions.style.display = 'none'; }

// Rematch: re-run the SAME arena/config with the SAME players in the SAME room. Either player's click
// restarts everyone (broadcast rmatch); each client re-enters the arena, which re-sends rcfg + rready —
// the referee resets the finished race on the fresh rcfg and re-opens the gate once both are ready again.
function _raceRequestRematch(btn) {
  if (!_raceOver) return;
  if (btn) { btn.textContent = 'WAITING…'; btn.disabled = true; btn.style.opacity = '0.6'; }
  _requestPointerLock();   // this click is a user gesture — recapture the mouse so the initiator drops straight back in (no extra click)
  if (_mp && _mp.send) _mp.send({ t: 'rmatch' });   // pull the other player into the rematch too
  _raceRematch();
}
function _raceRematch() {
  if (!_raceOver) return;   // already restarting (a duplicate/echoed rmatch) — ignore
  _raceHideResultActions();
  _raceHideBanner();
  // Defer the arena rebuild to the tick's safe point. _enterLevel recreates every rigid body, and firing
  // that from the rmatch network callback was a prime source of the Rapier re-entrancy freeze.
  _raceRematchPending = true;
}

// Send our race config + ready signal once we're both in the level AND connected to the relay. Driven
// from the animate loop (via _raceTick) because the relay connects asynchronously after level entry.
function _raceSignalReadyIfPossible() {
  if (_raceReadySent || !_RACE_ON) return;
  if (!_mp || !_mp.connected) return;
  _mp.send({ t: 'rcfg', metric: _RACE_METRIC, arena: _RACE_ARENA });
  _mp.send({ t: 'rready' });
  _raceReadySent = true;
  _raceLocked = true;   // stay locked until the referee opens the gate (rgo)
  _raceShowBanner('WAITING FOR<br>PLAYERS…', false);
}

function _raceOnGo(at, slot, slots) {
  _raceGoAt = at || (Date.now() + 3000);
  _raceStarted = false;   // becomes true when the countdown reaches zero (in _raceTick)
  // Referee handed us a unique spawn slot — snap there for the countdown. We're still input-locked, so
  // the reposition is invisible-feeling (no player is closest-to-loot by luck, and no two overlap).
  if (typeof slot === 'number' && slot >= 0) {
    _raceSpawnSlot = slot;
    _raceSpawnCount = (typeof slots === 'number' && slots > 0) ? slots : Math.max(slot + 1, 2);
    _raceSpawnApply = true;   // apply from the tick, not this network callback (Rapier mutation)
  }
}

function _raceOnStandings(board) {
  if (!Array.isArray(board)) return;
  _raceBoard = board;
  _raceRenderHud();
}

// A referee-authoritative race can leave a client with blocks the winner already cleared — a death
// whose netId never matched here (e.g. a mid-race _setupLevel rebuild reassigned ids, so a peer's 'd'
// found nothing). The referee owns the score, so at the finish we just clear any stragglers cosmetically
// so every screen shows the SAME emptied arena instead of one player left staring at floating blocks.
function _raceClearLeftoverBlocks() {
  if (!blocks.length) return;
  _netRemoteContext = true;   // these aren't our kills — don't rebroadcast 'd' to the (now-closed) referee
  try {
    for (const b of blocks.slice()) {
      if (!b.userData || b.userData.netDead) continue;
      try { breakBlock(b, _dislodgePopDir, 5, true); } catch (e) { /* keep clearing the rest */ }
    }
  } finally { _netRemoteContext = false; }
}

function _raceOnResult(board, winners, reason) {
  if (Array.isArray(board)) { _raceBoard = board; _raceRenderHud(); }
  _raceOver = true;
  _raceLocked = true;   // freeze input at the finish
  _raceClearPending = true;   // reconcile leftover blocks from the TICK (breakBlock mutates Rapier — not from onmessage)
  const myId = _mp && _mp.myId ? _mp.myId() : null;
  const iWon = Array.isArray(winners) && myId && winners.includes(myId);
  const tie = Array.isArray(winners) && winners.length > 1;
  const key = _RACE_METRIC === 'coins' ? 'coins' : 'smashes';
  const winNames = (Array.isArray(winners) && Array.isArray(board))
    ? board.filter((r) => winners.includes(r.id)).map((r) => r.name)
    : [];
  const left = reason === 'opponent_left';
  let head;
  if (left) {
    // Opponent disconnected. Post-GO the survivor takes the win by forfeit; pre-GO there's no result yet.
    head = winNames.length === 0
      ? 'RACE CANCELLED<br><span style="font-size:0.42em;-webkit-text-stroke:2px #16161f;">opponent left</span>'
      : `YOU WIN!<br><span style="font-size:0.42em;-webkit-text-stroke:2px #16161f;">opponent left</span>`;
  } else if (winNames.length === 0) head = 'RACE OVER';
  else if (tie) head = `TIE!<br><span style="font-size:0.5em">${winNames.join(' & ')}</span>`;
  else head = iWon ? 'YOU WIN!' : `${winNames[0]} WINS!`;
  if (left && winNames.length === 0) { _raceShowBanner(head, true); _raceFinishUi(false); return; }  // cancelled: no score line
  const unit = key === 'coins' ? 'coins' : 'smashes';
  const top = (Array.isArray(board) && board.length) ? board[0][key] : 0;
  _raceShowBanner(`${head}<br><span style="font-size:0.4em;-webkit-text-stroke:2px #16161f;">${top} ${unit}</span>`, true);
  _raceFinishUi(!left);   // opponent_left leaves us solo — no rematch possible, just Leave
}
// Present the finish: free the mouse (it's captured mid-race) so the result buttons are clickable, and
// surface Play Again / Leave. The unlock handler special-cases _raceOver so it won't pop the pause menu
// or the spectate "click to aim" hint here.
function _raceFinishUi(canRematch) {
  if (controls.isLocked) controls.unlock();
  _raceShowResultActions(canRematch);
}

// Corner standings panel — YOU highlighted, sorted by the race metric (referee order).
// Multiplayer scoreboard — the match's own HUD (the solo coin panel is hidden in a match). Kit-styled
// (yellow box, ink border, pixel font) so it matches the rest of the HUD, and takes the prime top-right
// spot the coin counter used to hold. Header is the CHOSEN metric (smashes/coins); the leader gets a star
// and YOU is highlighted. No "race" wording (it's a smash-off, not a race).
function _raceHudEsc(s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }
function _raceRenderHud() {
  if (!_RACE_ON) return;
  if (!_raceHud) {
    _raceHud = document.createElement('div');
    _raceHud.style.cssText = 'position:fixed;top:14px;right:14px;z-index:1300;width:252px;'
      + 'background:var(--col-bg-panel, #FFE600);border:3px solid var(--col-ink, #0a0820);'
      + 'box-shadow:4px 4px 0 var(--col-ink, #0a0820);border-radius:9px;padding:11px 13px;'
      + 'font-family:var(--font-body, "Pixelify Sans"),system-ui,sans-serif;color:var(--col-ink, #0a0820);'
      + 'pointer-events:none;';
    document.body.appendChild(_raceHud);
  }
  const key = _RACE_METRIC === 'coins' ? 'coins' : 'smashes';
  const myId = _mp && _mp.myId ? _mp.myId() : null;
  const label = key === 'coins' ? 'COINS' : 'SMASHES';
  // Smashes: the bar fills toward the WIN GOAL (majority of blocks) so a full bar == won, with no dead
  // space after it, and the match ends the instant someone reaches it. Coins: no fixed goal, so the bar
  // tracks arena progress (blocks broken / total) while the number stays the coin score. Rank chip
  // (1/2/3/4) is the position badge; YOU is pink.
  const smashesMode = key === 'smashes';
  const winGoal = Math.floor((_raceTotalBlocks || 0) / 2) + 1;   // majority = guaranteed win
  const barMax = smashesMode
    ? (winGoal || 1)
    : (_raceTotalBlocks || _raceBoard.reduce((m, r) => Math.max(m, r.smashes || 0), 0) || 1);
  const topSmashes = _raceBoard.reduce((m, r) => Math.max(m, r.smashes || 0), 0);
  const blocksLeft = Math.max(0, (_raceTotalBlocks || 0) - _raceBoard.reduce((s, r) => s + (r.smashes || 0), 0));
  const readout = smashesMode
    ? `${Math.max(0, winGoal - topSmashes)} to win`
    : `${blocksLeft} block${blocksLeft === 1 ? '' : 's'} left`;
  let html = `<div style="font-family:var(--font-display);font-size:12px;letter-spacing:1px;">MOST ${label} WINS</div>`;
  html += `<div style="font-size:10px;font-weight:600;opacity:0.72;margin:3px 0 8px;">${readout}</div>`;
  _raceBoard.forEach((r, i) => {
    const me = myId && r.id === myId;
    const nm = _raceHudEsc(me ? 'YOU' : (r.name || 'Player'));
    const val = r[key] || 0;
    const pct = Math.min(100, Math.round(((r.smashes || 0) / barMax) * 100));
    // Bar fill = the player's identity colour (matches their avatar's ring/accent), so the standings read
    // at a glance. Relay stamps colorIdx on the board row; fall back to the peer registry, then ink.
    const cidx = (typeof r.colorIdx === 'number' && r.colorIdx >= 0) ? r.colorIdx
      : (_mp && _mp.colorIdxForId ? _mp.colorIdxForId(r.id) : -1);
    const fill = (cidx >= 0) ? _MP_PLAYER_COLORS[cidx % _MP_PLAYER_COLORS.length]
      : (me ? 'var(--col-pink, #FF3B6B)' : 'var(--col-ink, #0a0820)');
    html += `<div style="margin-top:${i ? 8 : 0}px;">`
      + `<div style="display:flex;justify-content:space-between;gap:7px;align-items:center;font-size:17px;font-weight:600;${me ? 'color:var(--col-pink, #FF3B6B);' : ''}">`
        + `<span style="display:flex;align-items:center;gap:6px;min-width:0;flex:1 1 auto;">`
          + `<span style="flex:0 0 auto;font-family:var(--font-display);font-size:11px;line-height:1;background:var(--col-ink, #0a0820);color:#fff;border-radius:4px;padding:3px 5px;">${i + 1}</span>`
          + `<span style="flex:0 0 auto;width:11px;height:11px;border-radius:3px;background:${fill};border:2px solid var(--col-ink, #0a0820);"></span>`
          + `<span style="min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${nm}</span>`
        + `</span>`
        + `<span style="flex:0 0 auto;padding-left:6px;">${val}</span>`
      + `</div>`
      + `<div style="position:relative;height:10px;margin-top:4px;background:rgba(10,8,32,0.16);border:2px solid var(--col-ink, #0a0820);border-radius:6px;overflow:hidden;">`
        + `<div style="height:100%;width:${pct}%;background:${fill};transition:width 200ms ease;"></div>`
      + `</div>`
    + `</div>`;
  });
  _raceHud.innerHTML = html;
  _raceHud.style.display = _raceBoard.length ? 'block' : 'none';
}

// Per-frame race driver: signals ready when able, runs the GO countdown, and fires rend when the arena
// empties. Called from _checkLevelEnd / the animate loop while a race level is active.
function _raceTick() {
  if (!_RACE_ON) return;
  _raceSignalReadyIfPossible();
  if (_raceGoAt && !_raceStarted && !_raceOver) {
    const remain = _raceGoAt - Date.now();
    if (remain > 0) {
      const n = Math.ceil(remain / 1000);
      _raceShowBanner(String(n), true);
    } else {
      _raceStarted = true;
      _raceLocked = false;
      _raceShowBanner('GO!', true);
      setTimeout(() => { if (_raceStarted && !_raceOver) _raceHideBanner(); }, 700);
    }
  }
}

// Tell peers where our song is (continuous seconds since song start) so a joiner can drop into the same
// part. Silent if we're not actually playing — then we have nothing to hand out.
function _mpSendMusicPos() {
  if (!_mp || !_mp.send || !_musicStarted || !audioCtx) return;
  const pos = audioCtx.currentTime - _musicLoopStartTime;
  if (pos >= 0) _mp.send({ t: 'msync', pos });
}

// A peer punched. Make their avatar jab and (on a hit) play the matching smash sound — but quantize
// BOTH to this client's local beat grid so the partner reads as in-time with the music, the same way
// our own smashes snap. The sound never carries a GOOD/PERFECT/MISS judgement: it's a musical note in
// the peer's voice, harmonically coherent with ours. A miss still jabs the fist, just with no smash.
// (Layer 1: each client snaps to ITS OWN grid; a shared cross-client downbeat is the next increment.)
function _mpApplyPunch(msg) {
  if (!_mp || !audioCtx) return;
  const hand = msg.h === 1 ? 1 : 0;
  const tNow = audioCtx.currentTime;
  // Two timing models by source (the puncher tags each hit):
  //   • ASSIST auto-fire (msg.au): the game already timed it to the beat, so SNAP to the next quarter-note
  //     beat and stagger bursts onto consecutive beats (each its own beat, capped 2 ahead). This is the
  //     locked feel Rich likes in assist mode — left exactly as it was.
  //   • MANUAL (msg.au falsy): the punch's timing IS the content, so REPRODUCE its beat-phase (msg.ph, 0..1)
  //     a whole number of beats later. Delaying by whole beats preserves where it sits inside the beat, so
  //     an off-beat hit sounds off. No quantization, no galloping — honest timing, just latency-shifted.
  let snapped;
  if (!_musicStarted) {
    snapped = tNow;                                  // no music yet: just play it
  } else if (msg.au) {
    const beat = _snapToNextBeat(tNow);
    snapped = Math.min(Math.max(beat, _mpNextPunchBeat), beat + 2 * MUSIC_BEAT);
    _mpNextPunchBeat = snapped + MUSIC_BEAT;
  } else {
    const ph = (msg.ph || 0) * MUSIC_BEAT;           // seconds into the beat to reproduce
    const nowElapsed = tNow - _musicLoopStartTime;
    const k = Math.ceil((nowElapsed - ph) / MUSIC_BEAT);
    snapped = _musicLoopStartTime + k * MUSIC_BEAT + ph;
    while (snapped < tNow + 0.06) snapped += MUSIC_BEAT;   // keep it safely in the future, same phase
  }
  const delayMs = Math.max(0, (snapped - tNow) * 1000);
  // Audio: schedule the smash AT `snapped` (sample-accurate) so it lands on the beat. setTimeout
  // can't do this — its callback fires whenever the event loop is free, several ms off the grid.
  if (msg.hit) {
    // Spatialise from the peer avatar's position so distance attenuates it (a null position would
    // play dead-centre at full volume — the "really loud" smash). Fall back to null if no avatar yet.
    const havePos = _mp.getAvatarPos && _mp.getAvatarPos(msg.from, _mpPunchPos);
    playSmashSound(msg.mat || 'brick', 1, true, havePos ? _mpPunchPos : null, snapped);
  }
  // Spring reach: the peer's fist stretches out to meet the block in 3D (up/down too). Pass OUR copy of
  // the block's world position (by netId) so the fist lands where the block is HERE even if cause-mode
  // positions have drifted (same principle as the coin volley). The avatar derives direction + distance.
  let tx, ty, tz;
  if (msg.hit && msg.tid !== undefined) {
    const tb = _netById.get(msg.tid);
    if (tb && tb.position) { tx = tb.position.x; ty = tb.position.y; tz = tb.position.z; }
  }
  // The jab animation lands on the beat (a few ms of timer jitter on a visual is invisible; only AUDIO
  // needs sample-accurate scheduling). Coins are NOT fired here — they come from the charged chip-storm
  // launch (the 'cs' event), not from ordinary punches.
  setTimeout(() => { if (_mp && _mp.punch) _mp.punch(msg.from, hand, !!msg.ch, tx, ty, tz); }, delayMs);
}
let _mpNextPunchBeat = 0;                 // audio time of the next FREE beat for a peer smash (burst staggering)
const _mpPunchPos = new THREE.Vector3();  // reused scratch for the peer-smash spatial position
const _mpFxPos = new THREE.Vector3();     // reused scratch for the peer fist position

// A short rising "swoop" from a peer's fist as they suck coins into orbit — spatialized so it comes from
// their position. The peer's whole gather→hold→fire was silent from the observer's side before this.
const _mpCoinSuckPos = new THREE.Vector3();
function _playPeerCoinSuck(position) {
  if (!audioCtx) return;
  const t0 = audioCtx.currentTime;
  const len = 0.15;
  const osc = audioCtx.createOscillator();
  const env = audioCtx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(300, t0);
  osc.frequency.exponentialRampToValueAtTime(900, t0 + len);   // rising pitch reads as "pulling in"
  env.gain.setValueAtTime(0.0001, t0);
  env.gain.exponentialRampToValueAtTime(0.045, t0 + 0.02);
  env.gain.exponentialRampToValueAtTime(0.0001, t0 + len);
  osc.connect(env).connect(_spatialDestination(position));
  osc.start(t0); osc.stop(t0 + len + 0.02);
}

// A soft high "shimmer" ping for coins CIRCLING a peer's fist — played sparsely (throttled) while the
// ring is held so the observer hears the coins are still gathered, not just on the gather/fire transients.
function _playPeerOrbitShimmer(position) {
  if (!audioCtx) return;
  const t0 = audioCtx.currentTime;
  const len = 0.13;
  const dest = _spatialDestination(position);
  for (const f of [1560, 2340]) {   // two bright partials → a small coin "ting"
    const osc = audioCtx.createOscillator();
    const env = audioCtx.createGain();
    osc.type = 'sine';
    osc.frequency.value = f;
    env.gain.setValueAtTime(0.0001, t0);
    env.gain.exponentialRampToValueAtTime(f === 1560 ? 0.016 : 0.009, t0 + 0.008);
    env.gain.exponentialRampToValueAtTime(0.0001, t0 + len);
    osc.connect(env).connect(dest);
    osc.start(t0); osc.stop(t0 + len + 0.02);
  }
}

// A peer launched a charged chip-storm: show a cosmetic coin volley from their fist. Aimed at a locked
// block (by netId, on OUR client) if they were locked, else fired straight along their aim direction.
const _mpVolleyDir = new THREE.Vector3();
function _mpApplyCoinStorm(msg) {
  if (!_mp) return;
  const hand = msg.h === 1 ? 1 : 0;
  if (!(_mp.getFistPos && _mp.getFistPos(msg.from, hand, _mpFxPos))) return;     // need THAT peer's fist as the source
  const fx = _mpFxPos.x, fy = _mpFxPos.y, fz = _mpFxPos.z;
  const n = msg.n || 8;
  let target = null;
  if (msg.tid !== undefined) { const b = _netById.get(msg.tid); if (b && b.position) target = b.position; }  // locked
  let dir = null;
  if (!target && Array.isArray(msg.dir)) {
    _mpVolleyDir.set(msg.dir[0] || 0, msg.dir[1] || 0, msg.dir[2] || 0);
    if (_mpVolleyDir.lengthSq() > 1e-6) dir = _mpVolleyDir;   // unlocked: fly straight along the aim direction
  }
  if (!target && !dir) return;   // nothing to aim at
  _firePeerCoinVolley(fx, fy, fz, target, dir, n, msg.from);
  // Audio: the ascending release arpeggio from the peer's fist. The landing TINKLE now fires on actual
  // coin arrival (in _peerVolleyImpact), so it works for both locked and course-corrected unlocked hits.
  if (audioCtx) { _mpCoinSuckPos.set(fx, fy, fz); playChargeRelease(typeof msg.c === 'number' ? msg.c : 0.85, _mpCoinSuckPos); }
  const o = _peerOrbitFor(msg.from);
  if (o) { o[hand].count = 0; o[hand].shown = 0; }   // SNAP the ring empty so the fire reads as a crisp launch
}

// A peer's charge-gather ring update: how many coins are orbiting that fist + the charge (ring size). Kept
// per-peer so multiple players can charge at once without clobbering each other's ring.
function _mpApplyOrbit(msg) {
  const h = msg.h === 1 ? 1 : 0;
  const o = _peerOrbitFor(msg.from);
  if (!o) return;
  const prevN = o[h].count;
  o[h].count = msg.n || 0;
  o[h].charge = msg.c || 0;
  o[h].until = performance.now() + 280;   // expire if updates stop (cancel / packet loss)
  if (o[h].count > 0) _ensurePeerOrbitPool();
  // Fresh gather starting (empty → coins) → recall any of this peer's still-flying volley back to the fist,
  // so we don't keep showing coins mid-air while the shooter is already sucking the next batch in.
  if (prevN === 0 && o[h].count > 0) _peerVolleyRecall(msg.from, h);
  // Audio (was silent): a soft rising "suck" swoop from the peer's fist as they gather more coins, so the
  // observer hears the pull-in. Throttled since orb updates arrive ~10Hz and a fresh gather ramps fast.
  if (o[h].count > prevN && audioCtx && _mp && _mp.getFistPos) {
    const now = performance.now();
    if (now - (o[h].lastSuckMs || 0) > 110 && _mp.getFistPos(msg.from, h, _mpCoinSuckPos)) {
      o[h].lastSuckMs = now;
      _playPeerCoinSuck(_mpCoinSuckPos);
    }
  }
  // feed that peer's avatar spring coil so a charging spring glove lifts/glows on the observer's screen
  if (_mp && _mp.setCharge) _mp.setCharge(msg.from, h, o[h].count > 0 ? o[h].charge : 0);
}

const _mpDeathDir = new THREE.Vector3();   // scratch for the decoded remote smash direction
function _mpApplyDeath(msg) {
  const b = _netById.get(msg.id);
  if (!b) return;
  if (!b.userData || b.userData.netDead) return;  // already gone — no-op
  // Reproduce the smasher's destruction on this screen. The death carries the smash intent (dir/str/ub);
  // with it we drive the SAME directional piece launch + cosmetic impact FX (dust / sparks / aesthetic
  // chunks) the smasher saw, instead of the old weak straight-up pop. Fall back to the ground-pop defaults
  // for deaths with no impulse data (legacy senders / dislodge pops).
  let dir = _dislodgePopDir, str = 5, ub = true;
  if (Array.isArray(msg.dir)) {
    _mpDeathDir.set(msg.dir[0] || 0, msg.dir[1] || 0, msg.dir[2] || 0);
    if (_mpDeathDir.lengthSq() > 1e-6) {
      dir = _mpDeathDir;
      str = (typeof msg.str === 'number') ? msg.str : 12;
      ub = !!msg.ub;
    }
  }
  // Cosmetic-only break FX: perCoinScore=0 makes spawnChunks emit dust/sparks + aesthetic chunks but
  // spawn NO currency (we must never drop the peer's coins on our floor). Runs BEFORE breakBlock removes
  // the block, and at OUR copy's position (cause mode positions can diverge). WRAPPED so a spawn error can
  // NEVER abort the death below — otherwise a throw here left the block un-destroyed AND un-netDead, i.e.
  // floating forever on this screen (the "string of crates in the sky" bug).
  try {
    if (b.position) {
      spawnChunks(b.position, dir, b.userData.material, 0, false, 1, 1, b, 'good');
      impactRing(b.position, dir, b);   // radiating shockwave decal — the "force blasting out" cue the smasher sees
    }
  } catch (e) { /* FX only — the death is what matters, keep going */ }
  // Course-correct this peer's in-flight unlocked coin volley onto the block they just broke, so an
  // unlocked storm's coins converge + fade on the confirmed hit instead of sailing past.
  if (b.position && msg.from != null) _peerVolleyRedirect(msg.from, b.position.x, b.position.y, b.position.z);
  _netRemoteContext = true;
  try { breakBlock(b, dir, str, ub); }
  finally { _netRemoteContext = false; }
  _mpCauseWakeAll();   // cause mode: wake peer blocks so anything resting on the destroyed one falls (no floaters)
}

// A peer cracked a block without killing it — mirror the damage so the crack/dent shows on our screen
// too (the block stays pristine here otherwise; only the hitter runs the damage logic). `ht` is the
// peer's resulting hitsTaken; we repaint to that level + darken to match. No physics, no coins.
function _mpApplyDamage(msg) {
  const b = _netById.get(msg.id);
  if (!b || !b.userData || b.userData.netDead) return;
  const ud = b.userData;
  const ht = msg.ht;
  if (typeof ht !== 'number' || ht <= 0) return;
  ud.hitsTaken = Math.max(ud.hitsTaken || 0, ht);
  _netRemoteContext = true;   // suppress the _paintBlockDamage re-broadcast (this IS the remote apply)
  try {
    const c = ud.originalColor;
    if (c) { const v = Math.pow(CRACK_DARKEN_PER_HIT, ht); b.material.color.setRGB(c.r * v, c.g * v, c.b * v); }
    _paintBlockDamage(b, ht);
  } finally { _netRemoteContext = false; }
  b.scale.set(0.93, 0.93, 0.93);   // brief crack pulse, mirrors the local feedback
  setTimeout(() => { if (b.parent && !ud.netDead) b.scale.set(1, 1, 1); }, 90);
  // Course-correct this peer's in-flight unlocked volley onto the block their chip just hit (didn't break).
  if (b.position && msg.from != null) _peerVolleyRedirect(msg.from, b.position.x, b.position.y, b.position.z);
}

// Record a new authoritative pose for an owned-remote block. We keep the TWO most recent samples (prev +
// curr) with their arrival times; _mpUpdateRemoteBlocks interpolates between them at a render-behind
// clock. This is standard entity interpolation: it plays the real sampled path back smoothly and absorbs
// jitter (a late sample just stretches the prev->curr segment, so the block glides instead of snapping).
function _mpPushSample(ud, px, py, pz, qx, qy, qz, qw) {
  const now = performance.now();
  if (!ud.netCurrP) {
    ud.netPrevP = new THREE.Vector3(px, py, pz);
    ud.netPrevQ = new THREE.Quaternion(qx, qy, qz, qw);
    ud.netCurrP = new THREE.Vector3(px, py, pz);
    ud.netCurrQ = new THREE.Quaternion(qx, qy, qz, qw);
    ud.netPrevT = ud.netCurrT = now;
  } else {
    ud.netPrevP.copy(ud.netCurrP); ud.netPrevQ.copy(ud.netCurrQ); ud.netPrevT = ud.netCurrT;
    ud.netCurrP.set(px, py, pz); ud.netCurrQ.set(qx, qy, qz, qw); ud.netCurrT = now;
  }
  ud.netParked = false;
}

function _mpApplyUnanchor(msg) {
  const b = _netById.get(msg.id);
  if (!b) return;
  const ud = b.userData;
  if (!ud || ud.netDead || !ud.body) return;
  _netRemoteContext = true;
  try { if (!ud.dynamic) unanchorBlock(b); }   // detach from the support graph + tag the cascade peer-owned
  finally { _netRemoteContext = false; }
  ud.owned = false;
  ud.ownedRemote = true;
  ud.netNeedsUnanchor = false;
  ud.netSettled = false;
  const body = ud.body;
  const m = msg.p, n = msg.q;
  const px = m[0] / _MP_POS, py = m[1] / _MP_POS, pz = m[2] / _MP_POS;
  const qx = n[0] / _MP_ROT, qy = n[1] / _MP_ROT, qz = n[2] / _MP_ROT, qw = n[3] / _MP_ROT;

  if (_MP_MODE === 'cause') {
    // Cause-based: the block is already dynamic (unanchorBlock above). Seed the SAME start pose + kick
    // the owner had, then hand it to our local physics. The collapse is real + smooth here; it diverges
    // from the owner's over time (accepted), but synced unanchor + death events keep every block's STATE
    // matched, so it never floats or pops out of a standing wall. wakeUp so a re-seeded block can't sleep.
    body.setTranslation({ x: px, y: py, z: pz }, true);
    body.setRotation({ x: qx, y: qy, z: qz, w: qw }, true);
    if (msg.lv) body.setLinvel({ x: msg.lv[0] / _MP_POS, y: msg.lv[1] / _MP_POS, z: msg.lv[2] / _MP_POS }, true);
    if (msg.av) body.setAngvel({ x: msg.av[0] / _MP_ROT, y: msg.av[1] / _MP_ROT, z: msg.av[2] / _MP_ROT }, true);
    if (body.wakeUp) body.wakeUp();
    return;
  }

  // Stream mode: kinematic replay. Freeze the body; the streamed motion drives mesh + collider.
  body.setBodyType(RAPIER.RigidBodyType.Fixed, false);
  ud.dynamic = false;          // syncBlocks skips it now; the follow updater owns the mesh
  body.setTranslation({ x: px, y: py, z: pz }, false);
  body.setRotation({ x: qx, y: qy, z: qz, w: qw }, false);
  b.position.set(px, py, pz);
  b.quaternion.set(qx, qy, qz, qw);
  ud.netCurrP = null;   // fresh block — reseed the interpolation buffer from this start pose
  _mpPushSample(ud, px, py, pz, qx, qy, qz, qw);
}

function _mpApplyMotion(msg) {
  const s = msg.s;
  for (let i = 0; i < s.length; i++) {
    const e = s[i];
    const b = _netById.get(e[0]);
    if (!b) continue;
    const ud = b.userData;
    if (!ud || ud.netDead || !ud.body || !ud.ownedRemote) continue;
    if (ud.netSettled) ud.netSettled = false;  // owner shoved it again after it had settled — resume following
    _mpPushSample(ud, e[1] / _MP_POS, e[2] / _MP_POS, e[3] / _MP_POS,
      e[4] / _MP_ROT, e[5] / _MP_ROT, e[6] / _MP_ROT, e[7] / _MP_ROT);
  }
}

function _mpApplySettle(msg) {
  const b = _netById.get(msg.id);
  if (!b) return;
  const ud = b.userData;
  if (!ud || ud.netDead || !ud.body) return;
  // Final authoritative pose. The collider snaps to rest immediately (solid collision); the mesh glides
  // the last sample-to-rest gap via the same interpolation, then parks once it arrives (bounds the work).
  const m = msg.p, n = msg.q;
  const px = m[0] / _MP_POS, py = m[1] / _MP_POS, pz = m[2] / _MP_POS;
  const qx = n[0] / _MP_ROT, qy = n[1] / _MP_ROT, qz = n[2] / _MP_ROT, qw = n[3] / _MP_ROT;
  ud.body.setTranslation({ x: px, y: py, z: pz }, false);
  ud.body.setRotation({ x: qx, y: qy, z: qz, w: qw }, false);
  _mpPushSample(ud, px, py, pz, qx, qy, qz, qw);
  ud.netSettled = true;
}

// Owner side: each frame, for blocks WE own, fire the one-shot 'unanchor', then stream the collapse
// motion at ~15Hz (one batched 'm' message for all moving owned blocks), and a final 'settle' once a
// block comes to rest. Deaths are handled in breakBlock. Called from the animate loop.
let _mpMotionAccum = 0;
function _mpSyncOwnedBlocks(dt) {
  if (!_mp || !_mp.connected) return;
  // 1. One-shot unanchor the frame a block is dislodged — must go out before any motion that references it.
  //    In cause mode this carries the block's velocity (the KICK), so the peer's local sim starts from the
  //    same impulse; in stream mode it's pose-only (the motion follows).
  for (const block of blocks) {
    const ud = block.userData;
    if (!ud.owned || !ud.dynamic || !ud.body || !ud.netNeedsUnanchor || ud.netDead) continue;
    const t = ud.body.translation();
    const r = ud.body.rotation();
    const u = { t: 'u', id: ud.netId,
      p: [_mpEncP(t.x), _mpEncP(t.y), _mpEncP(t.z)],
      q: [_mpEncQ(r.x), _mpEncQ(r.y), _mpEncQ(r.z), _mpEncQ(r.w)] };
    if (_MP_MODE === 'cause') {
      const lv = ud.body.linvel();
      const av = ud.body.angvel();
      u.lv = [_mpEncP(lv.x), _mpEncP(lv.y), _mpEncP(lv.z)];
      u.av = [_mpEncQ(av.x), _mpEncQ(av.y), _mpEncQ(av.z)];
    }
    _mp.send(u);
    ud.netNeedsUnanchor = false;
    ud.netRestMs = 0;
  }
  // 1b. Re-kick: owned blocks that took an ADDITIONAL shove AFTER their 'u' already went out (flagged in
  //     applyImpulseToBlock). Read the fresh velocity + send it HERE — this runs post stepPhysics/syncBlocks
  //     so the world is not borrowed. Absolute velocity, so a stale/dup 'k' can't compound on the receiver.
  if (_reKickOn()) {
    for (const block of blocks) {
      const ud = block.userData;
      if (!ud.netReKicked) continue;
      ud.netReKicked = false;
      if (!ud.owned || !ud.dynamic || !ud.body || ud.netDead || ud.netNeedsUnanchor || ud.netId === undefined) continue;
      try {
        const lv = ud.body.linvel();
        const av = ud.body.angvel();
        _mp.send({ t: 'k', id: ud.netId,
          lv: [_mpEncP(lv.x), _mpEncP(lv.y), _mpEncP(lv.z)],
          av: [_mpEncQ(av.x), _mpEncQ(av.y), _mpEncQ(av.z)] });
        _reKickStats.sent++;
        _reKickStats.lastOp = { kind: 'send', netId: ud.netId, t: performance.now() };
      } catch (e) {
        _reKickStats.lastError = { kind: 'send', netId: ud.netId, msg: (e && e.message) || String(e), t: performance.now() };
        console.error('[re-kick] SEND threw for block', ud.netId, '— world may be poisoned. stats:', _reKickStats, e);
        throw e;
      }
    }
  }
  // Cause mode: physics runs locally on BOTH sides, so there's no motion or settle to stream. Deaths
  // (authoritative) ride the existing 'd' event. Done.
  if (_MP_MODE === 'cause') return;
  // 2. (Stream mode) Stream the collapse: every owned, still-moving block's pose, batched. Send a 'settle'
  //    for any that has come to rest (so the receiver freezes it on the exact authoritative pose).
  _mpMotionAccum += dt;
  if (_mpMotionAccum >= _MP_MOTION_MS / 1000) {
    _mpMotionAccum = 0;
    const samples = [];
    for (const block of blocks) {
      const ud = block.userData;
      if (!ud.owned || !ud.dynamic || !ud.body || ud.netDead) continue;
      const body = ud.body;
      const t = body.translation();
      const r = body.rotation();
      const v = body.linvel();
      const w = body.angvel();
      const atRest = Math.hypot(v.x, v.y, v.z) < _MP_REST_LIN && Math.hypot(w.x, w.y, w.z) < _MP_REST_ANG;
      if (atRest) {
        ud.netRestMs = (ud.netRestMs || 0) + _MP_MOTION_MS;
        if (ud.netRestMs >= _MP_REST_HOLD_MS) {
          // Crossed into rest. Send settle ONCE per rest spell; while it stays at rest, stream nothing.
          // (settle is NOT a permanent latch — a settled block that gets shoved again by the ongoing
          // collapse un-settles below and resumes streaming, else it freezes mid-air on the receiver.)
          if (!ud.netSettled) {
            _mp.send({ t: 's', id: ud.netId,
              p: [_mpEncP(t.x), _mpEncP(t.y), _mpEncP(t.z)],
              q: [_mpEncQ(r.x), _mpEncQ(r.y), _mpEncQ(r.z), _mpEncQ(r.w)] });
            ud.netSettled = true;
          }
          continue;
        }
      } else {
        ud.netRestMs = 0;
        ud.netSettled = false;  // it's moving again — resume streaming this frame
      }
      samples.push([ud.netId, _mpEncP(t.x), _mpEncP(t.y), _mpEncP(t.z),
        _mpEncQ(r.x), _mpEncQ(r.y), _mpEncQ(r.z), _mpEncQ(r.w)]);
    }
    if (samples.length) _mp.send({ t: 'm', s: samples });
  }
}

// Receiver side: draw owned-remote blocks at a render-behind clock, interpolating between the last two
// real samples (kinematic replay), and ride the collider under the mesh. Called from the animate loop.
function _mpUpdateRemoteBlocks() {
  if (!_mpEnabled) return;
  const renderT = performance.now() - _MP_INTERP_DELAY;
  for (const block of blocks) {
    const ud = block.userData;
    if (!ud.ownedRemote || ud.netDead || ud.netParked || !ud.netCurrP) continue;
    const span = ud.netCurrT - ud.netPrevT;
    let a = span > 0 ? (renderT - ud.netPrevT) / span : 1;
    if (a < 0) a = 0; else if (a > 1) a = 1;
    block.position.lerpVectors(ud.netPrevP, ud.netCurrP, a);
    block.quaternion.copy(ud.netPrevQ).slerp(ud.netCurrQ, a);
    if (ud.body) {
      ud.body.setTranslation({ x: block.position.x, y: block.position.y, z: block.position.z }, false);
      ud.body.setRotation({ x: block.quaternion.x, y: block.quaternion.y, z: block.quaternion.z, w: block.quaternion.w }, false);
    }
    // Settled and the render clock has caught up to the final pose: park it to bound per-frame work.
    if (ud.netSettled && a >= 1 && renderT >= ud.netCurrT) ud.netParked = true;
  }
}

// Cause mode (receiver): when an authoritative death removes a block, wake every peer-owned dynamic block
// so anything that was resting on the destroyed one re-checks support and falls instead of hanging (Rapier
// does NOT auto-wake a body when a neighbour's collider is removed, which is the classic floater cause).
// Fired only on the discrete death events, so resting rubble can still sleep in between (no perma-jitter).
function _mpCauseWakeAll() {
  if (_MP_MODE !== 'cause') return;
  for (const block of blocks) {
    const ud = block.userData;
    if (ud.ownedRemote && ud.dynamic && !ud.netDead && ud.body && ud.body.wakeUp) ud.body.wakeUp();
  }
}

// MP boot curtain — covers the canvas from page load until the real match arena is built, so the default
// sandbox backdrop never flashes while we fetch/build it (UGC arenas need a server fetch, which is the
// most visible gap). Auto-hides after the arena's first paint (from _setupLevel), with a safety timeout.
let _mpBootCurtain = null;
function _showMpBootCurtain() {
  if (_mpBootCurtain) return;
  _mpBootCurtain = document.createElement('div');
  _mpBootCurtain.style.cssText = 'position:fixed;inset:0;z-index:3000;display:flex;align-items:center;justify-content:center;'
    + 'background:#0a0820;transition:opacity 280ms ease;';
  const t = document.createElement('div');
  t.style.cssText = 'font-family:var(--font-display);font-size:15px;letter-spacing:2px;color:var(--col-cyan, #00E5FF);'
    + '-webkit-text-stroke:2px var(--col-ink, #0a0820);paint-order:stroke;text-transform:uppercase;';
  t.textContent = 'Loading arena...';
  _mpBootCurtain.appendChild(t);
  document.body.appendChild(_mpBootCurtain);
  setTimeout(_hideMpBootCurtain, 10000);   // never leave a hung/failed boot stuck behind the curtain
}
function _hideMpBootCurtain() {
  if (!_mpBootCurtain) return;
  const el = _mpBootCurtain; _mpBootCurtain = null;
  el.style.opacity = '0';
  setTimeout(() => el.remove(), 300);
}

// Multiplayer co-op prototype: ?mp=ROOM connects to the local relay (tools/mp-relay.mjs),
// renders a peer avatar, and syncs block destruction. Dev-only, entirely gated behind the param.
// Open the SAME ?mp=ROOM&<level> in two windows to pair them on the same level.
{
  if (_MP_ROOM) {
    _showMpBootCurtain();   // hide the sandbox backdrop until the match arena is built (see _setupLevel)
    // Spectate affordance: a non-blocking hint + click-to-relock, so an idle MP window stays a live
    // visible spectator (the scene keeps rendering) instead of freezing on the pause menu.
    _mpSpectateBadge = document.createElement('button');
    // Browsers require ONE real click to capture the mouse (pointer lock) after a page load — there is no
    // way around that first gesture. So we don't make it a button to press: in a race it's a subtle,
    // NON-interactive hint (pointer-events:none) under the countdown, and clicking ANYWHERE on the arena
    // captures the mouse. The player clicks in during the 3·2·1, and GO drops them straight into play with
    // nothing to press. In plain co-op it stays the small top "click to play" hint.
    if (_RACE_ON) {
      _mpSpectateBadge.textContent = 'click anywhere to aim';
      _mpSpectateBadge.style.cssText = 'position:fixed;bottom:16%;left:50%;transform:translateX(-50%);'
        + 'z-index:2000;pointer-events:none;display:none;padding:0;border:none;background:transparent;'
        + 'color:#8fe6ff;font:700 15px/1 system-ui,sans-serif;letter-spacing:1px;text-shadow:0 2px 4px #000;';
    } else {
      _mpSpectateBadge.textContent = '▶ CLICK TO PLAY (co-op)';
      _mpSpectateBadge.style.cssText = 'position:fixed;top:14px;left:50%;transform:translateX(-50%);'
        + 'z-index:2000;cursor:pointer;display:none;padding:11px 20px;border:3px solid #33ccff;'
        + 'border-radius:9px;background:rgba(0,0,0,0.8);color:#33ccff;font:700 16px/1 system-ui,sans-serif;'
        + 'letter-spacing:1px;';
      // A real click = a user gesture, which is what pointer lock requires. Clicking the badge re-locks.
      _mpSpectateBadge.addEventListener('click', (e) => { e.stopPropagation(); _requestPointerLock(); });
    }
    document.body.appendChild(_mpSpectateBadge);
    // Clicking anywhere on the arena while idle captures the mouse — this is the single gesture that gets
    // a race player in (the hint above just tells them so). Also re-locks after an Esc mid-run.
    renderer.domElement.addEventListener('click', () => {
      if (_mpEnabled && !controls.isLocked && _runActive && !_levelEnded && !_raceOver) _requestPointerLock();
    });
    // Surface the hint/badge immediately (the auto-lock on level entry is denied without a gesture).
    _mpSpectateBadge.style.display = 'block';
    // Connection-status badge — so a network blip reads as "reconnecting" rather than "the game broke."
    // Sits just under the spectate badge; only visible while we're not connected.
    const _mpStatusBadge = document.createElement('div');
    _mpStatusBadge.textContent = '⟳ Reconnecting…';
    _mpStatusBadge.style.cssText = 'position:fixed;top:56px;left:50%;transform:translateX(-50%);'
      + 'z-index:2000;display:none;padding:7px 16px;border:2px solid #ffb020;border-radius:8px;'
      + 'background:rgba(0,0,0,0.82);color:#ffb020;font:700 13px/1 system-ui,sans-serif;letter-spacing:0.5px;';
    document.body.appendChild(_mpStatusBadge);
    import('./net/mp.js').then(({ mpInit }) => {
      _mp = mpInit({
        url: _MP_RELAY,
        room: _MP_ROOM,
        name: _mpPlayerName(),   // the player's saved nickname (Guest## only if they never set one)
        THREE, scene, camera,
        onNet: _mpOnNet,   // routes d / u / s peer events into the block sync
        onStatus: (s) => { _mpStatusBadge.style.display = (s === 'connected' || s === 'closed') ? 'none' : 'block'; },
        loadout: _runtimeGloveLoadout.slice(0, 2),   // our equipped gloves, so the peer's avatar wears them
        buildAvatarFist,                              // real fist model builder (FP bits stripped)
        springEnvMap: springMetalEnvMap,             // chrome reflections for the avatar's spring coil
        playerColors: _MP_PLAYER_COLORS,             // per-player identity palette (avatar accents + HUD bars)
        // resolve a block by netId to OUR copy's live position, so the peer's avatar aims/reaches at
        // where the block is on THIS screen (cause-mode positions can diverge — see the coin volley).
        resolveAim: (netId) => { const b = _netById.get(netId); return (b && b.position) ? b.position : null; },
      });
    }).catch((e) => console.error('[mp] failed to load net module', e));
  }
}

// Deep-link: if the page was opened with ?play=<id>, fetch that UGC level and drop the player
// onto the ready page (PLAY button) so they can enter it with one click. We use the ready
// page rather than auto-entering because pointer-lock requires a fresh user gesture.
{
  const playId = new URLSearchParams(location.search).get('play');
  if (playId) {
    // Strip the query so back-navigation / refresh doesn't keep firing this.
    history.replaceState(null, '', location.pathname);
    (async () => {
      try {
        const res = await fetch(`${UGC_SERVER_URL}/play/${encodeURIComponent(playId)}`);
        if (!res.ok) throw new Error(`server returned ${res.status}`);
        const data = await res.json();
        const { level, song, prompt } = data;
        _ugcLevelSpec = level;
        _ugcSongSpec = song;
        _ugcId = playId;
        _ugcPrompt = prompt;
        _ugcCreatorName = data.creatorName || null;
        LEVEL_CONFIG['ugc-current'].songSpec = song;
        // In an mp/race room, skip the ready page and drop straight into the shared arena so both
        // players land in the SAME level together (the "click to get ready" gesture covers pointer
        // lock). Solo deep-links still get the ready page (PLAY needs a fresh gesture for lock anyway).
        if (_MP_ROOM) _enterLevel('ugc-current', { force: true });
        else _renderUgcReadyPage(prompt);
      } catch (e) {
        console.error('[deep-link play]', e);
        // Fall through silently — user just sees the normal home view.
      }
    })();
  }
}

// Race/co-op boot fallback. A race link MUST land the player in the shared arena. Normally the boot flag
// (?gauntlet or ?play=<id>) does that, but if a link carries the race config (mp + arena) WITHOUT a boot
// flag, enter the arena from ?arena= so the player joins the session instead of stranding on the home
// screen (where a first-timer could wander into level1). Gauntlet enters directly; UGC arenas still need
// the ?play=<id> flag (the "Play with a Friend" modal always includes it), so we only cover gauntlet here.
if (_MP_ROOM && _RACE_ARENA && !_runActive
    && !new URLSearchParams(location.search).has('gauntlet')
    && !new URLSearchParams(location.search).has('play')
    && !new URLSearchParams(location.search).has('level')) {
  if (_RACE_ARENA === 'gauntlet') _enterLevel('gauntlet', { force: true });
  else if (LEVEL_CONFIG[_RACE_ARENA]) _enterLevel(_RACE_ARENA, { force: true });
  // UGC arenas still require ?play=<id> (ready page); the lobby launch always builds that in.
}

// Pause menu — overlay shown when the player exits pointer-lock mid-run (Esc). Keeps
// the run state alive so RESUME re-acquires lock without rebuilding the world. QUIT
// route is context-aware: UGC levels go to the Community page, world levels go back
// to their owning world's level select, sandbox/fallback go to the world tile screen.
const _pauseMenu = document.createElement('div');
_pauseMenu.style.cssText = 'position:fixed;inset:0;display:none;align-items:center;justify-content:center;'
  + 'z-index:1050;background:rgba(26,16,64,0.55);pointer-events:auto;';
const _pauseCard = document.createElement('div');
_pauseCard.style.cssText = 'background:var(--col-bg-panel);border:6px solid var(--col-cyan);'
  + 'box-shadow:6px 6px 0 var(--col-ink);padding:32px 36px 28px;display:flex;flex-direction:column;'
  + 'align-items:stretch;gap:14px;min-width:280px;max-width:360px;';
const _pauseTitle = document.createElement('div');
_pauseTitle.textContent = 'PAUSED';
// Title is cyan with ink stroke — text-only treatment keeps it visually distinct from the
// buttons below so the eye reads "header" not "fourth button". Cyan also frees pink for
// the destructive action and gold for the caution action.
_pauseTitle.style.cssText = 'font-family:var(--font-display);font-size:28px;color:var(--col-cyan);'
  + '-webkit-text-stroke:4px var(--col-ink);text-stroke:4px var(--col-ink);paint-order:stroke;'
  + 'text-align:center;letter-spacing:2px;margin-bottom:10px;';
_pauseCard.appendChild(_pauseTitle);
// Pause buttons map color to intent: cyan = primary (continue), gold = caution (lose
// in-run progress), pink = destructive (leave the level). RESUME is the primary action so
// it also gets a larger font + extra padding to draw the eye first.
function _makePauseBtn(label, variant, primary) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = label;
  const bg = variant === 'pink'   ? 'var(--col-pink)'
           : variant === 'green'  ? 'var(--col-green)'
           : variant === 'gold'   ? 'var(--col-gold)'
           : variant === 'orange' ? 'var(--col-orange)'
           : 'var(--col-cyan)';
  const fontSize = primary ? 18 : 13;
  const padV = primary ? 18 : 12;
  btn.style.cssText = `font-family:var(--font-display);font-size:${fontSize}px;letter-spacing:1px;`
    + `padding:${padV}px 18px;background:${bg};color:#FFFFFF;`
    + '-webkit-text-stroke:2px var(--col-ink);text-stroke:2px var(--col-ink);paint-order:stroke;'
    + 'border:3px solid var(--col-ink);'
    + 'box-shadow:4px 4px 0 var(--col-ink);cursor:pointer;text-transform:uppercase;'
    + 'transition:transform 60ms steps(1), box-shadow 60ms steps(1);';
  btn.addEventListener('mousedown', (e) => e.stopPropagation());
  btn.addEventListener('mouseenter', () => { btn.style.background = 'var(--col-bg-panel)'; });
  btn.addEventListener('mouseleave', () => { btn.style.background = bg; });
  btn.addEventListener('mousedown',  () => { btn.style.transform = 'translate(2px,2px)'; btn.style.boxShadow = '2px 2px 0 var(--col-ink)'; });
  btn.addEventListener('mouseup',    () => { btn.style.transform = ''; btn.style.boxShadow = '4px 4px 0 var(--col-ink)'; });
  return btn;
}
const _pauseResumeBtn   = _makePauseBtn('RESUME', 'green', true);
const _pauseRestartBtn  = _makePauseBtn('RESTART LEVEL', 'cyan', false);
const _pauseCalibrateBtn = _makePauseBtn('CALIBRATE AUDIO', 'orange', false);
const _pauseQuitBtn     = _makePauseBtn('QUIT', 'pink', false);
_pauseCard.appendChild(_pauseResumeBtn);
_pauseCard.appendChild(_pauseRestartBtn);
_pauseCard.appendChild(_pauseCalibrateBtn);
_pauseCard.appendChild(_pauseQuitBtn);
_pauseMenu.appendChild(_pauseCard);
document.body.appendChild(_pauseMenu);
// True pause: suspend the audio context (freezes music + audioCtx.currentTime, which is
// what the level timer + scheduler read), cancel the next music-loop setTimeout, and gate
// the main render loop's game updates on `_isPaused`. Because audioCtx.currentTime stops,
// _levelStartAudio-based elapsed math naturally pauses too, so the song can't end while
// the player is on the pause screen.
let _isPaused = false;
function _pauseGame() {
  if (_isPaused) return;
  _isPaused = true;
  if (_musicTimeoutId) {
    clearTimeout(_musicTimeoutId);
    _musicTimeoutId = null;
  }
  if (audioCtx && audioCtx.state === 'running') {
    audioCtx.suspend().catch(() => {});
  }
}
function _resumeGame() {
  if (!_isPaused) return;
  _isPaused = false;
  if (audioCtx && audioCtx.state === 'suspended') {
    audioCtx.resume().catch(() => {});
  }
  // Kick the music loop chain back to life. _scheduleNextLoop reschedules itself; we just
  // need to fire it once after pause cleared the pending timeout.
  if (_musicStarted && !_musicTimeoutId) {
    _scheduleNextLoop();
  }
}
function _hidePauseMenu() {
  _pauseMenu.style.display = 'none';
  // Reset any armed confirm states so reopening the menu doesn't surface stale "ARE YOU SURE?"
  _cancelPauseConfirms();
}
function _showPauseMenu() {
  // Multiplayer: the world is shared and can't pause. Any pause trigger (Esc, lost lock, lock
  // error) just drops this window into live spectate instead — no menu, no freeze.
  if (_mpEnabled) {
    if (_mpSpectateBadge) _mpSpectateBadge.style.display = 'block';
    return;
  }
  _pauseMenu.style.display = 'flex';
  _pauseGame();
}
_pauseResumeBtn.onclick = (e) => {
  e.stopPropagation();
  _cancelPauseConfirms();
  _resumeGame();
  _hidePauseMenu();
  _requestPointerLock();
};
_pauseCalibrateBtn.onclick = (e) => {
  e.stopPropagation();
  _cancelPauseConfirms();
  _startCalibration();
};

// =========================================================
// Audio calibration — measures the player's perceived offset between the API beat clock
// and what their audio output actually delivers. Triggered from the pause menu. Music
// keeps playing during calibration; a dedicated metronome click overlays the music on
// every beat. Player taps to the beat for 8 seconds; we capture API-time clicks, snap
// each to its nearest scheduled tick, and store the median offset to localStorage. The
// `_beatElapsed()` helper subtracts that offset, shifting scoring + visual peaks to land
// where the player actually hears the beat.
// =========================================================
const _CALIBRATION_DURATION_MS = 8000;
const _CALIBRATION_TICK_FREQ_HZ = 1400;
const _CALIBRATION_TICK_VOL = 0.4;
const _CALIBRATION_TRIM_FIRST_N = 2;     // first taps are warm-up; trim them
const _CALIBRATION_WARN_MS = 800;        // offset > this triggers a "this seems off" confirm

let _calibrationActive = false;
let _calibrationStartTickIdx = 0;
let _calibrationTickTimes = [];   // API times of scheduled metronome ticks
let _calibrationTaps = [];        // API times of player taps during the window
let _calibrationOscNodes = [];    // refs to scheduled oscillators so we can stop them on cancel
let _calibrationEndTimeoutId = null;

const _calibrationOverlay = document.createElement('div');
_calibrationOverlay.style.cssText = 'position:fixed;inset:0;display:none;align-items:center;'
  + 'justify-content:center;z-index:1100;background:rgba(10,8,32,0.92);pointer-events:auto;'
  + 'flex-direction:column;gap:20px;color:#fff;font-family:var(--font-display);'
  + 'text-align:center;padding:32px;user-select:none;';
const _calCard = document.createElement('div');
_calCard.style.cssText = 'background:var(--col-bg-panel);border:6px solid var(--col-cyan);'
  + 'box-shadow:6px 6px 0 var(--col-ink);padding:32px 36px;display:flex;flex-direction:column;'
  + 'align-items:center;gap:18px;min-width:360px;max-width:480px;';
const _calTitle = document.createElement('div');
_calTitle.textContent = 'CALIBRATE AUDIO';
_calTitle.style.cssText = 'font-size:24px;color:var(--col-ink);letter-spacing:2px;';
const _calBody = document.createElement('div');
_calBody.style.cssText = 'font-size:13px;color:var(--col-ink);line-height:1.6;'
  + 'font-family:var(--font-body,ui-monospace),monospace;opacity:0.85;';
const _calStatus = document.createElement('div');
_calStatus.style.cssText = 'font-size:18px;color:var(--col-ink);min-height:24px;letter-spacing:1px;';
const _calBeatDot = document.createElement('div');
// Large central dot that pulses with each scheduled tick — visual confirmation that the
// metronome is firing. Tap on this OR the surrounding card to register a tap.
_calBeatDot.style.cssText = 'width:96px;height:96px;border-radius:50%;background:var(--col-pink);'
  + 'border:6px solid var(--col-ink);box-shadow:4px 4px 0 var(--col-ink);'
  + 'transform:scale(1);transition:transform 90ms ease-out;cursor:pointer;';
const _calBtnRow = document.createElement('div');
_calBtnRow.style.cssText = 'display:flex;gap:12px;margin-top:6px;flex-wrap:wrap;justify-content:center;';
_calCard.appendChild(_calTitle);
_calCard.appendChild(_calBody);
_calCard.appendChild(_calBeatDot);
_calCard.appendChild(_calStatus);
_calCard.appendChild(_calBtnRow);
_calibrationOverlay.appendChild(_calCard);
document.body.appendChild(_calibrationOverlay);

// Click anywhere on the overlay (or the central dot) registers a tap during the active
// window. Outside the active window the click does nothing — buttons handle their own.
_calibrationOverlay.addEventListener('mousedown', (e) => {
  if (!_calibrationActive) return;
  // Ignore clicks on buttons in the row.
  if (e.target.tagName === 'BUTTON') return;
  e.stopPropagation();
  _calibrationTaps.push(audioCtx.currentTime);
  _calBeatDot.style.transform = 'scale(1.15)';
  setTimeout(() => { _calBeatDot.style.transform = 'scale(1)'; }, 90);
});

function _setCalButtons(buttons) {
  _calBtnRow.textContent = '';
  for (const b of buttons) _calBtnRow.appendChild(b);
}

function _calMakeBtn(label, variant, onClick) {
  const btn = _makePauseBtn(label, variant, false);
  btn.style.fontSize = '14px';
  btn.style.padding = '10px 16px';
  btn.onclick = (e) => { e.stopPropagation(); onClick(); };
  return btn;
}

function _scheduleCalibrationTicks() {
  // Start ticks ~250ms in the future so the audio context has time to fully resume and
  // first tick lands cleanly. End at the calibration duration.
  const startTime = audioCtx.currentTime + 0.25;
  const endTime = startTime + (_CALIBRATION_DURATION_MS / 1000);
  // First tick at the first beat boundary >= startTime.
  const elapsedAtStart = startTime - _musicLoopStartTime;
  const firstBeatIdx = Math.ceil(elapsedAtStart / MUSIC_BEAT);
  for (let i = 0; ; i++) {
    const tickTime = _musicLoopStartTime + (firstBeatIdx + i) * MUSIC_BEAT;
    if (tickTime > endTime) break;
    _scheduleOneTick(tickTime);
    _calibrationTickTimes.push(tickTime);
    // Schedule a dot-pulse at the tick time using setTimeout offset from now (wall-clock
    // approximation is fine — visual flash doesn't need sample-accurate timing).
    const msFromNow = (tickTime - audioCtx.currentTime) * 1000;
    setTimeout(() => {
      if (!_calibrationActive) return;
      _calBeatDot.style.transform = 'scale(1.25)';
      setTimeout(() => { _calBeatDot.style.transform = 'scale(1)'; }, 100);
    }, msFromNow);
  }
}

function _scheduleOneTick(tickTime) {
  const osc = audioCtx.createOscillator();
  const gain = audioCtx.createGain();
  osc.type = 'square';
  osc.frequency.value = _CALIBRATION_TICK_FREQ_HZ;
  gain.gain.setValueAtTime(0.0001, tickTime);
  gain.gain.linearRampToValueAtTime(_CALIBRATION_TICK_VOL, tickTime + 0.003);
  gain.gain.exponentialRampToValueAtTime(0.001, tickTime + 0.07);
  osc.connect(gain).connect(_sfxBus || audioCtx.destination);
  osc.start(tickTime);
  osc.stop(tickTime + 0.1);
  _calibrationOscNodes.push(osc);
}

function _startCalibration() {
  if (_calibrationActive) return;
  _calibrationTaps = [];
  _calibrationTickTimes = [];
  _calibrationOscNodes = [];
  _calBody.textContent = 'Tap with the metronome click. Music will play.';
  _calStatus.textContent = 'Get ready...';
  _setCalButtons([
    _calMakeBtn('CANCEL', 'pink', () => _stopCalibration('cancel')),
  ]);
  _calibrationOverlay.style.display = 'flex';

  // Resume the audio context (was suspended by _pauseGame) so the metronome + music can
  // play. We DON'T flip _isPaused — game logic stays frozen, only audio runs. The music
  // scheduler restart mirrors what _resumeGame does without the game-state side effects.
  const begin = () => {
    if (_musicStarted && !_musicTimeoutId) _scheduleNextLoop();
    _calibrationActive = true;
    _scheduleCalibrationTicks();
    _calStatus.textContent = 'Tap to the beat!';
    _calibrationEndTimeoutId = setTimeout(() => _stopCalibration('complete'), _CALIBRATION_DURATION_MS + 300);
  };
  if (audioCtx && audioCtx.state === 'suspended') {
    audioCtx.resume().then(begin).catch(begin);
  } else {
    begin();
  }
}

function _stopCalibration(reason) {
  if (!_calibrationActive && reason !== 'cancel') return;
  _calibrationActive = false;
  if (_calibrationEndTimeoutId) { clearTimeout(_calibrationEndTimeoutId); _calibrationEndTimeoutId = null; }
  // Stop scheduled future ticks (already-played ones can't be unstopped, but we cancel
  // anything still pending).
  for (const osc of _calibrationOscNodes) { try { osc.stop(); } catch {} }
  _calibrationOscNodes = [];
  // Re-pause audio: clear music scheduler + suspend audioCtx. Mirrors _pauseGame's audio
  // half without touching _isPaused.
  if (_musicTimeoutId) { clearTimeout(_musicTimeoutId); _musicTimeoutId = null; }
  if (audioCtx && audioCtx.state === 'running') audioCtx.suspend().catch(() => {});

  if (reason === 'cancel') {
    _calibrationOverlay.style.display = 'none';
    return;
  }
  _showCalibrationResult();
}

function _showCalibrationResult() {
  const trimmedTaps = _calibrationTaps.slice(_CALIBRATION_TRIM_FIRST_N);
  if (trimmedTaps.length === 0) {
    _calStatus.textContent = "No taps registered — let's try again.";
    _setCalButtons([
      _calMakeBtn('RETRY', 'cyan', () => _startCalibration()),
      _calMakeBtn('CANCEL', 'pink', () => { _calibrationOverlay.style.display = 'none'; }),
    ]);
    return;
  }
  // Snap each tap to its nearest scheduled tick; collect signed offsets.
  const offsets = trimmedTaps.map(tap => {
    let bestTick = _calibrationTickTimes[0], bestDist = Math.abs(tap - bestTick);
    for (const tick of _calibrationTickTimes) {
      const d = Math.abs(tap - tick);
      if (d < bestDist) { bestTick = tick; bestDist = d; }
    }
    return tap - bestTick;  // positive = tap was after tick = player late
  });
  // Median is more robust to one stray tap than mean.
  offsets.sort((a, b) => a - b);
  const median = offsets[Math.floor(offsets.length / 2)];
  const ms = Math.round(median * 1000);
  const absMs = Math.abs(ms);

  const sign = ms > 0 ? 'late' : ms < 0 ? 'early' : 'on beat';
  _calBody.innerHTML = `<div>You tapped <strong>${absMs}ms ${sign}</strong> on average across ${trimmedTaps.length} taps.</div>`
    + (ms > 0
        ? `<div style="margin-top:8px;">Looks like your audio is delayed by ${absMs}ms — saving this will shift the game's scoring to match what you hear.</div>`
        : ms < 0
          ? `<div style="margin-top:8px;">You're consistently early. Saving this will shift scoring to match.</div>`
          : `<div style="margin-top:8px;">Nice — no calibration needed.</div>`);
  _calStatus.textContent = '';

  const doSave = () => {
    _saveAudioOffsetSec(median);
    _calStatus.textContent = `Saved (${ms >= 0 ? '+' : ''}${ms}ms).`;
    _setCalButtons([
      _calMakeBtn('CLOSE', 'green', () => { _calibrationOverlay.style.display = 'none'; }),
    ]);
  };

  const buttons = [];
  if (absMs > _CALIBRATION_WARN_MS) {
    _calBody.innerHTML += `<div style="margin-top:10px;color:var(--col-pink);">This is quite large (>${_CALIBRATION_WARN_MS}ms). You might want to redo it.</div>`;
    buttons.push(_calMakeBtn('RETRY', 'cyan', () => _startCalibration()));
    buttons.push(_calMakeBtn('SAVE ANYWAY', 'orange', doSave));
  } else {
    buttons.push(_calMakeBtn('SAVE', 'green', doSave));
    buttons.push(_calMakeBtn('RETRY', 'cyan', () => _startCalibration()));
  }
  buttons.push(_calMakeBtn('CANCEL', 'pink', () => { _calibrationOverlay.style.display = 'none'; }));
  _setCalButtons(buttons);
}

// Two-click confirmation for the destructive paths. First click arms the button ("ARE YOU
// SURE?" + auto-revert at 3s). Second click within the window commits. Anything else cancels.
const _pauseConfirmArmed = { restart: false, quit: false };
const _pauseConfirmTimers = { restart: null, quit: null };
const _pauseConfirmLabels = { restart: 'RESTART LEVEL', quit: 'QUIT' };
function _cancelPauseConfirms() {
  for (const k of ['restart', 'quit']) {
    if (_pauseConfirmTimers[k]) { clearTimeout(_pauseConfirmTimers[k]); _pauseConfirmTimers[k] = null; }
    _pauseConfirmArmed[k] = false;
  }
  _pauseRestartBtn.textContent = _pauseConfirmLabels.restart;
  _pauseQuitBtn.textContent    = _pauseConfirmLabels.quit;
}
function _armPauseConfirm(key, btn) {
  // Disarm the OTHER button if it was armed — only one confirmation at a time.
  for (const k of ['restart', 'quit']) {
    if (k !== key && _pauseConfirmTimers[k]) {
      clearTimeout(_pauseConfirmTimers[k]);
      _pauseConfirmTimers[k] = null;
      _pauseConfirmArmed[k] = false;
      (k === 'restart' ? _pauseRestartBtn : _pauseQuitBtn).textContent = _pauseConfirmLabels[k];
    }
  }
  _pauseConfirmArmed[key] = true;
  btn.textContent = 'ARE YOU SURE?';
  _pauseConfirmTimers[key] = setTimeout(() => {
    _pauseConfirmArmed[key] = false;
    _pauseConfirmTimers[key] = null;
    btn.textContent = _pauseConfirmLabels[key];
  }, 3000);
}

_pauseRestartBtn.onclick = (e) => {
  e.stopPropagation();
  if (!_currentLevel) { _hidePauseMenu(); return; }
  if (!_pauseConfirmArmed.restart) { _armPauseConfirm('restart', _pauseRestartBtn); return; }
  _cancelPauseConfirms();
  _resumeGame();           // resume audio so the new level's startMusic plays cleanly
  _hidePauseMenu();
  _runActive = false;      // force _enterLevel's fresh-rebuild path instead of resume
  _enterLevel(_currentLevel);
};
_pauseQuitBtn.onclick = (e) => {
  e.stopPropagation();
  if (!_pauseConfirmArmed.quit) { _armPauseConfirm('quit', _pauseQuitBtn); return; }
  _cancelPauseConfirms();
  _resumeGame();           // resume audio so any home-screen music etc. plays cleanly
  _hidePauseMenu();
  _runActive = false;
  _coinHudGroup.style.display = 'none';
  _streakBadge.style.display = 'none';
  _coachDisarm();
  _hudSpeakerFloat.style.display = 'none';
  _hudSpeakerFloatRight.style.display = 'none';
  if (_isPracticeLevel()) {
    _practiceMode = null;
    _setRuntimeGloveLoadout(gloveLoadout);
  }
  // Route by level type so QUIT lands the player where they came from.
  if (_currentLevel === 'ugc-current') {
    _homeView = 'community';
    _currentWorld = null;
  } else {
    const owningWorld = WORLDS.find(w => w.levelIds.includes(_currentLevel));
    if (owningWorld) {
      _currentWorld = owningWorld.id;
      _homeView = 'world-select';
    } else {
      _currentWorld = null;
      _homeView = 'world-select';
    }
  }
  _renderHome();
  lockHint.style.display = 'block';
  document.body.classList.remove('in-game');
};

// Esc toggles the pause menu. Both directions are handled here so the response is
// instant — without this, opening waits for the browser's pointerlock unlock event
// (~50-100ms), which felt laggy. Closing also rides this path. The browser's parallel
// unlock still fires; the unlock handler's _showPauseMenu call is a safe no-op because
// _pauseGame is _isPaused-guarded and display:flex is idempotent.
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  // Pause menu open → close (resume).
  if (_pauseMenu.style.display === 'flex') {
    e.preventDefault();
    _pauseResumeBtn.click();
    return;
  }
  // "Stay paused" path: user just hit RESUME and the relock is pending (browser blocks
  // pointer-lock for ~1.25s after every Esc, so a quick Esc-Esc-Esc gets caught here).
  // Without this, the third Esc would do nothing until the retry locks ~1.3s later.
  // Cancel the pending relock and re-show the pause menu instantly.
  if (_wantLock && !controls.isLocked && _runActive && !_levelEnded) {
    e.preventDefault();
    _wantLock = false;  // pending retry's check skips when this is false
    _showPauseMenu();
    return;
  }
  // Mid-run, pointer locked → open pause menu immediately. Skip if a clear screen is
  // already up (its own dismiss should take priority).
  if (!_runActive || _levelEnded) return;
  if (_clearScreen && _clearScreen.style.display === 'flex') return;
  if (!controls.isLocked) return;
  _showPauseMenu();
});

// Per-tab audio mute — press M to silence THIS tab. Handy for multiplayer testing across two tabs/
// machines where the browser can't mute a single tab's WebAudio. Cuts the whole master bus off the
// speakers (music + every SFX), so it's a true local mute that changes nothing the peer hears.
let _audioMuted = false;
let _muteIndicator = null;
function _applyMute() {
  if (audioCtx && _masterBus) {
    try {
      if (_audioMuted) _masterBus.disconnect(audioCtx.destination);
      else _masterBus.connect(audioCtx.destination);
    } catch { /* already (dis)connected — fine */ }
  }
  if (!_muteIndicator) {
    _muteIndicator = document.createElement('div');
    _muteIndicator.style.cssText = 'position:fixed;top:10px;right:12px;z-index:99999;font:bold 12px system-ui,sans-serif;color:#fff;background:rgba(180,30,30,0.88);padding:4px 9px;border-radius:5px;pointer-events:none;letter-spacing:0.5px;';
    _muteIndicator.textContent = 'MUTED  (M)';
    document.body.appendChild(_muteIndicator);
  }
  _muteIndicator.style.display = _audioMuted ? 'block' : 'none';
}
document.addEventListener('keydown', (e) => {
  if (e.code !== 'KeyM') return;
  if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA')) return;  // don't hijack typing
  _audioMuted = !_audioMuted;
  _applyMute();
});

// Inline display toggle (bypasses the .hidden class — inline style would override it anyway).
// Also toggles body.in-game so the CSS rule with !important catches any state-machine
// edge case where the inline display would otherwise leak menu chrome into gameplay.
controls.addEventListener('lock',   () => {
  _wantLock = false;  // achieved
  _pointerLockRetryAttempted = false;  // success — clear retry latch for the next entry
  lockHint.style.display = 'none';
  document.body.classList.add('in-game');
  _hidePauseMenu();  // RESUME path: pointer lock re-acquired, hide pause overlay.
  if (_mpSpectateBadge) _mpSpectateBadge.style.display = 'none';  // MP: now driving this window
  // The coin HUD is the SOLO score panel. In a multiplayer match it's hidden: the MP scoreboard
  // (_raceRenderHud) shows the chosen metric (smashes/coins) instead, so we don't double up.
  const _showSoloCoinHud = !_RACE_ON;
  _coinHudGroup.style.display = _showSoloCoinHud ? 'flex' : 'none';   // show coin HUD only while playing solo
  _streakBadge.style.display = 'flex';    // streak badge follows the same gameplay-only lifecycle (flex = its column layout)
  _updateStreakHud();                     // refresh opacity for the current streak (0 on a fresh level = hidden)
  _coachShow();                           // Level 1 controls coach (no-op unless armed for L1)
  _hudSpeakerFloat.style.display = 'block';
  _hudSpeakerFloatRight.style.display = 'block';
  // HUD entrance — quick slide/pop-in so the gameplay UI feels produced instead of blinking
  // on. The WAAPI transforms win over the per-frame beat-anchor transform during the short
  // run, then release back to it (no fill:forwards). Coin panel drops in from the top; the
  // speakers rise from the bottom with a small stagger.
  if (_showSoloCoinHud) _coinHudGroup.animate([
    { opacity: 0, transform: 'translateY(-26px) scale(0.96)' },
    { opacity: 1, transform: 'translateY(0) scale(1)' },
  ], { duration: 420, easing: 'cubic-bezier(0.2,0.8,0.2,1)' });
  [_hudSpeakerFloat, _hudSpeakerFloatRight].forEach((el, i) => {
    el.animate([
      { opacity: 0, transform: 'translateY(34px) scale(0.8)' },
      { opacity: 1, transform: 'translateY(0) scale(1)' },
    ], { duration: 460, delay: 70 + i * 50, easing: 'cubic-bezier(0.2,0.8,0.2,1)', fill: 'backwards' });
  });
});
controls.addEventListener('unlock', () => {
  _wantLock = false;  // user Esc'd or browser dropped lock; don't auto-relock
  _coinHudGroup.style.display = 'none';   // always hide HUD on exit (covers clear-screen too)
  _streakBadge.style.display = 'none';    // hide the streak badge too so it can't linger over menus
  _coachHide();                           // hide the L1 coach card on exit (stays armed for resume)
  _hudSpeakerFloat.style.display = 'none';
  _hudSpeakerFloatRight.style.display = 'none';
  // Clear screen takes priority — don't double-overlay it with the home panel or pause menu.
  if (_clearScreen && _clearScreen.style.display === 'flex') return;
  // Race finish: we deliberately freed the cursor to click Play Again / Leave. Don't show the spectate
  // "click to aim" hint (which would re-lock and hide the cursor) or the pause menu here.
  if (_raceOver) { if (_mpSpectateBadge) _mpSpectateBadge.style.display = 'none'; return; }
  // Multiplayer: the world is SHARED, so releasing the mouse does NOT pause it — the window stays
  // live and keeps syncing (that's what lets a second window show you moving/smashing while you
  // play the other). Just surface a non-blocking "click to play" badge; clicking re-locks.
  if (_mpEnabled && _runActive && !_levelEnded) {
    if (_mpSpectateBadge) _mpSpectateBadge.style.display = 'block';
    return;
  }
  // Mid-run Esc → pause menu instead of bouncing to home. Keeps the world built so RESUME
  // is instant. _runActive stays true; RESTART/QUIT clear it explicitly.
  if (_runActive && !_levelEnded) {
    _showPauseMenu();
    return;
  }
  _renderHome();
  lockHint.style.display = 'block';
  document.body.classList.remove('in-game');
});

// Browser blocks lock requests for ~1.25s after Esc-exit. If our request lands during that
// window pointerlockerror fires; retry once at 1.3s while user-activation is still valid.
// If retry also fails (cooldown longer than expected, or activation expired), restore the
// home panel so the user can click again — otherwise they're stranded with no cursor and no UI.
let _pointerLockRetryAttempted = false;
document.addEventListener('pointerlockerror', () => {
  if (!_wantLock) return;
  if (_pointerLockRetryAttempted) {
    // Both attempts failed — typical cause is the player mashing Esc (each press resets
    // the browser's pointer-lock cooldown, so the retry lands inside another cooldown
    // window). If a run is still alive, keep it: re-show the pause menu so RESUME is one
    // click away. Otherwise (no run in progress) fall back to home as the original safety net.
    _wantLock = false;
    _pointerLockRetryAttempted = false;
    if (_runActive && !_levelEnded) {
      _showPauseMenu();
      return;
    }
    if (lockHint) lockHint.style.display = 'block';
    _renderHome();
    return;
  }
  if (_pointerLockRetryPending) return;
  _pointerLockRetryPending = true;
  _pointerLockRetryAttempted = true;
  setTimeout(() => {
    _pointerLockRetryPending = false;
    if (_wantLock && !controls.isLocked) {
      try { controls.lock(); } catch (_) {}
    }
  }, 1300);
});

// (Skip-section button is now a row inside the admin panel below.)

// Brick bit-crusher — quantize curve + drive + tone LP; mixed in parallel with dry brick body.
// Hoisted above the mix panel so the panel UI can read default values without TDZ errors.
const _brickCrush = { level: 1.0, bits: 4, drive: 2.53, mix: 0.37, tone: 4480, hpf: 54 };
const _crateCrush = { level: 1.0, bits: 4, drive: 3.33, mix: 0.5, tone: 4929, hpf: 80 };
// Click-start blip — quieter default; tunable from the mix panel.
const _clickShape = { level: 0.4 };
// Metal sound-shaping (NOT bit-crusher — metal is a clean bell tone). Defaults all 1.0/0/20000 = unchanged sound.
// `partialMode` swaps the metal-smash partial ratios between three timbral families:
//   • 'inharmonic' (default): [1.0, 1.62, 2.71, 3.95] — physical-metal-plate modes; sounds
//      most "metallic" but partials clash harmonically with whatever chord is playing
//   • 'glockenspiel':         [1, 2.76, 5.4] — real glockenspiel bar partials. Bell-like
//      shimmer that's better tuned to musical intervals than inharmonic
//   • 'harmonic':             [1, 2, 3, 4] — pure harmonic series. Cleanest fit with any
//      chord (all partials are octaves and 5ths of the fundamental). Most chime-like
//
// Each mode has its OWN shape settings (level / sustain / strike / clang / detune /
// brightness). The admin sliders edit whichever mode is currently active and auto-sync
// to the saved values when you flip modes.
const METAL_PARTIAL_MODES = {
  inharmonic:   { partials: [1.0, 1.62, 2.71, 3.95], gains: [0.42, 0.30, 0.18, 0.10] },
  glockenspiel: { partials: [1, 2.76, 5.4],          gains: [0.50, 0.30, 0.15] },
  harmonic:     { partials: [1, 2, 3, 4],            gains: [0.50, 0.28, 0.16, 0.08] },
};
const _metalShapes = {
  // Original defaults — tuned for the "metallic plate" character.
  inharmonic:   { level: 0.73, sustain: 1.41, strike: 1.16, clang: 1.12, detune: 1.8, brightness: 20000 },
  // Glockenspiel: louder + longer ring, less noisy attack, brighter clang for shimmer.
  glockenspiel: { level: 0.85, sustain: 1.80, strike: 0.70, clang: 1.40, detune: 0.5, brightness: 20000 },
  // Harmonic chime: tuned to sit in the mix without dwarfing the music. Lower level + short
  // sustain stops the partials sustaining into the next musical event.
  harmonic:     { level: 0.63, sustain: 1.00, strike: 0.60, clang: 1.30, detune: 0.3, brightness: 20000 },
};
let _metalPartialMode = 'harmonic';
function _activeMetalShape() { return _metalShapes[_metalPartialMode] || _metalShapes.inharmonic; }

// Deterministic per-block per-partial detune. Hashes (block position + partial index) into
// a stable -maxCents..+maxCents value. Same block hit repeatedly always produces the same
// harmonic shape; different blocks still get varied shapes for organic variety. Replaces
// the previous Math.random() approach which made each hit on the same block sound subtly
// different (audibly inconsistent).
function _detuneCentsForPartial(pos, partialIdx, maxCents) {
  if (!pos || maxCents <= 0) return 0;
  const x = Math.floor((pos.x + 1000) * 100) | 0;
  const y = Math.floor((pos.y + 1000) * 100) | 0;
  const z = Math.floor((pos.z + 1000) * 100) | 0;
  let h = (x * 73856093) ^ (y * 19349663) ^ (z * 83492791) ^ (partialIdx * 12345701);
  h = (h ^ (h >>> 13)) * 0x5bd1e995;
  h = (h ^ (h >>> 15)) >>> 0;
  const r = ((h & 0xffff) / 65535) * 2 - 1;  // -1..+1
  return r * maxCents;
}
let _brickCrushCurve = null;
let _brickCrushCurveBits = -1;
function _getBrickCrushCurve(bits) {
  if (bits === _brickCrushCurveBits && _brickCrushCurve) return _brickCrushCurve;
  const samples = 4096;
  const curve = new Float32Array(samples);
  const levels = Math.pow(2, bits) - 1;
  const step = 2 / levels;
  for (let i = 0; i < samples; i++) {
    const x = (i / (samples - 1)) * 2 - 1;
    curve[i] = Math.max(-1, Math.min(1, Math.round(x / step) * step));
  }
  _brickCrushCurveBits = bits;
  _brickCrushCurve = curve;
  return curve;
}

// Admin panel — dev/config controls. Header + collapsible body. Sliders + dev buttons live here.
const _adminPanel = document.createElement('div');
_adminPanel.style.cssText = 'position:fixed;bottom:14px;left:14px;z-index:1000;padding:0;font:11px ui-monospace,monospace;background:rgba(40,40,50,0.85);color:#cfd6dc;border:1px solid #555;border-radius:3px;display:flex;flex-direction:column;user-select:none;max-width:280px;';
// Hidden for launch — flip to '' (empty string) or remove this line to restore the dev panel.
// Admin panel hidden by default. Visit `?admin` (or `?admin=1`) to auto-open it on load —
// bookmark that URL for a one-click way back into tuning without pressing backtick.
// Admin access is restricted to local dev (`vite dev` on localhost / 127.0.0.1). On the
// public deploy this guard is false so the URL flag is inert and the backtick key
// shortcut also does nothing — players can't stumble into the dashboard.
const _IS_ADMIN_HOST = (typeof location !== 'undefined') && (
  location.hostname === 'localhost' || location.hostname === '127.0.0.1' || location.hostname === '[::1]'
);
const _adminUrlOpen = _IS_ADMIN_HOST && (new URLSearchParams(location.search).has('admin'));
_adminPanel.style.display = _adminUrlOpen ? 'flex' : 'none';

// Header bar — clickable to collapse/expand the body.
const _adminHeader = document.createElement('div');
_adminHeader.style.cssText = 'padding:6px 10px;display:flex;align-items:center;justify-content:space-between;cursor:pointer;background:rgba(20,20,28,0.6);border-bottom:1px solid #444;';
const _adminTitle = document.createElement('span');
_adminTitle.textContent = 'admin';
_adminTitle.style.cssText = 'font-size:11px;letter-spacing:0.15em;text-transform:uppercase;color:#ffd66b;';
// Global "reset all" — flips every section's reset list. Sections are wired up below;
// the button reads them by name at click time so order doesn't matter.
const _resetAllBtn = document.createElement('button');
_resetAllBtn.textContent = '↺ all';
_resetAllBtn.title = 'reset every section to its shipped defaults';
_resetAllBtn.style.cssText = 'font-size:10px;background:transparent;color:#888;border:1px solid #555;border-radius:2px;padding:0 6px;margin-left:auto;margin-right:8px;cursor:pointer;line-height:16px;';
_resetAllBtn.addEventListener('mousedown', (e) => e.stopPropagation());
_resetAllBtn.addEventListener('click', (e) => {
  e.stopPropagation();
  for (const list of [_volResets, _brickResets, _crateResets, _metalResets, _toppleResets, _boundsResets, _visualResets]) {
    if (list) for (const r of list) r();
  }
});
const _adminToggle = document.createElement('span');
_adminToggle.textContent = '▼';
_adminToggle.style.cssText = 'font-size:10px;color:#888;';
_adminHeader.appendChild(_adminTitle);
_adminHeader.appendChild(_resetAllBtn);
_adminHeader.appendChild(_adminToggle);
_adminPanel.appendChild(_adminHeader);

// Body — holds all the slider rows + dev buttons. Toggled via the header.
const _adminBody = document.createElement('div');
_adminBody.style.cssText = 'padding:8px 12px;display:flex;flex-direction:column;gap:4px;max-height:80vh;overflow-y:auto;';
_adminPanel.appendChild(_adminBody);

let _adminCollapsed = false;
_adminHeader.addEventListener('click', (e) => {
  e.stopPropagation();
  _adminCollapsed = !_adminCollapsed;
  _adminBody.style.display = _adminCollapsed ? 'none' : 'flex';
  _adminToggle.textContent = _adminCollapsed ? '▶' : '▼';
});
// Block clicks/mousedowns on the panel from bubbling — otherwise they hit the canvas pointer-lock listener.
_adminPanel.addEventListener('mousedown', (e) => e.stopPropagation());
_adminPanel.addEventListener('click', (e) => e.stopPropagation());

// Dev row — skip-to-next-section + unlock-all toggle for level testing.
const _devRow = document.createElement('div');
_devRow.style.cssText = 'display:flex;gap:6px;margin-bottom:4px;';
const _skipBtn = document.createElement('button');
_skipBtn.textContent = 'next 16 bars';
_skipBtn.style.cssText = 'flex:1;padding:4px 8px;font:11px ui-monospace,monospace;background:rgba(60,60,70,0.85);color:#cfd6dc;border:1px solid #555;border-radius:2px;cursor:pointer;';
_skipBtn.addEventListener('click', (e) => { e.stopPropagation(); skipToNextSection(); });
_devRow.appendChild(_skipBtn);
const _unlockBtn = document.createElement('button');
_unlockBtn.style.cssText = 'flex:1;padding:4px 8px;font:11px ui-monospace,monospace;background:rgba(60,60,70,0.85);color:#cfd6dc;border:1px solid #555;border-radius:2px;cursor:pointer;';
function _renderUnlockBtn() {
  _unlockBtn.textContent = _devUnlockAll ? 'unlocks: ON' : 'unlocks: OFF';
  _unlockBtn.style.color = _devUnlockAll ? '#ffd66b' : '#cfd6dc';
}
_renderUnlockBtn();
_unlockBtn.addEventListener('click', (e) => {
  e.stopPropagation();
  _devUnlockAll = !_devUnlockAll;
  try { localStorage.setItem(_DEV_UNLOCK_KEY, _devUnlockAll ? '1' : '0'); } catch (e) {}
  _renderUnlockBtn();
  _renderHome();
});
_devRow.appendChild(_unlockBtn);
_adminBody.appendChild(_devRow);

const _rewardTestRow = document.createElement('div');
_rewardTestRow.style.cssText = 'display:flex;gap:6px;margin-bottom:4px;';
const _rewardTestBtn = document.createElement('button');
_rewardTestBtn.style.cssText = 'flex:1;padding:4px 8px;font:11px ui-monospace,monospace;background:rgba(60,60,70,0.85);color:#cfd6dc;border:1px solid #555;border-radius:2px;cursor:pointer;';
function _renderRewardTestBtn() {
  _rewardTestBtn.textContent = _devUnlockRewardTest ? 'reward test: ON' : 'reward test: OFF';
  _rewardTestBtn.style.color = _devUnlockRewardTest ? '#ffd66b' : '#cfd6dc';
}
_renderRewardTestBtn();
_rewardTestBtn.addEventListener('click', (e) => {
  e.stopPropagation();
  _devUnlockRewardTest = !_devUnlockRewardTest;
  try { localStorage.setItem(_DEV_UNLOCK_REWARD_TEST_KEY, _devUnlockRewardTest ? '1' : '0'); } catch (e) {}
  _renderRewardTestBtn();
});
_rewardTestRow.appendChild(_rewardTestBtn);
_adminBody.appendChild(_rewardTestRow);

// Alias kept so legacy slider/divider/header helpers below don't need rewriting.
const _mixPanel = _adminBody;
// Returns { row, reset } — reset() restores slider to its initial 'value' and fires onChange.
function _makeSlider({ label, min, max, step, value, format, onChange }) {
  const row = document.createElement('label');
  row.style.cssText = 'display:flex;align-items:center;gap:8px;cursor:pointer;';
  const tag = document.createElement('span');
  tag.style.cssText = 'width:42px;';
  tag.textContent = label;
  const slider = document.createElement('input');
  slider.type = 'range';
  slider.min = String(min); slider.max = String(max); slider.step = String(step);
  slider.value = String(value);
  slider.style.cssText = 'width:110px;';
  const valTag = document.createElement('span');
  valTag.style.cssText = 'width:46px;text-align:right;';
  valTag.textContent = format(value);
  const stop = (e) => e.stopPropagation();
  slider.addEventListener('mousedown', stop);
  slider.addEventListener('click', stop);
  slider.addEventListener('input', (e) => {
    e.stopPropagation();
    const v = parseFloat(slider.value);
    valTag.textContent = format(v);
    onChange(v);
  });
  row.appendChild(tag); row.appendChild(slider); row.appendChild(valTag);
  const reset = () => {
    slider.value = String(value);
    valTag.textContent = format(value);
    onChange(value);
  };
  return { row, reset };
}
// Adds slider to mix panel and pushes its reset fn into the supplied list.
function _addSlider(resetList, opts) {
  const s = _makeSlider(opts);
  _mixPanel.appendChild(s.row);
  resetList.push(s.reset);
}
function _addDivider() {
  const d = document.createElement('div');
  d.style.cssText = 'border-top:1px solid #444;margin:4px 0 2px;';
  _mixPanel.appendChild(d);
}
// Section header with inline ↺ reset button that fires every reset fn in the list.
function _addSectionHeader(text, resetList) {
  const row = document.createElement('div');
  row.style.cssText = 'display:flex;align-items:center;justify-content:space-between;margin-bottom:1px;';
  const l = document.createElement('span');
  l.style.cssText = 'color:#888;font-size:10px;';
  l.textContent = text;
  const btn = document.createElement('button');
  btn.textContent = '↺';
  btn.title = 'reset section to defaults';
  btn.style.cssText = 'font-size:11px;background:transparent;color:#888;border:1px solid #555;border-radius:2px;padding:0 5px;cursor:pointer;line-height:14px;';
  btn.addEventListener('mousedown', (e) => e.stopPropagation());
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    for (const r of resetList) r();
  });
  row.appendChild(l); row.appendChild(btn);
  _mixPanel.appendChild(row);
}

const _volResets = [];
_addSectionHeader('volume', _volResets);
_addSlider(_volResets, {
  label: 'music', min: 0, max: 100, step: 1, value: 23,
  format: (v) => String(v | 0),
  onChange: (v) => {
    const g = v / 100;
    if (_musicGain && audioCtx) {
      const t = audioCtx.currentTime;
      _musicGain.gain.cancelScheduledValues(t);
      _musicGain.gain.setValueAtTime(g, t);
    }
    _MUSIC_VOLUME_TARGET = g;
  },
});
_addSlider(_volResets, {
  label: 'sfx', min: 0, max: 100, step: 1, value: 100,
  format: (v) => String(v | 0),
  onChange: (v) => {
    if (_sfxBus && audioCtx) {
      const t = audioCtx.currentTime;
      _sfxBus.gain.cancelScheduledValues(t);
      _sfxBus.gain.setValueAtTime(v / 100, t);
    }
  },
});
_addSlider(_volResets, {
  label: 'click', min: 0, max: 150, step: 1, value: 40,  // 40% = current default click-start level
  format: (v) => `${v | 0}%`,
  onChange: (v) => { _clickShape.level = v / 100; },
});
// Timing window slider removed 2026-05-16 — locked to the 78% default below so in-game
// adjustments can't drift the playtest baseline. To re-expose, copy back the _addSlider
// call with the old label/format/onChange and bump _timingLeniency in step.
// Keyboard input has higher latency + bigger key travel than mouse clicks; this multiplier
// scales BOTH perfect and good windows for keyboard-triggered presses only. Default 2× so
// space-bar timing feels comparable to mouse without widening the global window (which
// would make mash-to-win viable on mouse too). 100% = no boost; 400% = very forgiving.
_addSlider(_volResets, {
  label: 'kb x', min: 100, max: 400, step: 5, value: 140,
  format: (v) => `${(v / 100).toFixed(2)}x`,
  onChange: (v) => { _kbLeniencyMult = v / 100; },
});

_addDivider();
const _brickResets = [];
_addSectionHeader('brick crush', _brickResets);
_addSlider(_brickResets, {
  label: 'level', min: 0, max: 150, step: 1, value: 100,
  format: (v) => `${v | 0}%`,
  onChange: (v) => { _brickCrush.level = v / 100; },
});
_addSlider(_brickResets, {
  label: 'bits', min: 1, max: 8, step: 1, value: 4,
  format: (v) => `${v | 0}-bit`,
  onChange: (v) => { _brickCrush.bits = v | 0; },
});
_addSlider(_brickResets, {
  label: 'drive', min: 0, max: 100, step: 1, value: 58,  // 58 → ~2.53×
  format: (v) => `${(0.5 + v / 100 * 3.5).toFixed(2)}x`,
  onChange: (v) => { _brickCrush.drive = 0.5 + v / 100 * 3.5; },
});
_addSlider(_brickResets, {
  label: 'mix', min: 0, max: 100, step: 1, value: 37,
  format: (v) => `${v | 0}%`,
  onChange: (v) => { _brickCrush.mix = v / 100; },
});
_addSlider(_brickResets, {
  label: 'tone', min: 0, max: 100, step: 1, value: 69,  // 69 → ~4480 Hz
  format: (v) => `${Math.round(500 * Math.pow(24, v / 100))} Hz`,
  onChange: (v) => { _brickCrush.tone = 500 * Math.pow(24, v / 100); },
});
_addSlider(_brickResets, {
  label: 'hpf', min: 0, max: 100, step: 1, value: 23,  // 23 → ~54 Hz
  format: (v) => `${Math.round(20 * Math.pow(75, v / 100))} Hz`,
  onChange: (v) => { _brickCrush.hpf = 20 * Math.pow(75, v / 100); },
});

_addDivider();
const _crateResets = [];
_addSectionHeader('crate crush', _crateResets);
_addSlider(_crateResets, {
  label: 'level', min: 0, max: 150, step: 1, value: 100,
  format: (v) => `${v | 0}%`,
  onChange: (v) => { _crateCrush.level = v / 100; },
});
_addSlider(_crateResets, {
  label: 'bits', min: 1, max: 8, step: 1, value: 4,
  format: (v) => `${v | 0}-bit`,
  onChange: (v) => { _crateCrush.bits = v | 0; },
});
_addSlider(_crateResets, {
  label: 'drive', min: 0, max: 100, step: 1, value: 81,  // 81 → ~3.33×
  format: (v) => `${(0.5 + v / 100 * 3.5).toFixed(2)}x`,
  onChange: (v) => { _crateCrush.drive = 0.5 + v / 100 * 3.5; },
});
_addSlider(_crateResets, {
  label: 'mix', min: 0, max: 100, step: 1, value: 50,
  format: (v) => `${v | 0}%`,
  onChange: (v) => { _crateCrush.mix = v / 100; },
});
_addSlider(_crateResets, {
  label: 'tone', min: 0, max: 100, step: 1, value: 72,  // 72 → ~4929 Hz
  format: (v) => `${Math.round(500 * Math.pow(24, v / 100))} Hz`,
  onChange: (v) => { _crateCrush.tone = 500 * Math.pow(24, v / 100); },
});
_addSlider(_crateResets, {
  label: 'hpf', min: 0, max: 100, step: 1, value: 32,  // 32 → ~80 Hz
  format: (v) => `${Math.round(20 * Math.pow(75, v / 100))} Hz`,
  onChange: (v) => { _crateCrush.hpf = 20 * Math.pow(75, v / 100); },
});

_addDivider();
const _metalResets = [];
_addSectionHeader('metal shape', _metalResets);

// Metal-shape sliders — edit the ACTIVE mode's settings. When the mode selector flips,
// each slider re-reads the new mode's saved value and updates its visible position. So
// per-mode tunings persist independently across mode switches.
//
// Each entry's `field` is the key on the active shape; `toSlider` converts the param value
// to a slider position (0-max); `fromSlider` does the inverse; `format` renders the label.
const METAL_SLIDER_SPECS = [
  { field: 'level',      label: 'level',   min: 0, max: 150, step: 1, fromSlider: (v) => v / 100,                                          toSlider: (p) => Math.round(p * 100),              format: (v) => `${v | 0}%` },
  { field: 'sustain',    label: 'sustain', min: 0, max: 100, step: 1, fromSlider: (v) => 0.3 + v / 100 * 2.7,                              toSlider: (p) => Math.round((p - 0.3) / 2.7 * 100), format: (v) => `${(0.3 + v / 100 * 2.7).toFixed(2)}x` },
  { field: 'strike',     label: 'strike',  min: 0, max: 100, step: 1, fromSlider: (v) => v / 50,                                            toSlider: (p) => Math.round(p * 50),                format: (v) => `${(v / 50).toFixed(2)}x` },
  { field: 'clang',      label: 'clang',   min: 0, max: 100, step: 1, fromSlider: (v) => v / 50,                                            toSlider: (p) => Math.round(p * 50),                format: (v) => `${(v / 50).toFixed(2)}x` },
  { field: 'detune',     label: 'detune',  min: 0, max: 100, step: 1, fromSlider: (v) => v * 0.3,                                           toSlider: (p) => Math.round(p / 0.3),               format: (v) => `${(v * 0.3).toFixed(1)}¢` },
  { field: 'brightness', label: 'bright',  min: 0, max: 100, step: 1,
    fromSlider: (v) => v >= 100 ? 20000 : 500 * Math.pow(40, v / 100),
    toSlider:   (p) => p >= 20000 ? 100 : Math.round(Math.log(p / 500) / Math.log(40) * 100),
    format: (v) => v >= 100 ? 'off' : `${Math.round(500 * Math.pow(40, v / 100))} Hz`,
  },
];
const _metalSliderRefs = {};  // field -> { slider, valTag, format }
for (const spec of METAL_SLIDER_SPECS) {
  const init = spec.toSlider(_activeMetalShape()[spec.field]);
  const s = _makeSlider({
    label: spec.label, min: spec.min, max: spec.max, step: spec.step, value: init,
    format: spec.format,
    onChange: (v) => { _activeMetalShape()[spec.field] = spec.fromSlider(v); },
  });
  _mixPanel.appendChild(s.row);
  _metalSliderRefs[spec.field] = { slider: s.row.querySelector('input'), valTag: s.row.children[2], spec };
}

// Save factory defaults for each per-mode shape so the section reset returns each mode
// to its original tuning, not just the active one.
const _metalShapesDefaults = JSON.parse(JSON.stringify(_metalShapes));
_metalResets.push(() => {
  for (const k of Object.keys(_metalShapesDefaults)) Object.assign(_metalShapes[k], _metalShapesDefaults[k]);
  _resyncMetalSliders();
});

function _resyncMetalSliders() {
  const shape = _activeMetalShape();
  for (const spec of METAL_SLIDER_SPECS) {
    const ref = _metalSliderRefs[spec.field];
    if (!ref) continue;
    const sliderVal = spec.toSlider(shape[spec.field]);
    ref.slider.value = String(sliderVal);
    ref.valTag.textContent = spec.format(sliderVal);
  }
}

// Metal partial-mode selector — three buttons in a row. Click switches the active mode
// AND re-syncs the metal-shape sliders to the new mode's saved values. Each mode keeps
// its own independent tuning (level / sustain / strike / clang / detune / brightness).
{
  const stop = (e) => e.stopPropagation();
  const initial = _metalPartialMode;
  const row = document.createElement('div');
  row.style.cssText = 'display:flex;align-items:center;gap:6px;flex-wrap:wrap;';
  const tag = document.createElement('span');
  tag.style.cssText = 'width:42px;';
  tag.textContent = 'mode';
  row.appendChild(tag);
  const btns = {};
  const refresh = () => {
    for (const k of Object.keys(btns)) {
      const active = _metalPartialMode === k;
      btns[k].style.borderColor = active ? '#ffd66b' : '#666';
      btns[k].style.color       = active ? '#ffd66b' : '#bbb';
    }
  };
  for (const mode of ['inharmonic', 'glockenspiel', 'harmonic']) {
    const b = document.createElement('button');
    b.textContent = mode;
    b.style.cssText = 'padding:3px 8px;font:10px ui-monospace,monospace;background:transparent;color:#bbb;border:1px solid #666;border-radius:3px;cursor:pointer;';
    b.addEventListener('mousedown', stop);
    b.addEventListener('click', (e) => {
      e.stopPropagation();
      _metalPartialMode = mode;
      refresh();
      _resyncMetalSliders();   // pull in new mode's saved param values
    });
    btns[mode] = b;
    row.appendChild(b);
  }
  refresh();
  _adminBody.appendChild(row);
  _metalResets.push(() => { _metalPartialMode = initial; refresh(); _resyncMetalSliders(); });
}

// Smash-pitch experiments. The single-octave bright pool is the default; chord-rich is an
// experimental harmony mode (filters the pool to chord tones + scale 2nd/6th, updating
// every 2 bars with the chord progression). Kept here for future A/B testing — felt duller
// than the bright default in initial play, but worth revisiting under different songs.
// Declared up here (rather than next to the audio code far below) so the admin checkbox
// setup that references it doesn't crash on module load with a TDZ ReferenceError.
let _chordRichSmashes = false;
_addDivider();
const _smashPitchResets = [];
_addSectionHeader('smash pitch', _smashPitchResets);
{
  const stop = (e) => e.stopPropagation();
  const initial = _chordRichSmashes;
  const row = document.createElement('label');
  row.style.cssText = 'display:flex;align-items:center;gap:8px;cursor:pointer;';
  const cb = document.createElement('input');
  cb.type = 'checkbox';
  cb.style.cursor = 'pointer';
  cb.checked = initial;
  cb.addEventListener('mousedown', stop);
  cb.addEventListener('click', stop);
  cb.addEventListener('change', (e) => {
    e.stopPropagation();
    _chordRichSmashes = cb.checked;
  });
  const lbl = document.createElement('span');
  lbl.textContent = 'chord-rich (follow chord changes)';
  row.appendChild(cb); row.appendChild(lbl);
  _adminBody.appendChild(row);
  _smashPitchResets.push(() => { cb.checked = initial; _chordRichSmashes = initial; });
}

// Per-material break-topple knobs — radius (m) and strength (multiplier on NEARBY_MULT).
// Crate defaults to 0/0 so a level-1 crate-only field has clean, non-cascading breaks.
_addDivider();
const _toppleResets = [];
_addSectionHeader('break topple', _toppleResets);
_addSlider(_toppleResets, {
  label: 'crate r', min: 0, max: 15, step: 0.1, value: 0,
  format: (v) => `${v.toFixed(1)}m`,
  onChange: (v) => { BREAK_TOPPLE.crate.radius = v; },
});
_addSlider(_toppleResets, {
  label: 'crate s', min: 0, max: 200, step: 1, value: 0,
  format: (v) => `${(v / 100).toFixed(2)}x`,
  onChange: (v) => { BREAK_TOPPLE.crate.strength = v / 100; },
});
_addSlider(_toppleResets, {
  label: 'brick r', min: 0, max: 15, step: 0.1, value: 4,
  format: (v) => `${v.toFixed(1)}m`,
  onChange: (v) => { BREAK_TOPPLE.brick.radius = v; },
});
_addSlider(_toppleResets, {
  label: 'brick s', min: 0, max: 200, step: 1, value: 60,
  format: (v) => `${(v / 100).toFixed(2)}x`,
  onChange: (v) => { BREAK_TOPPLE.brick.strength = v / 100; },
});
_addSlider(_toppleResets, {
  label: 'metal r', min: 0, max: 15, step: 0.1, value: 9,
  format: (v) => `${v.toFixed(1)}m`,
  onChange: (v) => { BREAK_TOPPLE.metal.radius = v; },
});
_addSlider(_toppleResets, {
  label: 'metal s', min: 0, max: 200, step: 1, value: 100,
  format: (v) => `${(v / 100).toFixed(2)}x`,
  onChange: (v) => { BREAK_TOPPLE.metal.strength = v / 100; },
});

// Apex jump — post-impact Y kick + hang-time on successful upward smashes. Tunes the platforming feel.
_addDivider();
const _apexResets = [];
_addSectionHeader('apex jump', _apexResets);
_addSlider(_apexResets, {
  label: 'boost', min: 0, max: 25, step: 0.5, value: 11,
  format: (v) => `${v.toFixed(1)} m/s`,
  onChange: (v) => { APEX_BOOST_VEL = v; },
});
_addSlider(_apexResets, {
  label: 'coast', min: 0, max: 400, step: 10, value: 100,
  format: (v) => `${v | 0}ms`,
  onChange: (v) => { APEX_COAST_MS = v; },
});
_addSlider(_apexResets, {
  label: 'float', min: 0, max: 600, step: 10, value: 130,
  format: (v) => `${v | 0}ms`,
  onChange: (v) => { APEX_FLOAT_MS = v; },
});
_addSlider(_apexResets, {
  label: 'f.grav', min: 0, max: 100, step: 1, value: 55,
  format: (v) => `${(v / 100).toFixed(2)}x`,
  onChange: (v) => { APEX_FLOAT_GRAVITY_MULT = v / 100; },
});

// Airborne hover — fires when the player smashes a block at horizontal aim while in the
// air. Reuses the apex coast/float pipeline (Math.max'd) but with a tunable upward kick
// instead of the apex's _upness-scaled boost. Toggle lives in lunge-feel section.
_addDivider();
const _hoverResets = [];
_addSectionHeader('air hover', _hoverResets);
_addSlider(_hoverResets, {
  label: 'lift', min: 0, max: 12, step: 0.5, value: 3,
  format: (v) => `${v.toFixed(1)} m/s`,
  onChange: (v) => { HOVER_LIFT_VEL = v; },
});
_addSlider(_hoverResets, {
  label: 'refire', min: 0, max: 2000, step: 50, value: 700,
  format: (v) => `${v | 0}ms`,
  onChange: (v) => { HOVER_LIFT_LOCKOUT_MS = v; },
});
_addSlider(_hoverResets, {
  label: 'coast', min: 0, max: 600, step: 10, value: 200,
  format: (v) => `${v | 0}ms`,
  onChange: (v) => { HOVER_COAST_MS = v; },
});
_addSlider(_hoverResets, {
  label: 'float', min: 0, max: 800, step: 10, value: 300,
  format: (v) => `${v | 0}ms`,
  onChange: (v) => { HOVER_FLOAT_MS = v; },
});

// Lunge feel — toggles for the two stand-off behaviors. Both default ON; flipping either off
// reverts to the pre-tweak behavior so we can A/B during play.
_addDivider();
const _lungeFeelResets = [];
_addSectionHeader('lunge feel', _lungeFeelResets);
{
  const stop = (e) => e.stopPropagation();
  const _addLungeToggle = (label, getOn, setOn) => {
    const initial = getOn();
    const row = document.createElement('label');
    row.style.cssText = 'display:flex;align-items:center;gap:8px;cursor:pointer;font:11px ui-monospace,monospace;color:#cfd6dc;padding:2px 0;';
    const cb = document.createElement('input');
    cb.type = 'checkbox';
    cb.style.cursor = 'pointer';
    cb.checked = initial;
    cb.addEventListener('mousedown', stop);
    cb.addEventListener('click', stop);
    cb.addEventListener('change', (e) => { e.stopPropagation(); setOn(cb.checked); });
    const lbl = document.createElement('span');
    lbl.textContent = label;
    row.appendChild(cb); row.appendChild(lbl);
    _adminBody.appendChild(row);
    _lungeFeelResets.push(() => { cb.checked = initial; setOn(initial); });
  };
  _addLungeToggle('context standoff (only look-down keeps tight reach)',
    () => _contextStandoffOn,
    (v) => { _contextStandoffOn = v; });
  _addLungeToggle('impact recoil (small pushback on every hit)',
    () => _impactRecoilOn,
    (v) => { _impactRecoilOn = v; RECOIL_SPEED = v ? 1.2 : 0; });
  _addLungeToggle('move substepping (no tunneling through walls)',
    () => _moveSubstepOn,
    (v) => { _moveSubstepOn = v; });
  _addLungeToggle('ray-aim targeting (lock onto block you point at, not behind)',
    () => _rayAimTargeting,
    (v) => { _rayAimTargeting = v; });
  _addLungeToggle('slot-aligned cooldown (cap at 8ths, any fist combination)',
    () => _sharedCooldownOn,
    (v) => { _sharedCooldownOn = v; });
  _addLungeToggle('airborne hover-on-hit (mid-air horizontal smash → brief hang to re-aim)',
    () => _hoverHitOn,
    (v) => { _hoverHitOn = v; });
}

// Per-level bounds — margins around the block AABB ∪ player spawn. Edits write to LEVEL_BOUNDS
// for the active level so tweaks persist across retries (until reload). The reset (↺) clears the
// override for the active level, falling back to BOUNDS_DEFAULTS.
_addDivider();
const _boundsResets = [];
_addSectionHeader('level bounds', _boundsResets);
const _boundsSliderRefs = {};
function _makeBoundsSlider(field, label, min, max, step, fmt) {
  const id0 = _currentLevel || 'sandbox';
  const initial = _resolveBounds(id0)[field];
  const row = document.createElement('label');
  row.style.cssText = 'display:flex;align-items:center;gap:8px;cursor:pointer;';
  const tag = document.createElement('span');
  tag.style.cssText = 'width:42px;';
  tag.textContent = label;
  const slider = document.createElement('input');
  slider.type = 'range';
  slider.min = String(min); slider.max = String(max); slider.step = String(step);
  slider.value = String(initial);
  slider.style.cssText = 'width:110px;';
  const valTag = document.createElement('span');
  valTag.style.cssText = 'width:46px;text-align:right;';
  valTag.textContent = fmt(initial);
  const stop = (e) => e.stopPropagation();
  slider.addEventListener('mousedown', stop);
  slider.addEventListener('click', stop);
  slider.addEventListener('input', (e) => {
    e.stopPropagation();
    const v = parseFloat(slider.value);
    valTag.textContent = fmt(v);
    const id = _currentLevel || 'sandbox';
    LEVEL_BOUNDS[id] = { ...(LEVEL_BOUNDS[id] || {}), [field]: v };
    _clearBounds();
    _buildBounds(_resolveBounds(id));
  });
  row.appendChild(tag); row.appendChild(slider); row.appendChild(valTag);
  _adminBody.appendChild(row);
  _boundsSliderRefs[field] = { slider, valTag, fmt };
  _boundsResets.push(() => {
    const id = _currentLevel || 'sandbox';
    if (LEVEL_BOUNDS[id]) delete LEVEL_BOUNDS[id][field];
    const v = _resolveBounds(id)[field];
    slider.value = String(v);
    valTag.textContent = fmt(v);
    _clearBounds();
    _buildBounds(_resolveBounds(id));
  });
}
_makeBoundsSlider('marginForward', 'fwd',    0, 60, 0.5, (v) => `${v.toFixed(1)}m`);
_makeBoundsSlider('marginBehind',  'behind', 0, 30, 0.5, (v) => `${v.toFixed(1)}m`);
_makeBoundsSlider('marginSide',    'side',   0, 30, 0.5, (v) => `${v.toFixed(1)}m`);
_makeBoundsSlider('height',        'height', 4, 20, 0.5, (v) => `${v.toFixed(1)}m`);
// Hook called by _rebuildBoundsForLevel — refreshes slider positions to the new level's effective margins.
_refreshBoundsSliders = (levelId) => {
  const m = _resolveBounds(levelId);
  for (const field of Object.keys(_boundsSliderRefs)) {
    const ref = _boundsSliderRefs[field];
    ref.slider.value = String(m[field]);
    ref.valTag.textContent = ref.fmt(m[field]);
  }
};

// Visual FX — checkbox toggles for sky-decoration experiments. All off by default
// so the shipped look is unchanged unless you explicitly opt in. Each toggle also
// has a reset hook so the section's ↺ (and the global "reset all") clear them.
_addDivider();
const _visualResets = [];
_addSectionHeader('visual fx', _visualResets);
{
  const stop = (e) => e.stopPropagation();
  const _addToggle = (label, getOn, setOn) => {
    // Snapshot the shipped default so the section ↺ and global ↺ all return to
    // whatever this toggle was at module load — not always false.
    const initial = getOn();
    const row = document.createElement('label');
    row.style.cssText = 'display:flex;align-items:center;gap:8px;cursor:pointer;';
    const cb = document.createElement('input');
    cb.type = 'checkbox';
    cb.style.cursor = 'pointer';
    cb.checked = initial;
    cb.addEventListener('mousedown', stop);
    cb.addEventListener('click', stop);
    cb.addEventListener('change', (e) => {
      e.stopPropagation();
      setOn(cb.checked);
    });
    const lbl = document.createElement('span');
    lbl.textContent = label;
    row.appendChild(cb); row.appendChild(lbl);
    _mixPanel.appendChild(row);
    _visualResets.push(() => {
      cb.checked = initial;
      setOn(initial);
    });
  };
  _addToggle('clouds', () => _skyFx.cloudsOn, (v) => {
    _skyFx.cloudsOn = v;
    _cloudGroup.visible = v;
  });
  _addToggle('birds', () => _skyFx.birdsOn, (v) => {
    _skyFx.birdsOn = v;
    _birdGroup.visible = v;
    if (!v) for (const b of _birds) { b.userData.alive = false; b.visible = false; }
  });
  // Bird size — multiplier on each bird's randomised base scale. Live-applies to
  // currently-flying birds so the slider feels responsive while testing.
  _addSlider(_visualResets, {
    label: 'bird sz', min: 30, max: 300, step: 1, value: 300,
    format: (v) => `${(v / 100).toFixed(2)}x`,
    onChange: (v) => {
      _birdSizeMult = v / 100;
      for (const b of _birds) _applyBirdScale(b);
    },
  });
  _addToggle('sky beat pulse', () => _skyFx.beatPulseOn, (v) => {
    _skyFx.beatPulseOn = v;
    if (!v) sky.material.uniforms['rayleigh'].value = _SKY_BASELINE_RAYLEIGH;
  });
  _addToggle('boombox', () => _skyFx.boomboxOn, (v) => {
    _skyFx.boomboxOn = v;
    _boomboxGroup.visible = v;
  });
  // Boombox size — same shape as the bird slider; live-applies to the assembly.
  _addSlider(_visualResets, {
    label: 'bb sz', min: 30, max: 300, step: 1, value: 100,
    format: (v) => `${(v / 100).toFixed(2)}x`,
    onChange: (v) => {
      _boomboxSizeMult = v / 100;
      _applyBoomboxScale();
    },
  });
  _addToggle('wall speakers', () => _skyFx.wallSpeakersOn, (v) => {
    _skyFx.wallSpeakersOn = v;
    _speakerGroup.visible = v;
  });
  _addToggle('wall trim', () => _skyFx.wallTrimOn, (v) => {
    _skyFx.wallTrimOn = v;
    _wallTrimGroup.visible = v;
  });
  // Wall speaker size — multiplier on cone size. Rebuilds the rig because stack
  // positions depend on size; cheap (no physics, shared geo + texture).
  _addSlider(_visualResets, {
    label: 'spk sz', min: 30, max: 300, step: 1, value: 100,
    format: (v) => `${(v / 100).toFixed(2)}x`,
    onChange: (v) => {
      _speakerSizeMult = v / 100;
      _buildSpeakers();
    },
  });
  _addToggle('beam tip at surface', () => _beamTipAtSurface, (v) => {
    _beamTipAtSurface = v;
  });
  _addToggle('contact ring', () => _contactRingOn, (v) => {
    _contactRingOn = v;
    if (!v) _contactRingMat.opacity = 0;
  });
  _addToggle('beat ring (centre)', () => _beatRingOn, (v) => {
    _beatRingOn = v;
    if (!v) _beatRing.style.opacity = '0';
  });
  _addToggle('approach squares (target faces)', () => _approachHaloOn, (v) => {
    _approachHaloOn = v;
    if (nearHighlight && nearHighlight.userData && nearHighlight.userData.faceSquares) {
      for (const sq of nearHighlight.userData.faceSquares) sq.visible = v;
    }
    if (nearHighlight && nearHighlight.userData && nearHighlight.userData.faceTextsWhite) {
      for (const txt of nearHighlight.userData.faceTextsWhite) txt.visible = v;
    }
    if (nearHighlight && nearHighlight.userData && nearHighlight.userData.faceTextsColor) {
      for (const txt of nearHighlight.userData.faceTextsColor) txt.visible = v;
    }
  });
  _addToggle('target tick (audio cue on beat when target highlighted)', () => _targetTickOn, (v) => {
    _targetTickOn = v;
  });
  _addToggle('impact flash v2', () => _impactFlashV2, (v) => {
    _impactFlashV2 = v;
  });
  // Target-block beat pulse intensity. 0 = off, 100 = strong cyan-bright snap on every beat.
  // Default 45 puts a clearly visible "hit on this beat" cue on the current target without
  // washing out the underlying material; raise if players still can't read the timing.
  _addSlider(_visualResets, {
    label: 'tgt pulse', min: 0, max: 100, step: 1, value: 4,
    format: (v) => `${v | 0}%`,
    onChange: (v) => { BLOCK_BEAT_PULSE_INTENSITY = v / 100; },
  });
}

// UGC pill visibility — checkboxes that hide/show each Create-page pill. Toggling
// re-renders the home view if the player happens to be on Create so the change is
// instant. Initial values come from `_ugcPillVisible` declared up by the create-page
// helpers; the section ↺ / global ↺ snap each toggle back to that shipped default.
_addDivider();
const _ugcPillResets = [];
_addSectionHeader('ugc pills', _ugcPillResets);
{
  const stop = (e) => e.stopPropagation();
  const refreshIfOnCreate = () => { if (_homeView === 'create') _renderHome(); };
  const _addPillToggle = (label, key) => {
    const initial = _ugcPillVisible[key];
    const row = document.createElement('label');
    row.style.cssText = 'display:flex;align-items:center;gap:8px;cursor:pointer;';
    const cb = document.createElement('input');
    cb.type = 'checkbox';
    cb.style.cursor = 'pointer';
    cb.checked = initial;
    cb.addEventListener('mousedown', stop);
    cb.addEventListener('click', stop);
    cb.addEventListener('change', (e) => {
      e.stopPropagation();
      _ugcPillVisible[key] = cb.checked;
      refreshIfOnCreate();
    });
    const lbl = document.createElement('span');
    lbl.textContent = label;
    row.appendChild(cb); row.appendChild(lbl);
    _mixPanel.appendChild(row);
    _ugcPillResets.push(() => {
      cb.checked = initial;
      _ugcPillVisible[key] = initial;
      refreshIfOnCreate();
    });
  };
  _addPillToggle('design: standard',     'full');
  _addPillToggle('design: light',        'lean');
  _addPillToggle('design: architecture', 'freeform');
  _addPillToggle('design: creatures',    'creatures');
  _addPillToggle('music: classic 8 bit',             'defaultMusic');
  _addPillToggle('music: modern retro',              'freeformMusic');
  _addPillToggle('music: experimental modern retro', 'experimentalMusic');
  _addPillToggle('music: pruned modern retro',       'prunedMusic');
}

// Admin: local-data reset. Wipes nickname + per-level personal best + the "MINE" UGC
// ledger from this browser's localStorage so the next play feels like a fresh visitor
// (modal prompts for name, BEST badges revert to NEW). Doesn't touch server-side
// leaderboard scores, which are a separate concern.
_addDivider();
{
  const stop = (e) => e.stopPropagation();
  const header = document.createElement('div');
  header.style.cssText = 'color:#888;font-size:10px;margin-bottom:1px;';
  header.textContent = 'reset local data';
  _mixPanel.appendChild(header);

  const status = document.createElement('div');
  status.style.cssText = 'color:#cfd6dc;font-size:10px;margin:1px 0 4px;font-family:ui-monospace,monospace;';
  const refreshStatus = () => {
    const name = (() => { try { return localStorage.getItem('smash-player-name-v1') || ''; } catch { return ''; } })();
    const progressKeys = (() => { try { return Object.keys(JSON.parse(localStorage.getItem('smash-progress-v1') || '{}')).length; } catch { return 0; } })();
    const myUgc = (() => { try { return JSON.parse(localStorage.getItem('smash-my-ugc-v1') || '[]').length; } catch { return 0; } })();
    status.textContent = `name: ${name || '(none)'}  ·  ${progressKeys} levels played  ·  ${myUgc} mine`;
  };
  refreshStatus();
  _mixPanel.appendChild(status);

  const resetBtn = document.createElement('button');
  resetBtn.type = 'button';
  resetBtn.textContent = 'wipe nickname + progress';
  resetBtn.style.cssText = 'padding:6px 10px;font-size:10px;cursor:pointer;background:var(--col-pink, #ff3b6b);'
    + 'color:var(--col-ink, #1a1a2e);border:2px solid var(--col-ink, #1a1a2e);font-family:ui-monospace,monospace;';
  resetBtn.addEventListener('mousedown', stop);
  resetBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    try {
      localStorage.removeItem('smash-player-name-v1');
      localStorage.removeItem('smash-progress-v1');
      localStorage.removeItem('smash-my-ugc-v1');
    } catch {}
    refreshStatus();
    resetBtn.textContent = 'cleared! reload page';
    setTimeout(() => { resetBtn.textContent = 'wipe nickname + progress'; }, 1800);
  });
  _mixPanel.appendChild(resetBtn);
}

// Perf overlay section — toggle to show/hide top-left FPS+graph readout, and a button to copy
// Block death style toggle — flip between the new ghost-fly-and-fade and the original
// shrink-in-place. Lets us revert if the new behavior turns out wrong on something.
_addDivider();
{
  const _deathResets = [];
  _addSectionHeader('block death', _deathResets);
  const stop = (e) => e.stopPropagation();
  const initial = _ghostDeath.enabled;
  const row = document.createElement('label');
  row.style.cssText = 'display:flex;align-items:center;gap:8px;cursor:pointer;';
  const cb = document.createElement('input');
  cb.type = 'checkbox';
  cb.style.cursor = 'pointer';
  cb.checked = initial;
  cb.addEventListener('mousedown', stop);
  cb.addEventListener('click', stop);
  cb.addEventListener('change', (e) => {
    e.stopPropagation();
    _ghostDeath.enabled = cb.checked;
  });
  const lbl = document.createElement('span');
  lbl.textContent = 'ghost death (fly + fade)';
  row.appendChild(cb); row.appendChild(lbl);
  _mixPanel.appendChild(row);
  _deathResets.push(() => {
    cb.checked = initial;
    _ghostDeath.enabled = initial;
  });

  // Dislodged-block self-destruct (single-player feel test for the co-op "move = pop" rule).
  // Two stacked checkboxes give three states: both off = today's behaviour (rubble persists);
  // "dislodged pop on settle" on = move=pop (pops wherever it stops); add "...ground only" =
  // pops only blocks that came to rest on the floor (rubble landing on other blocks survives).
  const mkDeathToggle = (label, getOn, setOn) => {
    const init = getOn();
    const r = document.createElement('label');
    r.style.cssText = 'display:flex;align-items:center;gap:8px;cursor:pointer;';
    const c = document.createElement('input');
    c.type = 'checkbox';
    c.style.cursor = 'pointer';
    c.checked = init;
    c.addEventListener('mousedown', stop);
    c.addEventListener('click', stop);
    c.addEventListener('change', (e) => { e.stopPropagation(); setOn(c.checked); });
    const l = document.createElement('span');
    l.textContent = label;
    r.appendChild(c); r.appendChild(l);
    _mixPanel.appendChild(r);
    _deathResets.push(() => { c.checked = init; setOn(init); });
  };
  mkDeathToggle('dislodged pop on settle', () => _dislodgePop, (v) => { _dislodgePop = v; });
  mkDeathToggle('  ...ground only', () => _dislodgePopGroundOnly, (v) => { _dislodgePopGroundOnly = v; });
}

// Storm power tuning panel — sliders for AOE radius, AOE damage, shockwave radius,
// chunk impulse. Toggle "force on" applies them regardless of current streak so Rich can
// dial in values that feel right before mapping them to streak tiers.
_addDivider();
{
  const _stormResets = [];
  _addSectionHeader('storm power', _stormResets);
  const stop = (e) => e.stopPropagation();
  // Force-on toggle.
  {
    const initial = _stormPower.forceOn;
    const row = document.createElement('label');
    row.style.cssText = 'display:flex;align-items:center;gap:8px;cursor:pointer;';
    const cb = document.createElement('input');
    cb.type = 'checkbox';
    cb.style.cursor = 'pointer';
    cb.checked = initial;
    cb.addEventListener('mousedown', stop);
    cb.addEventListener('click', stop);
    cb.addEventListener('change', (e) => {
      e.stopPropagation();
      _stormPower.forceOn = cb.checked;
    });
    const lbl = document.createElement('span');
    lbl.textContent = 'force on (apply at any streak)';
    row.appendChild(cb); row.appendChild(lbl);
    _mixPanel.appendChild(row);
    _stormResets.push(() => {
      cb.checked = initial;
      _stormPower.forceOn = initial;
    });
  }
  _addSlider(_stormResets, {
    label: 'max AOE r',  min: 0, max: 6, step: 0.1, value: _stormPower.aoeRadiusMax,
    format: (v) => `${v.toFixed(1)}m`,
    onChange: (v) => { _stormPower.aoeRadiusMax = v; },
  });
  _addSlider(_stormResets, {
    label: 'max AOE dmg', min: 1, max: 3, step: 1, value: _stormPower.aoeDamageMax,
    format: (v) => String(v | 0),
    onChange: (v) => { _stormPower.aoeDamageMax = v; },
  });
  _addSlider(_stormResets, {
    label: 'max shock×', min: 1, max: 3, step: 0.1, value: _stormPower.shockwaveRadiusMultMax,
    format: (v) => `${v.toFixed(1)}x`,
    onChange: (v) => { _stormPower.shockwaveRadiusMultMax = v; },
  });
  _addSlider(_stormResets, {
    label: 'max chunk×', min: 1, max: 4, step: 0.1, value: _stormPower.chunkImpulseMultMax,
    format: (v) => `${v.toFixed(1)}x`,
    onChange: (v) => { _stormPower.chunkImpulseMultMax = v; },
  });
}

// a text summary of the rolling 30s perf buffer to the clipboard. Useful when capturing for
// video to confirm whether dips actually surface on the recording-quality run.
_addDivider();
{
  const header = document.createElement('div');
  header.style.cssText = 'color:#888;font-size:10px;margin-bottom:1px;';
  header.textContent = 'perf';
  _mixPanel.appendChild(header);

  const toggleRow = document.createElement('label');
  toggleRow.style.cssText = 'display:flex;align-items:center;gap:8px;cursor:pointer;';
  const toggle = document.createElement('input');
  toggle.type = 'checkbox';
  toggle.style.cssText = 'cursor:pointer;';
  const stopProp = (e) => e.stopPropagation();
  toggle.addEventListener('mousedown', stopProp);
  toggle.addEventListener('click', stopProp);
  toggle.addEventListener('change', (e) => {
    e.stopPropagation();
    _perfOverlayOn = toggle.checked;
    _perfContainerEl.style.display = _perfOverlayOn ? 'block' : 'none';
    if (_perfOverlayOn) {
      _perfStartMs = performance.now();
      _perfWorstMs = 0;
      _perfWorstAtMs = 0;
      _audioSmashCalls = _audioSmashDropped = 0;
      _audioClinkCalls = _audioClinkDropped = 0;
      _audioRubbleCalls = _audioRubbleDropped = 0;
      _audioCtxStateLog = '';
      _audioCtxLastState = '';
    }
  });
  const lbl = document.createElement('span');
  lbl.textContent = 'overlay (top-left fps + graph)';
  toggleRow.appendChild(toggle);
  toggleRow.appendChild(lbl);
  _mixPanel.appendChild(toggleRow);

  const copyBtn = document.createElement('button');
  copyBtn.textContent = 'copy perf log';
  copyBtn.style.cssText = 'padding:4px 8px;font:11px ui-monospace,monospace;background:rgba(60,60,70,0.85);color:#cfd6dc;border:1px solid #555;border-radius:2px;cursor:pointer;margin-top:3px;';
  copyBtn.addEventListener('mousedown', stopProp);
  copyBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    const log = _buildPerfLog();
    const restore = () => { copyBtn.textContent = 'copy perf log'; };
    navigator.clipboard.writeText(log).then(
      () => { copyBtn.textContent = 'copied ✓'; setTimeout(restore, 1500); },
      () => { copyBtn.textContent = 'copy failed'; setTimeout(restore, 1500); }
    );
  });
  _mixPanel.appendChild(copyBtn);
}

// =========================================================
// Trailer-mode recording — captures the renderer canvas (video) and the master
// audio bus (audio) into a single synced WebM via MediaRecorder. Bypasses OS-level
// capture entirely so OBS-style audio glitches can't occur; also gives streamers
// a no-setup "record this run" affordance via the same path. Quality presets
// trade fps + bitrate; resolution is whatever the canvas is currently rendering.
// =========================================================
_addDivider();
{
  const _recResets = [];
  _addSectionHeader('recording', _recResets);
  const stop = (e) => e.stopPropagation();

  let _mediaRecorder = null;
  let _recordedChunks = [];
  let _streamDest = null;
  let _recAudioSource = null;  // The node tapped for recording (_masterBus or _musicGain).
  let _recDisplayStream = null;  // getDisplayMedia stream for tab-capture mode; null otherwise.
  let _recVideoTracks = [];  // Canvas-captureStream video tracks. Stopped explicitly on teardown
                             // — MediaRecorder doesn't stop source tracks itself, so leaving them
                             // running between recordings keeps the canvas being sampled and
                             // causes the audio graph to glitch on the next recording's setup.
  let _recForceCleanupTimer = null;  // Safety net: if onstop never fires after .stop(), force cleanup.
  let _startingRec = false;  // Guards against double-start during async tab-capture permission prompt.
  let _recStartMs = 0;
  let _recTimer = null;
  let _recActiveMode = 'video+audio';  // Snapshot of the mode at recording start, for filename.
  // Locked-resolution recording path: offscreen 2D canvas at preset.w × preset.h.
  // Each frame we drawImage the WebGL canvas into it with aspect-fit letterboxing.
  // The MediaRecorder captures the offscreen canvas, not the game canvas — so the
  // encoder always sees a fixed pixel count regardless of the player's screen.
  let _recOffscreen = null;
  let _recOffCtx = null;
  let _recRafId = 0;

  // Presets. w/h = null means "use canvas native size" (direct WebGL captureStream,
  // skips the per-frame drawImage downscale). Native is the default — best on machines
  // where the player's screen already matches their target output resolution.
  // The locked-resolution paths below are the escape hatch for recording from a screen
  // larger than the target output (e.g. recording a 1080p trailer on a 4K monitor).
  const _qualityPresets = [
    { label: 'native · 60fps · 24 Mbps', fps: 60, bps: 24_000_000, w: null, h: null },
    { label: '720p · 30fps · 6 Mbps',    fps: 30, bps:  6_000_000, w: 1280, h:  720 },
    { label: '1080p · 30fps · 10 Mbps',  fps: 30, bps: 10_000_000, w: 1920, h: 1080 },
    { label: '1080p · 60fps · 16 Mbps',  fps: 60, bps: 16_000_000, w: 1920, h: 1080 },
    { label: '1440p · 60fps · 24 Mbps',  fps: 60, bps: 24_000_000, w: 2560, h: 1440 },
  ];
  let _qualityIdx = 0;

  // Recording modes:
  // - video+audio: canvas + master bus (music + SFX). Default trailer/streamer use.
  // - audio-only:  master bus only (music + SFX), no video track. For audio stems.
  // - song-only:   _musicGain tap only (procedural music, no SFX or impacts).
  //                Useful for grabbing clean music stems to layer over different footage.
  // For audio-only / song-only the quality preset is ignored.
  const _recModes = [
    { value: 'video+audio',    label: 'video + audio' },
    { value: 'video+ui+audio', label: 'video + UI + audio (tab capture)' },
    { value: 'audio-only',     label: 'audio only (music + SFX)' },
    { value: 'song-only',      label: 'song only (music stem)' },
  ];
  let _recMode = 'video+audio';

  const mRow = document.createElement('div');
  mRow.style.cssText = 'display:flex;align-items:center;gap:6px;font:11px ui-monospace,monospace;padding:2px 0;';
  const mLbl = document.createElement('span');
  mLbl.style.cssText = 'color:#888;min-width:46px;';
  mLbl.textContent = 'mode';
  const mSel = document.createElement('select');
  mSel.style.cssText = 'background:rgba(30,30,40,0.9);color:#cfd6dc;border:1px solid #555;font:11px ui-monospace,monospace;flex:1;cursor:pointer;';
  for (const m of _recModes) {
    const opt = document.createElement('option');
    opt.value = m.value;
    opt.textContent = m.label;
    mSel.appendChild(opt);
  }
  mSel.value = _recMode;
  mSel.addEventListener('mousedown', stop);
  mSel.addEventListener('click', stop);
  mSel.addEventListener('change', (e) => {
    e.stopPropagation();
    _recMode = mSel.value;
    // Disable the quality picker when video isn't being captured — visually signal
    // that the choice is inactive without hiding it (lets the user see what's there).
    const audioOnly = (_recMode !== 'video+audio');
    qSel.disabled = audioOnly;
    qSel.style.opacity = audioOnly ? '0.4' : '1';
  });
  mRow.appendChild(mLbl); mRow.appendChild(mSel);
  _mixPanel.appendChild(mRow);
  _recResets.push(() => {
    _recMode = 'video+audio';
    mSel.value = 'video+audio';
    qSel.disabled = false;
    qSel.style.opacity = '1';
  });

  const qRow = document.createElement('div');
  qRow.style.cssText = 'display:flex;align-items:center;gap:6px;font:11px ui-monospace,monospace;padding:2px 0;';
  const qLbl = document.createElement('span');
  qLbl.style.cssText = 'color:#888;min-width:46px;';
  qLbl.textContent = 'quality';
  const qSel = document.createElement('select');
  qSel.style.cssText = 'background:rgba(30,30,40,0.9);color:#cfd6dc;border:1px solid #555;font:11px ui-monospace,monospace;flex:1;cursor:pointer;';
  for (let i = 0; i < _qualityPresets.length; i++) {
    const opt = document.createElement('option');
    opt.value = String(i);
    opt.textContent = _qualityPresets[i].label;
    qSel.appendChild(opt);
  }
  qSel.value = String(_qualityIdx);
  qSel.addEventListener('mousedown', stop);
  qSel.addEventListener('click', stop);
  qSel.addEventListener('change', (e) => { e.stopPropagation(); _qualityIdx = Number(qSel.value); });
  qRow.appendChild(qLbl); qRow.appendChild(qSel);
  _mixPanel.appendChild(qRow);
  _recResets.push(() => { _qualityIdx = 0; qSel.value = '0'; });

  // HUD-hide checkbox. Independent of recording state — useful for previewing the
  // clean shot before hitting record, and for streamers who want to record over the
  // HUD via their own software.
  const _applyHudHidden = (v) => {
    const els = document.querySelectorAll('.ss-hud, .ss-streak-badge');
    for (const el of els) el.style.visibility = v ? 'hidden' : '';
  };
  const hudRow = document.createElement('label');
  hudRow.style.cssText = 'display:flex;align-items:center;gap:8px;cursor:pointer;font:11px ui-monospace,monospace;padding:2px 0;';
  const hudCb = document.createElement('input');
  hudCb.type = 'checkbox';
  hudCb.style.cursor = 'pointer';
  hudCb.addEventListener('mousedown', stop);
  hudCb.addEventListener('click', stop);
  hudCb.addEventListener('change', (e) => { e.stopPropagation(); _applyHudHidden(hudCb.checked); });
  const hudLbl = document.createElement('span');
  hudLbl.textContent = 'hide HUD (also hides it in tab-capture recording)';
  hudRow.appendChild(hudCb); hudRow.appendChild(hudLbl);
  _mixPanel.appendChild(hudRow);
  _recResets.push(() => { hudCb.checked = false; _applyHudHidden(false); });

  const statusRow = document.createElement('div');
  statusRow.style.cssText = 'font:11px ui-monospace,monospace;color:#888;padding:3px 0 1px;min-height:14px;';
  statusRow.textContent = 'idle';
  _mixPanel.appendChild(statusRow);

  const btn = document.createElement('button');
  btn.style.cssText = 'padding:6px 10px;font:11px ui-monospace,monospace;background:rgba(60,60,70,0.85);color:#cfd6dc;border:1px solid #555;border-radius:2px;cursor:pointer;margin-top:3px;width:100%;text-align:center;';
  btn.textContent = '● start recording';
  btn.addEventListener('mousedown', stop);

  const _fmtBytes = (b) => (b < 1024 * 1024) ? `${(b / 1024).toFixed(0)}KB` : `${(b / 1024 / 1024).toFixed(1)}MB`;
  const _fmtTime = (ms) => {
    const s = Math.floor(ms / 1000);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  };

  const _startRec = () => {
    if (_mediaRecorder || _startingRec) return;
    if (!audioCtx) {
      statusRow.textContent = 'audio not initialised — click into the game first';
      return;
    }
    _ensureAudioGraph();
    if (!_masterBus) {
      statusRow.textContent = 'audio graph unavailable';
      return;
    }

    const preset = _qualityPresets[_qualityIdx];
    const wantsTabCapture = (_recMode === 'video+ui+audio');
    const wantsVideo = wantsTabCapture || (_recMode === 'video+audio');

    // Pick the audio source node to tap:
    // - song-only mode taps _musicGain (procedural music only). Note that _musicGain
    //   is recreated when songs change (level transitions), so the tap goes silent if
    //   the song changes mid-record — restart recording on the new song to re-tap.
    // - all other modes tap _masterBus (everything routed through the compressor).
    _recAudioSource = _masterBus;
    if (_recMode === 'song-only') {
      if (!_musicGain) {
        statusRow.textContent = 'no music playing — start a level first';
        return;
      }
      _recAudioSource = _musicGain;
    }
    _recActiveMode = _recMode;

    // Produces a Promise<MediaStream | null>. Two paths:
    // - tab-capture mode: getDisplayMedia (browser prompts for which tab to share).
    //   Audio is OFF in the display request — we always use our internal master/music
    //   bus tap so the OS-audio glitch can't return.
    // - canvas modes: synchronous renderer-domElement.captureStream, optionally via
    //   an offscreen downscale canvas for locked-resolution presets.
    const buildVideoStream = () => {
      if (!wantsVideo) return Promise.resolve(null);
      if (wantsTabCapture) {
        statusRow.textContent = 'waiting for tab-share permission...';
        const videoConstraints = { frameRate: preset.fps };
        if (preset.w && preset.h) {
          videoConstraints.width = preset.w;
          videoConstraints.height = preset.h;
        }
        // Request audio from getDisplayMedia too. Tab audio is captured internally
        // by the browser at the same point as the video — guaranteed sync. If the user
        // declines audio in the picker (rare), we fall back to the master-bus tap.
        const opts = { video: videoConstraints, audio: true, preferCurrentTab: true };
        return navigator.mediaDevices.getDisplayMedia(opts).then((stream) => {
          _recDisplayStream = stream;
          // If the user clicks the browser's "Stop sharing" button instead of our
          // stop button / F9, gracefully tear down the recorder.
          const vTrack = stream.getVideoTracks()[0];
          if (vTrack) vTrack.addEventListener('ended', () => { if (_mediaRecorder) _stopRec(); });
          return stream;
        });
      }
      // Canvas capture path.
      let captureCanvas;
      if (preset.w && preset.h) {
        _recOffscreen = document.createElement('canvas');
        _recOffscreen.width = preset.w;
        _recOffscreen.height = preset.h;
        _recOffCtx = _recOffscreen.getContext('2d', { alpha: false });
        _recOffCtx.fillStyle = '#000';
        _recOffCtx.fillRect(0, 0, preset.w, preset.h);
        const _drawFrame = () => {
          if (!_mediaRecorder && !_startingRec) return;
          const src = renderer.domElement;
          const sw = src.width, sh = src.height;
          if (sw > 0 && sh > 0) {
            const tw = preset.w, th = preset.h;
            const sAspect = sw / sh;
            const tAspect = tw / th;
            let dx, dy, dw, dh;
            if (sAspect > tAspect) {
              dw = tw;
              dh = tw / sAspect;
              dx = 0;
              dy = (th - dh) / 2;
            } else {
              dh = th;
              dw = th * sAspect;
              dx = (tw - dw) / 2;
              dy = 0;
            }
            if (dx > 0 || dy > 0) {
              _recOffCtx.fillStyle = '#000';
              _recOffCtx.fillRect(0, 0, tw, th);
            }
            _recOffCtx.drawImage(src, dx, dy, dw, dh);
          }
          _recRafId = requestAnimationFrame(_drawFrame);
        };
        _recRafId = requestAnimationFrame(_drawFrame);
        captureCanvas = _recOffscreen;
      } else {
        captureCanvas = renderer.domElement;
      }
      return Promise.resolve(captureCanvas.captureStream(preset.fps));
    };

    _startingRec = true;
    buildVideoStream().then((videoStream) => {
      _startingRec = false;
      if (_mediaRecorder) return; // double-guard against race

      // Audio routing:
      // - Tab-capture mode: if getDisplayMedia returned an audio track (user
      //   accepted "share tab audio"), prefer it. Audio and video share the
      //   browser's tab-capture pipeline, which guarantees sync — combining
      //   our internal master-bus tap with the display video causes drift
      //   because the two streams have independent timestamps.
      // - Anything else (or tab-capture without granted audio): tap our
      //   internal bus, which is the only path that produces sync-clean audio
      //   for canvas-mode video and audio-only modes.
      const tabAudioTrack = (wantsTabCapture && _recDisplayStream)
        ? _recDisplayStream.getAudioTracks()[0]
        : null;
      let audioTrack;
      if (tabAudioTrack) {
        audioTrack = tabAudioTrack;
        // Skip the masterBus connection — we don't need our internal tap.
        _recAudioSource = null;
      } else {
        _streamDest = audioCtx.createMediaStreamDestination();
        _recAudioSource.connect(_streamDest);
        audioTrack = _streamDest.stream.getAudioTracks()[0];
      }
      // Capture video-track refs so teardown can stop them. canvas.captureStream tracks
      // keep the canvas sampling active until explicitly stopped or GC'd; leaving them
      // running between recordings causes audio-graph glitches on the next session.
      _recVideoTracks = videoStream ? videoStream.getVideoTracks() : [];
      const combined = videoStream
        ? new MediaStream([..._recVideoTracks, audioTrack])
        : new MediaStream([audioTrack]);

      const mimeCandidates = wantsVideo
        ? [
            'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
            'video/mp4;codecs=h264,aac',
            'video/mp4',
            'video/webm;codecs=vp9,opus',
            'video/webm;codecs=vp8,opus',
            'video/webm',
          ]
        : [
            'audio/mp4;codecs=mp4a.40.2',
            'audio/mp4',
            'audio/webm;codecs=opus',
            'audio/webm',
          ];
      let mime = '';
      for (const m of mimeCandidates) {
        if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m)) { mime = m; break; }
      }
      const _fileExt = (mime.startsWith('video/mp4') || mime.startsWith('audio/mp4')) ? 'mp4' : 'webm';

      _recordedChunks = [];
      try {
        _mediaRecorder = new MediaRecorder(combined, {
          mimeType: mime || undefined,
          videoBitsPerSecond: preset.bps,
          audioBitsPerSecond: 192_000,
        });
      } catch (err) {
        statusRow.textContent = `recorder failed: ${err.message}`;
        _teardownRecState();
        return;
      }
      _mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) _recordedChunks.push(e.data);
      };
      _mediaRecorder.onstop = () => {
        const isAudio = (_recActiveMode !== 'video+audio' && _recActiveMode !== 'video+ui+audio');
        const blob = new Blob(_recordedChunks, { type: mime || (isAudio ? 'audio/webm' : 'video/webm') });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        const suffix = _recActiveMode === 'song-only' ? '-song'
                     : _recActiveMode === 'audio-only' ? '-audio'
                     : _recActiveMode === 'video+ui+audio' ? '-tab' : '';
        a.href = url;
        a.download = `sounds-smashing-${stamp}${suffix}.${_fileExt}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 5000);
        const total = _recordedChunks.reduce((s, c) => s + c.size, 0);
        statusRow.textContent = `saved ${_fmtBytes(total)} · ${_fmtTime(performance.now() - _recStartMs)}`;
        _mediaRecorder = null;
        _teardownRecState();
        _recIndicator.style.display = 'none';
      };
      _mediaRecorder.onerror = (e) => {
        statusRow.textContent = `recorder error: ${e.error && e.error.name || 'unknown'}`;
        // Recorder errors can leave the recorder in a state where .stop() no-ops
        // and onstop never fires. Trigger our force-cleanup path so F9 can recover
        // without a page refresh.
        _stopRec();
      };
      _mediaRecorder.start(1000);
      _recStartMs = performance.now();
      btn.textContent = '■ stop recording';
      btn.style.background = 'rgba(140,40,40,0.9)';

      // REC indicator shows for canvas modes only — in tab-capture mode the indicator
      // would itself appear IN the recording (it's a DOM element on the captured tab).
      // The browser's own "this tab is being shared" pill in the tab bar provides
      // adequate feedback for tab-capture sessions.
      if (!wantsTabCapture) _recIndicator.style.display = 'flex';

      const _tick = () => {
        if (!_mediaRecorder) return;
        const bytes = _recordedChunks.reduce((s, c) => s + c.size, 0);
        const modeTag = _recActiveMode === 'song-only' ? 'song-only'
                      : _recActiveMode === 'audio-only' ? 'audio-only'
                      : _recActiveMode === 'video+ui+audio' ? `tab · ${preset.label}`
                      : preset.label;
        statusRow.textContent = `● rec ${_fmtTime(performance.now() - _recStartMs)} · ${_fmtBytes(bytes)} · ${modeTag}`;
        _recTimer = setTimeout(_tick, 500);
      };
      _tick();
    }).catch((err) => {
      _startingRec = false;
      const msg = err && (err.message || err.name) || 'unknown';
      statusRow.textContent = (err && err.name === 'NotAllowedError')
        ? 'share cancelled'
        : `setup failed: ${msg}`;
      _teardownRecState();
    });
  };

  // Tears down every transient bit set up during _startRec. Safe to call multiple
  // times. Doesn't clear _mediaRecorder (caller handles that — onstop nulls it after
  // the blob is flushed; teardown paths null it before calling this helper).
  const _teardownRecState = () => {
    try { if (_recAudioSource && _streamDest) _recAudioSource.disconnect(_streamDest); } catch (e) {}
    _recAudioSource = null;
    _streamDest = null;
    for (const t of _recVideoTracks) { try { t.stop(); } catch (e) {} }
    _recVideoTracks = [];
    if (_recDisplayStream) {
      for (const t of _recDisplayStream.getTracks()) { try { t.stop(); } catch (e) {} }
      _recDisplayStream = null;
    }
    if (_recRafId) { cancelAnimationFrame(_recRafId); _recRafId = 0; }
    _recOffscreen = null;
    _recOffCtx = null;
    if (_recForceCleanupTimer) { clearTimeout(_recForceCleanupTimer); _recForceCleanupTimer = null; }
  };

  const _stopRec = () => {
    if (!_mediaRecorder) return;
    if (_recTimer) { clearTimeout(_recTimer); _recTimer = null; }
    // Only call .stop() if recorder is in a state where stop() is valid. If it's
    // already 'inactive', the recorder is hung — skip straight to force-cleanup.
    const state = (_mediaRecorder && _mediaRecorder.state) || 'inactive';
    if (state !== 'inactive') {
      try { _mediaRecorder.stop(); } catch (e) {}
    }
    btn.textContent = '● start recording';
    btn.style.background = 'rgba(60,60,70,0.85)';
    // Safety net: if onstop doesn't fire within 1.5s (recorder hung from a prior error
    // or weird state), force-tear down everything so F9 can start a fresh recording.
    if (_recForceCleanupTimer) clearTimeout(_recForceCleanupTimer);
    _recForceCleanupTimer = setTimeout(() => {
      if (_mediaRecorder) {
        statusRow.textContent = 'recorder hung — force-cleaned';
        _mediaRecorder = null;
        _teardownRecState();
        _recIndicator.style.display = 'none';
      }
    }, 1500);
  };

  // Floating ● REC indicator — visible when recording, regardless of admin panel
  // state. DOM overlay so it does NOT appear in the captured video (captureStream
  // sees only the WebGL canvas).
  const _recIndicator = document.createElement('div');
  _recIndicator.style.cssText = 'position:fixed;top:14px;right:14px;z-index:9999;'
    + 'display:none;align-items:center;gap:6px;padding:4px 8px;'
    + 'background:rgba(0,0,0,0.7);border:1px solid #f33;border-radius:3px;'
    + 'font:11px ui-monospace,monospace;color:#fff;pointer-events:none;user-select:none;';
  const _recDot = document.createElement('span');
  _recDot.style.cssText = 'width:8px;height:8px;border-radius:50%;background:#f33;'
    + 'animation:_ssRecPulse 1s ease-in-out infinite;';
  const _recText = document.createElement('span');
  _recText.textContent = 'REC';
  _recIndicator.appendChild(_recDot);
  _recIndicator.appendChild(_recText);
  document.body.appendChild(_recIndicator);
  const _recStyle = document.createElement('style');
  _recStyle.textContent = '@keyframes _ssRecPulse{0%,100%{opacity:1}50%{opacity:0.3}}';
  document.head.appendChild(_recStyle);

  // Toggle wrapper — keeps the indicator + button in sync regardless of trigger
  // source (click, hotkey, or auto-stop via reset).
  // Indicator visibility is now managed inside _startRec / onstop (not here) so the
  // async tab-capture path can defer showing it until permission is granted, and so
  // canvas-mode recordings hide it via the onstop teardown path.
  const _toggleRec = () => {
    if (_mediaRecorder) _stopRec(); else _startRec();
  };
  btn.addEventListener('click', (e) => { e.stopPropagation(); _toggleRec(); });
  _mixPanel.appendChild(btn);

  // F9 toggle — works whether pointer is locked, panel open or closed, anywhere
  // in the app. preventDefault is a belt-and-braces guard (F9 is unbound in most
  // browsers but better safe).
  window.addEventListener('keydown', (e) => {
    if (e.code !== 'F9' || e.repeat) return;
    e.preventDefault();
    _toggleRec();
  });

  _recResets.push(() => {
    if (_mediaRecorder) { _stopRec(); _recIndicator.style.display = 'none'; }
  });
}

document.body.appendChild(_adminPanel);

// Coin score HUD — top-right, just below the FPS readout. Updated when coins are collected.
let _score = 0;
// Rolling count-up: the HUD shows _scoreDisplayed, which eases toward the true _score each
// frame (see _updateScoreRoll in the render loop) so a chunk of coins banking at once rolls
// up instead of snapping. Code that changes _score no longer writes the score text directly.
let _scoreDisplayed = 0;
let _scoreRollLastText = '';
function _updateScoreRoll(dt) {
  if (Math.abs(_score - _scoreDisplayed) < 0.5) {
    _scoreDisplayed = _score;
  } else {
    _scoreDisplayed += (_score - _scoreDisplayed) * Math.min(1, dt * 14);
  }
  const txt = String(Math.round(_scoreDisplayed));
  if (txt !== _scoreRollLastText) {
    _coinScoreSpan.textContent = txt;
    _scoreRollLastText = txt;
  }
}

// Per-material baseline coin count when a block breaks (good timing, no streak, no charge bonus).
// Shared by spawnChunks (loot drop count) and _computeBaseYield (level-target derivation).
const BLOCK_BASELINE_COINS = { crate: 1, brick: 3, metal: 5 };

// =========================================================
// Per-level run state. Recomputed on _enterLevel.
//   _levelBaseYield     : sum of BLOCK_BASELINE_COINS across all standing blocks at level start.
//   _levelStarThresholds: { one: 0.5×, two: 1.0×, three: 1.5× } of base yield.
//   _levelStartAudio    : audioCtx.currentTime when this run started (for song-end timing).
//   _levelDurationSec   : how long the song plays before clear screen fires (Infinity = sandbox).
//   _levelEnded         : guards the clear screen so it only fires once per run.
// =========================================================
let _levelBaseYield = 0;
let _levelStarThresholds = { one: 0, two: 0, three: 0 };
let _levelStartAudio = 0;
let _levelDurationSec = Infinity;
let _levelEnded = false;
// End-of-level coin animation gate. _endTriggered = ending sequence has begun (either song-up
// or all-blocks-broken buffer elapsed). _pendingCoinAwards = count of flying coins still in-flight;
// the clear screen waits until this hits 0 + no free floor chips remain. _endTriggeredAtMs gates
// a 5-second hard-end timeout so chips that never resolve don't hang the screen forever.
let _endTriggered = false;
let _endTriggeredAtMs = 0;
// Why the level ended: 'time' (song duration elapsed) or 'cleared' (all anchored blocks down).
// Used by the clear screen to pick the right fail-state subtext. Null until the trigger fires.
let _endReason = null;
let _endingCashedOut = false;
let _pendingCoinAwards = 0;
// Running SUM of the score value of coins still flying to the counter (not just the count, since
// coins can be worth >1). Folded into _score synchronously at level end so a big cash-out that
// hasn't finished animating still counts toward pass/fail. _coinAwardEpoch is bumped at level end
// and level start; any in-flight award callback whose captured epoch no longer matches becomes a
// no-op, so the folded-in value can't double-count and stale flights can't leak into the next run.
let _pendingCoinValue = 0;
let _coinAwardEpoch = 0;
// Per-run aggregates for end-of-level bonuses.
let _runLongestStreak = 0;
let _runPerfectHits = 0;
let _runTotalTimingHits = 0;

// Live target for the HUD progress bar. Sandbox falls back to a large number so the bar still
// fills meaningfully; real levels overwrite this with the 1-star threshold (the "pass" line).
let _hudTarget = 600;

// Streak: increments on every non-miss timing event (tap click-down, charge click-down,
// charge release), resets to 0 on a miss. Multiplies coin yield at higher tiers.
let _streak = 0;
// A miss puts the player out of rhythm. The next successful on-beat smash pays no coins
// and shows BACK ON BEAT, then normal rewards resume. Non-stacking: repeated misses only
// keep this at one recovery hit.
let _rhythmRecoveryDebt = false;
// Per-fist anti-mash floor. A scored punch sets the marker; subsequent punches within
// MIN_PUNCH_INTERVAL_MS on the same fist don't score (no streak tick, no coin bonus, no
// timing label) — but still smash visually. Lets the player land 16th-note rolls (~125ms)
// while preventing mashing-clears at 8+ punches/sec.
const MIN_PUNCH_INTERVAL_MS = 125;
const _lastScoredPunchMs = [0, 0];
// Cross-fist anti-mash floor — closes the spacebar-mash exploit. Spacebar synthesises
// alternating-fist presses, so the per-fist gate above never trips on rapid space spam
// (each fist's individual interval is fine). This timestamp tracks the LAST press on
// either fist (updated BEFORE the slot gate so even slot-gated presses contribute);
// presses within CROSS_FIST_MASH_MS of it get flagged rapid → tier='miss' regardless
// of which fist. Threshold tuned below the 125ms 16th-note interval so legitimate
// dual-press 16th-rolls still score, but spacebar mash at 10+ Hz gets caught.
const CROSS_FIST_MASH_MS = 120;
let _lastAnyPressMs = 0;
function _streakBonus(s) {
  if (s >= 50) return 3.0;  // god-mode tier, aligned with storm-max kicking in at 50 — coin mult + AOE + shockwave + chunk impulse all peak here
  if (s >= 20) return 2.0;
  if (s >= 10) return 1.5;
  if (s >= 5)  return 1.25;
  return 1.0;
}
function _streakTier(s) {
  // Below 50, fixed early-progression tiers. From 50 onwards, every multiple of 50 is a tier
  // (50, 100, 150, …) — endless celebrations for long chains.
  if (s >= 50) return Math.floor(s / 50) * 50;
  if (s >= 20) return 20;
  if (s >= 10) return 10;
  if (s >= 5)  return 5;
  return 0;
}
let _lastStreakTier = 0;
// Tracks last timing-event time so we can reset streaks when the player goes idle for >1.5
// beats. Init to a future time so streak doesn't insta-reset on first idle frame at load.
let _lastTimingEventMs = Number.POSITIVE_INFINITY;
function _onTimingEvent(tier, opts = {}) {
  _lastTimingEventMs = performance.now();
  if (tier === 'miss') {
    _rhythmRecoveryDebt = true;
    // softMiss: charge release events don't reset the streak — holding charged coins is
    // intentional play, players shouldn't feel rushed when coins are in orbit. The click-down
    // already graded the rhythm; release is bonus-only.
    if (!opts.softMiss) _streak = 0;
  } else {
    _streak += 1;
    if (_streak > _runLongestStreak) _runLongestStreak = _streak;
  }
  // Per-run hit accounting — feeds the end-of-level "Perfect %" bonus.
  _runTotalTimingHits++;
  if (tier === 'perfect') _runPerfectHits++;
  const newTier = _streakTier(_streak);
  if (newTier > _lastStreakTier) {
    _popStreakTierLabel(newTier);
  }
  _lastStreakTier = newTier;
  _updateStreakHud();
  // (Streak end no longer triggers cash-out — coin lifetime is independent of streak now.
  //  Coins live ~10s on the floor with a flash phase near the end before flying to counter.)
}

function _consumeRhythmRecoveryDebt() {
  if (!_rhythmRecoveryDebt) return false;
  _rhythmRecoveryDebt = false;
  return true;
}

// Sum of baseline coin counts across all currently-standing blocks. Used at level start to
// derive star thresholds without hardcoding numbers per layout.
function _computeBaseYield() {
  let total = 0;
  for (const b of blocks) total += BLOCK_BASELINE_COINS[b.userData.material] || 1;
  return total;
}

// Animated cash-out — every uncollected currency chip on the floor flies to the counter.
// Skips chips already in motion toward collection (orbiting toward a fist or pendingCollect after
// hitting a block as a projectile). Each chip preserves its rhythm-multiplier value at award.
// Called when a streak ends (miss or idle) — no coin is forfeit, just the option to charge them.
//
// Coins stagger out on a 16th-note grid (~125ms at 120 BPM) so the cash-out reads as musical
// instead of a single batched whoosh. Each coin's clink lands on the grid via _awardDirectCoins.
function _cashOutPhysicalCoins() {
  const toAward = [];
  for (let i = debris.length - 1; i >= 0; i--) {
    const chip = debris[i];
    const ud = chip.userData;
    if (!ud.isCurrency) continue;
    if (ud.orbiting) continue;
    if (ud.pendingCollect) continue;
    toAward.push(chip);
    debris.splice(i, 1);
  }
  if (toAward.length === 0) return;
  // Pre-increment synchronously — the setTimeouts below haven't fired yet, but _checkLevelEnd
  // checks the pending counter on this same tick. Passing alreadyCounted=true avoids double-count.
  _pendingCoinAwards += toAward.length;
  // Commit the WHOLE batch's value up front (not when each staggered flight starts). A big pile
  // cashes out over many seconds; if the level-end hard timeout fires mid-stagger, the fold-in must
  // still account for coins that are merely QUEUED here, not yet airborne.
  for (const chip of toAward) _pendingCoinValue += chip.userData.multiplier ?? 1;
  const epoch = _coinAwardEpoch;
  const THIRTYSECOND_MS = (MUSIC_BEAT / 8) * 1000;  // 62.5ms at 120 BPM (32nd-note grid — keeps cash-out musical but compressed for snappier clear screen)
  for (let idx = 0; idx < toAward.length; idx++) {
    const chip = toAward[idx];
    const ud = chip.userData;
    const value = ud.multiplier ?? 1;
    const pos = chip.position.clone();  // capture before removeChip disposes the mesh
    const note = ud.coinNote;           // preserve coin's voice into the bank clink
    _removeChip(chip);
    // Epoch-guard the deferred flight: once the level ends, the value is already folded into the
    // score, so a queued flight must not fire (it would double-count or leak into the next run).
    setTimeout(() => {
      if (epoch !== _coinAwardEpoch) return;
      _awardDirectCoins(1, value, pos, true, note);
    }, idx * THIRTYSECOND_MS);
  }
}

// Per-coin lifetime — every currency chip on the floor auto-flies to the counter after this many
// seconds, with a transparency flash in the last bit so the player gets a visual warning that the
// coin is about to be banked. Independent of streak so the player has reliable charge-suck time.
const COIN_LIFETIME_SEC = 6;
const COIN_FADE_START_SEC = 4.5;   // last 1.5s flashes
function _updatePhysicalCoinLifetimes() {
  const nowSec = performance.now() / 1000;
  for (let i = debris.length - 1; i >= 0; i--) {
    const chip = debris[i];
    const ud = chip.userData;
    if (!ud.isCurrency) continue;
    if (ud.orbiting || ud.pendingCollect) continue;
    // Armed projectiles in flight are doing their job — don't auto-bank mid-arc just because
    // the chip's original floor age happens to cross COIN_LIFETIME_SEC during flight.
    if (ud.projectileUntil && nowSec < ud.projectileUntil) continue;
    const age = nowSec - ud.spawnedAt;
    if (age >= COIN_LIFETIME_SEC) {
      // Auto-fly to counter. Pre-increment _pendingCoinAwards so end-of-level waits for the flight.
      _pendingCoinAwards += 1;
      const value = ud.multiplier ?? 1;
      _pendingCoinValue += value;  // pre-add value (alreadyCounted call below won't book it)
      const pos = chip.position.clone();
      const note = ud.coinNote;
      _removeChip(chip);
      debris.splice(i, 1);
      _awardDirectCoins(1, value, pos, true, note);
    } else if (age >= COIN_FADE_START_SEC) {
      // Flash the chip's opacity so the player sees it's about to expire (~5 Hz blink).
      if (!chip.material.transparent) chip.material.transparent = true;
      const flashHz = 5;
      const phase = (Math.sin(nowSec * Math.PI * 2 * flashHz) + 1) / 2;  // 0..1
      chip.material.opacity = 0.25 + phase * 0.75;
    }
  }
}

// Sync version of cash-out — used at end-of-level so floor coins are banked before bonus calc.
// Adds value directly to _score without the flying animation (clear screen is about to overlay).
function _flushPhysicalCoinsToScore() {
  let added = 0;
  for (let i = debris.length - 1; i >= 0; i--) {
    const chip = debris[i];
    const ud = chip.userData;
    if (!ud.isCurrency) continue;
    if (ud.orbiting) continue;
    if (ud.pendingCollect) continue;
    added += ud.multiplier ?? 1;
    _removeChip(chip);
    debris.splice(i, 1);
  }
  if (added > 0) {
    _score += added;  // HUD text rolls up via _updateScoreRoll
  }
}

// Shared streak-break: zero the streak + refresh the HUD. Used by the normal-mode idle timer and
// by the assist-mode onset-miss logic.
function _breakStreak() {
  if (_streak <= 0) return;
  _streak = 0;
  _lastStreakTier = 0;
  _updateStreakHud();
}

// Idle-reset check — in NORMAL play, resets the streak if the player goes 3s without a timing event.
// EXCEPTION: while either fist is actively charging, the timer is paused — committing to a
// charge mid-streak counts as continuous engagement (release on-beat to keep the chain alive).
// ASSIST mode uses a separate decay below: a bar-length grace window from the last landed hit
// (refreshed in _updateEasyMode on each landed hit). One bar is stable within a song — unlike the
// instantaneous gap between onsets, it doesn't shrink during dense fills (which made rapid double-
// whiffs feel illegible) or balloon on slow songs. Keep smashing within the bar and it holds.
function _checkStreakIdleReset() {
  if (_streak <= 0) return;
  if (_easyMode) {
    // Assist decay: a bar-length grace window from the last landed hit (set in _updateEasyMode).
    if (!audioCtx || !_musicStarted || _easyStreakGraceUntil <= 0) return;
    // Charging (right held) pauses the decay, like the main game's idle timer — refresh the window so
    // releasing after a long charge doesn't insta-break the streak.
    if (charging[1]) { _easyStreakGraceUntil = audioCtx.currentTime + EASY_STREAK_GRACE_BARS * MUSIC_BAR; return; }
    if (audioCtx.currentTime > _easyStreakGraceUntil) _breakStreak();
    return;
  }
  if (charging[0] || charging[1]) {
    // Currently charging — refresh the idle clock so release doesn't trigger an instant reset.
    _lastTimingEventMs = performance.now();
    return;
  }
  // 3s — generous enough for big chip-storm cascades to finish toppling without resetting the streak
  // (was 1250ms, which punished the player for watching satisfying destruction).
  const STREAK_IDLE_MS = 3000;
  if (performance.now() - _lastTimingEventMs > STREAK_IDLE_MS) _breakStreak();
}

// Group: number on top, progress bar below. Right-aligned so both align to the right edge.
// In-level score HUD — chunky yellow panel in top-right. Streak ribbon on top, COINS label
// + score number below, fat progress bar at the bottom with tick marks at the 1★/2★/3★
// thresholds so the player sees the goal posts as the bar fills. All styling lives in the
// .ss-hud kit classes; JS only constructs the DOM and updates state.
const _coinHudGroup = document.createElement('div');
_coinHudGroup.className = 'ss-hud';

// Streak HUD lives on the LEFT as its own card (see _streakBadge below). The coin HUD
// on the right is just coins + progress + blocks-left, kept focused on economy. Splitting
// streak vs economy into two corners means each piece of info has its own home and the
// player can glance to a known location for whichever they need.

// 2-frame 8x8 pixel-art coin sprite — full disc / narrow ellipse, side-by-side as a 16x8
// sprite sheet. Procedural so the kit stays asset-free; data URL is a one-time cost.
function _paintHudCoinSprite() {
  const c = document.createElement('canvas');
  c.width = 16; c.height = 8;
  const ctx = c.getContext('2d');
  const palette = { '.': null, b: '#0a0820', g: '#FFE600', h: '#fff89e', y: '#c8a000' };
  const frames = [
    // Frame 0 — full coin disc with a top-left highlight and a pixel quarter-note engraved
    // in darker gold (y), matching the in-world coin's stamped note (see getCoinBumpTexture).
    ['.bbbbbb.',
     'bhhggygb',
     'bghggygb',
     'bggggygb',
     'bgyyyygb',
     'bgyyyggb',
     'bggggggb',
     '.bbbbbb.'],
    // Frame 1 — edge-on ellipse, narrow.
    ['...bb...',
     '..bggb..',
     '.bgggb..',
     '.bghgb..',
     '.bggyb..',
     '.bgggb..',
     '..bggb..',
     '...bb...'],
  ];
  for (let f = 0; f < 2; f++) {
    const xOff = f * 8;
    for (let y = 0; y < 8; y++) {
      for (let x = 0; x < 8; x++) {
        const col = palette[frames[f][y][x]];
        if (col) { ctx.fillStyle = col; ctx.fillRect(xOff + x, y, 1, 1); }
      }
    }
  }
  return c.toDataURL();
}
const _hudCoinSpriteUrl = _paintHudCoinSprite();

// HUD-layer copy of the in-world wall-speaker cone (see _makeWallConeTexture). Renders the
// same pixel disc pattern to a data URL so the floating HUD anchor and the in-level
// speakers share one visual identity — the player reads them as the same object, just one
// on the wall and one in the corner.
function _paintHudWallSpeakerSprite() {
  const c = document.createElement('canvas');
  c.width = 32; c.height = 32;
  const ctx = c.getContext('2d');
  _drawPixelDisc(ctx, 16, 16, 15.5, '#3a3f48');
  _drawPixelDisc(ctx, 16, 16, 13,   '#7a8088');
  _drawPixelDisc(ctx, 16, 16,  8,   '#4a4f58');
  _drawPixelDisc(ctx, 16, 16,  4,   '#9aa0a8');
  ctx.fillStyle = '#cfd6dc'; ctx.fillRect(13, 13, 1, 1);
  return c.toDataURL();
}
const _hudWallSpeakerSpriteUrl = _paintHudWallSpeakerSprite();

// Floating speakers — bottom-left + bottom-right of the viewport, flanking the player like
// stage monitors. Both always-on rhythm anchors that don't require a target highlight,
// driven from _updateHudBeatAnchors alongside the coin panel glow.
const _hudSpeakerBaseCss = 'position:fixed;bottom:18px;width:64px;height:64px;'
  + `background-image:url(${_hudWallSpeakerSpriteUrl});background-size:contain;`
  + 'background-repeat:no-repeat;image-rendering:pixelated;image-rendering:crisp-edges;'
  + 'pointer-events:none;user-select:none;z-index:998;transform-origin:center;display:none;';
const _hudSpeakerFloat = document.createElement('div');
_hudSpeakerFloat.style.cssText = _hudSpeakerBaseCss + 'left:18px;';
document.body.appendChild(_hudSpeakerFloat);
const _hudSpeakerFloatRight = document.createElement('div');
_hudSpeakerFloatRight.style.cssText = _hudSpeakerBaseCss + 'right:18px;';
document.body.appendChild(_hudSpeakerFloatRight);

const _coinHud = document.createElement('div');
_coinHud.className = 'ss-hud__row';
const _coinIcon = document.createElement('span');
_coinIcon.className = 'ss-hud__coin';
_coinIcon.style.backgroundImage = `url(${_hudCoinSpriteUrl})`;
const _coinScoreSpan = document.createElement('span');
_coinScoreSpan.className = 'ss-hud__score';
_coinScoreSpan.textContent = '0';
// "/ N" target readout — sits next to the live score. Sized close to the live score so the
// goal stays readable mid-run (playtesters reported they couldn't tell what they were
// aiming for at the old 16px/0.55 opacity; bumped to 22px/0.85 2026-05-16). Still slightly
// smaller + dimmer than the live score so the running total stays the headline element.
// Populated from the 1★ threshold in _setHudGoal; hidden on sandbox / no-goal levels.
const _coinTargetSpan = document.createElement('span');
_coinTargetSpan.style.cssText = 'font-family:var(--font-display);font-size:22px;color:var(--col-ink);opacity:0.85;letter-spacing:1px;';
_coinTargetSpan.style.display = 'none';
_coinHud.appendChild(_coinIcon);
_coinHud.appendChild(_coinScoreSpan);
_coinHud.appendChild(_coinTargetSpan);
_coinHudGroup.appendChild(_coinHud);

const _coinBarBg = document.createElement('div');
_coinBarBg.className = 'ss-hud__bar';
const _coinBarFill = document.createElement('div');
_coinBarFill.className = 'ss-hud__bar-fill';
_coinBarBg.appendChild(_coinBarFill);
// Single tick marker at the 1★ "level cleared" threshold. Hidden in sandbox / no-target
// levels. Bar still fills toward 3★ — the tick sits mid-bar so players can see the
// fill approaching the pass line.
const _coinBarTick = document.createElement('div');
_coinBarTick.className = 'ss-hud__bar-tick';
_coinBarTick.style.display = 'none';
// Pink star icon rides with the tick — same left% positioning so they always line up.
// Speaks the kit's vocabulary (stars are the 1★/2★/3★ reward language) instead of a 9px text label.
const _coinBarTickLabel = document.createElement('div');
_coinBarTickLabel.className = 'ss-hud__bar-tick-label';
_coinBarTickLabel.textContent = '★';
_coinBarTickLabel.style.display = 'none';
_coinBarBg.appendChild(_coinBarTick);
_coinBarBg.appendChild(_coinBarTickLabel);
_coinHudGroup.appendChild(_coinBarBg);

// Blocks-left text removed — it cluttered the HUD and the on-block crack textures + the
// progress bar already telegraph "how close am I to clearing this level". Element kept as
// a hidden stub so any downstream code that touches _blocksLeftEl.textContent doesn't fault.
const _blocksLeftEl = document.createElement('div');
_blocksLeftEl.style.display = 'none';

document.body.appendChild(_coinHudGroup);
// HUD is hidden until pointer-lock = playing. Toggled on/off by the controls lock/unlock
// listeners further down so it never shows on the home screen, level select, or clear screen.
_coinHudGroup.style.display = 'none';

// Goal intro overlay — brief banner at level start showing the 1★ pass coin threshold so
// playtesters know what they're aiming for. Shown on every scored level (curated + UGC),
// sandbox skips. Added 2026-05-16 after playtest reported "didn't know what the goal was."
// After a hold beat, the banner SHRINKS + flies toward the HUD's top-right corner while
// the HUD's /N target span pulses — anchoring the visual association between "the GOAL I
// just read" and "the small /N in the corner" so players know where to glance during play.
// Kit brand-title styling: pixel display font, yellow with chunky ink stroke + drop shadow.
const _goalIntro = document.createElement('div');
_goalIntro.style.cssText = 'position:fixed;top:30%;left:50%;transform:translate(-50%,-50%) scale(1);'
  + 'font-family:var(--font-display);font-size:48px;letter-spacing:2px;color:var(--col-bg-panel);'
  + '-webkit-text-stroke:3px var(--col-ink);paint-order:stroke;'
  + 'text-shadow:0 4px 0 var(--col-ink),0 6px 12px rgba(0,0,0,0.4);'
  + 'pointer-events:none;text-align:center;white-space:nowrap;z-index:500;'
  + 'opacity:0;transform-origin:center center;'
  + 'transition:opacity 500ms ease, top 700ms cubic-bezier(0.55,0.05,0.7,0.15), '
  + 'left 700ms cubic-bezier(0.55,0.05,0.7,0.15), '
  + 'transform 700ms cubic-bezier(0.55,0.05,0.7,0.15);';
document.body.appendChild(_goalIntro);
const _goalIntroTimers = { shrink: null, flash: null };
function _clearGoalIntroTimers() {
  if (_goalIntroTimers.shrink) { clearTimeout(_goalIntroTimers.shrink); _goalIntroTimers.shrink = null; }
  if (_goalIntroTimers.flash)  { clearTimeout(_goalIntroTimers.flash);  _goalIntroTimers.flash  = null; }
}
function _flashHudGoal() {
  // Three quick pulses on the HUD /N target — scale-up + cyan tint, fall back to default.
  // 200ms per phase × 6 phases = 1.2s total. Drives the player's eye to the corner exactly
  // as the intro banner finishes its shrink, so the visual hand-off reads as one motion.
  const span = _coinTargetSpan;
  if (!span) return;
  const prevTransition = span.style.transition;
  span.style.transition = 'transform 180ms ease, color 180ms ease, opacity 180ms ease';
  span.style.transformOrigin = 'center center';
  let count = 0;
  const pulse = () => {
    const up = (count % 2 === 0);
    span.style.transform = up ? 'scale(1.6)' : 'scale(1)';
    span.style.color = up ? 'var(--col-cyan)' : 'var(--col-ink)';
    span.style.opacity = up ? '1' : '0.85';
    count++;
    if (count < 6) setTimeout(pulse, 200);
    else {
      // Restore the default styling explicitly so any leftover transform/colour doesn't
      // stick around between levels.
      setTimeout(() => {
        span.style.transform = '';
        span.style.color = 'var(--col-ink)';
        span.style.opacity = '0.85';
        span.style.transition = prevTransition;
      }, 220);
    }
  };
  pulse();
}
function _showGoalIntro(coinTarget) {
  if (!coinTarget) return;
  _clearGoalIntroTimers();
  _goalIntro.textContent = `GOAL: ${coinTarget} COINS`;
  // Reset to centred-and-large state before fading in. Otherwise a replayed intro starts
  // mid-shrink from the previous run.
  _goalIntro.style.top = '30%';
  _goalIntro.style.left = '50%';
  _goalIntro.style.transform = 'translate(-50%, -50%) scale(1)';
  // Force reflow so the opacity transition fires even after the reset.
  void _goalIntro.offsetWidth;
  _goalIntro.style.opacity = '1';
  // After a hold beat, fly toward the HUD's top-right corner while shrinking + fading.
  // Target coords are rough — the HUD position is class-driven so we can't read it exactly
  // without a layout query, but ~88% left / ~8% top hits the corner band on every viewport
  // we care about. The pulsing HUD /N is what actually sells the "look here" landing.
  _goalIntroTimers.shrink = setTimeout(() => {
    _goalIntro.style.top = '8%';
    _goalIntro.style.left = '88%';
    _goalIntro.style.transform = 'translate(-50%, -50%) scale(0.30)';
    _goalIntro.style.opacity = '0';
    _goalIntroTimers.shrink = null;
    // Kick the HUD flash off mid-shrink so the eye is drawn to the corner exactly as the
    // banner arrives there. 400ms offset = banner is ~60% into its 700ms move.
    _goalIntroTimers.flash = setTimeout(() => {
      _flashHudGoal();
      _goalIntroTimers.flash = null;
    }, 400);
  }, 1500);
}

// Set the bar's threshold target + tick mark positions. Pass `null` for sandbox / unscored
// levels (hides the bar). Pass the level's _levelStarThresholds object for scored levels —
// bar fills toward 3★, ticks at 1★/2★/3★ positions.
function _setHudGoal(thresholds) {
  if (!thresholds || !thresholds.one) {
    _coinBarBg.style.display = 'none';
    _coinTargetSpan.style.display = 'none';
    _coinBarTick.style.display = 'none';
    _coinBarTickLabel.style.display = 'none';
    return;
  }
  _coinBarBg.style.display = 'block';
  // Bar fills toward the 1★ goal — the end of the bar IS the goal. Star marker rides
  // the right edge as the finish-line icon; vertical tick is no longer needed since
  // the bar's own end-stroke defines the boundary.
  _coinBarTick.style.display = 'none';
  _coinBarTickLabel.style.left = '100%';
  _coinBarTickLabel.style.display = 'block';
  _coinTargetSpan.textContent = `/ ${thresholds.one}`;
  _coinTargetSpan.style.display = 'inline-block';
}

// Continuous rainbow sweep on the multiplier line — same hue cycle as the streak-tier
// banner that pops in the centre on tier crossings, but looping forever while a bonus is
// active. Reusing that visual language ties the persistent badge to the celebration moment
// so the player reads them as "the same thing, just on different timescales."
let _streakMultAnim = null;
function _startStreakRainbow() {
  if (_streakMultAnim) return;
  const frames = [];
  for (let i = 0; i <= 12; i++) {
    frames.push({ color: `hsl(${Math.round(i * 360 / 12)}, 100%, 65%)`, offset: i / 12 });
  }
  _streakMultAnim = _streakBadgeMult.animate(frames, {
    duration: 4000, iterations: Infinity, easing: 'linear',
  });
}
function _stopStreakRainbow() {
  if (_streakMultAnim) { _streakMultAnim.cancel(); _streakMultAnim = null; }
}

// Pill rainbow — same hue cycle as the multiplier text, applied to the fill background.
// Only runs at storm-max (streak >= 50). When it stops, the pill snaps back to its
// inline #00E5FF cyan baseline.
let _streakPillAnim = null;
function _startStreakPillRainbow() {
  if (_streakPillAnim) return;
  const frames = [];
  for (let i = 0; i <= 12; i++) {
    frames.push({ background: `hsl(${Math.round(i * 360 / 12)}, 100%, 60%)`, offset: i / 12 });
  }
  _streakPillAnim = _streakBadgePillFill.animate(frames, {
    duration: 4000, iterations: Infinity, easing: 'linear',
  });
}
function _stopStreakPillRainbow() {
  if (_streakPillAnim) { _streakPillAnim.cancel(); _streakPillAnim = null; }
}

// Storm-max effect envelope. Fires when storm power hits 100% (streak ≥ 50). Drives:
//   1. Rainbow tint on currency chips (per-coin hue offset so they shimmer, not all one color)
//   2. Rainbow overlay on the canvas (color-blended sky/scene wash)
//   3. One-shot ascending chime at the rising-edge crossing
// Intensity eases 0↔1 over ~800ms so transitions feel like a wave, not a snap.
let _stormMaxActive = false;
let _stormMaxIntensity = 0;
const STORM_MAX_FADE_RATE = 1.25;  // 1/sec — 800ms to full / clear

// Sky-only rainbow: mutate the Three.js Sky shader's sunPosition + rayleigh uniforms when
// storm-max is active. Rapidly orbiting the sun changes the atmospheric scattering colors
// across the spectrum (the same physics that makes sunsets red and noon-skies blue). Cached
// baseline restores naturally when intensity decays to 0. Affects ONLY the sky mesh —
// blocks, fists, debris, HUD are all untouched because they don't read these uniforms.
const _stormSkyBaselineSunPos = sky.material.uniforms['sunPosition'].value.clone();
const _stormSkyOrbitSun = new THREE.Vector3();
const _stormMaxTmpColor = new THREE.Color();
const _heatTmpColor = new THREE.Color();  // streak heat-up ember glow (crossfades into the storm-max rainbow)
const _rimTmpColor = new THREE.Color();   // fresnel rim tint, warmed by the same streak ramp
const _FIST_RIM_WARM = new THREE.Color(0xff8a3c);  // amber the rim shifts toward as the streak heats up
const _FIST_RIM_HOT = new THREE.Color(0xfff2e0);   // near-white the rim pushes to at storm-max
// Unified fist color update — handles both the charge-warming ramp (subtle gold→orange-red
// tint on skin as charge climbs, same curve as orbiting coins) AND the storm-max neon
// rainbow tint. Both effects are layered on top of each other and BOTH are restricted to
// SKIN parts (hand + knuckles, indexes 0-4) — wrist band, sleeve, and forearm stay their
// original muted colors so the player reads the warming/rainbow as their HAND heating up,
// not the whole costume.
function _updateFistColor() {
  const t = performance.now() / 1000;
  for (let fi = 0; fi < 2; fi++) {
    const fist = fi === 0 ? leftFist : rightFist;
    const mats = fist.userData.materials;
    const origs = fist.userData.originalColors;
    // Charge warming amount for this fist — subtle quadratic ramp, capped well under 1.0
    // so the tint never fully overrides the skin tone (per Rich: "more subtle than coins").
    const charge = chargeAmount[fi];
    const ramp = Math.max(0, (charge - SUCK_GRACE) / (1 - SUCK_GRACE));
    let warmT = ramp * ramp * 0.5;
    if (charge >= 0.99) {
      const pulse = (Math.sin(t * 8) + 1) * 0.5;
      warmT = Math.min(0.7, warmT + pulse * 0.15);
    }
    const skinCount = fist.userData.skinMatCount ?? 5;  // hand + knuckles (+ thumb); rest = wrist/sleeve
    for (let i = 0; i < mats.length; i++) {
      const isSkin = i < skinCount;
      // Step 1: base color. Skin parts warm with charge; non-skin parts stay original.
      if (isSkin && warmT > 0) {
        mats[i].color.copy(origs[i]).lerp(_COIN_PULSE_COLOR, warmT);
      } else {
        mats[i].color.copy(origs[i]);
      }
      // Step 2: streak GLOW on skin parts (hand + knuckles) — wrist/sleeve stay muted. Two stages that
      // crossfade: a streak HEAT-UP ember that builds from streak 5 (hands visibly warm as you climb),
      // handing off into the storm-max RAINBOW at 50+. Both drive the EMISSIVE channel because diffuse
      // albedo alone renders dull under the dim near-camera light (Lambert = albedo × light). The ember
      // cools the instant a miss resets the streak — free "you lost your heat" feedback.
      if (isSkin) {
        const heatT = Math.min(1, Math.max(0, (_streak - 5) / 40));  // 0 @ streak 5 → 1 @ streak 45
        if (heatT > 0 || _stormMaxIntensity > 0) {
          // Ember for the current streak: deep red-orange → amber, brightening as it heats.
          _heatTmpColor.setHSL(0.015 + 0.11 * heatT, 1, 0.42 + 0.16 * heatT);
          // Storm-max rainbow (cycling hue), then crossfade ember → rainbow by storm intensity.
          const hueDeg = ((t * 120 + i * 30) % 360 + 360) % 360;
          _stormMaxTmpColor.setHSL(hueDeg / 360, 1, 0.55);
          _heatTmpColor.lerp(_stormMaxTmpColor, _stormMaxIntensity);
          const pulse = 0.85 + 0.15 * Math.sin(t * 6);
          mats[i].color.lerp(_heatTmpColor, Math.max(heatT * 0.35, _stormMaxIntensity));
          mats[i].emissive.copy(_heatTmpColor);
          mats[i].emissiveIntensity = Math.max(heatT * 0.55, _stormMaxIntensity * 1.35) * pulse;
        } else if (mats[i].emissiveIntensity !== 0) {
          mats[i].emissive.setHex(0x000000);
          mats[i].emissiveIntensity = 1;  // back to material default (with black emissive = no glow)
        }
        // Fresnel rim tracks the same ramp: cool blue-white at rest → amber as the streak heats →
        // near-white at storm-max, and brightens a touch so the edge reads hotter the higher you climb.
        const rimU = mats[i].userData.rimUniforms;  // exists once the shader has compiled
        if (rimU) {
          const heatT2 = Math.min(1, Math.max(0, (_streak - 5) / 40));
          _rimTmpColor.copy(_FIST_RIM_COLOR).lerp(_FIST_RIM_WARM, heatT2);
          if (_stormMaxIntensity > 0) _rimTmpColor.lerp(_FIST_RIM_HOT, _stormMaxIntensity);
          rimU.uRimColor.value.copy(_rimTmpColor);
          rimU.uRimStrength.value = 0.55 + 0.3 * Math.max(heatT2, _stormMaxIntensity);
        }
      }
      const missT = Math.max(0, (missRecoveryUntil[fi] - performance.now()) / MISS_RECOVERY_MS);
      if (missT > 0 && isSkin) {
        mats[i].color.lerp(_C_CHARGE_MID, Math.min(1, missT * 1.35));
      }
    }
  }
}

// Charge vignette gets a hue-rotate filter at storm-max — the existing gold radial gradient
// rotates through the spectrum. Saturation boosted alongside so the vibrancy reads neon.
function _updateStormMaxVignette() {
  if (_stormMaxIntensity <= 0) {
    _chargeVignette.style.filter = '';
    return;
  }
  const hue = (performance.now() / 1000 * 60) % 360;
  _chargeVignette.style.filter = `hue-rotate(${hue}deg) saturate(${1 + _stormMaxIntensity * 1.5})`;
}

function _updateStormMaxSky() {
  if (_stormMaxIntensity <= 0) {
    // Restore the baseline sunPosition each frame while inactive. Without this, the last
    // mutated value from when storm was active would persist (_updateSkyBeatPulse only
    // writes rayleigh, not sunPosition, so it can't recover the value on its own).
    sky.material.uniforms['sunPosition'].value.copy(_stormSkyBaselineSunPos);
    return;
  }
  const t = performance.now() / 1000;
  // Orbit sun azimuthally at ~1.5 rad/sec → full circle every ~4.2s. Elevation fixed at
  // ~45° so the sun stays visible-in-sky rather than ducking below the horizon (which
  // would turn the sky black at the worst possible "you earned a reward!" moment).
  const azimuthRad = t * 1.5;
  const elevationRad = Math.PI / 4;
  _stormSkyOrbitSun.setFromSphericalCoords(1, Math.PI / 2 - elevationRad, azimuthRad);
  // Lerp from baseline → orbit by envelope intensity. Smooth fade in/out.
  sky.material.uniforms['sunPosition'].value.lerpVectors(_stormSkyBaselineSunPos, _stormSkyOrbitSun, _stormMaxIntensity);
  // Boost rayleigh too — exaggerates the color shift so the wash is dramatic, not subtle.
  // Oscillating boost gives a "breathing" feel on top of the orbital hue shift.
  const rayleighBoost = _stormMaxIntensity * (3 + Math.sin(t * 2.5) * 2);
  sky.material.uniforms['rayleigh'].value = _SKY_BASELINE_RAYLEIGH + rayleighBoost;
}

function _playStormMaxChime() {
  if (!audioCtx || !_sfxBus) return;
  // Rising major arpeggio — A, C#, E, A (octave). Sine waves stacked with short bell-like
  // envelopes; routed through the SFX bus so it follows the player's SFX volume slider.
  const notes = [880, 1108.73, 1318.51, 1760];
  const t0 = audioCtx.currentTime;
  notes.forEach((f, i) => {
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.type = 'sine';
    osc.frequency.value = f;
    const t = t0 + i * 0.07;
    gain.gain.setValueAtTime(0.0, t);
    gain.gain.linearRampToValueAtTime(0.11, t + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.45);
    osc.connect(gain).connect(_sfxBus);
    osc.start(t);
    osc.stop(t + 0.5);
  });
}

function _updateStormMaxState(dt) {
  const wantOn = _stormPowerFactor() >= 1.0;
  if (wantOn && !_stormMaxActive) {
    _stormMaxActive = true;
    _playStormMaxChime();
    _updateStreakHud();  // light up the multiplier + pill rainbow
  } else if (!wantOn && _stormMaxActive) {
    _stormMaxActive = false;
    _updateStreakHud();  // drop back to orange multiplier + cyan pill
  }
  const target = wantOn ? 1 : 0;
  if (_stormMaxIntensity < target) {
    _stormMaxIntensity = Math.min(target, _stormMaxIntensity + STORM_MAX_FADE_RATE * dt);
  } else if (_stormMaxIntensity > target) {
    _stormMaxIntensity = Math.max(target, _stormMaxIntensity - STORM_MAX_FADE_RATE * dt);
  }
  // Sky visuals run separately in _updateStormMaxSky() (after _updateSkyBeatPulse) so
  // shader uniform writes don't get clobbered. Coin rainbow tint runs inline in the
  // currency-chip color loop.
}

// Per-frame pill update. Mirrors _checkStreakIdleReset's pause-on-charge logic so the
// visual never disagrees with the reset rule. Bar tints red as it gets close to zero so
// the urgency is felt before the streak actually breaks.
function _updateStreakPill() {
  if (_streak < 3) { _streakBadgePill.style.display = 'none'; return; }
  _streakBadgePill.style.display = 'block';
  if (_easyMode) {
    // Assist decay is the bar-length grace window from the last hit (see _checkStreakIdleReset). The
    // pill tops back up to full on each landed hit, then drains toward empty as the window runs out,
    // reddening as it nears zero. Full while charging (decay paused).
    if (!audioCtx || !_musicStarted || _easyStreakGraceUntil <= 0 || charging[1]) { _paintStreakPill(1); return; }
    const now = audioCtx.currentTime;
    const span = EASY_STREAK_GRACE_BARS * MUSIC_BAR;
    const remain = Math.max(0, Math.min(1, (_easyStreakGraceUntil - now) / span));
    _paintStreakPill(remain);  // width + cyan→pink tint as it drains
    return;
  }
  const STREAK_IDLE_MS = 3000;  // keep in sync with _checkStreakIdleReset
  const charging01 = charging[0] || charging[1];
  // While charging, treat the pill as freshly-pinned so it stays at 100% (matches the
  // pause behavior in _checkStreakIdleReset that refreshes _lastTimingEventMs each frame).
  const sinceMs = charging01 ? 0 : (performance.now() - _lastTimingEventMs);
  _paintStreakPill(Math.max(0, 1 - sinceMs / STREAK_IDLE_MS));
}
// Paint the streak pill fill from a 0..1 "remaining" fraction. Color shifts from cyan (safe) →
// pink (about to expire) over the last ~40% so the urgency is felt before the streak breaks.
function _paintStreakPill(remain) {
  _streakBadgePillFill.style.width = `${remain * 100}%`;
  if (remain < 0.4) {
    const t = remain / 0.4;  // 1 at the 40% mark, 0 at zero
    // Lerp between pink (R=255,G=59,B=107) at low and cyan (R=0,G=229,B=255) at high.
    const r = Math.round(255 * (1 - t) +   0 * t);
    const g = Math.round( 59 * (1 - t) + 229 * t);
    const b = Math.round(107 * (1 - t) + 255 * t);
    _streakBadgePillFill.style.background = `rgb(${r},${g},${b})`;
  } else {
    _streakBadgePillFill.style.background = '#00E5FF';
  }
}
// Streak card — top-left, own home, separate from the coin HUD on the right. Two stacked
// lines: count + "STREAK" label on top, multiplier + "COINS" label below. Multiplier line
// hides during the 3-4 build-up (no bonus active yet) so the player isn't shown a misleading
// "1.0×". Whole card hides when streak < 3.
const _streakBadge = document.createElement('div');
_streakBadge.className = 'ss-streak-badge';  // marker class so the recording panel's HUD-hide can target it
_streakBadge.style.cssText = 'position:fixed;top:14px;left:14px;pointer-events:none;user-select:none;'
  + 'z-index:998;background:var(--col-bg-panel);'
  + 'border:var(--border-chunky);box-shadow:var(--shadow-hard);'
  + 'padding:10px 14px;display:flex;flex-direction:column;align-items:flex-start;gap:4px;'
  + 'opacity:0;transition:opacity 200ms ease-out;'
  + 'font-family:var(--font-display);-webkit-text-stroke:3px var(--col-ink);paint-order:stroke;'
  + 'letter-spacing:1px;'
  // Fixed width so the pill below doesn't oscillate as the text changes length (count
  // crosses 1 → 2 → 3 digit boundaries, multiplier line appears/disappears at streak 5).
  // Sized to the WORST-CASE content "2.0× COIN DROPS" at the current font sizes plus
  // padding, with margin for future tier text.
  + 'width:360px;box-sizing:border-box;';
const _streakBadgeCount = document.createElement('div');
_streakBadgeCount.style.cssText = 'font-size:20px;color:#FF3B6B;transition:color 180ms linear;white-space:nowrap;';
const _streakBadgeMult = document.createElement('div');
// white-space:nowrap pins the multiplier to a single line so "1.25× COIN DROPS" doesn't
// wrap mid-text and push the pill below it out of place. height reserves the slot during
// build-up (3-4) so the card doesn't grow when the multiplier first appears.
// font-size 18px to keep "1.25× COIN DROPS" inside the 300px card without overshoot.
_streakBadgeMult.style.cssText = 'font-size:18px;color:#FF8C3A;transition:color 180ms linear;height:20px;white-space:nowrap;margin-top:6px;';
// Idle-reset countdown pill — drains over STREAK_IDLE_MS (3s) from full → empty so the
// player has a visible "your streak is about to die" cue. Full immediately after each
// scored hit; pauses while a fist is charging (matches _checkStreakIdleReset's pause).
// Width 100% spans the badge content; bar fills from left and the right side drains away.
const _streakBadgePill = document.createElement('div');
_streakBadgePill.style.cssText = 'width:100%;height:6px;background:var(--col-ink);'
  + 'border:1px solid var(--col-ink);position:relative;overflow:hidden;margin-top:2px;';
const _streakBadgePillFill = document.createElement('div');
_streakBadgePillFill.style.cssText = 'height:100%;background:#00E5FF;width:100%;'
  + 'transform-origin:left center;transition:background 200ms linear;';
_streakBadgePill.appendChild(_streakBadgePillFill);
_streakBadge.appendChild(_streakBadgeCount);
_streakBadge.appendChild(_streakBadgeMult);
_streakBadge.appendChild(_streakBadgePill);
document.body.appendChild(_streakBadge);

function _updateStreakHud() {
  const bonus = _streakBonus(_streak);
  if (_streak >= 3) {
    _streakBadgeCount.textContent = `×${_streak} STREAK`;
    // Continuous heat escalation — the count warms white → amber → hot-orange as the streak
    // climbs (heatT 0 at streak 5, 1 at streak 50), mirroring the fist heat ramp so a building
    // streak feels like it heats up rather than staying flat until the storm-max rainbow. Uses
    // the count's own text-shadow for the glow (the beat anchor owns the badge box-shadow).
    const heatT = Math.max(0, Math.min(1, (_streak - 5) / 45));
    const hg = Math.round(255 - heatT * (255 - 106));
    const hb = Math.round(255 - heatT * (255 - 40));
    _streakBadgeCount.style.color = `rgb(255,${hg},${hb})`;
    _streakBadgeCount.style.textShadow = heatT > 0.02
      ? `0 0 ${(heatT * 16).toFixed(1)}px rgba(255,120,40,${(0.85 * heatT).toFixed(2)})`
      : 'none';
    _streakBadge.style.opacity = '1';
    if (bonus > 1.0) {
      _streakBadgeMult.textContent = `${bonus}× COIN DROPS`;
      // Rainbow on multiplier + pill ONLY at storm-max (streak >= 50). Below that the
      // multiplier reads in its static orange and the pill stays cyan.
      if (_stormMaxActive) {
        _startStreakRainbow();
        _startStreakPillRainbow();
      } else {
        _stopStreakRainbow();
        _stopStreakPillRainbow();
      }
    } else {
      _streakBadgeMult.textContent = '';
      _stopStreakRainbow();
      _stopStreakPillRainbow();
    }
  } else {
    _streakBadge.style.opacity = '0';
    _stopStreakRainbow();
    _stopStreakPillRainbow();
  }
}

// "STREAK 5x!" celebration label when crossing a streak tier (5 / 10 / 20).
// Sits ABOVE the reticule (upper third) so it stays visually separate from the timing-tier
// labels (which live in the lower third). Bounces in, holds, bounces out, with the text
// hue cycling through the rainbow across the lifetime.
function _popStreakTierLabel(tier) {
  if (!tier) return;
  const wrapper = document.createElement('div');
  wrapper.style.position = 'fixed';
  wrapper.style.top = `${Math.round(window.innerHeight * 0.22)}px`;
  wrapper.style.left = '0';
  wrapper.style.right = '0';
  wrapper.style.display = 'flex';
  wrapper.style.justifyContent = 'center';
  wrapper.style.pointerEvents = 'none';
  wrapper.style.zIndex = '999998';

  const el = document.createElement('div');
  el.textContent = `STREAK ${tier}×!`;
  el.style.fontFamily = '"Press Start 2P", ui-monospace, Menlo, Consolas, monospace';
  el.style.fontSize = (tier >= 20 ? 40 : tier >= 10 ? 32 : 26) + 'px';
  el.style.letterSpacing = '1px';
  el.style.color = 'hsl(0, 100%, 65%)';
  // paint-order: stroke keeps the rainbow letter body fully visible; the stroke acts as a
  // clean navy outline. Without it the 6px stroke would swallow Press Start 2P's pixel lines.
  el.style.paintOrder = 'stroke';
  el.style.webkitTextStroke = '6px #0a0820';
  el.style.textStroke = '6px #0a0820';
  wrapper.appendChild(el);
  document.body.appendChild(wrapper);

  const DURATION = 2400;

  // Wrapper: slower bounce-in scale + downward settle, long hold, bounce-out + fade. The entry
  // is paced so the text is readable as it lands; hold gives time to register the streak tier.
  wrapper.animate(
    [
      { transform: 'translateY(-20px) scale(0.5)', opacity: 0,   offset: 0.00 },
      { transform: 'translateY(6px) scale(1.15)',  opacity: 1,   offset: 0.30 },  // overshoot
      { transform: 'translateY(0) scale(1.0)',     opacity: 1,   offset: 0.42 },  // settle
      { transform: 'translateY(0) scale(1.0)',     opacity: 1,   offset: 0.80 },  // hold (~900ms)
      { transform: 'translateY(-40px) scale(0.7)', opacity: 0,   offset: 1.00 },  // exit + fade
    ],
    { duration: DURATION, easing: 'ease-out', fill: 'forwards' }
  );

  // Text color: full rainbow sweep through the hue wheel during the visible window.
  const rainbowFrames = [];
  for (let i = 0; i <= 12; i++) {
    rainbowFrames.push({ color: `hsl(${Math.round(i * 360 / 12)}, 100%, 65%)`, offset: i / 12 });
  }
  el.animate(rainbowFrames, { duration: DURATION, easing: 'linear', fill: 'forwards' });

  setTimeout(() => wrapper.remove(), DURATION + 50);
}

// Goal-star anticipation pulse — once the bar fill crosses 70%, the pink star scales
// to the beat using the shared _beatTargetPulse cos² curve (same one the block highlight
// rides). Settles back to scale 1 when the player clears the goal (bar goes rainbow)
// or drops below the trigger threshold. Runs every frame from the main loop.
function _updateGoalStarPulse() {
  if (_coinBarTickLabel.style.display === 'none') return;
  const passThreshold = (_levelStarThresholds && _levelStarThresholds.one) || Infinity;
  const cleared = _score >= passThreshold;
  const fillFrac = passThreshold !== Infinity ? (_score / passThreshold) : 0;
  // Pulse when approaching the goal (>70% fill) but not yet cleared. Anticipation only.
  let scale = 1;
  if (!cleared && fillFrac > 0.7) {
    const pulse = _beatTargetPulse();  // 0..1 cos² beat curve
    scale = 1 + pulse * 0.35;
  }
  _coinBarTickLabel.style.transform = `translateX(-50%) scale(${scale.toFixed(3)})`;
}

function _bumpCoinHud() {
  _coinScoreSpan.style.transform = 'scale(1.25)';
  setTimeout(() => { _coinScoreSpan.style.transform = 'scale(1)'; }, 60);
  // Coin icon bounce — quick scale-up with a small upward hop, then settle back. Web
  // Animations API restarts cleanly on rapid coin pickups, so chained scoops stay lively.
  _coinIcon.animate([
    { transform: 'scale(1) translateY(0)' },
    { transform: 'scale(1.35) translateY(-6px)', offset: 0.45 },
    { transform: 'scale(1) translateY(0)' },
  ], { duration: 260, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' });
  const pct = Math.min(100, (_score / Math.max(1, _hudTarget)) * 100);
  _coinBarFill.style.width = pct + '%';
  // Rainbow shimmer kicks in at the 1★ pass threshold ("you've cleared, now climbing for
  // higher stars"). Sandbox / no-thresholds: never rainbow (passThreshold = Infinity).
  const passThreshold = (_levelStarThresholds && _levelStarThresholds.one) || Infinity;
  const cleared = _score >= passThreshold;
  _coinBarFill.classList.toggle('ss-hud__bar-fill--cleared', cleared);
  // Hide the "/ N" denominator once cleared — "112 / 42" reads as confusing once you're
  // past the goal. The rainbow bar + bare score number tells the whole story post-clear.
  if (passThreshold !== Infinity) {
    _coinTargetSpan.style.display = cleared ? 'none' : 'inline-block';
  }
}

// Pop a brief timing label above the reticle to signal hit quality. Plain DOM, fades up
// + out over ~700ms.
//
// Label per tier:
//   • PERFECT → "PERFECT"  (you nailed it; no correction info)
//   • GOOD    → "GOOD"     (you scored; no correction info)
//   • MISS    → "TOO EARLY" or "TOO LATE" depending on which side of the beat you landed,
//               since the miss itself says "didn't score" and the direction is the
//               actionable correction. Falls back to "BAD" if direction is unknown
//               (e.g. no music playing).
function _popTimingLabel(tier, direction = null) {
  // Color triad: gold (perfect), cyan (good), hot pink-red (miss).
  const styles = {
    perfect: { text: 'PERFECT', size: 72, color: '#FFE600' },  // big celebration — success should read as loud as a miss
    good:    { text: 'GOOD',    size: 40, color: '#00E5FF' },
    back:    { text: 'BACK ON BEAT!', size: 46, color: '#FFFFFF' },
    miss:    { text: 'BAD',     size: 86, color: '#FF3B6B' },  // overridden below if direction is known
  };
  const s = styles[tier];
  if (!s) return;
  const isMiss = tier === 'miss';
  const isBack = tier === 'back';
  const text = (tier === 'miss' && direction)
    ? (direction === 'early' ? 'TOO EARLY' : 'TOO LATE')
    : s.text;

  const wrapper = document.createElement('div');
  wrapper.style.position = 'fixed';
  // Good/perfect stay low and celebratory. Miss correction goes close to the reticle so
  // players cannot miss the cause: their timing, not their aim or the block.
  if (isMiss) {
    wrapper.style.top = '43%';
    wrapper.style.transform = 'translateY(-50%)';
  } else {
    wrapper.style.bottom = `${Math.round(window.innerHeight * 0.22)}px`;
  }
  wrapper.style.left = '0';
  wrapper.style.right = '0';
  wrapper.style.display = 'flex';
  wrapper.style.justifyContent = 'center';
  wrapper.style.pointerEvents = 'none';
  wrapper.style.zIndex = '999999';

  const el = document.createElement('div');
  el.textContent = text;
  el.style.color = s.color;
  el.style.fontFamily = '"Press Start 2P", ui-monospace, Menlo, Consolas, monospace';
  el.style.fontSize = isMiss
    ? `clamp(42px, 8vw, ${s.size}px)`
    : s.size + 'px';
  el.style.letterSpacing = '1px';
  el.style.textAlign = 'center';
  el.style.lineHeight = '1.05';
  el.style.maxWidth = '96vw';
  // paint-order: stroke = stroke renders BEHIND the fill, so the inside half of the stroke
  // is covered by the color and only the outside half shows as a clean outline.
  el.style.paintOrder = 'stroke';
  el.style.webkitTextStroke = isMiss ? '9px #0a0820' : '6px #0a0820';
  el.style.textStroke = isMiss ? '9px #0a0820' : '6px #0a0820';
  if (isMiss) {
    el.style.filter = 'drop-shadow(6px 6px 0 rgba(10,8,32,0.75))';
  }
  wrapper.appendChild(el);

  document.body.appendChild(wrapper);

  // Web Animations API — bulletproof against the CSS-transition race where the browser collapses
  // initial + final state into one frame and skips the visible portion entirely.
  const DURATION = isMiss ? 920 : 700;
  const frames = isMiss
    ? [
        { transform: 'translateY(-50%) scale(0.75)', opacity: 0 },
        { transform: 'translateY(-50%) scale(1.12)', opacity: 1, offset: 0.16 },
        { transform: 'translateY(-50%) scale(1.00)', opacity: 1, offset: 0.62 },
        { transform: 'translateY(-62%) scale(0.95)', opacity: 0 },
      ]
    : [
        { transform: 'translateY(0)',     opacity: 1 },
        { transform: 'translateY(-30px)', opacity: 0 },
      ];
  wrapper.animate(frames, { duration: DURATION, easing: 'ease-out', fill: 'forwards' });
  setTimeout(() => wrapper.remove(), DURATION + 50);
}

// Charge reticule — small ring + center dot at screen center, in beam color (0xff5a3a).
// Visible only while a fist is past SUCK_GRACE (chip-storm wind-up). Pure CSS, no canvas.
const _reticle = document.createElement('div');
_reticle.style.cssText = 'position:fixed;top:50%;left:50%;width:14px;height:14px;margin:-7px 0 0 -7px;'
  + 'border:1px solid #ff5a3a;border-radius:50%;box-shadow:0 0 4px rgba(255,90,58,0.6);'
  + 'pointer-events:none;opacity:0;transition:opacity 80ms linear;z-index:999;';
const _reticleDot = document.createElement('div');
_reticleDot.style.cssText = 'position:absolute;top:50%;left:50%;width:2px;height:2px;'
  + 'margin:-1px 0 0 -1px;background:#ff5a3a;border-radius:50%;';
_reticle.appendChild(_reticleDot);
document.body.appendChild(_reticle);

// Charge-meter ring — SVG circle that fills clockwise as the held charge accumulates.
// Encircles the beat ring (~56px max). Visible only while a fist is actively charging.
const _CHARGE_RING_R = 36;
const _CHARGE_RING_CIRC = 2 * Math.PI * _CHARGE_RING_R;  // ~226.2
const _chargeRingSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
_chargeRingSvg.setAttribute('width', '100');
_chargeRingSvg.setAttribute('height', '100');
_chargeRingSvg.setAttribute('viewBox', '0 0 100 100');
_chargeRingSvg.style.cssText = 'position:fixed;top:50%;left:50%;margin:-50px 0 0 -50px;'
  + 'pointer-events:none;opacity:0;transition:opacity 120ms linear;z-index:998;'
  + 'transform:rotate(-90deg);';  // rotate so fill starts at 12 o'clock
const _chargeRingCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
_chargeRingCircle.setAttribute('cx', '50');
_chargeRingCircle.setAttribute('cy', '50');
_chargeRingCircle.setAttribute('r', String(_CHARGE_RING_R));
_chargeRingCircle.setAttribute('fill', 'none');
_chargeRingCircle.setAttribute('stroke', '#ffd66b');
_chargeRingCircle.setAttribute('stroke-width', '2.5');
_chargeRingCircle.setAttribute('stroke-linecap', 'round');
_chargeRingCircle.setAttribute('stroke-dasharray', String(_CHARGE_RING_CIRC));
_chargeRingCircle.setAttribute('stroke-dashoffset', String(_CHARGE_RING_CIRC));
_chargeRingCircle.style.filter = 'drop-shadow(0 0 4px rgba(255,214,107,0.7))';
_chargeRingSvg.appendChild(_chargeRingCircle);
document.body.appendChild(_chargeRingSvg);
// Charge progress + "release now" cues are handled by coin + fist color warming now.
// The ring's only remaining job is the SPRAY-mode targeting indicator — appears when
// there's no target locked but a charge is in progress, paired with the small red reticle
// to signal "you're about to fire camera-forward." No fill animation — fully drawn gold
// circle, just opacity-gated on the spray-charge condition.
function updateChargeRing() {
  const charging0 = charging[0] && chargeAmount[0] > SUCK_GRACE;
  const charging1 = charging[1] && chargeAmount[1] > SUCK_GRACE;
  const target = lockedTarget || nearTarget;
  if (!target && (charging0 || charging1)) {
    _chargeRingCircle.setAttribute('stroke-dashoffset', '0');  // fully drawn gold ring
    _chargeRingSvg.style.opacity = '1';
  } else {
    _chargeRingSvg.style.opacity = '0';
  }
}

// Charge-mode vignette — soft warm-gold halo at the screen edges, matching the coin colour
// the player is gathering. Screen-blend mode reads as edge-glow (light gathering), not edge-
// darkening (alarm/anger). Center stays clear so aim and reticle aren't compromised.
const _chargeVignette = document.createElement('div');
_chargeVignette.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:996;'
  + 'background:radial-gradient(ellipse at center,'
    + 'transparent 40%,rgba(255,196,74,0.18) 75%,rgba(255,180,40,0.55) 100%);'
  + 'opacity:0;mix-blend-mode:screen;';
document.body.appendChild(_chargeVignette);
const VIGNETTE_RATE = 5.0;  // opacity units per second (~200ms full ramp)
let _vignetteOpacity = 0;
function updateChargeVignette(dt) {
  const wantOn = (charging[0] && chargeAmount[0] > SUCK_GRACE)
              || (charging[1] && chargeAmount[1] > SUCK_GRACE);
  const target = wantOn ? 1 : 0;
  if (_vignetteOpacity < target) {
    _vignetteOpacity = Math.min(target, _vignetteOpacity + VIGNETTE_RATE * dt);
  } else if (_vignetteOpacity > target) {
    _vignetteOpacity = Math.max(target, _vignetteOpacity - VIGNETTE_RATE * dt);
  }
  _chargeVignette.style.opacity = String(_vignetteOpacity);
}

// Beat-cue reticule — thin ring at screen center that contracts each quarter-note beat.
// Largest at the start of a beat, tightest right at the next beat. Hit when small = on rhythm.
// Updated per frame in updateBeatCue() below using the music's beat phase.
const _beatRing = document.createElement('div');
_beatRing.style.cssText = 'position:fixed;top:50%;left:50%;'
  + 'border:1px solid #cfd6dc;border-radius:50%;'
  + 'pointer-events:none;opacity:0;z-index:998;'
  + 'box-sizing:border-box;';
document.body.appendChild(_beatRing);
const BEAT_RING_MAX = 56;  // px diameter at start of beat
const BEAT_RING_MIN = 14;  // px diameter at the beat moment
const BEAT_PULSE_DECAY_MS = 180;  // exponential half-life for the on-beat pulse fade-out

// Scoring grid: every 8th note (quarter beats AND offbeats). 8 hit-targets per bar at 120 BPM.
// Hits in between (16th-note positions) fall into the miss gap.
// `let` so applySong() can update it when BPM changes — at 120 BPM this is 0.25s
// (one eighth note); at other BPMs it's 60/bpm/2 seconds.
let SCORE_GRID = 0.25;  // eighth-note duration at the active song's BPM
// Phase windows expressed as fraction of an eighth (250ms). Tight perfect, generous good,
// but good must stay under 0.5 so 16ths (50% of an eighth from nearest 8th) score MISS.
const BEAT_PERFECT_BASE = 0.16;  // ±40ms — perfect (2×) at 100% leniency
const BEAT_GOOD_BASE = 0.36;     // ±90ms — good (1×) at 100% leniency. 16ths at 0.5 → outside → miss
let _timingLeniency = 0.78;      // ±70ms good window at 78% — settled value after the
                                  // 2026-05-16 playtest. Original 0.5 (±45ms) was too tight;
                                  // 1.0 (±90ms) was too loose. Slider removed at the same
                                  // time to lock the playtest baseline in place.

// User-measured audio offset in seconds. Positive = player's audio is delayed (they
// hear the beat late, so the game subtracts their offset from elapsed → scoring +
// visual peaks shift to match what they hear). Read fresh each call so a recalibration
// takes effect immediately. Empty / unset = 0 = no compensation.
const _AUDIO_OFFSET_KEY = 'smash-audio-offset-sec';
function _loadAudioOffsetSec() {
  try {
    const v = localStorage.getItem(_AUDIO_OFFSET_KEY);
    if (v == null) return 0;
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  } catch { return 0; }
}
function _saveAudioOffsetSec(sec) {
  try { localStorage.setItem(_AUDIO_OFFSET_KEY, String(sec)); } catch {}
}

// Elapsed-since-music-start helper used by scoring + visual cues. Subtracts the
// user-calibrated audio offset (set via the pause menu's CALIBRATE AUDIO flow). For an
// uncalibrated player the offset is 0 and behaviour matches the original raw-elapsed
// code. For a calibrated Bluetooth player who hears the beat ~200ms late, the offset
// is ~0.2 and we shift scoring + visuals to match what they hear.
//
// Auto-compensation via audioCtx.outputLatency was tried 2026-05-24 and rejected —
// Chrome on Windows reports the full pipeline (API + OS + driver), which inflates the
// value far above the actual perceptual delay and desynced visuals on wired audio.
// User calibration measures what the player ACTUALLY hears, regardless of browser.
function _beatElapsed() {
  if (!_musicStarted || !audioCtx) return null;
  const elapsed = audioCtx.currentTime - _musicLoopStartTime - _loadAudioOffsetSec();
  return elapsed >= 0 ? elapsed : null;
}

// Returns 0/1/2 timing multiplier based on how close `now` is to the nearest 8th-note beat.
// Returns 1 (no penalty/no bonus) if music isn't playing — keeps debug/no-music play sensible.
function _currentBeatMultiplier(extraLeniency = 1) {
  const elapsed = _beatElapsed();
  if (elapsed === null) return 1;
  const phase = (elapsed % SCORE_GRID) / SCORE_GRID;
  // Distance from nearest 8th: 0 at exact-8th (phase 0 OR phase 1), 0.5 halfway between two 8ths.
  const dist = Math.min(phase, 1 - phase);
  const lenience = _timingLeniency * extraLeniency;
  if (dist <= BEAT_PERFECT_BASE * lenience) return 1.5;
  if (dist <= BEAT_GOOD_BASE * lenience) return 1;
  return 0;
}
function _currentBeatTier(extraLeniency = 1) {
  const m = _currentBeatMultiplier(extraLeniency);
  // PERFECT multiplier reduced from 2 → 1.5 (2026-05-13) to close the spacebar-mash exploit:
  // a phase-locked 8 Hz mash produced 50/50 PERFECT/MISS, which used to score 1.0× base
  // (above 0.8× 1★ threshold). At 1.5× it scores 0.75× base → fails. Tier comparison uses
  // >= so the threshold survives any future numeric tweaks.
  return m >= 1.5 ? 'perfect' : m >= 1 ? 'good' : 'miss';
}
// Returns 'early' or 'late' (or null if music isn't playing) based on which side of the
// nearest 8th-note beat the click landed. Used by the timing-label pop to show a small
// direction indicator on imperfect hits so players can self-correct without trial-and-
// error. phase < 0.5 = just past the previous 8th = LATE; phase >= 0.5 = approaching
// the next 8th = EARLY.
function _currentBeatDirection() {
  const elapsed = _beatElapsed();
  if (elapsed === null) return null;
  const phase = (elapsed % SCORE_GRID) / SCORE_GRID;
  return phase < 0.5 ? 'late' : 'early';
}
let _beatLastPhase = 0;
let _beatLastWrapMs = -Infinity;
// Reticule impact pulse — fired by tap punches landing on a block. Brief enlarge + extra glow
// on top of the normal beat-ring rendering so a successful punch feels acknowledged.
let _beatRingImpactStartMs = -Infinity;
const BEAT_IMPACT_MS = 220;
function triggerReticuleImpact() {
  _beatRingImpactStartMs = performance.now();
}
// Subtle tick blip on each beat when the player has a target highlighted. Sine 1500Hz with
// fast attack + 50ms decay; routed through _sfxBus so it follows the SFX volume slider.
// Volume ramps over the first 4 seconds of holding a target — softer than the previous
// fixed level when you first lock on, gradually rising to a "more helpful" level if you're
// still hovering on the target. The idea: if you're confidently aiming, the tick stays
// quiet and unobtrusive; if you're waiting/judging the timing, the tick rises to help.
const _TARGET_TICK_VOL_PEAK   = 0.245;                  // peak volume after full ramp — soft enough to sit alongside the music
const _TARGET_TICK_SILENT_MS  = 200;                    // no tick at all for first 0.2s of lock
const _TARGET_TICK_RAMP_MS    = 2000;                   // then ramp from 0 to peak over 2s
                                                         // (slow ramp so the peak arrives later — confident players who fire
                                                         // quickly hear almost nothing; only lingerers get the gradual assist)
function _playTargetTick(volume) {
  if (!audioCtx || !_sfxBus) return;
  const t = audioCtx.currentTime;
  const osc = audioCtx.createOscillator();
  const gain = audioCtx.createGain();
  osc.type = 'sine';
  osc.frequency.value = 1500;
  gain.gain.setValueAtTime(0.0, t);
  gain.gain.linearRampToValueAtTime(volume, t + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
  osc.connect(gain).connect(_sfxBus);
  osc.start(t);
  osc.stop(t + 0.06);
}
let _targetTickLockStartMs = -1;
let _targetTickLastTarget = null;
function _updateTargetTick() {
  if (!_targetTickOn) return;
  if (!_musicStarted || !audioCtx) return;
  if (!controls.isLocked) return;
  const target = lockedTarget || nearTarget;
  if (!target) {
    // Target lost — reset phase, the ramp timer, and the "last target" memo so the next lock
    // starts soft again.
    _lastTargetTickPhase = 0;
    _targetTickLockStartMs = -1;
    _targetTickLastTarget = null;
    return;
  }
  // Restart the ramp on every NEW target acquisition. Each lock is a fresh judgment moment —
  // confident aim sweeps stay silent, lingering on one target gets the gradual assist.
  if (target !== _targetTickLastTarget) {
    _targetTickLockStartMs = performance.now();
    _targetTickLastTarget = target;
  }
  const elapsed = _beatElapsed();
  if (elapsed === null) return;
  // 2-beat period — matches the highlight pulse cycle so the audio tick lines up with
  // the SMASH NOW! / face-square peak instead of firing on every quarter beat.
  const TICK_PERIOD = MUSIC_BEAT * 2;
  const phase = (elapsed % TICK_PERIOD) / TICK_PERIOD;
  // Wrap detection — phase jumps from ~1 back to ~0 means a fresh 2-beat cycle just started.
  if (phase < _lastTargetTickPhase) {
    const sinceLockMs = performance.now() - _targetTickLockStartMs;
    // Silent for the first second of lock — confident players who fire on beat never hear
    // the tick. After that, ramp from 0 → peak with a quadratic ease-in, so the rise stays
    // gentle early on and accelerates if the player keeps lingering past 2s.
    if (sinceLockMs >= _TARGET_TICK_SILENT_MS) {
      const rampT = Math.min(1, (sinceLockMs - _TARGET_TICK_SILENT_MS) / _TARGET_TICK_RAMP_MS);
      const easedT = rampT * rampT;
      const volume = _TARGET_TICK_VOL_PEAK * easedT;
      if (volume > 0.0001) _playTargetTick(volume);
    }
  }
  _lastTargetTickPhase = phase;
}
// HUD beat anchors — drives two subtle, always-on rhythm cues whenever the in-game HUD is
// up: (1) the floating bottom-left speaker scales/brightens on the downbeat; (2) the
// yellow coin panel gets a faint cyan glow that pulses on the same cadence. Both decay
// exponentially after the wrap so the off-beat moments stay quiet and the player gets a
// clean rhythm reference even when no block is targeted. Quarter-note period — matches
// the music's underlying pulse so beginners can read the spine of the rhythm without
// needing a target highlight.
let _hudBeatLastPhase = 0;
let _hudBeatLastWrapMs = -Infinity;
function _updateHudBeatAnchors() {
  if (!_musicStarted || !audioCtx) return;
  // Clear screen takes over after a level ends — the in-game HUD is hidden, but the lit
  // stars on the clear panel should still pulse. Bail only if BOTH the in-game HUD AND
  // the clear-screen star pulse target are inactive.
  const hudShown = _coinHudGroup.style.display !== 'none';
  const clearShown = !!_activeClearStars && _clearScreen.style.display !== 'none';
  if (!hudShown && !clearShown) return;
  const elapsed = _beatElapsed();
  if (elapsed === null) return;
  const phase = (elapsed % MUSIC_BEAT) / MUSIC_BEAT;
  if (phase < _hudBeatLastPhase) _hudBeatLastWrapMs = performance.now();
  _hudBeatLastPhase = phase;
  const ageMs = performance.now() - _hudBeatLastWrapMs;
  // Same exponential decay shape as the centre beat ring so the two anchors thump in sync.
  const pulseT = Math.exp(-ageMs / BEAT_PULSE_DECAY_MS);
  // Floating speakers (bottom-left + bottom-right): scale 1.00 → 1.20 on the downbeat.
  // Brightness lifts alongside for a subtle "thump" without the cones changing colour.
  const spkScale = (1 + pulseT * 0.20).toFixed(3);
  const spkBright = (1 + pulseT * 0.40).toFixed(3);
  _hudSpeakerFloat.style.transform = `scale(${spkScale})`;
  _hudSpeakerFloat.style.filter = `brightness(${spkBright})`;
  _hudSpeakerFloatRight.style.transform = `scale(${spkScale})`;
  _hudSpeakerFloatRight.style.filter = `brightness(${spkBright})`;
  // Panels (coin + streak): tiny scale tick PLUS a strong cyan halo. Scale stays small
  // (1.025 peak) so it doesn't fight the score number's own scale bump; the cyan halo
  // (30px / alpha 0.75 peak) carries the visible pulse against the bright yellow.
  const panelScale = (1 + pulseT * 0.025).toFixed(4);
  const glowSize = (30 * pulseT).toFixed(1);
  const glowAlpha = (0.75 * pulseT).toFixed(3);
  const panelShadow = `6px 6px 0 #0a0820, 0 0 ${glowSize}px rgba(0, 229, 255, ${glowAlpha})`;
  _coinHudGroup.style.transform = `scale(${panelScale})`;
  _coinHudGroup.style.boxShadow = panelShadow;
  // Streak badge stays opacity-gated (hidden when streak < 3) — applying transform/shadow
  // here is a no-op while invisible and just lights up alongside the coin panel when the
  // badge fades in.
  _streakBadge.style.transform = `scale(${panelScale})`;
  _streakBadge.style.boxShadow = panelShadow;
  // Clear-screen stars — small scale tick on lit stars only, on the same beat cadence.
  // Tiny (1.0 → 1.08) so it reads as a celebration shimmer rather than a UI pulse.
  // Off-stars (the placeholder ☆) and not-yet-stamped stars skip the pulse so the
  // reveal sequence still controls when each star "wakes up."
  if (_activeClearStars) {
    const starScale = (1 + pulseT * 0.08).toFixed(4);
    for (const s of _activeClearStars) {
      if (s.classList.contains('ss-stars__on')) {
        s.style.transform = `scale(${starScale})`;
      }
    }
  }
}

// (_beatRingOn declared up near the other admin-toggle flags around line 1213.)
function updateBeatCue() {
  if (!_beatRingOn || !controls.isLocked) {
    // Hide the reticle ring on menu screens (pointer unlocked) — only relevant during gameplay.
    _beatRing.style.opacity = '0';
    return;
  }
  const elapsed = _beatElapsed();
  if (elapsed === null) { _beatRing.style.opacity = '0'; return; }
  // 0..1 progress through the current quarter-note beat. Visual pulses on every beat only —
  // offbeat 8ths still score (per SCORE_GRID), but the player reads them off the music's snare
  // and 8th-note feel rather than competing with another visual pulse.
  const phase = (elapsed % MUSIC_BEAT) / MUSIC_BEAT;
  // Detect beat wrap (phase jumps from ~1 back to ~0) → mark the moment.
  if (phase < _beatLastPhase) _beatLastWrapMs = performance.now();
  _beatLastPhase = phase;

  // Exponential decay tween: pulseT = 1.0 right at the wrap, decays smoothly back to 0 across
  // the beat. Each visual property lerps between "approach" and "pulse" styles by this curve.
  const ageMs = performance.now() - _beatLastWrapMs;
  const pulseT = Math.exp(-ageMs / BEAT_PULSE_DECAY_MS);

  // Approach values (smooth grey contraction).
  const approachSize = BEAT_RING_MAX - (BEAT_RING_MAX - BEAT_RING_MIN) * phase;
  const approachOpacity = 0.15 + 0.35 * phase;
  const approachR = 207, approachG = 214, approachB = 220;  // #cfd6dc

  // Pulse values (warm gold punch, slightly larger than min so the size delta is visible).
  const pulseSize = BEAT_RING_MIN + 4;
  const pulseOpacity = 1.0;
  const pulseR = 255, pulseG = 214, pulseB = 107;  // #ffd66b

  // Lerp every property by pulseT.
  const size = approachSize + (pulseSize - approachSize) * pulseT;
  const opacity = approachOpacity + (pulseOpacity - approachOpacity) * pulseT;
  const r = Math.round(approachR + (pulseR - approachR) * pulseT);
  const g = Math.round(approachG + (pulseG - approachG) * pulseT);
  const b = Math.round(approachB + (pulseB - approachB) * pulseT);
  const borderWidth = 1 + 2 * pulseT;       // 1px → 3px
  const glow = 8 * pulseT;                   // 0px → 8px shadow blur
  const glowAlpha = 0.8 * pulseT;

  // Streak intensification: at higher streaks, the on-beat pulse glows more saturated.
  const streakBoost = _streak >= 20 ? 1.0 : _streak >= 10 ? 0.6 : _streak >= 5 ? 0.3 : 0;
  let finalGlow = glow + 12 * streakBoost * pulseT;
  let finalBorderWidth = borderWidth + 1.5 * streakBoost * pulseT;
  let finalGlowAlpha = Math.min(1, glowAlpha + 0.4 * streakBoost * pulseT);
  let finalSize = size;

  // Impact pulse — when a tap punch lands, briefly enlarge + brighten the ring on top of
  // whatever beat/streak state it's in. Eases out across BEAT_IMPACT_MS.
  const impactAge = performance.now() - _beatRingImpactStartMs;
  if (impactAge < BEAT_IMPACT_MS) {
    const impactT = 1 - impactAge / BEAT_IMPACT_MS;  // 1 at fire, 0 at end
    finalSize += 18 * impactT;
    finalGlow += 14 * impactT;
    finalGlowAlpha = Math.min(1, finalGlowAlpha + 0.6 * impactT);
    finalBorderWidth += 1.5 * impactT;
  }

  _beatRing.style.width = `${finalSize}px`;
  _beatRing.style.height = `${finalSize}px`;
  _beatRing.style.marginLeft = `${-finalSize / 2}px`;
  _beatRing.style.marginTop = `${-finalSize / 2}px`;
  _beatRing.style.borderWidth = `${finalBorderWidth}px`;
  _beatRing.style.borderColor = `rgb(${r},${g},${b})`;
  _beatRing.style.boxShadow = finalGlow > 0.1 ? `0 0 ${finalGlow}px rgba(255,214,107,${finalGlowAlpha})` : 'none';
  _beatRing.style.opacity = String(opacity);
}

const keys = { w: false, a: false, s: false, d: false, e: false };
document.addEventListener('keydown', (e) => {
  if (e.code === 'KeyW') keys.w = true;
  else if (e.code === 'KeyA') keys.a = true;
  else if (e.code === 'KeyS') keys.s = true;
  else if (e.code === 'KeyD') keys.d = true;
  else if (e.code === 'KeyE') keys.e = true;
  // Backtick admin-panel shortcut disabled 2026-05-16 to lock the playtest baseline so
  // the panel can't be accidentally opened mid-run. To restore, re-add a Backquote
  // handler gated on _IS_ADMIN_HOST that toggles _adminPanel.style.display.
});
document.addEventListener('keyup', (e) => {
  if (e.code === 'KeyW') keys.w = false;
  else if (e.code === 'KeyA') keys.a = false;
  else if (e.code === 'KeyS') keys.s = false;
  else if (e.code === 'KeyD') keys.d = false;
  else if (e.code === 'KeyE') keys.e = false;
});

// Keyboard punch (laptop / no-mouse path). Space = left fist, Right Alt = right fist:
// both thumbs flanking the spacebar, so the left hand keeps WASD and punching never touches
// the trackpad. Added because laptop trackpad buttons are muted by the OS while movement
// keys are held ("disable touchpad while typing"), so trackpad players literally cannot
// punch while moving. Mouse buttons are unchanged. We re-dispatch as synthetic mouse events
// so the whole smash pipeline (charging, slot gate, fist voices, glove behaviour, dual-
// charge, and the wider keyboard-leniency window keyed on !e.isTrusted) runs through one path.
//   • preventDefault while locked: stops Space scrolling the page and Alt grabbing the
//     browser menu. Gated on isLocked so typing Space in name/prompt fields is unaffected.
//   • e.repeat guard: a held key auto-repeats keydown; only the first starts the charge,
//     matching a held mouse button. keyup fires the release.
//   • keyup dispatches the release ungated so a charge always clears, even if lock dropped.
//   • Right Alt is AltGr on some international layouts (it can emit a phantom Ctrl); harmless
//     here since we only act on AltRight and nothing binds Ctrl.
const _PUNCH_KEY_BUTTON = { Space: 0, AltRight: 2 };  // → left fist (button 0), right fist (button 2)
document.addEventListener('keydown', (e) => {
  const button = _PUNCH_KEY_BUTTON[e.code];
  if (button === undefined || !controls.isLocked) return;
  e.preventDefault();
  if (e.repeat) return;
  document.dispatchEvent(new MouseEvent('mousedown', { button }));
});
document.addEventListener('keyup', (e) => {
  const button = _PUNCH_KEY_BUTTON[e.code];
  if (button === undefined) return;
  if (controls.isLocked) e.preventDefault();
  document.dispatchEvent(new MouseEvent('mouseup', { button }));
});

let punchInProgress = false;
let nextPunchAllowed = 0;
const raycaster = new THREE.Raycaster();
const screenCenter = new THREE.Vector2(0, 0);

// Mouse held → charge accumulates. Mouse released → fire punch with current charge level.
// Left mouse drives the left fist, right mouse drives the right fist.
// Per-fist timing multipliers captured at mousedown (rhythm-relevant moment for taps,
// click-down half of the charge timing for charges). Charge release adds the second timing
// in mouseup. Default to 1 (good) so non-music play still scores normally.
const _clickMultiplier = [1, 1];
// Tier strings ('perfect' / 'good' / 'miss') captured alongside multiplier so visuals can key
// off the *quality* of the timing, not just the score value. Used for chip glow + trail effects.
const _clickTier = ['good', 'good'];

// Captured at mousedown but applied lazily at mouseup once we know whether the press is
// resolving as a tap or a charge. This keeps streak/label from firing twice for a single
// charge gesture (was: press graded + release graded, double-credit), and stops "good/bad
// label appears while the player is still charging and hasn't smashed anything yet."
//   • Tap (release with charge ≤ SUCK_GRACE) → apply this captured press grading.
//   • Charge (release with charge > SUCK_GRACE) → discard this; release timing scores.
// Tradeoff: tap label is delayed until release. Capped by SUCK_GRACE (~300ms); for fast
// taps the lag is the press-to-release gap, which is the player's own click duration.
const _pressTier = ['good', 'good'];
const _pressTiming = [1, 1];
const _pressHasTarget = [false, false];
const _pressIsRapid = [false, false];
const _pressNowMs = [0, 0];
const _pressDirection = [null, null];  // 'early' | 'late' | null — for the timing label sub-text on imperfect hits
const _pressSongPhase = [0, 0];        // [L,R] beat-phase (0..1) at press — sent in 'pn' so a co-op peer can
                                       // reproduce the punch's exact off-beat timing (captured at press, not
                                       // release, because the local smash sound plays at press).

// Easy-mode state (_EASY_MODE_KEY / _easyMode / _setEasyMode) is hoisted up near the top-right
// bar (~line 6085) because the ASSIST toggle button reads it at module load — declaring it here
// would put it in the temporal dead zone for that button and kill module init.

document.addEventListener('mousedown', (e) => {
  if (e.button !== 0 && e.button !== 2) return;
  // Assist mode: LEFT button held = auto-punch stream (driven from the tick in _updateEasyMode, not
  // the deferred-press model — just flag it held and consume). RIGHT button = the coin charge: arm it
  // here (charging[1] + chargeStartTime), then updateCharge ramps it and updateSuckSystem vacuums
  // coins, exactly like the main game; the matching mouseup fires the volley. Synthetic auto-fires
  // (_autoFiringNow) fall through to the normal punch path below.
  if (_easyMode && !_autoFiringNow) {
    // NOTE: no e.isTrusted guard here — the keyboard punch handler re-dispatches synthetic
    // (untrusted) mouse events for Space / Right Alt, and those MUST work in assist too
    // (laptop-trackpad players can't click while moving). The auto-punch stream is already
    // excluded by the !_autoFiringNow guard above, so synthetic == keyboard input here.
    if (e.button === 0) {
      // Punch and charge are mutually exclusive — pressing punch cancels any in-progress charge so you
      // can't hold both to keep the streak alive risk-free (punch refreshes the grace, charge pauses it).
      // Clear BOTH fists since the assist charge arms both hands (see the button-2 branch).
      for (let fi = 0; fi < 2; fi++) { if (charging[fi]) { charging[fi] = false; chargeAmount[fi] = 0; } }
      _easyPunchHeld = true;
      return;
    }
    if (e.button === 2 && controls.isLocked && !isMissRecovering()) {
      // Charge press stops the auto-punch stream (mutual exclusion — see above). One right-button charge
      // arms BOTH hands (like holding L+R in normal mode), so both gloves vacuum + unleash together.
      _easyPunchHeld = false;
      const _ct = performance.now() / 1000;
      for (let fi = 0; fi < 2; fi++) {
        charging[fi] = true;
        chargeStartTime[fi] = _ct;
        chargeAmount[fi] = 0;
        _chargingSource[fi] = 'mouse';
      }
    }
    return;
  }
  if (!controls.isLocked) return;
  if (isMissRecovering()) return;
  const fist = e.button === 0 ? 0 : 1;
  // Cross-fist mash gap — captured against the previous press BEFORE the slot gate and
  // BEFORE updating _lastAnyPressMs, so slot-gated presses still feed the running interval.
  // Used later to flag rapid alternating mash (spacebar exploit) as miss-tier.
  const _crossMashGap = performance.now() - _lastAnyPressMs;
  _lastAnyPressMs = performance.now();
  if (charging[fist]) {
    // Keyboard hold-to-charge auto-repeats keydown, which the keyboard scaffolding turns
    // into repeated synthetic (untrusted) mousedowns while the key is held; those must
    // still be ignored so the charge keeps building. But for a REAL mouse button
    // (trusted), a fresh press while this fist is still 'charging' means the matching
    // mouseup was never delivered (coalesced/dropped button event, common on laptop
    // trackpad buttons under pointer lock). Without recovery, charging[fist] stays stuck
    // true and EVERY following press on this button is swallowed by this guard until a
    // stray mouseup clears it, which reads to the player as "my taps aren't registering."
    // Recover by abandoning the orphaned charge (mirroring _cancelAllCharges for this
    // fist) and falling through so this press registers normally.
    if (!e.isTrusted) return;
    charging[fist] = false;
    chargeAmount[fist] = 0;
    pendingLaunchTime[fist] = -Infinity;
  }
  const now = performance.now() / 1000;
  // Cooldown gate. Two modes:
  //   • Shared (default): music-aligned slot tracking. Each press rounds to the nearest 8th
  //     of the song grid; one press per slot regardless of fist (single-fist tap, alternating,
  //     and mashing all cap at 8th rate). ±125ms tolerance per slot is the natural rounding
  //     boundary, wider than the timing-perfect window so motor variance can't block presses
  //     the scoring system would still credit. Dual-press exception: a second fire in the
  //     same slot is allowed if the OTHER fist was pressed within DUAL_PRESS_WINDOW (preserves
  //     the dual-charge radius bonus); _slotDualPressUsed caps it at one extra per slot.
  //   • Legacy: per-fist gate set at RELEASE (mouseup). Single-fist effective rate becomes
  //     hold_time + cooldown ≈ 1/beat; alternating bypasses by doubling fists.
  if (_sharedCooldownOn) {
    const slot = _getCurrentBeatSlot();
    // Dual-press = the other fist is STILL HELD (charging) AND was pressed within the dual
    // window. Both conditions matter: rapid keyboard alternation has the second condition
    // (presses within 100ms) but never the first (single key alternates, can't hold both),
    // so the exception correctly never fires for spacebar mash. Mouse dual-charge requires
    // both buttons to be held simultaneously, which satisfies both.
    const dualPressActive = charging[1 - fist] && (now - _lastPressTime[1 - fist]) < DUAL_PRESS_WINDOW;
    if (slot > _lastConsumedSlot) {
      _lastConsumedSlot = slot;
    } else {
      // Same or earlier slot — allowed only via dual-press exception, max one per slot.
      if (!dualPressActive) return;
      if (_slotDualPressUsed === slot) return;
      _slotDualPressUsed = slot;
    }
    _lastPressTime[fist] = now;
  } else {
    if (now < nextPunchAllowedByFist[fist]) return;
  }

  charging[fist] = true;
  chargeStartTime[fist] = now;
  chargeAmount[fist] = 0;
  // Capture press-time grading metadata but DON'T fire _onTimingEvent or _popTimingLabel
  // yet — mouseup decides what to do with this. The press might resolve as a tap (apply
  // this grading retroactively) or as a charge (discard this, grade the release only).
  // Anti-mash: presses within MIN_PUNCH_INTERVAL_MS of the last scored punch on this fist,
  // OR within CROSS_FIST_MASH_MS of ANY recent press (spacebar/alternating exploit), are
  // flagged so the eventual grading lands as a miss-tier no-op.
  const pressTarget = lockedTarget || nearTarget;
  const _hasTarget = fistCanTarget(fist, pressTarget);
  // Detect keyboard-driven press via e.isTrusted (synthetic events from keyboard handler are
  // false; real mouse clicks are true). Wider timing window applies for keyboard input only.
  // Source is stamped onto _chargingSource so the matching mouseup uses the same multiplier.
  const _kbInput = !e.isTrusted;
  _chargingSource[fist] = _kbInput ? 'keyboard' : 'mouse';
  const _leniencyMult = _kbInput ? _kbLeniencyMult : 1;
  // Easy-mode auto-fires are pinned to GOOD and skip the rapid/direction checks (they're synthetic and
  // always "on time" by construction). Manual play grades normally.
  const _tier = _autoFiringNow ? 'good' : _currentBeatTier(_leniencyMult);
  const _timing = _autoFiringNow ? 1.0 : _currentBeatMultiplier(_leniencyMult);  // pin auto to the good-tier floor
  const _nowMs = performance.now();
  const _isRapid = _autoFiringNow ? false
                 : ((_nowMs - _lastScoredPunchMs[fist]) < MIN_PUNCH_INTERVAL_MS
                    || _crossMashGap < CROSS_FIST_MASH_MS);
  _pressTier[fist] = _tier;
  _pressTiming[fist] = _timing;
  _pressHasTarget[fist] = _hasTarget;
  _pressIsRapid[fist] = _isRapid;
  _pressNowMs[fist] = _nowMs;
  _pressDirection[fist] = _autoFiringNow ? null : _currentBeatDirection();
  _pressSongPhase[fist] = _currentSongBeatPhase();   // where in the beat this press landed (for co-op peer audio)
  // Click multiplier is finalised at mouseup (tap branch) once we know whether to apply
  // the press grading. Safe defaults here so any path that reads them between press and
  // release sees a no-op.
  _clickMultiplier[fist] = 0;
  _clickTier[fist] = 'miss';
  // Click-start audio cue — always fires regardless of target so the player can audibly
  // gauge timing on the moment of press. Pitch follows the tier: perfect rings high, miss thuds low.
  playClickStart(_tier);

  // Play the smash sound NOW so click→sound is instant — players feel responsive and can rhythm
  // their clicks to the music. If they hold past SUCK_GRACE the mouseup handler plays an additional
  // release-time smash to represent the charged impact (intentional double-sound to keep musical feel).
  //
  // Per-fist VOICE: left = short tap, right = long ring/sustain. Decoupled from whether the
  // block actually dies on this hit — the player chose this voice by choosing the button, so
  // the sound is predictable and rhythm-locked instead of flipping on hidden break state.
  // Spacebar alternates fists, so keyboard players still get both voices in sequence.
  if (_hasTarget && pressTarget && pressTarget.userData && _tier !== 'miss') {
    const ht = (pressTarget.userData.hitsTaken || 0) + 1;
    const total = pressTarget.userData.hitsToBreak;
    const pitch = smashPitchMultiplier(ht, total);
    const useRingVoice = (fist === 1);  // right click = ring; left click = tap
    playSmashSound(pressTarget.userData.material, pitch, useRingVoice, pressTarget.position);
    // SHARED SPECTACLE (cross-mode consistency): at high streak, layer the per-material destruction
    // soundscape onto a KILLING normal-mode smash — the same streak-driven band assist mode uses, so the
    // sensory payoff grows with streak identically in both modes. Cosmetic only: no damage/AOE/coins
    // change here, normal balance is untouched. Fires on the beat (with the smash sound at press) so it
    // doesn't flam against the +180ms physical break. Assist handles its own soundscape in
    // _updateEasyMode (predicted on-beat), hence the !_easyMode gate to avoid double-voicing.
    if (!_easyMode && ht >= total && _streak >= EASY_SECTION_MIN_STREAK) {
      const _spec = _easyTapBlast();
      const _r = EASY_BLAST_AOE_RADIUS * _spec;
      const _tally = _easyDisturbanceTally(pressTarget.position, _r, pressTarget);
      const _pm = pressTarget.userData.material;
      if (_tally[_pm] !== undefined) _tally[_pm]++;  // + the block being smashed, so a lone kill voices too
      _easyDestructionSound(_tally, _streak, pressTarget.position);
    }
  }
});

document.addEventListener('mouseup', (e) => {
  if (e.button !== 0 && e.button !== 2) return;
  // Assist: LEFT release stops the auto-punch stream. RIGHT release fires the coin charge — same
  // suck/launch machinery as the main game, but pinned to GOOD tier (the player isn't timing it) so
  // an off-beat release never zeroes the payout. Break-loot scales with the streak built by the
  // auto-stream, which is what bridges brick/metal-heavy levels over the goal. Auto-fire taps
  // (_autoFiringNow) still run the normal path below.
  if (_easyMode && !_autoFiringNow) {
    if (e.button === 0) { _easyPunchHeld = false; return; }
    if (e.button === 2) {
      if (!charging[0] && !charging[1]) return;
      // Lock the aimed block ONCE so both hands' chips converge on it. Clear the lock shortly after launch
      // so it doesn't freeze the auto-stream's targeting (updateNear early-returns while lockedTarget set).
      lockedTarget = fistCanTarget(1, nearTarget) ? nearTarget : null;
      const _lockT = lockedTarget;
      if (_lockT) setTimeout(() => { if (lockedTarget === _lockT) lockedTarget = null; }, 250);
      const sb = _streakBonus(_streak);
      // One optional timing flourish: a release in the PERFECT window grades perfect (×2 break-loot via
      // tierMult, doubled self-bank, chip trails, and a PERFECT pop). Anything else stays GOOD — off-beat
      // is never penalised. Graded once and applied to both hands' volleys.
      const _onBeat = _currentBeatTier(1) === 'perfect';
      const _relTier = _onBeat ? 'perfect' : 'good';
      const _tMult = _onBeat ? 2 : 1;
      let anyCoins = false, maxCharge = 0;
      for (let fi = 0; fi < 2; fi++) {
        if (!charging[fi]) continue;
        charging[fi] = false;
        const charge = chargeAmount[fi];
        chargeAmount[fi] = 0;
        if (charge <= SUCK_GRACE) continue;
        if (charge > maxCharge) maxCharge = charge;
        for (const chip of debris) {
          if (chip.userData.isCurrency && chip.userData.assignedFist === fi) { anyCoins = true; break; }
        }
        pendingLaunchTime[fi] = performance.now() / 1000;  // updateSuckSystem's gather→launch path fires it
        pendingLaunchCharge[fi] = charge;
        pendingLaunchTimingMult[fi] = _tMult * sb;   // per-coin self-bank: doubled on a perfect release
        pendingLaunchReleaseTier[fi] = _relTier;
        pendingLaunchComboTier[fi] = _relTier;       // break-loot count ×2 on perfect (tierMult)
        pendingLaunchStreakBonus[fi] = sb;
        pendingLaunchSuppressCoins[fi] = false;
      }
      if (_onBeat) _popTimingLabel('perfect', null);
      if (anyCoins) {
        const releasePos = new THREE.Vector3();
        rightFist.getWorldPosition(releasePos);   // single release sound at the right hand (don't double playChargeRelease)
        playChargeRelease(maxCharge, releasePos);
      }
    }
    return;
  }
  const fist = e.button === 0 ? 0 : 1;
  if (!charging[fist]) return;
  if (isMissRecovering()) {
    charging[fist] = false;
    chargeAmount[fist] = 0;
    return;
  }

  charging[fist] = false;
  const charge = chargeAmount[fist];
  chargeAmount[fist] = 0;


  // Two attack modes:
  //   • Tap (charge ≤ SUCK_GRACE): melee punch via firePunch (existing path).
  //   • Charge (charge > SUCK_GRACE): chip-storm projectile fire — no melee smash. Damage comes
  //     from the launched chips colliding with blocks (see drainCollisionEvents).
  if (charge > SUCK_GRACE) {
    pendingLaunchTime[fist] = performance.now() / 1000;
    pendingLaunchCharge[fist] = charge;
    // Release-only timing model: the click was just rhythm pacing feedback, the RELEASE is
    // what scores. Click tier no longer combines into chip-storm coin yield, no more squared-
    // streak factor. Lock is REFRESHED at release — explicitly take nearTarget (which is null
    // if nothing is highlighted) so stale locks from earlier taps don't leak into spray play.
    // The highlight is the contract: if highlighted at release, that's your target; otherwise
    // chips spray camera-forward (existing damage path) but no streak/label fires.
    lockedTarget = fistCanTarget(fist, nearTarget) ? nearTarget : null;
    const _relLeniencyMult = _chargingSource[fist] === 'keyboard' ? _kbLeniencyMult : 1;
    const _relTier = _currentBeatTier(_relLeniencyMult);
    const _relTiming = _currentBeatMultiplier(_relLeniencyMult);
    const _relNowMs = performance.now();
    const _relIsRapid = (_relNowMs - _lastScoredPunchMs[fist]) < MIN_PUNCH_INTERVAL_MS;
    let hasCoins = false;
    for (const chip of debris) {
      if (chip.userData.isCurrency && chip.userData.assignedFist === fist) { hasCoins = true; break; }
    }
    // Charge is graded ONLY on release now (press grading was discarded because the
    // gesture became a charge). softMiss flag dropped: the release IS the scoring
    // event, so a missed release resets the streak just like a missed tap. Locked
    // target required — releasing into empty space ships chips with no rhythm credit.
    let _relSuppressCoins = false;
    if (lockedTarget && !_relIsRapid) {
      _onTimingEvent(_relTier);
      if (_relTier === 'miss') {
        _popTimingLabel(_relTier, _currentBeatDirection());
      } else {
        _relSuppressCoins = _consumeRhythmRecoveryDebt();
        _popTimingLabel(_relSuppressCoins ? 'back' : _relTier, _currentBeatDirection());
      }
      _lastScoredPunchMs[fist] = _relNowMs;
    }
    if (!lockedTarget && hasCoins && !_relIsRapid && _relTier === 'miss') {
      _onTimingEvent(_relTier);
      _popTimingLabel(_relTier, _currentBeatDirection());
      _lastScoredPunchMs[fist] = _relNowMs;
    }
    // Chip yield (coin tier, count) tracks release timing alone, regardless of lock. Locked
    // hits compound with streak; spray hits get colored pops but don't build streak. Rapid-
    // suppressed releases land as 'miss' tier — matches click-side symmetry under mash.
    pendingLaunchTimingMult[fist] = _relTiming * _streakBonus(_streak);
    pendingLaunchReleaseTier[fist] = _relIsRapid ? 'miss' : _relTier;
    pendingLaunchComboTier[fist] = _relIsRapid ? 'miss' : _relTier;
    pendingLaunchStreakBonus[fist] = _streakBonus(_streak);
    pendingLaunchSuppressCoins[fist] = _relSuppressCoins;
    // Legacy gate-set on release — only used when _sharedCooldownOn is OFF. With shared
    // cooldown on, the press-time gate is the sole gatekeeper.
    if (!_sharedCooldownOn) {
      nextPunchAllowedByFist[fist] = (performance.now() / 1000) + PUNCH_COOLDOWN;
    }
    // Coin-arpeggio crescendo from the fist position — but only if there are actually coins
    // assigned to this fist that will get launched. No coins = no payoff sound.
    if (hasCoins) {
      const fistObj = fist === 0 ? leftFist : rightFist;
      const releasePos = new THREE.Vector3();
      fistObj.getWorldPosition(releasePos);
      playChargeRelease(charge, releasePos);
    }
    return;
  }
  // Tap path — release happened before SUCK_GRACE, so this is a quick click-punch and we
  // now apply the captured press grading. Tier/timing/_hasTarget/rapid were all snapped
  // at mousedown; we just fire them off here so the GOOD/PERFECT/BAD label and streak
  // event reflect the press's rhythm without any double-grading from the charge path.
  let suppressCoins = false;
  if (_pressHasTarget[fist] && !_pressIsRapid[fist]) {
    _onTimingEvent(_pressTier[fist]);
    if (_pressTier[fist] === 'miss') {
      _popTimingLabel(_pressTier[fist], _pressDirection[fist]);
    } else {
      suppressCoins = _consumeRhythmRecoveryDebt();
      // Assist mode performs the timing for you, so the GOOD/PERFECT verdict is meaningless —
      // the in-world "+1" pop is the only feedback. Skip the big timing label entirely.
      if (!_easyMode) _popTimingLabel(suppressCoins ? 'back' : _pressTier[fist], _pressDirection[fist]);
    }
    _lastScoredPunchMs[fist] = _pressNowMs[fist];
  }
  if (_pressIsRapid[fist]) {
    _clickMultiplier[fist] = 0;
    _clickTier[fist] = 'miss';
  } else {
    _clickMultiplier[fist] = _pressTiming[fist] * _streakBonus(_streak);
    _clickTier[fist] = _pressHasTarget[fist] ? _pressTier[fist] : 'miss';
  }
  firePunch(charge, fist, _clickMultiplier[fist], suppressCoins);
});

// Easy-mode driver — once per kick/snare onset, dispatch a synthetic tap at whatever block the player
// is aimed at, with _autoFiringNow so it grades as a clean GOOD. The player just roams and points; the
// game performs the rhythm. Kicks and snares push from separate loops so the onset queue isn't time-
// sorted — drain any onset already past, order-independent; same-frame hits collapse to one smash.
function _updateEasyMode() {
  if (!_easyMode || !controls.isLocked || !_musicStarted || !audioCtx) return;
  const now = audioCtx.currentTime;
  // Freshness window: fire for any onset that came due recently, drop genuinely stale ones so a
  // backlog can't burst. Beat-scaled (~0.7 beat) and tempo-aware so a frame hitch during a big
  // smash — which can stall longer than a fixed 180ms — doesn't drop the punch and skip a beat.
  // Safe to widen: the drain collapses to at most ONE punch per frame regardless of how many
  // onsets are pending, so a wider window never machine-guns.
  const freshWindow = MUSIC_BEAT * 0.7;
  let onset = false;
  for (let i = _easyOnsetTimes.length - 1; i >= 0; i--) {
    const t = _easyOnsetTimes[i];
    if (t <= now) {
      _easyOnsetTimes.splice(i, 1);
      if (now - t < freshWindow) onset = true;
    }
  }
  if (!onset) return;
  // Auto-punches only fire while the player HOLDS the left button (the punch trigger) — the right
  // button is the charge. Holding to engage gives the player agency over when/where to unleash while
  // the game still nails the timing for them.
  if (!_easyPunchHeld) return;
  // Each onset is an "expected punch": a reachable block under the reticle fires a tap and the streak
  // ticks (via _onTimingEvent in the tap path); otherwise the onset passes and the bar-length grace
  // timer (see _checkStreakIdleReset) handles decay.
  _easyLastOnsetT = now;                            // mark this onset so the pill can pace from here to the next
  const target = lockedTarget || nearTarget;
  let fist, canHit;
  if (charging[1]) {
    // Right fist is mid-charge — auto-punch ONLY with the left, so a synthetic right-tap can't hijack
    // (and prematurely fire) the player's in-progress charge.
    fist = 0;
    canHit = !!target && fistCanTarget(0, target);
  } else {
    fist = _easyAltFist;
    canHit = !!target;
    if (canHit && !fistCanTarget(fist, target)) fist = 1 - fist;  // aimed block out of this glove's reach -> try the other
    if (canHit && !fistCanTarget(fist, target)) canHit = false;   // out of reach for both gloves
  }
  if (!canHit) {
    return;  // no reachable block this onset — the bar-length grace timer handles decay (see _checkStreakIdleReset)
  }
  // Landed a hit — refresh the bar-length grace window so the streak holds for another bar.
  _easyStreakGraceUntil = now + EASY_STREAK_GRACE_BARS * MUSIC_BAR;
  if (!charging[1]) _easyAltFist = 1 - fist;        // alternate next time for tap/ring variety
  const button = fist === 0 ? 0 : 2;
  _autoFiringNow = true;
  document.dispatchEvent(new MouseEvent('mousedown', { button }));
  document.dispatchEvent(new MouseEvent('mouseup', { button }));
  _autoFiringNow = false;
  // Destruction soundscape fires HERE, on the beat with the smash — not at the +180ms physical impact
  // (which produced an audible flam). We predict the blast's victims; the real break/coins still
  // resolve at impact for the visuals. Only when this tap will actually kill its block (so a blast
  // happens) and the streak is high enough for the blast to exist.
  const _willKill = (target.userData.hitsTaken + 1) >= target.userData.hitsToBreak;
  if (_willKill && _streak >= EASY_SECTION_MIN_STREAK) {
    // Soundscape rides the SPECTACLE curve (climbs from streak 5) and voices the materials DISTURBED
    // near the smash — decoupled from how many the AOE actually kills (which now holds off until deep
    // streak). Keeps the per-material band rich and growing with streak while the real damage stays low.
    const _spec = _easyTapBlast();
    const _r = EASY_BLAST_AOE_RADIUS * _spec;
    const _tally = _easyDisturbanceTally(target.position, _r, target);
    const _pm = target.userData.material;
    if (_tally[_pm] !== undefined) _tally[_pm]++;  // + the block we just tapped, so a direct kill voices too
    _easyDestructionSound(_tally, _streak, target.position);
  }
}

// Suppress the browser's right-click menu inside the canvas
document.addEventListener('contextmenu', (e) => e.preventDefault());

// Cancel any in-progress charges if window loses focus or pointer-lock is exited
function _cancelAllCharges() {
  charging[0] = charging[1] = false;
  chargeAmount[0] = chargeAmount[1] = 0;
  pendingLaunchTime[0] = pendingLaunchTime[1] = -Infinity;
  _easyPunchHeld = false;
}
window.addEventListener('blur', _cancelAllCharges);
controls.addEventListener('unlock', _cancelAllCharges);

function firePunch(charge, fistIndex, timingMult = 1, suppressCoins = false) {
  // Race start gate: no smashing before the GO countdown fires (or after the finish) — keeps the
  // block scramble fair so load-time / reflexes at the buzzer don't decide the race.
  if (_raceLocked) return;
  const now = performance.now() / 1000;
  // Legacy per-fist gate — only used when _sharedCooldownOn is OFF. Mousedown is the sole
  // gatekeeper in shared mode (and in legacy mode mousedown also gates, so the fire path
  // never gets here without a successful press anyway; this is just a safety net).
  if (!_sharedCooldownOn) {
    if (now < nextPunchAllowedByFist[fistIndex]) return;
    nextPunchAllowedByFist[fistIndex] = now + PUNCH_COOLDOWN;
  }

  // Charged punches land 20% faster — scale impact delay, lunge math, and animation playback together.
  const isCharged = charge >= CHARGE_THRESHOLD;
  const speedMult = isCharged ? CHARGED_SPEED_MULT : 1.0;
  const impactDelay = IMPACT_DELAY / speedMult;
  const myGlove = gloveForFist(fistIndex);
  const myGloveConfig = gloveConfig(fistIndex);
  const movesPlayer = myGloveConfig.movesPlayer;

  activeFist = fistIndex;
  fistAnimStart[fistIndex] = now;
  fistAnimSpeed[fistIndex] = speedMult;

  lockNearAsTarget();
  playWhoosh();

  // Capture per-punch state in closure so concurrent punches don't share globals at impact time.
  let myTarget = lockedTarget;
  const myCharge = charge;
  // Capture the click's timing tier here so the impact-time pop color matches the smash's
  // RHYTHM quality, not whatever the global tier is by the time the setTimeout fires.
  const myTier = _clickTier[fistIndex] || 'good';

  // (Smash sound was already played on mousedown for instant input→audio response.)

  // Dual-charge detection: charged punches get bigger radius when both fists fire near-simultaneously.
  let myRadius = CHARGED_RADIUS_SOLO;
  if (charge >= CHARGE_THRESHOLD) {
    const other = 1 - fistIndex;
    const otherFullCharge = charging[other] && chargeAmount[other] >= CHARGE_THRESHOLD;
    const otherFiredRecently = (now - lastChargedPunchTime[other]) <= DUAL_CHARGE_WINDOW;
    if (otherFullCharge || otherFiredRecently) {
      myRadius = CHARGED_RADIUS_DUAL;
    }
    lastChargedPunchTime[fistIndex] = now;
  }

  let lungeFwd;
  let lungeDist = myGloveConfig.lungeBase;
  if (myTarget) {
    // Aim at where the player is looking on the block (ray hit), not the centre. Direction and
    // distance both pull from that point so a beam-end aim lands at the beam-end.
    const camForward = new THREE.Vector3();
    camera.getWorldDirection(camForward);
    const aimPoint = rayHitBlock(myTarget, camera.position, camForward)
                  || closestPointOnBlock(myTarget, camera.position);
    if (aimPoint.distanceTo(camera.position) > myGloveConfig.lockRange) {
      myTarget = null;
      if (lockedTarget) clearLock();
    }
  }

  // Tell the peer we punched so their copy of us jabs and (on a hit) sings a smash note on their beat
  // grid. Coins are NOT sent here — they ride the charged chip-storm launch ('cs'). Never carries timing.
  if (_mp && _mp.send) {
    _mp.send({
      t: 'pn',
      h: fistIndex,
      hit: myTarget ? 1 : 0,
      mat: myTarget ? (myTarget.userData && myTarget.userData.material) : null,
      ch: isCharged ? 1 : 0,
      tid: myTarget ? (myTarget.userData && myTarget.userData.netId) : undefined,  // so the peer's spring stretches to it
      au: _autoFiringNow ? 1 : 0,             // 1 = rhythm-assist auto-fire (observer snaps to beat; feels good)
      ph: _pressSongPhase[fistIndex],         // 0 = manual: the beat-phase to reproduce so off-beat sounds off
    });
  }

  if (myTarget) {
    // Aim at where the player is looking on the block (ray hit), not the centre. Direction and
    // distance both pull from that point so a beam-end aim lands at the beam-end.
    const camForward = new THREE.Vector3();
    camera.getWorldDirection(camForward);
    const aimPoint = rayHitBlock(myTarget, camera.position, camForward)
                  || closestPointOnBlock(myTarget, camera.position);
    const toTarget = aimPoint.clone().sub(camera.position);
    if (toTarget.lengthSq() > 0.0001) toTarget.normalize();
    lungeFwd = toTarget;
    // Context-aware stand-off: only sufficiently DOWNWARD aim gets the tight LUNGE_REACH (so
    // look-down vault smashes carry through over the broken block via XZ momentum). Look-up
    // and horizontal aim both get LUNGE_REACH_HORIZONTAL — for tower-climb look-up especially,
    // a bigger stand-off keeps the player from ending up inside the tower's XZ footprint and
    // then phasing laterally when the apex boost rises into the next block. Lerp on the
    // look-down component (clamped 0..1) so the transition feels smooth.
    const lookDownness = Math.max(0, -toTarget.y);  // 0 = horizontal/up, 1 = straight down
    const effectiveReach = _contextStandoffOn
      ? (LUNGE_REACH_HORIZONTAL + (LUNGE_REACH - LUNGE_REACH_HORIZONTAL) * lookDownness)
      : LUNGE_REACH;
    lungeDist = Math.max(0, aimPoint.distanceTo(camera.position) - effectiveReach);
    if (myGlove === GLOVE_SPRING) {
      const visualReach = -(Math.max(0.65, aimPoint.distanceTo(camera.position) - LEFT_ARM_TARGET_VISUAL_GAP));
      if (fistIndex === 0) {
        leftArmReachHasTarget = true;
        leftArmReachExtraZ = Math.max(LEFT_ARM_REACH_MAX_Z, visualReach);
      } else {
        rightArmReachHasTarget = true;
        rightArmReachExtraZ = Math.max(LEFT_ARM_REACH_MAX_Z, visualReach);
      }
    }
    if (_aimAssist) startAutoFace(myTarget.position);  // precision mode aims by hand — no camera swing
  } else {
    // No lock — keep horizontal so air-punching upward doesn't yeet the player skyward.
    lungeFwd = new THREE.Vector3();
    camera.getWorldDirection(lungeFwd);
    lungeFwd.y = 0;
    if (lungeFwd.lengthSq() > 0.0001) lungeFwd.normalize();
    if (myGlove === GLOVE_SPRING) {
      if (fistIndex === 0) {
        leftArmReachHasTarget = false;
        leftArmReachExtraZ = LEFT_ARM_REACH_FALLBACK_Z;
      } else {
        rightArmReachHasTarget = false;
        rightArmReachExtraZ = LEFT_ARM_REACH_FALLBACK_Z;
      }
    }
  }
  if (movesPlayer) {
    applyLunge(lungeFwd, lungeDist, impactDelay);
  }

  // How upward was this lunge? 0 = horizontal/down, 1 = straight up. Captured here so the
  // apex boost (post-impact) can scale by aim verticality.
  const _upness = Math.max(0, lungeFwd.y);

  setTimeout(() => {
    doImpactOn(myTarget, myCharge, myRadius, timingMult, myTier, fistIndex, suppressCoins, movesPlayer);
    // Apex boost: if we lunged upward and the smash actually broke the block (block goes
    // dynamic + walkthrough on break, so Y motion is unobstructed), kick Y velocity so the
    // player arcs cleanly over where the block was. Math.max preserves any leftover lunge
    // momentum on long lunges where it already exceeds the boost.
    if (movesPlayer && _upness > 0 && myTarget && !blocks.includes(myTarget)) {
      // Airborne gate: boost only fires if the player isn't standing on the ground.
      // Match the hover-on-hit airborne threshold (0.3m clearance) for consistency.
      const _feetYAtApex = camera.position.y - PLAYER_FOOT_OFFSET;
      const _supportYAtApex = findHighestSupportBelow(camera.position.x, _feetYAtApex, camera.position.z);
      const _airborneAtApex = _feetYAtApex > _supportYAtApex + APEX_AIRBORNE_CLEARANCE;
      if (_airborneAtApex) {
        moveVelocity.y = Math.max(moveVelocity.y, _upness * APEX_BOOST_VEL);
        const _apexNow = performance.now() / 1000;
        apexCoastUntil = _apexNow + APEX_COAST_MS / 1000;
        apexFloatUntil = apexCoastUntil + APEX_FLOAT_MS / 1000;
      }
    }
    // Airborne hover-on-hit: when smashing at horizontal aim while in the air, suspend
    // gravity briefly so the player can re-aim instead of dropping. Doesn't fire on the
    // ground (airborne check) or on look-down vault smashes (aim-angle threshold rejects
    // strong lookdown). Math.max with the apex windows so an upward apex smash that's
    // also borderline-horizontal never gets its boost shortened.
    if (movesPlayer && _hoverHitOn && myTarget && Math.abs(lungeFwd.y) < HOVER_AIM_HORIZ_THRESHOLD) {
      const _feetY = camera.position.y - PLAYER_FOOT_OFFSET;
      const _supportY = findHighestSupportBelow(camera.position.x, _feetY, camera.position.z);
      // Airborne threshold raised 0.1m → 0.3m 2026-05-16. Old 10cm clearance was too easy
      // to pass — a tiny lunge bounce, cumulative lift from a prior hit, or ground-Y noise
      // could meet it, and beginners drifted skyward on hits they thought were grounded.
      // 30cm requires the player to be meaningfully airborne (mid-jump or genuinely falling).
      const _airborne = _feetY > _supportY + 0.3;
      if (_airborne) {
        const _now = performance.now() / 1000;
        apexCoastUntil = Math.max(apexCoastUntil, _now + HOVER_COAST_MS / 1000);
        apexFloatUntil = Math.max(apexFloatUntil, apexCoastUntil + HOVER_FLOAT_MS / 1000);
        // Lift fires on every fresh airborne smash (full HOVER_LIFT_VEL kick) but locks out
        // for HOVER_LIFT_LOCKOUT_MS so chained hits on stacked thin blocks can't accumulate
        // altitude. Coast/float still extends within the lockout — subsequent hits get the
        // hang-time but not the boost. After the gap elapses, the next smash earns a fresh
        // lift, which keeps the "I caught air on that hit" feel for genuine sky smashes.
        if ((_now - _lastHoverLiftTime) * 1000 >= HOVER_LIFT_LOCKOUT_MS) {
          moveVelocity.y = Math.max(moveVelocity.y, HOVER_LIFT_VEL);
          _lastHoverLiftTime = _now;
        }
      }
    }
    // Only clear the global lock if a newer punch hasn't already overridden it.
    if (lockedTarget === myTarget) clearLock();
  }, impactDelay * 1000);
}

function doImpactOn(target, charge, chargedRadius, timingMult = 1, tier = 'good', fistIndex = null, suppressCoins = false, movesPlayer = true) {
  if (!target || !blocks.includes(target)) return; // destroyed mid-charge
  const forward = new THREE.Vector3();
  camera.getWorldDirection(forward);

  raycaster.set(camera.position, forward);
  raycaster.far = TARGET_RANGE * 2;
  const rayHits = raycaster.intersectObject(target, false);
  const impactPos = rayHits.length > 0
    ? rayHits[0].point
    : closestPointOnBlock(target, camera.position);

  applyHit(target, impactPos, forward, charge, chargedRadius, timingMult, tier, fistIndex, suppressCoins, movesPlayer);
}

// Movement — velocity + acceleration + damping. Lunge is just a velocity impulse into this system.
// Terminal walk speed = MOVE_ACCEL / MOVE_DAMP. Damping rate sets how long momentum lingers.
const MOVE_ACCEL = 80;
const MOVE_DAMP = 5;            // 1/s — residual decays as e^(-DAMP*t); terminal walk = ACCEL/DAMP = 16... see below
const WALK_TERMINAL = 11;       // m/s cap on input-driven speed (lunge can briefly exceed)
const LUNGE_BASE_DIST = 0.6;
const LUNGE_REACH = 0.6;
// Context-aware stand-off: when the smash aim is closer to horizontal (walls / eye-level
// crates), the lunge stops further short so the player has visual room to observe and reaim
// — matches the hold-back habit experienced players develop. When aim is vertical (look-down
// vault smashes that ride the apex boost), we keep the tighter LUNGE_REACH so carry-through
// over the killed block still feels right. Lerps between the two by aim verticality.
const LUNGE_REACH_HORIZONTAL = 1.4;
// _contextStandoffOn / _impactRecoilOn / RECOIL_SPEED — declared up near the top of the file
// because the admin toggle that reads them runs at module-load BEFORE this point. TDZ trap.
const LUNGE_PEAK_CAP = 100; // covers the doubled lock range within the same IMPACT_DELAY window
const PLAYER_GRAVITY = -30;        // m/s² — pulls player down whenever they're above the ground
const PLAYER_FOOT_OFFSET = 1.65;   // camera eye level above the surface the player is standing on
const PLAYER_HEAD_CLEARANCE = 0.1; // gap between camera (eyes) and the crown of the head — used for ceiling bonks
let lungeGravityPauseUntil = -Infinity;  // gravity off until this timestamp; set by applyLunge so vertical trajectories track the lock target
// Apex boost: a successful upward smash kicks Y velocity so the player arcs over where the
// block was. Two post-impact windows then shape the arc:
//   * coast (Y preserved, no damping, no gravity) — pure hang-time
//   * float (gravity reduced to APEX_FLOAT_GRAVITY_MULT) — slow deceleration before normal fall
// Turns hanging-block smashes into platforming jumps. All four are admin-tunable.
let APEX_BOOST_VEL = 11;             // m/s peak Y kick when aiming straight up; scaled by lungeFwd.y
// Apex boost only fires when the player is airborne — boost-from-ground was confusing
// playtesters who got launched off short stacks they thought were grounded encounters.
// Same 0.3m clearance as hover-on-hit. Target height is no longer gated separately; once
// you're in the air, an upward-aimed smash gets the full boost regardless of how high the
// target was. Replaces the earlier target-height threshold (which was tried at 3.3m and 6m).
const APEX_AIRBORNE_CLEARANCE = 0.3;
let APEX_COAST_MS = 100;             // hang-time after the kick where Y is preserved
let APEX_FLOAT_MS = 130;             // post-coast window where gravity is reduced
let APEX_FLOAT_GRAVITY_MULT = 0.55;  // gravity multiplier during the float window (0 = none, 1 = full)
let apexCoastUntil = -Infinity;
let apexFloatUntil = -Infinity;
const moveVelocity = new THREE.Vector3();

// Head bob — Doom-style. bobDistance accumulates with movement; sine-driven offset applied at render.
let bobDistance = 0;
const BOB_VERT_RAD_PER_M = 2.5;   // vertical bob: ~0.4 cycles per meter walked
const BOB_HORIZ_RAD_PER_M = 1.25; // horizontal sway: half rate (one full sway per 2 vertical bobs)
const BOB_VERT_MAG = 0.05;
const BOB_HORIZ_MAG = 0.03;

function applyLunge(direction, distance, impactDelay = IMPACT_DELAY) {
  const f = new THREE.Vector3(direction.x, direction.y, direction.z);
  if (f.lengthSq() < 0.0001) return;
  f.normalize();
  // Solve peak velocity so displacement during the (possibly shortened) charge window ≈ distance.
  const integral = (1 - Math.exp(-MOVE_DAMP * impactDelay)) / MOVE_DAMP;
  const d = Math.max(0, distance ?? LUNGE_BASE_DIST);
  const peak = Math.min(d / Math.max(integral, 0.001), LUNGE_PEAK_CAP);
  moveVelocity.addScaledVector(f, peak);
  // Pause gravity through the lunge window so vertical components carry to the target accurately.
  lungeGravityPauseUntil = performance.now() / 1000 + impactDelay;
}

// Brake-on-impact — kills forward motion at the moment of contact and adds a small recoil.
// This is what makes hitting something feel solid instead of like gliding through. Toggle
// flips between active recoil (1.2 m/s gentle pushback) and pure brake (0 = legacy).
// RECOIL_SPEED + _impactRecoilOn declared up near the top of the file (TDZ — admin toggle reads them at module load).
function brakeOnImpact(forward) {
  const f = new THREE.Vector3(forward.x, 0, forward.z);
  if (f.lengthSq() < 0.0001) return;
  f.normalize();

  // Zero the velocity component along impact direction (preserve lateral motion).
  const along = moveVelocity.x * f.x + moveVelocity.z * f.z;
  if (along > 0) {
    moveVelocity.x -= f.x * along;
    moveVelocity.z -= f.z * along;
  }
  // Small reverse impulse — the "you bounced off it" feel.
  moveVelocity.x -= f.x * RECOIL_SPEED;
  moveVelocity.z -= f.z * RECOIL_SPEED;
}

function applyMissRecoil(forward) {
  const f = new THREE.Vector3(forward.x, 0, forward.z);
  if (f.lengthSq() < 0.0001) return;
  f.normalize();
  moveVelocity.x -= f.x * MISS_RECOIL_EXTRA_SPEED;
  moveVelocity.z -= f.z * MISS_RECOIL_EXTRA_SPEED;
}

function triggerMissRecovery(fistIndex) {
  if (fistIndex == null) return;
  missRecoveryUntil[fistIndex] = performance.now() + MISS_RECOVERY_MS;
}

function isMissRecovering() {
  const nowMs = performance.now();
  return nowMs < missRecoveryUntil[0] || nowMs < missRecoveryUntil[1];
}

// World-AABB XZ extents for a block (handles Y rotation conservatively).
function _blockXZHalfExtents(block) {
  const halfW = block.geometry.parameters.width / 2;
  const halfD = block.geometry.parameters.depth / 2;
  if (block.rotation.y === 0) return [halfW, halfD];
  const c = Math.abs(Math.cos(block.rotation.y));
  const s = Math.abs(Math.sin(block.rotation.y));
  return [halfW * c + halfD * s, halfW * s + halfD * c];
}

// Highest block-top below the player's feet (or 0 = floor). Tells movement where to clamp.
// Includes both anchored blocks and dynamic blocks that are essentially at rest — so a knocked-
// loose block sitting on the floor (or another block) still counts as a platform you can land on.
const _SUPPORT_REST_VEL = 1.5;  // m/s threshold — moving blocks aren't stable platforms
function findHighestSupportBelow(px, feetY, pz) {
  let bestY = 0;  // floor
  for (const block of blocks) {
    if (block.userData.dynamic) {
      // Dynamic block — only counts as support if it's not actively moving.
      const body = block.userData.body;
      if (!body) continue;
      const v = body.linvel();
      if (Math.hypot(v.x, v.y, v.z) > _SUPPORT_REST_VEL) continue;
    }
    const [bx, bz] = _blockXZHalfExtents(block);
    if (Math.abs(px - block.position.x) > bx) continue;
    if (Math.abs(pz - block.position.z) > bz) continue;
    const top = block.position.y + block.geometry.parameters.height / 2;
    if (top > feetY + 0.001) continue;  // block is above the feet — not a support
    if (top > bestY) bestY = top;
  }
  return bestY;
}

// Lowest static-block bottom above the player's head — for ceiling bonks while rising/vaulting.
function findLowestCeilingAbove(px, headY, pz) {
  let bestY = Infinity;
  for (const block of blocks) {
    if (block.userData.dynamic) continue;
    const [bx, bz] = _blockXZHalfExtents(block);
    if (Math.abs(px - block.position.x) > bx) continue;
    if (Math.abs(pz - block.position.z) > bz) continue;
    const bot = block.position.y - block.geometry.parameters.height / 2;
    if (bot < headY - 0.001) continue;
    if (bot < bestY) bestY = bot;
  }
  return bestY;
}

function updateMovement(dt) {
  // Camera-aligned planar right & forward (matches PointerLockControls.moveForward semantics).
  const camRight = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 0);
  camRight.y = 0;
  if (camRight.lengthSq() > 0.0001) camRight.normalize(); else camRight.set(1, 0, 0);
  const camForward = new THREE.Vector3().crossVectors(WORLD_Y, camRight).normalize();

  // Race start gate: pin the player still until GO (and after the finish). Punch/lunge is already blocked
  // in firePunch; this stops WASD walking too and kills any residual XZ drift so nobody creeps toward a
  // head start during the 3·2·1. Y is left alone so gravity keeps them grounded.
  if (_raceLocked) { moveVelocity.x = 0; moveVelocity.z = 0; }
  if (controls.isLocked && !_raceLocked) {
    const inF = (keys.w ? 1 : 0) - (keys.s ? 1 : 0);
    const inR = (keys.d ? 1 : 0) - (keys.a ? 1 : 0);
    let dirX = camForward.x * inF + camRight.x * inR;
    let dirZ = camForward.z * inF + camRight.z * inR;
    const len = Math.sqrt(dirX * dirX + dirZ * dirZ);
    if (len > 1) { dirX /= len; dirZ /= len; }

    // Soft cap: only accelerate toward terminal speed along input direction.
    // Lunge impulses can push us above the cap freely; damping pulls us back.
    // Hold E to sprint — 2.16× walk speed (was 1.8× before the 2026-05-16 keyboard
    // strip; restored 20% faster per playtest follow-up).
    const sprintMult = keys.e ? 2.16 : 1;
    const terminal = WALK_TERMINAL * sprintMult;
    const accel = MOVE_ACCEL * sprintMult;
    if (len > 0) {
      const speedInDir = moveVelocity.x * dirX + moveVelocity.z * dirZ;
      const accelMult = Math.max(0, 1 - speedInDir / terminal);
      moveVelocity.x += dirX * accel * accelMult * dt;
      moveVelocity.z += dirZ * accel * accelMult * dt;
    }
  }

  // Damping (exponential decay) — XZ always; Y only during lunge so the trajectory math holds.
  // Outside lunge windows, gravity drives Y instead.
  const damp = Math.exp(-MOVE_DAMP * dt);
  moveVelocity.x *= damp;
  moveVelocity.z *= damp;
  const _nowS = performance.now() / 1000;
  const lunging = _nowS < lungeGravityPauseUntil;
  const apexCoasting = _nowS < apexCoastUntil;
  const apexFloating = _nowS < apexFloatUntil;
  if (lunging) {
    moveVelocity.y *= damp;
  } else if (apexCoasting) {
    // Apex hang-time after a successful upward smash. Y velocity preserved so the boost
    // carries cleanly before gravity grabs back.
  } else if (apexFloating) {
    // Reduced-gravity window after the coast. Player decelerates gracefully instead of being
    // yanked down at full -30 the moment coast ends. Reads as floaty.
    moveVelocity.y += PLAYER_GRAVITY * APEX_FLOAT_GRAVITY_MULT * dt;
  } else {
    moveVelocity.y += PLAYER_GRAVITY * dt;
  }

  // Move freely, then push out of any walls/blocks we end up overlapping. With substepping on,
  // chunk the XZ motion into ≤MOVE_SUBSTEP_DIST slices and resolve collision after each — at
  // lunge peak (~50–100 m/s) a single 16ms step can travel >1m, larger than PLAYER_RADIUS, so
  // the AABB sweep misses fast crossings and the player tunnels through walls. Substepping
  // caps each slice below the player radius so detection is reliable at any speed.
  const beforeX = camera.position.x, beforeZ = camera.position.z;
  const dx = moveVelocity.x * dt, dz = moveVelocity.z * dt;
  if (_moveSubstepOn) {
    const MOVE_SUBSTEP_DIST = 0.25;  // m — under PLAYER_RADIUS (0.4) so collision can never miss
    const totalDist = Math.hypot(dx, dz);
    const steps = Math.max(1, Math.ceil(totalDist / MOVE_SUBSTEP_DIST));
    const stepX = dx / steps, stepZ = dz / steps;
    for (let i = 0; i < steps; i++) {
      camera.position.x += stepX;
      camera.position.z += stepZ;
      resolvePlayerCollision();
    }
  } else {
    camera.position.x += dx;
    camera.position.z += dz;
    resolvePlayerCollision();
  }
  const actualDx = camera.position.x - beforeX;
  const actualDz = camera.position.z - beforeZ;

  // Vertical integration + ground/block-top clamp.
  const prevCamY = camera.position.y;
  camera.position.y += moveVelocity.y * dt;

  // Land on whichever surface is below the feet (floor or block top — whichever is highest).
  // Use the highest feet position this frame so a fast fall doesn't tunnel past block tops.
  const feetY = camera.position.y - PLAYER_FOOT_OFFSET;
  const prevFeetY = prevCamY - PLAYER_FOOT_OFFSET;
  const supportFeetY = Math.max(feetY, prevFeetY);
  const supportY = findHighestSupportBelow(camera.position.x, supportFeetY, camera.position.z);
  const standCameraY = supportY + PLAYER_FOOT_OFFSET;
  if (camera.position.y <= standCameraY) {
    camera.position.y = standCameraY;
    if (moveVelocity.y < 0) moveVelocity.y = 0;
  }

  // Bonk on a ceiling block when rising/vaulting under it.
  if (moveVelocity.y > 0) {
    const headY = camera.position.y + PLAYER_HEAD_CLEARANCE;
    const ceilY = findLowestCeilingAbove(camera.position.x, headY, camera.position.z);
    if (ceilY < Infinity && headY > ceilY) {
      camera.position.y = ceilY - PLAYER_HEAD_CLEARANCE;
      moveVelocity.y = 0;
    }
  }

  playerBody.setNextKinematicTranslation({ x: camera.position.x, y: camera.position.y, z: camera.position.z });

  // If significantly blocked, replace velocity with actual achieved (prevents momentum accumulation against walls).
  const desired2 = dx * dx + dz * dz;
  const achieved2 = actualDx * actualDx + actualDz * actualDz;
  if (desired2 > 0.0001 && achieved2 < desired2 * 0.25) {
    moveVelocity.x = actualDx / Math.max(dt, 0.001);
    moveVelocity.z = actualDz / Math.max(dt, 0.001);
  }

  bobDistance += Math.hypot(actualDx, actualDz);
}

// Auto-face — rotate the camera (yaw only) toward the locked target during the charge.
// Mirrors PunchAutoFaceClient. By impact frame, the player is looking AT the target.
const AUTO_FACE_DURATION = 0.13; // collapses with the rest of the rhythm
const WORLD_Y = new THREE.Vector3(0, 1, 0);
let autoFaceStartTime = -Infinity;
let autoFaceTotalAngle = 0;
let autoFaceAngleApplied = 0;

// Yaw-only by design. A vertical (pitch) auto-aim was tried and removed: tilting the view
// up/down onto the target read as disorienting (Rich is motion sensitive). Auto-face swings
// the view left/right only; the player handles vertical aim.
function startAutoFace(targetPos) {
  const camForward = new THREE.Vector3();
  camera.getWorldDirection(camForward);
  camForward.y = 0;
  if (camForward.lengthSq() < 0.0001) return;
  camForward.normalize();

  const toTarget = new THREE.Vector3().subVectors(targetPos, camera.position);
  toTarget.y = 0;
  if (toTarget.lengthSq() < 0.0001) return;
  toTarget.normalize();

  const dot = camForward.dot(toTarget);
  const crossY = camForward.z * toTarget.x - camForward.x * toTarget.z;
  const angle = Math.atan2(crossY, dot);

  autoFaceTotalAngle = angle;
  autoFaceAngleApplied = 0;
  autoFaceStartTime = performance.now() / 1000;
}

function updateAutoFace(dt) {
  const now = performance.now() / 1000;
  const t = (now - autoFaceStartTime) / AUTO_FACE_DURATION;
  if (t >= 1) {
    if (autoFaceAngleApplied !== autoFaceTotalAngle) {
      camera.rotateOnWorldAxis(WORLD_Y, autoFaceTotalAngle - autoFaceAngleApplied);
      autoFaceAngleApplied = autoFaceTotalAngle;
    }
    return;
  }
  // Ease-out quadratic: most of the rotation happens early in the swing.
  const eased = 1 - Math.pow(1 - t, 2);
  const targetApplied = autoFaceTotalAngle * eased;
  const delta = targetApplied - autoFaceAngleApplied;
  if (Math.abs(delta) > 1e-5) {
    camera.rotateOnWorldAxis(WORLD_Y, delta);
    autoFaceAngleApplied = targetApplied;
  }
}

// Damage from a single armed projectile chip striking a block. Each chip is one-shot — chip count
// = damage potential. Designed for chip-storm charge attacks where dozens of chips converge on a target.
let _lastProjectileShakeMs = 0;
// One shockwave per storm-release. Set true on launch, cleared on first chip-break — that
// break fires a single big topple/shake/flash, subsequent breaks just remove blocks quietly.
const _pendingShockwave = [false, false];
let _projSoundWindowStart = 0;  // start of current 120ms projectile-sound window
let _projSoundCount = 0;        // sounds played this window (capped at 3)
function applyProjectileHit(block, chip) {
  if (!block || !blocks.includes(block)) return;
  // Refresh the streak idle timer on every projectile impact. Streak doesn't grow through
  // projectiles (still earned by punching to rhythm), but it doesn't reset while chips are
  // landing — fixes the case where a chip-storm flies for 2s and times out the streak.
  _lastTimingEventMs = performance.now();
  // Assist: a landed charge hit keeps the streak alive too, same as a punch — refresh the bar-length
  // grace window so a successful storm (not just the charge itself) counts toward sustaining it.
  if (_easyMode && audioCtx && _musicStarted) {
    _easyStreakGraceUntil = audioCtx.currentTime + EASY_STREAK_GRACE_BARS * MUSIC_BAR;
  }

  // Damage uses the LAUNCH speed stamped on the chip, not its body.linvel(). Rapier's collision
  // response zeroes the chip's velocity before drainCollisionEvents fires, so reading linvel()
  // here would always return ~5 m/s and floor every chip's damage to 1.
  const speed = chip.userData.projectileSpeed ?? 30;
  const damage = Math.max(1, Math.round(speed / 20));
  block.userData.hitsTaken += damage;
  const ht = block.userData.hitsTaken;
  const total = block.userData.hitsToBreak;

  const impactPos = chip.position.clone();
  // Forward direction from current (post-collision) velocity if any, fallback to Z+. Used for
  // visual/cascade direction — exact value isn't critical, the magnitude doesn't matter here.
  const v = chip.userData.body ? chip.userData.body.linvel() : { x: 0, y: 0, z: 1 };
  const forward = new THREE.Vector3(v.x, v.y, v.z);
  if (forward.lengthSq() > 0.0001) forward.normalize();
  else forward.set(0, 0, 1);

  if (ht >= total) {
    _breaksThisFrame++;
    _easyChargeStormCoins = true;  // assist: charge-storm loot keeps full material × streak (reset before the return below)
    // Block destroyed. Sound + visuals are rate-limited so a chip-volley breaking many blocks at
    // once doesn't stack hundreds of audio nodes / chunk spawns into a single frame.
    const nowMs = performance.now();
    // Smash-sound budget: max 3 projectile-break sounds per 120ms window. Beyond that the block
    // still breaks but plays no sound — prevents the deferred-setTimeout pile-up that froze the
    // browser when 30+ smash sounds hit Web Audio in the same frame.
    if (nowMs - _projSoundWindowStart > 120) {
      _projSoundWindowStart = nowMs;
      _projSoundCount = 0;
    }
    if (_projSoundCount < 3) {
      _projSoundCount++;
      const pitch = smashPitchMultiplier(ht, total);
      const material = block.userData.material;
      const soundPos = block.position.clone();
      if (audioCtx) {
        const delay = Math.max(0, _snapToNextSixteenth(audioCtx.currentTime) - audioCtx.currentTime);
        setTimeout(() => playSmashSound(material, pitch, true, soundPos), delay * 1000);
      } else {
        playSmashSound(material, pitch, true, soundPos);
      }
    }
    // One-shot shockwave per storm: the FIRST chip-break of this release fires a single big
    // topple/shake/flash centred on that break. Subsequent breaks in the same storm just
    // remove their block + drop coins quietly. Reads as a cinematic single-blow impact.
    // Storm power scales with the player's current streak: 0 below tier 5, up to 1.0 at
    // streak 50+. Tap once into the factor for all storm-power scaling on this chip break.
    const power = _stormPowerFactor();
    const fist = chip.userData.assignedFist;
    if (fist != null && _pendingShockwave[fist]) {
      _pendingShockwave[fist] = false;
      cameraShake(0.22, 0.28);
      flashScreen(0.13);
      // Bigger and meatier than the old per-break topple — single hammer-blow strength
      // (0.85 scale ≈ melee, 9m radius covers the whole hit stack and one ring out).
      // Storm-power scales the radius from 1.0× to shockwaveRadiusMultMax.
      const shockMult = 1 + (_stormPower.shockwaveRadiusMultMax - 1) * power;
      applyNearbyTopple(impactPos, forward, block, 0.85, 9 * shockMult);
    }
    // Per-break chunk visuals + coin payout still run, but rate-limited so a 30-chip volley
    // breaking 5 blocks doesn't stack 5 chunk bursts in the same frame.
    if (nowMs - _lastProjectileShakeMs > 120) {
      // Break-loot count scales with the combo timing tier captured at launch:
      //   miss    → 0 bonus coins (rhythm penalty)
      //   good    → base count (current material loot)
      //   perfect → 2× base count (rhythm reward)
      // Per-coin score = streak bonus from launch moment, so chained on-beat play snowballs.
      const tier = chip.userData.comboTier ?? 'good';
      const tierMult = chip.userData.suppressBreakCoins ? 0 : tier === 'perfect' ? 2 : tier === 'good' ? 1 : 0;
      const streak = chip.userData.streakBonusAtLaunch ?? 1;
      const impulseMult = 1 + (_stormPower.chunkImpulseMultMax - 1) * power;
      spawnChunks(impactPos, forward, block.userData.material, streak, true, tierMult, impulseMult, block, tier);
      _lastProjectileShakeMs = nowMs;
    }
    // Chip-storm direct kill: impulse along the chip's forward (away from the player).
    breakBlock(block, forward, 12);
    // Storm-power AOE damage: every chip-break damages blocks within aoeRadius (scaled by
    // streak). One level deep — AOE-triggered breaks do not chain another AOE.
    // Pass through perCoinScore + tier so AOE-killed blocks pay out the same loot as the
    // originating chip break. High streak should clear faster AND score more, not less.
    const aoeRadius = _stormPower.aoeRadiusMax * power;
    const aoeDamage = power > 0 ? Math.max(1, Math.round(_stormPower.aoeDamageMax * power)) : 0;
    if (aoeRadius > 0 && aoeDamage > 0) {
      const tier = chip.userData.comboTier ?? 'good';
      const tierMult = chip.userData.suppressBreakCoins ? 0 : tier === 'perfect' ? 2 : tier === 'good' ? 1 : 0;
      const streak = chip.userData.streakBonusAtLaunch ?? 1;
      const impulseMult = 1 + (_stormPower.chunkImpulseMultMax - 1) * power;
      _applyChipAOEDamage(impactPos, forward, aoeRadius, aoeDamage, streak, tierMult, impulseMult, tier);
    }
    _easyChargeStormCoins = false;
    return;
  }

  // Cracking hit — visual + small impulse so the block visibly nudges (raw chip mass is too
  // low to push a heavy block, so we add an explicit impulse). Metal stays anchored when cracked.
  if (block.userData.material !== 'metal') {
    const impulseScale = Math.min(0.4, 0.12 + speed / 250);
    applyImpulseToBlock(block, impactPos, forward, impulseScale);
  }
  _paintBlockDamage(block, ht);
  const c = block.userData.originalColor;
  const darken = Math.pow(CRACK_DARKEN_PER_HIT, ht);
  block.material.color.setRGB(c.r * darken, c.g * darken, c.b * darken);
  // Deliberately NO spawnChips here — the projectile chip itself is the visual feedback.
  // A 50-chip volley would otherwise spawn 200+ aesthetic chips in one frame and stall.
}

function applyHit(block, impactPos, forward, charge = 0, chargedRadius = CHARGED_RADIUS_SOLO, timingMult = 1, tier = 'good', fistIndex = null, suppressCoins = false, movesPlayer = true) {
  const isCharged = charge >= CHARGE_THRESHOLD;
  const isRhythmMiss = tier === 'miss' || timingMult <= 0;

  // Synchronized juice burst — every layer fires this frame. Charged hits amplify shake/flash.
  if (movesPlayer) brakeOnImpact(forward);
  cameraShake(isRhythmMiss ? 0.035 : isCharged ? 0.18 : 0.10, isRhythmMiss ? 0.06 : isCharged ? 0.20 : 0.13);
  if (!isRhythmMiss) flashScreen(isCharged ? 0.16 : 0.08);
  impactRing(impactPos, forward, block);
  triggerReticuleImpact();  // brief reticule pulse acknowledges every landed tap punch

  if (isRhythmMiss) {
    if (movesPlayer) applyMissRecoil(forward);
    triggerMissRecovery(fistIndex);
    return;
  }

  _coachNoteSmash();  // L1 coach: first landed hit -> celebrate + minimize (no-op unless armed)

  if (isCharged) {
    // Charged punch: directly-hit block instant-breaks. Radius victims take +2 damage so crates
    // and bricks shatter in radius; metals still crack and need a follow-up.
    // Knockback is moderated — surrounding blocks tip rather than fly, so heavy materials feel heavy.
    // Smash sound was already played at click time in firePunch — only spawn the visual debris here.
    spawnChunks(impactPos, forward, block.userData.material, suppressCoins ? 0 : timingMult, false, suppressCoins ? 0 : 1, 1, block, tier);
    applyNearbyTopple(impactPos, forward, block, 0.7);
    // Charged direct kill: strong forward impulse from the punch.
    breakBlock(block, forward, 14);

    const CHARGED_RADIUS_DAMAGE = 2;
    const victims = [];
    for (const other of blocks) {
      if (other === block) continue;
      if (other.position.distanceTo(block.position) <= chargedRadius) {
        victims.push(other);
      }
    }
    for (const t of victims) {
      const tImpact = closestPointOnBlock(t, block.position);
      t.userData.hitsTaken += CHARGED_RADIUS_DAMAGE;
      const ht = t.userData.hitsTaken;
      const total = t.userData.hitsToBreak;
      if (ht >= total) {
        // Broken in radius — chunks but NO secondary cascade. The primary cascade does the pushing.
        spawnChunks(tImpact, forward, t.userData.material, suppressCoins ? 0 : timingMult, false, suppressCoins ? 0 : 1, 1, t, tier);
        // Radius victim: impulse outward from the punched block's centre.
        const rDir = new THREE.Vector3(t.position.x - block.position.x, 0.2, t.position.z - block.position.z);
        if (rDir.lengthSq() < 0.0001) rDir.copy(forward);
        breakBlock(t, rDir, 9);
      } else {
        // Cracked but didn't break — visual feedback only.
        spawnChips(tImpact, forward, t.userData.material);
        const v = Math.pow(CRACK_DARKEN_PER_HIT, ht);
        const c = t.userData.originalColor;
        t.material.color.setRGB(c.r * v, c.g * v, c.b * v);
        _paintBlockDamage(t, ht);
      }
    }
    return;
  }

  block.userData.hitsTaken += 1;
  const ht = block.userData.hitsTaken;
  const total = block.userData.hitsToBreak;
  const isFinal = ht >= total;

  // Smash sound was already played at click time in firePunch — impact only handles physics + visual.

  if (isFinal) {
    // Block destroyed — cascade unanchors the surrounding structure.
    spawnChunks(impactPos, forward, block.userData.material, suppressCoins ? 0 : timingMult, false, suppressCoins ? 0 : 1, 1, block, tier);
    // Assist-mode power ramp: the blast scalar (_easyTapBlast) grows continuously with streak AND
    // keeps climbing past 50, so each kill gets a bigger AOE (chain-clears neighbours), wider topple,
    // harder debris, meatier shake, and a more violent primary break the higher you are. Coins follow
    // the full economy now (material × streak via timingMult), and the AOE only destroys blocks that
    // already exist, so the score is still bounded by the level's block value (baseYield).
    // TWO DIALS, now SHARED across BOTH modes (Rich 2026-06-16: bigger normal levels took too long to
    // clear). Spectacle (debris violence) rides _easyTapBlast and climbs from streak 5 so power is FELT
    // early. The AOE chain-clear + extra topple ride _easyDamageBlast and hold at zero until streak 35,
    // ramping to full by 80, so levels don't evaporate early but a sustained streak clears clusters fast.
    // Screen shake has its own slow ramp pushed even later (peaks ~streak 130) so the view-jolt can't
    // make play harder until very deep. NOTE on scoring: AOE-cleared coins are flat +1 in assist (the
    // _easyChargeStormCoins/spawnChunks clamp) but pay the FULL economy in normal — so a high-streak
    // normal chain clears faster AND scores more (intended: the reward for sustaining a big streak).
    let toppleScale = 1.0;
    let killImpulse = 10;
    const spec = _easyTapBlast();
    const dmg = _easyDamageBlast();
    if (spec > 0) {
      killImpulse = 10 + 8 * spec;                 // more violent primary break / flying rubble (cosmetic, early)
    }
    if (dmg > 0) {
      const aoeRadius = EASY_BLAST_AOE_RADIUS * dmg;
      const aoeDamage = Math.max(1, Math.round(EASY_BLAST_AOE_DAMAGE * dmg));
      const impulseMult = 1 + 1.2 * dmg;           // AOE debris flies harder as real destruction grows
      // In assist the SOUNDSCAPE was already fired on the beat in _updateEasyMode (predicted); in normal
      // it fires on the beat in the press handler. Either way the physical AOE/breaks/coins resolve here.
      _applyChipAOEDamage(impactPos, forward, aoeRadius, aoeDamage, timingMult, 1, impulseMult, tier);
      toppleScale = 1 + 0.8 * dmg;                 // extra topple only once real destruction kicks in
      const shake = Math.min(1, Math.max(0, (_streak - EASY_DAMAGE_MIN_STREAK) / (130 - EASY_DAMAGE_MIN_STREAK)));
      cameraShake(0.18 * shake, 0.20 * shake);
    }
    applyNearbyTopple(impactPos, forward, block, toppleScale);
    // Tap kill: forward impulse from the punch direction.
    breakBlock(block, forward, killImpulse);
    return;
  }

  // Cracking hit (not final) — small impulse on the direct block only. No cascade so
  // the rest of the structure stays intact until something actually breaks.
  // Metal blocks stay rigidly anchored even when cracked — gives them a "solid" feel and makes
  // them reliable for the natural physics of standing on / vaulting to. Crate/brick still
  // unanchor on crack so landing on damaged ones is risky.
  if (block.userData.material !== 'metal') {
    applyImpulseToBlock(block, impactPos, forward, 0.25);
  }

  // Crack feedback: scale pulse + V-mult darken
  block.scale.set(0.88, 0.88, 0.88);
  setTimeout(() => block.scale.set(1, 1, 1), 90);

  const v = Math.pow(CRACK_DARKEN_PER_HIT, ht);
  const c = block.userData.originalColor;
  block.material.color.setRGB(c.r * v, c.g * v, c.b * v);

  // Visual crack — promote to per-block crack texture (if not already) and paint this hit.
  _paintBlockDamage(block, ht);

  spawnChips(impactPos, forward, block.userData.material);

  // Cracking-hit coin trickle: 1 currency chip per non-break hit, stamped with timing multiplier.
  // Every on-rhythm hit rewards — the big payout still lands on the final break via spawnChunks.
  // Off-beat hits skip the coin spawn entirely (no worthless gold-chip clutter on the floor).
  if (timingMult > 0 && !suppressCoins) {
    _spawnDebrisBatch(impactPos, forward, 1, [0.3, 0.5], [4, 9], true, block.userData.material, timingMult);
  }
}

function breakBlock(block, impulseDir = null, impulseStrength = 0, upwardBurst = false) {
  // Multiplayer: broadcast this death whenever WE destroy a block by our own action (a smash, our AOE,
  // our ground-pop). The only thing we must NOT rebroadcast is a death we're applying because a peer told
  // us to (_netRemoteContext) — that would echo. We deliberately do NOT gate on ownedRemote: in cause
  // mode a block can be flagged peer-owned on one screen but standing on the other (divergent cascades),
  // and if we suppressed those, smashing such a block locally would never reach the other player. Rule:
  // whoever destroys it by their own action broadcasts it; netDead makes a duplicate an idempotent no-op.
  if (block.userData && !block.userData.netDead) {
    if (_mp && !_netRemoteContext && block.userData.netId !== undefined) {
      const _dmsg = { t: 'd', id: block.userData.netId };
      // Carry the SMASH INTENT so the observer reproduces the real destruction (directional knockback +
      // dust/sparks/chunks) instead of a weak generic vertical pop: dir = world-space impulse direction,
      // str = impulse strength, ub = upward-burst flag (ground-pops pop up, smashes go directional). Omit
      // for deaths with no impulse data (shrink-fade path) — the observer then falls back to the pop.
      if (impulseDir) {
        _dmsg.dir = [Math.round(impulseDir.x * 100) / 100, Math.round(impulseDir.y * 100) / 100, Math.round(impulseDir.z * 100) / 100];
        _dmsg.str = impulseStrength;
        if (upwardBurst) _dmsg.ub = 1;
      }
      // Race (coins metric): carry the coin value this break paid so the referee credits it to whoever's
      // claim wins the block. Smashes metric ignores it (the referee counts 1 per winning claim).
      if (_RACE_ON && _RACE_METRIC === 'coins') _dmsg.cv = _lastBreakCoins;
      _mp.send(_dmsg);
    }
    block.userData.netDead = true;
  }
  // Clear targeting state if this is the active near/lock.
  if (nearTarget === block) setNearTarget(null);
  if (lockedTarget === block) lockedTarget = null;

  // Cascade the support graph BEFORE removing the body — dependents lose this supporter, and
  // any that hit zero remaining supporters unanchor (and recursively cascade their own
  // dependents). Without this, blocks resting on a destroyed block stay magically anchored.
  for (const S of block.userData.supporters) {
    S.userData.dependents.delete(block);
  }
  block.userData.supporters.clear();
  for (const D of block.userData.dependents) {
    D.userData.supporters.delete(block);
    if (D.userData.supporters.size === 0 && !D.userData.permanentAnchor) {
      unanchorBlock(D);
    }
  }
  block.userData.dependents.clear();

  // Remove from the smashable-blocks list immediately so targeting/scoring stops considering it.
  const i = blocks.indexOf(block);
  if (i >= 0) blocks.splice(i, 1);

  // Ghost-death path: dissect the block into 2 / 4 / 8 pieces depending on size. Each
  // piece becomes a non-colliding dynamic body that tumbles off on its own impulse + spin,
  // fades over GHOST_DEATH_FADE_MS, then disposes via _updateDyingBlocks.
  if (_ghostDeath.enabled && impulseDir && block.userData.body) {
    _splitBlockIntoPieces(block, impulseDir, impulseStrength, upwardBurst);
    return;
  }

  // Original shrink-and-fade path (toggle off, or no impulse data).
  if (block.userData.body) {
    _blockByBodyHandle.delete(block.userData.body.handle);
    physicsWorld.removeRigidBody(block.userData.body);
    block.userData.body = null;
  }
  const start = performance.now();
  const duration = 160;
  block.material.transparent = true;
  function shrink() {
    const t = (performance.now() - start) / duration;
    if (t >= 1) {
      scene.remove(block);
      block.geometry.dispose();
      block.material.dispose();
      if (block.userData.crackTexture) block.userData.crackTexture.dispose();
      return;
    }
    const s = 1 - t;
    block.scale.set(s, s, s);
    block.material.opacity = s;
    requestAnimationFrame(shrink);
  }
  shrink();
}

// Cleave a dying block into 2, 4, or 8 pieces and launch each on its own impulse + spin.
// Piece count auto-scales with block volume: small blocks split in 2 (the original "drama
// halves"), medium into 4 quarters, large into 8 octants. The split axes are picked by
// dimension (longest-first) so a tall pillar splits along its length first; cubes fall
// back to impulse-aligned for the 2-piece case so the cleave reads "across" the punch.
// Each piece becomes a non-colliding dynamic body added to _dyingBlocks for fade+dispose.
function _splitBlockIntoPieces(block, impulseDir, impulseStrength, upwardBurst = false) {
  const params = block.geometry.parameters;
  const w = params.width, h = params.height, d = params.depth;
  const ud = block.userData;

  // Scale impulse by the block's largest dimension so big blocks fly proportionally to their
  // size — without this, a 60m tower's halves get the same 12 m/s as a 1m crate and barely
  // budge against gravity. Linear from 1m=1× up to 12m+=4×.
  const maxDim = Math.max(w, h, d);
  const sizeBoost = Math.max(1, Math.min(4, maxDim / 4));
  impulseStrength = impulseStrength * sizeBoost;

  // Decide piece count by volume. 4 pieces is the new floor (was 2, but felt understated
  // on small crates) — even 1m cubes split into quarters. Bigger set-pieces octant-split.
  const volume = w * h * d;
  const pieceCount = volume < 8 ? 4 : 8;

  // Pick a split strategy based on shape.
  //
  // NORMAL (near-cubic / slab-like): halve along the longest 2 (4-piece) or all 3 (8-piece)
  // axes so the result reads as quarters / octants.
  //
  // ELONGATED (one axis dominates, e.g. tall pillars): the normal strategy halves the long
  // axis only ONCE while ALSO halving a short axis, producing thin tall strips. For these
  // objects, slice perpendicular to the long axis MULTIPLE times instead, keeping the
  // cross-section intact — reads as "snapped chunks" rather than "stripped."
  //
  // Detection: longest > 3× the second-longest. (Using second-longest, not the shortest,
  // so a flat slab — long+long+short — stays in the normal path and tiles into quarters.)
  const dims = [w, h, d];
  const axesByLength = [0, 1, 2].sort((a, b) => dims[b] - dims[a]);
  const longAxis = axesByLength[0];
  const elongated = dims[longAxis] / Math.max(dims[axesByLength[1]], 0.001) > 3;

  // Build the list of pieces — each carries its own dims and a local-frame offset from
  // the original block centre. Two code paths feed the same downstream spawn loop.
  const pieces = [];
  if (elongated) {
    // Slice along the long axis only, into `pieceCount` equal segments. Each segment
    // keeps the full cross-section in the other two dims.
    const longLen = dims[longAxis];
    const segLen = longLen / pieceCount;
    const segDims = [w, h, d];
    segDims[longAxis] = segLen;
    for (let i = 0; i < pieceCount; i++) {
      // Centre offset along the long axis: spreads pieces from -L/2 + segLen/2 to
      // +L/2 - segLen/2 in equal steps. (i + 0.5 - N/2) * segLen.
      const localOffset = [0, 0, 0];
      localOffset[longAxis] = (i + 0.5 - pieceCount / 2) * segLen;
      pieces.push({ dims: segDims.slice(), localOffset });
    }
  } else {
    // 4 pieces split along the two longest axes; 8 pieces split along all three.
    const splitAxes = pieceCount === 4
      ? [axesByLength[0], axesByLength[1]]
      : [0, 1, 2];
    const baseDims = [w, h, d];
    const offsetMags = [0, 0, 0];
    for (const ax of splitAxes) {
      baseDims[ax] = baseDims[ax] / 2;
      offsetMags[ax] = dims[ax] / 4;
    }
    const N = splitAxes.length;
    for (let i = 0; i < (1 << N); i++) {
      const signs = [0, 0, 0];
      for (let k = 0; k < N; k++) {
        signs[splitAxes[k]] = (i & (1 << k)) ? 1 : -1;
      }
      pieces.push({
        dims: baseDims.slice(),
        localOffset: [signs[0] * offsetMags[0], signs[1] * offsetMags[1], signs[2] * offsetMags[2]],
      });
    }
  }

  const dir = new THREE.Vector3(impulseDir.x, impulseDir.y, impulseDir.z);
  if (dir.lengthSq() < 0.0001) dir.set(0, 1, 0);
  else dir.normalize();

  for (const piece of pieces) {
    const pDims = piece.dims;
    const localOffset = new THREE.Vector3(
      piece.localOffset[0], piece.localOffset[1], piece.localOffset[2],
    );
    const worldOffset = localOffset.clone().applyQuaternion(block.quaternion);

    // Piece mesh: own geometry sized to the piece, cloned material so it fades independently.
    // The map (texture) is shared via the cloned reference — every piece renders with the
    // original block's texture state, including any crack overlays from prior damage.
    const halfGeo = new THREE.BoxGeometry(pDims[0], pDims[1], pDims[2]);
    applyTilingUVs(halfGeo, pDims[0], pDims[1], pDims[2]);
    const halfMat = block.material.clone();
    halfMat.transparent = true;
    const halfMesh = new THREE.Mesh(halfGeo, halfMat);
    halfMesh.castShadow = true;
    halfMesh.position.copy(block.position).add(worldOffset);
    halfMesh.quaternion.copy(block.quaternion);
    scene.add(halfMesh);

    // Non-colliding dynamic body so the piece tumbles freely without snagging on the world.
    const bodyDesc = RAPIER.RigidBodyDesc.dynamic()
      .setTranslation(halfMesh.position.x, halfMesh.position.y, halfMesh.position.z)
      .setRotation({
        x: halfMesh.quaternion.x, y: halfMesh.quaternion.y,
        z: halfMesh.quaternion.z, w: halfMesh.quaternion.w,
      });
    const body = physicsWorld.createRigidBody(bodyDesc);
    physicsWorld.createCollider(
      RAPIER.ColliderDesc.cuboid(pDims[0] / 2, pDims[1] / 2, pDims[2] / 2)
        .setCollisionGroups(0x00000000),
      body,
    );

    // Launch: hit direction + STRONG outward separation (so the cleave is visible) + small
    // random spread. No deliberate upward bias here — for a vertical split (top/bottom halves
    // of a tall pillar), an upward bias would push BOTH halves up and prevent the bottom
    // from falling away. The outward direction handles separation correctly for any axis.
    const outward = localOffset.clone().applyQuaternion(block.quaternion).normalize();
    const launch = dir.clone().multiplyScalar(0.4)
      .add(outward.multiplyScalar(0.9))
      .add(new THREE.Vector3(
        (Math.random() - 0.5) * 0.5,
        (Math.random() - 0.5) * 0.5,
        (Math.random() - 0.5) * 0.5,
      ));
    // Ground-pop bursts throw pieces UP and out so they don't sink into the floor where the
    // block landed. Normal smashes keep their axis-true launch (see the no-upward-bias note above).
    if (upwardBurst) launch.y = Math.abs(launch.y) + 1.4;
    launch.normalize();
    const mass = body.mass();
    body.applyImpulse({
      x: launch.x * impulseStrength * mass,
      y: launch.y * impulseStrength * mass,
      z: launch.z * impulseStrength * mass,
    }, true);
    body.setAngvel({
      x: (Math.random() - 0.5) * 16,
      y: (Math.random() - 0.5) * 16,
      z: (Math.random() - 0.5) * 16,
    }, true);

    halfMesh.userData = {
      body,
      dying: true,
      dyingStart: performance.now(),
      crackTexture: null,  // halves don't own the original texture; not disposed here.
    };
    _dyingBlocks.push(halfMesh);
  }

  // Dispose the original block — the two halves replace it visually and physically.
  if (ud.body) {
    _blockByBodyHandle.delete(ud.body.handle);
    physicsWorld.removeRigidBody(ud.body);
    ud.body = null;
  }
  scene.remove(block);
  block.geometry.dispose();
  block.material.dispose();
  // Original crackTexture (if any) is still referenced by the halves' cloned materials.
  // Not disposed here; acceptable small leak vs the alternative of broken textures on halves.
}

// Per-frame update for blocks in the ghost-death state — sync mesh to physics body, hold
// full opacity for GHOST_DEATH_HOLD_MS so the player gets to see the tumble, then linear
// fade over GHOST_DEATH_FADE_MS, then dispose.
function _updateDyingBlocks() {
  const now = performance.now();
  const total = GHOST_DEATH_HOLD_MS + GHOST_DEATH_FADE_MS;
  for (let i = _dyingBlocks.length - 1; i >= 0; i--) {
    const block = _dyingBlocks[i];
    const ud = block.userData;
    const elapsed = now - ud.dyingStart;
    if (elapsed >= total) {
      // Final cleanup
      scene.remove(block);
      block.geometry.dispose();
      block.material.dispose();
      if (ud.crackTexture) ud.crackTexture.dispose();
      if (ud.body) {
        _blockByBodyHandle.delete(ud.body.handle);
        physicsWorld.removeRigidBody(ud.body);
        ud.body = null;
      }
      _dyingBlocks.splice(i, 1);
    } else {
      const tr = ud.body.translation();
      const rot = ud.body.rotation();
      block.position.set(tr.x, tr.y, tr.z);
      block.quaternion.set(rot.x, rot.y, rot.z, rot.w);
      // Hold full opacity during the flight phase, then linear fade over GHOST_DEATH_FADE_MS.
      if (elapsed > GHOST_DEATH_HOLD_MS) {
        const fadeT = (elapsed - GHOST_DEATH_HOLD_MS) / GHOST_DEATH_FADE_MS;
        block.material.opacity = 1 - fadeT;
      }
    }
  }
}

// =========================================================
// Smash impact FX — pooled dust puffs + sparks on hits/breaks
// =========================================================
// Two THREE.Points clouds (one draw call each), each backed by a FIXED pre-allocated pool reused across
// every impact — no per-hit allocation, no GC churn, and the per-frame cost is constant regardless of how
// many hits land. Dust = soft round alpha sprites; sparks = tiny additive dots. Per-material recipe: brick =
// reddish dust, metal = bright sparks + a little grey dust, crate = tan woody dust + a few splinter sparks.
// Emitted from spawnChunks (break) and spawnChips (crack); ticked by _updateImpactFX(dt). When no particles
// are alive the tick early-outs and uploads nothing.

function _makeImpactDotTex(soft) {
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const ctx = c.getContext('2d');
  const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(soft ? 0.5 : 0.4, soft ? 'rgba(255,255,255,0.45)' : 'rgba(255,255,255,0.85)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

function _makeImpactCloud(poolSize, texture, blending) {
  const geo = new THREE.BufferGeometry();
  const pos = new Float32Array(poolSize * 3);
  const aColor = new Float32Array(poolSize * 3);
  const aAlpha = new Float32Array(poolSize);
  const aSize = new Float32Array(poolSize);
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('aColor', new THREE.BufferAttribute(aColor, 3));
  geo.setAttribute('aAlpha', new THREE.BufferAttribute(aAlpha, 1));
  geo.setAttribute('aSize', new THREE.BufferAttribute(aSize, 1));
  const mat = new THREE.ShaderMaterial({
    uniforms: { uTex: { value: texture }, uScale: { value: window.innerHeight } },
    transparent: true, depthWrite: false, blending,
    vertexShader: `
      attribute vec3 aColor; attribute float aAlpha; attribute float aSize;
      varying vec3 vColor; varying float vAlpha; uniform float uScale;
      void main() {
        vColor = aColor; vAlpha = aAlpha;
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = aSize * uScale / max(-mv.z, 0.001);
        gl_Position = projectionMatrix * mv;
      }`,
    fragmentShader: `
      uniform sampler2D uTex; varying vec3 vColor; varying float vAlpha;
      void main() {
        if (vAlpha <= 0.0) discard;
        vec4 t = texture2D(uTex, gl_PointCoord);
        gl_FragColor = vec4(vColor, t.a * vAlpha);
      }`,
  });
  const points = new THREE.Points(geo, mat);
  points.frustumCulled = false;   // emitted at the action; never cull the whole cloud
  scene.add(points);
  const P = [];
  for (let i = 0; i < poolSize; i++) P.push({ active: false });
  return { points, geo, pos, aColor, aAlpha, aSize, P, cursor: 0, live: 0 };
}

const _DUST_CLOUD = _makeImpactCloud(256, _makeImpactDotTex(true), THREE.NormalBlending);

// Spark streaks — rendered as additive LINE SEGMENTS (tail→head) so each spark is a motion-blurred streak
// like a real metal strike, not a round dot. One LineSegments draw call, fixed pool. The tail trails behind
// the head along the velocity (length scaled by speed); per-vertex colour fades head(bright/hot)→tail(dim)
// and dies over the spark's life. Additive so dense overlapping cores clip toward white-hot.
function _makeSparkStreaks(poolSize) {
  const geo = new THREE.BufferGeometry();
  const pos = new Float32Array(poolSize * 2 * 3);
  const col = new Float32Array(poolSize * 2 * 3);
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
  const mat = new THREE.LineBasicMaterial({
    vertexColors: true, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
  });
  const lines = new THREE.LineSegments(geo, mat);
  lines.frustumCulled = false;
  scene.add(lines);
  const P = [];
  for (let i = 0; i < poolSize; i++) P.push({ active: false });
  return { lines, geo, pos, col, P, cursor: 0, live: 0 };
}
const _SPARK_STREAKS = _makeSparkStreaks(240);

function _emitSpark(x, y, z, o) {
  const cloud = _SPARK_STREAKS;
  let idx = -1;
  for (let n = 0; n < cloud.P.length; n++) {
    const c = (cloud.cursor + n) % cloud.P.length;
    if (!cloud.P[c].active) { idx = c; break; }
  }
  if (idx === -1) idx = cloud.cursor;
  cloud.cursor = (idx + 1) % cloud.P.length;
  const p = cloud.P[idx];
  if (!p.active) cloud.live++;
  p.active = true;
  p.x = x; p.y = y; p.z = z;
  p.vx = o.vx; p.vy = o.vy; p.vz = o.vz;
  p.life = 0; p.maxLife = o.maxLife;
  p.grav = o.grav; p.drag = o.drag; p.len = o.len; p.intensity = o.intensity;
  p.seed = Math.random() * 100;   // per-spark phase so the crackle flicker isn't synchronised
}

function _tickSparkStreaks(dt) {
  const cloud = _SPARK_STREAKS;
  if (cloud.live === 0) return;
  const { P, pos, col } = cloud;
  for (let i = 0; i < P.length; i++) {
    const p = P[i];
    const a = i * 6;
    if (!p.active) { col[a] = col[a+1] = col[a+2] = col[a+3] = col[a+4] = col[a+5] = 0; continue; }
    p.life += dt;
    if (p.life >= p.maxLife) {
      p.active = false; cloud.live--;
      col[a] = col[a+1] = col[a+2] = col[a+3] = col[a+4] = col[a+5] = 0;
      continue;
    }
    const drag = Math.max(0, 1 - p.drag * dt);
    p.vx *= drag; p.vz *= drag;
    p.vy = p.vy * drag + p.grav * dt;
    p.x += p.vx * dt; p.y += p.vy * dt; p.z += p.vz * dt;
    // Tail trails behind the head along the velocity direction; longer the faster it moves.
    const spd = Math.hypot(p.vx, p.vy, p.vz) || 1;
    const len = Math.min(0.6, p.len * spd);
    const tx = p.x - (p.vx / spd) * len, ty = p.y - (p.vy / spd) * len, tz = p.z - (p.vz / spd) * len;
    pos[a] = p.x; pos[a+1] = p.y; pos[a+2] = p.z;     // head
    pos[a+3] = tx; pos[a+4] = ty; pos[a+5] = tz;      // tail
    const lt = p.life / p.maxLife;
    // Temperature cooling: white-hot at birth → yellow → orange → deep red as it ages (g/b fall faster
    // than r). This is what reads as real hot metal rather than a flat-coloured streak.
    const tr = 1.0 - 0.15 * lt;
    const tg = Math.max(0, 0.92 - 0.85 * lt);
    const tb = Math.max(0, 0.62 - 0.7 * lt);
    // Crackle flicker — fast per-spark brightness shimmer so the sparks twinkle as they fly.
    const flick = 0.68 + 0.32 * Math.sin(p.life * 46 + p.seed);
    const b = Math.pow(1 - lt, 0.6) * p.intensity * flick;
    col[a] = tr * b; col[a+1] = tg * b; col[a+2] = tb * b;                       // head: bright/hot
    col[a+3] = tr * b * 0.15; col[a+4] = tg * b * 0.15; col[a+5] = tb * b * 0.15; // tail: dim
  }
  cloud.geo.attributes.position.needsUpdate = true;
  cloud.geo.attributes.color.needsUpdate = true;
}

function _emitImpactParticle(cloud, x, y, z, o) {
  // Find a free slot scanning from the cursor; if the pool is exhausted, recycle the cursor slot.
  let idx = -1;
  for (let n = 0; n < cloud.P.length; n++) {
    const c = (cloud.cursor + n) % cloud.P.length;
    if (!cloud.P[c].active) { idx = c; break; }
  }
  if (idx === -1) idx = cloud.cursor;
  cloud.cursor = (idx + 1) % cloud.P.length;
  const p = cloud.P[idx];
  if (!p.active) cloud.live++;
  p.active = true;
  p.x = x; p.y = y; p.z = z;
  p.vx = o.vx; p.vy = o.vy; p.vz = o.vz;
  p.life = 0; p.maxLife = o.maxLife;
  p.size0 = o.size0; p.sizeGrow = o.sizeGrow;
  p.drag = o.drag; p.grav = o.grav; p.fadePow = o.fadePow;
  p.alpha = o.alpha ?? 1;   // peak opacity (dust stays thin/hazy; sparks full)
  p.r = o.r; p.g = o.g; p.b = o.b;
}

// Per-material recipe. dust/spark are 0..1 amount multipliers; colours are linear-ish rgb triples.
const _IMPACT_RECIPE = {
  brick: { dust: 1.0, dustCol: [0.58, 0.34, 0.24], spark: 0 },   // dust only
  metal: { dust: 0,   dustCol: [0.50, 0.52, 0.55], spark: 1.0, sparkCol: [1.0, 0.62, 0.26] },  // sparks only
  crate: { dust: 0,   dustCol: [0.74, 0.56, 0.36], spark: 0 },   // neither
};

function _spawnImpactFX(position, material, scale = 1, breaking = true, forward = null) {
  if (!position) return;
  const r = _IMPACT_RECIPE[material] || _IMPACT_RECIPE.brick;
  const s = Math.max(0.4, Math.min(2.5, scale));   // clamp how much block size scales the burst (down for small parts, up for big)
  const dustN = Math.round((breaking ? 9 : 4) * r.dust * s);
  for (let i = 0; i < dustN; i++) {
    const ang = Math.random() * Math.PI * 2;
    const sp = (0.6 + Math.random() * 1.2) * s;
    _emitImpactParticle(_DUST_CLOUD, position.x, position.y, position.z, {
      vx: Math.cos(ang) * sp, vy: 0.5 + Math.random() * 1.2, vz: Math.sin(ang) * sp,
      maxLife: 0.5 + Math.random() * 0.4,
      size0: (0.18 + Math.random() * 0.16) * s, sizeGrow: 0.6 * s,
      drag: 2.2, grav: -0.4, fadePow: 1.6,
      alpha: 0.48,   // more present puff (lower transparency) — brick dust reads more solid
      r: r.dustCol[0], g: r.dustCol[1], b: r.dustCol[2],
    });
  }
  if (r.spark > 0) {
    const sparkN = Math.round((breaking ? 22 : 4) * r.spark * s);   // dense burst on a break; just a few flecks on a non-destroy hit
    // Spray axis: back OUT of the struck surface (opposite the punch) with an upward bias, so sparks read
    // as knocked off the face toward the player instead of a uniform radial ball. Falls back to straight-up.
    let axx = 0, axy = 1, axz = 0;
    if (forward) {
      const fl = Math.hypot(forward.x, forward.y, forward.z) || 1;
      axx = -forward.x / fl; axy = -forward.y / fl + 0.5; axz = -forward.z / fl;
      const al = Math.hypot(axx, axy, axz) || 1; axx /= al; axy /= al; axz /= al;
    }
    // Impact flash at the contact point — the bright "crack" the streaks fly out of.
    _spawnImpactFlash(position, s);
    const SPREAD = 0.7;   // cone width: 0 = laser-tight, ~1 = wide fan
    for (let i = 0; i < sparkN; i++) {
      let dx = axx + (Math.random() * 2 - 1) * SPREAD;
      let dy = axy + (Math.random() * 2 - 1) * SPREAD;
      let dz = axz + (Math.random() * 2 - 1) * SPREAD;
      const dl = Math.hypot(dx, dy, dz) || 1; dx /= dl; dy /= dl; dz /= dl;
      const sp = (4.0 + Math.random() * 6.0) * s;    // fast outward
      _emitSpark(position.x, position.y, position.z, {
        vx: dx * sp, vy: dy * sp, vz: dz * sp,
        maxLife: 0.22 + Math.random() * 0.32,        // travels and arcs before dying
        len: 0.05,          // streak length per unit speed (head-to-tail) — clamped at 0.6m in the tick
        grav: -16.0,        // heavy gravity so streaks arc down hard
        drag: 0.2,
        intensity: 1.9,     // >1 so dense overlapping heads clip to white-hot cores
      });
    }
  }
}

// Cosmetic coin STREAM for a PEER's smash — a volley of real coins (the same makeCoinGeo + gold
// currency material the game uses) fired from the observed player's FIST to the block they smashed,
// arcing + spinning, vanishing the instant they reach the target. Pooled meshes, NO physics and NO
// collection (Rich's rule: purely cosmetic, no floor clutter). The target is looked up on OUR client
// by netId so the coins fly into the block where IT actually is here, not where it was on the sender
// (cause-mode positions diverge). Pool is built lazily on first use so the coin texture/env map exist.
// Shared coin geometry + gold currency material, built lazily on first use (so the coin texture/env map
// exist). Reused by both the launch-volley pool and the charge-orbit pool below.
let _peerCoinGeo = null;
let _peerCoinMat = null;
function _ensurePeerCoinAssets() {
  if (_peerCoinGeo) return;
  _peerCoinGeo = makeCoinGeo(0.34);
  _peerCoinMat = new THREE.MeshStandardMaterial({
    color: 0xffe44a, map: getCoinBumpTexture(), emissive: _GOLD_COLOR, emissiveIntensity: 0.05,
    roughness: 0.28, metalness: 0.72, bumpMap: getCoinBumpTexture(), bumpScale: 0.02,
    envMap: springMetalEnvMap, envMapIntensity: 1.05,
  });
}

// Volley coins are BIGGER + GLOWIER than the orbit coins so they read from across the arena (part C).
// Separate assets so bumping the volley doesn't inflate the orbit ring around the fist.
let _peerVolleyGeo = null, _peerVolleyMat = null;
function _ensurePeerVolleyAssets() {
  if (_peerVolleyGeo) return;
  _peerVolleyGeo = makeCoinGeo(0.5);
  _peerVolleyMat = new THREE.MeshStandardMaterial({
    color: 0xffe44a, map: getCoinBumpTexture(), emissive: _GOLD_COLOR, emissiveIntensity: 0.45,  // glow so it pops at distance
    roughness: 0.28, metalness: 0.72, bumpMap: getCoinBumpTexture(), bumpScale: 0.02,
    envMap: springMetalEnvMap, envMapIntensity: 1.05,
  });
}
const _PEER_COIN_POOL = [];
let _peerCoinCursor = 0;
let _peerCoinReady = false;
function _ensurePeerCoinPool() {
  if (_peerCoinReady) return;
  _peerCoinReady = true;
  _ensurePeerVolleyAssets();
  const geo = _peerVolleyGeo, matl = _peerVolleyMat;
  for (let i = 0; i < 96; i++) {
    const m = new THREE.Mesh(geo, matl);
    m.visible = false;
    m.frustumCulled = false;
    scene.add(m);
    _PEER_COIN_POOL.push({ mesh: m, active: false, x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, speed: 0,
      dist: 0, targetDist: 0, homing: false, from: null, life: 0, maxLife: 0, delay: 0, rx: 0, ry: 0, rz: 0 });
  }
}
// Fire a VOLLEY of `count` coins from the peer's fist (fx,fy,fz). COSMETIC ("show that they fired"), tuned
// for an OBSERVER across the arena, NOT the shooter's real 25-106 m/s launch (a sub-second invisible blur
// from a distance). STRAIGHT LINE (matching what the shooter sees — no arc), at a fixed legible speed, and
// EMITTED STAGGERED so it reads as a flowing stream not one puff. Two aim modes:
//   • `target` (a THREE.Vector3 block position, when the shooter was locked) → coins home to it, fade on hit.
//   • `dir` (a unit direction, when unlocked) → coins fly straight that way and fade after maxLife if they
//     hit nothing; _peerVolleyRedirect can course-correct them onto a block the shooter actually breaks.
const _PEER_VOLLEY_SPEED = 72;      // fixed legible straight-line speed (m/s). Doubled 2026-07-04 — the old
                                    // 36 read as sluggish from across the arena; the real fired coins are fast.
const _PEER_VOLLEY_MAXLIFE = 1.4;   // unlocked coins that hit nothing fade after this (they "went into nothing")
const _peerRecallPos = new THREE.Vector3();
// The observed player started sucking coins AGAIN → recall their still-flying volley: steer each of that
// peer's in-flight coins back toward the sucking fist and fade them out (reads as "pulled back in"). Fixes
// coins that kept sailing on-screen after the shooter had already started gathering the next storm.
function _peerVolleyRecall(from, hand) {
  if (!_peerCoinReady || from == null) return;
  for (const c of _PEER_COIN_POOL) {
    if (!c.active || c.from !== from) continue;
    c.recall = true;
    c.recallHand = hand;
  }
}
function _firePeerCoinVolley(fx, fy, fz, target, dir, count, from) {
  _ensurePeerCoinPool();
  const n = Math.max(1, Math.min(count, _PEER_COIN_POOL.length));
  // Fire as a near-simultaneous BURST (one "volley unit"), matching how the shooter actually flings them,
  // instead of a trickle. A hair of stagger (<=~50ms total) keeps it from looking like one rigid blob;
  // the per-coin position + direction spread does the rest.
  const gap = Math.min(0.008, 0.05 / n);
  for (let k = 0; k < n; k++) {
    const c = _PEER_COIN_POOL[_peerCoinCursor];
    _peerCoinCursor = (_peerCoinCursor + 1) % _PEER_COIN_POOL.length;
    c.x = fx + (Math.random() - 0.5) * 0.3;
    c.y = fy + (Math.random() - 0.5) * 0.3;
    c.z = fz + (Math.random() - 0.5) * 0.3;
    let ax, ay, az;
    if (target) {   // aim straight at the block with a small splash spread
      ax = target.x + (Math.random() - 0.5) * 1.4 - c.x;
      ay = target.y + (Math.random() - 0.5) * 1.4 - c.y;
      az = target.z + (Math.random() - 0.5) * 1.4 - c.z;
    } else {        // fly along the aim direction with a slight spread
      ax = dir.x + (Math.random() - 0.5) * 0.09;
      ay = dir.y + (Math.random() - 0.5) * 0.09;
      az = dir.z + (Math.random() - 0.5) * 0.09;
    }
    const len = Math.hypot(ax, ay, az) || 1;
    c.vx = (ax / len) * _PEER_VOLLEY_SPEED;
    c.vy = (ay / len) * _PEER_VOLLEY_SPEED;
    c.vz = (az / len) * _PEER_VOLLEY_SPEED;
    c.speed = _PEER_VOLLEY_SPEED;
    c.dist = 0;
    c.homing = !!target;
    c.targetDist = target ? Math.hypot(target.x - c.x, target.y - c.y, target.z - c.z) : 0;
    c.from = from;
    c.life = 0;
    c.maxLife = _PEER_VOLLEY_MAXLIFE;
    c.delay = k * gap;                          // staggered emission
    c.recall = false; c.fade = 1;               // recall-and-fade state (set if the shooter re-sucks)
    c.active = true;
    c.mesh.visible = false;                     // hidden until its delay elapses
    c.mesh.scale.setScalar(1);                  // reset (recall shrinks the mesh; this coin may be reused)
    c.rx = (Math.random() * 2 - 1) * 14;
    c.ry = (Math.random() * 2 - 1) * 14;
    c.rz = (Math.random() * 2 - 1) * 14;
  }
}
// A small gold flash + tinkle where a coin lands (hit confirmed). Throttled so a stream of arrivals reads
// as a few splashes, not one per coin.
let _lastPeerVolleyImpactMs = 0;
const _peerVolleyImpactPos = new THREE.Vector3();
function _peerVolleyImpact(x, y, z) {
  const nowMs = performance.now();
  if (nowMs - _lastPeerVolleyImpactMs < 55) return;
  _lastPeerVolleyImpactMs = nowMs;
  _peerVolleyImpactPos.set(x, y, z);
  _spawnImpactFlash(_peerVolleyImpactPos, 0.7);
  if (audioCtx) playCoinTinkle(3, _peerVolleyImpactPos);
}
// Course-correct: a block just got hit/destroyed by peer `from` — steer that peer's in-flight, non-homing
// volley coins that are heading roughly toward it onto it, so they converge + fade on arrival (hit
// confirmed) instead of sailing off into nothing. Only same-peer coins already ahead of the block.
const _PEER_VOLLEY_HOMING_DOT = 0.25;   // ~75° cone: only redirect coins already pointed near the block
function _peerVolleyRedirect(from, bx, by, bz) {
  if (!_peerCoinReady || from == null) return;
  for (const c of _PEER_COIN_POOL) {
    if (!c.active || c.homing || c.from !== from) continue;
    if (c.life < c.delay) continue;                   // not emitted yet
    const dx = bx - c.x, dy = by - c.y, dz = bz - c.z;
    const dlen = Math.hypot(dx, dy, dz) || 1;
    if ((c.vx * dx + c.vy * dy + c.vz * dz) / (c.speed * dlen) < _PEER_VOLLEY_HOMING_DOT) continue;
    c.vx = (dx / dlen) * c.speed;
    c.vy = (dy / dlen) * c.speed;
    c.vz = (dz / dlen) * c.speed;
    c.homing = true;
    c.targetDist = c.dist + dlen;   // arrive after covering the remaining gap, then fade
  }
}
function _updatePeerCoins(dt) {
  if (!_peerCoinReady) return;
  for (const c of _PEER_COIN_POOL) {
    if (!c.active) continue;
    // Recall: the shooter re-started sucking → curve back toward their fist and shrink-fade out.
    if (c.recall) {
      if (_mp && _mp.getFistPos && _mp.getFistPos(c.from, c.recallHand, _peerRecallPos)) {
        const dx = _peerRecallPos.x - c.x, dy = _peerRecallPos.y - c.y, dz = _peerRecallPos.z - c.z;
        const dl = Math.hypot(dx, dy, dz) || 1;
        const rs = c.speed * 1.4;   // pulled back a bit faster than it flew out
        c.x += (dx / dl) * rs * dt; c.y += (dy / dl) * rs * dt; c.z += (dz / dl) * rs * dt;
        c.fade -= dt / 0.3;
        if (c.fade <= 0 || dl < 0.5) { c.active = false; c.mesh.visible = false; c.mesh.scale.setScalar(1); continue; }
        c.mesh.visible = true;
        c.mesh.position.set(c.x, c.y, c.z);
        c.mesh.scale.setScalar(Math.max(0.05, c.fade));
        c.mesh.rotation.x += c.rx * dt; c.mesh.rotation.y += c.ry * dt; c.mesh.rotation.z += c.rz * dt;
      } else {
        c.active = false; c.mesh.visible = false; c.mesh.scale.setScalar(1);   // no fist to return to — just drop
      }
      continue;
    }
    c.life += dt;
    if (c.life < c.delay) { c.mesh.visible = false; continue; }   // not emitted yet (staggered stream)
    c.x += c.vx * dt; c.y += c.vy * dt; c.z += c.vz * dt;
    c.dist += c.speed * dt;
    // Homing (locked, or course-corrected onto a hit block): fade when it reaches the block = hit confirmed.
    if (c.homing && c.dist >= c.targetDist) { c.active = false; c.mesh.visible = false; _peerVolleyImpact(c.x, c.y, c.z); continue; }
    // Otherwise fade after maxLife (unlocked shot that hit nothing — "went into nothing").
    if (c.life - c.delay >= c.maxLife) { c.active = false; c.mesh.visible = false; continue; }
    c.mesh.visible = true;
    c.mesh.position.set(c.x, c.y, c.z);
    c.mesh.rotation.x += c.rx * dt;
    c.mesh.rotation.y += c.ry * dt;
    c.mesh.rotation.z += c.rz * dt;
  }
}

// Charge-orbit ring: a cosmetic ring of coins circling a charging peer's fist, mirroring the coins
// they've gathered. Driven by throttled `orb` updates {count, charge} per fist; auto-clears if updates
// stop (cancel / packet loss) and is cleared instantly when the volley fires ('cs'). Reuses the coin
// model. Separate pool from the volley so the two never fight over slots.
const _PEER_ORBIT_POOL = [];
let _peerOrbitReady = false;
function _ensurePeerOrbitPool() {
  if (_peerOrbitReady) return;
  _peerOrbitReady = true;
  _ensurePeerCoinAssets();
  for (let i = 0; i < 48; i++) {
    const m = new THREE.Mesh(_peerCoinGeo, _peerCoinMat);
    m.visible = false;
    m.frustumCulled = false;
    scene.add(m);
    _PEER_ORBIT_POOL.push(m);
  }
}
// Per-fist orbit state set from `orb` messages. `until` auto-expires the ring if updates stop.
// Per-peer charge-gather ring state, keyed by the sender id (msg.from). Each entry is [leftHand, rightHand].
const _peerOrbit = new Map();
function _peerOrbitFor(from) {
  // `from` may be undefined against an older relay that doesn't stamp it; the Map keys on it fine and mp.js
  // maps undefined -> its SOLO peer, so the ring still works (as single-peer) until the relay is upgraded.
  let o = _peerOrbit.get(from);
  if (!o) { o = [{ count: 0, charge: 0, until: 0 }, { count: 0, charge: 0, until: 0 }]; _peerOrbit.set(from, o); }
  return o;
}
let _peerOrbitAngle = 0;
const _orbFwd = new THREE.Vector3();
const _orbRight = new THREE.Vector3();
const _orbUp = new THREE.Vector3(0, 1, 0);
const _orbShimmerPos = new THREE.Vector3();
function _updatePeerOrbit(dt) {
  if (!_peerOrbitReady) return;
  _peerOrbitAngle += dt * 2.0;   // ring spin
  const now = performance.now();
  let used = 0;
  for (const [from, o] of _peerOrbit) {
    for (let f = 0; f < 2; f++) {
      const slot = o[f];
      if (slot.count > 0 && now > slot.until) slot.count = 0;   // updates stopped → clear (cancel / loss backstop)
      // Ease a DISPLAYED count toward the real one so coins gather in / dissipate out smoothly instead of
      // snapping to full count (the "arrive or leave orbit instantly" feel).
      if (slot.shown === undefined) slot.shown = 0;
      slot.shown += (slot.count - slot.shown) * Math.min(1, dt * 7);
      if (slot.count === 0 && slot.shown < 0.06) { slot.shown = 0; continue; }
      if (slot.shown < 0.06) continue;
      if (!(_mp && _mp.getFistPos && _mp.getFistPos(from, f, _mpFxPos))) continue;
      if (!(_mp.getFacing && _mp.getFacing(from, _orbFwd))) continue;
      // Match the real orbit: centre sits SUCK_ORBIT_FORWARD ahead of the fist along the peer's facing,
      // offset SUCK_ORBIT_SIDEWAYS to that fist's side — so the ring floats in front, not inside the body.
      _orbRight.crossVectors(_orbFwd, _orbUp).normalize();
      const sideSign = f === 0 ? -1 : 1;
      const cx = _mpFxPos.x + _orbFwd.x * SUCK_ORBIT_FORWARD + sideSign * _orbRight.x * SUCK_ORBIT_SIDEWAYS;
      const cy = _mpFxPos.y + _orbFwd.y * SUCK_ORBIT_FORWARD + sideSign * _orbRight.y * SUCK_ORBIT_SIDEWAYS;
      const cz = _mpFxPos.z + _orbFwd.z * SUCK_ORBIT_FORWARD + sideSign * _orbRight.z * SUCK_ORBIT_SIDEWAYS;
      const radius = SUCK_ORBIT_RADIUS;
      const n = Math.min(Math.max(1, Math.round(slot.shown)), _PEER_ORBIT_POOL.length - used);
      for (let i = 0; i < n; i++, used++) {
        const m = _PEER_ORBIT_POOL[used];
        const ang = _peerOrbitAngle + (i / Math.max(1, n)) * Math.PI * 2;
        m.visible = true;
        m.position.set(cx + Math.cos(ang) * radius, cy + Math.sin(ang * 1.7 + i) * 0.12, cz + Math.sin(ang) * radius);
        m.rotation.x += dt * 6;
        m.rotation.y += dt * 5;
      }
      // Sparse "coins circling" shimmer while the ring is genuinely HELD (count>0, not the fire/cancel
      // fade), spatialized at the ring — so the observer hears the held coins, not just the transients.
      if (audioCtx && slot.count > 0 && slot.shown > 0.5) {
        if (now - (slot.lastShimmerMs || 0) > 300) {
          slot.lastShimmerMs = now;
          _orbShimmerPos.set(cx, cy, cz);
          _playPeerOrbitShimmer(_orbShimmerPos);
        }
      }
      if (used >= _PEER_ORBIT_POOL.length) break;
    }
    if (used >= _PEER_ORBIT_POOL.length) break;
  }
  for (let i = used; i < _PEER_ORBIT_POOL.length; i++) _PEER_ORBIT_POOL[i].visible = false;
}

function _tickImpactCloud(cloud, dt) {
  if (cloud.live === 0) return;   // nothing alive → skip integration + buffer upload entirely
  const { P, pos, aColor, aAlpha, aSize } = cloud;
  for (let i = 0; i < P.length; i++) {
    const p = P[i];
    if (!p.active) { aAlpha[i] = 0; continue; }
    p.life += dt;
    if (p.life >= p.maxLife) { p.active = false; cloud.live--; aAlpha[i] = 0; continue; }
    const drag = Math.max(0, 1 - p.drag * dt);
    p.vx *= drag; p.vz *= drag;
    p.vy = p.vy * drag + p.grav * dt;
    p.x += p.vx * dt; p.y += p.vy * dt; p.z += p.vz * dt;
    const lt = p.life / p.maxLife;
    pos[i * 3] = p.x; pos[i * 3 + 1] = p.y; pos[i * 3 + 2] = p.z;
    aColor[i * 3] = p.r; aColor[i * 3 + 1] = p.g; aColor[i * 3 + 2] = p.b;
    aAlpha[i] = Math.pow(1 - lt, p.fadePow) * p.alpha;
    aSize[i] = Math.max(0.001, p.size0 + p.sizeGrow * lt);
  }
  cloud.geo.attributes.position.needsUpdate = true;
  cloud.geo.attributes.aColor.needsUpdate = true;
  cloud.geo.attributes.aAlpha.needsUpdate = true;
  cloud.geo.attributes.aSize.needsUpdate = true;
}

// Impact flash — a brief bright additive burst at the contact point, the "crack" the sparks fly out of.
// Small pre-built pool of additive sprites reused round-robin; each scales up + fades over ~110ms.
function _makeFlashTex() {
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const ctx = c.getContext('2d');
  const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.18, 'rgba(255,244,210,0.85)');
  g.addColorStop(0.5, 'rgba(255,200,120,0.25)');
  g.addColorStop(1, 'rgba(255,170,90,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 128, 128);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
const _FLASH_POOL = [];
const _FLASH_TEX = _makeFlashTex();
for (let i = 0; i < 12; i++) {
  const mat = new THREE.SpriteMaterial({
    map: _FLASH_TEX, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
  });
  const sprite = new THREE.Sprite(mat);
  sprite.visible = false;
  scene.add(sprite);
  _FLASH_POOL.push({ sprite, active: false, life: 0, maxLife: 0.11, base: 0.5 });
}
let _flashCursor = 0;
let _flashLive = 0;
function _spawnImpactFlash(position, scale) {
  let slot = null;
  for (let n = 0; n < _FLASH_POOL.length; n++) {
    const f = _FLASH_POOL[(_flashCursor + n) % _FLASH_POOL.length];
    if (!f.active) { slot = f; break; }
  }
  if (!slot) slot = _FLASH_POOL[_flashCursor];
  _flashCursor = (_flashCursor + 1) % _FLASH_POOL.length;
  if (!slot.active) _flashLive++;
  slot.active = true;
  slot.life = 0;
  slot.base = 0.55 * scale;
  slot.sprite.position.copy(position);
  slot.sprite.visible = true;
}
function _updateFlashes(dt) {
  if (_flashLive === 0) return;
  for (let i = 0; i < _FLASH_POOL.length; i++) {
    const f = _FLASH_POOL[i];
    if (!f.active) continue;
    f.life += dt;
    if (f.life >= f.maxLife) { f.active = false; _flashLive--; f.sprite.visible = false; continue; }
    const t = f.life / f.maxLife;
    f.sprite.material.opacity = 1 - t;          // fade out
    const sc = f.base * (0.7 + t * 1.1);        // pop outward as it fades
    f.sprite.scale.set(sc, sc, sc);
  }
}

function _updateImpactFX(dt) {
  _tickImpactCloud(_DUST_CLOUD, dt);
  _tickSparkStreaks(dt);
  _updateFlashes(dt);
}

// =========================================================
// Juice — camera shake, flash, ring, sound, debris
// =========================================================

let shakeEndTime = 0;
let shakeMag = 0;
function cameraShake(duration, magnitude) {
  shakeEndTime = performance.now() + duration * 1000;
  shakeMag = magnitude;
}

const flashEl = document.getElementById('flash');
// flashScreen DISABLED — some players found the per-impact full-screen white pulse
// headache-inducing, especially during fast tap chains and chip-storm releases. Kept
// as a no-op so existing callsites (applyHit, applyProjectileHit shockwave) compile
// without changes. Remove entirely after this decision settles.
function flashScreen(/* duration = 0.12 */) {}

function impactRing(position, forward, block = null) {
  if (_impactFlashV2 && block) {
    // V2: pixel-art ring decal flat to the block face. Smaller peak + faster fade than V1
    // so it reads as a brief flash rather than a billowing shockwave. Reuses the contact
    // ring's texture for visual consistency between "armed" and "fired" states.
    const geo = new THREE.PlaneGeometry(0.4, 0.4);
    const mat = new THREE.MeshBasicMaterial({
      map: _contactRingMat.map,
      color: 0xffe080,
      transparent: true,
      opacity: 0.55,
      blending: THREE.AdditiveBlending,
      depthTest: true,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const mesh = new THREE.Mesh(geo, mat);
    const normal = _faceNormalAt(block, position);
    mesh.position.copy(position).addScaledVector(normal, 0.014);
    mesh.lookAt(mesh.position.clone().add(normal));
    scene.add(mesh);

    const start = performance.now();
    const duration = 180;
    function stepV2() {
      const t = (performance.now() - start) / duration;
      if (t >= 1) {
        scene.remove(mesh);
        geo.dispose();
        mat.dispose();
        return;
      }
      const s = 0.7 + t * 2.0;  // tighter pop — peak ~2.7×
      mesh.scale.set(s, s, s);
      mat.opacity = 0.55 * (1 - t);
      requestAnimationFrame(stepV2);
    }
    stepV2();
    return;
  }

  // V1: original expanding yellow billboard ring.
  const geo = new THREE.RingGeometry(0.15, 0.22, 32);
  const mat = new THREE.MeshBasicMaterial({
    color: 0xffe080,
    transparent: true,
    opacity: 0.85,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
  const ring = new THREE.Mesh(geo, mat);
  ring.position.copy(position);
  ring.lookAt(position.clone().add(forward));
  scene.add(ring);

  const start = performance.now();
  const duration = 180;
  function step() {
    const t = (performance.now() - start) / duration;
    if (t >= 1) {
      scene.remove(ring);
      geo.dispose();
      mat.dispose();
      return;
    }
    const s = 0.5 + t * 9; // expand outward
    ring.scale.set(s, s, s);
    mat.opacity = 0.85 * (1 - t);
    requestAnimationFrame(step);
  }
  step();
}

// Pitch ramp ported from PunchPitchClient.SmashPitchMultiplier.
// Source has FINAL_SEMITONES=5 so the break "releases" up the scale — feels chirpy for rock smashes
// where most blocks are 1-hit. We drop final to 0 so peak pitch sits at base, not above it.
const FINAL_SEMITONES = 0;
const START_OCTAVES_BELOW = 2;
const MIN_PITCH_MULT = 0.55;
function smashPitchMultiplier(hitIndex, hitsToBreak) {
  hitsToBreak = Math.max(1, hitsToBreak);
  hitIndex = Math.max(1, Math.min(hitIndex, hitsToBreak));
  const semisToMult = (s) => Math.pow(2, s / 12);
  const multToSemis = (m) => 12 * Math.log2(m);

  const endMult = semisToMult(FINAL_SEMITONES);
  if (hitsToBreak === 1) return endMult;
  if (hitIndex === hitsToBreak) return endMult;

  const patterns = { 2: -5, 3: -7, 4: -9, 5: -12, 6: -12 };
  const startBase = patterns[hitsToBreak] ?? -24;
  const startSemis = startBase - 12 * Math.max(0, START_OCTAVES_BELOW);
  const startMult = Math.max(MIN_PITCH_MULT, semisToMult(startSemis));
  const startSemisClamped = multToSemis(startMult);

  const t = (hitIndex - 1) / (hitsToBreak - 1);
  const semis = startSemisClamped + (FINAL_SEMITONES - startSemisClamped) * t;
  return Math.max(MIN_PITCH_MULT, semisToMult(semis));
}

// BRICK SMASH — noise-dominant chiptune patch (rocky shatter).
//   Crack:  highpassed noise click (initial fracture transient)
//   Body:   3-bit quantized noise through a swept resonant lowpass — the main rock-shatter sound
//   Sub:    small triangle thump for impact weight
//   Grains: random short noise bursts in the tail emulating clattering rock fragments
//   Rumble (final only): longer low-mid noise tail for the satisfying break-open
let audioCtx = null;

// Web Audio listener — tracks camera each frame so positioned sounds spatialise correctly.
const _listenerForwardTmp = new THREE.Vector3();
function updateAudioListener() {
  if (!audioCtx) return;
  const L = audioCtx.listener;
  camera.getWorldDirection(_listenerForwardTmp);
  if (L.positionX) {
    L.positionX.value = camera.position.x;
    L.positionY.value = camera.position.y;
    L.positionZ.value = camera.position.z;
    L.forwardX.value = _listenerForwardTmp.x;
    L.forwardY.value = _listenerForwardTmp.y;
    L.forwardZ.value = _listenerForwardTmp.z;
    L.upX.value = 0;
    L.upY.value = 1;
    L.upZ.value = 0;
  } else if (L.setPosition) {
    L.setPosition(camera.position.x, camera.position.y, camera.position.z);
    L.setOrientation(_listenerForwardTmp.x, _listenerForwardTmp.y, _listenerForwardTmp.z, 0, 1, 0);
  }
}

// Audio graph: SFX → _sfxBus (volume control) → _masterBus (compressor) → destination.
// Music → _masterBus directly (its own _musicGain handles music level).
// Reverb is a parallel send from SFX through a convolver back into _sfxBus.
let _masterBus = null;
let _sfxBus = null;
let _reverbConvolver = null;
const _DEFAULT_SFX_VOLUME = 1.0;
function _ensureAudioGraph() {
  if (!audioCtx || _masterBus) return;
  const comp = audioCtx.createDynamicsCompressor();
  comp.threshold.value = -16;
  comp.knee.value = 8;
  comp.ratio.value = 4;
  comp.attack.value = 0.005;
  comp.release.value = 0.10;
  comp.connect(audioCtx.destination);
  _masterBus = comp;
  if (_audioMuted) { try { comp.disconnect(audioCtx.destination); } catch { /* noop */ } }  // honor a mute set before audio existed

  // SFX volume bus — all sound effects route through this. Music bypasses it (uses _musicGain).
  _sfxBus = audioCtx.createGain();
  _sfxBus.gain.value = _DEFAULT_SFX_VOLUME;
  _sfxBus.connect(_masterBus);

  // Procedural impulse response — stereo noise with exponential decay (~0.7s tail).
  const irLen = Math.floor(audioCtx.sampleRate * 0.7);
  const ir = audioCtx.createBuffer(2, irLen, audioCtx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const data = ir.getChannelData(ch);
    for (let i = 0; i < irLen; i++) {
      const decay = Math.exp(-i / (irLen * 0.32));
      data[i] = (Math.random() * 2 - 1) * decay;
    }
  }
  _reverbConvolver = audioCtx.createConvolver();
  _reverbConvolver.buffer = ir;
  const wet = audioCtx.createGain();
  wet.gain.value = 0.07;  // subtle — adds spatial body without drowning the source
  _reverbConvolver.connect(wet).connect(_sfxBus);  // wet goes through SFX bus so volume scales together
}

// Active scale frequencies (the song's key), octaves 1-7. Used to tune SFX pitches to the music.
// Initialised to A minor; swapped per song via applySong() to the song's scaleHz.
let _activeScaleHz = [
  27.50, 30.87, 32.70, 36.71, 41.20, 43.65, 49.00,           // octave 1 (A0 root)
  55.00, 61.74, 65.41, 73.42, 82.41, 87.31, 98.00,           // octave 2
  110.00, 123.47, 130.81, 146.83, 164.81, 174.61, 196.00,    // octave 3 (A2 root)
  220.00, 246.94, 261.63, 293.66, 329.63, 349.23, 392.00,    // octave 4
  440.00, 493.88, 523.25, 587.33, 659.25, 698.46, 783.99,    // octave 5
  880.00, 987.77, 1046.50, 1174.66, 1318.51, 1396.91, 1567.98, // octave 6
  1760.00, 1975.53, 2093.00, 2349.32, 2637.02, 2793.83, 3135.96, // octave 7
];

// Pick a random scale note in [min, max]. Falls back to the midpoint if no scale notes are in range.
function _pickScaleNote(min, max) {
  const candidates = _activeScaleHz.filter(f => f >= min && f <= max);
  if (candidates.length === 0) return (min + max) / 2;
  return candidates[Math.floor(Math.random() * candidates.length)];
}

// Snap an arbitrary frequency to the nearest scale note (log distance for perceptual evenness).
function _snapScale(freq) {
  let best = _activeScaleHz[0], bestDist = Math.abs(Math.log2(freq / best));
  for (const n of _activeScaleHz) {
    const d = Math.abs(Math.log2(freq / n));
    if (d < bestDist) { best = n; bestDist = d; }
  }
  return best;
}

// Hash a world position to a deterministic scale note in [lowFreq, highFreq]. Same block always gets
// the same note, so multi-hit smashes land consistently and chains of different blocks read as a melody.
function _hashPositionToScale(pos, lowFreq, highFreq) {
  const x = Math.floor((pos.x + 1000) * 100) | 0;
  const y = Math.floor((pos.y + 1000) * 100) | 0;
  const z = Math.floor((pos.z + 1000) * 100) | 0;
  // 32-bit mix — cheap and stable.
  let h = (x * 73856093) ^ (y * 19349663) ^ (z * 83492791);
  h = (h ^ (h >>> 13)) * 0x5bd1e995;
  h = (h ^ (h >>> 15)) >>> 0;
  const candidates = _activeScaleHz.filter(f => f >= lowFreq && f <= highFreq);
  return candidates[h % candidates.length];
}

// Returns the set of scale degrees (0-6, indices into _activeScaleHz mod 7) that smash
// pitches should be drawn from RIGHT NOW based on the song's current chord — the chord
// tones plus the song's 2nd and 6th scale degrees as melodic neighbors. The music loop
// is 8 bars / 4 chords, each chord 2 bars long. Returns null when the music isn't running
// (smashes then fall back to the full scale).
function _currentChordRichDegrees() {
  if (!_musicStarted || !audioCtx || !_activeSong || !_activeSong.chords) return null;
  const elapsed = audioCtx.currentTime - _musicLoopStartTime;
  if (elapsed < 0) return null;
  const loopElapsed = ((elapsed % MUSIC_LOOP_DURATION) + MUSIC_LOOP_DURATION) % MUSIC_LOOP_DURATION;
  // Walk the chord schedule to find which chord owns the current loop position.
  // Variable-length chords mean we can't just divide by a fixed chord-bar count.
  const schedule = _chordSchedule();
  let chord = null;
  for (const entry of schedule) {
    const start = entry.startBar * MUSIC_BAR;
    const end = start + entry.bars * MUSIC_BAR;
    if (loopElapsed >= start && loopElapsed < end) { chord = entry.chord; break; }
  }
  if (!chord && schedule.length > 0) chord = schedule[schedule.length - 1].chord;
  if (!chord) return null;
  const degrees = new Set([1, 5]);  // scale 2nd + 6th — always present as melodic neighbors
  // Engine uses shorthand chord field names r/t/f/o (root, third, fifth, octave).
  // The chord-rich smash filter previously read .root/.third/etc. and got `undefined`,
  // which collapsed the candidate pool to scale-2nd+6th only — that was the "duller" feel.
  for (const toneHz of _chordTones(chord)) {
    if (typeof toneHz !== 'number') continue;
    let bestI = 0, bestDist = Infinity;
    for (let i = 0; i < _activeScaleHz.length; i++) {
      const d = Math.abs(Math.log2(_activeScaleHz[i] / toneHz));
      if (d < bestDist) { bestDist = d; bestI = i; }
    }
    degrees.add(bestI % 7);
  }
  return degrees;
}

// Position-hashed pitch like _hashPositionToScale, but the candidate pool is filtered to
// scale degrees that fit the song's CURRENT chord (chord tones + scale 2nd/6th). As the
// song moves through its progression, the same block hits a different (but always
// chord-locked) note — feels like the smashes are joining the chord change. Falls back
// to the full scale if music hasn't started or chord lookup fails.
function _hashPositionToChordRich(pos, lowFreq, highFreq) {
  // Admin toggle off → original single-octave bright behavior.
  if (!_chordRichSmashes) return _hashPositionToScale(pos, lowFreq, highFreq);
  const allowed = _currentChordRichDegrees();
  const candidates = [];
  for (let i = 0; i < _activeScaleHz.length; i++) {
    const f = _activeScaleHz[i];
    if (f < lowFreq || f > highFreq) continue;
    if (allowed && !allowed.has(i % 7)) continue;
    candidates.push(f);
  }
  if (candidates.length === 0) return _hashPositionToScale(pos, lowFreq, highFreq);
  const x = Math.floor((pos.x + 1000) * 100) | 0;
  const y = Math.floor((pos.y + 1000) * 100) | 0;
  const z = Math.floor((pos.z + 1000) * 100) | 0;
  let h = (x * 73856093) ^ (y * 19349663) ^ (z * 83492791);
  h = (h ^ (h >>> 13)) * 0x5bd1e995;
  h = (h ^ (h >>> 15)) >>> 0;
  return candidates[h % candidates.length];
}

// Helper: returns the node a sound's master gain should connect to. Spatialised sounds get a
// PannerNode that fans out to both SFX bus (dry) and reverb convolver (wet send). Non-positional
// sounds connect straight to the SFX bus. Both routes get the SFX volume slider applied.
function _spatialDestination(position) {
  if (!audioCtx) return null;
  _ensureAudioGraph();
  if (!position) return _sfxBus;
  const panner = audioCtx.createPanner();
  // equalpower (stereo L/R balance) is ~free; HRTF (3D convolution) is what was overrunning
  // the audio thread on dense L10 cascades and producing the "stops for a few seconds" glitch.
  panner.panningModel = 'equalpower';
  panner.distanceModel = 'inverse';
  panner.refDistance = 3;
  panner.maxDistance = 100;
  panner.rolloffFactor = 1.4;
  if (panner.positionX) {
    panner.positionX.value = position.x;
    panner.positionY.value = position.y;
    panner.positionZ.value = position.z;
  } else if (panner.setPosition) {
    panner.setPosition(position.x, position.y, position.z);
  }
  panner.connect(_sfxBus);
  panner.connect(_reverbConvolver);
  return panner;
}

function _playBrickSmash(pitchMult, isFinal, position, startTime) {
  if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)({ latencyHint: 'interactive' });
  const t0 = startTime !== undefined ? startTime : audioCtx.currentTime;
  const sr = audioCtx.sampleRate;

  const dest = _spatialDestination(position);
  const master = audioCtx.createGain();
  master.gain.value = (isFinal ? 0.85 : 0.68) * _brickCrush.level;
  // Shared high-pass after master so it cuts low end on both dry and crushed paths.
  const hpf = audioCtx.createBiquadFilter();
  hpf.type = 'highpass';
  hpf.frequency.value = _brickCrush.hpf;
  hpf.Q.value = 0.7;
  master.connect(hpf);
  // Parallel routing: dry path + crushed path mixed by _brickCrush.mix (0=clean, 1=fully crushed).
  const dry = audioCtx.createGain();
  dry.gain.value = 1 - _brickCrush.mix;
  hpf.connect(dry).connect(dest);
  const drive = audioCtx.createGain();
  drive.gain.value = _brickCrush.drive;
  const shaper = audioCtx.createWaveShaper();
  shaper.curve = _getBrickCrushCurve(_brickCrush.bits);
  const tone = audioCtx.createBiquadFilter();
  tone.type = 'lowpass';
  tone.frequency.value = _brickCrush.tone;
  tone.Q.value = 0.7;
  const wet = audioCtx.createGain();
  wet.gain.value = _brickCrush.mix;
  hpf.connect(drive).connect(shaper).connect(tone).connect(wet).connect(dest);

  // 3-bit quantized noise buffer — chunky, gritty samples
  function noiseBuffer(durationSec, bitDepth = 3) {
    const len = Math.max(1, Math.floor(sr * durationSec));
    const buf = audioCtx.createBuffer(1, len, sr);
    const data = buf.getChannelData(0);
    const levels = Math.pow(2, bitDepth) - 1;
    const step = 2 / levels;
    for (let i = 0; i < len; i++) {
      const env = 1 - i / len;
      const v = (Math.random() * 2 - 1) * env;
      data[i] = Math.round(v / step) * step;
    }
    return buf;
  }

  // 1a) Tonal PUNCH — triangle thump at the impact frame, the "fist meets stone" tone
  // Per-block scale-tuned punch tone — different bricks land on different scale notes.
  // Range tightened to a single octave (165-330 Hz, ~F3-E4) so chained brick smashes never
  // jump more than 7 scale steps. Pool is further filtered to the song's current chord
  // tones plus scale 2nd/6th, so smashes harmonize with whichever chord is playing now.
  const brickBase = position ? _hashPositionToChordRich(position, 165, 330) : 174.61;
  {
    const punch = audioCtx.createOscillator();
    const env = audioCtx.createGain();
    punch.type = 'triangle';
    punch.frequency.setValueAtTime(_snapScale(brickBase * pitchMult), t0);
    punch.frequency.exponentialRampToValueAtTime(_snapScale(brickBase * 0.5 * pitchMult), t0 + 0.035);
    env.gain.setValueAtTime(0.001, t0);
    env.gain.linearRampToValueAtTime(0.85, t0 + 0.003);
    env.gain.exponentialRampToValueAtTime(0.001, t0 + 0.06);
    punch.connect(env).connect(master);
    punch.start(t0); punch.stop(t0 + 0.07);
  }

  // 1b) Noise CRACK — broadband click with the punch, gives the impact its "edge"
  {
    const len = 0.025;
    const src = audioCtx.createBufferSource();
    src.buffer = noiseBuffer(len, 3);
    const lp = audioCtx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 700 * pitchMult;
    lp.Q.value = 1.5;
    const env = audioCtx.createGain();
    env.gain.setValueAtTime(0.9, t0);
    env.gain.exponentialRampToValueAtTime(0.001, t0 + len);
    src.connect(lp).connect(env).connect(master);
    src.start(t0); src.stop(t0 + len);
  }

  // 2) Crunchy BODY — heavy 3-bit noise sweeping low. Sits in the 200-700Hz "stone" range.
  {
    const len = 0.16;
    const src = audioCtx.createBufferSource();
    src.buffer = noiseBuffer(len, 3);
    const lp = audioCtx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(900 * pitchMult, t0);
    lp.frequency.exponentialRampToValueAtTime(80 * pitchMult, t0 + 0.10);
    lp.Q.value = 3.5; // less resonance peak — was screechy at Q=6
    const env = audioCtx.createGain();
    env.gain.setValueAtTime(0.001, t0);
    env.gain.linearRampToValueAtTime(0.95, t0 + 0.005);
    env.gain.exponentialRampToValueAtTime(0.001, t0 + len);
    src.connect(lp).connect(env).connect(master);
    src.start(t0); src.stop(t0 + len);
  }

  // 3) Sub BOOM — louder, longer triangle for the breaking-stone weight.
  {
    const sub = audioCtx.createOscillator();
    const env = audioCtx.createGain();
    sub.type = 'triangle';
    sub.frequency.setValueAtTime(70 * pitchMult, t0);
    sub.frequency.exponentialRampToValueAtTime(28 * pitchMult, t0 + 0.10);
    env.gain.setValueAtTime(0.001, t0);
    env.gain.linearRampToValueAtTime(0.85, t0 + 0.006);
    env.gain.exponentialRampToValueAtTime(0.001, t0 + 0.18);
    sub.connect(env).connect(master);
    sub.start(t0); sub.stop(t0 + 0.20);
  }

  // 4) Granular debris tail — short low-mid bursts simulating settling fragments
  const grainCount = isFinal ? 7 : 3;
  for (let g = 0; g < grainCount; g++) {
    const offset = 0.05 + Math.random() * 0.20;
    const len = 0.015 + Math.random() * 0.030;
    const src = audioCtx.createBufferSource();
    src.buffer = noiseBuffer(len, 3);
    const lp = audioCtx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = _pickScaleNote(200, 900) * pitchMult;
    lp.Q.value = 2;
    const env = audioCtx.createGain();
    const peak = 0.18 + Math.random() * 0.15;
    env.gain.setValueAtTime(0.001, t0 + offset);
    env.gain.linearRampToValueAtTime(peak, t0 + offset + 0.002);
    env.gain.exponentialRampToValueAtTime(0.001, t0 + offset + len);
    src.connect(lp).connect(env).connect(master);
    src.start(t0 + offset); src.stop(t0 + offset + len);
  }

  // 5) Final-hit RUMBLE — long low tail; the rock breaks open and you hear the cavern.
  if (isFinal) {
    const len = 0.34;
    const src = audioCtx.createBufferSource();
    src.buffer = noiseBuffer(len, 3);
    const lp = audioCtx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(700 * pitchMult, t0);
    lp.frequency.exponentialRampToValueAtTime(60 * pitchMult, t0 + 0.20);
    lp.Q.value = 3.5;
    const env = audioCtx.createGain();
    env.gain.setValueAtTime(0.001, t0);
    env.gain.linearRampToValueAtTime(0.55, t0 + 0.008);
    env.gain.exponentialRampToValueAtTime(0.001, t0 + len);
    src.connect(lp).connect(env).connect(master);
    src.start(t0); src.stop(t0 + len);

    // Sub-rumble sine — only audible on headphones / subwoofer but adds real "weight"
    const subRumble = audioCtx.createOscillator();
    const subEnv = audioCtx.createGain();
    subRumble.type = 'sine';
    subRumble.frequency.setValueAtTime(45 * pitchMult, t0);
    subRumble.frequency.exponentialRampToValueAtTime(25 * pitchMult, t0 + 0.25);
    subEnv.gain.setValueAtTime(0.001, t0);
    subEnv.gain.linearRampToValueAtTime(0.7, t0 + 0.01);
    subEnv.gain.exponentialRampToValueAtTime(0.001, t0 + 0.30);
    subRumble.connect(subEnv).connect(master);
    subRumble.start(t0); subRumble.stop(t0 + 0.32);
  }
  return master;
}

// WOOD SMASH — dry crack with a short woody resonance and splinter grains. No deep rumble.
function _playWoodSmash(pitchMult, isFinal, position, startTime) {
  if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)({ latencyHint: 'interactive' });
  const t0 = startTime !== undefined ? startTime : audioCtx.currentTime;
  const sr = audioCtx.sampleRate;
  const dest = _spatialDestination(position);
  const master = audioCtx.createGain();
  master.gain.value = (isFinal ? 0.75 : 0.55) * _crateCrush.level;
  // Same crush/HPF chain as bricks but with its own config (_crateCrush).
  const hpf = audioCtx.createBiquadFilter();
  hpf.type = 'highpass';
  hpf.frequency.value = _crateCrush.hpf;
  hpf.Q.value = 0.7;
  master.connect(hpf);
  const dry = audioCtx.createGain();
  dry.gain.value = 1 - _crateCrush.mix;
  hpf.connect(dry).connect(dest);
  const drive = audioCtx.createGain();
  drive.gain.value = _crateCrush.drive;
  const shaper = audioCtx.createWaveShaper();
  shaper.curve = _getBrickCrushCurve(_crateCrush.bits);  // same quantize curve cache works for both
  const tone = audioCtx.createBiquadFilter();
  tone.type = 'lowpass';
  tone.frequency.value = _crateCrush.tone;
  tone.Q.value = 0.7;
  const wet = audioCtx.createGain();
  wet.gain.value = _crateCrush.mix;
  hpf.connect(drive).connect(shaper).connect(tone).connect(wet).connect(dest);

  function noise(durationSec) {
    const len = Math.max(1, Math.floor(sr * durationSec));
    const buf = audioCtx.createBuffer(1, len, sr);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    return buf;
  }

  // 1) Wood crack — bandpass noise around 400-700Hz. Short, woody, NOT glassy.
  {
    const len = 0.030;
    const src = audioCtx.createBufferSource();
    src.buffer = noise(len);
    const bp = audioCtx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 550 * pitchMult;
    bp.Q.value = 2.5;
    const env = audioCtx.createGain();
    env.gain.setValueAtTime(0.001, t0);
    env.gain.linearRampToValueAtTime(0.95, t0 + 0.002);
    env.gain.exponentialRampToValueAtTime(0.001, t0 + len);
    src.connect(bp).connect(env).connect(master);
    src.start(t0); src.stop(t0 + len);
  }

  // 2) Box thud — a low-mid noise body, like the dull "thunk" of a crate hitting the ground.
  // Lowpassed noise rather than a pure tone reads as wood (textured) instead of metal (pitched).
  {
    const len = 0.10;
    const src = audioCtx.createBufferSource();
    src.buffer = noise(len);
    const lp = audioCtx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(400 * pitchMult, t0);
    lp.frequency.exponentialRampToValueAtTime(140 * pitchMult, t0 + 0.07);
    lp.Q.value = 2.5;
    const env = audioCtx.createGain();
    env.gain.setValueAtTime(0.001, t0);
    env.gain.linearRampToValueAtTime(0.7, t0 + 0.005);
    env.gain.exponentialRampToValueAtTime(0.001, t0 + len);
    src.connect(lp).connect(env).connect(master);
    src.start(t0); src.stop(t0 + len);
  }

  // 3) Sub thunk — per-block scale-tuned triangle sweep. Different crates land on different scale notes.
  const woodBase = position ? _hashPositionToChordRich(position, 110, 220) : 110;
  {
    const sub = audioCtx.createOscillator();
    const env = audioCtx.createGain();
    sub.type = 'triangle';
    sub.frequency.setValueAtTime(_snapScale(woodBase * pitchMult), t0);
    sub.frequency.exponentialRampToValueAtTime(_snapScale(woodBase * 0.55 * pitchMult), t0 + 0.08);
    env.gain.setValueAtTime(0.001, t0);
    env.gain.linearRampToValueAtTime(0.45, t0 + 0.004);
    env.gain.exponentialRampToValueAtTime(0.001, t0 + 0.10);
    sub.connect(env).connect(master);
    sub.start(t0); sub.stop(t0 + 0.12);
  }

  // 4) Splinter clatter — short low-mid bandpass bursts, "boards rattling", tuned to scale.
  const grainCount = isFinal ? 5 : 2;
  for (let g = 0; g < grainCount; g++) {
    const offset = 0.03 + Math.random() * 0.13;
    const len = 0.014 + Math.random() * 0.022;
    const src = audioCtx.createBufferSource();
    src.buffer = noise(len);
    const bp = audioCtx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = _pickScaleNote(300, 900) * pitchMult;
    bp.Q.value = 3;
    const env = audioCtx.createGain();
    const peak = 0.20 + Math.random() * 0.12;
    env.gain.setValueAtTime(0.001, t0 + offset);
    env.gain.linearRampToValueAtTime(peak, t0 + offset + 0.002);
    env.gain.exponentialRampToValueAtTime(0.001, t0 + offset + len);
    src.connect(bp).connect(env).connect(master);
    src.start(t0 + offset); src.stop(t0 + offset + len);
  }
  return master;
}

// METAL SMASH — bright clang. Sharp transient + tonal harmonics that ring out, longer on final.
function _playMetalSmash(pitchMult, isFinal, position, startTime) {
  if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)({ latencyHint: 'interactive' });
  const t0 = startTime !== undefined ? startTime : audioCtx.currentTime;
  const sr = audioCtx.sampleRate;
  const dest = _spatialDestination(position);
  const master = audioCtx.createGain();
  const _ms = _activeMetalShape();
  master.gain.value = (isFinal ? 0.65 : 0.52) * _ms.level;
  // Master brightness lowpass — at default (20kHz) it's effectively transparent; pull down to dull the clang.
  const bright = audioCtx.createBiquadFilter();
  bright.type = 'lowpass';
  bright.frequency.value = _ms.brightness;
  bright.Q.value = 0.7;
  master.connect(bright).connect(dest);

  // 1) Sharp ping transient — quick high-passed noise burst (gain scaled by strike knob)
  {
    const len = 0.014;
    const buf = audioCtx.createBuffer(1, Math.max(1, Math.floor(sr * len)), sr);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    const src = audioCtx.createBufferSource();
    src.buffer = buf;
    const hp = audioCtx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 2000;
    const env = audioCtx.createGain();
    env.gain.setValueAtTime(0.65 * _ms.strike, t0);
    env.gain.exponentialRampToValueAtTime(0.001, t0 + len);
    src.connect(hp).connect(env).connect(master);
    src.start(t0); src.stop(t0 + len);
  }

  // 2) Inharmonic partials — sustain knob scales ring length; clang knob lifts upper partials;
  // detune adds random per-hit cents to each partial for organic shimmer.
  const ringDuration = (isFinal ? 0.85 : 0.32) * _ms.sustain;
  // Tightened from 220-660 (1.6 octaves — produced jarring leaps between adjacent blocks) to
  // 330-660 (~E4-E5, exactly one octave above brick). Wood/brick/metal now sit at three
  // consecutive octave bands, each with one octave of headroom. Pool filtered to current
  // chord tones + scale 2nd/6th so smashes harmonize with the chord that's playing.
  const baseFreq = position ? _hashPositionToChordRich(position, 330, 660) : _snapScale(440);
  const fundamental = _snapScale(baseFreq * pitchMult);
  // Partial ratios + gains come from the active mode (inharmonic / glockenspiel / harmonic).
  // Defaults to inharmonic (the physical-metal-plate sound) if the mode is unset/invalid.
  const mode = METAL_PARTIAL_MODES[_metalPartialMode] || METAL_PARTIAL_MODES.inharmonic;
  const partials = mode.partials;
  const partialGains = mode.gains;
  for (let i = 0; i < partials.length; i++) {
    const osc = audioCtx.createOscillator();
    const env = audioCtx.createGain();
    osc.type = 'sine';
    // Per-block per-partial detune (deterministic via position hash). Same block always
    // sounds the same; different blocks vary. Fundamental skipped so perceived pitch is stable.
    const detuneCents = i === 0 ? 0 : _detuneCentsForPartial(position, i, _ms.detune);
    osc.frequency.value = fundamental * partials[i] * Math.pow(2, detuneCents / 1200);
    // clang scales partials >0; fundamental untouched so volume balance stays sane.
    const gain = i === 0 ? partialGains[i] : partialGains[i] * _ms.clang;
    env.gain.setValueAtTime(0.001, t0);
    env.gain.linearRampToValueAtTime(gain, t0 + 0.005);
    env.gain.exponentialRampToValueAtTime(0.001, t0 + ringDuration);
    osc.connect(env).connect(master);
    osc.start(t0); osc.stop(t0 + ringDuration + 0.05);
  }
  return master;
}

// =========================================================
// Assist-mode destruction soundscape. When an auto-tap's blast destroys NEIGHBOURS, each material
// present plays ONE scaled "section" voice (not one-per-block, which floods the engine and dies).
// The materials act as instrument sections that get richer with streak: metal = harmony (a chord
// that thickens), brick = bass (sub-boom), crate = percussion (splinter clatter). All chord-locked
// to the song so they harmonise. Cheap: a few short oscillators/grains per event, one music duck.
// =========================================================

// Current chord object from the song schedule (null if music isn't running). Mirrors the lookup in
// _currentChordRichDegrees so the section voices land in-key with whatever chord is playing now.
function _currentChord() {
  if (!_musicStarted || !audioCtx || !_activeSong || !_activeSong.chords) return null;
  const elapsed = audioCtx.currentTime - _musicLoopStartTime;
  if (elapsed < 0) return null;
  const loopElapsed = ((elapsed % MUSIC_LOOP_DURATION) + MUSIC_LOOP_DURATION) % MUSIC_LOOP_DURATION;
  const schedule = _chordSchedule();
  for (const entry of schedule) {
    const start = entry.startBar * MUSIC_BAR;
    const end = start + entry.bars * MUSIC_BAR;
    if (loopElapsed >= start && loopElapsed < end) return entry.chord;
  }
  return schedule.length > 0 ? schedule[schedule.length - 1].chord : null;
}

// Shared tempo-synced feedback delay for the god-tier (50+) metal echoes. Built once, cheap: one
// DelayNode + a lowpass-in-the-loop (echoes darken) + a feedback gain (~3-4 repeats). Metal sends
// into it only at 50+, so the chord rings out in on-grid, in-key echoes — the "god mode" signature.
let _metalDelay = null, _metalDelayInput = null;
function _ensureMetalDelay() {
  if (_metalDelay || !audioCtx || !_sfxBus) return;
  const delay = audioCtx.createDelay(1.5);
  delay.delayTime.value = 0.25;
  const lp = audioCtx.createBiquadFilter();
  lp.type = 'lowpass'; lp.frequency.value = 2200;   // each repeat a little darker
  const fb = audioCtx.createGain();
  fb.gain.value = 0.42;                              // feedback amount -> ~3-4 audible echoes
  const wet = audioCtx.createGain();
  wet.gain.value = 0.5;                              // how loud the echoes are
  delay.connect(lp); lp.connect(fb); fb.connect(delay);  // feedback loop
  delay.connect(wet); wet.connect(_sfxBus);              // audible echoes -> SFX bus
  _metalDelay = delay; _metalDelayInput = delay;
}

// METAL section — a ringing chord locked to the current chord. Streak grows it from a single root
// note into a full triad+octave, so harmony thickens as you climb. count = metal blocks killed.
function _easyMetalChord(count, streak, position) {
  const chord = _currentChord();
  const tones = chord ? _chordTones(chord) : [_snapScale(440)];
  const voices = streak >= 75 ? 4 : streak >= 50 ? 3 : streak >= 35 ? 2 : 1;  // thickens slowly from the gate
  const dest = _spatialDestination(position);
  const out = audioCtx.createGain();
  out.connect(dest);
  // God-tier: send the chord into the tempo-synced feedback delay so it echoes out in-key on the grid.
  if (streak >= 50) {
    _ensureMetalDelay();
    if (_metalDelayInput) {
      _metalDelay.delayTime.value = MUSIC_BEAT / 2;  // 8th-note echoes, locked to tempo
      const send = audioCtx.createGain();
      send.gain.value = 0.6;
      out.connect(send).connect(_metalDelayInput);
    }
  }
  const t0 = audioCtx.currentTime;
  const ring = 0.4 + Math.min(0.4, streak / 100);   // longer ring higher up
  const vol = Math.min(0.32, 0.12 + count * 0.04) * _easySectionIntensity(streak);  // build in from the gate
  for (let i = 0; i < voices; i++) {
    let freq = tones[Math.min(i, tones.length - 1)] || 440;
    while (freq < 440) freq *= 2; while (freq > 1320) freq /= 2;  // lift into the metal ring octave
    for (let p = 0; p < 2; p++) {  // 2 sine partials for a touch of metallic shimmer
      const osc = audioCtx.createOscillator();
      const env = audioCtx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq * (p === 0 ? 1 : 2.01);
      const g = vol * (p === 0 ? 1 : 0.22);
      env.gain.setValueAtTime(0.001, t0);
      env.gain.linearRampToValueAtTime(g, t0 + 0.006);
      env.gain.exponentialRampToValueAtTime(0.001, t0 + ring);
      osc.connect(env).connect(out);
      osc.start(t0); osc.stop(t0 + ring + 0.05);
    }
  }
}

// BRICK section — a deep sub-boom on the chord ROOT (the bass) plus a short mid "stone crack" body
// so it has presence on any speaker, not just a subwoofer. Deeper/longer/louder with streak + count.
function _easyBrickBoom(count, streak, position) {
  const chord = _currentChord();
  let f = chord ? chord.r : 110;
  while (f > 90) f /= 2; while (f < 40) f *= 2;     // drop the root into a sub octave (~45-90 Hz)
  const dest = _spatialDestination(position);
  const t0 = audioCtx.currentTime;
  const sr = audioCtx.sampleRate;
  const len = 0.22 + Math.min(0.4, streak / 120) + Math.min(0.15, count * 0.03);
  const vol = Math.min(0.5, 0.28 + count * 0.05) * _easySectionIntensity(streak);  // build in from the gate
  // Sub-boom — the low-end weight.
  const sub = audioCtx.createOscillator();
  const env = audioCtx.createGain();
  sub.type = 'triangle';
  sub.frequency.setValueAtTime(f * 2.5, t0);                  // bigger pitch drop = bigger "whoomp"
  sub.frequency.exponentialRampToValueAtTime(f, t0 + 0.12);
  env.gain.setValueAtTime(0.001, t0);
  env.gain.linearRampToValueAtTime(vol, t0 + 0.01);
  env.gain.exponentialRampToValueAtTime(0.001, t0 + len);
  sub.connect(env).connect(dest);
  sub.start(t0); sub.stop(t0 + len + 0.05);
  // Mid "stone crack" body — short lowpass-noise punch for presence on laptop/phone speakers.
  const clen = 0.11;
  const buf = audioCtx.createBuffer(1, Math.max(1, Math.floor(sr * clen)), sr);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
  const src = audioCtx.createBufferSource();
  src.buffer = buf;
  const lp = audioCtx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.setValueAtTime(600, t0);
  lp.frequency.exponentialRampToValueAtTime(160, t0 + 0.08);
  lp.Q.value = 2;
  const cenv = audioCtx.createGain();
  cenv.gain.setValueAtTime(0.001, t0);
  cenv.gain.linearRampToValueAtTime(vol * 0.7, t0 + 0.004);
  cenv.gain.exponentialRampToValueAtTime(0.001, t0 + clen);
  src.connect(lp).connect(cenv).connect(dest);
  src.start(t0); src.stop(t0 + clen);
  // Low-mid BODY — a tonal layer ~2 octaves above the sub (chord root in the ~150-330 Hz "chest"
  // range) so the boom has real size on laptop/phone speakers, not just sub you can't hear. Punchy
  // with its own pitch drop. This is what makes it read BIG rather than just deep.
  {
    let bf = f * 4; while (bf > 330) bf /= 2; while (bf < 150) bf *= 2;
    const body = audioCtx.createOscillator();
    const benv = audioCtx.createGain();
    body.type = 'triangle';
    body.frequency.setValueAtTime(bf * 1.5, t0);
    body.frequency.exponentialRampToValueAtTime(bf, t0 + 0.06);
    benv.gain.setValueAtTime(0.001, t0);
    benv.gain.linearRampToValueAtTime(vol * 0.85, t0 + 0.005);
    benv.gain.exponentialRampToValueAtTime(0.001, t0 + len * 0.7);
    body.connect(benv).connect(dest);
    body.start(t0); body.stop(t0 + len * 0.7 + 0.05);
  }
  // God-tier (50+): a sub-octave sine that swells in under the boom — the bass going subterranean.
  if (streak >= 50) {
    const so = audioCtx.createOscillator();
    const soenv = audioCtx.createGain();
    so.type = 'sine';
    so.frequency.setValueAtTime(f / 2, t0);
    soenv.gain.setValueAtTime(0.001, t0);
    soenv.gain.linearRampToValueAtTime(vol * 0.7, t0 + 0.07);  // slower swell, not an instant thump
    soenv.gain.exponentialRampToValueAtTime(0.001, t0 + len + 0.25);
    so.connect(soenv).connect(dest);
    so.start(t0); so.stop(t0 + len + 0.3);
  }
}

// CRATE section — a tuned woody KNOCK (tom/woodblock on the chord third) with a short splinter-noise
// crack on top. The pitched knock gives crate a real percussion voice in the mix instead of faint
// gravel; the splinter grains keep its dry woody texture. Knock + grain density grow with streak/count.
function _easyCrateClatter(count, streak, position) {
  const chord = _currentChord();
  let knockHz = chord ? chord.t : 220;              // tuned to the chord third
  while (knockHz < 180) knockHz *= 2; while (knockHz > 480) knockHz /= 2;  // tom range
  const dest = _spatialDestination(position);
  const t0 = audioCtx.currentTime;
  const sr = audioCtx.sampleRate;
  const vol = Math.min(0.42, 0.22 + count * 0.05) * _easySectionIntensity(streak);  // build in from the gate
  // Pitched woody knock — the percussion "voice".
  const knock = audioCtx.createOscillator();
  const kenv = audioCtx.createGain();
  knock.type = 'triangle';
  knock.frequency.setValueAtTime(knockHz, t0);
  knock.frequency.exponentialRampToValueAtTime(knockHz * 0.6, t0 + 0.05);
  kenv.gain.setValueAtTime(0.001, t0);
  kenv.gain.linearRampToValueAtTime(vol, t0 + 0.003);
  kenv.gain.exponentialRampToValueAtTime(0.001, t0 + 0.13);
  knock.connect(kenv).connect(dest);
  knock.start(t0); knock.stop(t0 + 0.15);
  // Splinter crack on top — a few tight scale-tuned noise grains for the woody texture.
  const grains = Math.min(6, 2 + Math.floor(count / 2) + Math.floor(streak / 30));
  for (let g = 0; g < grains; g++) {
    const offset = g * 0.018 + Math.random() * 0.010;
    const len = 0.012 + Math.random() * 0.016;
    const buf = audioCtx.createBuffer(1, Math.max(1, Math.floor(sr * len)), sr);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    const src = audioCtx.createBufferSource();
    src.buffer = buf;
    const bp = audioCtx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = _pickScaleNote(500, 1400);
    bp.Q.value = 2.5;
    const env = audioCtx.createGain();
    const peak = 0.16 + Math.random() * 0.10;
    env.gain.setValueAtTime(0.001, t0 + offset);
    env.gain.linearRampToValueAtTime(peak, t0 + offset + 0.002);
    env.gain.exponentialRampToValueAtTime(0.001, t0 + offset + len);
    src.connect(bp).connect(env).connect(dest);
    src.start(t0 + offset); src.stop(t0 + offset + len);
  }
  // God-tier (50+): a quantized 16th-note tick roll after the knock — the percussion section
  // breaking into a fill. Light, higher ticks on the music grid.
  if (streak >= 50) {
    const step = MUSIC_BEAT / 4;  // 16th note
    for (let r = 1; r <= 3; r++) {
      const ot = t0 + r * step;
      const osc = audioCtx.createOscillator();
      const env = audioCtx.createGain();
      osc.type = 'triangle';
      osc.frequency.value = knockHz * 1.5;
      env.gain.setValueAtTime(0.001, ot);
      env.gain.linearRampToValueAtTime(vol * 0.5, ot + 0.002);
      env.gain.exponentialRampToValueAtTime(0.001, ot + 0.05);
      osc.connect(env).connect(dest);
      osc.start(ot); osc.stop(ot + 0.07);
    }
  }
}

// Read-only prediction of which materials the assist blast will destroy, so the soundscape can fire
// ON THE BEAT with the smash rather than at the +180ms physical impact. Mirrors _applyChipAOEDamage's
// selection (blocks within radius that this damage would finish off), excluding the primary block
// (which plays its own smash). Close enough for audio — the real break/coins resolve at impact.
function _easyBlastTally(center, radius, damage, exclude) {
  const tally = { crate: 0, brick: 0, metal: 0 };
  let count = 0;
  for (const other of blocks) {
    if (count >= NEARBY_MAX_PARTS) break;
    if (other === exclude) continue;
    if (other.position.distanceTo(center) > radius) continue;
    count++;
    if ((other.userData.hitsTaken + damage) >= other.userData.hitsToBreak) {
      const m = other.userData.material;
      if (tally[m] !== undefined) tally[m]++;
    }
  }
  return tally;
}

// Read-only scan of which materials sit NEAR a smash, regardless of whether they break. The assist
// soundscape now voices off DISTURBANCE (decoupled from actual kills) so the per-material band stays
// rich and grows with streak even though the real AOE damage is dialled way down and holds off until
// deep streak. The primary (smashed) block is always added by the caller.
function _easyDisturbanceTally(center, radius, exclude) {
  const tally = { crate: 0, brick: 0, metal: 0 };
  let count = 0;
  for (const other of blocks) {
    if (count >= NEARBY_MAX_PARTS) break;
    if (other === exclude) continue;
    if (other.position.distanceTo(center) > radius) continue;
    count++;
    const m = other.userData.material;
    if (tally[m] !== undefined) tally[m]++;
  }
  return tally;
}

// Dispatcher — fire one section voice per material that actually had a kill, duck the music once.
function _easyDestructionSound(tally, streak, position) {
  if (!audioCtx || !tally) return;
  let any = false;
  if (tally.metal > 0) { _easyMetalChord(tally.metal, streak, position); any = true; }
  if (tally.brick > 0) { _easyBrickBoom(tally.brick, streak, position); any = true; }
  if (tally.crate > 0) { _easyCrateClatter(tally.crate, streak, position); any = true; }
  if (any) _duckMusic();
}

// COIN TINKLE — soft bell shower at break time. Triangle wave + lower pitch range for less piercing feel.
function playCoinTinkle(count, position) {
  if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)({ latencyHint: 'interactive' });
  // Snap the splash onto the next 16th-note boundary so per-break tinkles sit on the music
  // grid instead of firing at random sub-beat moments. Worst-case delay ~125ms (one 16th).
  const t0 = _snapToNextSixteenth(audioCtx.currentTime);
  const master = audioCtx.createGain();
  master.gain.value = 0.05 + Math.min(count, 8) * 0.012;
  master.connect(_spatialDestination(position));

  const dings = Math.min(2 + Math.floor(count * 0.35), 4);
  for (let i = 0; i < dings; i++) {
    const offset = i * 0.025 + Math.random() * 0.012;
    const len = 0.09 + Math.random() * 0.06;
    const freq = _pickScaleNote(1100, 1800);  // active song scale
    const osc = audioCtx.createOscillator();
    const env = audioCtx.createGain();
    osc.type = 'triangle';
    osc.frequency.value = freq;
    env.gain.setValueAtTime(0.001, t0 + offset);
    env.gain.linearRampToValueAtTime(0.35, t0 + offset + 0.003);
    env.gain.exponentialRampToValueAtTime(0.001, t0 + offset + len);
    osc.connect(env).connect(master);
    osc.start(t0 + offset); osc.stop(t0 + offset + len + 0.02);
  }
}

// COIN CLINK — quiet landing tap. Triangle wave, lower pitch, short envelope so a pile of coins doesn't grate.
// Caller can pass a fixed note so the same coin always plays the same pitch on every bounce.
function playCoinClink(intensity, position, fixedNote, startTime) {
  if (!audioCtx) return;
  // Per-frame cap: cash-out and music clinks are sparse (well under cap), but the collision
  // drainer passes a 16th-quantized future startTime — those still all originate in one frame
  // and pile up at the same future audio moment, so the cap MUST apply regardless of startTime.
  if (_coinClinksThisFrame >= _COIN_CLINK_MAX_PER_FRAME) { _audioClinkDropped++; return; }
  _coinClinksThisFrame++;
  _audioClinkCalls++;
  const t0 = startTime !== undefined ? startTime : audioCtx.currentTime;
  const len = 0.04 + Math.random() * 0.03;
  const freq = fixedNote || _pickScaleNote(750, 1100);
  const osc = audioCtx.createOscillator();
  const env = audioCtx.createGain();
  osc.type = 'triangle';
  osc.frequency.value = freq;
  env.gain.setValueAtTime(0.001, t0);
  env.gain.linearRampToValueAtTime(intensity * 0.10, t0 + 0.002);
  env.gain.exponentialRampToValueAtTime(0.001, t0 + len);
  const dest = _spatialDestination(position);
  osc.connect(env).connect(dest);
  // When position is null we skip the panner (cheap), but we still want the bell-tone reverb
  // tail — without it the clinks sound dry/flat. Reverb convolver is a shared node; more inputs
  // sum into one signal, so adding a send here is essentially free.
  if (!position && _reverbConvolver) env.connect(_reverbConvolver);
  osc.start(t0); osc.stop(t0 + len + 0.02);
}

// Coin-arpeggio crescendo on charge release — ascends the A minor scale, volume builds across
// the run, final note holds longer. Uses the song's key so it sits in the music. Charge level
// scales the run length and end pitch (full charge ends an octave higher than half charge).
function playChargeRelease(charge, position) {
  if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)({ latencyHint: 'interactive' });
  // Snap the run's start onto the next 16th and space the notes by 32nd-notes so the
  // ascending arpeggio lands as a proper musical run in the song's tempo, not random sub-beat sparkle.
  const t0 = _snapToNextSixteenth(audioCtx.currentTime);
  const noteCount = 7 + Math.round(charge * 2);  // 7 notes at SUCK_GRACE, 9 at full charge
  const noteSpacing = MUSIC_BEAT / 8;             // 32nd-note at the active BPM (~62.5ms @ 120 BPM)
  // Start near A4 (440 Hz) and walk up the scale. Find the index of A4 in the scale array.
  const startFreq = 440;
  let startIdx = 0;
  for (let i = 0; i < _activeScaleHz.length; i++) {
    if (_activeScaleHz[i] >= startFreq) { startIdx = i; break; }
  }
  // Step size: full charge climbs further (more octaves spanned).
  const stepSize = charge >= 0.85 ? 2 : 1;  // 2 = thirds (faster ascent), 1 = adjacent steps

  for (let i = 0; i < noteCount; i++) {
    const isFinal = (i === noteCount - 1);
    const noteIdx = Math.min(_activeScaleHz.length - 1, startIdx + i * stepSize);
    const freq = _activeScaleHz[noteIdx];
    const t = t0 + i * noteSpacing;
    const len = isFinal ? 0.18 : (0.05 + Math.random() * 0.025);
    // Crescendo: each note slightly louder than the last; final note loudest.
    const progress = i / Math.max(1, noteCount - 1);
    const peak = (0.06 + 0.10 * progress) * (isFinal ? 1.4 : 1.0);

    const osc = audioCtx.createOscillator();
    const env = audioCtx.createGain();
    osc.type = 'triangle';
    osc.frequency.value = freq;
    env.gain.setValueAtTime(0.001, t);
    env.gain.linearRampToValueAtTime(peak, t + 0.003);
    env.gain.exponentialRampToValueAtTime(0.001, t + len);
    osc.connect(env).connect(_spatialDestination(position));
    osc.start(t); osc.stop(t + len + 0.02);
  }
}

// Briefly duck the music gain so the smash can punch through without fighting the mix.
// Drop fast (5ms) to ~50%, hold for the impact transient (80ms), recover smoothly (200ms).
function _duckMusic() {
  if (!_musicGain || !audioCtx) return;
  const t = audioCtx.currentTime;
  const g = _musicGain.gain;
  g.cancelScheduledValues(t);
  g.setValueAtTime(g.value, t);
  const tgt = _MUSIC_VOLUME_TARGET;
  g.linearRampToValueAtTime(tgt * 0.5, t + 0.005);
  g.setValueAtTime(tgt * 0.5, t + 0.085);
  g.linearRampToValueAtTime(tgt, t + 0.285);
}

// Smash dispatcher — picks per-material synth, forwards the impact position for spatialisation,
// and ducks the music briefly so the smash cuts through.
function playSmashSound(material, pitchMult, isFinal, position, startTime) {
  // Per-frame cap: a cascade can want 5+ smashes in the same frame; stacking them past the
  // bus headroom is what produces the bit-crushed crackle. 2 keeps the punch without clipping.
  if (_smashSoundsThisFrame >= _SMASH_MAX_PER_FRAME) { _audioSmashDropped++; return null; }
  _smashSoundsThisFrame++;
  _audioSmashCalls++;
  let master;
  // startTime (optional) schedules the smash at an exact future audio time — used to land a peer's
  // quantized smash precisely on the beat grid (setTimeout is far too jittery to align rhythmically).
  if (material === 'crate') master = _playWoodSmash(pitchMult, isFinal, position, startTime);
  else if (material === 'metal') master = _playMetalSmash(pitchMult, isFinal, position, startTime);
  else master = _playBrickSmash(pitchMult, isFinal, position, startTime);
  _duckMusic();
  return master;  // returned so caller can fade out the sound (used by charge-attack click suppression)
}

// =========================================================
// Music — single 16-bar chiptune loop, A minor, 120 BPM. Bass + drums + lead.
// Schedules ahead so timing is sample-accurate; loops itself on a setTimeout chain.
// =========================================================
// BPM-derived constants — recomputed by applySong() when a song spec changes BPM.
// MUSIC_LOOP_BARS is locked at 16 (per the song schema's locked structural skeleton).
let MUSIC_BPM = 120;
let MUSIC_BEAT = 60 / MUSIC_BPM;        // 0.5s at 120bpm
let MUSIC_BAR = MUSIC_BEAT * 4;         // 2.0s at 120bpm
const MUSIC_LOOP_BARS = 8;
let MUSIC_LOOP_DURATION = MUSIC_BAR * MUSIC_LOOP_BARS;  // 16s at 120bpm — was 32s before halving

// The default song spec — preserves the original A minor 120 BPM Am→F→C→G feel.
// Applied at boot. Per-world override happens at level entry via applySong().
const DEFAULT_SONG = {
  id: 'w1-default-am',
  bpm: 120,
  scaleHz: [
    27.50, 30.87, 32.70, 36.71, 41.20, 43.65, 49.00,
    55.00, 61.74, 65.41, 73.42, 82.41, 87.31, 98.00,
    110.00, 123.47, 130.81, 146.83, 164.81, 174.61, 196.00,
    220.00, 246.94, 261.63, 293.66, 329.63, 349.23, 392.00,
    440.00, 493.88, 523.25, 587.33, 659.25, 698.46, 783.99,
    880.00, 987.77, 1046.50, 1174.66, 1318.51, 1396.91, 1567.98,
    1760.00, 1975.53, 2093.00, 2349.32, 2637.02, 2793.83, 3135.96,
  ],
  chords: [
    { r: 220.00, t: 261.63, f: 329.63, o: 440.00 }, // Am
    { r: 174.61, t: 220.00, f: 261.63, o: 349.23 }, // F
    { r: 130.81, t: 164.81, f: 196.00, o: 261.63 }, // C
    { r: 196.00, t: 246.94, f: 293.66, o: 392.00 }, // G
  ],
  // 8-note motif using chord-tone indices: 0=root, 1=third, 2=fifth, 3=octave-up.
  // Default is the original ascend-then-descend through chord tones, broadcast across all chords.
  leadMotif: [0, 1, 2, 3, 2, 1, 0, 1],
  leadMotifBusy: [0, 1, 2, 3, 3, 2, 1, 0, 1, 2],
  leadWaveform: 'square',
};

// Busy-variant rhythm fingerprints — different slot positions on the 16th-note grid
// for the busy lead pattern. Each rhythm gives a distinctly different rhythmic feel
// even with the same melodic notes. Used by tonal+ / var+ / poly+ / build+ sections.
// Each rhythm has bar0 + bar1 slot arrays (10 slots each, on a 16-slot grid).
const BUSY_RHYTHMS = {
  // Default — front-loaded 16ths into end-of-chord pickup. The W1 default sound.
  chunky: {
    bar0: [0, 1, 2, 3, 4, 6, 8, 10, 12, 14],
    bar1: [0, 2, 4, 6, 8, 10, 12, 13, 14, 15],
  },
  // Pairs of 16ths on each beat — bouncy, gallop-y, like ti-ka ti-ka ti-ka ti-ka.
  galloping: {
    bar0: [0, 1, 4, 5, 8, 9, 10, 12, 13, 15],
    bar1: [0, 1, 4, 5, 8, 9, 12, 13, 14, 15],
  },
  // Off-beat emphasis — slots on 3, 7, 11. Quirky / eerie / unsettling lopsidedness.
  syncopated: {
    bar0: [0, 3, 4, 7, 8, 11, 12, 13, 14, 15],
    bar1: [0, 3, 4, 7, 8, 9, 11, 12, 14, 15],
  },
  // Dense scattered 16ths — lots of activity, asymmetric within the bar.
  flurry: {
    bar0: [0, 1, 2, 3, 4, 5, 8, 10, 12, 14],
    bar1: [0, 2, 3, 4, 5, 8, 10, 11, 13, 15],
  },
  // Backbeat emphasis with 16th flourishes — locked-in, propulsive, mechanical.
  driving: {
    bar0: [0, 2, 3, 4, 6, 8, 10, 11, 12, 14],
    bar1: [0, 2, 4, 6, 7, 8, 10, 12, 14, 15],
  },
};

// Vibe presets — named bundles of structural defaults (mode/scale, BPM, density,
// waveform). When a song spec sets `vibe: "<name>"`, these defaults fill in any
// fields the spec didn't explicitly specify. Motifs are NEVER preset-filled — those
// are always per-prompt LLM-authored, so 1000 "scary" songs share Phrygian/sparse
// but each has its own melody. Individual spec fields override the preset.
//
// Each preset suggests 1-2 modes; the LLM picks the scaleHz from those. BPM is a
// recommendation; LLM can pick any value. Density and waveform are exact defaults.
const VIBE_PRESETS = {
  bright:       { bpm: 128, drumDensity: 'normal',  bassDensity: 'normal', leadWaveform: 'square',   busyRhythm: 'chunky'     },
  triumphant:   { bpm: 132, drumDensity: 'normal',  bassDensity: 'normal', leadWaveform: 'square',   busyRhythm: 'driving'    },
  flying:       { bpm: 125, drumDensity: 'normal',  bassDensity: 'sparse', leadWaveform: 'triangle', busyRhythm: 'galloping'  },
  dreamy:       { bpm: 115, drumDensity: 'sparse',  bassDensity: 'sparse', leadWaveform: 'triangle', busyRhythm: 'chunky'     },
  calm:         { bpm: 115, drumDensity: 'sparse',  bassDensity: 'medium', leadWaveform: 'triangle', busyRhythm: 'chunky'     },
  melancholic:  { bpm: 115, drumDensity: 'sparse',  bassDensity: 'medium', leadWaveform: 'triangle', busyRhythm: 'chunky'     },
  mysterious:   { bpm: 118, drumDensity: 'sparse',  bassDensity: 'sparse', leadWaveform: 'triangle', busyRhythm: 'syncopated' },
  eerie:        { bpm: 115, drumDensity: 'sparse',  bassDensity: 'sparse', leadWaveform: 'square',   busyRhythm: 'syncopated' },
  scary:        { bpm: 100, drumDensity: 'sparse',  bassDensity: 'sparse', leadWaveform: 'sawtooth', busyRhythm: 'syncopated' },
  tense:        { bpm: 128, drumDensity: 'normal',  bassDensity: 'normal', leadWaveform: 'sawtooth', busyRhythm: 'flurry'     },
  frantic:      { bpm: 145, drumDensity: 'dense',   bassDensity: 'normal', leadWaveform: 'sawtooth', busyRhythm: 'flurry'     },
  mechanical:   { bpm: 138, drumDensity: 'dense',   bassDensity: 'dense',  leadWaveform: 'sawtooth', busyRhythm: 'driving'    },
  epic:         { bpm: 135, drumDensity: 'normal',  bassDensity: 'dense',  leadWaveform: 'sawtooth', busyRhythm: 'driving'    },
  playful:      { bpm: 130, drumDensity: 'normal',  bassDensity: 'normal', leadWaveform: 'square',   busyRhythm: 'galloping'  },
  dance:        { bpm: 140, drumDensity: 'dense',   bassDensity: 'normal', leadWaveform: 'sawtooth', busyRhythm: 'galloping'  },
};

let _activeSong = DEFAULT_SONG;

// Swap the active song. Updates BPM-derived constants, scale array, and the
// chord progression that the music loops read at schedule time. Safe to call
// between sections — applies on the next loop scheduling.
function applySong(spec) {
  // Merge vibe-preset defaults for any field the spec doesn't already specify.
  // Spec fields always win — preset only fills the gaps.
  const baseSpec = spec || DEFAULT_SONG;
  const preset = baseSpec.vibe ? VIBE_PRESETS[baseSpec.vibe] : null;
  if (preset) {
    _activeSong = {
      ...baseSpec,
      bpm:          baseSpec.bpm          ?? preset.bpm,
      drumDensity:  baseSpec.drumDensity  ?? preset.drumDensity,
      bassDensity:  baseSpec.bassDensity  ?? preset.bassDensity,
      leadWaveform: baseSpec.leadWaveform ?? preset.leadWaveform,
      busyRhythm:   baseSpec.busyRhythm   ?? preset.busyRhythm,
    };
  } else {
    _activeSong = baseSpec;
  }
  MUSIC_BPM = _activeSong.bpm;
  MUSIC_BEAT = 60 / MUSIC_BPM;
  MUSIC_BAR = MUSIC_BEAT * 4;
  MUSIC_LOOP_DURATION = MUSIC_BAR * MUSIC_LOOP_BARS;
  _activeScaleHz = _activeSong.scaleHz;
  // Timing-window grid + punch cooldown both derive from the BPM. Without these
  // updates, scoring drifts and punches fire at the wrong cadence.
  SCORE_GRID = MUSIC_BEAT / 2;
  PUNCH_COOLDOWN = MUSIC_BEAT / 2;
  // Push reverb / delay parameters from the new song into the live effect chain.
  // No-op before startMusic() builds the chain — startMusic() also applies them.
  _applyMusicEffectsFromSong();
}
const MUSIC_VOLUME = 0.23;  // dialed in by ear
let _MUSIC_VOLUME_TARGET = MUSIC_VOLUME;  // current target after slider adjustment; ducking ramps back to this
let _musicGain = null;
let _musicStarted = false;
let _musicNextStart = 0;

// Tracks which of the 8 sections the scheduler is currently building events for. Set
// in _scheduleNextLoop *before* the section dispatcher runs so all reads inside the
// section function see the right index. Allows per-section field overrides via
// _activeSong.sections[_currentSection][fieldName].
let _currentSection = 0;

// Resolve a song field with per-section override fallback. Section overrides take
// precedence — `_activeSong.sections[_currentSection].drumDensity` if set, else
// `_activeSong.drumDensity`. Returns undefined if neither is set.
function _sectionField(name) {
  const sec = _activeSong.sections && _activeSong.sections[_currentSection];
  if (sec && sec[name] !== undefined) return sec[name];
  return _activeSong[name];
}

// Per-section transpose multiplier. `transpose: 5` semitones = 1.3348× pitch. Applied
// at every chord-tone read site via _transposedChord. Default 0 (no shift). Common uses:
// chorus +5 (subdominant lift), bridge -2 (whole-step down), final section +12 (octave up).
function _currentTransposeMult() {
  const t = _activeSong.sections && _activeSong.sections[_currentSection]
    ? _activeSong.sections[_currentSection].transpose : null;
  if (typeof t !== 'number' || t === 0) return 1;
  return Math.pow(2, t / 12);
}

// Returns a chord with all tone Hz multiplied by the current section's transpose. If no
// transpose, returns the original chord (no allocation). Schedulers call this once per
// chord; downstream reads of chord.r/t/f/o/s are then automatically transposed.
function _transposedChord(chord) {
  const m = _currentTransposeMult();
  if (m === 1) return chord;
  const out = { r: chord.r * m, t: chord.t * m, f: chord.f * m, o: chord.o * m };
  if (typeof chord.s === 'number') out.s = chord.s * m;
  return out;
}

// Per-role volume multipliers — let the song spec rebalance the mix without touching the
// engine's baseline peak gains. Default 1.0 means "use the engine's tuned values".
// Clamped to [0, 2] so a song can't blow out the master bus. Section overrides supported.
function _vol(role) {
  const v = _sectionField(role + 'Volume');
  if (typeof v !== 'number') return 1;
  return Math.max(0, Math.min(2, v));
}

// Chord tones list. Adds the optional extension (`chord.s` — seventh, 9th, sus4, or any
// other note the LLM picked) as a 5th element when present. All motif resolvers and the
// pad voicer iterate this list, so adding `s` automatically extends melodic + harmonic
// language without per-call changes.
function _chordTones(chord) {
  const t = [chord.r, chord.t, chord.f, chord.o];
  if (typeof chord.s === 'number') t.push(chord.s);
  return t;
}

// Motif-only tone resolution. Routes through chord tones (indices 0-4) by default, OR
// through the full scaleHz array when the song opts into `motifMode: 'scale'` — the
// latter unlocks 28-49 melodic degrees (passing tones, modal flavors, stepwise motion)
// instead of constraining motifs to the 4-5 chord tones. Returns an indexable array
// the same shape as _chordTones used to return; existing motif call sites reading
// `tones[idx]` work unchanged in either mode. Smash gameplay logic still uses the
// real _chordTones — it needs harmonic-only filtering, not melodic range.
function _motifTones(chord) {
  if (_activeSong.motifMode === 'scale') {
    const scale = _activeSong.scaleHz;
    if (!Array.isArray(scale) || scale.length === 0) return [];
    const m = _currentTransposeMult();
    return m === 1 ? scale : scale.map(f => f * m);
  }
  return _chordTones(chord);
}

// Swing nudge in seconds — how much to delay an off-beat 8th-note (the "and" of each beat,
// 16th-position 2 mod 4). Swing 0 = straight, swing 100 = full triplet feel (off-beat
// lands at 2/3 of beat instead of 1/2). Other values interpolate linearly.
function _swingNudgeForSlot(slot16) {
  const swing = _sectionField('swing');
  if (typeof swing !== 'number' || swing <= 0) return 0;
  if ((slot16 % 4) !== 2) return 0;  // only the "and" 8th gets swung
  const amount = Math.max(0, Math.min(100, swing)) / 100;
  return amount * (MUSIC_BEAT / 6);
}

// Single-note voice helper — used by bass and lead. Role determines which song-level
// volume multiplier applies; default is 'lead' since most call sites are lead/melody.
// A note whose onset is already behind the audio clock. Only ever true during a mid-song resync (a
// joining peer adopts the session's current position by re-running the CURRENT section from its real,
// now-past start). Web Audio would clamp such onsets to "now" and blare them all at once, so the voices
// skip them. In normal play every onset is scheduled ahead, so this never fires.
function _noteIsPast(time) { return audioCtx && time < audioCtx.currentTime - 0.002; }

function _musicVoice(freq, time, duration, type, peakGain, role = 'lead') {
  if (_noteIsPast(time)) return;
  const peak = peakGain * _vol(role);
  // Web Audio's exponentialRampToValueAtTime throws if it ramps to/from 0. When a per-
  // section override sets a role's volume to 0, peak collapses to 0 and the envelope
  // ramps below would throw — killing the section's schedule and stopping music forever.
  // Just skip the voice entirely; it would be inaudible anyway.
  if (peak <= 1e-6) return;
  // Defensive NaN guard — if a UGC song's lead motif uses an out-of-range chord-tone
  // index (e.g. 5+ on a chord without `s`), `tones[idx]` is undefined, freq becomes
  // NaN, and AudioParam.value throws TypeError, halting the entire music loop. Skip
  // the voice cleanly instead so the rest of the section still plays.
  if (!Number.isFinite(freq) || freq <= 0) return;
  const osc = audioCtx.createOscillator();
  const env = audioCtx.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  env.gain.setValueAtTime(0.0001, time);
  env.gain.linearRampToValueAtTime(peak, time + 0.005);
  env.gain.exponentialRampToValueAtTime(peak * 0.4, time + duration * 0.5);
  env.gain.exponentialRampToValueAtTime(0.0001, time + duration);
  osc.connect(env).connect(_musicGain);
  osc.start(time); osc.stop(time + duration + 0.02);
}

// Drum voices — each ~30-50ms.
// Drum kit profiles — each kit retunes the kick/snare/hat envelopes for a different
// stylistic feel. Values are deltas/multipliers vs the default.
//   kickPitch: starting frequency of the kick oscillator
//   kickEnd:   end frequency after pitch sweep
//   kickDecay: how long the kick body rings (seconds)
//   snareFreq: bandpass center frequency for the snare noise
//   snareDecay: snare tail length
//   hatHpf:    highpass cutoff for the hi-hat
//   hatDecay:  hi-hat tail length
const DRUM_KITS = {
  default:   { kickPitch: 120, kickEnd: 45, kickDecay: 0.10, snareFreq: 1800, snareDecay: 0.09, hatHpf: 7000, hatDecay: 0.025 },
  lofi:      { kickPitch: 90,  kickEnd: 35, kickDecay: 0.18, snareFreq: 1200, snareDecay: 0.07, hatHpf: 5000, hatDecay: 0.020 },  // muted, low-passed feel
  punchy:    { kickPitch: 150, kickEnd: 50, kickDecay: 0.07, snareFreq: 2200, snareDecay: 0.06, hatHpf: 9000, hatDecay: 0.018 },  // tight, modern, bright
  acoustic:  { kickPitch: 90,  kickEnd: 50, kickDecay: 0.20, snareFreq: 2000, snareDecay: 0.12, hatHpf: 7500, hatDecay: 0.04  },  // warm, ringing, fuller tail
  '808':     { kickPitch: 80,  kickEnd: 30, kickDecay: 0.30, snareFreq: 2500, snareDecay: 0.04, hatHpf: 9000, hatDecay: 0.012 },  // long sub kick, sharp snap, tight hat
  distorted: { kickPitch: 110, kickEnd: 40, kickDecay: 0.13, snareFreq: 1500, snareDecay: 0.10, hatHpf: 6000, hatDecay: 0.022 },  // heavier, muddier
};
function _activeDrumKit() {
  return DRUM_KITS[_sectionField('drumKit')] || DRUM_KITS.default;
}

function _musicKick(time, peak) {
  if (_noteIsPast(time)) return;
  peak = peak * _vol('drum');
  if (peak <= 1e-6) return;
  if (_easyMode) _easyOnsetTimes.push(time);  // easy: capture every kick/snare onset; stale ones are dropped on music restart + by the recency gate
  const kit = _activeDrumKit();
  const osc = audioCtx.createOscillator();
  const env = audioCtx.createGain();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(kit.kickPitch, time);
  osc.frequency.exponentialRampToValueAtTime(kit.kickEnd, time + 0.06);
  env.gain.setValueAtTime(0.0001, time);
  env.gain.linearRampToValueAtTime(peak, time + 0.003);
  env.gain.exponentialRampToValueAtTime(0.0001, time + kit.kickDecay);
  osc.connect(env).connect(_musicGain);
  osc.start(time); osc.stop(time + kit.kickDecay + 0.02);
}

function _musicSnare(time, peak) {
  if (_noteIsPast(time)) return;
  peak = peak * _vol('drum');
  if (peak <= 1e-6) return;
  if (_easyMode) _easyOnsetTimes.push(time);  // easy: capture every kick/snare onset; stale ones are dropped on music restart + by the recency gate
  const kit = _activeDrumKit();
  const sr = audioCtx.sampleRate;
  const len = Math.max(0.05, kit.snareDecay + 0.02);
  const buf = audioCtx.createBuffer(1, Math.floor(sr * len), sr);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  const src = audioCtx.createBufferSource();
  src.buffer = buf;
  const bp = audioCtx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = kit.snareFreq;
  bp.Q.value = 1;
  const env = audioCtx.createGain();
  env.gain.setValueAtTime(0.0001, time);
  env.gain.linearRampToValueAtTime(peak, time + 0.002);
  env.gain.exponentialRampToValueAtTime(0.0001, time + kit.snareDecay);
  src.connect(bp).connect(env).connect(_musicGain);
  src.start(time); src.stop(time + len);
}

// Pad — sustained triangle voice for atmospheric chord triads behind the lead.
// Slow attack and tail so the chord swells under the melody rather than punching.
function _musicPad(time, freq, duration, peak) {
  if (_noteIsPast(time)) return;
  peak = peak * _vol('pad');
  if (peak <= 1e-6) return;  // muted pad — skip (avoids exponentialRamp-to-0 throw)
  const osc = audioCtx.createOscillator();
  const env = audioCtx.createGain();
  osc.type = 'triangle';
  osc.frequency.value = freq;
  env.gain.setValueAtTime(0.0001, time);
  env.gain.linearRampToValueAtTime(peak, time + 0.4);
  env.gain.setValueAtTime(peak, time + Math.max(0.4, duration - 0.4));
  env.gain.exponentialRampToValueAtTime(0.0001, time + duration);
  osc.connect(env).connect(_musicGain);
  osc.start(time); osc.stop(time + duration + 0.1);
}

// Tom — triangle thump at variable frequency, used for drum fills (tom rolls).
function _musicTom(time, freq, peak) {
  if (_noteIsPast(time)) return;
  peak = peak * _vol('drum');
  if (peak <= 1e-6) return;
  const osc = audioCtx.createOscillator();
  const env = audioCtx.createGain();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(freq * 1.6, time);
  osc.frequency.exponentialRampToValueAtTime(freq, time + 0.08);
  env.gain.setValueAtTime(0.0001, time);
  env.gain.linearRampToValueAtTime(peak, time + 0.003);
  env.gain.exponentialRampToValueAtTime(0.0001, time + 0.16);
  osc.connect(env).connect(_musicGain);
  osc.start(time); osc.stop(time + 0.18);
}

// 1-bar drum fill — kick+snare on beat 1, snare on 2, then a rising tom roll over beats 3-4.
// Used at the end of each 16-bar section as a "next section coming" cue.
// Drum density profiles. Each defines kick/snare/hat positions on a 16-slot bar
// (0=beat-1, 4=beat-2, 8=beat-3, 12=beat-4). `lastBarSnare` is an extra snare slot
// fired only on the final bar of each chord (a "tag" snare).
const DRUM_PROFILES = {
  sparse: {
    kicks: [0],                   // heartbeat — kick on beat 1 only
    snares: [8],                  // snare on beat 3 only
    hats: [0, 4, 8, 12],          // hat on quarter-notes (4 per bar)
    lastBarSnare: null,
  },
  normal: {
    kicks: [0, 8],                // beats 1 and 3
    snares: [4, 12],              // backbeat: beats 2 and 4
    hats: [0, 2, 4, 6, 8, 10, 12, 14],   // hat on 8th-notes (8 per bar)
    lastBarSnare: 14,             // extra snare on "and of 4" of last bar
  },
  dense: {
    kicks: [0, 4, 8, 12],         // every beat
    snares: [4, 6, 12, 14],       // backbeat + ghost snares on "and of 2" / "and of 4"
    hats: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],   // 16th-note hats
    lastBarSnare: 14,
  },
};

// Schedule one bar of drums based on the active song's `drumDensity`. Call at the
// start of each bar; the function emits all kick/snare/hat hits. `lastBarOfChord`
// triggers the profile's tag snare if the profile defines one.
// Named drum-pattern presets — one-stop-shop slot bundles for specific genres. When the
// song spec sets `drumPattern`, this overrides the density-tier profile entirely. Each
// preset is a hand-tuned 16th-note slot layout so the LLM picks the rhythmic culture by
// name instead of authoring slots from scratch (which risks off-grid grooves).
const DRUM_PATTERNS = {
  'rock-backbeat':       { kicks: [0, 8],            snares: [4, 12],         hats: [0, 2, 4, 6, 8, 10, 12, 14],         lastBarSnare: 14 },
  'disco-four-on-floor': { kicks: [0, 4, 8, 12],     snares: [4, 12],         hats: [0, 2, 4, 6, 8, 10, 12, 14],         lastBarSnare: null },
  'breakbeat':           { kicks: [0, 7],            snares: [4, 12, 14],     hats: [0, 2, 4, 6, 8, 10, 12, 14],         lastBarSnare: 10 },
  'shuffle':             { kicks: [0, 8],            snares: [4, 12],         hats: [0, 2, 4, 6, 8, 10, 12, 14],         lastBarSnare: 14 },
  'samba':               { kicks: [0, 6, 8, 14],    snares: [3, 11],          hats: [0, 4, 8, 12],                       lastBarSnare: null },
  'trap-rolls':          { kicks: [0, 6, 8],         snares: [4, 12],         hats: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15], lastBarSnare: null },
  'bossa':               { kicks: [0, 5, 8, 11],     snares: [],              hats: [0, 2, 4, 6, 8, 10, 12, 14],         lastBarSnare: null },
  'motorik':             { kicks: [0, 4, 8, 12],     snares: [4, 12],         hats: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15], lastBarSnare: null },
  'half-time':           { kicks: [0],               snares: [8],             hats: [0, 2, 4, 6, 8, 10, 12, 14],         lastBarSnare: null },
  'dembow':              { kicks: [0, 6, 8, 14],    snares: [4, 12],          hats: [0, 2, 4, 6, 8, 10, 12, 14],         lastBarSnare: null },
};
function _activeDrumPattern() {
  const name = _sectionField('drumPattern');
  return (name && DRUM_PATTERNS[name]) || null;
}

// Drum-nod amount (0..1) for the co-op peer avatar's head bob. Read off the observer's OWN synced
// beat grid + the live drum pattern, so it costs zero netcode: the song and BPM are identical on both
// clients, so the nod lands on the same kick/snare hits the observer is hearing. We find the most recent
// kick or snare onset within the bar (wrapping from the previous bar), then return a quick attack/decay
// envelope from it — chin snaps down on the hit, eases back up over ~half a beat. Kicks nod deeper than
// snares. Used in the render loop via _mp.setHeadBob(); harmless (returns 0) before music starts.
function _avatarDrumNod() {
  if (!_musicStarted || !audioCtx) return 0;
  const profile = _activeDrumPattern() || DRUM_PROFILES[_sectionField('drumDensity')] || DRUM_PROFILES.normal;
  if (!profile) return 0;
  const sixteenth = MUSIC_BEAT / 4;
  const barLen = MUSIC_BEAT * 4;
  const elapsed = audioCtx.currentTime - _musicLoopStartTime;
  const barPhase = ((elapsed % barLen) + barLen) % barLen;
  const attack = 0.045;             // s — quick chin-down swing into the hit
  const decay = MUSIC_BEAT * 0.5;   // ease back up over ~half a beat
  let nod = 0;
  const sample = (slot, weight) => {
    let dt = barPhase - slot * sixteenth;
    if (dt < 0) dt += barLen;       // onset is ahead in this bar -> it actually fired late in the previous one
    if (dt > barLen * 0.5) return;  // too long ago to still be nodding
    const env = dt < attack ? dt / attack : Math.exp(-(dt - attack) / decay);
    const v = env * weight;
    if (v > nod) nod = v;
  };
  for (const s of profile.kicks) sample(s, 1.0);
  for (const s of profile.snares) sample(s, 0.6);
  return nod;
}

function _scheduleDrums(barStart, lastBarOfChord) {
  // Drum pattern preset takes priority; otherwise fall back to density-tier profile.
  const profile = _activeDrumPattern() || DRUM_PROFILES[_sectionField('drumDensity')] || DRUM_PROFILES.normal;
  const sixteenth = MUSIC_BEAT / 4;
  for (const slot of profile.kicks) _musicKick(barStart + slot * sixteenth, slot === 0 ? 0.55 : 0.50);
  for (const slot of profile.snares) _musicSnare(barStart + slot * sixteenth, 0.40);
  for (const slot of profile.hats) _musicHat(barStart + slot * sixteenth, 0.10);
  if (lastBarOfChord && profile.lastBarSnare !== null && profile.lastBarSnare !== undefined) {
    _musicSnare(barStart + profile.lastBarSnare * sixteenth, 0.32);
  }
  // Add-on layers that work alongside any pattern/density: clap layer parallels the snare
  // slots (hip-hop / dance / R&B); open-hat layer adds long-decay "tss" on off-beat 8ths
  // (disco / funk / house). Both togglable independently of the underlying drum pattern.
  if (_sectionField('useClap')) {
    for (const slot of profile.snares) _musicClap(barStart + slot * sixteenth, 0.32);
  }
  if (_sectionField('useOpenHat')) {
    for (const slot of [2, 6, 10, 14]) _musicOpenHat(barStart + slot * sixteenth, 0.09);
  }
}

// Open hat — same noise source as closed hat but ~6× longer decay and slightly wider
// HPF, gives the "tss" sustain that defines disco/funk/house grooves. Pairs with closed
// hats on the on-beat; this fires on the off-beats.
function _musicOpenHat(time, peak) {
  if (_noteIsPast(time)) return;
  peak = peak * _vol('drum');
  if (peak <= 1e-6) return;
  const sr = audioCtx.sampleRate;
  const len = 0.22;
  const buf = audioCtx.createBuffer(1, Math.floor(sr * len), sr);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  const src = audioCtx.createBufferSource();
  src.buffer = buf;
  const hp = audioCtx.createBiquadFilter();
  hp.type = 'highpass';
  const kit = _activeDrumKit();
  hp.frequency.value = Math.max(4000, kit.hatHpf - 1500);  // a bit wider than closed hat
  const env = audioCtx.createGain();
  env.gain.setValueAtTime(0.0001, time);
  env.gain.linearRampToValueAtTime(peak, time + 0.001);
  env.gain.exponentialRampToValueAtTime(0.0001, time + 0.18);  // long decay = "tss" sustain
  src.connect(hp).connect(env).connect(_musicGain);
  src.start(time); src.stop(time + len);
}

// Clap — three short noise bursts in quick succession with mild offsets. Real claps are
// multi-hand, never perfectly synchronized; sampling that captures the "thwack" character
// that distinguishes a clap from a tighter snare. Bandpassed to ~1500 Hz for the body.
function _musicClap(time, peak) {
  if (_noteIsPast(time)) return;
  peak = peak * _vol('drum');
  if (peak <= 1e-6) return;
  const sr = audioCtx.sampleRate;
  const offsets = [0, 0.012, 0.024, 0.038];  // 4 micro-bursts spread over ~38ms
  for (let n = 0; n < offsets.length; n++) {
    const off = offsets[n];
    const len = 0.04;
    const buf = audioCtx.createBuffer(1, Math.floor(sr * len), sr);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    const src = audioCtx.createBufferSource();
    src.buffer = buf;
    const bp = audioCtx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1500;
    bp.Q.value = 2;
    const env = audioCtx.createGain();
    // Slight peak shaping — earlier hits softer, last hit louder, gives the "clap" rhythm.
    const localPeak = peak * (n === offsets.length - 1 ? 1.0 : 0.6);
    env.gain.setValueAtTime(0.0001, time + off);
    env.gain.linearRampToValueAtTime(localPeak, time + off + 0.002);
    env.gain.exponentialRampToValueAtTime(0.0001, time + off + 0.045);
    src.connect(bp).connect(env).connect(_musicGain);
    src.start(time + off); src.stop(time + off + len);
  }
}

// Big fill — replaces normal drums on the FINAL bar of the 16-bar loop.
// 1 kick + 2 snares on beats 1-2, then 8 toms on 16ths across beats 3-4.
function _scheduleDrumFill(barStart) {
  _musicKick(barStart + 0 * MUSIC_BEAT, 0.55);
  _musicSnare(barStart + 0 * MUSIC_BEAT, 0.30);
  _musicSnare(barStart + 1 * MUSIC_BEAT, 0.40);
  const tomFreqs = [70, 80, 95, 110, 130, 150, 175, 200];
  for (let i = 0; i < 8; i++) {
    _musicTom(barStart + 2 * MUSIC_BEAT + i * (MUSIC_BEAT / 4), tomFreqs[i], 0.45);
  }
}

// Mid-loop fill — overlaid on top of the normal drum pattern at bar 7 (the
// 8-bar phrase boundary). 4 snare 16ths on beat 4 + a tom pickup leading into
// bar 8. Lighter than the big fill — punctuation, not climax.
function _scheduleDrumFillMid(barStart) {
  for (let i = 0; i < 4; i++) {
    _musicSnare(barStart + 3 * MUSIC_BEAT + i * (MUSIC_BEAT / 4), 0.30);
  }
  _musicTom(barStart + 3.75 * MUSIC_BEAT, 110, 0.32);
}

// Short bandpassed noise tick at a high pitch — math-rock woodblock for the polyrhythm layer.
function _musicWoodblock(time, peak) {
  if (_noteIsPast(time)) return;
  const sr = audioCtx.sampleRate;
  const len = 0.025;
  const buf = audioCtx.createBuffer(1, Math.floor(sr * len), sr);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  const src = audioCtx.createBufferSource();
  src.buffer = buf;
  const bp = audioCtx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = 2350;  // sits between standard pitches — slight microtonal flavour
  bp.Q.value = 4;
  const env = audioCtx.createGain();
  env.gain.setValueAtTime(0.0001, time);
  env.gain.linearRampToValueAtTime(peak, time + 0.001);
  env.gain.exponentialRampToValueAtTime(0.0001, time + len);
  src.connect(bp).connect(env).connect(_musicGain);
  src.start(time); src.stop(time + len);
}

function _musicHat(time, peak) {
  if (_noteIsPast(time)) return;
  peak = peak * _vol('drum');
  if (peak <= 1e-6) return;
  const sr = audioCtx.sampleRate;
  const len = 0.03;
  const buf = audioCtx.createBuffer(1, Math.floor(sr * len), sr);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  const src = audioCtx.createBufferSource();
  src.buffer = buf;
  const hp = audioCtx.createBiquadFilter();
  hp.type = 'highpass';
  const kit = _activeDrumKit();
  hp.frequency.value = kit.hatHpf;
  const env = audioCtx.createGain();
  env.gain.setValueAtTime(0.0001, time);
  env.gain.linearRampToValueAtTime(peak, time + 0.001);
  env.gain.exponentialRampToValueAtTime(0.0001, time + kit.hatDecay);
  src.connect(hp).connect(env).connect(_musicGain);
  src.start(time); src.stop(time + Math.max(len, kit.hatDecay));
}

// Schedule one bar of bass following a chord-tone-index pattern (4 entries — one per beat).
// 0=root, 1=third, 2=fifth, 3=octave-up. The active song's bassDensity scales the rhythm:
//   sparse: only the first note plays, sustained for the whole bar (drone)
//   normal: 4 quarter notes (the pattern as written)
//   dense:  8 eighth notes (each pattern entry doubled)
// `bassDiv` halves or quarters frequencies to keep bass in proper register for high-key songs.
// Bass pattern accepts either a 4-element array (quarter-note pattern, the legacy shape)
// or an 8-element array (eighth-note pattern, used in dense mode for true eighth-note
// motion rather than the legacy "repeat each chord-tone twice" fill). Each value is a
// chord-tone index: 0=root, 1=third, 2=fifth, 3=octave-up of root.
function _scheduleBassPattern(barStart, pattern, chord, bassDiv) {
  const density = _sectionField('bassDensity') || 'normal';
  const tones = [chord.r, chord.t, chord.f, chord.o];
  // Helper: pull a chord tone safely from any index in either pattern length.
  const tone = (i) => {
    const v = pattern[i % pattern.length];
    if (typeof v !== 'number') return tones[0];
    const idx = ((v | 0) % 4 + 4) % 4;
    return tones[idx];
  };
  if (density === 'sparse') {
    _musicVoice(tone(0) / bassDiv, barStart, MUSIC_BEAT * 0.85, 'square', 0.18, 'bass');
  } else if (density === 'medium') {
    _musicVoice(tone(0) / bassDiv, barStart + 0 * MUSIC_BEAT, MUSIC_BEAT * 0.85, 'square', 0.18, 'bass');
    const beat3Idx = pattern.length === 8 ? 4 : 2;
    _musicVoice(tone(beat3Idx) / bassDiv, barStart + 2 * MUSIC_BEAT, MUSIC_BEAT * 0.85, 'square', 0.18, 'bass');
  } else if (density === 'dense') {
    const eighth = MUSIC_BEAT / 2;
    if (pattern.length === 8) {
      for (let i = 0; i < 8; i++) {
        _musicVoice(tone(i) / bassDiv, barStart + i * eighth, eighth * 0.85, 'square', 0.18, 'bass');
      }
    } else {
      for (let i = 0; i < 4; i++) {
        const f = tone(i) / bassDiv;
        _musicVoice(f, barStart + i * MUSIC_BEAT, eighth * 0.85, 'square', 0.18, 'bass');
        _musicVoice(f, barStart + i * MUSIC_BEAT + eighth, eighth * 0.85, 'square', 0.18, 'bass');
      }
    }
  } else {
    for (let i = 0; i < 4; i++) {
      const idx = pattern.length === 8 ? i * 2 : i;
      _musicVoice(tone(idx) / bassDiv, barStart + i * MUSIC_BEAT, MUSIC_BEAT * 0.85, 'square', 0.18, 'bass');
    }
  }
}

// Pick the active busyRhythm — falls through to chunky (the W1 default behavior).
// Reads via _sectionField so a section can override busyRhythm per arrangement.
function _activeBusyRhythm() {
  return BUSY_RHYTHMS[_sectionField('busyRhythm')] || BUSY_RHYTHMS.chunky;
}

// Counter-melody — a second voice playing chord-tone arpeggios at quarter-note pace
// (slower than the main lead's eighths). Same waveform as the lead, but at half volume
// and using a different chord-tone sequence (fifth-third-root-octave) so it sits as a
// distinct voice rather than doubling the lead. Toggled on per song via `counterMelody: true`.
function _scheduleCounterMelody(barStart, chord, octaveMult, waveform) {
  const counterTones = [chord.f, chord.t, chord.r, chord.o];
  for (let i = 0; i < 4; i++) {
    _musicVoice(counterTones[i] * octaveMult, barStart + i * MUSIC_BEAT, MUSIC_BEAT * 0.85, waveform, 0.05);
  }
}

// Resolve a motif for a specific chord. Accepts either:
//  - a flat array of chord-tone indices (applied uniformly to all 4 chords) — legacy format
//  - a 2D array of 4 sub-arrays (one per chord) — per-chord melodic narrative
// Returns null if motif is undefined.
function _motifForChord(motif, chordIdx) {
  if (!motif) return null;
  if (Array.isArray(motif[0])) return motif[chordIdx % motif.length];
  return motif;
}

// Schedule one 8-bar loop starting at startTime. Returns the timestamp when it ends.
// 4 chords × 2 bars per chord. Mid-fill on bar 3 (4-bar phrase boundary), big fill on bar 7.
// Schedule a "busy 4/4" lead pattern — 10 notes per bar on a 16th-note grid mixing 8ths and a
// short 16th-note flourish. Same downbeat lock, no triplets, just denser than straight eighths.
// `slots` is positions in 16ths (0..15), `notes` is the freq for each slot, parallel arrays.
function _scheduleBusyLead(barStart, slots, notes, octaveMult, peak, waveform = 'square') {
  const dt = MUSIC_BAR / 16;
  for (let i = 0; i < slots.length; i++) {
    const nextSlot = i + 1 < slots.length ? slots[i + 1] : 16;
    const dur = (nextSlot - slots[i]) * dt * 0.85;
    _musicVoice(notes[i] * octaveMult, barStart + slots[i] * dt + _swingNudgeForSlot(slots[i]), dur, waveform, peak);
  }
}

// Variable-length chord scheduling. Returns an array of { chordIdx, chord, startBar, bars }
// describing each chord's slot in the 8-bar loop. Each chord's `bars` defaults to 2 if
// unspecified (preserves the legacy 4-chord-each-2-bars layout). LLM may now author any
// number of chords with any per-chord bar lengths, as long as they sum to MUSIC_LOOP_BARS (8).
// Examples:
//   • Legacy: 4 chords × 2 bars = 8 bars         (old default behavior)
//   • Drone:  1 chord × 8 bars = 8 bars          (single sustained chord)
//   • Stabs:  8 chords × 1 bar = 8 bars          (fast harmonic motion)
//   • Mixed:  3 chords (2,4,2) bars = 8 bars     (uneven phrase shape)
function _chordSchedule() {
  const chords = _activeSong.chords || [];
  const out = [];
  let startBar = 0;
  for (let i = 0; i < chords.length; i++) {
    const chord = chords[i];
    const bars = (typeof chord.bars === 'number' && chord.bars > 0) ? chord.bars : 2;
    out.push({ chordIdx: i, chord, startBar, bars });
    startBar += bars;
    if (startBar >= MUSIC_LOOP_BARS) break;  // safety: cap at loop length
  }
  return out;
}

function _scheduleMusicLoop(startTime, busy = false) {
  // root,3rd,5th,octave (in Hz) per chord. Read from the active song spec.
  const chords = _activeSong.chords;

  for (const entry of _chordSchedule()) {
    const chordIdx = entry.chordIdx;
    // Apply per-section transpose to chord tones. All downstream reads of chord.r/t/f/o/s
    // (pad voicing, bass pattern tones, lead motif tones) automatically pick up the shift.
    const chord = _transposedChord(entry.chord);
    const chordStart = startTime + entry.startBar * MUSIC_BAR;
    const chordBars = entry.bars;

    for (let bar = 0; bar < chordBars; bar++) {
      const barStart = chordStart + bar * MUSIC_BAR;
      const lastBarOfChord = bar === chordBars - 1;
      // Absolute bar index in the 8-bar loop (0..7). Used for whole-loop markers (final
      // bar fill, mid-loop fill) so they fire at the right musical positions regardless
      // of how the chord progression is partitioned.
      const absBarIdx = entry.startBar + bar;
      const isLoopFinalBar = absBarIdx === MUSIC_LOOP_BARS - 1;

      // DRUMS — pattern picked by the active song's drumDensity (sparse/normal/dense).
      // Bar 7: big tom-roll fill (next-section cue). Bar 3: light snare flam overlay
      // marking the 4-bar phrase boundary.
      const isLoopMidBar = absBarIdx === (MUSIC_LOOP_BARS / 2) - 1;
      if (isLoopFinalBar) {
        _scheduleDrumFill(barStart);
      } else {
        _scheduleDrums(barStart, lastBarOfChord);
        if (isLoopMidBar) _scheduleDrumFillMid(barStart);
      }

      // BASS — root, root, fifth, root (quarter notes), one octave below the chord root.
      // Bass octave: drop an extra octave when chord roots are high (above ~200 Hz)
      // so high-key songs (e.g. C-major / Lydian / "calm") still have a real bass register.
      const bassDiv = chord.r > 200 ? 4 : 2;
      // Tonal bass — root-root-fifth-root pattern (anchoring). Density scales rhythm.
      _scheduleBassPattern(barStart, _sectionField('bassPattern') || [0, 0, 2, 0], chord, bassDiv);

      // LEAD — motifs may be a single 8/10-note array (broadcast to all chords) OR
      // an array of 4 sub-arrays (one motif per chord, for melodic narrative). _motifTones
      // returns chord tones (default — indices 0-4 for r/t/f/o/s) or the full scaleHz
      // array (when song specifies `motifMode: 'scale'` for stepwise + modal melodies).
      const motif = _motifForChord(_activeSong.leadMotif, chordIdx) || [0, 1, 2, 3, 2, 1, 0, 1];
      const tones = _motifTones(chord);
      const leadWf = _sectionField('leadWaveform') || 'square';
      if (busy) {
        // Tonal+ alternates bar 0 / bar 1 of the active busyRhythm — same shape rule as var+/build+.
        const rhythm = _activeBusyRhythm();
        const slots = (bar === 0) ? rhythm.bar0 : rhythm.bar1;
        const motifBusy = _motifForChord(_activeSong.leadMotifBusy, chordIdx) || [0, 1, 2, 3, 3, 2, 1, 0, 1, 2];
        const notes = motifBusy.map((idx) => tones[idx]);
        _scheduleBusyLead(barStart, slots, notes, 1, 0.10, leadWf);
      } else {
        for (let i = 0; i < 8; i++) {
          _musicVoice(tones[motif[i]], barStart + i * (MUSIC_BEAT / 2) + _swingNudgeForSlot(i * 2), (MUSIC_BEAT / 2) * 0.85, leadWf, 0.10);
        }
      }
      if (_sectionField('counterMelody')) _scheduleCounterMelody(barStart, chord, 1, leadWf);
    }
  }
  return startTime + MUSIC_LOOP_DURATION;
}

// Variation loop — same groove as the tonal loop, with small melodic shifts per bar within each
// chord and one bent third (50 cents flat) at the end of each chord cycle as a "blue" pickup.
// withPercussion: layer a 3-against-2 woodblock polyrhythm on top (Angine de Poitrine math-rock).
function _scheduleMicrotonalLoop(startTime, withPercussion = false, busy = false) {
  const QUARTER_DOWN = Math.pow(2, -50 / 1200);
  const chords = _activeSong.chords;

  const leadOctave = withPercussion ? 2 : 1;  // P lifts the lead an octave up for energy
  for (const entry of _chordSchedule()) {
    const chordIdx = entry.chordIdx;
    const chord = _transposedChord(entry.chord);
    const chordBars = entry.bars;
    const tBent = chord.t * QUARTER_DOWN;
    const chordStart = startTime + entry.startBar * MUSIC_BAR;

    // Pad — sustained chord triad spanning this chord's bars (variable length).
    const padDuration = chordBars * MUSIC_BAR - 0.05;
    _musicPad(chordStart, chord.r, padDuration, 0.06);
    _musicPad(chordStart, chord.t, padDuration, 0.06);
    _musicPad(chordStart, chord.f, padDuration, 0.06);
    if (typeof chord.s === 'number') _musicPad(chordStart, chord.s, padDuration, 0.05);

    for (let bar = 0; bar < chordBars; bar++) {
      const barStart = chordStart + bar * MUSIC_BAR;
      const lastBarOfChord = bar === chordBars - 1;
      const absBarIdx = entry.startBar + bar;
      const isLoopFinalBar = absBarIdx === MUSIC_LOOP_BARS - 1;

      // Drums — same density as tonal loop. Bar 7: big fill. Bar 3: mid fill.
      const isLoopMidBar = absBarIdx === (MUSIC_LOOP_BARS / 2) - 1;
      if (isLoopFinalBar) {
        _scheduleDrumFill(barStart);
      } else {
        _scheduleDrums(barStart, lastBarOfChord);
        if (isLoopMidBar) _scheduleDrumFillMid(barStart);
      }

      // Bass — walking pattern (root-third-fifth-third) for var/poly. Density scales rhythm.
      const bassDiv = chord.r > 200 ? 4 : 2;
      _scheduleBassPattern(barStart, _sectionField('bassPatternVar') || [0, 1, 2, 1], chord, bassDiv);

      // Polyrhythmic woodblock — 3 hits per 2 beats (6 per bar), runs against the 4/4 drum pulse.
      // Adds rhythmic tension without changing the underlying groove. Math-rock signature layer.
      // Skip on the final bar so the drum fill doesn't get crowded.
      if (withPercussion && !isLoopFinalBar) {
        for (let i = 0; i < 6; i++) {
          _musicWoodblock(barStart + i * (MUSIC_BAR / 6), 0.32);
        }
      }

      // Lead — 2 bars per chord. Bar 0 uses a front-loaded 16th pattern; bar 1 uses
      // the end-of-chord pickup pattern with bent-third on the final note.
      // Song's motif (if defined) supplies notes; otherwise per-bar defaults apply.
      const tones = _motifTones(chord);
      const songMotifVar = _motifForChord(_activeSong.leadMotifVar, chordIdx);
      const songMotifVarBusy = _motifForChord(_activeSong.leadMotifVarBusy, chordIdx);
      const leadWf = _sectionField('leadWaveform') || 'square';
      if (busy) {
        const rhythm = _activeBusyRhythm();
        const slots = (bar === 0) ? rhythm.bar0 : rhythm.bar1;
        let notes;
        if (songMotifVarBusy) {
          notes = songMotifVarBusy.map((idx) => tones[idx]);
        } else if (bar === 0) {
          notes = [chord.r, chord.t, chord.f, chord.o, chord.t, chord.f, chord.t, chord.r, chord.t, chord.f];
        } else {
          notes = [chord.r, chord.t, chord.f, chord.o, chord.f, chord.t, chord.r, chord.t, chord.f, tBent];
        }
        if (bar === 1) notes[notes.length - 1] = tBent;
        _scheduleBusyLead(barStart, slots, notes, leadOctave, 0.10, leadWf);
      } else {
        let leadNotes;
        if (songMotifVar) {
          leadNotes = songMotifVar.map((idx) => tones[idx]);
          if (bar === 1) leadNotes[leadNotes.length - 1] = tBent;
        } else if (bar === 0) {
          leadNotes = [chord.r, chord.t, chord.f, chord.o, chord.f, chord.t, chord.r, chord.t];
        } else {
          leadNotes = [chord.r, chord.t, chord.f, chord.o, chord.f, chord.t, chord.r, tBent];
        }
        for (let i = 0; i < 8; i++) {
          _musicVoice(leadNotes[i] * leadOctave, barStart + i * (MUSIC_BEAT / 2) + _swingNudgeForSlot(i * 2), (MUSIC_BEAT / 2) * 0.85, leadWf, 0.10);
        }
      }
      if (_sectionField('counterMelody')) _scheduleCounterMelody(barStart, chord, leadOctave, leadWf);
    }
  }
  return startTime + MUSIC_LOOP_DURATION;
}

// Build-up loop — natural snare-based escalation back to the start. Inherits the variation/poly
// feel (pad, octave-up lead, bent third pickups) and progressively adds snare density across
// 8 bars. Bar 8 climaxes on beats 1-2 and releases on beats 3-4 so the next tonal bar lands fresh.
// Snare escalation: bars 0-1 backbeat, 2-3 + ghosts, 4-5 every quarter, 6 every 8th, 7 climax-release.
function _scheduleBuildupLoop(startTime, busy = false) {
  const QUARTER_DOWN = Math.pow(2, -50 / 1200);
  const chords = _activeSong.chords;
  const leadOctave = 2;

  for (const entry of _chordSchedule()) {
    const chordIdx = entry.chordIdx;
    const chord = _transposedChord(entry.chord);
    const chordBars = entry.bars;
    const tBent = chord.t * QUARTER_DOWN;
    const chordStart = startTime + entry.startBar * MUSIC_BAR;

    const padDuration = chordBars * MUSIC_BAR - 0.05;
    _musicPad(chordStart, chord.r, padDuration, 0.06);
    _musicPad(chordStart, chord.t, padDuration, 0.06);
    _musicPad(chordStart, chord.f, padDuration, 0.06);
    if (typeof chord.s === 'number') _musicPad(chordStart, chord.s, padDuration, 0.05);

    for (let bar = 0; bar < chordBars; bar++) {
      const barStart = chordStart + bar * MUSIC_BAR;
      const totalBarIdx = entry.startBar + bar;
      const isLastBar = totalBarIdx === MUSIC_LOOP_BARS - 1;
      const lastBarOfChord = bar === chordBars - 1;

      // Lead — bar 0 uses front-loaded shape, bar 1 uses end-of-chord pickup with bent third.
      // Song's motif (if defined) supplies notes; per-chord motif resolved via _motifForChord.
      const tones = _motifTones(chord);
      const songMotifBuild = _motifForChord(_activeSong.leadMotifBuild, chordIdx);
      const songMotifBuildBusy = _motifForChord(_activeSong.leadMotifBuildBusy, chordIdx);
      const leadWf = _sectionField('leadWaveform') || 'square';
      let leadNotes;
      if (songMotifBuild) {
        leadNotes = songMotifBuild.map((idx) => tones[idx]);
        if (bar === 1) leadNotes[leadNotes.length - 1] = tBent;
      } else if (bar === 0) {
        leadNotes = [chord.r, chord.t, chord.f, chord.o, chord.f, chord.t, chord.r, chord.t];
      } else {
        leadNotes = [chord.r, chord.t, chord.f, chord.o, chord.f, chord.t, chord.r, tBent];
      }

      // Busy variant — slot pattern from active busyRhythm.
      let busySlots, busyNotes;
      if (busy) {
        const rhythm = _activeBusyRhythm();
        busySlots = (bar === 0) ? rhythm.bar0 : rhythm.bar1;
        if (songMotifBuildBusy) {
          busyNotes = songMotifBuildBusy.map((idx) => tones[idx]);
        } else if (bar === 0) {
          busyNotes = [chord.r, chord.t, chord.f, chord.o, chord.t, chord.f, chord.t, chord.r, chord.t, chord.f];
        } else {
          busyNotes = [chord.r, chord.t, chord.f, chord.o, chord.f, chord.t, chord.r, chord.t, chord.f, tBent];
        }
        if (bar === 1) busyNotes[busyNotes.length - 1] = tBent;
      }

      if (isLastBar) {
        // CLIMAX beats 1-2: snare 16ths + descending tom hits.
        for (let i = 0; i < 8; i++) {
          _musicSnare(barStart + i * (MUSIC_BEAT / 4), 0.42);
        }
        const tomFreqs = [200, 160, 120, 80];
        for (let i = 0; i < 4; i++) {
          _musicTom(barStart + i * (MUSIC_BEAT / 2), tomFreqs[i], 0.50);
        }
        // RELEASE beats 3-4: silence except the pad. Bass and lead truncate at beat 2.
        _musicVoice(chord.r / 2, barStart + 0 * MUSIC_BEAT, MUSIC_BEAT * 0.85, 'square', 0.18, 'bass');
        _musicVoice(chord.r / 2, barStart + 1 * MUSIC_BEAT, MUSIC_BEAT * 0.85, 'square', 0.18, 'bass');
        for (let i = 0; i < 4; i++) {
          _musicVoice(leadNotes[i] * leadOctave, barStart + i * (MUSIC_BEAT / 2) + _swingNudgeForSlot(i * 2), (MUSIC_BEAT / 2) * 0.85, leadWf, 0.10);
        }
        continue;
      }

      // Kick + hat backbone — uses the active song's drumDensity. Snare escalation
      // (below) is independent — the buildup's escalating snare IS the section's identity.
      const buildProfile = DRUM_PROFILES[_sectionField('drumDensity')] || DRUM_PROFILES.normal;
      const sixteenth16 = MUSIC_BEAT / 4;
      for (const slot of buildProfile.kicks) _musicKick(barStart + slot * sixteenth16, slot === 0 ? 0.55 : 0.50);
      for (const slot of buildProfile.hats) _musicHat(barStart + slot * sixteenth16, 0.10);

      // Snare escalation across 8 bars.
      // Bars 0-1: standard backbeat (snare on 2 and 4).
      // Bars 2-3: + ghost snares on "and" of 2 and 4.
      // Bars 4-5: snare on every quarter beat.
      // Bar 6: snare on every 8th (rolling tension before climax).
      _musicSnare(barStart + 1 * MUSIC_BEAT, 0.40);
      _musicSnare(barStart + 3 * MUSIC_BEAT, 0.40);
      if (totalBarIdx >= 2 && totalBarIdx < 4) {
        _musicSnare(barStart + 1.5 * MUSIC_BEAT, 0.22);
        _musicSnare(barStart + 3.5 * MUSIC_BEAT, 0.22);
      } else if (totalBarIdx >= 4 && totalBarIdx < 6) {
        _musicSnare(barStart + 0 * MUSIC_BEAT, 0.30);
        _musicSnare(barStart + 2 * MUSIC_BEAT, 0.30);
      } else if (totalBarIdx === 6) {
        for (let i = 0; i < 8; i++) {
          _musicSnare(barStart + i * (MUSIC_BEAT / 2), 0.32);
        }
      }
      if (lastBarOfChord && totalBarIdx < 6) _musicSnare(barStart + 3.5 * MUSIC_BEAT, 0.32);

      // Bass — walking pattern (matches var/poly), octave-drop for high-key songs, density-aware.
      const bassDiv = chord.r > 200 ? 4 : 2;
      _scheduleBassPattern(barStart, _sectionField('bassPatternVar') || [0, 1, 2, 1], chord, bassDiv);
      if (busy) {
        _scheduleBusyLead(barStart, busySlots, busyNotes, leadOctave, 0.10, leadWf);
      } else {
        for (let i = 0; i < 8; i++) {
          _musicVoice(leadNotes[i] * leadOctave, barStart + i * (MUSIC_BEAT / 2) + _swingNudgeForSlot(i * 2), (MUSIC_BEAT / 2) * 0.85, leadWf, 0.10);
        }
      }
      if (_sectionField('counterMelody')) _scheduleCounterMelody(barStart, chord, leadOctave, leadWf);
    }
  }
  return startTime + MUSIC_LOOP_DURATION;
}

let _musicLoopIndex = 0;
let _musicTimeoutId = null;  // pending re-schedule timeout — cleared on skip to avoid duplicate scheduling
// Eight-section progression: first pass (4 sections) is the original 4/4 form with the lead
// in straight eighths (8 notes/bar). Second pass re-runs all four with a "busy" 4/4 lead —
// 10 notes/bar on a 16th-note grid (mix of 8ths and short 16th-note flourishes), with each
// bar getting a different rhythmic + melodic shape. No triplets; same chords, tempo, bars,
// downbeats locked; lead just packs more notes per bar with a fresh contour.
const _musicSections = [
  (st) => _scheduleMusicLoop(st),
  (st) => _scheduleMicrotonalLoop(st, false),
  (st) => _scheduleMicrotonalLoop(st, true),
  (st) => _scheduleBuildupLoop(st),
  (st) => _scheduleMusicLoop(st, true),
  (st) => _scheduleMicrotonalLoop(st, false, true),
  (st) => _scheduleMicrotonalLoop(st, true, true),
  (st) => _scheduleBuildupLoop(st, true),
];
function _scheduleNextLoop() {
  if (!_musicStarted) return;
  // Set section index BEFORE invoking — all _sectionField() / _vol() / _transposedChord()
  // reads inside the section function need to see the right index.
  _currentSection = _musicLoopIndex % _musicSections.length;
  const section = _musicSections[_currentSection];
  // Wrap the scheduling call so any exception inside a section function (e.g. an exotic
  // per-section override that produces an invalid Web Audio param) doesn't kill the
  // re-schedule below — without this, music goes silent forever on a single bad section.
  // We still advance the loop time as if the section played, so timing stays sane and the
  // next section gets its turn.
  try {
    _musicNextStart = section(_musicNextStart);
  } catch (e) {
    console.warn('[music] section', _currentSection, 'threw — skipping its events:', e);
    _musicNextStart += MUSIC_LOOP_DURATION;
  }
  _musicLoopIndex++;
  // Re-trigger about half a loop before this one ends — keeps next iteration always queued.
  const msUntilNext = (_musicNextStart - audioCtx.currentTime - MUSIC_LOOP_DURATION / 2) * 1000;
  _musicTimeoutId = setTimeout(_scheduleNextLoop, Math.max(50, msUntilNext));
}

let _musicLoopStartTime = 0;
function startMusic() {
  if (_musicStarted) return;
  if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)({ latencyHint: 'interactive' });
  _ensureAudioGraph();
  _musicGain = audioCtx.createGain();
  _musicGain.gain.setValueAtTime(0.0001, audioCtx.currentTime);
  _musicGain.gain.linearRampToValueAtTime(_MUSIC_VOLUME_TARGET, audioCtx.currentTime + 2.0);  // 2s fade-in to user-set level
  _musicGain.connect(_masterBus);
  _buildMusicEffects();
  _applyMusicEffectsFromSong();
  _musicNextStart = audioCtx.currentTime + 0.1;
  _musicLoopStartTime = _musicNextStart;
  _musicStarted = true;
  _easyOnsetTimes.length = 0;  // fresh timeline -> drop any onsets queued against the old one
  _scheduleNextLoop();
  // In co-op, ask whoever's already playing for their song position so we drop into the same part.
  if (_mp && _mp.send) _mp.send({ t: 'mreq' });
}

// Reverb (convolver with procedural decaying-noise IR) and delay (with feedback loop)
// run as parallel post-volume sends off _musicGain. Both sends default to 0 wetness;
// per-song `reverbWetness` and `delayFeedback` raise them. Following _musicGain (the
// volume-controlled tap) means wet signals follow the music volume slider and ducking.
let _musicReverbSend = null;
let _musicReverb = null;
let _musicDelaySend = null;
let _musicDelay = null;
let _musicDelayFeedback = null;

function _buildMusicEffects() {
  // Tear down previous chain (reset on startMusic so dev fast-forwards rebuild cleanly).
  for (const n of [_musicReverbSend, _musicReverb, _musicDelaySend, _musicDelay, _musicDelayFeedback]) {
    if (n) try { n.disconnect(); } catch {}
  }
  // Reverb: ConvolverNode with a procedural exponentially-decaying-noise impulse response
  // (~2s). Cheap, runtime-built, no asset to ship. Stereo for spatial wash.
  const sr = audioCtx.sampleRate;
  const irLen = Math.floor(sr * 2.0);
  const ir = audioCtx.createBuffer(2, irLen, sr);
  for (let ch = 0; ch < 2; ch++) {
    const data = ir.getChannelData(ch);
    for (let i = 0; i < irLen; i++) {
      const decay = Math.exp(-3 * i / irLen);  // exponential tail, ~3 time constants over 2s
      data[i] = (Math.random() * 2 - 1) * decay;
    }
  }
  _musicReverb = audioCtx.createConvolver();
  _musicReverb.buffer = ir;
  _musicReverbSend = audioCtx.createGain();
  _musicReverbSend.gain.value = 0;
  _musicGain.connect(_musicReverbSend);
  _musicReverbSend.connect(_musicReverb);
  _musicReverb.connect(_masterBus);

  // Delay: 3/16-note delay (a "dotted-eighth" feel at 4/4) with feedback loop.
  // Feedback gain controls how many echoes you hear; send gain controls overall wetness.
  _musicDelay = audioCtx.createDelay(2.0);
  _musicDelay.delayTime.value = MUSIC_BEAT * 0.75;  // 3/16 note — classic dub feel
  _musicDelayFeedback = audioCtx.createGain();
  _musicDelayFeedback.gain.value = 0;
  _musicDelaySend = audioCtx.createGain();
  _musicDelaySend.gain.value = 0;
  _musicGain.connect(_musicDelaySend);
  _musicDelaySend.connect(_musicDelay);
  _musicDelay.connect(_musicDelayFeedback);
  _musicDelayFeedback.connect(_musicDelay);  // feedback loop
  _musicDelay.connect(_masterBus);
}

// Apply the active song's effect parameters. `reverbWetness` (0-1) sets reverb send;
// `delayFeedback` (0-0.85) sets feedback. Send levels for delay scale with feedback so
// no-delay songs are cleanly silent, not just decayed-out.
function _applyMusicEffectsFromSong() {
  if (!_musicReverbSend || !_musicDelaySend) return;
  const reverb = Math.max(0, Math.min(1, _activeSong.reverbWetness ?? 0));
  const delayFb = Math.max(0, Math.min(0.85, _activeSong.delayFeedback ?? 0));
  _musicReverbSend.gain.value = reverb * 0.6;  // 0.6 cap so even 1.0 stays musical, not ambient soup
  _musicDelaySend.gain.value = delayFb > 0 ? 0.35 : 0;
  _musicDelayFeedback.gain.value = delayFb;
  _musicDelay.delayTime.value = MUSIC_BEAT * 0.75;
}

// Debug — fast-forward to the start of the next section. Disconnects the live music gain so any
// pending oscillators go to a dead node, then schedules the target section from "now."
function skipToNextSection() {
  if (!_musicStarted || !audioCtx) return;
  // Cancel the pending re-schedule so we don't end up with two scheduler chains running.
  if (_musicTimeoutId !== null) {
    clearTimeout(_musicTimeoutId);
    _musicTimeoutId = null;
  }
  if (_musicGain) _musicGain.disconnect();
  _musicGain = audioCtx.createGain();
  _musicGain.gain.value = _MUSIC_VOLUME_TARGET;
  _musicGain.connect(_masterBus);
  _buildMusicEffects();
  _applyMusicEffectsFromSong();

  const elapsed = audioCtx.currentTime - _musicLoopStartTime;
  const currentBar = Math.floor(elapsed / MUSIC_BAR);
  const currentSectionIdx = Math.floor(currentBar / MUSIC_LOOP_BARS) % _musicSections.length;
  const targetSectionIdx = (currentSectionIdx + 1) % _musicSections.length;

  // Anchor HUD time so currentTime maps to bar 0 of the target section.
  _musicLoopStartTime = audioCtx.currentTime - targetSectionIdx * MUSIC_LOOP_DURATION;
  _musicNextStart = audioCtx.currentTime + 0.05;
  _musicLoopIndex = targetSectionIdx;
  _easyOnsetTimes.length = 0;  // timeline jumped -> drop onsets queued against the old section
  _scheduleNextLoop();
}

// HUD label for current music section + bar — null if music hasn't started.
function getMusicLabel() {
  if (!_musicStarted || !audioCtx) return null;
  const elapsed = audioCtx.currentTime - _musicLoopStartTime;
  if (elapsed < 0) return null;
  const totalBars = Math.floor(elapsed / MUSIC_BAR);
  const sectionIdx = Math.floor(totalBars / MUSIC_LOOP_BARS) % _musicSections.length;
  const barInSection = (totalBars % MUSIC_LOOP_BARS) + 1;
  const names = ['tonal', 'var', 'poly', 'build', 'tonal+', 'var+', 'poly+', 'build+'];
  return `${names[sectionIdx]} ${barInSection}/${MUSIC_LOOP_BARS}`;
}

const debris = [];

// Rubble geometry — shape per material/kind so each reads distinctly:
//   currency → coin disc; crate → wood splinter; metal → flat plate; brick (default) → irregular chunks.
function makeCoinGeo(size) {
  const r = size * 0.6;
  const h = size * 0.18;
  const segs = 24;
  const positions = [];
  const indices = [];

  function addRing(radius, y) {
    const start = positions.length / 3;
    for (let s = 0; s < segs; s++) {
      const a = (s / segs) * Math.PI * 2;
      positions.push(Math.cos(a) * radius, y, Math.sin(a) * radius);
    }
    return start;
  }

  const top = [
    addRing(0, h * 0.50),
    addRing(r * 0.52, h * 0.50),
    addRing(r * 0.62, h * 0.43),
    addRing(r * 0.72, h * 0.51),
    addRing(r, h * 0.55),
  ];
  const bottom = [
    addRing(0, -h * 0.50),
    addRing(r * 0.52, -h * 0.50),
    addRing(r * 0.62, -h * 0.43),
    addRing(r * 0.72, -h * 0.51),
    addRing(r, -h * 0.55),
  ];

  function stitch(ringA, ringB, flip = false) {
    for (let s = 0; s < segs; s++) {
      const a = ringA + s;
      const b = ringA + ((s + 1) % segs);
      const c = ringB + s;
      const d = ringB + ((s + 1) % segs);
      if (flip) indices.push(a, b, c, b, d, c);
      else indices.push(a, c, b, b, c, d);
    }
  }

  for (let i = 0; i < top.length - 1; i++) stitch(top[i], top[i + 1], true);
  for (let i = 0; i < bottom.length - 1; i++) stitch(bottom[i], bottom[i + 1], false);
  stitch(top[top.length - 1], bottom[bottom.length - 1], true);

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  // Planar UVs projected from the face plane (XZ) so the stamped note/rim bump texture lands on both faces.
  // Without UVs the bumpMap can't sample (it was previously a no-op). Center → (0.5,0.5), rim → unit circle.
  const uv = [];
  for (let i = 0; i < positions.length; i += 3) {
    let u = (positions[i] / r) * 0.5 + 0.5;
    const v = (positions[i + 2] / r) * 0.5 + 0.5;
    if (positions[i + 1] > 0) u = 1 - u;   // flip U on the +y face so the note reads forward on both sides
    uv.push(u, v);
  }
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}

function makeRubbleGeo(size, isCurrency, material) {
  if (isCurrency) {
    return makeCoinGeo(size);
  }
  if (material === 'crate') {
    // Stretched octahedron — pointed at both ends, sharp angular splinter feel.
    const geo = new THREE.OctahedronGeometry(size * 0.5, 0);
    geo.scale(1.2, 0.22, 0.22);
    return geo;
  }
  if (material === 'metal') {
    return new THREE.BoxGeometry(size, size * 0.15, size * 0.6);
  }
  // Brick (and unknown) — irregular icosahedron polyhedra only. The previous angular-box
  // variant was removed because at distance the cuboid chunks could be mistaken for tiny
  // unbroken smashable blocks.
  const geo = new THREE.IcosahedronGeometry(size * 0.55, 0);
  const pos = geo.attributes.position;
  const groups = new Map();
  for (let i = 0; i < pos.count; i++) {
    const key = pos.getX(i).toFixed(4) + ',' + pos.getY(i).toFixed(4) + ',' + pos.getZ(i).toFixed(4);
    let g = groups.get(key);
    if (!g) groups.set(key, g = []);
    g.push(i);
  }
  for (const indices of groups.values()) {
    const factor = 0.78 + Math.random() * 0.42;
    const i0 = indices[0];
    const x = pos.getX(i0), y = pos.getY(i0), z = pos.getZ(i0);
    for (const i of indices) {
      pos.setX(i, x * factor);
      pos.setY(i, y * factor);
      pos.setZ(i, z * factor);
    }
  }
  pos.needsUpdate = true;
  geo.computeBoundingSphere();
  return geo;
}

// Two debris kinds. Aesthetic = material-textured chunks for visual flavour, fade out and don't suck.
// Currency = gold loot, persists, gets sucked into fist orbits and launched. Separate caps so a big
// smash of bricks doesn't crowd out collected gold.
const MAX_AESTHETIC = 400;
const MAX_CURRENCY = 200;
// Aesthetic chunks fade fast: visual juice of a break is in the first 2-3s, anything past 5s
// is just physics overhead. Tuned 2026-05-02 alongside chunk freeze-after-settle (below) to
// keep L10 frame budget healthy.
const AESTHETIC_LIFETIME = 5;      // seconds before aesthetic chips despawn
const AESTHETIC_FADE = 1;          // seconds at end of life to fade opacity 1→0
const _GOLD_COLOR = 0xffd700;

// Coin color-tween palette — pulses between gold and a glowing warm orange-red while orbiting.
// Softer than pure red so it reads as "energy gathering" rather than alarm. Returns to gold
// when the coin lands. Reused refs so we don't allocate per-frame.
const _COIN_BASE_COLOR = new THREE.Color(_GOLD_COLOR);
const _COIN_PULSE_COLOR = new THREE.Color(0xff6e3a);
let _coinBumpTexture = null;
function getCoinBumpTexture() {
  if (_coinBumpTexture) return _coinBumpTexture;
  const S = 256;
  const canvas = document.createElement('canvas');
  canvas.width = S;
  canvas.height = S;
  const ctx = canvas.getContext('2d');
  const cx = S / 2, cy = S / 2;
  // Grayscale luminance map used as BOTH the colour map (× the gold material colour) and the bump map.
  // Brighter = brighter gold + raised; darker = darker gold + recessed. Bright field + bright rim with a
  // dark separating groove, and a DARK recessed eighth-note in the middle (the SOUNDS SMASHING mark). The
  // note reads via colour contrast (a darker-gold engraving), not just emboss, so it shows on the metal.
  ctx.fillStyle = 'rgb(236,236,236)';            // bright gold field
  ctx.fillRect(0, 0, S, S);
  ctx.beginPath();                                // dark groove separating field from rim
  ctx.strokeStyle = 'rgb(120,120,120)';
  ctx.lineWidth = S * 0.03;
  ctx.arc(cx, cy, S * 0.40, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();                                // bright raised rim ring
  ctx.strokeStyle = 'rgb(255,255,255)';
  ctx.lineWidth = S * 0.055;
  ctx.arc(cx, cy, S * 0.45, 0, Math.PI * 2);
  ctx.stroke();
  // Dark engraved eighth-note, drawn as a PIXEL bitmap (blocky 8-bit form to match the kit's pixel art).
  const NOTE = [
    '................',
    '................',
    '.........XX.....',
    '.........XX.....',
    '.........XX.....',
    '.........XX.....',
    '.........XX.....',
    '.........XX.....',
    '.........XX.....',
    '.........XX.....',
    '......XXXXX.....',
    '....XXXXXXX.....',
    '...XXXXXXXX.....',
    '...XXXXXXX......',
    '....XXXXX.......',
    '................',
  ];
  ctx.fillStyle = 'rgb(122,122,122)';
  const boxW = S * 0.58;
  const cell = boxW / 16;
  // Center on the note's INK bounding box (the glyph isn't symmetric, so centering the raw 16×16 grid
  // looks off). Find the min/max occupied cells and offset so the ink's centre lands on the coin centre.
  let minC = 16, maxC = -1, minR = 16, maxR = -1;
  for (let row = 0; row < 16; row++) {
    for (let col = 0; col < 16; col++) {
      if (NOTE[row][col] === 'X') {
        if (col < minC) minC = col; if (col > maxC) maxC = col;
        if (row < minR) minR = row; if (row > maxR) maxR = row;
      }
    }
  }
  const x0 = cx - ((minC + maxC + 1) / 2) * cell;
  const y0 = cy - ((minR + maxR + 1) / 2) * cell;
  for (let row = 0; row < 16; row++) {
    for (let col = 0; col < 16; col++) {
      if (NOTE[row][col] === 'X') {
        ctx.fillRect(x0 + col * cell, y0 + row * cell, Math.ceil(cell) + 1, Math.ceil(cell) + 1);
      }
    }
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.magFilter = THREE.NearestFilter;   // keep the note's pixels crisp on close/large coins
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  _coinBumpTexture = tex;
  return tex;
}

// Trail-dot setup for perfect-release chips. Shared geometry; per-dot material is cloned so we
// can fade alpha individually. Dots auto-despawn after a short lifetime.
const _trailDotGeo = new THREE.SphereGeometry(0.10, 6, 5);
const _trailDots = []; // active fading dots: [{mesh, mat, expiresAt}]
const _TRAIL_DOT_LIFE_MS = 280;
function _spawnTrailDot(worldPos) {
  const mat = new THREE.MeshBasicMaterial({
    color: _GOLD_COLOR,
    transparent: true,
    opacity: 0.85,
  });
  const m = new THREE.Mesh(_trailDotGeo, mat);
  m.position.copy(worldPos);
  scene.add(m);
  _trailDots.push({ mesh: m, mat, expiresAt: performance.now() + _TRAIL_DOT_LIFE_MS, bornAt: performance.now() });
}
function _updateTrailDots() {
  const now = performance.now();
  for (let i = _trailDots.length - 1; i >= 0; i--) {
    const d = _trailDots[i];
    if (now >= d.expiresAt) {
      scene.remove(d.mesh);
      d.mat.dispose();
      _trailDots.splice(i, 1);
      continue;
    }
    const t = (now - d.bornAt) / _TRAIL_DOT_LIFE_MS;
    d.mat.opacity = 0.85 * (1 - t);
    d.mesh.scale.setScalar(1 - t * 0.5);  // shrinks slightly as it fades
  }
}

// Cached CanvasTexture per material — chips share the same texture object so spawning is cheap.
const _materialTextures = {};
function getMaterialTexture(material) {
  if (_materialTextures[material]) return _materialTextures[material];
  const tex = new THREE.CanvasTexture(getBaseTextureCanvas(material));
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  // RepeatWrapping so rubble UVs scaled past 1.0 (pieces larger than 1m, e.g. big-chunk
  // sizes for tall towers) tile cleanly instead of clamping to the texture edge.
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  _materialTextures[material] = tex;
  return tex;
}

// Hammered-dent texture for metal rubble — visually distinct from the rivet-and-hatch block surface.
function paintMetalRubbleTexture(ctx, w, h) {
  ctx.fillStyle = '#888a92';
  ctx.fillRect(0, 0, w, h);
  // 5 circular dents — radial gradient sells the concave look without normal mapping.
  for (let i = 0; i < 5; i++) {
    const cx = 8 + Math.random() * (w - 16);
    const cy = 8 + Math.random() * (h - 16);
    const r = 4 + Math.random() * 4;
    const grad = ctx.createRadialGradient(cx + 1, cy + 1, 0, cx, cy, r);
    grad.addColorStop(0, '#5a5d65');
    grad.addColorStop(0.6, '#7a7d85');
    grad.addColorStop(1, '#a8aab2');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
  }
  // Scuff lines for grit.
  ctx.strokeStyle = 'rgba(50, 55, 65, 0.5)';
  ctx.lineWidth = 1;
  for (let i = 0; i < 4; i++) {
    const x1 = Math.random() * w, y1 = Math.random() * h;
    const len = 6 + Math.random() * 14;
    const angle = Math.random() * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x1 + Math.cos(angle) * len, y1 + Math.sin(angle) * len);
    ctx.stroke();
  }
}

// Rubble can have a different texture than the block (e.g., metal chunks look hammered/dented).
const _rubbleTextures = {};
function getRubbleTexture(material) {
  if (_rubbleTextures[material]) return _rubbleTextures[material];
  if (material === 'metal') {
    const canvas = document.createElement('canvas');
    canvas.width = TEXTURE_SIZE;
    canvas.height = TEXTURE_SIZE;
    paintMetalRubbleTexture(canvas.getContext('2d'), TEXTURE_SIZE, TEXTURE_SIZE);
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.magFilter = THREE.NearestFilter;
    tex.minFilter = THREE.NearestFilter;
    // RepeatWrapping so rubble UV scale past 1.0 tiles cleanly (matches block textures).
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    _rubbleTextures[material] = tex;
    return tex;
  }
  return getMaterialTexture(material);
}

function _removeChip(chip) {
  scene.remove(chip);
  chip.geometry.dispose();
  chip.material.dispose();
  if (chip.userData.body) _chipByBodyHandle.delete(chip.userData.body.handle);
  physicsWorld.removeRigidBody(chip.userData.body);
}

// Per-frame chip-cleanup budget — disposing 50+ rigid bodies + meshes in a single frame stalls
// the browser. Excess chips stay flagged and get processed on subsequent frames.
const _COLLECT_BUDGET = 8;
let _collectsThisFrame = 0;

function _pruneOldestOfKind(isCurrency, max) {
  let count = 0;
  for (const chip of debris) if (chip.userData.isCurrency === isCurrency) count++;
  while (count >= max) {
    // Prefer to prune an inactive chip first. Only fall back to armed projectiles if no
    // inactive chips exist — keeps in-flight projectiles alive when possible so they get
    // a chance to land on their target. Currency chips that get culled are auto-banked
    // (same path as natural expiry) so no earned score is silently lost.
    const nowSec = performance.now() / 1000;
    let target = -1;
    let armedFallback = -1;
    for (let i = 0; i < debris.length; i++) {
      const chip = debris[i];
      if (chip.userData.isCurrency !== isCurrency) continue;
      const armed = chip.userData.projectileUntil && nowSec < chip.userData.projectileUntil;
      if (armed) {
        if (armedFallback === -1) armedFallback = i;
        continue;
      }
      target = i;
      break;
    }
    if (target === -1) target = armedFallback;
    if (target === -1) break;  // shouldn't happen given count >= max, but guards against an infinite loop
    const chip = debris[target];
    if (isCurrency && !chip.userData.orbiting && !chip.userData.pendingCollect) {
      const value = chip.userData.multiplier ?? 1;
      const pos = chip.position.clone();
      const note = chip.userData.coinNote;
      _pendingCoinAwards += 1;
      _pendingCoinValue += value;  // pre-add value (alreadyCounted call below won't book it)
      _removeChip(chip);
      debris.splice(target, 1);
      _awardDirectCoins(1, value, pos, true, note);
    } else {
      _removeChip(chip);
      debris.splice(target, 1);
    }
    count--;
  }
}

function _spawnDebrisBatch(position, forward, count, sizeRange, speedRange, isCurrency, material, timingMult = 1) {
  _pruneOldestOfKind(isCurrency, isCurrency ? MAX_CURRENCY : MAX_AESTHETIC);
  const dir = forward.clone().normalize();
  const nowSec = performance.now() / 1000;
  for (let i = 0; i < count; i++) {
    const size = sizeRange[0] + Math.random() * (sizeRange[1] - sizeRange[0]);

    const geo = makeRubbleGeo(size, isCurrency, material);
    // REPLACE UVs with a planar projection from vertex coordinates so rubble shows the
    // same per-meter texture density as blocks. Box rubble (metal) had default 0-1 UVs
    // per face that made small pieces show the full texture compressed onto their face.
    // Sphere-like rubble (octahedron crate, icosahedron brick) had sphere-mapped UVs that
    // made each face show a tiny sliver of the texture stretched huge. Both look wrong
    // next to blocks. Projecting (vertex.x, vertex.y) into UV at TEXTURE_TILE_PER_METER
    // scale gives consistent per-meter density on every geometry shape; some faces show
    // texture stretched at angles, but the average density matches blocks. Skipped for
    // currency (no texture, just gold).
    if (!isCurrency && geo.attributes.position) {
      const pos = geo.attributes.position;
      const newUV = new Float32Array(pos.count * 2);
      // Bumped 3× past the strict per-meter density. Per-meter-accurate looked correct
      // mathematically (same brick count per metre as the source wall) but read as "huge
      // bricks on small fragments" because the rubble surface is irregular and a single
      // brick sprawls across multiple tilted faces. The 3× factor gives finer pattern that
      // sits visually flush with the source block's brickwork.
      const t = TEXTURE_TILE_PER_METER * 3;
      for (let v = 0; v < pos.count; v++) {
        newUV[v * 2]     = pos.getX(v) * t;
        newUV[v * 2 + 1] = pos.getY(v) * t;
      }
      geo.setAttribute('uv', new THREE.BufferAttribute(newUV, 2));
    }
    let mat;
    if (isCurrency) {
      mat = new THREE.MeshStandardMaterial({
        color: 0xffe44a,
        map: getCoinBumpTexture(),       // grayscale × gold → bright gold field + darker-gold engraved note
        emissive: _GOLD_COLOR,
        emissiveIntensity: 0.03,
        roughness: 0.28,                 // eased off pure-mirror so the painted note actually reads
        metalness: 0.72,
        bumpMap: getCoinBumpTexture(),   // same texture also embosses the note + rim for a little depth
        bumpScale: 0.02,
        envMap: springMetalEnvMap,
        envMapIntensity: 1.05,
      });
    } else {
      // Aesthetic: tinted material with a rubble-specific texture (metal gets dents; others reuse block tex).
      // Tint slightly darker than the block surface — reads as "interior" rather than just a chip.
      const m = MATERIALS[material] || MATERIALS.brick;
      mat = new THREE.MeshStandardMaterial({
        color: 0xa8a8a8,
        map: getRubbleTexture(material),
        roughness: m.roughness,
        metalness: m.metalness,
        flatShading: true,
        transparent: true,
        opacity: 1,
      });
    }
    const chip = new THREE.Mesh(geo, mat);
    chip.castShadow = true;

    const spawn = position.clone().add(new THREE.Vector3(
      (Math.random() - 0.5) * 0.4,
      (Math.random() - 0.5) * 0.3,
      (Math.random() - 0.5) * 0.4,
    ));

    const speed = speedRange[0] + Math.random() * (speedRange[1] - speedRange[0]);
    const spread = new THREE.Vector3(
      (Math.random() - 0.5) * 1.4,
      Math.random() * 0.9 + 0.4,
      (Math.random() - 0.5) * 1.4,
    ).normalize();
    const vel = dir.clone().multiplyScalar(speed * 0.6).add(spread.multiplyScalar(speed * 0.7));
    // Currency pops upward harder so it visually separates from the lateral debris fan.
    if (isCurrency) vel.y += 4 + Math.random() * 3;

    const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2,
      Math.random() * Math.PI * 2,
    ));

    const bodyDesc = RAPIER.RigidBodyDesc.dynamic()
      .setTranslation(spawn.x, spawn.y, spawn.z)
      .setRotation({ x: q.x, y: q.y, z: q.z, w: q.w })
      .setLinvel(vel.x, vel.y, vel.z)
      .setAngvel({
        x: (Math.random() - 0.5) * 14,
        y: (Math.random() - 0.5) * 14,
        z: (Math.random() - 0.5) * 14,
      })
      .setAngularDamping(2.0)
      .setLinearDamping(0.3);
    const body = physicsWorld.createRigidBody(bodyDesc);

    // Collider shape per kind/material — matches the geometry so chips tumble and rest naturally.
    // Currency spawns as a cylinder so coins lie flat on surfaces; while in orbit it'll be swapped
    // for a sphere (cheap chip-chip contact under suction/launch chaos), then swapped back on settle.
    let colliderDesc;
    if (isCurrency) {
      colliderDesc = RAPIER.ColliderDesc.cylinder(size * 0.09, size * 0.6);
    } else if (material === 'crate') {
      colliderDesc = RAPIER.ColliderDesc.cuboid(size * 0.6, size * 0.11, size * 0.11);
    } else if (material === 'metal') {
      colliderDesc = RAPIER.ColliderDesc.cuboid(size * 0.5, size * 0.075, size * 0.3);
    } else {
      colliderDesc = RAPIER.ColliderDesc.cuboid(size * 0.42, size * 0.42, size * 0.42);
    }
    colliderDesc
      .setRestitution(0.15)
      .setFriction(0.7)
      .setDensity(1.5)
      .setActiveEvents(RAPIER.ActiveEvents.COLLISION_EVENTS);
    const collider = physicsWorld.createCollider(colliderDesc, body);
    // Currency chips → DEBRIS_GROUPS (collide with floor/walls/blocks; pass through other chips
    // and armed projectiles). Aesthetic rubble → AESTHETIC_CHUNK_GROUPS (also pass through other
    // chunks/chips/armed; blocks filter them out too — chunks only collide with floor + walls).
    collider.setCollisionGroups(isCurrency ? DEBRIS_GROUPS : AESTHETIC_CHUNK_GROUPS);

    chip.userData.body = body;
    chip.userData.collider = collider;
    chip.userData.isCurrency = isCurrency;
    chip.userData.material = material;  // for material-aware landing-click sounds
    chip.userData.spawnedAt = nowSec;
    if (isCurrency) {
      chip.userData.coinSize = size;
      chip.userData.usingSphere = false;  // starts as cylinder; swap on orbit-enter
      chip.userData.coinNote = _pickScaleNote(750, 1100);  // fixed note per coin — same coin plays same pitch each bounce
      chip.userData.settledAt = nowSec;   // collision is frozen 2s after this
      chip.userData.multiplier = timingMult;  // rhythm-timing multiplier — applied at HUD-collection
    }
    _chipByBodyHandle.set(body.handle, chip);

    scene.add(chip);
    debris.push(chip);
  }
}

// Crack effect — a few aesthetic chips, no currency (block didn't break).
function spawnChips(position, forward, material) {
  _spawnDebrisBatch(position, forward, 5, [0.08, 0.16], [3, 6], false, material);
  _spawnImpactFX(position, material, 1, false, forward);  // small dust/spark puff on a non-breaking crack
}

// Break effect — bigger aesthetic chunks plus a currency drop. Loot scales with material tier.
// `perCoinScore` is per-coin payout (defaults 1). `coinCountMult` scales the number of coins
// dropped (defaults 1 — preserve material default). For chip-storm break loot, callers pass
// a tier-based count mult (0/1/2 for miss/good/perfect) and the streak bonus as per-coin score.
function spawnChunks(position, forward, material, perCoinScore = 1, directAward = false, coinCountMult = 1, speedMult = 1, sourceBlock = null, tier = 'good') {
  _lastBreakCoins = 0;   // race: reset up front so a zero-coin break (miss / pop) can't leak a stale value to the death claim
  const aestheticCount = { crate: 4, brick: 11, metal: 11 };
  const aestheticSize  = { crate: [0.3, 0.62], brick: [0.18, 0.42], metal: [0.18, 0.42] };
  const m = material || 'brick';
  // Scale chunk size with the source block's smallest dimension so big blocks produce
  // proportionally bigger debris (readable from a distance). Capped at 4× so monumental
  // 60m towers don't spew absurdly large chunks. Small cubes (≤1.5m min dim) unchanged.
  let chunkScale = 1;
  let fxScale = 1;
  if (sourceBlock && sourceBlock.geometry && sourceBlock.geometry.parameters) {
    const p = sourceBlock.geometry.parameters;
    const minDim = Math.min(p.width, p.height, p.depth);
    chunkScale = Math.max(1, Math.min(4, minDim / 1.5));
    // FX scale is NOT floored at 1 (unlike chunkScale, which the debris sizing needs), so small blocks
    // get proportionally SMALLER sparks/dust/flash instead of sitting at baseline.
    fxScale = Math.min(2.5, minDim / 1.5);
  }
  const baseSize = aestheticSize[m] || [0.18, 0.42];
  const sizeRange = chunkScale === 1 ? baseSize : [baseSize[0] * chunkScale, baseSize[1] * chunkScale];
  const speedRange = [5 * speedMult, 11 * speedMult];
  _spawnDebrisBatch(position, forward, aestheticCount[m] || 11, sizeRange, speedRange, false, m);
  _spawnImpactFX(position, m, fxScale, true, forward);  // dust/spark burst on break, scaled by block size
  // Off-beat / mashed hits (miss tier) destroy the block visually but pay ZERO coins.
  // Rewritten 2026-05-13: previous soft penalty (0.5× score) let mashers clear levels with
  // some coins. New rule unifies mash + bad timing under a single "off-beat = nothing" gate,
  // matching the rhythm-game promise of the kit. Aesthetic chunks still fly so the smash
  // feels like progress; the absence of coins + the existing BAD label tells the player why.
  if (tier === 'miss') return;
  // BACK ON BEAT recovery hit: damage/break normally, but pay no coins once. Callers pass
  // zero here so the penalty lives on the player state rather than poisoning the block.
  if (perCoinScore <= 0 || coinCountMult <= 0) return;
  // Visible coin COUNT scales with the timing × streak multiplier so a perfect-on-streak smash
  // visibly drops more coins than a mash. Each chip is then worth a flat 1 score (was N×score
  // per chip) — total score per smash is unchanged (count × 1 == old count × perCoinScore),
  // but the player SEES the reward magnitude in the chip pile instead of having to read the
  // score counter. Visual signal: more gold dropping = better hit.
  const baseCount = Math.round((BLOCK_BASELINE_COINS[m] || 3) * Math.max(coinCountMult, 1));
  let coins = Math.max(1, Math.round(baseCount * perCoinScore));
  // Assist economy: flat +1 per block for the auto-punch stream (and its AOE) — keeps the baseline
  // simple and the score honest. The ONE exception is the coin charge: a charge-storm break sets
  // _easyChargeStormCoins, so its loot keeps the full material × streak payout. That makes the
  // streak-powered charge the lever that lifts a run toward the goals, while plain auto-play stays at
  // +1. (Full unification was tried and overshot — every run hit 3 stars off the streak multiplier.)
  if (_easyMode && !_easyChargeStormCoins) coins = 1;
  _lastBreakCoins = coins;   // race (coins metric): the score value this break paid, attached to the death claim
  if (directAward) {
    // Chip-storm break: spawn purely-visual flying coins (no physics) that arc to the counter.
    // Each chip = 1 score now (multiplier already baked into count).
    _awardDirectCoins(coins, 1, position);
  } else {
    _spawnDebrisBatch(position, forward, coins, [0.3, 0.5], [4, 9], true, m, 1);
  }
  playCoinTinkle(coins, position);
  // Per-hit pop: count matches chips exactly, color matches the timing tier (gold = perfect,
  // cyan = good). Player learns "yellow number = I nailed it."
  _popCoinAmountAtWorld(coins, position, _popColorForTier(tier));
}

// Direct coin award helper — used by chip-storm breaks. Spawns a burst of DOM coin dots at the
// break point, each flies to the HUD with a slight delay, scores on arrival. No physics, no
// world clutter, full slot-machine payout feel.
const _awardProjTmp = new THREE.Vector3();
// Pop "+N" gold label above the coin counter — fires for batch-award calls (count > 1) so
// the player sees the magnitude of break-loot. Skipped for single-chip collections (count = 1)
// to avoid flooding the screen with "+1" text on every chip impact.
// Tier → color map matches the PERFECT/GOOD/MISS label palette so the player learns
// "color = timing quality" once across both surfaces. Gold = perfect, cyan = good,
// pink = miss/mash.
const _POP_COLOR_PERFECT = '#FFE600';
const _POP_COLOR_GOOD    = '#00E5FF';
const _POP_COLOR_MISS    = '#FF3B6B';
function _popColorForTier(tier) {
  if (tier === 'perfect') return _POP_COLOR_PERFECT;
  if (tier === 'miss')    return _POP_COLOR_MISS;
  return _POP_COLOR_GOOD;
}
// "+N" billboard at the impact point in 3D space. Uses a CanvasTexture rendered to a
// THREE.Sprite (auto-faces camera). Floats up + scales in + fades over 700ms. Lives at
// the action instead of cluttering the lower-third (where the timing label already sits),
// and naturally scales with depth so far hits don't dominate the screen. Pixel-art
// preserved via NearestFilter on the texture + bold Press Start 2P-equivalent rendering.
function _renderPopCanvas(amount, color) {
  const canvas = document.createElement('canvas');
  canvas.width = 128; canvas.height = 40;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = false;
  const text = `+${amount}`;
  ctx.font = 'bold 22px "Press Start 2P", ui-monospace, monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const cx = canvas.width / 2, cy = canvas.height / 2;
  // Ink stroke for the chunky-pixel-with-outline look matching the rest of the kit.
  ctx.strokeStyle = '#0a0820';
  ctx.lineWidth = 6;
  ctx.lineJoin = 'round';
  ctx.strokeText(text, cx, cy);
  ctx.fillStyle = color;
  ctx.fillText(text, cx, cy);
  return canvas;
}
function _popCoinAmountAtWorld(amount, worldPos, color = _POP_COLOR_PERFECT) {
  if (amount <= 0 || !worldPos) return;
  const canvas = _renderPopCanvas(amount, color);
  const tex = new THREE.CanvasTexture(canvas);
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.generateMipmaps = false;
  // Canvas paints in sRGB (the color space the monitor expects), but three.js defaults
  // CanvasTexture to Linear. Without this hint the renderer applies a second sRGB encoding
  // on output, brightening/shifting the result so canvas-painted #FFE600 doesn't match
  // the DOM timing label's #FFE600. Tagging as sRGB makes the rendered colors match exactly.
  tex.colorSpace = THREE.SRGBColorSpace;
  const mat = new THREE.SpriteMaterial({
    map: tex,
    transparent: true,
    depthTest: false,   // always visible above blocks — feedback shouldn't be hidden
    depthWrite: false,
  });
  const sprite = new THREE.Sprite(mat);
  sprite.position.copy(worldPos);
  sprite.position.y += 0.3;  // lift slightly above impact so it doesn't overlap chunks
  // World-space size with strong distance-aware dampening. Two adjustments compounded:
  //   • Close-range floor cut to 0.35× (was 0.5×) → close-up melee taps shrink another
  //     30% so the pop doesn't fight for screen space when you're an arm's length away.
  //   • Far-range ceiling raised to 2.0× (was 1.0×) → distant chip-storm impacts grow
  //     large enough in world space that perspective doesn't shrink them to illegible
  //     specks. Net visual size at far ≈ double the previous tuning.
  const baseScale = 0.7;
  const dist = worldPos.distanceTo(camera.position);
  // Plateau in the close-range (d ≤ 1m, including the typical 0.6–1.4m post-lunge stand-off)
  // at the 0.08 floor — punch impacts at melee distance always read small. Past 1m the
  // curve ramps linearly to the 2.0× cap by 3m. Compensates for perspective making
  // close sprites huge AND respects that the lunge always leaves a stand-off gap, so
  // the entire "close hit" band needs the floor treatment, not just literal d=0.
  const closeFactor = 0.08 + 1.92 * Math.min(1, Math.max(0, (dist - 1.0) / 2.0));
  const effectiveScale = baseScale * closeFactor;
  sprite.scale.set(effectiveScale, effectiveScale * 0.31, 1);
  sprite.renderOrder = 1005;
  scene.add(sprite);
  const startTime = performance.now();
  const startY = sprite.position.y;
  const DURATION = 900;
  const RISE_DISTANCE = 0.5;  // half the previous 1.0m for a gentler drift
  const tick = () => {
    const t = (performance.now() - startTime) / DURATION;
    if (t >= 1) {
      scene.remove(sprite);
      mat.dispose();
      tex.dispose();
      return;
    }
    // Fade in 0-0.15, hold 0.15-0.7, fade out 0.7-1 (proportions kept similar to before).
    let opacity;
    if (t < 0.15) opacity = t / 0.15;
    else if (t < 0.7) opacity = 1;
    else opacity = 1 - (t - 0.7) / 0.3;
    mat.opacity = opacity;
    // Rise with cubic ease-out — fast initial pop upward, decelerates to a gentle hover
    // as it fades. Feels like a coin tossed into the air finding its peak.
    const easeOutCubic = 1 - Math.pow(1 - t, 3);
    sprite.position.y = startY + easeOutCubic * RISE_DISTANCE;
    // Pop-in scale during the fade-in portion. Uses effectiveScale (the distance-dampened
    // base) so the close-range halving applies through the whole animation, not just on spawn.
    const enterT = Math.min(1, t / 0.15);
    const s = 0.6 + 0.4 * enterT;
    sprite.scale.set(effectiveScale * s, effectiveScale * 0.31 * s, 1);
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

function _awardDirectCoins(count, multiplier, worldPos, alreadyCounted = false, note = null) {
  if (count <= 0 || multiplier <= 0) return;
  // Track in-flight coins so end-of-level can wait for them to land before showing the clear screen.
  // Callers that pre-increment (e.g. cash-out, which defers via setTimeout) pass alreadyCounted=true
  // so we don't double-count.
  // `note`, if provided, is the coin's fixed coinNote — used so the landing clink matches the
  // pitch the player has been hearing on the floor. Direct-award break-loot (no source chip) leaves
  // it null and gets a random scale note picked at landing time.
  // Track outstanding count + VALUE so the level-end fold-in is exact. Callers that pre-count
  // (cash-out / lifetime auto-bank / prune, alreadyCounted=true) also pre-add the value themselves,
  // so we only book it here for the direct (!alreadyCounted) callers. Snapshot the epoch so a level
  // end/restart can neutralize this flight.
  if (!alreadyCounted) {
    _pendingCoinAwards += count;
    _pendingCoinValue += count * multiplier;
  }
  const epoch = _coinAwardEpoch;
  // (Removed the duplicate "+N" pop near the counter — the floating "+N" at the smash point
  // (_popCoinAmountAtWorld) already reads the magnitude, and two pops double-signalled the
  // same award. The world pop also keeps the reward read where the player is looking.)
  // Project world position to screen so the flying coins start from the break point.
  _awardProjTmp.copy(worldPos).project(camera);
  const offscreen = _awardProjTmp.z > 1
    || Math.abs(_awardProjTmp.x) > 1.4 || Math.abs(_awardProjTmp.y) > 1.4;
  const startX = (_awardProjTmp.x * 0.5 + 0.5) * window.innerWidth;
  const startY = (-_awardProjTmp.y * 0.5 + 0.5) * window.innerHeight;
  const hudRect = _coinHud.getBoundingClientRect();
  const endX = hudRect.left + hudRect.width * 0.5;
  const endY = hudRect.top + hudRect.height * 0.5;

  const FLIGHT_MS = 420;

  for (let i = 0; i < count; i++) {
    setTimeout(() => {
      const award = () => {
        // Level already ended or a new run started: the value was folded into the score (or reset)
        // at the epoch bump, so adding it here would double-count or bleed into the next level.
        if (epoch !== _coinAwardEpoch) return;
        _score += multiplier;  // HUD text rolls up via _updateScoreRoll
        _bumpCoinHud();
        if (audioCtx) {
          const snapped = _snapToNextSixteenth(audioCtx.currentTime);
          const delay = Math.max(0, snapped - audioCtx.currentTime);
          const landingNote = note ?? _pickScaleNote(750, 1100);
          setTimeout(() => playCoinClink(0.8, null, landingNote), delay * 1000);
        }
        _pendingCoinAwards = Math.max(0, _pendingCoinAwards - 1);
        _pendingCoinValue = Math.max(0, _pendingCoinValue - multiplier);
      };
      if (offscreen) { award(); return; }

      // Slight per-coin jitter on the start position so the burst reads as a cluster.
      const jx = (Math.random() - 0.5) * 30;
      const jy = (Math.random() - 0.5) * 30;
      const dotSize = multiplier >= 2 ? 11 : 9;
      const grad = multiplier >= 2
        ? 'radial-gradient(circle at 30% 30%,#fffce0,#ffd66b 60%,#a06b00)'
        : 'radial-gradient(circle at 30% 30%,#fff5b8,#ffcd44 70%,#a06b00)';
      const shadow = multiplier >= 2 ? '0 0 12px rgba(255,214,107,0.95)' : '0 0 6px rgba(255,205,68,0.7)';
      const sx = startX + jx - dotSize / 2;
      const sy = startY + jy - dotSize / 2;
      const ex = endX - dotSize / 2;
      const ey = endY - dotSize / 2;
      const dot = document.createElement('div');
      dot.style.cssText = `position:fixed;width:${dotSize}px;height:${dotSize}px;border-radius:50%;`
        + `background:${grad};box-shadow:${shadow};pointer-events:none;z-index:997;`
        + `left:${sx}px;top:${sy}px;`;
      document.body.appendChild(dot);
      // Web Animations API — guarantees the from/to keyframes paint correctly (CSS transitions
      // can collapse to the final state if the browser hasn't painted the initial state yet).
      dot.animate(
        [
          { transform: 'translate(0, 0)',                  opacity: 1 },
          { transform: `translate(${ex - sx}px, ${ey - sy}px)`, opacity: 0.4 },
        ],
        { duration: FLIGHT_MS, easing: 'cubic-bezier(0.55,0.0,0.7,1.0)', fill: 'forwards' }
      );
      setTimeout(() => { dot.remove(); award(); }, FLIGHT_MS);
    }, i * 50);  // staggered burst — coins fly in sequence not all at once
  }
}

// Pull all debris toward the given fist's world position. Velocity is set directly each frame so
// the rubble visibly accumulates into a mound as charge builds.
const _camForward = new THREE.Vector3();
const _camRight = new THREE.Vector3();
const _worldUp = new THREE.Vector3(0, 1, 0);
const _fistPosL = new THREE.Vector3();
const _fistPosR = new THREE.Vector3();
const _suckDestinations = []; // reused per-frame; each entry: {fist,x,y,z,radius,charge}

// Single per-frame suction updater. Handles four cases:
//   - one fist charging        → orbit at that fist + camera-forward offset
//   - both fists charging      → single shared orbit at midpoint + forward offset
//   - one or both gathering    → per-fist destination collapsing to that fist for launch
//   - chips with multiple destinations active partition by nearest
// Co-op: while we hold a charge, tell the peer how many coins are orbiting each fist (+ charge) so they
// can draw the gathering ring. Throttled ~10Hz; sends one count:0 on a cancel (a launch clears via 'cs').
const _mpOrbLastSent = [0, 0];
const _mpOrbWasActive = [false, false];
function _mpSendOrbit(nowSec) {
  if (!_mp || !_mp.send) return;
  const nowMs = nowSec * 1000;
  for (let f = 0; f < 2; f++) {
    const active = charging[f] && chargeAmount[f] > SUCK_GRACE;
    if (active) {
      _mpOrbWasActive[f] = true;
      if (nowMs - _mpOrbLastSent[f] >= 100) {
        _mpOrbLastSent[f] = nowMs;
        let n = 0;
        for (const chip of debris) {
          const ud = chip.userData;
          if (ud.isCurrency && ud.assignedFist === f && ud.orbiting) n++;
        }
        _mp.send({ t: 'orb', h: f, n, c: chargeAmount[f] });
      }
    } else if (_mpOrbWasActive[f]) {
      _mpOrbWasActive[f] = false;
      if (pendingLaunchTime[f] <= 0) _mp.send({ t: 'orb', h: f, n: 0, c: 0 });  // cancelled (no launch)
    }
  }
}

function updateSuckSystem(nowSec) {
  if (_mp) _mpSendOrbit(nowSec);
  _suckDestinations.length = 0;

  const activeL = charging[0] && chargeAmount[0] > SUCK_GRACE;
  const activeR = charging[1] && chargeAmount[1] > SUCK_GRACE;
  const pendingL = pendingLaunchTime[0] > 0;
  const pendingR = pendingLaunchTime[1] > 0;

  if (activeL || activeR || pendingL || pendingR) {
    camera.getWorldDirection(_camForward);
    _camForward.normalize();
    _camRight.crossVectors(_camForward, _worldUp).normalize();  // camera-right for per-fist sideways spread
    leftFist.getWorldPosition(_fistPosL);
    rightFist.getWorldPosition(_fistPosR);

    if (activeL) {
      _suckDestinations.push({
        fist: 0,
        x: _fistPosL.x + _camForward.x * SUCK_ORBIT_FORWARD - _camRight.x * SUCK_ORBIT_SIDEWAYS,
        y: _fistPosL.y + _camForward.y * SUCK_ORBIT_FORWARD - _camRight.y * SUCK_ORBIT_SIDEWAYS,
        z: _fistPosL.z + _camForward.z * SUCK_ORBIT_FORWARD - _camRight.z * SUCK_ORBIT_SIDEWAYS,
        radius: SUCK_ORBIT_RADIUS,
        charge: displayedCharge[0],
      });
    }
    if (activeR) {
      _suckDestinations.push({
        fist: 1,
        x: _fistPosR.x + _camForward.x * SUCK_ORBIT_FORWARD + _camRight.x * SUCK_ORBIT_SIDEWAYS,
        y: _fistPosR.y + _camForward.y * SUCK_ORBIT_FORWARD + _camRight.y * SUCK_ORBIT_SIDEWAYS,
        z: _fistPosR.z + _camForward.z * SUCK_ORBIT_FORWARD + _camRight.z * SUCK_ORBIT_SIDEWAYS,
        radius: SUCK_ORBIT_RADIUS,
        charge: displayedCharge[1],
      });
    }

    for (let f = 0; f < 2; f++) {
      if (pendingLaunchTime[f] <= 0) continue;
      const elapsed = nowSec - pendingLaunchTime[f];
      if (elapsed >= SUCK_GATHER_DURATION) {
        launchSuckedDebris(f, pendingLaunchCharge[f], pendingLaunchTimingMult[f], pendingLaunchReleaseTier[f], pendingLaunchComboTier[f], pendingLaunchStreakBonus[f], pendingLaunchSuppressCoins[f]);
        pendingLaunchSuppressCoins[f] = false;
        pendingLaunchTime[f] = -Infinity;
        continue;
      }
      const tProgress = elapsed / SUCK_GATHER_DURATION;
      const fistP = (f === 0) ? _fistPosL : _fistPosR;
      const sideSign = (f === 0) ? -1 : 1;
      // Lerp the lateral offset from full orbit sideways → a small near-center value over the
      // gather. This pulls each fist's bunch toward the reticle midline before launch, so when
      // chips fire camera-forward they originate as a tight central cluster instead of two
      // wide rails 1m off-axis. Direction stays parallel-forward — no crossing paths.
      const GATHER_END_SIDEWAYS = 0.15;
      const gatherSideways = SUCK_ORBIT_SIDEWAYS * (1 - tProgress) + GATHER_END_SIDEWAYS * tProgress;
      _suckDestinations.push({
        fist: f,
        x: fistP.x + _camForward.x * SUCK_ORBIT_FORWARD + sideSign * _camRight.x * gatherSideways,
        y: fistP.y + _camForward.y * SUCK_ORBIT_FORWARD + sideSign * _camRight.y * gatherSideways,
        z: fistP.z + _camForward.z * SUCK_ORBIT_FORWARD + sideSign * _camRight.z * gatherSideways,
        radius: SUCK_ORBIT_RADIUS * (1 - tProgress),
        charge: pendingLaunchCharge[f],
      });
    }
  }

  const anyDest = _suckDestinations.length > 0;
  const SUCK_RATE_BASE = 18;
  const SUCK_RATE_CHARGE = 12;
  const MAX_SUCK_SPEED = 35;

  for (const chip of debris) {
    const ud = chip.userData;
    if (!ud.isCurrency) continue;  // aesthetic rubble doesn't suck — it stays where it falls
    const wasOrbiting = ud.orbiting === true;

    if (anyDest && !wasOrbiting) {
      // Thaw frozen coins so suction can move them again — restore dynamic body + recreate collider.
      if (ud.frozen) {
        ud.body.setBodyType(RAPIER.RigidBodyType.Dynamic, true);
        ud.frozen = false;
        // Collider was removed at freeze time; we need to recreate one. Sphere creation below will
        // handle this via the !usingSphere path since usingSphere stays false on a frozen+nulled collider.
        ud.usingSphere = false;
        ud.collider = null;
      }
      if (ud.collider === undefined || ud.collider === null) {
        if (ud.coinSize !== undefined) {
          // Frozen coin had its collider removed — make a fresh sphere directly.
          const sphereDesc = RAPIER.ColliderDesc.ball(ud.coinSize * 0.42)
            .setRestitution(0.15).setFriction(0.7).setDensity(1.5)
            .setActiveEvents(RAPIER.ActiveEvents.COLLISION_EVENTS);
          ud.collider = physicsWorld.createCollider(sphereDesc, ud.body);
          ud.usingSphere = true;
        } else {
          ud.collider = ud.body.collider(0);
        }
      }
      // Currency: swap cylinder → sphere for cheap chip-chip contact during orbit chaos.
      if (ud.coinSize !== undefined && !ud.usingSphere) {
        physicsWorld.removeCollider(ud.collider, false);
        const sphereDesc = RAPIER.ColliderDesc.ball(ud.coinSize * 0.42)
          .setRestitution(0.15).setFriction(0.7).setDensity(1.5)
          .setActiveEvents(RAPIER.ActiveEvents.COLLISION_EVENTS);
        ud.collider = physicsWorld.createCollider(sphereDesc, ud.body);
        ud.usingSphere = true;
      }
      ud.collider.setCollisionGroups(ORBIT_COLLISION_GROUPS);
      ud.orbiting = true;
      ud.settledAt = undefined;  // active again — collision-freeze timer doesn't run
      // Init once ever, reused on subsequent re-entries.
      if (ud.suckPhase === undefined) {
        ud.suckPhase = Math.random() * Math.PI * 2;
        ud.suckRadiusMult = 0.85 + Math.random() * 0.3;  // tighter band → chips hug the outer edge, ring reads as solid
        ud.suckYOffset = (Math.random() - 0.5) * 0.4;
        ud.orbitSpeedMult = 0.9 + Math.random() * 0.2;
        const ax = Math.random() - 0.5, ay = Math.random() - 0.5, az = Math.random() - 0.5;
        const alen = Math.hypot(ax, ay, az) || 1;
        const amag = 0.85 + Math.random() * 0.4;
        ud.orbitAngvel = { x: ax / alen * amag, y: ay / alen * amag, z: az / alen * amag };
      }
      // Snap angvel to the slow orbital value at entry — persists through orbit without per-frame writes.
      ud.body.setAngvel(ud.orbitAngvel, true);
      // Re-entering orbit cancels any pending dispersal from a previous launch.
      ud.dispersingUntil = undefined;
    } else if (!anyDest && wasOrbiting && ud.dispersingUntil === undefined) {
      // Just left orbit (typically post-launch). Hold orbit groups for a beat so chips spread out
      // before chip-chip collision re-enables — avoids the all-collide-in-one-frame solver spike.
      // Per-chip jitter spreads the collision-wave across ~200ms instead of one frame.
      ud.dispersingUntil = nowSec + SUCK_DISPERSE_DELAY + Math.random() * 0.2;
      // Storm-max coin rainbow: stamp a 1s "still glowing" window from the moment the chip
      // leaves the player's orbit. After that the chip settles back to plain gold (or to
      // whatever the projectile launch state froze it at, if it's mid-flight).
      ud.rainbowUntil = nowSec + 1.0;
    }
    // Dispersal complete → restore default collision groups. Currency keeps its sphere collider
    // indefinitely after launch (cylinder swap-back removed — sphere math is much cheaper, and
    // resting orientation isn't visually important enough to pay for it on big chip volleys).
    if (ud.dispersingUntil !== undefined && nowSec >= ud.dispersingUntil) {
      ud.collider.setCollisionGroups(DEBRIS_GROUPS);  // back to debris group: passes through other chips and armed projectiles, settles on floor
      ud.orbiting = false;
      ud.dispersingUntil = undefined;
      ud.settledAt = nowSec;  // start the 2s collision-freeze countdown
    }
    if (!anyDest) continue;

    const t = ud.body.translation();
    let dest = _suckDestinations[0];
    if (_suckDestinations.length > 1) {
      let minD2 = Infinity;
      for (const d of _suckDestinations) {
        const dd = (t.x - d.x) * (t.x - d.x) + (t.y - d.y) * (t.y - d.y) + (t.z - d.z) * (t.z - d.z);
        if (dd < minD2) { minD2 = dd; dest = d; }
      }
    }
    ud.assignedFist = dest.fist;
    // Click-tier orbit glow removed — under the release-only timing model, the click is just
    // pacing feedback, not a scoring event. Per-fist click tier no longer carries reward
    // semantics, so coloring chips by it would mislead the player about what determines
    // their coin yield (the release tier does).

    if (ud.suckPhase === undefined) {
      ud.suckPhase = Math.random() * Math.PI * 2;
      ud.suckRadiusMult = 0.85 + Math.random() * 0.3;  // tighter band → chips hug the outer edge, ring reads as solid
      ud.suckYOffset = (Math.random() - 0.5) * 0.4;
      // Slow per-chip orbit angvel — overrides the chaotic spin chips inherit from being smashed.
      // Random direction, magnitude ~1 rad/s with small variation.
      const ax = Math.random() - 0.5, ay = Math.random() - 0.5, az = Math.random() - 0.5;
      const alen = Math.hypot(ax, ay, az) || 1;
      const amag = 0.85 + Math.random() * 0.4;  // 0.85..1.25 rad/s
      ud.orbitAngvel = { x: ax / alen * amag, y: ay / alen * amag, z: az / alen * amag };
      ud.orbitSpeedMult = 0.9 + Math.random() * 0.2;  // small per-chip drift on orbital speed
    }
    ud.body.setAngvel(ud.orbitAngvel, true);

    const angle = ud.suckPhase + nowSec * SUCK_ORBIT_SPEED * ud.orbitSpeedMult;
    const radiusFraction = dest.radius / SUCK_ORBIT_RADIUS;
    const r = dest.radius * ud.suckRadiusMult;
    const targetX = dest.x + Math.cos(angle) * r;
    const targetY = dest.y + ud.suckYOffset * radiusFraction;
    const targetZ = dest.z + Math.sin(angle) * r;

    const dx = targetX - t.x;
    const dy = targetY - t.y;
    const dz = targetZ - t.z;
    const dist = Math.hypot(dx, dy, dz);
    if (dist < 1e-4) continue;
    const SUCK_RATE = SUCK_RATE_BASE + dest.charge * SUCK_RATE_CHARGE;
    const speed = Math.min(dist * SUCK_RATE, MAX_SUCK_SPEED);
    ud.body.setLinvel({
      x: (dx / dist) * speed,
      y: (dy / dist) * speed,
      z: (dz / dist) * speed,
    }, true);

    // Rhythmic orbit clink — coins don't collide while orbiting, so we drive their note here.
    // Each coin has a stable 16th-note slot. Fire density varies by music section so the orbit
    // pattern matches the song's character: sparse in tonal, doubling in var, beat-locked in poly,
    // escalating in build (with bar 16 silent for the release).
    if (ud.isCurrency && _musicStarted && audioCtx) {
      if (ud.coinBeatSlot === undefined) {
        ud.coinBeatSlot = Math.floor(Math.random() * 16);
        ud.lastClinkTotal16th = -1;
      }
      const elapsed = audioCtx.currentTime - _musicLoopStartTime;
      if (elapsed >= 0) {
        const sixteenthDur = MUSIC_BEAT / 4;
        const total16th = Math.floor(elapsed / sixteenthDur);
        const totalBars = Math.floor(total16th / 16);
        const sectionIdx = Math.floor(totalBars / MUSIC_LOOP_BARS) % _musicSections.length;
        const barInSection = totalBars % MUSIC_LOOP_BARS;
        const barSlot = total16th % 16;
        // Period: how often (in 16ths) each coin fires. Smaller period = denser rhythm.
        let period;
        if (sectionIdx === 0)      period = 16;  // tonal — once per bar
        else if (sectionIdx === 1) period = 8;   // var   — twice per bar
        else if (sectionIdx === 2) period = 4;   // poly  — every beat (4× per bar)
        else {
          // build — density escalates with bars 0-3/4-7/8-11/12-14, then silent on bar 15.
          if (barInSection >= 15)     period = 0;   // release (silent)
          else if (barInSection >= 12) period = 4;
          else if (barInSection >= 8)  period = 4;
          else if (barInSection >= 4)  period = 8;
          else                         period = 16;
        }
        if (period > 0) {
          const fireSlot = ud.coinBeatSlot % period;
          if (barSlot % period === fireSlot && total16th !== ud.lastClinkTotal16th) {
            playCoinClink(0.25, null, ud.coinNote);  // null position: skip panner+reverb tap (see drain comment at line ~525)
            ud.lastClinkTotal16th = total16th;
          }
        }
      }
    }
  }
}

// Shoot rubble that's accumulated near the fist forward in the camera's looking direction.
const _launchFistPos = new THREE.Vector3();
const _launchForward = new THREE.Vector3();
function launchSuckedDebris(fistIndex, charge, timingMult = 1, releaseTier = 'good', comboTier = 'good', streakBonus = 1, suppressBreakCoins = false) {
  const fist = fistIndex === 0 ? leftFist : rightFist;
  fist.getWorldPosition(_launchFistPos);
  camera.getWorldDirection(_launchForward);
  _launchForward.normalize();

  // If there's a locked target, compute the aim point on the block (same logic as firePunch).
  // Chips will then be directed individually toward that point — they converge on the beam target
  // instead of fanning out along the camera-forward direction.
  let aimPoint = null;
  if (lockedTarget) {
    aimPoint = rayHitBlock(lockedTarget, camera.position, _launchForward)
            || closestPointOnBlock(lockedTarget, camera.position);
  }

  // Quadratic ramp on the gauge — early-release storms feel like a flubbed throw, full-charge
  // storms feel like a proper haymaker. damage = round(speed/20) so the values map roughly to
  // 1 / 2 / 4 chip damage across the gauge. A 25% buff kicks in at max charge — the reward for
  // holding to the pulse-signaled peak.
  const remapped = Math.max(0, (charge - SUCK_GRACE) / (1 - SUCK_GRACE));
  let baseSpeed = 25 + 60 * remapped * remapped;  // 25 m/s (gauge empty) → 85 m/s (full)
  if (charge >= 0.99) baseSpeed *= 1.25;          // max-charge force buff
  const PROJECTILE_LIFETIME = 2.0;           // seconds a chip remains armed for damage after launch
  const armedUntil = (performance.now() / 1000) + PROJECTILE_LIFETIME;

  // Launch every chip currently orbiting this fist (no distance gate). All chips aim at the
  // same locked target with a small splash — when the front block breaks, chips behind it
  // arrive at empty space and naturally fly through to the next block in their path. The
  // armed-projectile collision group filters out aesthetic chunks + other projectiles so
  // chips don't bump into debris on the way through.
  let _csLaunched = 0;   // co-op: how many coins this storm actually fired (sent to the peer's view)
  for (const chip of debris) {
    if (!chip.userData.isCurrency) continue;  // aesthetic chips never get launched
    if (chip.userData.assignedFist !== fistIndex) continue;
    if (!chip.userData.orbiting) continue;    // only currently-orbiting chips fire
    _csLaunched++;
    const body = chip.userData.body;
    const t = body.translation();

    // Per-chip direction: aim at the target's aim point if locked, else camera-forward.
    // Per-chip splash: each chip aims at the primary point + a quadratic-falloff offset.
    // Most chips land near the primary (preserves the punch), some spill to neighbouring
    // blocks (storm reads as concentrated barrage with spread, not pinpoint or fan).
    let dirX = _launchForward.x, dirY = _launchForward.y, dirZ = _launchForward.z;
    if (aimPoint) {
      const SPLASH_RADIUS = 2.5;
      const splashDist = Math.pow(Math.random(), 2) * SPLASH_RADIUS;
      const splashAngle = Math.random() * Math.PI * 2;
      const ax = aimPoint.x + Math.cos(splashAngle) * splashDist - t.x;
      const ay = aimPoint.y + (Math.random() - 0.5) * SPLASH_RADIUS * 0.6 - t.y;
      const az = aimPoint.z + Math.sin(splashAngle) * splashDist - t.z;
      const len = Math.hypot(ax, ay, az);
      if (len > 0.001) { dirX = ax / len; dirY = ay / len; dirZ = az / len; }
    }
    // Tight velocity spread so the volley arrives near-synchronised even with splashed aim.
    const spread = aimPoint ? 3 : 5;
    body.setLinvel({
      x: dirX * baseSpeed + (Math.random() - 0.5) * spread,
      y: dirY * baseSpeed + (aimPoint ? (Math.random() - 0.5) * spread : Math.random() * 4),
      z: dirZ * baseSpeed + (Math.random() - 0.5) * spread,
    }, true);
    // Switch to the armed-projectile collision group — only collides with blocks/world, not
    // aesthetic chunks or other armed chips. So when chip 1 breaks the front block, chip 2
    // arrives at the empty-but-cluttered-with-debris space and just flies straight through.
    if (chip.userData.collider) chip.userData.collider.setCollisionGroups(ARMED_PROJECTILE_GROUPS);
    body.setAngvel({
      x: (Math.random() - 0.5) * 12,
      y: (Math.random() - 0.5) * 12,
      z: (Math.random() - 0.5) * 12,
    }, true);
    // Continuous Collision Detection — without this, chips at 80+ m/s tunnel straight through
    // blocks in a single 60Hz physics step (chip moves >1m per step, block is ~1m wide). CCD
    // sweeps the chip's path each step so impacts always register. Slight perf cost but the
    // alternative is "chip looks like it hit but didn't" which is what the user was seeing.
    body.enableCcd(true);
    // Arm a fresh shockwave for this storm — first chip-break consumes it.
    _pendingShockwave[fistIndex] = true;
    // Arm as projectile — first block contact deals damage and consumes the arming.
    chip.userData.projectileUntil = armedUntil;
    // Reset the chip's age clock at launch so it gets a fresh COIN_LIFETIME_SEC to actually
    // land after the projectileUntil window expires. Without this, an old floor coin fired
    // late in its life would auto-bank from mid-air the instant projectileUntil expired —
    // looked like the coin "disappeared into the UI before hitting anything."
    chip.userData.spawnedAt = performance.now() / 1000;
    chip.userData.projectileCharge = charge;
    // Damage uses the LAUNCH speed, not the chip's body velocity at impact-time. Rapier's
    // collision response zeros the chip's velocity before drainCollisionEvents fires, so
    // reading body.linvel() in applyProjectileHit always returns a near-zero number → every
    // chip would only ever deal 1 damage. Storing the intended speed here fixes that.
    chip.userData.projectileSpeed = baseSpeed;
    // Stamp the rhythm timing multiplier onto each chip — applied at HUD-collection time so
    // perfect-perfect storms award per-chip bonuses, off-beat releases score zero.
    chip.userData.multiplier = timingMult;
    chip.userData.canDamageBlocks = releaseTier !== 'miss' && comboTier !== 'miss';
    // Tier + streak bonus driving break-loot scaling at impact time. Chip-itself collection is
    // a flat +1 (the chip was earned by a prior punch); break-loot count varies with the
    // combined click+release tier, and per-coin score is the streak bonus from the launch moment.
    chip.userData.comboTier = comboTier;
    chip.userData.streakBonusAtLaunch = streakBonus;
    chip.userData.suppressBreakCoins = suppressBreakCoins;
    // Perfect release: chip flies with a trail. Frame loop spawns trail dots while this is set.
    chip.userData.hasTrail = (releaseTier === 'perfect');
    chip.userData.lastTrailMs = 0;
  }

  // Co-op: tell the peer to show this storm — a cosmetic coin volley from our fist. ALWAYS sent when coins
  // launched (locked or not). We carry the aim DIRECTION (camera-forward) so an unlocked storm still shows
  // a stream flying that way on the observer; if we were locked, we also carry the target block netId so
  // their coins converge on it. (The observer flies them straight and course-corrects onto whatever block
  // we actually break — see _peerVolleyRedirect.)
  if (_mp && _mp.send && _csLaunched > 0) {
    const _cs = { t: 'cs', h: fistIndex, n: _csLaunched, c: charge,
      dir: [Math.round(_launchForward.x * 100) / 100, Math.round(_launchForward.y * 100) / 100, Math.round(_launchForward.z * 100) / 100] };
    if (lockedTarget && lockedTarget.userData && lockedTarget.userData.netId !== undefined) {
      _cs.tid = lockedTarget.userData.netId;
    }
    _mp.send(_cs);
  }
}

function updateDebris(dt) {
  const alpha = Math.min(physicsAccum / PHYSICS_DT, 1);
  const nowSec = performance.now() / 1000;
  for (let i = debris.length - 1; i >= 0; i--) {
    const chip = debris[i];
    const ud = chip.userData;

    const t = ud.body.translation();
    const r = ud.body.rotation();
    if (ud.prevPos) {
      _interpVec.set(t.x, t.y, t.z);
      chip.position.lerpVectors(ud.prevPos, _interpVec, alpha);
      _interpQuat.set(r.x, r.y, r.z, r.w);
      chip.quaternion.copy(ud.prevQuat).slerp(_interpQuat, alpha);
    } else {
      chip.position.set(t.x, t.y, t.z);
      chip.quaternion.set(r.x, r.y, r.z, r.w);
    }

    // Perfect-release trail — spawn a fading gold dot at the chip's current position every
    // ~50ms while the projectile is still armed. Only fires for chips tagged with hasTrail.
    if (ud.hasTrail && ud.projectileUntil && nowSec < ud.projectileUntil) {
      const nowMs = performance.now();
      if (nowMs - ud.lastTrailMs >= 50) {
        _spawnTrailDot(chip.position);
        ud.lastTrailMs = nowMs;
      }
    }


    // Coin color: stays the original gold while orbiting and not maxed. Only when the held
    // charge hits max does it pulse between gold and a warm orange-red — clear signal that
    // releasing now will land the force buff. Once launched, color is frozen at launch state.
    // Storm-max rainbow: ONLY applied while a coin is actively in the player's orbit, or
    // within 1s of leaving it (rainbowUntil window). Floor coins from prior smashes stay
    // plain gold — the rainbow becomes "what you're working WITH" rather than ambient noise.
    if (ud.isCurrency && chip.material) {
      const isProjectile = ud.projectileUntil !== undefined && nowSec < ud.projectileUntil;
      if (!isProjectile) {
        // Base color: gold for floor coins. Orbiting coins progressively warm gold→orange-red
        // as the fist's charge climbs from SUCK_GRACE to max. Quadratic ramp keeps the lower
        // charge band subtle and concentrates visible color shift in the upper half — that's
        // where the "release window" lives. At max (≥0.99) a pulse layers on top as the
        // "release now" beacon.
        if (ud.orbiting) {
          const charge = chargeAmount[ud.assignedFist] ?? 0;
          const ramp = Math.max(0, (charge - SUCK_GRACE) / (1 - SUCK_GRACE));
          let t = ramp * ramp;
          if (charge >= 0.99) {
            const pulse = (Math.sin(performance.now() / 1000 * 8) + 1) * 0.5;
            t = Math.min(1, t + pulse * 0.3);
          }
          chip.material.color.lerpColors(_COIN_BASE_COLOR, _COIN_PULSE_COLOR, t);
        } else {
          chip.material.color.copy(_COIN_BASE_COLOR);
        }
        // Rainbow only on chips the player is actively engaged with: currently orbiting,
        // or within the 1s rainbowUntil window stamped when they leave orbit.
        const rainbowable = ud.orbiting || (ud.rainbowUntil !== undefined && nowSec < ud.rainbowUntil);
        if (_stormMaxIntensity > 0 && rainbowable) {
          const hueDeg = ((performance.now() / 1000 * 120 + chip.position.x * 40 + chip.position.z * 40) % 360 + 360) % 360;
          _stormMaxTmpColor.setHSL(hueDeg / 360, 1, 0.65);
          chip.material.color.lerp(_stormMaxTmpColor, _stormMaxIntensity);
        }
        if (chip.material.emissive) {
          chip.material.emissive.copy(chip.material.color);
          chip.material.emissiveIntensity = (_stormMaxIntensity > 0 && rainbowable) ? 0.16 : 0.10;
        }
      }
      // Projectile phase: color stays at whatever it was on launch (no per-frame mutation).
    }

    // Hit-collection: armed chip struck a block this frame. Animate it to the counter via the
    // same flying-coin helper used for break-loot — visually consistent so every coin that
    // counts toward the score reads as flying in. Budgeted per frame to avoid body-removal stalls.
    if (ud.pendingCollect) {
      if (_collectsThisFrame < _COLLECT_BUDGET) {
        _collectsThisFrame++;
        // Chip-itself collection: flat +1 for on-beat fired chips. Off-beat fired chips can
        // still visibly ricochet/return, but they score 0 and cannot damage blocks.
        _awardDirectCoins(1, ud.canDamageBlocks === false ? 0 : 1, chip.position, false, ud.coinNote);
        _removeChip(chip);
        debris.splice(i, 1);
      }
      // else: leave pendingCollect set; next frame's loop will pick it up.
      continue;
    }

    // Collision freeze: 2s after a coin settles AND it's moving slowly, remove its collider
    // and pin the body fixed. Mesh stays visible at rest; physics cost drops to zero.
    // The velocity check prevents freezing chips that are still mid-flight — long-distance
    // shots take >2s to land and would otherwise get pinned in midair.
    if (ud.isCurrency && ud.settledAt !== undefined && !ud.frozen
        && nowSec - ud.settledAt > 2.0) {
      const v = ud.body.linvel();
      const stillMoving = Math.hypot(v.x, v.y, v.z) > 1.0;
      // Floor proximity gate — only freeze when the chip is actually NEAR the floor.
      // Was previously freezing anything slow-moving, which caused two visible bugs:
      //   (a) chips at the apex of their arc (briefly stationary) froze in midair.
      //   (b) chips resting on top of another chip/chunk/block froze, then were stranded
      //       in space when the thing beneath them disappeared.
      // Coins eventually slide off obstacles to the floor or get auto-banked at 6s lifetime,
      // so the unfrozen population is bounded — perf cost is small. Threshold is generous
      // (1m) so coins sitting on the floor with small radii still pass.
      const py = ud.body.translation().y;
      const onFloor = py < 1.0;
      if (!stillMoving && onFloor) {
        if (ud.collider) {
          physicsWorld.removeCollider(ud.collider, false);
          ud.collider = null;
        }
        ud.body.setBodyType(RAPIER.RigidBodyType.Fixed, false);
        ud.frozen = true;
      }
      // Else: defer — re-checks each frame and freezes the moment the chip is at rest on the floor.
    }

    // Aesthetic chunk freeze: 1s post-spawn AND moving slowly, drop their physics cost.
    // Mirrors the currency freeze above. On L10, dense cascades spawn 200+ chunks that would
    // otherwise do collision math for their full lifetime — freezing them after they settle
    // is what lets the level breathe. Removing the collider also stops them firing rubble
    // clicks on every settle-bounce, which compounds the audio thinning we already shipped.
    if (!ud.isCurrency && !ud.frozen && nowSec - ud.spawnedAt > 1.0) {
      const v = ud.body.linvel();
      const stillMoving = Math.hypot(v.x, v.y, v.z) > 1.0;
      if (!stillMoving) {
        if (ud.collider) {
          physicsWorld.removeCollider(ud.collider, false);
          ud.collider = null;
        }
        ud.body.setBodyType(RAPIER.RigidBodyType.Fixed, false);
        ud.frozen = true;
      }
    }

    // Aesthetic chips fade out and despawn after their lifetime. Currency persists (cap-pruned only).
    if (!ud.isCurrency) {
      const age = nowSec - ud.spawnedAt;
      if (age >= AESTHETIC_LIFETIME) {
        _removeChip(chip);
        debris.splice(i, 1);
        continue;
      }
      const fadeStart = AESTHETIC_LIFETIME - AESTHETIC_FADE;
      if (age > fadeStart) {
        chip.material.opacity = (AESTHETIC_LIFETIME - age) / AESTHETIC_FADE;
      }
    }
  }
}

window.addEventListener('resize', () => {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, window.innerHeight);
  _DUST_CLOUD.points.material.uniforms.uScale.value = window.innerHeight;
  if (nearHighlight && nearHighlight.userData && nearHighlight.userData.lineMat) {
    nearHighlight.userData.lineMat.resolution.set(window.innerWidth, window.innerHeight);
  }
  beamL.mat.resolution.set(window.innerWidth, window.innerHeight);
  beamR.mat.resolution.set(window.innerWidth, window.innerHeight);
});

// Frame loop — runs at the display's native refresh rate (60/120/144 Hz).
// Game state uses dt for everything, so behavior is identical regardless of fps;
// physics is fixed-timestep internally. The label shows the actual rate.
let lastTickTimeMs = -1;
let fpsAccumMs = 0;
let fpsFrames = 0;
let fpsMaxFrameMs = 0;  // longest frame seen in the current window — surfaces spikes the smoothed avg hides

// Perf overlay (toggled from admin panel). Container holds a text label + a sparkline canvas.
// The frametime ring buffer always records (cheap), but the label/graph only redraws when on.
const _perfContainerEl = document.getElementById('fps');
const fpsLabelEl = document.createElement('div');
fpsLabelEl.style.cssText = 'white-space:pre;';
_perfContainerEl.innerHTML = '';
_perfContainerEl.appendChild(fpsLabelEl);
const _perfGraphCanvas = document.createElement('canvas');
_perfGraphCanvas.width = 180;
_perfGraphCanvas.height = 36;
_perfGraphCanvas.style.cssText = 'display:block;margin-top:4px;background:rgba(0,0,0,0.45);border:1px solid #2a2a32;';
_perfContainerEl.appendChild(_perfGraphCanvas);
const _perfGraphCtx = _perfGraphCanvas.getContext('2d');

// 30s @ 60fps ring buffer. Float32 keeps writes cheap; we only walk it when the graph redraws.
const _PERF_BUFFER_SIZE = 1800;
const _perfFrameMs = new Float32Array(_PERF_BUFFER_SIZE);
let _perfBufferIdx = 0;
let _perfBufferLen = 0;
let _perfOverlayOn = false;
let _perfWorstMs = 0;          // worst frame since overlay was last enabled (for the copy log)
let _perfWorstAtMs = 0;        // wall-clock time of that frame
let _perfStartMs = 0;          // when overlay was enabled — duration in the log starts here

// Audio diagnostic counters — total calls + dropped calls since overlay was last enabled. Used
// to confirm whether the audio thread is being asked to play more sounds than it can handle, or
// whether the AudioContext itself is entering a bad state (suspended/interrupted).
let _audioSmashCalls = 0, _audioSmashDropped = 0;
let _audioClinkCalls = 0, _audioClinkDropped = 0;
let _audioRubbleCalls = 0, _audioRubbleDropped = 0;
let _audioCtxStateLog = '';     // semicolon-list of state changes seen during the run
let _audioCtxLastState = '';    // last state seen, for change detection

// Walk the ring buffer in chronological order, oldest sample first.
function _readPerfBufferOrdered() {
  const out = new Float32Array(_perfBufferLen);
  if (_perfBufferLen < _PERF_BUFFER_SIZE) {
    for (let i = 0; i < _perfBufferLen; i++) out[i] = _perfFrameMs[i];
  } else {
    for (let i = 0; i < _PERF_BUFFER_SIZE; i++) {
      out[i] = _perfFrameMs[(_perfBufferIdx + i) % _PERF_BUFFER_SIZE];
    }
  }
  return out;
}

// Sparkline of fps over the buffer window. 0–120 fps range with grid lines at 30 / 60.
function _drawPerfGraph() {
  const ctx = _perfGraphCtx;
  const w = _perfGraphCanvas.width;
  const h = _perfGraphCanvas.height;
  ctx.clearRect(0, 0, w, h);
  // grid lines: 30 (warning) and 60 (good) fps
  ctx.lineWidth = 1;
  ctx.strokeStyle = 'rgba(120,80,60,0.5)';
  const y30 = h - (30 / 120) * h;
  ctx.beginPath(); ctx.moveTo(0, y30); ctx.lineTo(w, y30); ctx.stroke();
  ctx.strokeStyle = 'rgba(80,90,80,0.5)';
  const y60 = h - (60 / 120) * h;
  ctx.beginPath(); ctx.moveTo(0, y60); ctx.lineTo(w, y60); ctx.stroke();

  if (_perfBufferLen < 2) return;
  ctx.strokeStyle = '#9fe6a0';
  ctx.lineWidth = 1;
  ctx.beginPath();
  // Walk in order without allocating — read directly from the ring.
  const startOffset = _perfBufferLen < _PERF_BUFFER_SIZE ? 0 : _perfBufferIdx;
  for (let i = 0; i < _perfBufferLen; i++) {
    const ms = _perfFrameMs[(startOffset + i) % _PERF_BUFFER_SIZE];
    const fps = Math.min(120, 1000 / Math.max(0.1, ms));
    const x = (i / (_perfBufferLen - 1)) * w;
    const y = h - (fps / 120) * h;
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
  ctx.stroke();
}

// Builds a text summary of the perf buffer for clipboard copy. Compact — designed to be
// readable as plain text and small enough to paste into a chat without dominating context.
function _buildPerfLog() {
  const samples = _readPerfBufferOrdered();
  if (samples.length === 0) return '(no perf samples — enable overlay first)';
  const sorted = Array.from(samples).sort((a, b) => a - b);
  const pct = (p) => sorted[Math.min(sorted.length - 1, Math.floor((p / 100) * sorted.length))];
  const sum = sorted.reduce((a, v) => a + v, 0);
  const avgMs = sum / sorted.length;
  const avgFps = 1000 / avgMs;
  const minFps = 1000 / sorted[sorted.length - 1];
  const p99Ms = pct(99), p95Ms = pct(95), p50Ms = pct(50);
  const drops30 = sorted.filter(ms => 1000 / ms < 30).length;
  const drops45 = sorted.filter(ms => 1000 / ms < 45).length;
  const durSec = sum / 1000;
  const last60 = Array.from(samples.slice(-60)).map(v => v.toFixed(1)).join(', ');
  // Audio diagnostics — counters since overlay was enabled, plus AudioContext state changes.
  // Rates are per second (calls / duration) so they're comparable across short and long runs.
  const ctxState = audioCtx ? audioCtx.state : '(no ctx)';
  const baseLat = audioCtx ? (audioCtx.baseLatency * 1000).toFixed(1) : '?';
  const outLat = (audioCtx && audioCtx.outputLatency !== undefined) ? (audioCtx.outputLatency * 1000).toFixed(1) : '?';
  const rate = (n) => durSec > 0 ? (n / durSec).toFixed(1) : '0.0';
  const lines = [
    '=== Sounds Smashing perf log ===',
    `level: ${_currentLevel || '(none)'}`,
    `duration: ${durSec.toFixed(1)}s (${samples.length} frames)`,
    `fps: avg ${avgFps.toFixed(1)} · min ${minFps.toFixed(1)}`,
    `frame ms: p50 ${p50Ms.toFixed(1)} · p95 ${p95Ms.toFixed(1)} · p99 ${p99Ms.toFixed(1)}`,
    `worst frame: ${_perfWorstMs.toFixed(1)}ms (~${(1000 / Math.max(0.1, _perfWorstMs)).toFixed(0)}fps)`,
    `drops <30fps: ${drops30} (${(drops30 / samples.length * 100).toFixed(1)}%)`,
    `drops <45fps: ${drops45} (${(drops45 / samples.length * 100).toFixed(1)}%)`,
    '--- audio ---',
    `ctx state: ${ctxState} (history: ${_audioCtxStateLog || ctxState})`,
    `latency: base ${baseLat}ms  output ${outLat}ms`,
    `smash:  ${_audioSmashCalls} played, ${_audioSmashDropped} dropped (${rate(_audioSmashCalls)}/s, ${rate(_audioSmashDropped)}/s drop)`,
    `clink:  ${_audioClinkCalls} played, ${_audioClinkDropped} dropped (${rate(_audioClinkCalls)}/s, ${rate(_audioClinkDropped)}/s drop)`,
    `rubble: ${_audioRubbleCalls} played, ${_audioRubbleDropped} dropped (${rate(_audioRubbleCalls)}/s, ${rate(_audioRubbleDropped)}/s drop)`,
    `last 60 frame ms: [${last60}]`,
  ];
  return lines.join('\n');
}

// Diagnostic: count collision events drained per frame so the slow-frame logger has the data.
let _collisionEventsThisFrame = 0;
let _projectileHitsThisFrame = 0;
let _breaksThisFrame = 0;

// Per-frame caps on big SFX. When dense cascades (L10) or chip-storms layer many smash/coin
// sounds into the same audio buffer, the bus clips into a bit-crushed crackle that cuts out.
// Capping at the source keeps individual sounds clean — drops are imperceptible because the
// dropped voices were going to mask each other anyway.
let _smashSoundsThisFrame = 0;
let _coinClinksThisFrame = 0;
const _SMASH_MAX_PER_FRAME = 2;
const _COIN_CLINK_MAX_PER_FRAME = 4;

// Throttled logger for the per-frame MP/race guards. Surfaces the FIRST error immediately, then at most
// one per 2s, so a recurring bad frame is visible in the console (with the label + stack) without spamming
// it to death. The guards themselves are what keep a network-driven throw from freezing the render loop.
let _mpLoopErrAt = 0;
// ---- physics-poison diagnostics + graceful fail --------------------------------------------------
// A Rapier re-entrancy panic ("recursive use of an object ... unsafe aliasing in rust") permanently
// poisons the physics world: the leaked borrow makes EVERY later Rapier call throw, so the frame dies at
// the first physics touch (updateMovement's player move) and the screen freezes. We can't un-poison the
// wasm world, but we CAN (a) capture a rich snapshot of the FIRST failure so we can find the root cause,
// and (b) fail gracefully with a "please refresh" overlay instead of an opaque hang.
function _isPoisonError(e) {
  const m = (e && (e.message || String(e))) || '';
  return /recursive use|unsafe aliasing/i.test(m);
}
function _dumpFatal(e, where) {
  if (_firstFatalDumped) return;   // the first failure is the root cause; later ones are just the symptom
  _firstFatalDumped = true;
  try {
    console.error('%c[PHYSICS FATAL] first occurrence — copy everything from here up', 'color:#fff;background:#c0392b;padding:2px 8px;font-weight:bold;');
    console.error('where:', where, '| phase:', _phase);
    console.error('error:', e);
    console.error('stack:', e && e.stack);
    const snap = {
      where, phase: _phase, level: _currentLevel, raceOn: _RACE_ON, mpMode: _MP_MODE,
      myId: (_mp && _mp.myId) ? _mp.myId() : null,
      peers: (_mp && _mp.peerIds) ? _mp.peerIds() : null,
      pendingNetRapier: _pendingNetRapier.length, pendingKicks: _pendingKicks.length,
      pendingProjectile: _pendingProjectileImpacts.length,
      rematchPending: _raceRematchPending, spawnApply: _raceSpawnApply, clearPending: _raceClearPending,
      blocks: (typeof blocks !== 'undefined' && blocks) ? blocks.length : null,
      debris: (typeof debris !== 'undefined' && debris) ? debris.length : null,
    };
    console.error('state:', snap);
    console.error('state JSON (paste this):', JSON.stringify(snap));
  } catch (dumpErr) { console.error('[dump failed]', dumpErr); }
}
let _poisonOverlayEl = null;
function _showPoisonOverlay() {
  if (_poisonOverlayEl) return;
  try { if (_mp && _mp.close) _mp.close(); } catch { /* already gone */ }
  const el = document.createElement('div');
  el.style.cssText = 'position:fixed;inset:0;z-index:5000;display:flex;flex-direction:column;align-items:center;'
    + 'justify-content:center;gap:18px;background:rgba(10,8,32,0.92);color:#fff;text-align:center;padding:32px;';
  const h = document.createElement('div');
  h.style.cssText = 'font-family:var(--font-display);font-size:26px;letter-spacing:1px;color:var(--col-pink,#FF3B6B);'
    + '-webkit-text-stroke:2px var(--col-ink,#0a0820);paint-order:stroke;text-transform:uppercase;';
  h.textContent = 'Match hit a snag';
  const m = document.createElement('div');
  m.style.cssText = 'font-family:"Jersey 25",var(--font-body,ui-monospace),monospace;font-size:22px;max-width:420px;line-height:1.2;';
  m.textContent = 'The physics engine tripped up. Refresh the page to jump back in.';
  const b = document.createElement('button');
  b.type = 'button'; b.className = 'ss-btn';
  b.style.cssText = 'padding:14px 28px;font-family:var(--font-display);font-size:18px;color:#fff;'
    + '-webkit-text-stroke:2px var(--col-ink,#0a0820);paint-order:stroke;background:var(--col-green,#43d675);';
  b.textContent = 'Refresh';
  b.onclick = () => location.reload();
  el.append(h, m, b);
  document.body.appendChild(el);
  _poisonOverlayEl = el;
}
function _mpLoopError(e, where) {
  if (_isPoisonError(e)) { _dumpFatal(e, 'guard:' + where); _physicsPoisoned = true; }
  const now = performance.now();
  if (now - _mpLoopErrAt > 2000) { _mpLoopErrAt = now; console.error('[mp loop guard] ' + where + ':', e); }
}
function tick(nowMs) {
  requestAnimationFrame(tick);
  const _frameStart = performance.now();
  _collectsThisFrame = 0;  // resets per-frame chip-cleanup budget
  _collisionEventsThisFrame = 0;
  _projectileHitsThisFrame = 0;
  _breaksThisFrame = 0;
  _smashSoundsThisFrame = 0;
  _coinClinksThisFrame = 0;

  const elapsedMs = lastTickTimeMs < 0 ? 16.67 : (nowMs - lastTickTimeMs);
  lastTickTimeMs = nowMs;

  fpsAccumMs += elapsedMs;
  fpsFrames++;
  if (elapsedMs > fpsMaxFrameMs) fpsMaxFrameMs = elapsedMs;

  // Always record into the ring buffer — single Float32 write per frame, basically free.
  _perfFrameMs[_perfBufferIdx] = elapsedMs;
  _perfBufferIdx = (_perfBufferIdx + 1) % _PERF_BUFFER_SIZE;
  if (_perfBufferLen < _PERF_BUFFER_SIZE) _perfBufferLen++;
  if (_perfOverlayOn && elapsedMs > _perfWorstMs) {
    _perfWorstMs = elapsedMs;
    _perfWorstAtMs = nowMs;
  }

  if (fpsAccumMs >= 500) {
    const fps = Math.round((fpsFrames * 1000) / fpsAccumMs);
    const minFps = Math.round(1000 / fpsMaxFrameMs);
    if (_perfOverlayOn) {
      let chips = 0, chunks = 0;
      for (const d of debris) (d.userData.isCurrency ? chips++ : chunks++);
      let standing = 0, dynamicCount = 0;
      for (const b of blocks) (b.userData.dynamic ? dynamicCount++ : standing++);
      const musicLabel = getMusicLabel();
      // Sample AudioContext state every fps update; log transitions so a transient suspend
      // shows up in the copy log even if it recovered before the user hit "copy".
      const ctxState = audioCtx ? audioCtx.state : '(no ctx)';
      if (ctxState !== _audioCtxLastState) {
        _audioCtxStateLog += (_audioCtxStateLog ? ';' : '') + ctxState;
        _audioCtxLastState = ctxState;
      }
      fpsLabelEl.textContent =
        `${fps} fps  min ${minFps}\n` +
        `chips ${chips}  chunks ${chunks}\n` +
        `blocks ${standing}  dyn ${dynamicCount}\n` +
        `audio ${ctxState}  smash ${_audioSmashCalls}/${_audioSmashDropped}d  clink ${_audioClinkCalls}/${_audioClinkDropped}d  rubble ${_audioRubbleCalls}/${_audioRubbleDropped}d` +
        (musicLabel ? `\n${musicLabel}` : '');
      _drawPerfGraph();
    }
    fpsAccumMs = 0;
    fpsFrames = 0;
    fpsMaxFrameMs = 0;
  }

  const dt = Math.min(elapsedMs / 1000, 0.05);

  // Physics-poison gate: once the Rapier world is poisoned every physics call throws, so stop touching it
  // and show a "please refresh" overlay instead of a silent freeze. The rAF keeps ticking (rescheduled at
  // the top) so the overlay stays responsive.
  if (_physicsPoisoned) { _showPoisonOverlay(); return; }

  // Pause gate: skip all game-state updates while paused. Canvas keeps its last rendered
  // frame underneath the pause overlay (browsers don't auto-clear), so the scene appears
  // frozen — matching the real pause of physics + music. EXCEPTION: in multiplayer the world is
  // shared and must stay live even when this window has released the mouse, so a second window
  // keeps rendering + syncing while you play the other.
  if (_isPaused && !_mpEnabled) {
    return;
  }

  // Everything below touches Rapier. Wrap it so the FIRST re-entrancy panic is captured with a rich
  // snapshot (which _phase / which system) and latches _physicsPoisoned, rather than throwing uncaught
  // every frame forever with no context. _phase is updated as we go so the dump names the culprit system.
  try {
  _phase = 'move'; updateMovement(dt);
  updateAudioListener();
  updateNear();
  if (_coachActive && _coachPhase === 'approach' && nearTarget
      && (performance.now() - _coachApproachShownAt) >= _COACH_MIN_APPROACH_MS) _coachToSmash();  // L1 coach: block acquired (after the approach step has had time to read)
  _updateEasyMode();
  updateHighlightPulse();
  _updateGoalStarPulse();
  updateCharge(dt);
  updateSuckSystem(performance.now() / 1000);
  _phase = 'step'; stepPhysics(dt);
  _phase = 'sync'; syncBlocks();
  // Guarded: these process live network data (peer avatars, streamed/owned blocks) that can reference a
  // block or peer removed a frame earlier. An unguarded throw here recurs every frame BEFORE the render
  // call, which is exactly the "screen freezes and I can't get back in" hang — the rAF keeps spinning but
  // nothing ever repaints. Catch + log (throttled) so a bad frame is skipped, not fatal.
  try {
    if (_mp) {
      // tell the peer what we're locked onto (by netId) so their copy of us turns + aims at it
      if (_mp.setAim) _mp.setAim(lockedTarget && lockedTarget.userData ? lockedTarget.userData.netId : null);
      // and our streak + storm intensity, so their copy of us glows (heat ember → storm rainbow) like our fists
      if (_mp.setFx) _mp.setFx(_streak, _stormMaxIntensity);
      // and the avatar's drum-nod amount, derived from OUR synced beat grid (zero netcode — see _avatarDrumNod)
      if (_mp.setHeadBob) _mp.setHeadBob(_avatarDrumNod());
      _phase = 'mp-tick'; _mp.tick(dt);            // send my transform + interpolate the peer avatar
      _phase = 'mp-deferred'; _mpApplyDeferred();  // apply queued peer death/unanchor/motion/settle + deferred spawn/rematch HERE
      _phase = 'mp-sync-owned'; _mpSyncOwnedBlocks(dt);  // stream owned blocks' start/settle poses (+ emit re-kicks)
      _phase = 'mp-kicks'; _mpDrainPendingKicks();  // apply queued peer re-kicks HERE — post-step, world not borrowed
    }
    _phase = 'mp-remote'; _mpUpdateRemoteBlocks();   // stream mode: render peer-owned blocks from their streamed motion
  } catch (e) { _mpLoopError(e, 'mp-tick'); }
  _phase = 'debris'; updateDebris(dt);
  updateAutoFace(dt);
  _phase = 'fists'; updateFists(dt);

  // Combine head bob + charge wind-up + camera shake into one offset. All temporary, reverted post-render.
  const tempOffset = new THREE.Vector3();
  const planarSpeed = Math.hypot(moveVelocity.x, moveVelocity.z);
  const bobAmount = Math.min(planarSpeed / WALK_TERMINAL, 1);
  if (bobAmount > 0.001) {
    tempOffset.x += Math.sin(bobDistance * BOB_HORIZ_RAD_PER_M) * BOB_HORIZ_MAG * bobAmount;
    tempOffset.y += Math.sin(bobDistance * BOB_VERT_RAD_PER_M) * BOB_VERT_MAG * bobAmount;
  }
  // Charge wind-up — body leans back (positional offset only). Yaw/pitch rotation was removed
  // because mouseup fires between frames when the camera is in its un-rotated state — players
  // were aiming by the rotated render but chips launched from the un-rotated forward, missing.
  const cL = displayedCharge[0];
  const cR = displayedCharge[1];
  const maxCharge = Math.max(cL, cR);
  if (maxCharge > 0.001) {
    const camRight = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 0);
    camRight.y = 0; if (camRight.lengthSq() > 0.0001) camRight.normalize();
    const camBack = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 2);
    camBack.y = 0; if (camBack.lengthSq() > 0.0001) camBack.normalize();
    const lateral = (cL - cR) * 0.10;
    tempOffset.addScaledVector(camRight, lateral);
    tempOffset.y += maxCharge * 0.10;
    tempOffset.addScaledVector(camBack, maxCharge * 0.18);
  }
  if (performance.now() < shakeEndTime) {
    tempOffset.x += (Math.random() - 0.5) * 2 * shakeMag;
    tempOffset.y += (Math.random() - 0.5) * 2 * shakeMag;
    tempOffset.z += (Math.random() - 0.5) * 2 * shakeMag;
  }
  camera.position.add(tempOffset);
  // Refresh fist world matrices then update beams — endpoints now reflect the rendered camera state.
  camera.updateMatrixWorld(true);
  leftFist.updateMatrixWorld(true);
  rightFist.updateMatrixWorld(true);
  updateEnergyBeam();
  updateBeatCue();
  _updateHudBeatAnchors();
  _updateScoreRoll(dt);
  _updateTargetTick();
  _updateLoadingSpeakerPulse();
  _phase = 'dying-blocks'; _updateDyingBlocks();
  _updateImpactFX(dt);
  try { _updatePeerCoins(dt); _updatePeerOrbit(dt); } catch (e) { _mpLoopError(e, 'peer-fx'); }
  _updateTrailDots();
  updateChargeVignette(dt);
  updateChargeRing();
  _checkStreakIdleReset();
  _updateStreakPill();
  _updateStormMaxState(dt);
  _updateFistColor();
  _updateStormMaxVignette();
  _phase = 'coin-life'; _updatePhysicalCoinLifetimes();
  try { _checkLevelEnd(); } catch (e) { _mpLoopError(e, 'level-end'); }
  _updateBlockBeatPulse();
  _updateClouds(dt);
  _updateBirds(dt);
  _updateBoombox();
  _updateSpeakers();
  _updateWallLeds();
  _updateSkyBeatPulse();
  // Storm-max sky orbit runs AFTER beat pulse so its sunPosition/rayleigh writes win
  // over the beat-pulse's rayleigh write while storm is active. When storm intensity
  // is 0 the function early-returns, leaving the beat pulse in control.
  _updateStormMaxSky();
  _phase = 'render'; renderer.render(scene, camera);
  camera.position.sub(tempOffset);
  } catch (_frameErr) {
    // The first uncaught throw in the physics/update region — capture it richly. If it's a Rapier
    // re-entrancy panic, latch the poison flag so the next frame shows the refresh overlay instead of
    // freezing. Non-poison throws are dumped once too (so we see them) but don't latch.
    _dumpFatal(_frameErr, 'frame:' + _phase);
    if (_isPoisonError(_frameErr)) { _physicsPoisoned = true; _showPoisonOverlay(); }
  }

  // Slow-frame diagnostic — log a snapshot whenever a frame takes >50ms so we can see what
  // state correlates with the freeze. Includes counts of every "this frame" thing we track.
  const _frameMs = performance.now() - _frameStart;
  if (_frameMs > 50) {
    let pendingCollects = 0, currencyCount = 0, frozenCount = 0, armedCount = 0;
    const nowSec = performance.now() / 1000;
    for (const c of debris) {
      if (c.userData.pendingCollect) pendingCollects++;
      if (c.userData.isCurrency) currencyCount++;
      if (c.userData.frozen) frozenCount++;
      if (c.userData.projectileUntil && c.userData.projectileUntil > nowSec) armedCount++;
    }
    console.warn(`[slow-frame] ${_frameMs.toFixed(0)}ms`
      + ` blocks=${blocks.length}`
      + ` debris=${debris.length}`
      + ` currency=${currencyCount}`
      + ` armed=${armedCount}`
      + ` pendingCollect=${pendingCollects}`
      + ` frozen=${frozenCount}`
      + ` collisionEvents=${_collisionEventsThisFrame}`
      + ` projectileHits=${_projectileHitsThisFrame}`
      + ` breaks=${_breaksThisFrame}`);
  }
}
requestAnimationFrame(tick);
